import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  Activity,
  ArrowUp,
  BarChart3,
  BookOpen,
  Bot,
  Calendar,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock3,
  ExternalLink,
  FileSpreadsheet,
  Home,
  Info,
  Loader2,
  Play,
  Radar,
  RefreshCw,
  ShieldCheck,
  Ship,
  SlidersHorizontal,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import './Fix2V5Sim.theme.css';

function splitSchedulerBullet(bullet) {
  const text = String(bullet || '').trim();
  const match = text.match(/^(\d{2}:\d{2}\s+.+?:)\s*(.*)$/);
  if (!match) {
    return { lead: text, description: '' };
  }
  return {
    lead: match[1],
    description: match[2] || '',
  };
}

function renderHowText(text) {
  return String(text || '')
    .split(/(`[^`]+`|\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((part, index) => {
      if (part.startsWith('`') && part.endsWith('`')) {
        return (
          <code key={`${part}-${index}`} className="rounded bg-slate-950/15 px-1 py-0.5 font-mono text-[0.92em]">
            {part.slice(1, -1)}
          </code>
        );
      }
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`${part}-${index}`}>{part.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    });
}

const MAX_AUTO_REFRESH_RESULTS_RANGE_DAYS = 45;
const NOBLE_PREVIEW_TIMEOUT_MS = 45000;
const LETS_TRADE_YACHT_TARGET_PLN = 2000000;
const LETS_TRADE_YACHT_IMAGE_URL = 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?auto=format&fit=crop&w=640&q=80';
const SIM_V5_CLIENT_CACHE_VERSION = 'goose-risk-management-ui-2026-05-18-v3';
const SIM_LANGUAGE_COOKIE = 'fix2_v5_sim_language';

function formatZeroBasedDeliveryHourLabel(hourValue) {
  const numericHour = Number(hourValue);
  if (!Number.isFinite(numericHour)) return '-';
  return `H${String(numericHour + 1).padStart(2, '0')}`;
}

function formatOneBasedDeliveryHourLabel(hourValue) {
  const numericHour = Number(hourValue);
  if (!Number.isFinite(numericHour)) return '-';
  return `H${String(numericHour).padStart(2, '0')}`;
}

const SIM_LANGUAGE_OPTIONS = [
  { key: 'en', label: 'English', shortLabel: 'EN', flag: '🇬🇧' },
  { key: 'pl', label: 'Polski', shortLabel: 'PL', flag: '🇵🇱' },
];
const MODIFY_SIMULATION_VARIANTS = [
  { key: 'goose-v3', label: 'Goose v3.1.0' },
  { key: 'goose-v5', label: 'Goose v5.0.0' },
];
const QUICK_REPLAY_BOOLEAN_FIELDS = new Set(['apply_ridge_shift', 'apply_risk_score']);
const QUICK_REPLAY_STRING_FIELDS = new Set(['risk_score_model', 'base_carry_mode']);
const DEFAULT_QUICK_REPLAY_FORM = {
  base_volume: '50',
  fix_i_model_spread: '5',
  prediction_filter_margin: '10',
  bucket1_volume: '40',
  bucket2_volume: '40',
  bucket3_volume: '40',
  np_constraint_bucket1: '0',
  np_constraint_bucket2: '0',
  np_constraint_bucket3: '0',
  fix_ii_ida1_bucket1_volume: '20',
  fix_ii_ida1_bucket2_volume: '40',
  fix_ii_ida1_bucket3_volume: '60',
  max_avg_volume_pct: '30',
  max_position: '60',
  ida1_margin: '1500',
  margin_tier1: '200',
  margin_tier2: '300',
  margin_tier3: '400',
  margin_tier4: '500',
  apply_ridge_shift: false,
  apply_risk_score: true,
  risk_score_model: 'prod-v2.0.0',
  ida2_margin: '1',
  ida2_min_edge_per_mwh: '20',
  base_carry_mode: 'strict_v4',
};

function getDefaultQuickReplayFormForVariant(variant = 'goose-v3') {
  return {
    ...DEFAULT_QUICK_REPLAY_FORM,
    apply_ridge_shift: variant === 'goose-v3',
  };
}
const PREDICTION_INPUT_CAPACITY_COLUMNS = [
  { column: 'np_import_constraint_pl', labelKey: 'importLimit', fallbackGroup: 'signals' },
  { column: 'np_export_constraint_pl', labelKey: 'exportLimit', fallbackGroup: 'signals' },
  { column: 'np_total_import_capacity', labelKey: 'importCapacity' },
  { column: 'np_total_export_capacity', labelKey: 'exportCapacity' },
];
const PINNED_PREDICTION_INPUT_COLUMNS = new Set(PREDICTION_INPUT_CAPACITY_COLUMNS.map(({ column }) => column));

function getCookieValue(name) {
  if (typeof document === 'undefined') {
    return '';
  }
  const cookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : '';
}

function getInitialSimulationLanguage() {
  const cookieValue = getCookieValue(SIM_LANGUAGE_COOKIE);
  return ['en', 'pl'].includes(cookieValue) ? cookieValue : 'en';
}

function setSimulationLanguageCookie(language) {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${SIM_LANGUAGE_COOKIE}=${encodeURIComponent(language)}; path=/; max-age=31536000; SameSite=Lax`;
}

function isClientNetworkErrorMessage(message) {
  const normalized = String(message || '').trim().toLowerCase();
  return (
    normalized === 'failed to fetch'
    || normalized === 'networkerror'
    || normalized.includes('network error')
    || normalized.includes('load failed')
    || normalized.includes('the network connection was lost')
  );
}

function isExpectedPreSettlementDataGapMessage(message) {
  const normalized = String(message || '').trim().toLowerCase();
  return [
    'failed to fetch fixing prices',
    'fixing prices',
    'realised market prices',
    'summary and daily results need',
    'no fix i/fix ii fixing prices',
  ].some((needle) => normalized.includes(needle));
}

function getInclusiveDaySpan(startDate, endDate) {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(Math.round((end.getTime() - start.getTime()) / 86400000) + 1, 0);
}

function shiftIsoDate(dateValue, dayDelta) {
  const normalizedDate = String(dateValue || '').slice(0, 10);
  const match = normalizedDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  const utcDate = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  utcDate.setUTCDate(utcDate.getUTCDate() + Number(dayDelta || 0));
  return utcDate.toISOString().slice(0, 10);
}

function getWarsawClockParts(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour || 0) * 60 + Number(parts.minute || 0),
  };
}

function shouldSuppressEarlyFix2BaseExecutionRecalcFailure(recalcStatus, deliveryDate) {
  const status = String(recalcStatus?.status || '').toLowerCase();
  const stage = String(recalcStatus?.current_stage || '').toLowerCase();
  if (status !== 'failed' || stage !== 'settle_fix2_partial') return false;
  return shouldSkipSameDayEarlyFix2Recalc(deliveryDate);
}

function shouldSkipSameDayEarlyFix2Recalc(deliveryDate) {
  const warsawNow = getWarsawClockParts();
  return String(deliveryDate || '').slice(0, 10) === warsawNow.date && warsawNow.minutes < (13 * 60 + 40);
}

function isManualRecalcActive(recalcStatus) {
  return ['pending', 'running'].includes(String(recalcStatus?.status || '').toLowerCase());
}

function getManualRecalcLabel(recalcStatus) {
  const status = String(recalcStatus?.status || '').toLowerCase();
  const rawStage = String(recalcStatus?.current_stage || '');
  const stage = rawStage.replaceAll('_', ' ');
  if (rawStage === 'wait_for_monitor') return 'Manual recalc waiting for monitor';
  if (status === 'completed') return 'Manual recalc completed';
  if (status === 'failed') return `Manual recalc failed${stage ? ` · ${stage}` : ''}`;
  if (status === 'running') return `Manual recalc running${stage ? ` · ${stage}` : ''}`;
  if (status === 'pending') return `Manual recalc pending${stage ? ` · ${stage}` : ''}`;
  return 'Manual recalc queued';
}

function getBaseExecutionRecalcLabel(recalcStatus, language = 'en') {
  const status = String(recalcStatus?.status || '').toLowerCase();
  const rawStage = String(recalcStatus?.current_stage || '');
  const stageLabel = recalcStatus?.current_stage_label || rawStage.replaceAll('_', ' ');
  const isPl = language === 'pl';
  if (status === 'completed') return isPl ? 'BASE przeliczony' : 'BASE settle completed';
  if (status === 'failed') return isPl ? `BASE recalc nieudany${stageLabel ? ` · ${stageLabel}` : ''}` : `BASE recalc failed${stageLabel ? ` · ${stageLabel}` : ''}`;
  if (status === 'running') return isPl ? `Przeliczanie BASE${stageLabel ? ` · ${stageLabel}` : ''}` : `BASE settle running${stageLabel ? ` · ${stageLabel}` : ''}`;
  if (status === 'pending') return isPl ? `BASE czeka na przeliczenie${stageLabel ? ` · ${stageLabel}` : ''}` : `BASE settle pending${stageLabel ? ` · ${stageLabel}` : ''}`;
  return isPl ? 'BASE recalc w kolejce' : 'BASE settle queued';
}

function getManualRecalcBadgeClass(recalcStatus) {
  const status = String(recalcStatus?.status || '').toLowerCase();
  if (status === 'completed') return 'border-emerald-200 bg-emerald-100 text-emerald-900';
  if (status === 'failed') return 'border-rose-200 bg-rose-100 text-rose-900';
  return 'border-amber-200 bg-amber-100 text-amber-900';
}

function getDayRadarMarketNewsText(marketNews) {
  if (marketNews?.status === 'success') {
    return marketNews.summary || 'Brak istotnych newsów w odpowiedzi web.';
  }
  if (marketNews?.status === 'unavailable') {
    const reasonCode = String(marketNews.reason_code || '').toLowerCase();
    const rawReason = String(marketNews.reason || '').trim();
    const rawLooksLike429 = /429|too many requests|rate limit/i.test(rawReason);
    if (reasonCode === 'openai_rate_limited' || rawLooksLike429) {
      return 'Niedostępne: OpenAI zwrócił limit 429. Radar liczbowy jest gotowy; pominięto tylko News / web.';
    }
    if (reasonCode === 'openai_quota_exhausted') {
      return 'Niedostępne: OpenAI zwrócił 429 dla kwoty/limitu organizacji. Radar liczbowy jest gotowy; trzeba odblokować limit API dla News / web.';
    }
    if (reasonCode === 'missing_openai_api_key') {
      return 'Niedostępne: brak konfiguracji OpenAI API dla sekcji News / web.';
    }
    if (reasonCode === 'openai_request_error') {
      return `Niedostępne: ${rawReason || 'OpenAI nie odpowiedział na czas dla sekcji News / web.'}`;
    }
    if (reasonCode === 'openai_upstream_error') {
      return `Niedostępne: ${rawReason || 'OpenAI/web_search zwrócił tymczasowy błąd upstream.'}`;
    }
    return `Niedostępne: ${rawReason || 'sekcja News / web chwilowo niedostępna'}`;
  }
  return 'Pominięto.';
}

function getDayRadarCitationHost(source) {
  const rawUrl = typeof source === 'string' ? source : source?.url;
  try {
    return new URL(rawUrl).hostname.replace(/^www\./, '');
  } catch {
    return rawUrl || 'source';
  }
}

function getDayRadarCitationLabel(source) {
  const title = String(source?.title || '').trim();
  if (title && title.length <= 54) return title;
  return getDayRadarCitationHost(source);
}

function renderDayRadarInlineText(text, darkMode) {
  const parts = String(text || '').split(/(\[[^\]]+\]\(https?:\/\/[^)]+\))/g).filter(Boolean);
  return parts.map((part, index) => {
    const match = part.match(/^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/);
    if (!match) {
      return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
    }
    return (
      <a
        key={`${match[2]}-${index}`}
        href={match[2]}
        target="_blank"
        rel="noreferrer"
        className={`inline-flex items-center gap-1 font-semibold underline decoration-dotted underline-offset-2 ${darkMode ? 'text-cyan-200' : 'text-cyan-700'}`}
      >
        {match[1]}
        <ExternalLink size={12} />
      </a>
    );
  });
}

function splitDayRadarNewsText(text) {
  const source = String(text || '').trim();
  const impactMatch = source.match(/Wpływ na nasz dzień\s*:\s*/i);
  if (!impactMatch) {
    const sourceMatch = source.match(/Źród(?:ło|ła)\s*:\s*/i);
    if (!sourceMatch) {
      return { factText: source, impactText: '', sourceText: '' };
    }
    return {
      factText: source.slice(0, sourceMatch.index).trim(),
      impactText: '',
      sourceText: source.slice(sourceMatch.index).trim(),
    };
  }

  const factText = source.slice(0, impactMatch.index).trim();
  const impactStart = impactMatch.index + impactMatch[0].length;
  const impactAndSource = source.slice(impactStart).trim();
  const sourceMatch = impactAndSource.match(/Źród(?:ło|ła)\s*:\s*/i);
  if (!sourceMatch) {
    return { factText, impactText: impactAndSource, sourceText: '' };
  }
  return {
    factText,
    impactText: impactAndSource.slice(0, sourceMatch.index).trim(),
    sourceText: impactAndSource.slice(sourceMatch.index).trim(),
  };
}

function parseDayRadarMarketNews(marketNews) {
  const fallbackText = getDayRadarMarketNewsText(marketNews);
  if (marketNews?.status !== 'success') {
    return { intro: fallbackText, bullets: [], sections: [], citations: [] };
  }
  const lines = String(marketNews.summary || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const bullets = [];
  const sections = [];
  let intro = '';
  let section = '';

  lines.forEach((line) => {
    const isBullet = /^[-–•]\s+/.test(line);
    const headingMatch = line.match(/^#{1,4}\s*(.+)$/);
    const clean = line.replace(/^[-–•]\s+/, '').trim();
    if (!clean) return;
    if (headingMatch) {
      section = headingMatch[1].trim().replace(/:$/, '');
      sections.push(section);
      return;
    }
    const looksLikeSection = !isBullet && clean.length <= 70 && !/[.!?)]$/.test(clean);
    if (!intro && !isBullet && !looksLikeSection) {
      intro = clean;
      return;
    }
    if (looksLikeSection) {
      section = clean.replace(/:$/, '');
      sections.push(section);
      return;
    }
    const severityMatch = clean.match(/^\[(HIGH|MED|MEDIUM|LOW)\]\s*/i);
    const severityRaw = severityMatch ? severityMatch[1].toLowerCase() : 'medium';
    const severity = severityRaw === 'high' ? 'high' : severityRaw === 'low' ? 'low' : 'medium';
    const text = severityMatch ? clean.slice(severityMatch[0].length).trim() : clean;
    bullets.push({ section, text, severity, ...splitDayRadarNewsText(text) });
  });

  if (!intro && !bullets.length) {
    intro = fallbackText;
  }

  return {
    intro,
    bullets,
    sections,
    citations: Array.isArray(marketNews.citations) ? marketNews.citations : [],
  };
}

function normalizeDayRadarTopicText(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/[^a-z0-9ąćęłńóśźż\s-]/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getDayRadarTopicKey(item) {
  const text = normalizeDayRadarTopicText(`${item?.section || ''} ${item?.factText || ''} ${item?.impactText || ''} ${item?.sourceText || ''}`);
  if (!text) return '';
  if (/cernavod|rumun|romani|nuclearelectrica|jadrow|nuclear|atom/.test(text)) return 'ro-nuclear';
  if (/sidc|xbid|jednolitego laczenia|intraday/.test(text)) return 'sidc-intraday';
  if (/jaworzno|tauron/.test(text)) return 'jaworzno';
  if (/ttf|gaz|gas/.test(text)) return 'ttf-gas';
  if (/transgranic|przepust|zdolnos|interkonekt|jao|core|cee|atc/.test(text)) return 'cross-border';
  return text.split(' ').filter((word) => word.length > 3).slice(0, 8).join('-');
}

function getDayRadarSeverityRank(severity) {
  return { high: 3, medium: 2, low: 1 }[String(severity || '').toLowerCase()] || 2;
}

function getDayRadarMaxSeverity(left, right) {
  return getDayRadarSeverityRank(left) >= getDayRadarSeverityRank(right) ? left : right;
}

function joinDayRadarTextParts(parts) {
  const seen = new Set();
  return (parts || [])
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .filter((part) => {
      const normalized = normalizeDayRadarTopicText(part);
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    })
    .join(' ');
}

function buildDayRadarMarketTape(anomalyMatches, newsItems) {
  const newsByTopic = (newsItems || []).reduce((acc, item) => {
    const topic = getDayRadarTopicKey(item);
    if (!topic) return acc;
    acc[topic] = acc[topic] || [];
    acc[topic].push(item);
    return acc;
  }, {});
  const usedTopics = new Set();
  const merged = [];

  (anomalyMatches || []).forEach((item, index) => {
    const topic = getDayRadarTopicKey(item);
    const relatedNews = topic ? newsByTopic[topic] || [] : [];
    const strongestNews = relatedNews.reduce((best, candidate) => (
      !best || getDayRadarSeverityRank(candidate.severity) > getDayRadarSeverityRank(best.severity) ? candidate : best
    ), null);
    if (topic) usedTopics.add(topic);
    merged.push({
      ...item,
      displaySection: 'Wyjaśnia sygnał danych',
      severity: strongestNews ? getDayRadarMaxSeverity(item.severity, strongestNews.severity) : item.severity,
      topicKey: topic || `anomaly-${index}`,
      sourceText: joinDayRadarTextParts([item.sourceText, strongestNews?.sourceText]),
      supportingNewsText: strongestNews && normalizeDayRadarTopicText(strongestNews.factText) !== normalizeDayRadarTopicText(item.factText)
        ? strongestNews.factText
        : '',
      impactText: item.impactText || strongestNews?.impactText || '',
      tapeKind: 'anomaly',
      originalIndex: index,
    });
  });

  (newsItems || []).forEach((item, index) => {
    const topic = getDayRadarTopicKey(item);
    if (topic && usedTopics.has(topic)) return;
    if (topic) usedTopics.add(topic);
    merged.push({
      ...item,
      displaySection: item.severity === 'high' ? 'Istotny news' : 'Market news',
      topicKey: topic || `news-${index}`,
      tapeKind: 'news',
      originalIndex: 100 + index,
    });
  });

  return merged
    .sort((left, right) => {
      const severityDelta = getDayRadarSeverityRank(right.severity) - getDayRadarSeverityRank(left.severity);
      if (severityDelta) return severityDelta;
      if (left.tapeKind !== right.tapeKind) return left.tapeKind === 'anomaly' ? -1 : 1;
      return left.originalIndex - right.originalIndex;
    })
    .slice(0, 8);
}

function isPartialFix2SettleRow(row) {
  return ['partial_fix2_settle', 'settled_fix2_fallback'].includes(row?.status);
}

function isPartialIda1SettleRow(row) {
  return ['settled_ida1_fallback', 'settled_ida1_fallback_no_settlement'].includes(row?.status);
}

function isIda1FinalFallbackSettleRow(row) {
  return row?.status === 'settled_ida1_fallback';
}

function isIda1PendingPartialSettleRow(row) {
  return row?.status === 'settled_ida1_fallback_no_settlement';
}

function isRealizedSimulationRow(row) {
  const status = String(row?.status || '');
  return status === 'partial_fix2_settle' || status.startsWith('settled');
}

function getSettleStageAvailabilityFromRow(row) {
  const status = String(row?.status || '');
  if (status === 'partial_fix2_settle' || status === 'settled_fix2_fallback') {
    return { fix2: true, ida1: false, ida2: false };
  }
  if (status === 'settled_ida1_fallback' || status === 'settled_ida1_fallback_no_settlement') {
    return { fix2: true, ida1: true, ida2: false };
  }
  if (status === 'settled_ida2_no_ida1') {
    return { fix2: true, ida1: false, ida2: true };
  }
  if (status === 'settled' || status === 'settled_ida2_no_ida2') {
    return { fix2: true, ida1: true, ida2: true };
  }
  return { fix2: false, ida1: false, ida2: false };
}

function isIda1BenchmarkRow(row) {
  const status = String(row?.status || '');
  return status === 'settled'
    || status === 'settled_ida1_fallback'
    || status === 'settled_ida1_fallback_no_settlement';
}

function getLiveProvisionalDailyResultValue(row) {
  if (!row) {
    return null;
  }
  if (isPartialFix2SettleRow(row)) {
    return row.daily_pnl_fix2_close;
  }
  if (isPartialIda1SettleRow(row)) {
    return row.daily_pnl;
  }
  if (row.daily_pnl_ida2_close !== null && row.daily_pnl_ida2_close !== undefined) {
    return row.daily_pnl_ida2_close;
  }
  return row.daily_pnl;
}

function getLiveProvisionalDailyAfterFeesValue(row) {
  if (!row) {
    return null;
  }
  if (isPartialFix2SettleRow(row)) {
    return row.daily_pnl_after_fees_fix2_close;
  }
  if (isPartialIda1SettleRow(row)) {
    return row.daily_pnl_after_fees;
  }
  if (row.daily_pnl_after_fees_ida2_close !== null && row.daily_pnl_after_fees_ida2_close !== undefined) {
    return row.daily_pnl_after_fees_ida2_close;
  }
  return row.daily_pnl_after_fees;
}

function getLegacyGooseComparablePnl(row) {
  if (!row) {
    return null;
  }
  if (row.daily_pnl_legacy_bison_fixing !== null && row.daily_pnl_legacy_bison_fixing !== undefined) {
    return Number(row.daily_pnl_legacy_bison_fixing);
  }
  const basePnl = Number(row.base_pnl);
  const fixingPnl = Number(row.probabilistic_pnl);
  if (!Number.isFinite(basePnl) || !Number.isFinite(fixingPnl)) {
    return null;
  }
  return Number((basePnl + fixingPnl).toFixed(2));
}

function getLegacyGooseComparableAfterFees(row) {
  if (!row) {
    return null;
  }
  if (row.daily_pnl_after_fees_legacy_bison_fixing !== null && row.daily_pnl_after_fees_legacy_bison_fixing !== undefined) {
    return Number(row.daily_pnl_after_fees_legacy_bison_fixing);
  }
  return null;
}

function getLegacyGooseComparableVolume(row) {
  if (!row) {
    return null;
  }
  if (row.total_volume_legacy_bison_fixing !== null && row.total_volume_legacy_bison_fixing !== undefined) {
    return Number(row.total_volume_legacy_bison_fixing);
  }
  return null;
}

function getLegacyGooseComparableFees(row) {
  if (!row) {
    return null;
  }
  const transactionFee = row.transaction_fee_legacy_bison_fixing;
  const volumeFee = row.volume_fee_legacy_bison_fixing;
  if (
    transactionFee !== null && transactionFee !== undefined
    && volumeFee !== null && volumeFee !== undefined
  ) {
    return Number((Number(transactionFee) + Number(volumeFee)).toFixed(2));
  }
  const gross = getLegacyGooseComparablePnl(row);
  const afterFees = getLegacyGooseComparableAfterFees(row);
  if (gross === null || afterFees === null) {
    return null;
  }
  return Number((gross - afterFees).toFixed(2));
}

function getGooseV5Ida1ReferencePnl(row) {
  if (!row) {
    return null;
  }
  if (row.bison_v5_ida1_bison_calc_pnl !== null && row.bison_v5_ida1_bison_calc_pnl !== undefined) {
    return Number(row.bison_v5_ida1_bison_calc_pnl);
  }
  if (row.v5_ida1_pnl !== null && row.v5_ida1_pnl !== undefined) {
    return Number(row.v5_ida1_pnl);
  }
  return null;
}

function getGooseV5Ida1ReferenceAfterFees(row) {
  if (!row) {
    return null;
  }
  if (row.bison_v5_ida1_bison_calc_after_fees !== null && row.bison_v5_ida1_bison_calc_after_fees !== undefined) {
    return Number(row.bison_v5_ida1_bison_calc_after_fees);
  }
  if (row.v5_ida1_after_fees !== null && row.v5_ida1_after_fees !== undefined) {
    return Number(row.v5_ida1_after_fees);
  }
  return null;
}

function getGooseV5Ida1ReferenceVolume(row) {
  if (!row) {
    return null;
  }
  if (row.bison_v5_ida1_bison_calc_volume_mwh !== null && row.bison_v5_ida1_bison_calc_volume_mwh !== undefined) {
    return Number(row.bison_v5_ida1_bison_calc_volume_mwh);
  }
  if (row.v5_ida1_volume_mwh !== null && row.v5_ida1_volume_mwh !== undefined) {
    return Number(row.v5_ida1_volume_mwh);
  }
  return null;
}

function getGooseV5Ida1ReferenceFees(row) {
  if (!row) {
    return null;
  }
  const gross = getGooseV5Ida1ReferencePnl(row);
  const afterFees = getGooseV5Ida1ReferenceAfterFees(row);
  if (gross === null || afterFees === null) {
    return null;
  }
  return Number((gross - afterFees).toFixed(2));
}

function HourlyPnLChart({ rows, formatNumber }) {
  const maxAbs = Math.max(
    ...rows.map((row) => Math.abs(Number(row.hourly_pnl || 0))),
    1,
  );

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
      <div className="text-sm font-semibold text-gray-700 mb-3">Hourly PnL</div>
      <div className="grid grid-cols-12 md:grid-cols-24 gap-2 items-end h-44">
        {rows.map((row) => {
          const value = Number(row.hourly_pnl || 0);
          const heightPct = Math.max((Math.abs(value) / maxAbs) * 100, value === 0 ? 2 : 8);
          return (
            <div key={`chart-${row.hour}`} className="flex flex-col items-center justify-end h-full">
              <div className="flex-1 flex items-end w-full">
                <div
                  title={`${formatZeroBasedDeliveryHourLabel(row.hour)}: ${formatNumber(value)} PLN`}
                  className={`w-full rounded-t ${value >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                  style={{ height: `${heightPct}%` }}
                />
              </div>
              <div className="text-[10px] text-gray-500 mt-2">{String(row.hour).padStart(2, '0')}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AccumulatedPnLChart({
  rows,
  parityRows = [],
  formatNumber,
  darkMode = false,
  copy = {},
  getIda1BenchmarkPnl,
}) {
  const chartCopy = {
    title: 'Accumulated PnL',
    body: 'The legend below shows cumulative PnL for the live Goose path, Goose IDA1 Stop, Fix II Stop, plus Goose v5/v3 reference replays.',
    currentResult: 'Current simulation result',
    mooseIda1Stop: 'Goose IDA1 Stop',
    fix2StopBenchmark: 'Fix II Stop Benchmark',
    tooltipFix2Stop: 'Acc. PnL (Fix II Stop Benchmark)',
    tooltipGooseV3: 'Acc. PnL (Goose v3 IDA1 reference)',
    tooltipGooseV5: 'Acc. PnL (Goose v5 IDA1 reference)',
    tooltipGooseIda1: 'Acc. PnL (Goose IDA1 Stop)',
    tooltipCurrent: 'Acc. PnL (current simulation result)',
    ...copy,
  };
  let liveProvisionalRunning = 0;
  let ida1V5BenchmarkRunning = 0;
  let bisonV5Running = 0;
  let bisonV3Running = 0;
  const bisonV3ByDate = new Map(
    [...parityRows]
      .filter((row) => row?.date && row?.bison_ida1_pnl !== null && row?.bison_ida1_pnl !== undefined)
      .map((row) => [String(row.date).slice(0, 10), Number(row.bison_ida1_pnl || 0)]),
  );
  const bisonV5ByDate = new Map(
    [...parityRows]
      .filter((row) => row?.date && row?.v5_ida1_pnl !== null && row?.v5_ida1_pnl !== undefined)
      .map((row) => [String(row.date).slice(0, 10), Number(row.v5_ida1_pnl || 0)]),
  );
  const chartColors = {
    liveProvisional: darkMode ? '#22c55e' : '#15803d',
    ida1Benchmark: darkMode ? '#f8fafc' : '#475569',
    bisonV5: darkMode ? '#22d3ee' : '#0891b2',
    fix2Close: darkMode ? '#60a5fa' : '#2563eb',
    bisonV3: darkMode ? '#fbbf24' : '#b45309',
    grid: darkMode ? '#334155' : '#d1d5db',
    axis: darkMode ? '#cbd5e1' : '#6b7280',
    zeroLine: darkMode ? '#64748b' : '#9ca3af',
    cardBg: darkMode ? 'bg-slate-900/80' : 'bg-white',
    cardBorder: darkMode ? 'border border-slate-700/70' : '',
    cardText: darkMode ? 'text-slate-100' : 'text-gray-800',
    mutedText: darkMode ? 'text-slate-300' : 'text-gray-500',
    legendBg: darkMode ? 'bg-slate-950/60 border border-slate-700/70' : 'bg-gray-50 border border-gray-200',
    tooltipBg: darkMode ? '#0f172a' : '#ffffff',
    tooltipBorder: darkMode ? '#334155' : '#d1d5db',
    tooltipText: darkMode ? '#e2e8f0' : '#111827',
  };
  const chartData = [...rows]
    .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    .map((row) => {
      const hasLiveProvisionalValue = isRealizedSimulationRow(row);
      if (hasLiveProvisionalValue) {
        liveProvisionalRunning = Number((
          liveProvisionalRunning + Number(getLiveProvisionalDailyResultValue(row) || 0)
        ).toFixed(2));
      }
      const dateKey = String(row.date).slice(0, 10);
      const rawIda1BenchmarkValue = getIda1BenchmarkPnl(row);
      const ida1V5BenchmarkValue = rawIda1BenchmarkValue === null ? null : Number(rawIda1BenchmarkValue);
      const hasIda1V5BenchmarkValue = isIda1BenchmarkRow(row) && Number.isFinite(ida1V5BenchmarkValue);
      if (hasIda1V5BenchmarkValue) {
        ida1V5BenchmarkRunning = Number((ida1V5BenchmarkRunning + ida1V5BenchmarkValue).toFixed(2));
      }
      const bisonV5Value = bisonV5ByDate.get(dateKey);
      const hasGooseV5Value = bisonV5Value !== null && bisonV5Value !== undefined && Number.isFinite(bisonV5Value);
      if (hasGooseV5Value) {
        bisonV5Running = Number((
          bisonV5Running + Number(bisonV5Value || 0)
        ).toFixed(2));
      }
      const bisonV3Value = bisonV3ByDate.get(dateKey);
      const hasGooseV3Value = bisonV3Value !== null && bisonV3Value !== undefined && Number.isFinite(bisonV3Value);
      if (hasGooseV3Value) {
        bisonV3Running = Number((bisonV3Running + bisonV3Value).toFixed(2));
      }
      return {
        date: String(row.date).slice(5, 10),
        accPnl: hasLiveProvisionalValue ? liveProvisionalRunning : null,
        accPnlIda1V5Benchmark: hasIda1V5BenchmarkValue ? ida1V5BenchmarkRunning : null,
        accPnlGooseV5: hasGooseV5Value ? bisonV5Running : null,
        accPnlGooseV3: hasGooseV3Value ? bisonV3Running : null,
        accPnlFix2Close: Number.isFinite(Number(row.acc_pnl_fix2_close))
          ? Number(row.acc_pnl_fix2_close)
          : null,
      };
    });
  const hasGooseV3Series = chartData.some((row) => row.accPnlGooseV3 !== null && row.accPnlGooseV3 !== undefined);
  const hasGooseV5Series = chartData.some((row) => row.accPnlGooseV5 !== null && row.accPnlGooseV5 !== undefined);
  const hasIda1V5BenchmarkSeries = chartData.some((row) => row.accPnlIda1V5Benchmark !== null && row.accPnlIda1V5Benchmark !== undefined);

  if (chartData.length === 0) {
    return null;
  }

  return (
    <div className={`${chartColors.cardBg} ${chartColors.cardBorder} rounded-xl shadow-sm p-6`}>
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className={`w-5 h-5 ${darkMode ? 'text-slate-100' : 'text-gray-700'}`} />
          <h2 className={`text-2xl font-bold ${chartColors.cardText}`}>{chartCopy.title}</h2>
        </div>
        <div className={`text-sm mb-4 ${chartColors.mutedText}`}>
          {chartCopy.body}
        </div>
        <div className={`mb-4 flex flex-wrap gap-3 rounded-xl px-4 py-3 text-xs font-medium ${chartColors.legendBg}`}>
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-black/10"
              style={{ backgroundColor: chartColors.liveProvisional }}
            />
            <span className={chartColors.cardText}>{chartCopy.currentResult}</span>
          </div>
          {hasIda1V5BenchmarkSeries && (
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-black/10"
                style={{ backgroundColor: chartColors.ida1Benchmark }}
              />
              <span className={chartColors.cardText}>{chartCopy.mooseIda1Stop}</span>
            </div>
          )}
          {hasGooseV5Series && (
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-black/10"
                style={{ backgroundColor: chartColors.bisonV5 }}
              />
              <span className={chartColors.cardText}>Goose v5</span>
            </div>
          )}
          {hasGooseV3Series && (
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-black/10"
                style={{ backgroundColor: chartColors.bisonV3 }}
              />
              <span className={chartColors.cardText}>Goose v3</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-black/10"
              style={{ backgroundColor: chartColors.fix2Close }}
            />
            <span className={chartColors.cardText}>{chartCopy.fix2StopBenchmark}</span>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid stroke={chartColors.grid} strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tick={{ fill: chartColors.axis, fontSize: 12 }}
              axisLine={{ stroke: chartColors.grid }}
              tickLine={{ stroke: chartColors.grid }}
            />
            <YAxis tickFormatter={(value) => {
              const numeric = Number(value || 0);
              const abs = Math.abs(numeric);
              if (abs >= 1_000_000) return `${(numeric / 1_000_000).toFixed(1)}M`;
              if (abs >= 1_000) return `${(numeric / 1_000).toFixed(0)}k`;
              return numeric.toFixed(0);
            }}
              tick={{ fill: chartColors.axis, fontSize: 12 }}
              axisLine={{ stroke: chartColors.grid }}
              tickLine={{ stroke: chartColors.grid }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: chartColors.tooltipBg,
                borderColor: chartColors.tooltipBorder,
                borderRadius: '14px',
                color: chartColors.tooltipText,
              }}
              itemStyle={{ color: chartColors.tooltipText }}
              labelStyle={{ color: chartColors.tooltipText, fontWeight: 600 }}
              formatter={(value, name) => [
                `${formatNumber(value)} PLN`,
                name === 'accPnlFix2Close'
                  ? chartCopy.tooltipFix2Stop
                  : name === 'accPnlGooseV3'
                    ? chartCopy.tooltipGooseV3
                  : name === 'accPnlGooseV5'
                  ? chartCopy.tooltipGooseV5
                  : name === 'accPnlIda1V5Benchmark'
                    ? chartCopy.tooltipGooseIda1
                    : chartCopy.tooltipCurrent,
              ]}
            />
            <ReferenceLine y={0} stroke={chartColors.zeroLine} strokeDasharray="4 4" />
            <Line
              type="monotone"
              dataKey="accPnl"
              stroke={chartColors.liveProvisional}
              strokeWidth={3}
              dot={{ r: 2, fill: chartColors.liveProvisional, stroke: chartColors.liveProvisional }}
              activeDot={{ r: 5, fill: chartColors.liveProvisional, stroke: chartColors.liveProvisional }}
            />
            {hasIda1V5BenchmarkSeries && (
              <Line
                type="monotone"
                dataKey="accPnlIda1V5Benchmark"
                stroke={chartColors.ida1Benchmark}
                strokeWidth={3}
                dot={{ r: 2, fill: chartColors.ida1Benchmark, stroke: chartColors.ida1Benchmark }}
                activeDot={{ r: 5, fill: chartColors.ida1Benchmark, stroke: chartColors.ida1Benchmark }}
              />
            )}
            {hasGooseV5Series && (
              <Line
                type="monotone"
                dataKey="accPnlGooseV5"
                stroke={chartColors.bisonV5}
                strokeWidth={3}
                dot={{ r: 2, fill: chartColors.bisonV5, stroke: chartColors.bisonV5 }}
                activeDot={{ r: 5, fill: chartColors.bisonV5, stroke: chartColors.bisonV5 }}
              />
            )}
            {hasGooseV3Series && (
              <Line
                type="monotone"
                dataKey="accPnlGooseV3"
                stroke={chartColors.bisonV3}
                strokeWidth={3}
                dot={{ r: 2, fill: chartColors.bisonV3, stroke: chartColors.bisonV3 }}
                activeDot={{ r: 5, fill: chartColors.bisonV3, stroke: chartColors.bisonV3 }}
              />
            )}
            <Line
              type="monotone"
              dataKey="accPnlFix2Close"
              stroke={chartColors.fix2Close}
              strokeWidth={3}
              dot={{ r: 2, fill: chartColors.fix2Close, stroke: chartColors.fix2Close }}
              activeDot={{ r: 5, fill: chartColors.fix2Close, stroke: chartColors.fix2Close }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const HOW_IT_WORKS_COPY = {
  en: {
    headerTitle: 'How It Works',
    headerSubtitle: 'A business-readable guide to Simulation v5: what belongs in Predictions, Data, Audit, Risk management, and Orders; how the day path is built; what morning Goose risk management, Fix I BUY risk controls, BASE carry, and spike risk mean; and how to read the result.',
    languageLabel: 'Language',
    quickReadTitle: 'Read this first',
    quickReadCards: [
      {
        label: 'Execution order',
        body: 'A delivery day moves stage by stage: BASE -> Fix I -> Fix II -> IDA1 -> IDA2. The full path and order-to-calculation checks live in Audit, while Let’s trade keeps only a compact path popover.',
      },
      {
        label: 'View split',
        body: 'Predictions explain model output, Data shows the input dataset, Audit explains execution consistency, and Orders shows the Noble/BOŚ baskets to submit.',
      },
      {
        label: 'Risk management has layers',
        body: 'Morning Goose risk management is the risk context for the BASE decision and Fix I baskets. It can submit an opposite Fix I hedge, block or cap a normal Fix I BUY before submission, or mark spike risk without submitting a hedge.',
      },
      {
        label: 'Goose risk context',
        body: 'Before BASE, the page shows Goose risk-management context only. It highlights weak or spiky hours and risk charges, but it does not write a BASE decision or submit a BASE order.',
      },
      {
        label: 'Official Goose',
        body: 'The official Goose overlay does not rebuild the whole day. It takes the Goose v5 path, evaluates the late residual, and decides whether it is worth carrying to IDA2 in overlay mode.',
      },
      {
        label: 'Core preset',
        body: 'The official preset keeps position limits, margins, and carry thresholds stable, so comparisons reflect model and policy changes rather than accidental parameter changes.',
      },
    ],
    flowTitle: 'One delivery day, staged left to right',
    flowCaption: 'The trader first reads the morning Goose risk-management result, then the auction path moves stage by stage. Each stage passes forward only the unclosed residual. If late data is missing, the day stays partial or fallback instead of pretending to be final IDA2.',
    flowSteps: [
      { label: 'BASE', note: 'day-wide setup' },
      { label: 'Fix I', note: 'first fixing leg' },
      { label: 'Fix II', note: 'v5 forecast path' },
      { label: 'IDA1', note: 'intermediate close' },
      { label: 'IDA2', note: 'Goose IDA2 Final' },
    ],
    overview: {
      title: 'How this simulation works',
      paragraphs: [
        'Simulation v5 is a **simulation-only shadow-trading monitor** for the `v5.0.0` stack. It rebuilds one staged execution path from `predictions_sim` rows, shared Goose runtime logic, and simulation-side settle history instead of reading the legacy Results ledger.',
        'The same delivery date is enriched during the day. The path starts from the stored Fix II plan, then incorporates IDA1 when the IDA1 prediction exists, and finally incorporates IDA2 when the IDA2 prediction and final settle inputs exist.',
        'Settle converts that staged path into realised business PnL. Earlier legal closes remain visible when later data are missing, so the page exposes partial and fallback states explicitly instead of fabricating a full late close.',
        'This page documents the current simulation contract: which models generated the path, which runtime preset sized it, which stage settled it, and which benchmark it should be compared against.',
      ],
      diagram: [
        { label: 'Predict rows', note: 'stored plan and forecasts' },
        { label: 'Settle task', note: 'market close and fallback guards' },
        { label: 'UI tabs', note: 'Predictions, Data, Audit, Orders' },
      ],
      examples: [
        'If Fix II exists but IDA1 is missing, the day can still show a Fix II partial result.',
        'If IDA2 prediction and IDA2 settlement prices exist, final settle can create Goose IDA2 Final. IDA1 is normally part of the staged path, but the explicit no-IDA1 final path can be used when IDA1 is missing.',
      ],
    },
    pipelineReason: {
      title: 'Why this is a pipeline, not an on-the-fly calculation',
      bullets: [
        'Predict stages persist a dated snapshot of model outputs, quantiles, staged execution rows, selected plan rows, and validation metadata. Settle consumes that snapshot, so the page explains the same artefact that AWS settled instead of silently rebuilding a fresh one during every refresh.',
        'The pipeline gives each delivery date an auditable trail: task family, image, task definition, timestamps, warnings, failures, and the stage that produced the visible business row.',
        'It also keeps the UI fast. Summary and Daily Results can read persisted rows cheaply while AWS handles large backfills, retries, and chunked refreshes in the background.',
        'This matters operationally because Fix II, IDA1, and IDA2 become available at different times. Persisted stages let the page expose the furthest legal close already known for that date instead of pretending the day is complete too early.',
      ],
      diagram: [
        { label: 'Predict', note: 'freeze model snapshot' },
        { label: 'Settle', note: 'realise with market data' },
        { label: 'Persist', note: 'audit, retry, compare' },
        { label: 'Render', note: 'fast UI reads' },
      ],
      examples: [
        'A large backfill can run as chunked AWS tasks without blocking the browser.',
        'If IDA2 is cancelled or missing, settle stores an earlier legal close instead of inventing Goose IDA2 Final.',
      ],
    },
    scheduler: {
      title: 'Simulation-only AWS cadence',
      intro: 'The pipeline view monitors training and prediction for Goose v3, Goose v5, IDA1, and IDA2. It keeps the focus on model readiness, published outputs, and the day radar used by this simulation.',
      bullets: [
        'All four models — Goose v3, Goose v5, IDA1 (ida1-v1.0.3), and IDA2 (ida2-v1.0.0) — are visible in the same train and predict monitor for easy readiness comparison on one delivery date.',
        '04:00 train-v500-sim-daily -> train-v500-sim: train the Goose v5 probabilistic simulation stack for the next delivery date.',
        '09:45 predict-v500-sim-daily -> predict-v500-sim: write the Goose v5 Fix II forecast, quantiles, and staged hourly execution plan.',
        '09:47 day-radar-v500-sim-daily -> day-radar-v500-sim: generate the day radar only if the v5 predict already exists.',
        '11:35 predict-v500-sim-ida1-daily -> predict-ida1-v103: refresh the same day with IDA1-enriched Goose v5 rows when IDA1 data is available.',
        '14:50 predict-v500-sim-ida2-daily -> predict-ida2-v100: refresh the staged Goose v5 path with IDA2 enrichment when the late forecast is available.',
        'IDA1 (train-ida1-v103) and IDA2 (train-ida2-v100) train on their own schedules independent of the Goose probabilistic train. Manual train triggers for all four models are available in the Pipeline section.',
      ],
    },
    baseSource: {
      title: 'Where BASE comes from',
      paragraphs: [
        'BASE has two separate meanings: the settlement price and the operating position. The `tge.base` price drives realised PnL. The hedge input only says what the trader actually did on BASE and which exposure now needs risk control.',
        'Simulation v5 never writes production `others.base_action`. The BASE execution input is saved to `predictions_sim.v5_base_execution_input`, a simulation-only layer used by hedge and replay.',
        'Saving BASE execution input can trigger an automatic Simulation v5 settle/replay for that delivery date. Until that AWS task finishes, Daily Results can show a pending BASE-settle badge and the previous PnL should be treated as stale.',
        'The default simulation BASE size is `50 MW`. If no explicit input or override exists, the runtime can show a fallback Buy, Sell, or Idle, but the trader hedge screen is primarily driven by the actual post-auction execution.',
        'The Goose risk-management panel is a radar, not an entry model. It shows Buy-side collapse risk, Sell-side spike risk, risk hours, and risk charges before the BASE price is known. The main decision stays conditional on the actual auction price and trader judgement.',
        'BASE hedge does not decide whether to enter BASE. It assumes BASE has already been traded and asks a narrower question: should some hours of that position be removed on Fix I?',
        'Risk is detected hourly from the forecast distribution: `q10`, `q50`, `q90`. `q50` shows the centre of the forecast, `q90` checks whether even the better case is still weak, and `q10` catches crash-tail risk.',
        'For Buy BASE, Goose risk management marks an hour as risky when the model sees a very low or negative-price regime: `q50 <= -50` and `q90 <= 25`, or the lower tail crashes with `q10 <= -200`. This mechanism evaluates hours `10-16`, where that risk is operationally most relevant for this layer.',
        'For Sell BASE, the risky case is the opposite: an expensive spike and forced buy-back above the BASE price. Spike Guard checks all hours, including evening peaks. If hedge economics pass, it can submit a Buy hedge on Fix I; if not, the day shows spike risk without hedge and no order is counted.',
        'For Buy BASE the hedge is Sell on Fix I. For Sell BASE the hedge is Buy on Fix I.',
        'A `0 MWh` hedge does not automatically mean BASE was Idle. It can mean the position exists, but Goose risk management did not find risky executable hedge hours, spike risk was detected without economic hedge, or Goose Fix I risk control acted as a block/cap rather than a hedge order.',
        'Goose risk (hard mode) is the negative-price mechanism. In hard mode it can reduce Buy BASE from `50 MW` to `10 MW`, `20 MW`, or `0 MW`. It protects downside, but can also remove upside if the market rebounds.',
        'Goose risk soft mode exists to avoid killing the full potential of a good BASE day. It detects the same risky hours, but keeps more of the position, usually `35/45 MW`, so the Fix I hedge is smaller.',
        'Goose risk defaults to hybrid mode. Hard remains active when there are fewer than `3` full-block risky hours, or when BASE price is in the neutral `100-180 PLN/MWh` band. Soft is used only for Buy BASE when hard would fully block at least `3` hours and BASE price is outside that band: `<= 100` or `>= 180 PLN/MWh`.',
        'This switch affects only risk-management hedge sizing. Other protections, such as Fix I BUY control, Fix II falling-knife stops, and IDA2 buy-carry blocking, stay on the harder risk logic.',
        'The hedge step creates a target profile after BASE execution is known. Example: Buy BASE `50 MW`, guard keeps only `10 MW` in hour 12, so the remaining `40 MW` becomes a Sell hedge on Fix I. If the target is `0 MW`, the hedge is `50 MW`.',
        'Fix I BUY Guard is different. It is an active Goose risk intervention on normal Fix I BUY orders: it can block the order entirely (`0 MW max`) or cap it before submission, for example to `20 MW`. It is not a hedge order, does not enter hedge fill rate, and has no hedge PnL.',
        'Older settled rows can show Fix I BUY Guard hours without original volume fields. In that case the UI should say `volume data missing`; only rows settled with the newer payload can show original, post-guard, and affected volume.',
        'BASE carry guard is also separate from hedge. It can force some BASE residual to close on Fix II or prevent carry into IDA, but its value belongs to the BASE/settle path, not to Fix I hedge PnL.',
        'BASE is still a day-wide block, so the hourly target profile is not directly tradable on the BASE auction. The executable version is: trade full BASE first, then use opposite Fix I hedge for the hours the guard wants to reduce.',
        '`Profile before hedge` is the target profile before hedge cost. `Executable BASE PnL` is the tradable view: `Profile before hedge + Fix I hedge PnL`.',
        'The settled BASE replay table is an audit view after market close. It can differ from the 09:45 hedge forecast because it uses realised prices and settle rows.',
        'Skip BASE disables only the BASE leg for that run. Fix I / Fix II / IDA1 / IDA2 can still trade independently if the probabilistic path has executable residual.',
      ],
      diagram: [
        { label: 'Actual BASE', note: 'Buy/Sell/Idle, MW, price' },
        { label: 'q10/q50/q90', note: 'hourly risk picture' },
        { label: 'Goose risk mode', note: 'how much MW to keep' },
        { label: 'Fix I hedge', note: 'opposite order' },
        { label: 'Fix I BUY Guard', note: 'block/cap, not hedge' },
        { label: 'Executable PnL', note: 'profile plus hedge' },
      ],
      examples: [
        'Buy BASE 50 MW and no risky hours: hedge is 0 MWh, but BASE still exists.',
        'Buy BASE 50 MW and Goose hard keeps 10 MW in hour 12: the system creates a 40 MW Sell hedge on Fix I.',
        'Buy BASE 50 MW and Goose soft keeps 35 MW: hedge is smaller because only 15 MW of risk is removed.',
        'Normal Fix I BUY 60 MW and Fix I BUY Guard cap 20 MW: the order sent after guard is 20 MW and the affected volume is 40 MW after the new settle payload.',
        'Spike risk without hedge can appear in the expanded day details, but it should not increase hedge-order days, fill rate, or hedge PnL.',
        'If the hedge panel says `NOT READY`, do not treat replay rows as a 09:45 hedge instruction.',
        'PnL example: `Profile before hedge = +130,593.98 PLN` and `Fix I hedge = -160,773.90 PLN` gives `Executable BASE PnL = -30,179.92 PLN`.',
      ],
    },
    v5Model: {
      title: 'V5 / Fix II model',
      bullets: [
        'The V5 probabilistic model is the primary Fix II forecasting stack for this simulation. It produces the hourly quantile envelope `Q10 / Q30 / Q50 / Q70 / Q90` and the staged Fix II execution path that the rest of the day inherits.',
        'The field shown as `Q50 / Fix II` is the model median forecast used by the runtime when it builds the Fix II leg. It is a forecast, not the realised market clearing price.',
        'The lower and upper quantiles shape the order ladder, directional bias, and residual-close logic. The Predictions tab shows the forecast envelope; the Audit tab shows the selected execution path and order consistency.',
        'Pipeline train cards expose the stored training snapshot for that model run, including stack mode and quality metrics such as `P50 MAE`, `Coverage80`, `Pinball avg`, and global scale.',
        'Fix I market price can arrive after the initial V5 prediction already exists. The Fix I execution task refreshes the fill/audit view: side, rows, volume, and average price. Once the real Fix I price is present, the UI can also show a provisional BASE mark to Fix I; final fixing PnL still enters the business result through Fix II partial settle or a later legal close.',
        'Status language follows pipeline semantics: `ready` means usable output exists, `running` means an active task is in progress, `pending` means no usable output has been observed yet, `failed` means an attempt ended with an error, and `not run yet` means no attempt exists.',
      ],
      diagram: [
        { label: 'Q10 / Q30', note: 'lower buy-side ladder' },
        { label: 'Q50', note: 'median path shown as forecast' },
        { label: 'Q70 / Q90', note: 'upper sell-side ladder' },
      ],
    },
    ida2Model: {
      title: 'IDA2 forecast model',
      bullets: [
        'The IDA2 model is a forecast model. It produces `q10 / q30 / q50 / q70 / q90` for the late IDA2 delivery window and is refreshed around `14:50`.',
        'IDA2 does not choose the close path on its own. The Goose overlay consumes the IDA2 forecast and decides whether eligible residual should be carried into the final IDA2 leg or blocked earlier.',
        'This split is visible in the benchmarks. `Fix II Stop Benchmark` is the simulation-side counterfactual that force-closes the later residual on Fix II. `Fix II Stop (Goose live stop)` is the legacy Goose-comparable `Base + Fixing` result before IDA1. `Goose v5 IDA1` is the Goose v5 IDA1 replay reference. `Goose IDA1 Stop` isolates the same simulation path stopped before the final Goose decision. `Goose IDA2 Final` is the fully settled version of that path when IDA2 is available.',
        'A day becomes `Goose IDA2 Final` only when the IDA2 prediction and IDA2 settlement prices exist and the final settle stage is allowed to run. IDA1 usually enriches the same staged path, but if IDA1 is missing the runtime can use the explicit no-IDA1 final path. If late inputs are missing or an auction was cancelled, the page exposes a pending, failed, blocked, or fallback state instead of fabricating a full IDA2 result.',
      ],
      diagram: [
        { label: 'Goose IDA1 Stop', note: 'stop the path here' },
        { label: 'IDA2 forecast', note: 'late signal at 14:50' },
        { label: 'Goose IDA2 Final', note: 'use IDA2 only when inputs exist' },
      ],
    },
    mooseModel: {
      title: 'Goose overlay policy',
      bullets: [
        'Goose is the simulation-side IDA2 risk overlay. It consumes residual candidates produced by the Goose live path and decides whether that residual should stay on the IDA1 stop path or be carried into the final IDA2 leg.',
        'Three Goose modes are exposed in Simulation v5. `overlay_handoff` is the official mode. `overlay_only` is a diagnostic mode that keeps the Goose v5 path and removes handoff behaviour. `full_strategy_experimental` is a research mode that lets Goose intervene earlier in the path and should not be treated as the official benchmark.',
        'In official `overlay_handoff`, Goose keeps the Goose v5 path through the early stages and applies the late residual policy with explicit carry branches. The policy does not touch production Goose tabs or write production orders.',
        'The active sell-to-IDA2 branch evaluates `action_to_close = sell` on `fix_i` or `bucket` residual in hour group `18_23`. It requires a signed `IDA2 q50 vs Fix II` edge greater than or equal to `ida2_min_edge_per_mwh` (default `20 PLN/MWh`) and an `IDA2 q50 vs IDA1` score greater than or equal to `-150 PLN/MWh`.',
        'The active morning sell branch evaluates `action_to_close = sell` on `bucket` residual in hour group `6_11`. It carries when the base rule passes (`Fix II -> IDA2` score >= `10`, `IDA2 vs IDA1` score >= `-150`) or when one of the strong overrides passes (`Fix II -> IDA2` score >= `35`, or score >= `30` with `IDA2 vs IDA1` >= `-190`).',
        'The active midday buy branch evaluates `action_to_close = buy` in hour group `12_17` and carries when the `IDA2 q50 vs IDA1` score is greater than or equal to `-39.245086 PLN/MWh`.',
        'BASE candidates are blocked inside Goose carry. BASE has its own carry runtime and does not enter the Goose IDA2 carry branches as a normal residual candidate.',
        'In `full_strategy_experimental`, Goose can also reroute part of the earlier Fix II residual path, including morning sell-to-IDA1 logic. That mode is useful for diagnostics and research, not as the canonical baseline.',
      ],
      examples: [
        'Evening sell residual, component `bucket`, hour `19`, `IDA2 q50 vs Fix II = +24`, `IDA2 vs IDA1 = -12`: Goose carries the candidate to IDA2.',
        'Morning sell residual, component `bucket`, hour `07`, `Fix II -> IDA2 = +34`, `IDA2 vs IDA1 = -170`: the strong morning override still carries the candidate to IDA2.',
        'Midday buy residual, hour `13`, `IDA2 vs IDA1 = -55`: Goose blocks the carry and the path stays closer to Goose IDA1 Stop.',
      ],
    },
    bisonVsMoose: {
    title: 'Decision models comparison',
      bullets: [
        '**Goose** is the production order-building runtime. It reads probabilistic quantiles, Fix I / Fix II inputs, BASE context, margins, bucket sizes, average-volume limits, and position caps, then builds the staged execution ladder through Fix I, Fix II, and IDA1.',
        'Once the preset and market inputs are fixed, the Goose replay is largely deterministic. It answers a sizing and execution question: given this ladder and these market prices, how much residual closes at each auction and how much remains open?',
        '**Goose v3 IDA1** and **Goose v5 IDA1** share the same Goose mechanics and the same form preset. The only difference is the probabilistic model version: `v3.1.0` versus `v5.0.0`.',
        'That means the `Goose v3 -> Goose v5` delta in the parity report is a pure model delta. Margin, filters, buckets, caps, ridge, and BASE source order remain unchanged across those two replay cards.',
        '**Goose** is a simulation-only policy layer that sits after the Goose live path. In official mode it does not rebuild the early ladder. It evaluates residual candidates and decides whether the late close remains at IDA1 or extends into IDA2.',
        'A Goose candidate includes at least: date, hour, quarter, component, side to close, entry price, residual size, Fix II context, IDA1 context, and IDA2 context. Goose converts those inputs into signed scores such as `IDA2 q50 vs Fix II` and `IDA2 q50 vs IDA1`, then applies branch-specific component and hour-group guards.',
        'The practical difference is scope. Goose controls the whole path through IDA1. Official Goose controls only the late residual decision layered on top of that path. Experimental Goose modes can retune earlier decisions, but those modes are not the official benchmark.',
      ],
      diagram: [
        { label: 'Goose live ladder', note: 'quantiles, buckets, margins, caps' },
        { label: 'Residual candidate', note: 'hour, side, entry, volume' },
        { label: 'Goose decision', note: 'carry to IDA2 or block' },
        { label: 'Goose IDA2 Final', note: 'compare to Goose v5 IDA1 overlay baseline' },
      ],
      keyValues: [
        { label: 'Goose decision unit', value: 'order ladder and staged close execution through Fix I, Fix II, and IDA1' },
        { label: 'Goose v3 reference preset', value: 'probabilistic model `v3.1.0`; Fix I `v2.0.0`; Fix II `v1.0.0-fix2`; IDA1 `ida1-v1.0.3`; `ida1_margin = 1000`; `prediction_filter_margin = 10`; `preds_diff_cutoff = 10`; buckets `20/40/60`; `max_avg_volume_pct = 30`; `max_position = 60`; ridge disabled' },
        { label: 'Goose v5 reference preset', value: 'probabilistic model `v5.0.0`; all remaining Goose execution parameters identical to Goose v3 IDA1' },
        { label: 'Official Goose preset', value: '`overlay_handoff`; same Goose v5 path through IDA1; `ida1_margin = 1000`; `prediction_filter_margin = 10`; `preds_diff_cutoff = 10`; `max_avg_volume_pct = 30`; `max_position = 60`; `base_carry_mode = strict_v4`; `ida2_min_edge_per_mwh = 20`' },
        { label: 'Diagnostic Goose modes', value: '`overlay_only` keeps the Goose v5 path and removes handoff behaviour. `full_strategy_experimental` allows earlier Goose interventions and is research-only.' },
        { label: 'strict_v4 carry', value: 'BASE residual carries only when the strict edge, width, and direction rules pass. Otherwise BASE is downgraded to an earlier legal close.' },
        { label: 'Goose decision unit', value: 'late residual candidate after Fix II / IDA1, evaluated for carry to IDA2 or block at the same late stage' },
        { label: 'Goose signals', value: 'signed forecast spread, side to close, hour group, residual size, component guard, and active branch threshold' },
        { label: 'Goose scope', value: 'Simulation v5 only; production Goose order tabs and production order flow remain unchanged' },
      ],
      examples: [
        'Goose example: residual survives Fix II; the IDA1 close ladder, margin, liquidity cap, and realised market price decide how much closes on IDA1.',
        'Goose example: after the same Goose live path, an evening sell candidate from `fix_i` or `bucket` has enough signed edge to IDA2, so Goose carries it to the final IDA2 settle.',
        'Blocked example: Goose still builds the earlier path, but Goose rejects the late carry, so the result remains close to Goose IDA1 Stop.',
      ],
    },
    residualFlow: {
      title: 'How residual moves between auctions',
      bullets: [
        'The strategy is staged. Each auction consumes part of the open position, and only the residual that remains open and legal can move further down the path.',
        'BASE residual and probabilistic fixing residual are related but they are not the same book. The BASE carry runtime can therefore gate BASE differently from the probabilistic fixing residual on the same delivery date.',
        'An Idle BASE context does not stop the whole day automatically. If the probabilistic path still has executable residual or bucket flow, later auctions can remain active even with BASE at zero.',
        'IDA1 is the first downstream close after Fix II. If IDA1 inputs exist and the residual remains eligible, part of the day settles on IDA1 while the rest either stops earlier or remains eligible for the final IDA2 policy step.',
        'IDA2 is the last close in the staged path. It becomes the strategy result only when the IDA2 prediction and settlement inputs exist; IDA1 can either enrich the path earlier or be absent through the explicit no-IDA1 final path. If those late IDA2 inputs are missing, the page stays on a transparent Fix II or IDA1 partial/fallback state instead of inventing a final close.',
        'That is why benchmark cards are whole-path comparisons rather than separate ledgers. `Fix II Stop Benchmark` is the forced early stop on the simulation path, `Fix II Stop (Goose live stop)` is the legacy `Base + Fixing` benchmark, `Goose v5 IDA1` is the Goose v5 IDA1 replay reference, `Goose IDA1 Stop` is the intermediate stop, and `Goose IDA2 Final` is the fully settled final strategy basis.',
      ],
      diagram: [
        { label: 'Open position', note: 'after BASE and Fix I' },
        { label: 'Fix II close', note: 'uses what it can execute' },
        { label: 'Eligible residual', note: 'passes guards only' },
        { label: 'IDA1 / IDA2', note: 'late closes if data exist' },
      ],
    },
    volumeAllocation: {
      title: 'Strategy ladders and residual flow across auctions',
      bullets: [
        'Simulation v5 uses the shared Goose live ladder sizes: `bucket1 = 20 MW`, `bucket2 = 40 MW`, `bucket3 = 60 MW`. These are nominal order sizes. Final execution can be smaller after quarter matching, liquidity caps, and residual constraints are applied.',
        'Fix I orders are built from the probabilistic quantile stack. Buy orders start nearer the lower quantiles, sell orders nearer the upper quantiles, and a sufficiently large Fix I / Fix II forecast gap can shift the ladder by one quantile step.',
        'Fix II then works on the residual left by the earlier stages. It can reduce excess position, close remaining Fix I and BASE residual, and then place capped residual bucket orders.',
        'IDA1 inherits only the position that survived those earlier stages. The runtime therefore does not allocate the whole day across all auctions upfront. Each stage receives only what remains after the previous stage.',
        'Venue chips and venue totals represent gross market activity by auction. The headline Total Volume card stays on the live realised strategy path basis for the selected range. Those two views are intentionally not expected to sum one-to-one.',
        'The official Goose overlay keeps this staged execution structure intact. It does not replace the ladder. It changes which late residual survives long enough to enter the final IDA2 step.',
      ],
      diagram: [
        { label: 'Nominal ladder', note: '20 / 40 / 60 MW' },
        { label: 'Curve match', note: 'market accepts less or more by quarter' },
        { label: 'Position caps', note: 'runtime trims risk' },
        { label: 'Residual', note: 'only leftovers move forward' },
      ],
      examples: [
        'A 60 MW bucket can execute as 17.5 MWh after quarter-hour matching and caps.',
        'Gross venue volume can count activity on multiple legs; final strategy volume follows the realised path.',
      ],
    },
    runtimeParameters: {
      title: 'Active runtime limits and knobs',
      bullets: [
        'This section highlights parameters that materially change the decision, not every technical knob.',
        '`overlay_handoff` is the official Goose overlay mode. It keeps the earlier Goose v5 path and applies Goose only to the late IDA2 decision.',
        '`base_volume = 50 MW` is the default BASE size when no explicit simulation context exists.',
        '`preds_diff_cutoff = 10` still belongs to the legacy/runtime BASE fallback and parity presets. The trader-facing morning view is Goose risk context: it does not write a manual BASE decision.',
        'The hedge step starts after the BASE auction. It does not choose BASE; it reads the actual position and may submit opposite Fix I hedge orders for risky hours. Buy BASE uses Goose risk, while Sell BASE can use Spike Guard.',
        '`NEGATIVE_PRICE_BASE_SHRINK_MODE = hybrid_v1` chooses hard vs soft mode inside Goose risk. Hard is the default. Soft is used only for Buy BASE when hard would fully block at least 3 hours and BASE price is outside `100-180 PLN/MWh`.',
        'Goose risk soft affects only risk-management hedge sizing. Fix I BUY control, Fix II falling-knife stops, and IDA2 buy-carry blocks remain on the harder risk logic.',
        'Fix I BUY Guard is counted as guard intervention, not as hedge: it can block or cap normal Fix I BUY before order submission. The new settle payload stores original volume, cap, post-guard volume, and affected volume per hour.',
        '`prediction_filter_margin` is the trend/filter margin applied to the Goose live path. The official value is `10 PLN/MWh`.',
        '`max_avg_volume_pct` is the average-liquidity cap used when sizing IDA1 residual. The official value is `30%` of benchmark average volume for the quarter.',
        '`max_position` is the hard open-position cap after the earlier closes. The official value is `60 MW`.',
        '`ida1_margin` is the Goose IDA1 order margin. The official value for Goose v3, Goose v5, and official Goose is `1000 PLN/MWh`.',
        '`ida2_min_edge_per_mwh` is the official Goose sell-to-IDA2 floor for the evening branch. The official value is `20 PLN/MWh`, and the runtime uses it directly when evaluating that branch.',
        'The official simulation baseline also fixes `ida2_margin = 1000`, bucket sizes `20 / 40 / 60`, `base_carry_mode = strict_v4`, and ridge disabled in the Goose parity replays.',
      ],
      keyValues: [
        { label: 'Goose v3 and Goose v5 parity preset', value: '`ida1_margin = 1000`, `prediction_filter_margin = 10`, `preds_diff_cutoff = 10`, buckets `20/40/60`, `max_avg_volume_pct = 30`, `max_position = 60`, ridge disabled' },
        { label: 'Official Goose overlay + handoff preset', value: '`overlay_handoff`, `ida1_margin = 1000`, `prediction_filter_margin = 10`, `preds_diff_cutoff = 10`, `max_avg_volume_pct = 30`, `max_position = 60`, `base_carry_mode = strict_v4`, `ida2_min_edge_per_mwh = 20`' },
        { label: 'Diagnostic full-strategy preset', value: '`full_strategy_experimental`; simulation-only research mode with earlier Goose interventions before the final IDA2 step' },
        { label: 'Default BASE fallback size', value: '`50 MW`' },
        { label: 'Goose risk context', value: 'read-only risk context: Buy-side collapse hours, Sell-side spike hours, q50 neutral buffer, and risk charge; no write to `others.base_action` and no BASE order generation' },
        { label: 'Risk-management hedge', value: 'Goose risk: Buy BASE, hours 10-16, q50 <= -50 and q90 <= 25 or q10 <= -200. Spike Guard: Sell BASE, all hours, high q50/q90 premium to daily q50. Hedge order = full block MW - target MW only when an order is actually submitted.' },
        { label: 'Fix I BUY Guard', value: 'Goose risk intervention on normal Fix I BUY: block to 0 MW or cap to the configured max before submission. Count as guard intervention, not hedge order/fill/PnL.' },
        { label: 'Spike risk without hedge', value: 'Risk signal only. Show in expanded day/risk-management audit context, but do not count as hedge order, fill, or PnL.' },
        { label: 'BASE carry guard', value: 'Can force BASE residual to Fix II or block carry to IDA. Its value is inside the BASE/settle path, outside Fix I hedge PnL.' },
        { label: 'Falling-knife stops', value: 'Fix II hard stop if long/buy falls > 200 PLN/MWh from entry; soft stop if drop > 75 and IDA1 q50 is not better than Fix II by 25 PLN/MWh' },
        { label: 'IDA2 sell branch floor', value: '`ida2_min_edge_per_mwh = 20 PLN/MWh` for the evening sell carry branch in official mode' },
      ],
    },
    executionMonitor: {
      title: 'Order and execution audit',
      bullets: [
        'The Audit tab is the canonical place for staged path and order consistency. It compares order preview, clearing prices, staged execution components, and the calculation row for every hour and quarter.',
        'This audit is not a second pricing model and not a prerequisite for Let’s trade. It explains why a row is executed, preview-only, calculation-only, or mismatched.',
        'Guard and hedge still use BASE execution input plus forecasts. The audit explains submitted, executed, blocked, capped, skipped, and calculation rows, but it should not be read as a new trading instruction.',
      ],
      diagram: [
        { label: 'Order preview', note: 'Noble / BOŚ basket rows' },
        { label: 'Execution', note: 'market price and expected fill' },
        { label: 'Calculation', note: 'component volume and PnL' },
      ],
    },
    status: {
      title: 'Current status and safeguards',
      bullets: [
        'A live day can remain intermediate for many hours. Fix I price or later enrichment legs can still be pending, so daily PnL remains provisional until the relevant market stage is complete.',
        '`Partial` means a correct earlier close has already been saved while later inputs are still missing. `Fallback` means the path has been forced to the furthest earlier legal close because a later auction or input is unavailable. `Blocked` means a prerequisite is missing, so the runtime is not allowed to invent a business result.',
        'Fix II partial settle can run on its own. IDA1 partial settle requires the IDA1 prediction. Final Goose IDA2 settle requires the IDA2 prediction and IDA2 settlement prices; if IDA1 is missing, the runtime can save the explicit `settled_ida2_no_ida1` path instead of blocking the whole day.',
        'Failed or blocked attempts remain pipeline facts. They belong in pipeline history and warning messages. They do not overwrite an already valid business result.',
        'The page therefore separates pipeline truth from business truth: tasks can fail, retry, and later succeed, while the visible daily result continues to point to the latest valid settled row.',
      ],
      keyValues: [
        { label: 'Fix II partial', value: 'requires the Fix II path' },
        { label: 'IDA1 partial', value: 'requires the IDA1 prediction' },
        { label: 'Goose IDA2 Final', value: 'requires IDA2 prediction and IDA2 settlement prices; IDA1 can be absent through the explicit `IDA2-no-IDA1` settle path' },
        { label: 'Failed task', value: 'pipeline fact, not a replacement for business PnL' },
      ],
    },
    numbers: {
      title: 'How to read the numbers',
      bullets: [
        'Base PnL is the contribution of the BASE leg before the fixing flow starts. It should be read separately from the probabilistic fixing flow because the BASE carry runtime can use a different carry policy from the probabilistic residual on the same date.',
        'Fix I / Fix II leg PnL is the combined contribution of the early fixing legs. `IDA1 leg PnL` and `IDA2 leg PnL` are only later residual-close components. They are not the full business result of the whole IDA1 or IDA2 path.',
        '`Fix I execution` means fill, not final settle. Once Fix I prices arrive, the UI can show which Fix I ladder rows traded and a provisional BASE mark to the realised Fix I price. Fix I ladder PnL remains open until Fix II or a later auction closes the residual.',
        'A benchmark stop includes everything up to the stop point: `BASE + Fix I/Fix II + IDA1` for `Goose IDA1 Stop` and for Goose IDA1 Stop, `BASE + Fix I/Fix II` for `Fix II Stop (Goose live stop)`, and a simulation-side forced early close for `Fix II Stop Benchmark`. That is why `IDA1 leg` can be negative while the Goose IDA1 Stop benchmark remains positive overall.',
        'Component values are not portable across paths. Goose IDA1, Goose IDA1 Stop, and Goose IDA2 Final can use the same market prices but distribute PnL differently because they create different residual after BASE and Fix II.',
        '`Live provisional total incl. partial settle` is the operational headline. It rolls each realised day to the furthest legal close already available, so it includes Fix II partial and IDA1 fallback days instead of waiting for final IDA2.',
        '`Goose IDA2 Final` is the clean settled benchmark. It includes only days that have already reached the final IDA2 basis, which is why it can differ from the live provisional total during the same session.',
        'Benchmark cards should be read as whole-path comparisons rather than as separate ledgers. `Gross delta` compares raw PnL, `after fees delta` compares net business result, and `fees` shows which path paid more or less transaction cost.',
        'The parity report is a diagnostic view. `Goose v3 IDA1` and `Goose v5 IDA1` are standalone Goose replays with explicit presets. `Goose IDA2 Final` is either the persisted settled path or the modified in-memory path when modified simulation is active.',
        'Read parity deltas in order: `Goose v3 -> Goose v5` is the model delta, `Goose v5 -> official Goose` is the overlay delta, and `Goose IDA1 Stop -> Goose IDA2 Final` is the pure late IDA2 policy delta.',
        'Summary prefers a persisted settle row for any day with `settled*` status. Live computation is shown while the day is still pending. If those values disagree for a settled day, that is a consistency issue, not a normal refresh delay.',
        'Fees are recomputed from gross sell value and total MWh. Tiny rounding gaps up to `0.01 PLN` are expected. Larger gaps usually mean a path or venue-inclusion difference, not a different fee rate.',
        '`predictions_sim.*` stores the staged plan, prediction history, simulation close history, manual simulation input, and recalc status. `others.base_action_override` stores explicit simulation overrides. Production `others.base_action` is not written by Simulation v5.',
        '`Final MAE on settled prices` compares stored `q50` forecasts with the realised settled market prices for Fix II, IDA1, and IDA2. It is an ex-post forecast error metric, separate from train-time `P50 MAE` shown on the pipeline cards.',
        '`P50 MAE`, `Coverage80`, `Pinball avg`, and global scale belong to the saved train snapshot for that run. They describe model quality for that run, not realised trading performance over the selected range.',
        'Preview rows are intentionally conservative. Fix I order preview uses the same basket as Let’s trade: Goose live ladder, BASE close rows, and Simulation v5 risk-management hedge rows. Staged Fix I execution rows remain an audit/path signal, not the Noble basket source.',
        'Fix I BUY risk-control rows are not Noble basket rows. They are Goose risk-management interventions before submission: blocked/capped volume belongs in risk-intervention stats, not hedge fill rate or hedge PnL.',
        'Spike risk without hedge is also outside hedge stats. It is context explaining why a risk was detected but no Fix I hedge order was economical.',
        'BASE carry guard value is already inside the realised BASE/settle path. Do not add it again as hedge PnL.',
        '`Profile before hedge` is intentionally shown beside `Fix I hedge` because the first number is not executable alone. The executable number is the sum of both columns and is the BASE result carried into Daily Results.',
      ],
      examples: [
        'Live provisional total answers: what is the best currently realised view of this date range?',
        'Goose IDA2 Final answers: what did the strategy return on days that reached the final IDA2 basis?',
        'Goose v5 IDA1 answers: what does the standalone Goose IDA1 replay return under the documented v5 preset?',
        'Goose IDA1 Stop answers: what does the same simulation path return if it stops before the final IDA2 step?',
      ],
    },
    manualOverrides: {
      title: 'Admin BASE replay overrides',
      bullets: [
        'BASE overrides are a hidden admin/replay tool for pinning the BASE leg on one delivery date: direction (`Buy / Sell / Idle`), volume in MWh, and the audit BASE price in PLN/MWh. They are not the normal trader flow.',
        'The normal Let’s trade flow saves BASE execution to `predictions_sim.v5_base_execution_input`. Admin overrides are separate and write only `others.base_action_override`, never production `others.base_action`.',
        'Saving or deleting an override can trigger the same staged recalculation chain for that date (`predict -> Fix II partial settle -> IDA1 partial settle -> final IDA2 settle`).',
        'Summary and Daily Results then show a pending state until the refresh completes, instead of pretending the result is already final.',
        'Days with an active override receive a `Manual` badge in Daily Results, and the expanded day detail shows who saved the override, when, and with which note.',
        'Removing the override returns the date to automatic simulation mode. The stored `base_price` remains visible for auditability, but realised PnL still uses the market settlement price from `tge.base`.',
      ],
      diagram: [
        { label: 'Save override', note: 'pin action, size, note' },
        { label: 'Freeze day', note: 'automatic writers skip it' },
        { label: 'Recalc chain', note: 'predict, partial, final settle' },
      ],
    },
  },
  pl: {
    headerTitle: 'Jak to działa',
    headerSubtitle: 'Krótki opis biznesowej logiki Simulation v5: co należy do Predykcji, Danych, Audytu, Risk management i Orderów; jak powstaje ścieżka dnia; co znaczy poranny Goose risk management, kontrola BUY Fix I, BASE carry i spike risk; oraz jak czytać wynik.',
    languageLabel: 'Język',
    quickReadTitle: 'Najpierw to',
    quickReadCards: [
      {
        label: 'Kolejność wykonania',
        body: 'Dzień idzie etapami: BASE -> Fix I -> Fix II -> IDA1 -> IDA2. Pełna ścieżka i kontrola order -> kalkulacja są w Audycie, a Let’s trade pokazuje tylko kompaktowy dymek ścieżki.',
      },
      {
        label: 'Podział widoków',
        body: 'Predykcje wyjaśniają output modelu, Dane pokazują input dataset, Audyt tłumaczy spójność execution, a Ordery pokazują koszyki Noble/BOŚ do wysłania.',
      },
      {
        label: 'Risk management ma warstwy',
        body: 'Goose risk score/shift jest kontekstem modelowego wejścia w BASE i koszyka Fix I. Risk management może wystawić przeciwny hedge Fix I, zablokować albo ograniczyć zwykły BUY Fix I, albo oznaczyć spike risk bez orderu hedge.',
      },
      {
        label: 'Goose risk dla BASE',
        body: 'Przed BASE ekran pokazuje risk score/shift Goose. Ten sygnał koryguje q50 używane w modelowym warunku wejścia w BASE, ale UI nie zapisuje ręcznego override ani nie składa orderu BASE.',
      },
      {
        label: 'Oficjalny Goose',
        body: 'Oficjalny Goose overlay nie przebudowuje całego dnia. Bierze ścieżkę Goose v5, patrzy na późny residual i decyduje, czy warto donieść go do IDA2.',
      },
      {
        label: 'Główny preset',
        body: 'Oficjalny preset trzyma stałe limity pozycji, marginy i progi carry, żeby porównywać zmiany modelu i polityki, a nie przypadkowe zmiany parametrów.',
      },
    ],
    flowTitle: 'Jeden dzień delivery, etapy od lewej do prawej',
    flowCaption: 'Trader najpierw widzi Goose risk-management context dla BASE, a potem ścieżka aukcyjna idzie etapami. Każdy etap przekazuje dalej tylko niedomkniętą resztę. Jeśli brakuje późnych danych, dzień zostaje jako partial albo fallback zamiast udawać finalne IDA2.',
    flowSteps: [
      { label: 'BASE', note: 'setup dzienny' },
      { label: 'Fix I', note: 'pierwsza noga fixingu' },
      { label: 'Fix II', note: 'ścieżka forecastu v5' },
      { label: 'IDA1', note: 'close pośredni' },
      { label: 'IDA2', note: 'Goose IDA2 Final' },
    ],
    overview: {
      title: 'Jak działa ta symulacja',
      paragraphs: [
        'Simulation v5 pokazuje jedną operacyjną ścieżkę dnia: co model chciał zrobić, co dało się już rozliczyć i gdzie dzień powinien się zatrzymać, jeśli brakuje późnych danych.',
        'Ta sama data delivery dojrzewa w ciągu dnia. Najpierw mamy plan Fix II, potem dochodzi IDA1, a na końcu IDA2, jeśli forecast i dane rynkowe są już gotowe.',
        'Settle zamienia plan w biznesowy PnL. Jeśli późne dane nie istnieją, widzimy najdalszy legalny partial albo fallback, a nie sztucznie domknięty wynik.',
        'Ta sekcja pomaga czytać ekran: skąd bierze się BASE, kiedy działa hedge, co jest benchmarkiem, a co jest bieżącą ścieżką tradingową.',
      ],
      diagram: [
        { label: 'Predict rows', note: 'zapisany plan i forecasty' },
        { label: 'Settle task', note: 'market close i guardy fallback' },
        { label: 'Zakładki UI', note: 'Predykcje, Dane, Audyt, Ordery' },
      ],
      examples: [
        'Jeżeli istnieje Fix II, ale brakuje IDA1, dzień może pokazać częściowy wynik Fix II.',
        'Jeżeli istnieje predykcja IDA2 i ceny rozliczeniowe IDA2, final settle może utworzyć Goose IDA2 Final. IDA1 normalnie wzbogaca staged path, ale gdy go brakuje, runtime może użyć jawnej ścieżki finalnej bez IDA1.',
      ],
    },
    pipelineReason: {
      title: 'Dlaczego pipeline, a nie zwykłe liczenie w locie',
      bullets: [
        'Wejścia forecastu nie są tylko wzorem na statycznych danych. Etapy predict zapisują snapshot modelu: outputy, kwantyle, staged rows, wybrany plan i metadane walidacji. Settle czyta ten snapshot zamiast po cichu budować nowy przy każdym refreshu strony.',
        'Pipeline daje każdej dacie delivery audytowalny ślad: family taska, obraz, task definition, timestampe, warningi, błędy i etap, który wyprodukował widoczny biznesowy row.',
        'Pipeline utrzymuje też szybkie UI. Summary i Daily Results czytają persisted rows tanio, a AWS bierze na siebie duże backfille, retry i chunkowane refreshy w tle.',
        'To ma znaczenie operacyjne, bo Fix II, IDA1 i IDA2 pojawiają się o różnych godzinach. Persisted etapy pozwalają stronie pokazać najdalszy legalny close znany już dla tej daty zamiast udawać zbyt wcześnie, że dzień jest kompletny.',
      ],
      diagram: [
        { label: 'Predict', note: 'zamrożenie snapshotu' },
        { label: 'Settle', note: 'realizacja na danych rynku' },
        { label: 'Persist', note: 'audyt, retry, porównanie' },
        { label: 'Render', note: 'szybki odczyt UI' },
      ],
      examples: [
        'Odświeżenie zakresu od 2025 do dziś może iść jako chunkowane taski AWS bez blokowania przeglądarki.',
        'Jeśli IDA2 jest odwołane albo brakuje danych, pipeline zapisuje fallback IDA1 zamiast wymyślać finalny wynik IDA2.',
      ],
    },
    scheduler: {
      title: 'Kadencja AWS tylko dla symulacji',
      intro: 'Widok pipeline monitoruje trening i predykcję dla Goose v3, Goose v5, IDA1 i IDA2. Skupia się na gotowości modeli, opublikowanych outputach i radarze dnia używanym przez tę symulację.',
      bullets: [
        'Wszystkie cztery modele — Goose v3, Goose v5, IDA1 (ida1-v1.0.3) i IDA2 (ida2-v1.0.0) — są widoczne w tym samym monitorze train i predict, co ułatwia porównanie gotowości dla jednej daty delivery.',
        '04:00 train-v500-sim-daily -> train-v500-sim: trening probabilistycznego stacka Goose v5 dla kolejnej daty delivery.',
        '09:45 predict-v500-sim-daily -> predict-v500-sim: zapis forecastu Goose v5 dla Fix II, kwantyli oraz staged hourly execution plan.',
        '09:47 day-radar-v500-sim-daily -> day-radar-v500-sim: generuje radar dnia tylko wtedy, gdy predict v5 już istnieje.',
        '11:35 predict-v500-sim-ida1-daily -> predict-ida1-v103: odświeżenie tego samego dnia o rows Goose v5 wzbogacone o IDA1, jeśli dane IDA1 są dostępne.',
        '14:50 predict-v500-sim-ida2-daily -> predict-ida2-v100: odświeżenie staged path Goose v5 o enrichment IDA2, jeśli późna predykcja jest dostępna.',
        'IDA1 (train-ida1-v103) i IDA2 (train-ida2-v100) trenują według własnych harmonogramów, niezależnie od treningu probabilistycznego Goose. Ręczne wywołanie treningu dla wszystkich czterech modeli jest dostępne w sekcji Pipeline.',
      ],
    },
    baseSource: {
      title: 'Skąd pochodzi BASE',
      paragraphs: [
        'BASE trzeba rozdzielić na dwa tematy: cenę rozliczeniową i decyzję operacyjną. Cena z `tge.base` służy do PnL. Input z ekranu hedge mówi tylko, co trader realnie zrobił na BASE i jaką ekspozycję trzeba później kontrolować.',
        'Simulation v5 nie zapisuje produkcyjnego `others.base_action`. Input wykonania BASE trafia do `predictions_sim.v5_base_execution_input`, czyli do osobnej warstwy symulacyjnej używanej do hedge i replay.',
        'Zapis inputu wykonania BASE może uruchomić automatyczny settle/replay Simulation v5 dla tej daty delivery. Dopóki task AWS się nie zakończy, Daily Results może pokazywać badge przeliczania BASE, a poprzedni PnL trzeba traktować jako nieaktualny.',
        'Domyślny rozmiar BASE w symulacji to `50 MW`. Jeśli nie ma jawnego inputu albo override, runtime może pokazać fallback Buy, Sell albo Idle, ale traderowy ekran hedge pracuje przede wszystkim na realnym wykonaniu po aukcji BASE.',
        'Panel Goose risk management przed BASE pokazuje tylko kontekst ryzyka i wpływ shiftu na q50 używane przez Goose do Buy/Sell/Idle, ale UI nie zapisuje ręcznego override `others.base_action`.',
        'BASE hedge nie decyduje, czy wejść w BASE. Zakłada, że BASE jest już zrobiony, i odpowiada na inne pytanie: czy część godzin tej pozycji trzeba zdjąć na Fix I.',
        'Ryzyko wykrywamy godzinowo na podstawie rozkładu forecastu: `q10`, `q50`, `q90`. `q50` mówi, gdzie model widzi środek rynku, `q90` sprawdza, czy nawet lepszy scenariusz nadal nie daje dużej ulgi, a `q10` łapie ryzyko mocnego crashu.',
        'Goose ryzyko (tryb hard) na Buy BASE oznacza godzinę jako ryzykowną, gdy model widzi scenariusz bardzo niskich albo ujemnych cen: `q50 <= -50` i `q90 <= 25`, albo dolny ogon mocno się załamuje przez `q10 <= -200`. Ten mechanizm patrzy na godziny `10-16`, gdzie to ryzyko jest najbardziej operacyjne dla tej warstwy.',
        'Dla Sell BASE ryzykiem jest odwrotność: drogi spike i odkupienie pozycji wyżej niż cena BASE. Spike Guard sprawdza wszystkie godziny, także wieczorne peaki. Jeśli ekonomia hedge przechodzi, może wystawić Buy hedge na Fix I; jeśli nie, dzień pokazuje spike risk bez hedge i nie liczy orderu.',
        'Dla Buy BASE hedge idzie jako Sell na Fix I. Dla Sell BASE hedge idzie jako Buy na Fix I.',
        'Jeśli hedge wynosi `0 MWh`, to nie znaczy automatycznie, że BASE był Idle. Pozycja może istnieć, ale Goose risk management nie znalazł wykonalnych godzin hedge, spike risk został wykryty bez ekonomicznego hedge albo kontrola BUY Fix I zadziałała jako blokada/limit zamiast orderu hedge.',
        'Goose ryzyko hard to mechanizm ryzyka cen ujemnych. W trybie hard mocno zmniejsza ekspozycję Buy BASE: może zejść z `50 MW` do `10 MW`, `20 MW` albo nawet `0 MW`. Chroni downside, ale może też zabrać upside, jeśli rynek odbije.',
        'Goose ryzyko soft powstało po to, żeby nie zabijać całego potencjału dobrego dnia BASE. Rozpoznaje te same ryzykowne godziny, ale zostawia większą część pozycji, zwykle `35/45 MW`, więc hedge na Fix I jest mniejszy.',
        'Goose ryzyko domyślnie działa w hybrydzie. Hard zostaje, gdy ryzykownych pełnych godzin jest mniej niż `3`, albo gdy BASE price jest w neutralnym paśmie `100-180 PLN/MWh`. Soft włącza się dla Buy BASE dopiero wtedy, gdy hard wyciąłby co najmniej `3` pełne godziny i cena BASE jest poza neutralnym pasmem: `<= 100` albo `>= 180 PLN/MWh`.',
        'Ten przełącznik dotyczy tylko sizingu hedge z risk management. Pozostałe zabezpieczenia ścieżki, takie jak kontrola BUY Fix I, falling-knife stop na Fix II i blokada buy-carry do IDA2, nadal używają twardszej logiki ryzyka.',
        'Krok hedge po wykonaniu BASE tworzy target profile. Przykład: mamy Buy BASE `50 MW`, a guard zostawia w danej godzinie tylko `10 MW`. Różnica `40 MW` staje się hedgem Sell na Fix I. Jeśli guard zostawia `0 MW`, hedge wynosi `50 MW`.',
        'Fix I BUY Guard jest czymś innym. To aktywna interwencja Goose ryzyka na zwykłych orderach BUY Fix I: może całkowicie zablokować order (`0 MW max`) albo ograniczyć go przed wysłaniem, np. do `20 MW`. To nie jest order hedge, nie wchodzi do fill rate hedge i nie ma hedge PnL.',
        'Starsze settled rows mogą pokazywać godziny Fix I BUY Guard bez pól wolumenu original. Wtedy UI powinno mówić `brak danych wolumenu`; dopiero rows po nowym payloadzie settle pokażą original, wolumen po guardzie i affected volume.',
        'BASE carry guard jest też osobny od hedge. Może wymusić domknięcie części residuala BASE na Fix II albo zablokować carry do IDA, ale jego wartość jest w ścieżce BASE/settle, a nie w PnL hedge Fix I.',
        'BASE jest nadal blokiem dziennym, więc nie da się zagrać od razu godzinowego target profile na aukcji BASE. Wykonalna wersja jest prosta: najpierw pełny BASE, potem przeciwny hedge na Fix I dla godzin, które guard chce zmniejszyć.',
        '`Profile before hedge` pokazuje wartość docelowego profilu przed kosztem hedge. `Executable BASE PnL` to wynik wykonalny: `Profile before hedge + Fix I hedge PnL`. Dlatego bardzo dobry profil może stać się słabszy, jeśli wymagany hedge jest drogi.',
        'Tabela settled BASE replay jest widokiem audytowym po zamknięciu rynku. Może różnić się od decyzji hedge 09:45, bo używa już zrealizowanych cen i settle rows.',
        'Skip BASE wyłącza tylko nogę BASE w danym runie. Fix I / Fix II / IDA1 / IDA2 mogą nadal handlować niezależnie, jeśli ścieżka probabilistyczna ma wykonalny residual.',
      ],
      diagram: [
        { label: 'Realny BASE', note: 'Buy/Sell/Idle, MW, cena' },
        { label: 'q10/q50/q90', note: 'obraz ryzyka godzin' },
        { label: 'Tryb Goose ryzyka', note: 'ile MW zostawić' },
        { label: 'Fix I hedge', note: 'przeciwny order' },
        { label: 'Fix I BUY Guard', note: 'blokada/limit, nie hedge' },
        { label: 'PnL wykonalny', note: 'profil plus hedge' },
      ],
      examples: [
        'Buy BASE 50 MW i brak ryzykownych godzin: hedge wynosi 0 MWh, ale BASE nadal istnieje.',
        'Buy BASE 50 MW i Goose hard zostawia 10 MW w godzinie 12: system tworzy 40 MW Sell hedge na Fix I.',
        'Buy BASE 50 MW i Goose soft zostawia 35 MW: hedge jest mniejszy, bo zdejmujemy tylko 15 MW ryzyka.',
        'Zwykły BUY Fix I 60 MW i Fix I BUY Guard limit 20 MW: po guardzie wysyłany jest order 20 MW, a affected volume wynosi 40 MW po nowym payloadzie settle.',
        'Spike risk bez hedge może pojawić się po rozwinięciu dnia, ale nie powinien zwiększać dni z hedge order, fill rate ani hedge PnL.',
        'Jeśli panel pokazuje `NOT READY`, nie traktujemy replay rows jako instrukcji hedge na 09:45.',
        'Przykład PnL: `Profile before hedge = +130,593.98 PLN` i `Fix I hedge = -160,773.90 PLN` daje `Executable BASE PnL = -30,179.92 PLN`.',
      ],
    },
    v5Model: {
      title: 'Model V5 / Fix II',
      bullets: [
        'V5 to bazowy model probabilistyczny dla nogi Fix II. Produkuje godzinową drabinę oraz stos kwantyli **Q10 / Q30 / Q50 / Q70 / Q90**, od którego zaczyna się staged trading flow.',
        'Pole opisane jako Q50 / Fix II to mediana forecastu modelu używana w symulacji przy budowie ścieżki Fix II. To **forecast**, a nie zrealizowana cena rynkowa.',
        'Niższe i wyższe kwantyle są potrzebne do budowy order laddera i widoku ryzyka. Zakładka Predykcje pokazuje obwiednię forecastu, a zakładka Audyt pokazuje wybraną ścieżkę wykonania i spójność orderów.',
        'Karty train pokazują zapisany snapshot treningu dla tej daty, razem z trybem stacka i metrykami jakości takimi jak P50 MAE, Coverage80, Pinball i scale.',
        'Cena Fix I może pojawić się później niż pierwotny predict. Task Fix I execution odświeża wtedy widok fill/audit: stronę, wiersze, wolumen i średnią cenę. Gdy realny Fix I już jest, UI może też pokazać prowizoryczny mark BASE do Fix I; finalny PnL fixingu nadal pojawia się dopiero przez Fix II partial settle albo późniejszy legalny close.',
        'Statusy na tej stronie mają znaczenie pipelineowe: ready oznacza gotowy używalny wynik, running aktywne wykonanie, pending brak używalnego wyniku, failed próbę zakończoną błędem, a not run yet brak jakiejkolwiek próby.',
      ],
      diagram: [
        { label: 'Q10 / Q30', note: 'dolna drabina buy' },
        { label: 'Q50', note: 'mediana pokazywana jako forecast' },
        { label: 'Q70 / Q90', note: 'górna drabina sell' },
      ],
    },
    ida2Model: {
      title: 'Model forecastu IDA2',
      bullets: [
        'IDA2 jest modelem forecastowym. Produkuje `q10 / q30 / q50 / q70 / q90` dla późnego okna IDA2 i odświeża się około `14:50`.',
        'IDA2 nie wybiera ścieżki close samodzielnie. Goose overlay konsumuje forecast IDA2 i decyduje, czy kwalifikujący się residual należy przenieść do finalnej nogi IDA2, czy zablokować go wcześniej.',
        'Ten podział jest widoczny w benchmarkach. `Fix II Stop Benchmark` to kontrfaktyczne domknięcie późniejszego residuala już na Fix II po stronie symulacji. `Fix II Stop (Goose live stop)` to legacy wynik porównywalny z Goose liczony jako `Base + Fixing` przed IDA1. `Goose v5 IDA1` to standalone replay Goose v5 IDA1. `Goose IDA1 Stop` izoluje tę samą ścieżkę symulacji zatrzymaną przed finalną decyzją Goose. `Goose IDA2 Final` jest w pełni settled wersją tej ścieżki, gdy IDA2 jest dostępne.',
        'Dzień staje się `Goose IDA2 Final` tylko wtedy, gdy istnieje predykcja IDA2, ceny rozliczeniowe IDA2 i final settle może legalnie wystartować. IDA1 zwykle wzbogaca tę samą ścieżkę wcześniej, ale jeśli go brakuje, runtime może użyć jawnej ścieżki finalnej bez IDA1. Jeżeli późnych danych brakuje albo aukcja została odwołana, strona pokazuje pending, failed, blocked albo fallback zamiast udawać pełny close IDA2.',
      ],
      diagram: [
        { label: 'Goose IDA1 Stop', note: 'zatrzymaj ścieżkę tutaj' },
        { label: 'Forecast IDA2', note: 'późny sygnał o 14:50' },
        { label: 'Goose IDA2 Final', note: 'użyj IDA2 tylko gdy dane istnieją' },
      ],
    },
    mooseModel: {
      title: 'Polityka Goose',
      bullets: [
        'Goose jest simulation-side warstwą polityki IDA2. Konsumuje kandydatów residualu wyprodukowanych przez ścieżkę ukształtowaną jak Goose i decyduje, czy ten residual pozostaje na ścieżce IDA1 stop, czy przechodzi do finalnej nogi IDA2.',
        'W Simulation v5 są dostępne trzy tryby Goose. `overlay_handoff` jest trybem oficjalnym. `overlay_only` jest trybem diagnostycznym, który zachowuje ścieżkę Goose v5 i usuwa zachowanie handoff. `full_strategy_experimental` jest trybem badawczym, który pozwala Goose ingerować wcześniej w ścieżkę i nie powinien być czytany jako oficjalny benchmark.',
        'W oficjalnym `overlay_handoff` Goose zachowuje ścieżkę Goose v5 przez wcześniejsze etapy i stosuje późną politykę residuala przez jawne carry branches. Polityka nie dotyka produkcyjnych zakładek Goose i nie zapisuje produkcyjnych zleceń.',
        'Aktywny branch sell-to-IDA2 ocenia `action_to_close = sell` na residualu `fix_i` albo `bucket` w grupie godzin `18_23`. Wymaga signed edge `IDA2 q50 vs Fix II` większego lub równego `ida2_min_edge_per_mwh` (domyślnie `20 PLN/MWh`) oraz score `IDA2 q50 vs IDA1` większego lub równego `-150 PLN/MWh`.',
        'Aktywny poranny branch sell ocenia `action_to_close = sell` na residualu `bucket` w grupie godzin `6_11`. Carry przechodzi, gdy baza reguły jest spełniona (`Fix II -> IDA2` >= `10`, `IDA2 vs IDA1` >= `-150`) albo gdy przechodzi jedna z mocnych nadpisań (`Fix II -> IDA2` >= `35`, albo >= `30` przy `IDA2 vs IDA1` >= `-190`).',
        'Aktywny branch midday buy ocenia `action_to_close = buy` w grupie godzin `12_17` i przenosi residual, gdy score `IDA2 q50 vs IDA1` jest większy lub równy `-39.245086 PLN/MWh`.',
        'Kandydaci BASE są zablokowani wewnątrz carry Goose. BASE ma własny runtime carry i nie wchodzi do branchy Goose IDA2 jako zwykły kandydat residualu.',
        'W `full_strategy_experimental` Goose może również przekierowywać część wcześniejszej ścieżki residualu Fix II, w tym poranną logikę sell-to-IDA1. Ten tryb jest przydatny diagnostycznie i badawczo, nie jako kanoniczny baseline.',
      ],
      examples: [
        'Wieczorny residual sell, komponent `bucket`, godzina `19`, `IDA2 q50 vs Fix II = +24`, `IDA2 vs IDA1 = -12`: Goose przenosi kandydata do IDA2.',
        'Poranny residual sell, komponent `bucket`, godzina `07`, `Fix II -> IDA2 = +34`, `IDA2 vs IDA1 = -170`: mocne poranne override nadal przenosi kandydata do IDA2.',
        'Midday residual buy, godzina `13`, `IDA2 vs IDA1 = -55`: Goose blokuje carry i ścieżka pozostaje bliżej Goose IDA1 Stop.',
      ],
    },
    bisonVsMoose: {
      title: 'Modele decyzyjne: porównanie',
      bullets: [
        '**Goose** jest produkcyjnym runtime budowania zleceń. Czyta kwantyle probabilistyczne, wejścia Fix I / Fix II, kontekst BASE, marginesy, rozmiary bucketów, limity średniego wolumenu i capy pozycji, a potem buduje staged execution ladder przez Fix I, Fix II i IDA1.',
        'Gdy preset i dane rynkowe są ustalone, replay Goose jest w dużej mierze deterministyczny. Odpowiada na pytanie sizingowe i executionowe: mając tę drabinę i te ceny rynkowe, ile residualu zamyka się na każdej aukcji i ile pozostaje otwarte.',
        '**Goose v3 IDA1** i **Goose v5 IDA1** używają tej samej mechaniki Goose oraz tego samego presetu formularza. Jedyną różnicą jest wersja modelu probabilistycznego: `v3.1.0` versus `v5.0.0`.',
        'Ponieważ Goose v5 IDA1 to Goose v3 IDA1 z podmienionym wyłącznie modelem probabilistycznym, delta Goose v3 -> v5 w parity report jest czystą deltą modelu: bez zmiany margin, drabiny, filtrów, capów ani ridge.',
        '**Goose** jest warstwą overlay ryzyka w Simulation v5, działającą po ścieżce ukształtowanej przez Goose. W oficjalnym trybie nie buduje od nowa wczesnej drabiny. Ocenia kandydatów residualu i decyduje, czy późny close pozostaje na IDA1, czy przechodzi do finalnego IDA2.',
        'Kandydat Goose zawiera co najmniej: datę, godzinę, kwadrans, komponent, stronę domknięcia, cenę wejścia, rozmiar residualu, kontekst Fix II, kontekst IDA1 i kontekst IDA2. Goose przelicza te wejścia na signed score, takie jak `IDA2 q50 vs Fix II` i `IDA2 q50 vs IDA1`, a następnie stosuje branch-specific guardy komponentu i grupy godzin.',
        'Praktyczna różnica polega na zakresie. Goose kontroluje całą ścieżkę do IDA1. Oficjalny Goose kontroluje wyłącznie późną decyzję o residualu nałożoną na tę ścieżkę. Eksperymentalne tryby Goose mogą retune-ować wcześniejsze decyzje, ale nie są oficjalnym benchmarkiem.',
      ],
      diagram: [
        { label: 'Drabina Goose', note: 'kwantyle, bucket, margin, cap' },
        { label: 'Kandydat residualu', note: 'godzina, strona, entry, wolumen' },
        { label: 'Decyzja Goose', note: 'carry do IDA2 albo block' },
        { label: 'Goose IDA2 Final', note: 'porównanie do overlay baseline Goose v5 IDA1' },
      ],
      keyValues: [
        { label: 'Jednostka decyzji Goose', value: 'order ladder i staged close execution przez Fix I, Fix II i IDA1' },
        { label: 'Preset referencyjny Goose v3', value: 'model probabilistyczny `v3.1.0`; Fix I `v2.0.0`; Fix II `v1.0.0-fix2`; IDA1 `ida1-v1.0.3`; `ida1_margin = 1000`; `prediction_filter_margin = 10`; `preds_diff_cutoff = 10`; buckety `20/40/60`; `max_avg_volume_pct = 30`; `max_position = 60`; ridge wyłączony' },
        { label: 'Preset referencyjny Goose v5', value: 'model probabilistyczny `v5.0.0`; wszystkie pozostałe parametry execution Goose są identyczne jak w Goose v3 IDA1' },
        { label: 'Oficjalny preset Goose', value: '`overlay_handoff`; ta sama ścieżka Goose v5 przez IDA1; `ida1_margin = 1000`; `prediction_filter_margin = 10`; `preds_diff_cutoff = 10`; `max_avg_volume_pct = 30`; `max_position = 60`; `base_carry_mode = strict_v4`; `ida2_min_edge_per_mwh = 20`' },
        { label: 'Diagnostyczne tryby Goose', value: '`overlay_only` zachowuje ścieżkę Goose v5 i usuwa handoff. `full_strategy_experimental` pozwala na wcześniejsze interwencje Goose i jest wyłącznie trybem badawczym.' },
        { label: 'strict_v4 carry', value: 'Residual BASE przechodzi dalej tylko wtedy, gdy przechodzą ścisłe reguły edge, width i direction. W przeciwnym razie BASE jest degradowany do wcześniejszego legalnego close.' },
        { label: 'Jednostka decyzji Goose', value: 'późny kandydat residualu po Fix II / IDA1, oceniany pod carry do IDA2 albo block na tym samym późnym etapie' },
        { label: 'Sygnały Goose', value: 'signed forecast spread, strona domknięcia, grupa godzin, rozmiar residualu, guard komponentu i aktywny branch threshold' },
        { label: 'Zakres Goose', value: 'tylko Simulation v5; produkcyjne zakładki Goose pozostają bez zmian' },
      ],
      examples: [
        'Przykład Goose: residual przeżył Fix II; drabina IDA1, margin, cap płynności i cena rynkowa decydują, ile domknie się na IDA1.',
        'Przykład Goose: po tej samej ścieżce ukształtowanej przez Goose wieczorny kandydat sell z `fix_i` albo `bucket` ma wystarczający signed edge do IDA2, więc Goose przenosi go do final settle IDA2.',
        'Przykład zablokowany: Goose nadal buduje wcześniejszą ścieżkę, ale Goose odrzuca późny carry, więc wynik pozostaje blisko Goose IDA1 Stop.',
      ],
    },
    residualFlow: {
      title: 'Jak residual przechodzi między aukcjami',
      bullets: [
        'Strategia działa etapami. Każda aukcja zużywa część otwartej pozycji, a dalej może przejść tylko residual, który po tym etapie nadal istnieje i spełnia guardy.',
        'Residual BASE i residual probabilistyczny są powiązane, ale nie są tym samym portfelem. Backendowy runtime BASE carry może dla tej samej daty inaczej gate-ować komponent BASE niż residual z fixingów.',
        'Idle na BASE nie zatrzymuje automatycznie całego dnia. Jeżeli probabilistic leg dalej ma wykonalny residual albo bucket flow, późniejsze aukcje mogą pozostać aktywne mimo zerowego BASE.',
        'IDA1 jest pierwszym downstream close. Jeżeli residual może iść dalej i istnieją wejścia IDA1, część dnia może zostać rozliczona na IDA1, a reszta albo zatrzymuje się wcześniej, albo pozostaje kandydatem do końcowej nogi IDA2.',
        'IDA2 jest ostatnim close w staged path. Wchodzi do wyniku strategii tylko wtedy, gdy istnieje predykcja IDA2 i wejścia settlement IDA2; IDA1 może wcześniej wzbogacić ścieżkę albo zostać pominięty przez jawną ścieżkę finalną bez IDA1. Jeżeli późne wejścia IDA2 nie istnieją, ekran zostaje na jawnym statusie partial albo fallback Fix II / IDA1 zamiast udawać finalne domknięcie.',
        'Dlatego benchmark cards trzeba czytać jako porównanie całych ścieżek, a nie osobne ledger-y. `Fix II Stop Benchmark` to wymuszone wczesne zatrzymanie na ścieżce symulacji, `Fix II Stop (Goose live stop)` to legacy benchmark `Base + Fixing`, `Goose IDA1 Stop` to standalone replay Goose v5 IDA1, `Goose IDA1 Stop` to pośrednie zatrzymanie, a `Goose IDA2 Final` to w pełni settled finalna baza strategii.',
      ],
      diagram: [
        { label: 'Otwarta pozycja', note: 'po BASE i Fix I' },
        { label: 'Close Fix II', note: 'wykonuje to, co może' },
        { label: 'Kwalifikowany residual', note: 'przechodzi tylko przez guardy' },
        { label: 'IDA1 / IDA2', note: 'późne close, jeśli są dane' },
      ],
    },
    volumeAllocation: {
      title: 'Drabiny strategii i przepływ residuala między aukcjami',
      bullets: [
        'Simulation v5 używa tych samych nominalnych rozmiarów drabiny co Goose: bucket1 = 20 MW, bucket2 = 40 MW, bucket3 = 60 MW. To są rozmiary zleceń, a nie gwarantowany wolumen wykonania; po curve matching, capach pozycji i dostępnym residualu wykonanie może być mniejsze.',
        'Zlecenia Fix I są budowane ze stosu kwantyli modelu. Buy startuje bliżej dolnych kwantyli, sell bliżej górnych, a wystarczająco duży rozjazd między forecastem Fix I i Fix II może przesunąć drabinę o jeden stopień kwantyla.',
        'Fix II pracuje już na residualu po wcześniejszych etapach. Najpierw może zredukować nadmiar pozycji, potem próbuje domknąć pozostały residual Fix I i BASE, a dopiero później wystawia capowane zlecenia bucketowe.',
        'BASE ma osobny kontekst dzienny. Jeśli nie ma inputu wykonania BASE ani admin override, runtime używa własnego symulacyjnego fallbacku 50 MW. Goose risk dla BASE tego fallbacku nie zapisuje ręcznie i nie dotyka produkcyjnego `others.base_action`.',
        'Runtime nie dzieli całego dnia z góry pomiędzy wszystkie aukcje. Każdy etap dostaje tylko pozycję, która przeszła przez poprzedni etap, a do IDA1 i później IDA2 może wejść wyłącznie **niedomknięty residual** spełniający guardy.',
        'Dlatego venue chips potrafią być większe niż główny headline strategii. Venue totals pokazują brutto aktywność rynkową na poszczególnych aukcjach, a karta Total Volume zostaje na bazie live realised path dla wybranego zakresu.',
      ],
      diagram: [
        { label: 'Nominalna drabina', note: '20 / 40 / 60 MW' },
        { label: 'Curve match', note: 'rynek wykonuje kwadranse' },
        { label: 'Cap pozycji', note: 'runtime przycina ryzyko' },
        { label: 'Residual', note: 'tylko reszta idzie dalej' },
      ],
      examples: [
        'Bucket 60 MW może skończyć jako 17.5 MWh po dopasowaniu kwadransów i capach.',
        'Gross venue volume może liczyć aktywność na wielu nogach; final strategy volume śledzi zrealizowaną ścieżkę.',
      ],
    },
    runtimeParameters: {
      title: 'Aktywne limity i parametry runtime',
      bullets: [
        'Ta sekcja pokazuje parametry, które realnie zmieniają decyzję. Nie chodzi o pełną listę techniczną, tylko o to, które progi wpływają na sizing, carry i hedge.',
        'Oficjalny tryb Goose overlay to `overlay_handoff`. Zostawia wcześniejszą ścieżkę Goose v5 i używa Goose dopiero do późnej decyzji IDA2.',
        '`base_volume = 50 MW` jest domyślnym rozmiarem BASE w symulacji, gdy nie ma jawnego kontekstu wykonania. To utrzymuje zgodność z mechaniką Goose.',
        '`preds_diff_cutoff = 10` nadal należy do legacy/runtime fallbacku BASE i presetów parity. Widok przed BASE pokazuje Goose risk-management context; nie zapisuje ręcznego override decyzji BASE.',
        'Krok hedge zaczyna działać dopiero po aukcji BASE. Nie wybiera BASE, tylko patrzy na realną pozycję i może wystawić przeciwne ordery hedge Fix I dla ryzykownych godzin. Buy BASE używa Goose ryzyka, a Sell BASE może użyć Spike Guarda.',
        '`NEGATIVE_PRICE_BASE_SHRINK_MODE = hybrid_v1` wybiera między hard i soft wewnątrz Goose ryzyka. Hard zostaje domyślnie. Soft włącza się tylko dla Buy BASE, gdy hard wyciąłby co najmniej 3 pełne godziny i BASE price jest poza `100-180 PLN/MWh`.',
        'Soft w Goose ryzyku dotyczy tylko sizingu hedge z risk management. Kontrola BUY Fix I, Fix II falling-knife stop i IDA2 buy-carry block zostają na twardszej logice.',
        'Fix I BUY Guard liczymy jako interwencję guarda, nie jako hedge: blokuje albo ogranicza zwykły BUY Fix I przed wysłaniem orderu. Nowy payload settle zapisuje per hour wolumen original, cap, wolumen po guardzie i affected volume.',
        '`prediction_filter_margin` jest trend/filter margin stosowanym do ścieżki w stylu Goose. Oficjalna wartość to `10 PLN/MWh`.',
        '`max_avg_volume_pct` jest limitem średniej płynności używanym przy sizingu residuala IDA1. Oficjalna wartość to `30%` benchmarkowej średniej płynności dla kwartału.',
        '`max_position` jest twardym capem otwartej pozycji po wcześniejszych close. Oficjalna wartość to `60 MW`.',
        '`ida1_margin` jest marginesem zleceń IDA1 w Goose. Oficjalna wartość dla Goose v3, Goose v5 i oficjalnego Goose to `1000 PLN/MWh`.',
        '`ida2_min_edge_per_mwh` jest oficjalnym progiem sell-to-IDA2 dla wieczornego branchu Goose. Oficjalna wartość to `20 PLN/MWh`, a runtime używa jej bezpośrednio przy ocenie tego branchu.',
        'Oficjalny baseline Goose live przypina `ida2_margin = 1000`, buckety, `base_carry_mode = strict_v4` oraz ustawienia ridge/risk zgodne z wybranym wariantem modelu.',
      ],
      keyValues: [
        { label: 'Preset porównania Goose v3/v5', value: 'Ta sama logika Goose i te same parametry wykonania; zmienia się wersja modelu probabilistycznego i właściwe dla niej ustawienie ridge.' },
        { label: 'Oficjalny preset Goose overlay + handoff', value: '`overlay_handoff`, `ida1_margin = 1000`, `prediction_filter_margin = 10`, `preds_diff_cutoff = 10`, `max_avg_volume_pct = 30`, `max_position = 60`, `base_carry_mode = strict_v4`, `ida2_min_edge_per_mwh = 20`' },
        { label: 'Diagnostyczny preset full-strategy', value: '`full_strategy_experimental`; tryb badawczy tylko dla symulacji z wcześniejszymi interwencjami Goose przed finalnym krokiem IDA2' },
        { label: 'Domyślny fallback BASE', value: '`50 MW`' },
        { label: 'Goose risk dla BASE', value: 'read-only Goose risk-management context: koryguje q50 używane w ocenie Buy/Sell/Idle; bez ręcznego zapisu do `others.base_action` i bez generowania orderu BASE z samego panelu UI' },
        { label: 'Risk-management hedge', value: 'Goose ryzyko: Buy BASE, godziny 10-16, q50 <= -50 i q90 <= 25 albo q10 <= -200. Spike Guard: Sell BASE, wszystkie godziny, wysoka premia q50/q90 do dziennego q50. Hedge order = pełny blok MW - target MW tylko wtedy, gdy order faktycznie jest wystawiony.' },
        { label: 'Fix I BUY Guard', value: 'Interwencja Goose ryzyka na zwykłym BUY Fix I: blokada do 0 MW albo limit do maksymalnego wolumenu przed wysłaniem. Liczyć jako guard intervention, nie hedge order/fill/PnL.' },
        { label: 'Spike risk bez hedge', value: 'Tylko sygnał ryzyka. Pokazywać w szczegółach dnia / audycie risk management, ale nie liczyć jako hedge order, fill ani PnL.' },
        { label: 'BASE carry guard', value: 'Może wymusić residual BASE na Fix II albo zablokować carry do IDA. Wartość jest w ścieżce BASE/settle, poza PnL hedge Fix I.' },
        { label: 'Falling-knife stops', value: 'hard stop na Fix II, jeśli long/buy spada > 200 PLN/MWh od wejścia; soft stop, jeśli spada > 75 i IDA1 q50 nie jest lepsze od Fix II o 25 PLN/MWh' },
        { label: 'Próg branchu IDA2 sell', value: '`ida2_min_edge_per_mwh = 20 PLN/MWh` dla wieczornego branchu carry sell w trybie oficjalnym' },
      ],
    },
    executionMonitor: {
      title: 'Audyt orderów i execution',
      bullets: [
        'Zakładka Audyt jest kanonicznym miejscem dla staged path i spójności orderów. Porównuje preview orderów, ceny aukcyjne, komponenty staged execution i wiersz kalkulacji dla każdej godziny oraz każdego kwadransa.',
        'To nie jest drugi model cenowy i nie jest prerequisite dla Let’s trade. Audyt tłumaczy, dlaczego wiersz jest executed, preview-only, calculation-only albo mismatch.',
        'Guard i hedge nadal korzystają z inputu wykonania BASE oraz forecastów. Audyt wyjaśnia wiersze submitted, executed, blocked, capped, skipped i calculation, ale nie jest nową instrukcją tradingową.',
      ],
      diagram: [
        { label: 'Preview orderów', note: 'koszyki Noble / BOŚ' },
        { label: 'Execution', note: 'cena rynku i expected fill' },
        { label: 'Kalkulacja', note: 'wolumen komponentu i PnL' },
      ],
    },
    status: {
      title: 'Obecny status i guardy',
      bullets: [
        'Bieżący dzień może długo pozostawać stanem pośrednim. Cena Fix I albo późniejsze nogi enrichment mogą nadal być pending, więc dzienny PnL pozostaje prowizoryczny, dopóki odpowiedni etap rynku nie zostanie domknięty.',
        '`Partial` oznacza, że poprawny wcześniejszy close został już zapisany, a późniejsze wejścia nadal nie istnieją. `Fallback` oznacza, że ścieżka została wymuszona do najdalszego wcześniejszego legalnego close, bo późniejsza aukcja lub input są niedostępne. `Blocked` oznacza brak prerequisite, więc runtime nie może wymyślać business result.',
        'Fix II partial settle może działać samodzielnie. IDA1 partial settle wymaga predykcji IDA1. Final Goose IDA2 settle wymaga predykcji IDA2 i cen rozliczeniowych IDA2; jeśli brakuje IDA1, runtime może zapisać jawną ścieżkę `settled_ida2_no_ida1` zamiast blokować cały dzień.',
        'Nieudane albo zablokowane próby pozostają faktami pipeline. Należą do historii pipeline i warningów. Nie nadpisują już ważnego biznesowego wyniku dnia.',
        'Strona rozdziela więc prawdę pipeline od prawdy biznesowej: taski mogą failować, retry-ować i później kończyć się sukcesem, a widoczny daily result nadal wskazuje na ostatni ważny settled row.',
      ],
      keyValues: [
        { label: 'Fix II partial', value: 'wymaga ścieżki Fix II' },
        { label: 'IDA1 partial', value: 'wymaga predykcji IDA1' },
        { label: 'Goose IDA2 Final', value: 'wymaga predykcji IDA2 i cen rozliczeniowych IDA2; IDA1 może nie istnieć przez jawną ścieżkę settle `IDA2-no-IDA1`' },
        { label: 'Failed task', value: 'fakt pipeline, nie zastępstwo za biznesowy PnL' },
      ],
    },
    numbers: {
      title: 'Jak czytać liczby',
      bullets: [
        'Base PnL to wkład pozycji bazowej zanim zacznie się fixing flow. Trzeba go czytać osobno od probabilistic flow, bo backendowy runtime BASE carry może prowadzić base i probabilistic residual inną polityką carry dla tej samej daty.',
        'Fix I / Fix II leg PnL to łączny wkład wczesnych fixing legs. IDA1 leg PnL i IDA2 leg PnL są tylko późniejszymi komponentami domykania residuala. Nie są pełnym wynikiem ścieżki IDA1 albo IDA2.',
        '`Fix I wykonanie` oznacza fill, a nie settle. Gdy pojawią się ceny Fix I, UI może pokazać, które wiersze drabiny Fix I weszły, stronę, wolumen i średnią cenę wykonania. Finansowy PnL pozostaje otwarty do domknięcia residualu na Fix II albo późniejszej aukcji.',
        'Benchmark stop obejmuje wszystko do miejsca zatrzymania: `Goose IDA1 Stop` obejmuje BASE + Fix I/Fix II + IDA1, `Fix II Stop (Goose live stop)` obejmuje BASE + Fix I/Fix II, a `Fix II Stop Benchmark` jest kontrfaktycznym wcześniejszym close po stronie symulacji. Dlatego noga IDA1 może być ujemna, a benchmark Goose IDA1 Stop nadal dodatni.',
        'Wartości komponentów nie są przenośne między ścieżkami. Replay Goose IDA1, Goose IDA1 Stop i Goose IDA2 Final mogą używać tych samych cen rynkowych, ale inaczej rozdzielić PnL, bo tworzą inny residual po BASE i Fix II.',
        '`Goose v5 IDA1` to standalone replay Goose v5 IDA1. `Goose IDA1 Stop` oznacza ścieżkę symulacji zatrzymaną na IDA1. `Goose IDA2 Final` wydłuża ten sam dzień do końcowej aukcji IDA2. `Fix II Stop Benchmark` to kontrfaktyczne wymuszone domknięcie na Fix II, a `Fix II Stop (Goose live stop)` to legacy wynik `Base + Fixing` przed IDA1.',
        'Live provisional total incl. partial settle to główny operacyjny headline. Zlicza każdy już zrealizowany dzień do najdalszego aktualnie dostępnego close, więc od razu uwzględnia partial Fix II i fallback IDA1 zamiast czekać na finalne IDA2.',
        'Goose IDA2 Final to czysty settled benchmark. Obejmuje wyłącznie dni już zamknięte przez finalną ścieżkę IDA2, dlatego w trakcie dnia może różnić się od live provisional total.',
        'Karty benchmarkowe trzeba czytać jako porównanie całych ścieżek, a nie osobne ledger-y. Delta gross porównuje surowy PnL, Delta after fees porównuje wynik netto biznesowy, a Delta fees pokazuje, która ścieżka zapłaciła więcej albo mniej kosztów.',
        'Raport parity jest widokiem diagnostycznym. `Goose v3 IDA1` i `Goose v5 IDA1` są standalone replayami Goose z jawnymi presetami. `Goose IDA2 Final` jest albo persisted settled path, albo zmodyfikowaną ścieżką in-memory, gdy aktywna jest modified simulation.',
        'Delty parity czytaj po kolei: `Goose v3 -> Goose v5` to delta modelu, `Goose v5 -> official Goose` to delta overlay, a `Goose IDA1 Stop -> Goose IDA2 Final` to czysta delta późnej polityki IDA2.',
        'Live vs persisted: Summary preferuje zapisany settle row dla każdego dnia ze statusem settled*. Live jest pokazywany, gdy dzień jest pending. Jeżeli te wartości różnią się dla settled day, to problem spójności, nie zwykły lag refreshu.',
        'Fee są przeliczane z gross sell value i całkowitego wolumenu MWh. Drobne różnice zaokrągleń do 0.01 PLN są normalne; większe zwykle oznaczają różnicę ścieżki albo venue inclusion, nie inny fee-rate.',
        'Dane operacyjne pochodzą z osobnych źródeł: predictions_sim.* trzyma staged plan, historię domknięcia symulacji, ręczny input symulacyjny i status przeliczenia po tym inpucie, a others.base_action_override trzyma jawne override symulacyjne. Produkcyjny others.base_action nie jest zapisywany przez Simulation v5.',
        'Final MAE on settled prices porównuje zapisane forecasty q50 do faktycznie rozliczonych cen rynku dla Fix II, IDA1 i IDA2. To błąd forecastu liczony ex post na realnych cenach, osobny od train-time P50 MAE pokazywanego na karcie pipeline.',
        'P50 MAE, Coverage80, Pinball avg i scale global należą do zapisanego snapshotu treningowego. Opisują stan modelu dla danego runu, a nie zrealizowany wynik tradingowy dla wybranego zakresu dat.',
        'Preview orderów Fix I używa tego samego koszyka co Let’s trade: Goose live ladder, wiersze zamknięcia BASE oraz hedge risk management Simulation v5. Staged rows wykonania Fix I zostają sygnałem audytowym/ścieżką, a nie źródłem koszyka Noble.',
        'Wiersze kontroli BUY Fix I nie są wierszami koszyka Noble. To interwencje Goose risk management przed wysłaniem orderu: wolumen blocked/capped należy do statystyk interwencji ryzyka, nie do hedge fill rate ani hedge PnL.',
        'Spike risk bez hedge też jest poza hedge stats. To kontekst mówiący, że ryzyko wykryto, ale order hedge Fix I nie był ekonomiczny.',
        'Wartość BASE carry guard jest już w zrealizowanej ścieżce BASE/settle. Nie należy dodawać jej drugi raz jako hedge PnL.',
        'Karty wolumenowe pokazują dostarczoną energię w MWh. Karty venue pokazują, gdzie był obrót, więc należy je czytać jako aktywność rynkową, a nie jako prosty jeden-do-jednego odpowiednik final volume strategii.',
        '`Profile before hedge` jest celowo pokazany obok `Fix I hedge`, bo pierwsza liczba nie jest samodzielnie wykonalna. Liczba wykonalna to suma obu kolumn i ona trafia jako wynik BASE do Daily Results.',
      ],
      examples: [
        'Live provisional total odpowiada na pytanie: jaki jest najlepszy bieżący widok zrealizowanego zakresu?',
        'Goose IDA2 Final odpowiada na pytanie: co wyszło na dniach porównywalnych aż do IDA2?',
        'Goose v5 IDA1 odpowiada na pytanie: co zwraca zakładka Goose IDA1 przy udokumentowanym presecie formularza v5?',
        'Goose IDA1 Stop odpowiada na pytanie: co zwraca Goose, jeśli dzień zatrzymuje się na IDA1 przed finalną decyzją IDA2?',
      ],
    },
    manualOverrides: {
      title: 'Admin override BASE do replay',
      bullets: [
        'BASE override to ukryte narzędzie admin/replay do przypięcia nogi BASE dla jednej daty delivery: kierunek (`Buy / Sell / Idle`), wolumen w MWh i audytową cenę BASE w PLN/MWh. To nie jest normalny flow tradera.',
        'Normalny flow Let’s trade zapisuje wykonanie BASE do `predictions_sim.v5_base_execution_input`. Admin override jest osobny i zapisuje wyłącznie `others.base_action_override`, nigdy produkcyjne `others.base_action`.',
        'Zapis lub usunięcie override może uruchomić ten sam staged recalc chain dla tej daty (`predict -> Fix II partial settle -> IDA1 partial settle -> final IDA2 settle`).',
        'Summary i Daily Results pokazują wtedy stan pending, dopóki refresh naprawdę się nie domknie, zamiast udawać finalny wynik.',
        'Dni z aktywnym override dostają badge `Manual` w Daily Results, a rozwinięty detail dnia pokazuje kto zapisał override, kiedy i z jaką notą.',
        'Usunięcie override przywraca datę do automatycznego trybu symulacji. Zapisane `base_price` pozostaje widoczne dla audytu, ale zrealizowany PnL nadal używa ceny settlement z `tge.base`.',
      ],
      diagram: [
        { label: 'Zapis override', note: 'kierunek, rozmiar, nota' },
        { label: 'Freeze dnia', note: 'automaty pomijają datę' },
        { label: 'Recalc chain', note: 'predict, partial, final settle' },
      ],
    },
  },
};

const MINIMAL_HOW_IT_WORKS_COPY = {
  en: {
    headerTitle: 'How It Works',
    headerSubtitle: 'Business and technical guide to how the Goose IDA2 simulation builds one visible trading path from BASE through Fix II, IDA1, and IDA2.',
    quickReadTitle: 'Read this first',
    quickReadCards: [
      {
        label: 'Business goal',
        body: 'This page answers one question: **what would our visible Goose path earn if a delivery day is closed stage by stage through `Fix II`, `IDA1`, and finally `IDA2`?** It is not a production Goose control panel and not a shadow-trading monitor.',
      },
      {
        label: 'Default operating mode',
        body: 'Normal UI runs on `Goose v3.1.0`. `Goose v5.0.0` exists only as a manual comparison mode inside `Modify simulation`, so the public path should be read as `Goose v3` unless you explicitly changed it.',
      },
      {
        label: 'Morning Goose role',
        body: 'Morning Goose risk management is **context before BASE**, not a manual trade blotter on this page. It shapes the BASE setup, the risk view, and later residual handling, but it does not write production Goose decisions from this screen.',
      },
      {
        label: 'What results mean',
        body: 'The page shows the **latest legal close for each day**. If `IDA2` is not available yet, the day can stop at `IDA1`; if `IDA1` is also unavailable, it can stop at `Fix II`. Partial and fallback states are deliberate and auditable.',
      },
    ],
    flowTitle: 'One day, one staged path',
    flowCaption: 'Business-wise, the simulation starts from a BASE posture, monetises the early fixing path, then closes or carries only the residual that is still open and still legal at each later stage.',
    flowSteps: [
      { label: 'BASE', note: 'default trading context before auction, shaped by Goose risk management' },
      { label: 'Fix II', note: 'main fixing path and first large monetisation of the day' },
      { label: 'IDA1', note: 'late close for residual still open after Fix II' },
      { label: 'IDA2', note: 'final late overlay where selected residual can still be handed off' },
    ],
    overview: {
      title: 'What this page is',
      paragraphs: [
        'This page is the operational view of the Goose IDA2 simulation. It exists to explain **what our simulation layer does with Goose inputs**, not to mutate production Goose.',
        'Technically, it combines stored prediction artefacts, runtime day details, staged order-plan rows, and realised market closes into one visible day path. Business-wise, that path is the version of the day we compare in Results, Daily Results, Orders, and Audit.',
      ],
    },
    pipelineReason: {
      title: 'Why pipeline status matters',
      bullets: [
        'Predictions, staged plans, day-details, and later close data appear at different times. The simulation therefore moves day by day through **real availability**, not through an assumed complete state.',
        'Pipeline cards are operational monitors for artefacts and runs. They tell you which model output already exists and which later screen can be trusted, but they are not themselves the business result.',
      ],
    },
    baseSource: {
      title: 'How BASE is handled',
      paragraphs: [
        'The default BASE context in this simulation is `50 MW`, unless you deliberately change the model or replay setup in `Modify simulation`.',
        'Goose risk management supplies the morning **risk-informed context** for BASE: direction, thresholds, risk shift, and later residual logic. It does not mean this page is a production BASE order entry screen.',
        'Any saved BASE execution input stays inside our simulation layer. It changes how this simulation replays the day, but it does not overwrite production Goose behaviour.',
      ],
    },
    scheduler: {
      title: 'What is monitored',
      intro: 'This panel monitors whether the artefacts needed by the simulation already exist for the selected delivery date.',
      bullets: [
        'For `Goose v3` and `Goose v5`, we monitor train and predict outputs, because those outputs determine which downstream sections can already show meaningful data.',
        'This monitor is about simulation readiness: model artefacts, plan rows, snapshots, and prediction outputs. It is not a live execution console.',
        'No production settle workflow is managed from here; this page observes our simulation path only.',
      ],
    },
    v5Model: {
      title: 'Predictions and Fix II',
      bullets: [
        '`Fix II q50` is the forecast anchor for the early part of the path. It is a model output, not a realised price, and it drives the main fixing leg visible in Predictions and the Fix II part of Results.',
        'When `Modify simulation` switches the model, the prediction payload changes with that chosen variant. That is why `Predictions`, `Data`, `Orders`, and later staged rows must always be read together.',
      ],
    },
    ida2Model: {
      title: 'IDA2 enrichment',
      bullets: [
        'IDA2 is the final late forecast layer. It matters only for residual that survives earlier stages and is still eligible to continue.',
        'The simulation decides whether that residual should stop on `IDA1` or continue into `IDA2`, and Orders/Audit expose the exact rows that reflect that handoff.',
      ],
    },
    mooseModel: {
      title: 'Goose risk-management overlay',
      bullets: [
        'Goose risk management shapes the path twice: first in the morning as BASE context, later as part of the residual-close logic that can still influence `Fix I`, `IDA1`, and `IDA2` behaviour.',
        'The simulation layer does not just display Goose values. It translates them into staged path choices, preview rows, and later benchmark comparisons.',
      ],
    },
    residualFlow: {
      title: 'Residual flow',
      bullets: [
        'Each stage may pass forward only the residual still open after all earlier legal closes and staged decisions have been applied.',
        'That is why a late `IDA1` or `IDA2` leg can exist even when the visible BASE setup is idle: later legs are not fresh standalone trades, but downstream closes of earlier exposure.',
      ],
    },
    volumeAllocation: {
      title: 'Orders and volume',
      bullets: [
        'Orders shows **submission baskets**, not a generic book of all possible rows. Noble and BOŚ are two renderings of the same staged path, translated into different venue conventions.',
        'Volume chips and venue cards show gross activity by leg. The headline PnL follows the realised staged path, so gross venue volume does not have to sum cleanly to one single business number.',
      ],
    },
    runtimeParameters: {
      title: 'What can change',
      bullets: [
        'The two user-facing levers are: model choice (`Goose v3.1.0` vs `Goose v5.0.0`) and replay/runtime parameters in `Modify simulation`.',
        'Those changes affect the simulation view, staged path, and benchmarks in memory until you run the modified replay. They do not silently rewrite the default public path.',
      ],
    },
    executionMonitor: {
      title: 'Audit and orders',
      bullets: [
        'Audit exists to answer: **do preview rows, execution path, and calculated PnL still describe the same business day?** If they diverge, this is where the mismatch should surface.',
        'Orders is the operational submission view. It should show the actual baskets implied by the active staged path, not a disconnected theoretical ladder.',
      ],
    },
    status: {
      title: 'How to read statuses',
      bullets: [
        '`Fix II`, `IDA1`, and `IDA2` tell you how far the **visible business path** reached for that day. They are stop labels for the path, not model-quality grades.',
        '`Partial` means a correct earlier close exists but the later one is not available yet. `Fallback` means the runtime legally stopped earlier instead of inventing a later result.',
      ],
    },
    manualOverrides: {
      title: 'Modify simulation',
      bullets: [
        'Use `Modify simulation` when you want to compare another model, change replay assumptions, or inspect how the same days behave under a different simulated setup.',
        'Normal browsing of Results, Risk management, Orders, and Audit should be interpreted as the default `Goose v3` simulation path unless the UI explicitly tells you a modified run is active.',
      ],
    },
    numbers: {
      title: 'How to read the numbers',
      bullets: [
        'Summary and Daily Results compare whole staged business paths. They should not be read as isolated venue ledgers or as one-to-one copies of a single prediction table.',
        '`Goose IDA2 Final` is the fully extended late-close path. `BASE Stop`, `Fix II Stop`, and `IDA1 Stop` are earlier stop benchmarks that help explain where value was created or lost before the final handoff.',
      ],
    },
  },
  pl: {
    headerTitle: 'Jak to działa',
    headerSubtitle: 'Przewodnik biznesowy i techniczny po tym, jak symulacja Goose IDA2 buduje widoczną ścieżkę dnia od BASE przez Fix II, IDA1 i IDA2.',
    quickReadTitle: 'Najpierw przeczytaj to',
    quickReadCards: [
      {
        label: 'Cel biznesowy',
        body: 'Ta strona odpowiada na jedno pytanie: **ile zarabia nasza widoczna ścieżka Goose, jeśli dzień domyka się etapami przez `Fix II`, `IDA1`, a na końcu `IDA2`?** To nie jest panel sterowania produkcyjnym Goose ani monitor shadow tradingu.',
      },
      {
        label: 'Tryb domyślny',
        body: 'Normalny UI działa na `Goose v3.1.0`. `Goose v5.0.0` istnieje tylko jako ręczne porównanie w `Modify simulation`, więc publiczną ścieżkę czytamy jako `Goose v3`, chyba że jawnie przełączyłeś model.',
      },
      {
        label: 'Rola porannego Goose',
        body: 'Goose risk management to **poranny kontekst przed BASE**, a nie ręczny blotter decyzji BASE na tej stronie. Kształtuje setup BASE, widok ryzyka i późniejsze traktowanie residualu, ale nie zapisuje stąd produkcyjnej decyzji Goose.',
      },
      {
        label: 'Co znaczą wyniki',
        body: 'Strona pokazuje **najdalszy legalny close danego dnia**. Jeśli `IDA2` nie jest jeszcze dostępne, dzień może zatrzymać się na `IDA1`; jeśli brakuje też `IDA1`, zatrzyma się na `Fix II`. Partial i fallback są tu celowe i audytowalne.',
      },
    ],
    flowTitle: 'Jeden dzień, jedna staged ścieżka',
    flowCaption: 'Biznesowo symulacja zaczyna od postawy BASE, monetyzuje wczesną ścieżkę fixingową, a później domyka albo niesie dalej tylko ten residual, który po wcześniejszych close’ach nadal jest otwarty i legalny.',
    flowSteps: [
      { label: 'BASE', note: 'domyślny kontekst handlu przed aukcją, kształtowany przez Goose risk management' },
      { label: 'Fix II', note: 'główna fixingowa ścieżka dnia i pierwsza duża monetyzacja' },
      { label: 'IDA1', note: 'późne domknięcie residualu, który przeżył Fix II' },
      { label: 'IDA2', note: 'finalny późny overlay, do którego może jeszcze trafić wybrany residual' },
    ],
    overview: {
      title: 'Czym naprawdę jest ten widok',
      paragraphs: [
        'To jest operacyjny widok symulacji Goose IDA2. Jego zadaniem jest pokazać, **co robi nasza warstwa symulacyjna z wejściami Goose**, a nie sterować produkcyjnym Goose.',
        'Technicznie strona łączy zapisane artefakty predykcji, runtime day-details, staged order-plan rows i zrealizowane close rynku w jedną widoczną ścieżkę dnia. Biznesowo ta ścieżka jest tym, co porównujemy potem w Results, Daily Results, Orders i Audit.',
      ],
    },
    pipelineReason: {
      title: 'Dlaczego pipeline ma znaczenie',
      bullets: [
        'Predykcje, staged plany, day-details i późniejsze close rynku pojawiają się o różnych porach. Dlatego symulacja przesuwa dzień zgodnie z **realną dostępnością danych**, a nie udaje pełny finalny stan.',
        'Karty pipeline są operacyjnym monitorem artefaktów i runów. Mówią, co już istnieje i które sekcje UI można czytać jako wiarygodne, ale same nie są wynikiem biznesowym.',
      ],
    },
    baseSource: {
      title: 'Jak traktujemy BASE',
      paragraphs: [
        'Domyślny kontekst BASE w tej symulacji to `50 MW`, chyba że świadomie zmienisz model albo replay setup w `Modify simulation`.',
        'Goose risk management dostarcza poranny **kontekst ryzyka dla BASE**: kierunek, progi, shift i późniejszą logikę residualu. To nie oznacza, że ten ekran jest produkcyjnym panelem wpisywania BASE.',
        'Każdy zapis inputu wykonania BASE zostaje w naszej warstwie symulacyjnej. Zmienia to replay dnia w symulacji, ale nie nadpisuje zachowania produkcyjnego Goose.',
      ],
    },
    scheduler: {
      title: 'Co jest monitorowane',
      intro: 'Ten panel monitoruje, czy artefakty potrzebne symulacji już istnieją dla wybranej daty delivery.',
      bullets: [
        'Dla `Goose v3` i `Goose v5` monitorujemy train i predict outputs, bo to one decydują, które dalsze sekcje mogą już pokazać sensowne dane.',
        'To jest monitoring gotowości symulacji: artefaktów modelu, plan rows, snapshotów i outputów predykcji. Nie jest to konsola live execution.',
        'Z tego panelu nie zarządzamy produkcyjnym settle workflow; obserwujemy wyłącznie ścieżkę naszej symulacji.',
      ],
    },
    v5Model: {
      title: 'Predykcje i Fix II',
      bullets: [
        '`Fix II q50` to forecast kotwiczący wczesną część ścieżki. To output modelu, nie zrealizowana cena rynku, i to on napędza główną fixingową nogę widoczną w `Predykcje` oraz później w `Results`.',
        'Kiedy `Modify simulation` zmienia model, zmienia się cały payload predykcji dla tego wariantu. Dlatego `Predykcje`, `Dane`, `Orders` i staged rows trzeba czytać razem, a nie osobno.',
      ],
    },
    ida2Model: {
      title: 'Warstwa IDA2',
      bullets: [
        'IDA2 to finalna późna warstwa forecastu. Ma znaczenie tylko dla residualu, który przetrwał wcześniejsze etapy i nadal kwalifikuje się do dalszego carry.',
        'Symulacja decyduje, czy ten residual ma skończyć się na `IDA1`, czy przejść do `IDA2`, a `Orders` i `Audit` pokazują dokładne rows odpowiadające temu handoffowi.',
      ],
    },
    mooseModel: {
      title: 'Overlay Goose risk management',
      bullets: [
        'Goose risk management wpływa na ścieżkę dwa razy: rano jako kontekst BASE oraz później jako część logiki residual-close, która może jeszcze zmienić zachowanie `Fix I`, `IDA1` i `IDA2`.',
        'Warstwa symulacyjna nie tylko wyświetla wartości Goose. Tłumaczy je na wybory staged path, preview rows i benchmarki, które później widzisz na ekranie.',
      ],
    },
    residualFlow: {
      title: 'Przepływ residualu',
      bullets: [
        'Każdy etap może przekazać dalej tylko residual, który pozostał otwarty po zastosowaniu wszystkich wcześniejszych legalnych close i staged decyzji.',
        'Dlatego późna noga `IDA1` lub `IDA2` może istnieć nawet wtedy, gdy widoczny setup BASE jest idle: późne nogi nie są świeżymi standalone trade’ami, tylko downstream close wcześniejszej ekspozycji.',
      ],
    },
    volumeAllocation: {
      title: 'Ordery i wolumen',
      bullets: [
        'Orders pokazuje **koszyki do wysłania**, a nie abstrakcyjną listę wszystkich możliwych wierszy. Noble i BOŚ to dwa widoki tej samej staged ścieżki, przetłumaczone na różne konwencje venue.',
        'Chipy wolumenu i karty venue pokazują aktywność brutto po nogach. Główny PnL idzie po zrealizowanej staged ścieżce, więc brutto venue volume nie musi składać się do jednej headline liczby biznesowej.',
      ],
    },
    runtimeParameters: {
      title: 'Co można zmieniać',
      bullets: [
        'Dwie główne dźwignie użytkownika to wybór modelu (`Goose v3.1.0` vs `Goose v5.0.0`) oraz parametry replay/runtime w `Modify simulation`.',
        'Te zmiany wpływają na widok symulacji, staged path i benchmarki w pamięci, dopóki nie uruchomisz modified replay. Nie przepisują po cichu domyślnej publicznej ścieżki.',
      ],
    },
    executionMonitor: {
      title: 'Audit i Orders',
      bullets: [
        'Audit odpowiada na pytanie: **czy preview rows, execution path i policzony PnL nadal opisują ten sam biznesowy dzień?** Jeśli pojawi się rozjazd, to właśnie tutaj powinien być widoczny.',
        'Orders to operacyjny widok submission baskets. Powinien pokazywać faktyczne koszyki wynikające z aktywnej staged ścieżki, a nie odklejony teoretyczny ladder.',
      ],
    },
    status: {
      title: 'Jak czytać statusy',
      bullets: [
        '`Fix II`, `IDA1` i `IDA2` mówią, dokąd doszła **widoczna biznesowa ścieżka dnia**. To etykiety stopu ścieżki, a nie oceny jakości modelu.',
        '`Partial` oznacza, że poprawny wcześniejszy close już istnieje, ale późniejszy jeszcze nie. `Fallback` oznacza, że runtime zatrzymał się legalnie wcześniej zamiast wymyślać późniejszy wynik.',
      ],
    },
    manualOverrides: {
      title: 'Modify simulation',
      bullets: [
        'Używaj `Modify simulation`, gdy chcesz porównać inny model, zmienić założenia replay albo zobaczyć, jak te same dni zachowują się pod innym setupem symulacyjnym.',
        'Zwykłe czytanie `Results`, `Risk management`, `Orders` i `Audit` należy interpretować jako domyślną ścieżkę `Goose v3`, chyba że UI wyraźnie pokazuje aktywną zmodyfikowaną symulację.',
      ],
    },
    numbers: {
      title: 'Jak czytać liczby',
      bullets: [
        'Summary i Daily Results porównują całe staged business paths. Nie należy czytać ich jak oddzielnych venue ledgerów albo jak kopii jednej tabeli predykcji.',
        '`Goose IDA2 Final` to pełna ścieżka do najpóźniejszego close. `BASE Stop`, `Fix II Stop` i `IDA1 Stop` są wcześniejszymi benchmarkami stopu, które pomagają zrozumieć, gdzie wartość została zrobiona lub stracona przed finalnym handoffem.',
      ],
    },
  },
};

function getHowItWorksCopy(language) {
  return (
    MINIMAL_HOW_IT_WORKS_COPY[language]
    || MINIMAL_HOW_IT_WORKS_COPY.en
    || HOW_IT_WORKS_COPY[language]
    || HOW_IT_WORKS_COPY.en
  );
}

const SIM_UI_COPY = {
  en: {
    pageSubtitle: 'Operational simulation view for the Goose IDA2 flow through Fix II, IDA1, and IDA2.',
    languageLabel: 'Language',
    mainNavLabel: 'Main workflow',
    technicalNavLabel: 'Technical',
    modifySimulation: 'Modify simulation',
    tabs: {
      'lets-trade': {
        label: "Let's trade",
        helper: 'Trader screen: Goose risk, baskets, and daily path',
      },
      results: {
        label: 'Summary',
        helper: 'Summary, benchmarks, accumulated PnL',
      },
      'daily-results': {
        label: 'Daily Results',
        helper: 'Day-by-day realised flow and drill-down',
      },
      radar: {
        label: 'Radar',
        helper: 'Private daily market radar after the active predict snapshot',
      },
      'base-simulation': {
        label: 'Risk management',
        helper: 'Goose risk management: morning Goose risk, Fix I hedge, Fix I BUY controls, carry, and execution audit',
      },
      overrides: {
        label: 'Modify BASE',
        helper: 'Manual or monitor-derived BASE overrides; freezes a day from auto updates',
      },
      pipeline: {
        label: 'Pipeline',
        helper: 'Status and run controls',
      },
      predictions: {
        label: 'Predictions',
        helper: 'Staged forecast table',
      },
      data: {
        label: 'Data',
        helper: 'Prediction input dataset',
      },
      audit: {
        label: 'Audit',
        helper: 'Staged path and order consistency',
      },
      orders: {
        label: 'Orders',
        helper: 'Noble order preview',
      },
      'how-it-works': {
        label: 'How It Works',
        helper: 'Methodology and metric glossary',
      },
    },
    modifier: {
      pendingText: 'These values have not been applied yet. Run again to refresh Summary and Daily Results.',
      rangeText: (start, end) => `Run an in-memory replay for ${start} to ${end}. Summary and Daily Results update immediately; saved Results stay unchanged.`,
      modelLabel: 'Model',
      close: 'Close',
      replaying: 'Replaying...',
      run: 'Run modified simulation',
      pendingTitle: 'Modified settings changed',
      pendingBody: 'Run modified simulation to update Summary and Daily Results for the values currently shown in the form.',
      activeTitle: 'Modified simulation active',
      activeBody: 'Summary and Daily Results are recalculated in memory for this range.',
      reset: 'Reset to saved Results',
      pendingBadge: 'pending replay',
      baseSummary: (value) => `BASE ${value || '-'} MW`,
      ida1CapSummary: (value) => `IDA1 cap ${value || '-'} MW`,
      ida1MarginSummary: (value) => `IDA1 margin ${value || '-'} PLN`,
      modes: {
        full_strategy_experimental: 'Goose full strategy',
        overlay_only: 'Goose overlay only',
        overlay_handoff: 'Goose overlay + handoff',
      },
      fields: {
        base_volume: { label: 'BASE volume', hint: 'Default: 50' },
        fix_i_model_spread: { label: 'Fix I model spread', hint: 'Goose default: 5' },
        prediction_filter_margin: { label: 'Trend/filter margin', hint: 'Default: 10' },
        bucket1_volume: { label: 'Fix I bucket 1', hint: 'Goose default: 40' },
        bucket2_volume: { label: 'Fix I bucket 2', hint: 'Goose default: 40' },
        bucket3_volume: { label: 'Fix I bucket 3', hint: 'Goose default: 40' },
        np_constraint_bucket1: { label: 'NP constraint bucket 1', hint: 'Goose default: 0' },
        np_constraint_bucket2: { label: 'NP constraint bucket 2', hint: 'Goose default: 0' },
        np_constraint_bucket3: { label: 'NP constraint bucket 3', hint: 'Goose default: 0' },
        fix_ii_ida1_bucket1_volume: { label: 'Fix II → IDA1 bucket 1', hint: 'Goose default: 20' },
        fix_ii_ida1_bucket2_volume: { label: 'Fix II → IDA1 bucket 2', hint: 'Goose default: 40' },
        fix_ii_ida1_bucket3_volume: { label: 'Fix II → IDA1 bucket 3', hint: 'Goose default: 60' },
        max_avg_volume_pct: { label: 'Max IDA1 avg volume', hint: 'Default: 30' },
        max_position: { label: 'Max IDA1 position', hint: 'Default: 60' },
        ida1_margin: { label: 'IDA1 margin', hint: 'Goose default: 1500' },
        margin_tier1: { label: 'Margin price < 500', hint: 'Goose default: 200' },
        margin_tier2: { label: 'Margin price 500-700', hint: 'Goose default: 300' },
        margin_tier3: { label: 'Margin price 700-1000', hint: 'Goose default: 400' },
        margin_tier4: { label: 'Margin price > 1000', hint: 'Goose default: 500' },
        apply_ridge_shift: { label: 'Apply ridge shift', hint: 'Variant default: v5 off, v3 on' },
        apply_risk_score: { label: 'Apply risk score', hint: 'Goose default: on' },
        risk_score_model: { label: 'Risk engine', hint: 'Goose default: prod-v2.0.0' },
        ida2_margin: { label: 'IDA2 margin', hint: 'IDA2 overlay parameter' },
        ida2_min_edge_per_mwh: { label: 'IDA2 min edge', hint: 'Default: 20' },
        base_carry_mode: { label: 'BASE carry policy', hint: 'IDA2 residual policy' },
      },
    },
    letsTrade: {
      eyebrow: 'Trader workspace',
      title: "Let's trade",
      subtitle: (date) => `Fast trading-day view for delivery ${date}: market picture, morning Goose risk management from AWS, then auction order baskets.`,
      deliveryDate: 'Delivery date',
      refresh: 'Refresh trader view',
      flow: {
        marketTitle: 'Read the day',
        marketBody: 'PnL, month result, BASE, real prices and model q50 in one place',
        entryTitle: 'Goose risk management',
        entryBody: 'Morning Goose AWS output, around 09:30, before the BASE auction',
        ordersTitle: 'Noble basket',
        ordersBody: 'Check deadlines, expand rows only when needed',
      },
      step1: 'Market and PnL snapshot',
      step1Title: 'Day overview',
      dayRadarSummary: 'Goose risk',
      dayRadarOpen: 'Open risk',
      dayRadarLoading: 'Loading Goose risk...',
      dayRadarMissing: 'No Goose risk yet',
      dayRadarWaiting: 'Waiting for predict',
      dayRadarReady: 'Goose risk ready',
      dayRadarError: 'Goose risk unavailable',
      dayRadarVerdicts: {
        normal: 'Normal',
        cautious: 'Cautious',
        avoid: 'Avoid',
        waiting: 'Waiting',
      },
      step2: "Today's Goose risk signal",
      step2Body: 'This section is read-only. Simulation keeps the default 50 MW BASE context here, while Goose shows only the morning risk signal that later shapes the trading path and baskets.',
      entrySummaryTitle: "What Goose suggests today",
      entrySummaryDefaultBase: 'Default simulation BASE: 50 MW',
      entrySummaryDefaultBaseHelper: 'Change the model only in Modify simulation.',
      entryRecommendation: 'Goose risk status',
      entryBuyThreshold: 'Buy-side risk',
      entrySellThreshold: 'Sell-side risk',
      entrySuggestedVolume: 'Shifted q50 reference',
      entryRiskCharge: 'Risk charge',
      entryRiskHours: 'Risk hours',
      entryTriggerTitle: "Today's Goose signal",
      entryTriggerIntro: 'Model trigger:',
      entryTriggerBuyPrefix: 'Buy at',
      entryTriggerSellPrefix: 'Sell at',
      entryTriggerBuySuffix: 'or lower',
      entryTriggerSellSuffix: 'or higher',
      entryTriggerUnit: 'PLN/MWh',
      entryTriggerBody: ({ buyPrice, sellPrice }) => `Conditional only: Buy at or below ${buyPrice}; Sell at or above ${sellPrice}.`,
      entryTriggerUnavailable: 'Today Goose has no ready signal yet.',
      entryWhy: 'Why?',
      entryWhyTitle: 'Why this BASE risk radar fired',
      entryIdleWhyTitle: 'Why the recommendation waits for price',
      entryIdleWhyBody: ({ buyPrice, sellPrice }) => `The model does not force Buy or Sell here. Treat the BASE auction price as the trigger: consider Buy only at or below ${buyPrice}, consider Sell only at or above ${sellPrice}, and stay Idle between those thresholds.`,
      entryMissing: 'Goose risk management is not ready yet. Missing forecast or Fix II plan data.',
      entryRadarObserveOnly: 'Goose risk updates the simulation context only; it does not create a manual BASE decision on this screen.',
      entryNeutralBuffer: (value) => `neutral q50 buffer ${value} PLN/MWh`,
      entryPriceTriggers: ({ buyPrice, sellPrice }) => `Buy only at or below ${buyPrice}; Sell only at or above ${sellPrice}`,
      entryVolumeSummary: ({ buyVolume, sellVolume }) => `Buy ${buyVolume} / Sell ${sellVolume}`,
      entryFullBlockHours: (hours) => `full ${hours} h`,
      entryStrongSpikeHours: (hours) => `strong ${hours} h`,
      entryBuyExplain: ({ hours, fullBlock, charge }) => `Buy risk: ${hours} risky hour${hours === 1 ? '' : 's'}, ${fullBlock} full-block hour${fullBlock === 1 ? '' : 's'}, ${charge} PLN/MWh risk charge.`,
      entrySellExplain: ({ hours, strong, charge }) => `Sell risk: ${hours} upside hour${hours === 1 ? '' : 's'}, ${strong} strong spike hour${strong === 1 ? '' : 's'}, ${charge} PLN/MWh risk charge.`,
      entryNoRiskHumanExplain: 'The radar checks q10/q50/q90 tails and does not see hours that materially hurt Buy or Sell BASE.',
      entryBuyRiskHumanExplain: ({ hours, fullBlock, charge, hoursLabel }) => [
        'The radar checks for hours where the price can fall very low before BASE.',
        'For Buy this is risk: after buying BASE we can be long exactly in cheap or negative hours.',
        `Here it sees ${hours} such hour${hours === 1 ? '' : 's'}${fullBlock ? `, including ${fullBlock} full block cut${fullBlock === 1 ? '' : 's'}` : ''}; the source is the q10/q50/q90 tail forecast for ${hoursLabel}, so it adds ${charge} PLN/MWh risk charge.`,
        'This shows the morning Goose risk decision; it is not a manual BASE override.',
      ],
      entrySellRiskHumanExplain: ({ hours, strong, charge, hoursLabel }) => [
        'The radar checks for hours where the price can spike hard before BASE.',
        'For Sell this is risk: after selling BASE we can be short exactly in expensive hours.',
        `Here it sees ${hours} such hour${hours === 1 ? '' : 's'}${strong ? `, including ${strong} strong spike hour${strong === 1 ? '' : 's'}` : ''}; the source is the q50/q90 tail forecast for ${hoursLabel}, so it adds ${charge} PLN/MWh risk charge.`,
        'This shows the morning Goose risk decision; it is not a manual BASE override.',
      ],
      entryRadarSideSummary: ({ buyHours, sellHours }) => {
        const dominantSide = sellHours >= buyHours ? 'Sell' : 'Buy';
        const dominantHours = Math.max(buyHours, sellHours);
        return dominantHours > 0
          ? `Active Goose risk: ${dominantSide} side (${dominantHours} h).`
          : 'No active Goose risk shift for this day.';
      },
      entryRiskLabels: {
        low: 'Low risk',
        medium: 'Medium risk',
        high: 'High risk',
        blocked: 'Very high risk',
        not_ready: 'Not ready',
        no_base: 'No BASE',
      },
      entryActions: {
        buy: 'Buy BASE',
        sell: 'Sell BASE',
        idle: 'Decision depends on BASE price',
        not_ready: 'Not ready',
      },
      loadingMarket: 'Loading market view...',
      dayPnl: 'Strategy result',
      dayPnlFallback: (date) => `Live / provisional for delivery ${date}`,
      dayPnlFix1Mark: 'Provisional after Fix I: BASE marked to realised Fix I price',
      dayPnlLiveStage: (stage) => `Live / provisional after ${stage}`,
      dayPnlWaitingFixI: (date) => `Waiting for realised Fix I or settle for delivery ${date}`,
      dayPnlAfterFees: (value) => `After fees ${value}`,
      monthPnl: 'Current month',
      monthPnlHelper: (days) => `${days} realised day${days === 1 ? '' : 's'}`,
      yachtButton: 'Do we have a yacht yet?',
      yachtTarget: 'Target: 2,000,000 PLN in the current month',
      yachtHappyTitle: 'Yes. Time to look at marinas.',
      yachtSadTitle: 'Not yet.',
      yachtHappyBody: (value) => `Current month is ${value}, so the yacht target is above water.`,
      yachtSadBody: (value, missing) => `Current month is ${value}. Missing ${missing} to the 2m PLN yacht line.`,
      yachtDescription: 'Example: a used 40-45 ft motor cruiser with two cabins, compact flybridge and coastal range. A 2m PLN budget is still a disciplined used-yacht conversation, not a superyacht fantasy.',
      yachtImageAlt: 'Motor yacht on water',
      baseAuction: 'BASE auction',
      modelQ50Base: 'Model q50 BASE',
      modelQ50Helper: 'daily Goose q50 for BASE context; different from Fix II q50',
      shiftedQ50Goose: 'Shifted q50 Goose',
      shiftedQ50GooseHelper: 'q50 after Goose risk shift for today',
      fixIHedge: 'Fix I hedge order',
      guardedHours: (count) => `${count} hedge order h`,
      hedgeOrderLimitRule: 'limit: Buy q60 + 25 / Sell q40 - 25',
      hedgeTooltipNoInput: 'Goose risk management does not yet have enough execution context to show a Fix I hedge.',
      hedgeTooltipNoHedgeIdle: 'No Fix I hedge was generated because the saved BASE context is Idle.',
      hedgeTooltipNoHedgeActive: ({ side, volume }) => `No Fix I hedge was generated: BASE is ${side} ${volume} MW, but Goose risk management did not find executable risky hours for this delivery day.`,
      hedgeTooltipSpikeSuppressed: 'Spike risk was detected, but the Fix I buy-back was not economic. Keeping the BASE exposure had better edge than hedging it on Fix I, so the hedge stayed at 0 MWh.',
      hedgeTooltipWithHedge: ({ side, hours, volume, rows, guardLabel, guardInfo }) => `${guardLabel}: ${guardInfo} Goose risk management marked ${hours} risky hour${hours === 1 ? '' : 's'} for the ${side} BASE position and created ${volume} MWh of Fix I hedge orders. Real protection appears only for rows that executed. ${rows ? `Rows: ${rows}.` : ''}`,
      postBaseGuardBreakdownTitle: 'Goose risk-management decision',
      postBaseGuardBreakdownSummary: ({ detected, hedged, skipped }) => `Goose risk management detected ${detected} risky hour${detected === 1 ? '' : 's'}: ${hedged} became Fix I hedge order${hedged === 1 ? '' : 's'}, ${skipped} skipped by economics.`,
      postBaseGuardHedgedRows: 'Hedge order hours',
      postBaseGuardSkippedRows: 'Skipped by economics',
      postBaseGuardEdge: ({ hedgeEdge, targetEdge }) => `Fix I buy-back edge ${hedgeEdge}; keeping q50 edge ${targetEdge}`,
      hedgeExplainButton: 'Why?',
      hedgeExplanationTitle: 'Hedge explanation',
      step3: 'Review Goose baskets and submit before auction',
      step3Body: 'This view stays focused on today: morning Goose risk-management context and the auction baskets it produces.',
      baseSettleRecalcActive: 'Daily Results are being recalculated for this BASE execution input. PnL may still show the previous settle until the task finishes.',
      baseSettleRecalcFailed: 'Automatic BASE settle failed. Daily Results may still show the previous settle.',
      baseSettleRecalcSkippedEarly: 'No automatic settle was started because this is today before 13:40 and Fix II prices are not final yet.',
      baseSettleRecalcSkippedFuture: 'No automatic settle was started because this delivery date is still in the future.',
      hedgeResultTitle: 'Goose risk-management result',
      hedgeResultReady: 'Calculated for this delivery day',
      noHedgeNeededTitle: 'Goose did not generate a Fix I hedge',
      noHedgeNeededBody: 'Goose risk management did not find executable risky hours for a Fix I hedge. The simulation keeps the default BASE context and sends no extra Fix I hedge here.',
      noHedgeNeededBodyForSide: ({ side, sideRiskHours, sideRiskCharge, otherSide, otherRiskHours }) => (
        otherRiskHours > 0
          ? `Goose checks the actual BASE exposure side: ${side}. For that side it has ${sideRiskHours} risk h and ${sideRiskCharge} PLN/MWh risk charge, so Fix I hedge stays at 0 MWh. The ${otherSide} signal (${otherRiskHours} h) does not apply to this position.`
          : `Goose checks the actual BASE exposure side: ${side}. For that side it has ${sideRiskHours} risk h and ${sideRiskCharge} PLN/MWh risk charge, so Fix I hedge stays at 0 MWh.`
      ),
      noBasePositionTitle: 'No BASE position to hedge',
      noBasePositionBody: 'Goose risk can flag risky hours for a potential BASE trade, but a Fix I hedge needs real exposure. Today BASE is Idle / 0 MW, so there is no exposure to remove on Fix I.',
      noHedgeSuppressedTitle: 'Risk detected, but hedge was not economic',
      noHedgeSuppressedBody: 'Goose risk management detected sell-side spike risk, but buying back on Fix I had worse edge than keeping the BASE exposure. The simulation keeps the Fix I hedge at 0 MWh.',
      changeBaseExecution: 'Manual simulation input',
      hideBaseExecutionForm: 'Hide simulation input',
      deleteBaseExecution: 'Remove BASE input',
      deletingBaseExecution: 'Removing...',
      deleteBaseExecutionConfirm: 'Remove saved simulation BASE execution input for this delivery day?',
      deleteBaseExecutionDone: 'BASE execution input removed. The simulation is back to its own BASE decision for this day.',
      executedBaseSide: 'Executed BASE side',
      mw: 'MW',
      basePrice: 'BASE price',
      calculating: 'Calculating...',
      saveHedge: 'Save & recalculate simulation',
      step4: 'Review Goose baskets and submit before auction',
      orderPreview: 'Order preview',
      orderTiming: {
        fix_i: (fmt) => `${fmt || 'Noble'} target: submit by 10:30 Warsaw time, before the Fix I auction gate`,
        fix_ii: (fmt) => `${fmt || 'Noble'} target: submit by 12:00 Warsaw time, before the Fix II order gate`,
        ida1: (fmt) => `${fmt || 'Noble'} target: submit by 15:00 Warsaw time, IDA1 GCT`,
        ida2: (fmt) => `${fmt || 'Noble'} target: submit by 22:00 Warsaw time, IDA2 GCT`,
      },
      orderDeadlines: {
        fix_i: '10:30',
        fix_ii: '12:00',
        ida1: '15:00',
        ida2: '22:00',
      },
      orderHelpers: {
        fix_i: 'Goose live Fix I + risk-management hedge with margin',
        fix_ii: 'Fix II ladder from staged plan',
        fixIIWaitingIda1: 'preliminary - waiting for IDA1 predict',
        ida1Ready: 'IDA1 close rows',
        ida1ClosePreview: 'close preview - waiting for IDA2 predict',
        ida1Pending: 'prediction pending',
        ida2Ready: 'IDA2 handoff rows',
        ida2Pending: 'prediction pending',
      },
      fixIIPendingIda1Title: 'Preliminary Fix II preview',
      fixIIPendingIda1Body: 'Fix II uses quarterly IDA1 quantiles. Until the IDA1 prediction is ready, treat this basket as a staged preview, not the final Noble basket.',
      ida1ClosePreviewTitle: 'IDA1 close preview',
      ida1ClosePreviewBody: 'These rows are only an IDA1 residual-close preview. After IDA2 predict, the runtime can update the IDA2 handoff split and this IDA1 order may change before submission.',
      nobleBadge: 'Noble basket',
      basketBadge: (fmt) => `${fmt} basket`,
      nobleHelper: 'XLSX handoff for auction orders',
      basketHelper: (fmt) => `XLSX handoff for auction orders (${fmt} format)`,
      deadlineLabel: 'Noble by',
      expandOrders: 'Expand order rows',
      collapseOrders: 'Collapse order rows',
      collapsedOrdersHint: (count, label) => `${count} ${label || 'order'} row${count === 1 ? '' : 's'} ready. Expand only when you need to inspect or export the Noble basket.`,
      refreshOrders: 'Refresh orders',
      downloadXlsx: 'Download Noble XLSX',
      downloadBosXlsx: 'Download BOŚ XLSX',
      loadingPreview: (label) => `Loading ${label || 'order'} preview...`,
      noPreview: (label) => `No ${label || 'order'} preview rows yet.`,
      marketCards: {
        fixIReal: 'Fix I real',
        fixIIReal: 'Fix II real',
        ida1Real: 'IDA1 real',
        ida2Real: 'IDA2 real',
        fixIIQ50: 'Fix II q50',
        ida1Q50: 'IDA1 q50',
        ida2Q50: 'IDA2 q50',
        storedForecast: 'stored forecast avg',
        actualReady: 'actual ready',
        actualUnavailable: 'actual not settled yet',
        pending: 'pending',
      },
      executionPathButton: 'Execution path',
      executionPathTitle: "Today's execution path",
      executionPathBody: 'Quick view of auction execution and later residual on the active Goose IDA2 path. Hour rows show Buy/Sell when the side is clear; otherwise they show a neutral close volume. Fix I is financially settled together with Fix II, so the additive Fix I/II result is shown on the Fix II card.',
      executionPathDetails: 'Executed hours',
      executionPathShow: 'Show hours',
      executionPathHide: 'Hide hours',
      executionNoRows: 'No executed rows yet',
      executionPending: 'Pending',
      executionEmpty: 'No fill',
      executionDone: 'Done',
      executionPreview: 'Preview',
      executionPreviewPnl: 'Preview PnL',
      executionFix2FallbackNoticeTitle: 'Daily Results stopped at Fix II',
      executionFix2FallbackNoticeBody: 'IDA1/IDA2 cards below are runtime/order-plan preview only. The official daily settle was finalised on Fix II, so IDA1/IDA2 PnL and volume are not part of Daily Results.',
      executionSettledDetailsMissingTitle: 'Execution path is waiting for day-details',
      executionSettledDetailsMissingBody: 'This day already has an official settle, so order-plan preview is not shown as executed path. Refresh status or wait for day-details.',
      fixIExecutionTitle: 'Fix I fill refresh',
      fixIExecutionBody: 'Fill view only: volume and price from Fix I. Financial result appears after Fix II or later close.',
      fixIExecutionReady: 'Fix I filled',
      fixIExecutionRefreshing: 'Refreshing Fix I fill',
      fixIExecutionWaiting: 'Waiting for Fix I',
      fixIExecutionRefreshButton: 'Refresh Fix I fill',
      fixIExecutionRefreshSuccess: 'Fix I fill refreshed',
      fixIExecutionNoFill: 'Fix I ready, no fill',
      executionAvg: 'Avg',
      executionRows: 'rows',
      executionCarry: 'carry',
      executionPnl: 'Stage PnL',
      executionPnlOpen: 'PnL: waiting for Fix II close',
      executionPnlClosedInFixII: 'PnL settled with Fix II',
      executionFixIIPnl: 'Fix I/II PnL',
      executionBaseMarkFixI: 'BASE mark after Fix I',
      executionTableHeaders: ['Hour', 'BASE', 'Fix I', 'Fix II', 'IDA1', 'IDA2', 'Hour PnL'],
      executionCloseLabel: 'Close',
      executionStageFill: 'Close',
      executionCloseFixI: 'Fix I close',
      executionFinalClose: 'Final close',
      executionHandoffTo: (stage) => `To ${stage}`,
      executionOrderSide: 'Order side',
      executionFixIIActivity: 'Close',
      executionResidualClose: 'residual close',
      executionFixIILadder: 'Fix II matrix execution',
      executionFixIILadderClose: 'Fix II matrix close',
      executionFixIPnlNote: 'Settled through Fix II',
      executionBaseSetup: 'BASE context',
      executionBaseClose: 'BASE close',
      executionBaseHedgeClose: 'BASE hedge close',
      executionBaseCashflow: 'BASE cashflow',
      executionBaseCloseCashflow: 'BASE close cashflow',
      hourPnlLabel: 'Hour PnL',
      expand15mPath: 'Show 15m',
      collapse15mPath: 'Hide 15m',
      quarterPathTitle: (hour) => `15m path for ${hour}`,
      noQuarterPath: 'No quarter-hour breakdown for this hour.',
      curveVolume: 'Curve',
      pathLabel: 'Path',
    },
    baseHedge: {
      eyebrow: 'Goose risk management',
      body: 'Audit the morning Goose risk-management output and its effects: hedge, Fix I BUY control, carry, spike risk, and executed rows. This stays inside our simulation layer and never writes `others.base_action`.',
      deliveryDay: 'Delivery day',
      executedSide: 'Executed side',
      executedMw: 'Executed MW',
      executionPrice: 'Execution price',
      saving: 'Saving...',
      saveCalculate: 'Save & recalculate simulation',
      refreshSavedInput: 'Refresh saved input',
      refreshSavedTitle: 'Reload the saved simulation BASE execution input or historical settled fallback.',
      loadingDecision: 'Loading Goose risk-management state...',
      noDecision: 'No Goose risk-management decision loaded yet.',
      noBaseSetup: 'NO BASE SETUP',
      idleBase: 'IDLE BASE',
      buyBase: 'BUY BASE',
      sellBase: 'SELL BASE',
      skipBase: 'SKIP BASE',
      baseSetup: 'BASE context',
      readiness: 'Readiness',
      ready: 'READY',
      notReady: 'NOT READY',
      baseTradePrice: 'BASE trade price',
      missing: 'Missing',
      q50Avg: 'q50 avg',
      dbPrice: 'DB price',
      used: 'used',
      available: 'available',
      targetProfile: 'Target profile',
      fullBlock: 'full block',
      fixIHedge: 'Fix I hedge order',
      forecastPnl: 'Forecast PnL',
      guardHours: (hours) => `risk ${hours || 0}h`,
      noHedgePlanned: 'No Fix I hedge order planned.',
      settledReplayTitle: 'Goose risk management audit',
      settledReplayBody: 'Shows Goose risk-management decisions, hedge orders, Fix I BUY controls, BASE carry, spike risk, and what actually executed for the selected range. Live rows are context only until the day is settled.',
      dayCount: (count) => `${count} day${count === 1 ? '' : 's'}`,
      liveBadge: 'Live / before settle',
      settledBadge: 'Replay',
      liveStatus: 'live, waiting for settle',
      liveRowNote: 'Live context: Goose risk and order preview. PnL is provisional until the day is settled.',
      noRows: 'No live or settled BASE rows for this range yet.',
      showDetails: 'Show details',
      hideDetails: 'Hide details',
      collapsedHint: 'Open for Goose risk, order and execution details.',
      auditDetails: 'Audit details',
      dayDetails: 'Day details',
      preBaseLoading: 'Loading Goose risk management...',
      preBaseMissing: 'No Goose risk-management snapshot',
      preBaseBuyRiskLabel: 'Buy risk',
      preBaseSellRiskLabel: 'Sell risk',
      columns: {
        date: 'Date',
        preBase: 'Goose risk context',
        decision: 'Decision',
        baseBlock: 'BASE block',
        targetProfile: 'Target profile',
        fixIHedge: 'Hedge order / executed',
        executableBasePnl: 'Executable BASE PnL',
        profileBeforeHedge: 'Profile before hedge',
        plan: 'Plan',
      },
      modelBlock: 'model block',
      counterfactualTarget: 'counterfactual target',
      noHedgeCounterfactual: 'no hedge; counterfactual',
      counterfactual: 'counterfactual',
      noBaseTrade: 'No BASE trade',
      noHedge: 'No hedge',
      preBaseFollowNoPrice: 'no BASE price',
      preBaseFollowOutside: 'outside thresholds',
      preBaseDeltaVsActual: 'vs actual',
      preBaseSummaryTitle: 'Goose risk context',
      preBaseSummaryBody: 'Goose risk score/shift context for BASE and later hedge decisions. This view explains risk; it is not a separate trader guard layer.',
      preBaseSummaryDelta: 'Radar days',
      preBaseSummaryCounterfactual: 'High / very high',
      preBaseSummaryActual: 'Medium',
      preBaseSummaryBetter: 'Low',
      preBaseSummaryWorse: 'Buy risk hours',
      preBaseSummarySkipped: 'Sell risk hours',
      guardCalibrationTitle: 'Risk-management execution audit',
      guardCalibrationBody: 'Range-level audit for Goose risk-management mechanisms. Order volume means what Goose submitted; executed volume is what actually filled on Fix I. Variant PnL is only a calibration replay, not a trading rule.',
      guardDays: 'Risk mgmt days',
      hedgeOrderDays: 'Days with hedge order',
      hedgeOrderVolume: 'Hedge order',
      hedgeExecutedVolume: 'Hedge executed',
      fillRate: 'Fill rate',
      zeroFillDays: 'Zero-fill days',
      buyGuardDays: 'Days with BUY Fix I control',
      buyBlockedHours: 'BUY blocked h',
      buyCappedHours: 'BUY capped h',
      buyGuardVolume: 'BUY blocked/capped volume',
      potentialBuyBeforeGuard: 'Potential BUY before control',
      guardInterventionTitle: 'Risk interventions',
      fixIBuyGuardLabel: 'Goose Fix I risk control',
      fixIBuyBlockedLabel: 'BUY Fix I blocked',
      fixIBuyCappedLabel: 'BUY Fix I capped',
      baseCarryGuardLabel: 'BASE carry control',
      forcedFix2Label: 'Forced to Fix II',
      blockedIda1Label: 'Blocked from IDA1',
      blockedIda2Label: 'Blocked from IDA2',
      guardInterventionHours: (blocked, capped) => `${blocked} h blocked / ${capped} h capped`,
      modeColumn: 'Risk mechanism',
      daysColumn: 'Days',
      orderHoursColumn: 'Signal / order h',
      executedPnlColumn: 'Executed hedge PnL',
      unfilledMarketableColumn: 'Unfilled as marketable',
      betterWorseColumn: 'Better / worse h',
      calibrationPlanColumn: 'Calibration note',
      variantsTitle: 'Limit variant replay',
      variantCurrent: 'Current limits',
      variantPlus: (amount) => `More aggressive +${amount}`,
      variantMarketable: 'Marketable cap',
      variantNoHedge: 'No hedge',
      variantDelta: 'delta vs current',
      noGuardCalibration: 'No Goose risk-management activity in this range.',
      guardModeNotes: {
        spike: 'Spike Guard should stay risk-first: hedge only when fill/edge is sensible.',
        spike_suppressed: 'Correct no-hedge state: risk was detected, but hedge economics rejected it.',
        negative_hard: 'Goose hard mode needs separate calibration because it can remove large BASE blocks.',
        negative_soft: 'Goose soft mode has a small sample here, so avoid tuning from this alone.',
      },
      actionLabels: {
        Buy: 'Buy',
        Sell: 'Sell',
        Idle: 'Idle',
      },
      entryRiskLabels: {
        low: 'Low risk',
        medium: 'Medium risk',
        high: 'High risk',
        blocked: 'Very high risk',
        not_ready: 'Not ready',
        no_base: 'No BASE',
      },
      entryActions: {
        buy: 'Buy BASE',
        sell: 'Sell BASE',
        idle: 'Depends on BASE price',
        not_ready: 'Not ready',
      },
    },
  },
  pl: {
    pageSubtitle: 'Operacyjny widok symulacji dla ścieżki Goose IDA2 przez Fix II, IDA1 i IDA2.',
    languageLabel: 'Język',
    mainNavLabel: 'Główne',
    technicalNavLabel: 'Techniczne',
    modifySimulation: 'Modyfikuj symulację',
    tabs: {
      'lets-trade': {
        label: "Let's trade",
        helper: 'Ekran tradera: Goose risk, koszyki i ścieżka dnia',
      },
      results: {
        label: 'Podsumowanie',
        helper: 'Podsumowanie, benchmarki i skumulowany PnL',
      },
      'daily-results': {
        label: 'Wyniki dzienne',
        helper: 'Dzień po dniu i drill-down',
      },
      radar: {
        label: 'Radar',
        helper: 'Prywatny radar dnia po aktywnym predict modelu',
      },
      'base-simulation': {
        label: 'Risk management',
        helper: 'Goose risk management: poranny Goose risk, hedge Fix I, kontrola BUY Fix I, carry i audyt wykonania',
      },
      overrides: {
        label: 'Modyfikuj BASE',
        helper: 'Ręczne override BASE dla daty symulacji',
      },
      pipeline: {
        label: 'Pipeline',
        helper: 'Status i ręczne triggery',
      },
      predictions: {
        label: 'Predykcje',
        helper: 'Tabela staged forecast',
      },
      data: {
        label: 'Dane',
        helper: 'Dataset wejściowy predykcji',
      },
      audit: {
        label: 'Audyt',
        helper: 'Staged path i spójność orderów',
      },
      orders: {
        label: 'Ordery',
        helper: 'Preview orderów Noble',
      },
      'how-it-works': {
        label: 'Jak to działa',
        helper: 'Metodologia i słownik metryk',
      },
    },
    modifier: {
      pendingText: 'Te wartości nie zostały jeszcze użyte. Uruchom ponownie symulację, żeby odświeżyć Summary i Daily Results.',
      rangeText: (start, end) => `Uruchom replay w pamięci dla zakresu ${start} - ${end}. Summary i Daily Results odświeżą się od razu; zapisane Results pozostaną bez zmian.`,
      modelLabel: 'Model',
      close: 'Zamknij',
      replaying: 'Przeliczanie...',
      run: 'Uruchom zmodyfikowaną symulację',
      pendingTitle: 'Zmieniono ustawienia symulacji',
      pendingBody: 'Uruchom zmodyfikowaną symulację, żeby Summary i Daily Results pokazały wartości z aktualnego formularza.',
      activeTitle: 'Zmodyfikowana symulacja aktywna',
      activeBody: 'Summary i Daily Results są przeliczone w pamięci dla tego zakresu.',
      reset: 'Wróć do zapisanych Results',
      pendingBadge: 'czeka na replay',
      baseSummary: (value) => `BASE ${value || '-'} MW`,
      ida1CapSummary: (value) => `limit IDA1 ${value || '-'} MW`,
      ida1MarginSummary: (value) => `margin IDA1 ${value || '-'} PLN`,
      modes: {
        full_strategy_experimental: 'Goose full strategy',
        overlay_only: 'Goose overlay only',
        overlay_handoff: 'Goose overlay + handoff',
      },
      fields: {
        base_volume: { label: 'Wolumen BASE', hint: 'Domyślnie: 50' },
        fix_i_model_spread: { label: 'Spread modelu Fix I', hint: 'Domyślnie Goose: 5' },
        prediction_filter_margin: { label: 'Margines trend/filter', hint: 'Domyślnie: 10' },
        bucket1_volume: { label: 'Bucket Fix I 1', hint: 'Domyślnie Goose: 40' },
        bucket2_volume: { label: 'Bucket Fix I 2', hint: 'Domyślnie Goose: 40' },
        bucket3_volume: { label: 'Bucket Fix I 3', hint: 'Domyślnie Goose: 40' },
        np_constraint_bucket1: { label: 'NP constraint bucket 1', hint: 'Domyślnie Goose: 0' },
        np_constraint_bucket2: { label: 'NP constraint bucket 2', hint: 'Domyślnie Goose: 0' },
        np_constraint_bucket3: { label: 'NP constraint bucket 3', hint: 'Domyślnie Goose: 0' },
        fix_ii_ida1_bucket1_volume: { label: 'Fix II → IDA1 bucket 1', hint: 'Domyślnie Goose: 20' },
        fix_ii_ida1_bucket2_volume: { label: 'Fix II → IDA1 bucket 2', hint: 'Domyślnie Goose: 40' },
        fix_ii_ida1_bucket3_volume: { label: 'Fix II → IDA1 bucket 3', hint: 'Domyślnie Goose: 60' },
        max_avg_volume_pct: { label: 'Maks. średni wolumen IDA1', hint: 'Domyślnie: 30' },
        max_position: { label: 'Maks. pozycja IDA1', hint: 'Domyślnie: 60' },
        ida1_margin: { label: 'Margin IDA1', hint: 'Domyślnie Goose: 1500' },
        margin_tier1: { label: 'Margin cena < 500', hint: 'Domyślnie Goose: 200' },
        margin_tier2: { label: 'Margin cena 500-700', hint: 'Domyślnie Goose: 300' },
        margin_tier3: { label: 'Margin cena 700-1000', hint: 'Domyślnie Goose: 400' },
        margin_tier4: { label: 'Margin cena > 1000', hint: 'Domyślnie Goose: 500' },
        apply_ridge_shift: { label: 'Ridge shift', hint: 'Domyślnie wariantu: v5 off, v3 on' },
        apply_risk_score: { label: 'Risk score', hint: 'Domyślnie Goose: on' },
        risk_score_model: { label: 'Risk engine', hint: 'Domyślnie Goose: prod-v2.0.0' },
        ida2_margin: { label: 'Margin IDA2', hint: 'Parametr overlay IDA2' },
        ida2_min_edge_per_mwh: { label: 'Min. edge IDA2', hint: 'Domyślnie: 20' },
        base_carry_mode: { label: 'Polityka BASE carry', hint: 'Polityka residualu do IDA2' },
      },
    },
    letsTrade: {
      eyebrow: 'Trader workspace',
      title: "Let's trade",
      subtitle: (date) => `Szybki widok dnia handlu dla delivery ${date}: obraz rynku, poranny Goose risk management z AWS i koszyki orderów.`,
      deliveryDate: 'Data delivery',
      refresh: 'Odśwież widok tradera',
      flow: {
        marketTitle: 'Obraz dnia',
        marketBody: 'Wynik, miesiąc, BASE, realne ceny i q50 modelu w jednym miejscu',
        entryTitle: 'Goose risk management',
        entryBody: 'Wynik Goose Risk Managment przed aukcją BASE',
        ordersTitle: 'Koszyk Noble',
        ordersBody: 'Sprawdź deadline, rozwiń wiersze tylko gdy trzeba',
      },
      step1: 'Obraz dnia',
      step1Title: 'Przegląd dnia',
      dayRadarSummary: 'Goose risk',
      dayRadarOpen: 'Otwórz risk',
      dayRadarLoading: 'Ładowanie Goose risk...',
      dayRadarMissing: 'Brak Goose risk',
      dayRadarWaiting: 'Czeka na predict',
      dayRadarReady: 'Goose risk gotowy',
      dayRadarError: 'Goose risk niedostępny',
      dayRadarVerdicts: {
        normal: 'Normalnie',
        cautious: 'Ostrożnie',
        avoid: 'Unikać',
        waiting: 'Czeka',
      },
      step2: 'Dzisiejszy sygnał Goose risk',
      step2Body: 'To sekcja tylko do odczytu. Symulacja zostawia tutaj domyślny kontekst BASE 50 MW, a Goose pokazuje wyłącznie poranny sygnał ryzyka, który później wpływa na ścieżkę dnia i koszyki.',
      entrySummaryTitle: 'Co Goose sugeruje dzisiaj',
      entrySummaryDefaultBase: 'Domyślny BASE w symulacji: 50 MW',
      entrySummaryDefaultBaseHelper: 'Model zmieniamy tylko przez Modify simulation.',
      entryRecommendation: 'Status Goose risk',
      entryBuyThreshold: 'Collapse risk Buy',
      entrySellThreshold: 'Sell-side risk',
      entrySuggestedVolume: 'Shifted q50 Goose',
      entryRiskCharge: 'Średni shift ryzyka',
      entryRiskHours: 'Godziny shiftu',
      entryTriggerTitle: 'Dzisiejszy sygnał Goose',
      entryTriggerIntro: 'Próg modelu:',
      entryTriggerBuyPrefix: 'Buy przy',
      entryTriggerSellPrefix: 'Sell przy',
      entryTriggerBuySuffix: 'albo niżej',
      entryTriggerSellSuffix: 'albo wyżej',
      entryTriggerUnit: 'PLN/MWh',
      entryTriggerBody: ({ buyPrice, sellPrice }) => `Tylko warunkowo: Buy przy ${buyPrice} albo niżej; Sell przy ${sellPrice} albo wyżej.`,
      entryTriggerUnavailable: 'Dzisiejszy sygnał Goose nie jest jeszcze gotowy.',
      entryWhy: 'Dlaczego?',
      entryWhyTitle: 'Dlaczego Goose risk tak zadziałał',
      entryIdleWhyTitle: 'Dlaczego decyzja czeka na cenę',
      entryIdleWhyBody: ({ buyPrice, sellPrice }) => `Model nie wymusza tutaj Buy ani Sell. Cena z aukcji BASE jest triggerem: Buy rozważamy dopiero przy ${buyPrice} albo niżej, Sell przy ${sellPrice} albo wyżej, a między tymi progami zostajemy Idle.`,
      entryMissing: 'Goose risk dla BASE nie jest jeszcze gotowy. Brakuje predykcji albo planu godzinowego.',
      entryRadarObserveOnly: 'To wynik Goose risk management: koryguje tylko kontekst symulacji i nie tworzy na tym ekranie ręcznej decyzji BASE.',
      entryNeutralBuffer: (value) => `bufor modelu Goose ${value} PLN/MWh`,
      entryPriceTriggers: ({ buyPrice, sellPrice }) => `Kupuj tylko przy ${buyPrice} albo niżej; sprzedawaj przy ${sellPrice} albo wyżej`,
      entryVolumeSummary: ({ buyVolume, sellVolume }) => `Kup ${buyVolume} / Sprzedaj ${sellVolume}`,
      entryFullBlockHours: (hours) => `pełne ${hours} h`,
      entryStrongSpikeHours: (hours) => `mocne ${hours} h`,
      entryBuyExplain: ({ hours, fullBlock, charge }) => `Goose risk Buy: ${hours} h ze shiftem, ${fullBlock} h z pełnym collapse shiftem, średnio ${charge} PLN/MWh.`,
      entrySellExplain: ({ hours, strong, charge }) => `Sell-side risk: ${hours} h sygnału, ${strong} mocne godziny, ${charge} PLN/MWh.`,
      entryNoRiskHumanExplain: 'Goose nie dostał dla tego dnia aktywnego risk score/shift, więc modelowy warunek BASE używa q50 bez dodatkowej korekty collapse risk.',
      entryBuyRiskHumanExplain: ({ hours, fullBlock, charge, hoursLabel }) => [
        'Goose czyta `predictions.risk_score` i zamienia go na ujemny shift kwantyli dla godzin z collapse risk.',
        'Ten shift wchodzi do średniego q50 używanego w modelowym warunku wejścia w BASE.',
        `Tutaj Goose widzi ${hours} h ze shiftem${fullBlock ? `, w tym ${fullBlock} h z pełnym shiftem collapse` : ''}; lista godzin: ${hoursLabel}. Średni shift wynosi ${charge} PLN/MWh.`,
        'Panel pokazuje decyzję Goose risk management sprzed BASE; nie zapisuje ręcznego override BASE.',
      ],
      entrySellRiskHumanExplain: ({ hours, strong, charge, hoursLabel }) => [
        'Goose risk score/shift jest dziś głównie collapse-risk dla strony Buy.',
        'Sell-side spike jest pokazywany dalej w risk management, gdy Goose wykrywa ekonomiczny albo nieekonomiczny odkup.',
        `Dla tej karty sygnał Sell ma ${hours} h${strong ? `, z czego ${strong} mocne` : ''}; lista godzin: ${hoursLabel}. Wartość pomocnicza: ${charge} PLN/MWh.`,
        'To nie jest ręczne wejście w BASE, tylko audyt porannego Goose risk.',
      ],
      entryRadarSideSummary: ({ buyHours, sellHours }) => {
        const dominantSide = sellHours >= buyHours ? 'Sell' : 'Buy';
        const dominantHours = Math.max(buyHours, sellHours);
        return dominantHours > 0
          ? `Aktywny Goose risk: strona ${dominantSide} (${dominantHours} h).`
          : 'Brak aktywnego Goose risk shift w tym dniu.';
      },
      entryRiskLabels: {
        low: 'Niskie ryzyko',
        medium: 'Uwaga: ryzyko',
        high: 'Wysokie ryzyko',
        blocked: 'Bardzo wysokie ryzyko',
        not_ready: 'Brak danych',
        no_base: 'Brak BASE',
      },
      entryActions: {
        buy: 'Buy BASE',
        sell: 'Sell BASE',
        idle: 'Decyzja zależy od ceny BASE',
        not_ready: 'Brak decyzji',
      },
      loadingMarket: 'Ładowanie widoku rynku...',
      dayPnl: 'Wynik strategii',
      dayPnlFallback: (date) => `Live / prowizorycznie dla delivery ${date}`,
      dayPnlFix1Mark: 'Prowizorycznie po Fix I: BASE oznaczony realną ceną Fix I',
      dayPnlLiveStage: (stage) => `Live / prowizorycznie po ${stage}`,
      dayPnlWaitingFixI: (date) => `Czeka na realny Fix I albo settle dla delivery ${date}`,
      dayPnlAfterFees: (value) => `Po fees ${value}`,
      monthPnl: 'Obecny miesiąc',
      monthPnlHelper: (days) => `${days} rozliczone dni`,
      yachtButton: 'Czy mamy już jacht?',
      yachtTarget: 'Cel: 2 000 000 PLN w obecnym miesiącu',
      yachtHappyTitle: 'Tak. Można oglądać mariny.',
      yachtSadTitle: 'Jeszcze nie.',
      yachtHappyBody: (value) => `Obecny miesiąc ma ${value}, więc próg jachtowy jest przebity.`,
      yachtSadBody: (value, missing) => `Obecny miesiąc ma ${value}. Do linii 2 mln PLN brakuje jeszcze ${missing}.`,
      yachtDescription: 'Przykład: używany motor cruiser 40-45 ft z dwiema kabinami, małym flybridge i zasięgiem na komfortowe rejsy przybrzeżne. Budżet 2 mln PLN to sensowny używany jacht, jeszcze nie superjacht.',
      yachtImageAlt: 'Jacht motorowy na wodzie',
      baseAuction: 'BASE auction',
      modelQ50Base: 'Model q50 BASE',
      modelQ50Helper: 'dzienny Goose q50 dla kontekstu BASE; to nie jest Fix II q50',
      shiftedQ50Goose: 'Shifted q50 Goose',
      shiftedQ50GooseHelper: 'q50 po dzisiejszym Goose risk shift',
      fixIHedge: 'Order hedge Fix I',
      guardedHours: (count) => `${count} h orderu hedge`,
      hedgeOrderLimitRule: 'limit: Buy q60 + 25 / Sell q40 - 25',
      hedgeTooltipNoInput: 'Goose risk management nie ma jeszcze wystarczającego kontekstu wykonania, żeby pokazać hedge Fix I.',
      hedgeTooltipNoHedgeIdle: 'Nie wygenerowano hedge Fix I, bo zapisany kontekst BASE to Idle.',
      hedgeTooltipNoHedgeActive: ({ side, volume }) => `Nie wygenerowano hedge Fix I: BASE jest ${side} ${volume} MW, ale Goose risk management nie znalazł wykonalnych ryzykownych godzin dla tej daty delivery.`,
      hedgeTooltipSpikeSuppressed: 'Ryzyko spike zostało wykryte, ale odkup na Fix I nie był ekonomiczny. Zostawienie ekspozycji BASE miało lepszy edge niż hedge na Fix I, więc hedge został 0 MWh.',
      hedgeTooltipWithHedge: ({ side, hours, volume, rows, guardLabel, guardInfo }) => `${guardLabel}: ${guardInfo} Goose risk management oznaczył ${hours} ryzykowne godziny dla pozycji BASE ${side} i wystawił ${volume} MWh orderów hedge Fix I. Realna ochrona pojawia się tylko tam, gdzie order się wykonał. ${rows ? `Wiersze: ${rows}.` : ''}`,
      postBaseGuardBreakdownTitle: 'Decyzja Goose risk management',
      postBaseGuardBreakdownSummary: ({ detected, hedged, skipped }) => `Goose risk management wykrył ${detected} h ryzyka: ${hedged} h trafiło do orderu hedge Fix I, ${skipped} h pominięto przez ekonomię.`,
      postBaseGuardHedgedRows: 'Godziny z orderem hedge',
      postBaseGuardSkippedRows: 'Pominięte przez ekonomię',
      postBaseGuardEdge: ({ hedgeEdge, targetEdge }) => `edge odkupu Fix I ${hedgeEdge}; edge zostawienia q50 ${targetEdge}`,
      hedgeExplainButton: 'Dlaczego?',
      hedgeExplanationTitle: 'Dlaczego hedge tak zadziałał',
      step3: 'Sprawdź koszyki Goose i złóż je przed aukcją',
      step3Body: 'Ten widok ma zostać przy dzisiejszym flow: poranny kontekst Goose risk management oraz koszyki aukcyjne, które z niego wynikają.',
      baseSettleRecalcActive: 'Wyniki dzienne przeliczają się po tym wykonaniu BASE. Do zakończenia taska PnL może jeszcze pokazywać poprzedni settle.',
      baseSettleRecalcFailed: 'Automatyczny settle po zmianie BASE nie powiódł się. Wyniki dzienne mogą nadal pokazywać poprzedni settle.',
      baseSettleRecalcSkippedEarly: 'Nie uruchomiłem automatycznego settle, bo to dzisiejszy delivery przed 13:40 i ceny Fix II nie są jeszcze finalne.',
      baseSettleRecalcSkippedFuture: 'Nie uruchomiłem automatycznego settle, bo ta data delivery jest jeszcze w przyszłości.',
      hedgeResultTitle: 'Wynik Goose risk management',
      hedgeResultReady: 'Policzony dla tej daty delivery',
      noHedgeNeededTitle: 'Goose nie wygenerował hedge Fix I',
      noHedgeNeededBody: 'Goose risk management nie znalazł wykonalnych ryzykownych godzin dla hedge Fix I. Symulacja zostawia domyślny kontekst BASE i nie dodaje tu hedge Fix I.',
      noHedgeNeededBodyForSide: ({ side, sideRiskHours, sideRiskCharge, otherSide, otherRiskHours }) => (
        otherRiskHours > 0
          ? `Goose sprawdza faktyczną stronę ekspozycji BASE: ${side}. Dla tej strony ma ${sideRiskHours} h ryzyka i ${sideRiskCharge} PLN/MWh kosztu, więc hedge Fix I zostaje 0 MWh. Sygnał Goose risk po stronie ${otherSide} (${otherRiskHours} h) nie dotyczy tej pozycji.`
          : `Goose sprawdza faktyczną stronę ekspozycji BASE: ${side}. Dla tej strony ma ${sideRiskHours} h ryzyka i ${sideRiskCharge} PLN/MWh kosztu, więc hedge Fix I zostaje 0 MWh.`
      ),
      noBasePositionTitle: 'Brak pozycji BASE do hedge',
      noBasePositionBody: 'Goose risk dla BASE może pokazywać ryzykowne godziny dla potencjalnej gry BASE, ale hedge Fix I wymaga realnej ekspozycji. Dzisiaj BASE jest Idle / 0 MW, więc nie ma ekspozycji do zdjęcia na Fix I.',
      noHedgeSuppressedTitle: 'Ryzyko jest, ale hedge nie był ekonomiczny',
      noHedgeSuppressedBody: 'Goose risk management wykryl sell-side spike risk, ale odkup na Fix I mial gorszy edge niz zostawienie ekspozycji BASE. Symulacja zostawia Fix I hedge na 0 MWh.',
      changeBaseExecution: 'Ręczny input symulacji',
      hideBaseExecutionForm: 'Zwiń input symulacji',
      deleteBaseExecution: 'Usuń input BASE',
      deletingBaseExecution: 'Usuwanie...',
      deleteBaseExecutionConfirm: 'Usunąć zapisany input wykonania BASE dla tej daty delivery?',
      deleteBaseExecutionDone: 'Input wykonania BASE usuniety. Symulacja wraca do wlasnej decyzji BASE dla tego dnia.',
      executedBaseSide: 'Strona BASE',
      mw: 'MW',
      basePrice: 'BASE price',
      calculating: 'Liczenie...',
      saveHedge: 'Zapisz i przelicz symulację',
      step4: 'Zobacz koszyki Goose i złóż je przed aukcją',
      orderPreview: 'Order preview',
      orderTiming: {
        fix_i: (fmt) => `Target ${fmt || 'Noble'}: złóż do 10:30 czasu Warszawa, przed bramką aukcji Fix I`,
        fix_ii: (fmt) => `Target ${fmt || 'Noble'}: złóż do 12:00 czasu Warszawa, przed bramką zleceń Fix II`,
        ida1: (fmt) => `Target ${fmt || 'Noble'}: złóż do 15:00 czasu Warszawa, GCT IDA1`,
        ida2: (fmt) => `Target ${fmt || 'Noble'}: złóż do 22:00 czasu Warszawa, GCT IDA2`,
      },
      orderDeadlines: {
        fix_i: '10:30',
        fix_ii: '12:00',
        ida1: '15:00',
        ida2: '22:00',
      },
      orderHelpers: {
        fix_i: 'Goose live Fix I + hedge z risk management',
        fix_ii: 'ladder Fix II ze staged plan',
        fixIIWaitingIda1: 'wstępne - czekamy na predict IDA1',
        ida1Ready: 'wiersze close IDA1',
        ida1ClosePreview: 'close preview - czekamy na predict IDA2',
        ida1Pending: 'predykcja pending',
        ida2Ready: 'wiersze handoff IDA2',
        ida2Pending: 'predykcja pending',
      },
      fixIIPendingIda1Title: 'Wstępny podgląd Fix II',
      fixIIPendingIda1Body: 'Fix II korzysta z kwartalnych kwantyli IDA1. Dopóki predykcja IDA1 nie jest gotowa, traktuj ten koszyk jako staged preview, a nie finalny koszyk Noble.',
      ida1ClosePreviewTitle: 'IDA1 close preview',
      ida1ClosePreviewBody: 'Te wiersze są tylko preview domknięcia residualu na IDA1. Po predict IDA2 runtime może zaktualizować split handoff do IDA2, więc ten order IDA1 może się jeszcze zmienić przed złożeniem.',
      nobleBadge: 'Koszyk Noble',
      basketBadge: (fmt) => `Koszyk ${fmt}`,
      nobleHelper: 'XLSX do złożenia orderów aukcyjnych',
      basketHelper: (fmt) => `XLSX do złożenia orderów aukcyjnych (format ${fmt})`,
      deadlineLabel: 'Noble do',
      expandOrders: 'Rozwiń ordery',
      collapseOrders: 'Zwiń ordery',
      collapsedOrdersHint: (count, label) => `${count} ${label || 'order'} ${count === 1 ? 'wiersz gotowy' : 'wierszy gotowych'}. Rozwiń dopiero, gdy chcesz sprawdzić albo pobrać koszyk Noble.`,
      refreshOrders: 'Odśwież ordery',
      downloadXlsx: 'Pobierz Noble XLSX',
      downloadBosXlsx: 'Pobierz BOŚ XLSX',
      loadingPreview: (label) => `Ładowanie preview ${label || 'order'}...`,
      noPreview: (label) => `Brak orderów ${label || 'order'} w preview.`,
      marketCards: {
        fixIReal: 'Fix I real',
        fixIIReal: 'Fix II real',
        ida1Real: 'IDA1 real',
        ida2Real: 'IDA2 real',
        fixIIQ50: 'Fix II q50',
        ida1Q50: 'IDA1 q50',
        ida2Q50: 'IDA2 q50',
        storedForecast: 'średnia forecastu',
        actualReady: 'actual ready',
        actualUnavailable: 'actual jeszcze nierozliczone',
        pending: 'pending',
      },
      executionPathButton: 'Ścieżka wykonania',
      executionPathTitle: 'Ścieżka wykonania dzisiaj',
      executionPathBody: 'Szybki widok wykonania aukcji i późniejszego residualu. W godzinach pokazujemy Kup/Sprzedaj, gdy strona jest jednoznaczna; w przeciwnym razie neutralne zamknięcie wolumenu. Fix I jest finansowo rozliczany razem z Fix II, więc addytywny wynik Fix I/II pokazujemy na karcie Fix II.',
      executionPathDetails: 'Godziny wykonania',
      executionPathShow: 'Pokaż godziny',
      executionPathHide: 'Ukryj godziny',
      executionNoRows: 'Brak wykonanych wierszy',
      executionPending: 'Czeka',
      executionEmpty: 'Brak wykonania',
      executionDone: 'Gotowe',
      executionPreview: 'Preview',
      executionPreviewPnl: 'PnL preview',
      executionFix2FallbackNoticeTitle: 'Wyniki dzienne zatrzymały się na Fix II',
      executionFix2FallbackNoticeBody: 'Karty IDA1/IDA2 poniżej są tylko runtime/order-plan preview. Oficjalny daily settle został sfinalizowany na Fix II, więc PnL i wolumen IDA1/IDA2 nie wchodzą do Wyników dziennych.',
      executionSettledDetailsMissingTitle: 'Ścieżka czeka na day-details',
      executionSettledDetailsMissingBody: 'Dzień ma już zapisany official settle, więc nie pokazujemy order-plan preview jako wykonanej ścieżki. Odśwież status albo poczekaj na day-details.',
      fixIExecutionTitle: 'Fix I fill refresh',
      fixIExecutionBody: 'Tylko fill: wolumen i cena z Fix I. Wynik finansowy pojawia się dopiero po domknięciu na Fix II albo później.',
      fixIExecutionReady: 'Fix I wykonany',
      fixIExecutionRefreshing: 'Odświeżanie fillu Fix I',
      fixIExecutionWaiting: 'Czeka na Fix I',
      fixIExecutionRefreshButton: 'Odśwież fill Fix I',
      fixIExecutionRefreshSuccess: 'Fill Fix I odświeżony',
      fixIExecutionNoFill: 'Fix I gotowy, brak wykonania',
      executionAvg: 'Śr.',
      executionRows: 'wiersze',
      executionCarry: 'residual',
      executionPnl: 'PnL etapu',
      executionPnlOpen: 'PnL: czeka na zamknięcie Fix II',
      executionPnlClosedInFixII: 'PnL rozliczony razem z Fix II',
      executionFixIIPnl: 'PnL Fix I/II',
      executionBaseMarkFixI: 'BASE mark po Fix I',
      executionTableHeaders: ['Godzina', 'BASE', 'Fix I', 'Fix II', 'IDA1', 'IDA2', 'PnL godziny'],
      executionCloseLabel: 'Zamknięcie',
      executionStageFill: 'Zamknięcie',
      executionCloseFixI: 'Domknięcie Fix I',
      executionFinalClose: 'Finalne domknięcie',
      executionHandoffTo: (stage) => `Do ${stage}`,
      executionOrderSide: 'Strona orderu',
      executionFixIIActivity: 'Zamknięcie',
      executionResidualClose: 'domknięcie residualu',
      executionFixIILadder: 'wykonanie matrix Fix II',
      executionFixIILadderClose: 'domknięcie matrix Fix II',
      executionFixIPnlNote: 'Rozliczane przez Fix II',
      executionBaseSetup: 'Kontekst BASE',
      executionBaseClose: 'Zamknięcie BASE',
      executionBaseHedgeClose: 'Hedge zamknięcie BASE',
      executionBaseCashflow: 'Cashflow BASE',
      executionBaseCloseCashflow: 'Cashflow zamknięcia BASE',
      hourPnlLabel: 'PnL godziny',
      expand15mPath: 'Pokaż 15m',
      collapse15mPath: 'Ukryj 15m',
      quarterPathTitle: (hour) => `Ścieżka 15m dla ${hour}`,
      noQuarterPath: 'Brak rozbicia 15-minutowego dla tej godziny.',
      curveVolume: 'Krzywa',
      pathLabel: 'Ścieżka',
    },
    baseHedge: {
      eyebrow: 'Goose risk management',
      body: 'Audyt porannego Goose risk management oraz jego skutkow: hedge, kontrola BUY Fix I, carry, spike risk i faktyczne wykonanie. To zostaje w naszej warstwie symulacji i nigdy nie zapisuje `others.base_action`.',
      deliveryDay: 'Dzień delivery',
      executedSide: 'Wykonana strona',
      executedMw: 'Wykonane MW',
      executionPrice: 'Cena wykonania',
      saving: 'Zapisywanie...',
      saveCalculate: 'Zapisz i przelicz symulację',
      refreshSavedInput: 'Odśwież zapisany input',
      refreshSavedTitle: 'Odswiez zapisany input wykonania BASE dla symulacji albo historyczny fallback settled.',
      loadingDecision: 'Ładowanie stanu Goose risk management...',
      noDecision: 'Brak załadowanej decyzji Goose risk management.',
      noBaseSetup: 'BRAK SETUPU BASE',
      idleBase: 'IDLE BASE',
      buyBase: 'KUP BASE',
      sellBase: 'SPRZEDAJ BASE',
      skipBase: 'POMIŃ BASE',
      baseSetup: 'Kontekst BASE',
      readiness: 'Gotowość',
      ready: 'GOTOWE',
      notReady: 'NIEGOTOWE',
      baseTradePrice: 'Cena transakcji BASE',
      missing: 'Brak',
      q50Avg: 'średnie q50',
      dbPrice: 'Cena z DB',
      used: 'użyta',
      available: 'dostępna',
      targetProfile: 'Profil docelowy',
      fullBlock: 'pełny blok',
      fixIHedge: 'Order hedge Fix I',
      forecastPnl: 'Prognozowany PnL',
      guardHours: (hours) => `ryzyko ${hours || 0} h`,
      noHedgePlanned: 'Brak zaplanowanego orderu hedge Fix I.',
      settledReplayTitle: 'Audyt Goose risk management',
      settledReplayBody: 'Pokazuje Goose risk dla BASE, decyzje risk management, ordery hedge, kontrolę BUY Fix I, BASE carry, spike risk i faktyczne wykonanie dla wybranego zakresu. Wiersz live jest tylko kontekstem do czasu rozliczenia dnia.',
      dayCount: (count) => `${count} ${count === 1 ? 'dzień' : 'dni'}`,
      liveBadge: 'Live / przed settle',
      settledBadge: 'Replay',
      liveStatus: 'live, czeka na settle',
      liveRowNote: 'Kontekst live: Goose risk i preview orderów. PnL jest prowizoryczny, dopóki dzień nie zostanie rozliczony.',
      noRows: 'Brak wierszy live lub settled BASE dla tego zakresu.',
      showDetails: 'Pokaż szczegóły',
      hideDetails: 'Ukryj szczegóły',
      collapsedHint: 'Rozwiń, żeby zobaczyć Goose risk, ordery i wykonanie.',
      auditDetails: 'Szczegóły audytu',
      dayDetails: 'Szczegóły dnia',
      preBaseLoading: 'Ładowanie Goose risk management...',
      preBaseMissing: 'Brak snapshotu Goose risk management',
      preBaseBuyRiskLabel: 'Collapse risk Buy',
      preBaseSellRiskLabel: 'Sell-side risk',
      columns: {
        date: 'Data',
        preBase: 'Kontekst Goose risk',
        decision: 'Decyzja',
        baseBlock: 'Blok BASE',
        targetProfile: 'Profil docelowy',
        fixIHedge: 'Order hedge / wykonany',
        executableBasePnl: 'Wykonalny BASE PnL',
        profileBeforeHedge: 'Profil przed hedge',
        plan: 'Plan',
      },
      modelBlock: 'blok modelu',
      counterfactualTarget: 'kontrfaktyczny profil',
      noHedgeCounterfactual: 'bez hedge; kontrfaktycznie',
      counterfactual: 'kontrfaktycznie',
      noBaseTrade: 'Brak trade BASE',
      noHedge: 'Brak hedge',
      preBaseFollowNoPrice: 'brak ceny BASE',
      preBaseFollowOutside: 'poza progami',
      preBaseDeltaVsActual: 'vs faktycznie',
      preBaseSummaryTitle: 'Kontekst Goose risk',
      preBaseSummaryBody: 'Kontekst z risk score/shift Goose dla BASE i późniejszych decyzji hedge. To opis ryzyka, nie osobna warstwa guarda traderowego.',
      preBaseSummaryDelta: 'Dni z Goose risk',
      preBaseSummaryCounterfactual: 'Wysokie / bardzo wysokie',
      preBaseSummaryActual: 'Średnie ryzyko',
      preBaseSummaryBetter: 'Niskie ryzyko',
      preBaseSummaryWorse: 'Godziny shiftu Buy',
      preBaseSummarySkipped: 'Godziny risk Sell',
      guardCalibrationTitle: 'Audyt wykonania risk management',
      guardCalibrationBody: 'Audyt mechanizmów Goose risk management dla wybranego zakresu. Order oznacza wolumen wystawiony przez Goose; wykonany oznacza faktyczny fill na Fix I. Warianty limitów to replay kalibracyjny, nie nowa reguła tradingowa.',
      guardDays: 'Dni z risk mgmt',
      hedgeOrderDays: 'Dni z hedge order',
      hedgeOrderVolume: 'Order hedge',
      hedgeExecutedVolume: 'Hedge wykonany',
      fillRate: 'Fill rate',
      zeroFillDays: 'Dni bez fillu',
      buyGuardDays: 'Dni z kontrolą BUY Fix I',
      buyBlockedHours: 'Godziny BUY zablokowane',
      buyCappedHours: 'Godziny BUY ograniczone',
      buyGuardVolume: 'Wolumen BUY zablok./ogran.',
      potentialBuyBeforeGuard: 'Potencjalny BUY przed kontrolą',
      guardInterventionTitle: 'Interwencje ryzyka',
      fixIBuyGuardLabel: 'Goose kontrola BUY Fix I',
      fixIBuyBlockedLabel: 'BUY Fix I zablokowany',
      fixIBuyCappedLabel: 'BUY Fix I limit',
      baseCarryGuardLabel: 'BASE carry control',
      forcedFix2Label: 'Wymuszone na Fix II',
      blockedIda1Label: 'Zablokowane przed IDA1',
      blockedIda2Label: 'Zablokowane przed IDA2',
      guardInterventionHours: (blocked, capped) => `${blocked} h blokady / ${capped} h limitu`,
      modeColumn: 'Mechanizm ryzyka',
      daysColumn: 'Dni',
      orderHoursColumn: 'Sygnał / order h',
      executedPnlColumn: 'PnL wykonanego hedge',
      unfilledMarketableColumn: 'Niewykonane jako market',
      betterWorseColumn: 'Lepsze / gorsze h',
      calibrationPlanColumn: 'Notatka kalibracyjna',
      variantsTitle: 'Replay wariantów limitu',
      variantCurrent: 'Obecne limity',
      variantPlus: (amount) => `Agresywniej +${amount}`,
      variantMarketable: 'Marketable cap',
      variantNoHedge: 'Bez hedge',
      variantDelta: 'delta vs obecnie',
      noGuardCalibration: 'Brak aktywności Goose risk management w tym zakresie.',
      guardModeNotes: {
        spike: 'Spike Guard powinien zostać risk-first: hedge tylko tam, gdzie fill/edge ma sens.',
        spike_suppressed: 'Poprawny stan bez hedge: ryzyko wykryte, ale ekonomia odrzuciła hedge.',
        negative_hard: 'Goose hard wymaga osobnej kalibracji, bo mocno redukuje blok BASE.',
        negative_soft: 'Goose soft ma małą próbkę w tym zakresie, więc nie stroimy go wyłącznie z tych danych.',
      },
      actionLabels: {
        Buy: 'Kup',
        Sell: 'Sprzedaj',
        Idle: 'Idle',
      },
      entryRiskLabels: {
        low: 'Niskie ryzyko',
        medium: 'Uwaga: ryzyko',
        high: 'Wysokie ryzyko',
        blocked: 'Bardzo wysokie ryzyko',
        not_ready: 'Brak danych',
        no_base: 'Brak BASE',
      },
      entryActions: {
        buy: 'KUP BASE',
        sell: 'SPRZEDAJ BASE',
        idle: 'Decyzja zależy od ceny',
        not_ready: 'Brak decyzji',
      },
    },
  },
};

const PIPELINE_TECH_COPY = {
  en: {
    simulationDate: 'Simulation Date',
    refreshStatus: 'Refresh Status',
    loading: 'Loading...',
    refreshTitle: 'Reload status, staged predict metadata, and pipeline state for selected date',
    run: 'Run',
    pipelineStatus: 'Pipeline Status',
    loadingPipeline: 'Loading pipeline data for the selected date...',
    noRun: 'No simulation run found for this date yet.',
    liveExecutionStatus: 'Live execution',
    noLiveExecution: 'No live execution status found yet.',
    executionStage: 'Stage',
    executionAttemptMissing: 'Attempt recorded for this stage, but no live values were saved.',
    executionStageWaiting: 'This execution stage has not been calculated yet for this date.',
    executionStageWaitingForPrediction: 'Prediction is ready, but this execution stage has not been calculated yet for this date.',
    fixIRefreshWaiting: 'Fix I refresh has not been run yet for this date.',
    labels: {
      date: 'Date',
      mode: 'Mode',
      status: 'Status',
      stackMode: 'Stack mode',
      started: 'Started',
      finished: 'Finished',
      warning: 'Warning',
      error: 'Error',
      planRows: 'Plan rows',
      averagePrice: 'Average price',
      latestIssue: 'Latest issue',
      lastAttempt: 'Last attempt',
      capacity: 'Capacity',
      fixIIPlan: 'Fix II plan',
      fixIExecution: 'Fix I fill',
      ida1Prediction: 'IDA1 prediction',
      ida2Prediction: 'IDA2 prediction',
      issueDate: 'Issue date',
      updated: 'Updated',
    },
    noPredict: 'No predict status found yet.',
    attempt: 'attempt',
    notRun: 'not run',
    unavailable: 'unavailable',
    predictionsTitle: 'Current prediction',
    predictionsMode: 'Predictions',
    dataMode: 'Data',
    predictionsBody: '`Fix I` shows the market price when available. `Fix II` is the stored forecast leg. `IDA1` and `IDA2` are later forecast enrichments for the same hourly path. Row colors and badges below describe only the `Fix I` arbitrage signal versus the next available leg, not the overall profitability of the full day.',
    dataBody: 'Dataset used to explain the prediction: source availability, model input signals, stored quantiles, and prediction metadata.',
    auditMode: 'Audit',
    auditBody: 'Staged auction path plus the order-to-execution consistency check for every hour and quarter.',
    auditConsistencyTitle: 'Order -> Execution -> Calculation',
    auditConsistencyBody: 'Canonical audit built from Noble/BOŚ order preview, clearing prices, staged execution components, and daily venue/order totals. Hour summary PnL uses the same hour basis as the execution path.',
    auditLoading: 'Loading order audit...',
    auditError: 'Could not load order audit.',
    auditOk: 'Consistent',
    auditMismatch: 'Needs review',
    auditHistoricalReplay: 'Historical replay',
    auditHistoricalReplayBody: 'Historical rows are compared against a preview rebuilt with the current runtime, so version-drift rows are separated from live mismatches.',
    auditNoRows: 'No audit rows for this date yet.',
    inputDatasetTitle: 'Prediction Input Dataset',
    inputDatasetBody: 'Read-only view of the data behind the selected prediction. It combines the S3 master dataset, DB signal features, stored Fix II quantiles, and later IDA1 / IDA2 q50 enrichments.',
    inputDatasetLoading: 'Loading prediction input dataset...',
    inputDatasetError: 'Could not load the prediction input dataset.',
    inputSourcesTitle: 'Input sources',
    sourceRows: 'rows',
    sourceColumns: 'columns',
    datasetTableTitle: 'Hourly input matrix',
    datasetTableBody: 'Same 24-hour delivery day, shown as market/reference values plus the input signals available to the model layer.',
    datasetCoverage: 'Data coverage',
    visibleColumns: 'visible columns',
    hiddenEmptyColumns: 'empty columns hidden',
    stagedPathTitle: 'Staged auction path',
    stagedPathBody: 'Execution audit built from the stored plan. This is useful after the dataset check, but it is not the raw model input table.',
    modelOutputs: 'Model outputs',
    marketContext: 'Market context',
    inputSignals: 'Input signals',
    baseDataset: 'Base dataset',
    readySource: 'ready',
    missingSource: 'missing',
    errorSource: 'error',
    pendingReadiness: 'Fix I prices and the Fix II forecast can still be pending for same-day execution windows; daily results should still be treated as intermediate until full market fixes arrive.',
    loadingPredictionRows: 'Loading staged prediction rows for this date...',
    loadingExecutionRows: 'Loading staged execution data for this date...',
    noPredictions: 'No predictions are available for this date yet, so the enriched execution view will appear later.',
    noExecutionData: 'No execution data are available for this date yet, so the staged auction path will appear later.',
    executionPathTitle: 'Plan vs execution',
    executionPathBody: 'Auction-stage summary from the active Goose IDA2 simulation path. Hour rows show Buy/Sell when the side is clear; otherwise they show a neutral close volume. Fix I is financially settled together with Fix II, so the additive Fix I/II result is shown on the Fix II card.',
    executionPathDetails: 'Executed hours',
    executionPathShow: 'Show hours',
    executionPathHide: 'Hide hours',
    executionNoRows: 'No executed rows yet',
    executionPending: 'Pending',
    executionEmpty: 'No fill',
    executionDone: 'Done',
    executionPreview: 'Preview',
    executionPreviewPnl: 'Preview PnL',
    executionFix2FallbackNoticeTitle: 'Daily Results stopped at Fix II',
    executionFix2FallbackNoticeBody: 'IDA1/IDA2 cards below are runtime/order-plan preview only. The official daily settle was finalised on Fix II, so IDA1/IDA2 PnL and volume are not part of Daily Results.',
    executionSettledDetailsMissingTitle: 'Execution path is waiting for day-details',
    executionSettledDetailsMissingBody: 'This day already has an official settle, so order-plan preview is not shown as executed path. Refresh status or wait for day-details.',
    fixIExecutionTitle: 'Fix I execution audit',
    fixIExecutionBody: 'Quick check of submitted Fix I orders against the realised Fix I fill. This is not settle and does not claim final PnL.',
    fixIExecutionReady: 'Fix I filled',
    fixIExecutionRefreshing: 'Refreshing Fix I fill',
    fixIExecutionWaiting: 'Waiting for Fix I',
    fixIExecutionRefreshButton: 'Refresh Fix I fill',
    fixIExecutionRefreshSuccess: 'Fix I fill refreshed',
    fixIExecutionNoFill: 'Fix I ready, no fill',
    fixIExecutionOpenPosition: 'waiting for Fix II settle',
    executionAvg: 'Avg',
    executionRows: 'rows',
    executionCarry: 'carry',
    executionPnl: 'Stage PnL',
    executionPnlOpen: 'PnL: waiting for Fix II close',
    executionPnlClosedInFixII: 'PnL settled with Fix II',
    executionFixIIPnl: 'Fix I/II PnL',
    executionTableHeaders: ['Hour', 'BASE', 'Fix I', 'Fix II', 'IDA1', 'IDA2', 'Hour PnL'],
    executionCloseLabel: 'Close',
    executionStageFill: 'Close',
    executionCloseFixI: 'Fix I close',
    executionFinalClose: 'Final close',
    executionHandoffTo: (stage) => `To ${stage}`,
    executionOrderSide: 'Order side',
    executionFixIIActivity: 'Close',
    executionResidualClose: 'residual close',
    executionFixIILadder: 'Fix II ladder',
    executionFixIILadderClose: 'Fix II ladder close',
    executionFixIPnlNote: 'Settled through Fix II',
    hedgeExplainButton: 'Why?',
    hedgeExplanationTitle: 'Hedge explanation',
    executionBaseSetup: 'BASE context',
    executionBaseClose: 'BASE close',
    executionBaseHedgeClose: 'BASE hedge close',
    executionBaseCashflow: 'BASE cashflow',
    executionBaseCloseCashflow: 'BASE close cashflow',
    favorableSignal: 'Favorable Fix I arbitrage vs next available leg',
    unfavorableSignal: 'Unfavorable Fix I arbitrage vs next available leg',
    neutralSignal: 'No active Fix I arbitrage signal',
    baseCarryChip: 'shows staged carry actions split by venue when they exist.',
    auctionCellsChip: 'show actual action, volume, and venue price for the staged path.',
    pricesRefsChip: 'combine market prices with model q50 / quantile references for manual checks.',
    noExecution: 'No execution',
    noClose: 'No close',
    showDetails: 'Show details',
    hideDetails: 'Hide details',
    noBaseCarry: 'No BASE carry',
    moreBaseStages: (count) => `+${count} more BASE stages in details`,
    baseCarryPath: (hour) => `BASE carry path for ${hour}`,
    noBaseCarryActivity: 'No BASE carry activity for this hour.',
    pricesAndRefs: 'Prices & model refs',
    fixIMarket: 'Fix I market',
    fixIIMarket: 'Fix II market',
    ida1Market: 'IDA1 market',
    ida2Market: 'IDA2 market',
    fixIIModel: 'Fix II model q50',
    ida1Model: 'IDA1 model q50',
    ida2Model: 'IDA2 model q50',
    fixIIQuantiles: 'Fix II quantiles',
    quarterPathTitle: (hour) => `15m path for ${hour}`,
    noQuarterPath: 'No quarter-hour breakdown for this hour.',
    curveVolume: 'Curve',
    pathLabel: 'Path',
    orderPreviewTitle: 'Order Preview',
    orderPreviewBody: (fmt) => fmt === 'BOŚ'
      ? 'Same v5 sim ladder, laid out section-per-stage with the BOŚ K/S column convention. Use to verify or submit baskets in BOŚ format.'
      : 'v5 sim limit ladder + close orders per stage. Noble format = one row per order with separate K/S sides.',
    exportXlsx: 'Download Noble XLSX',
    exportBosXlsx: 'Download BOŚ XLSX',
    loadingOrderPlan: 'Loading staged prediction rows for preview...',
    noOrderPlan: 'No staged plan is available yet, so Noble preview orders will appear later.',
    loadingFixI: 'Loading Goose Fix I preview rows...',
    loadingIda1: 'Loading Goose IDA1 preview rows...',
    loadingIda2: 'Loading Goose IDA2 preview rows...',
    failedFixI: 'Failed to load Goose Fix I preview rows.',
    failedIda1: 'Failed to load Goose IDA1 preview rows.',
    failedIda2: 'Failed to load Goose IDA2 preview rows.',
    retryFixI: 'Retry Fix I preview',
    retryIda1: 'Retry IDA1 preview',
    retryIda2: 'Retry IDA2 preview',
    previewSource: 'Preview source',
    limitPriceNote: 'Rows with reference/margin use Noble limit prices. Fix I risk-management hedge uses Buy q60 + 25 / Sell q40 - 25; IDA rows use their staged margin.',
    orderColumns: {
      instrument: 'Instrument',
      side: 'Side',
      price: 'Price',
      limitPrice: 'Limit price',
      referenceQ50: 'Reference',
      margin: 'Margin',
      volume: 'Volume',
    },
    predictionColumns: {
      hour: 'Hour',
      executed: 'Executed',
      executedVolFixI: 'Executed Vol @ Fix I',
      closedFixII: 'Closed @ Fix II',
      closedIda1: 'Closed @ IDA1',
      residualIda2: 'Residual to IDA2',
      closedIda2: 'Closed @ IDA2',
      finalVolume: 'Final Volume Contribution',
      loadBand: 'Load Band',
      capacitySource: 'Capacity Source',
      importLimit: 'Import Limit',
      exportLimit: 'Export Limit',
      importCapacity: 'Import Capacity',
      exportCapacity: 'Export Capacity',
      planIssue: 'Plan Issue',
      fixIAuction: 'Fix I auction',
      fixIIAuction: 'Fix II auction',
      ida1Auction: 'IDA1 auction',
      ida2Auction: 'IDA2 auction',
      pricesRefs: 'Prices & model refs',
    },
  },
  pl: {
    simulationDate: 'Data symulacji',
    refreshStatus: 'Odśwież status',
    loading: 'Ładowanie...',
    refreshTitle: 'Odśwież status, metadane predykcji i stan pipeline dla wybranej daty',
    run: 'Uruchom',
    pipelineStatus: 'Status pipeline',
    loadingPipeline: 'Ładowanie danych pipeline dla wybranej daty...',
    noRun: 'Brak uruchomienia symulacji dla tej daty.',
    liveExecutionStatus: 'Wykonanie live',
    noLiveExecution: 'Brak statusu wykonania live dla tej daty.',
    executionStage: 'Etap',
    executionAttemptMissing: 'Próba dla tego etapu istnieje, ale nie zapisano wartości live.',
    executionStageWaiting: 'Ten etap wykonania nie został jeszcze policzony dla tej daty.',
    executionStageWaitingForPrediction: 'Predykcja jest gotowa, ale ten etap wykonania nie został jeszcze policzony dla tej daty.',
    fixIRefreshWaiting: 'Odświeżenie Fix I nie zostało jeszcze uruchomione dla tej daty.',
    labels: {
      date: 'Data',
      mode: 'Tryb',
      status: 'Status',
      stackMode: 'Tryb stacka',
      started: 'Start',
      finished: 'Koniec',
      warning: 'Warning',
      error: 'Błąd',
      planRows: 'Wiersze planu',
      averagePrice: 'Średnia cena',
      latestIssue: 'Ostatnie wydanie',
      lastAttempt: 'Ostatnia próba',
      capacity: 'Capacity',
      fixIIPlan: 'Plan Fix II',
      fixIExecution: 'Fill Fix I',
      ida1Prediction: 'Predykcja IDA1',
      ida2Prediction: 'Predykcja IDA2',
      issueDate: 'Data issue',
      updated: 'Aktualizacja',
    },
    noPredict: 'Brak statusu predict dla tej daty.',
    attempt: 'próba',
    notRun: 'nieuruchomione',
    unavailable: 'niedostępne',
    predictionsTitle: 'Bieżąca predykcja',
    predictionsMode: 'Predykcje',
    dataMode: 'Dane',
    predictionsBody: '`Fix I` pokazuje cenę rynkową, gdy jest dostępna. `Fix II` to zapisany forecast. `IDA1` i `IDA2` są późniejszymi enrichments q50 dla tej samej ścieżki godzinowej. Kolory wierszy opisują tylko sygnał arbitrażu Fix I wobec następnej dostępnej nogi, nie pełną rentowność dnia.',
    dataBody: 'Dataset do wyjaśnienia predykcji: dostępność źródeł, sygnały wejściowe modelu, zapisane kwantyle i metadane predykcji.',
    auditMode: 'Audyt',
    auditBody: 'Staged path aukcyjny oraz kontrola spójności order -> execution -> kalkulacja dla każdej godziny i kwadransa.',
    auditConsistencyTitle: 'Order -> Execution -> Kalkulacja',
    auditConsistencyBody: 'Kanoniczny audyt z preview Noble/BOŚ, cen aukcyjnych, komponentów staged execution i dziennych sum venue/order. PnL w summary godziny używa tej samej podstawy co ścieżka wykonania.',
    auditLoading: 'Ładowanie audytu orderów...',
    auditError: 'Nie udało się załadować audytu orderów.',
    auditOk: 'Spójne',
    auditMismatch: 'Wymaga sprawdzenia',
    auditHistoricalReplay: 'Historyczny replay',
    auditHistoricalReplayBody: 'Historyczne wiersze są porównywane z preview odbudowanym aktualnym runtime, więc rozjazdy wersji są oddzielone od live mismatchy.',
    auditNoRows: 'Brak wierszy audytu dla tej daty.',
    inputDatasetTitle: 'Dataset wejściowy predykcji',
    inputDatasetBody: 'Widok read-only danych stojących za wybraną predykcją. Łączy master dataset z S3, sygnały z DB, zapisane kwantyle Fix II oraz późniejsze enrichments q50 dla IDA1 / IDA2.',
    inputDatasetLoading: 'Ładowanie datasetu wejściowego predykcji...',
    inputDatasetError: 'Nie udało się załadować datasetu wejściowego predykcji.',
    inputSourcesTitle: 'Źródła wejściowe',
    sourceRows: 'wiersze',
    sourceColumns: 'kolumny',
    datasetTableTitle: 'Godzinowa macierz wejściowa',
    datasetTableBody: 'Ten sam delivery day 24h, pokazany jako wartości rynkowe/referencyjne oraz sygnały wejściowe dostępne dla warstwy modelu.',
    datasetCoverage: 'Pokrycie danych',
    visibleColumns: 'widoczne kolumny',
    hiddenEmptyColumns: 'puste kolumny ukryte',
    stagedPathTitle: 'Staged path aukcyjny',
    stagedPathBody: 'Audyt wykonania z zapisanego planu. Przydaje się po sprawdzeniu datasetu, ale nie jest surową tabelą wejściową modelu.',
    modelOutputs: 'Output modelu',
    marketContext: 'Kontekst rynku',
    inputSignals: 'Sygnały wejściowe',
    baseDataset: 'Dataset bazowy',
    readySource: 'gotowe',
    missingSource: 'brak',
    errorSource: 'błąd',
    pendingReadiness: 'Ceny Fix I i forecast Fix II mogą nadal być pending dla dzisiejszych okien wykonania; wyniki dzienne traktuj jako pośrednie do czasu pełnych fixingów.',
    loadingPredictionRows: 'Ładowanie staged prediction rows dla tej daty...',
    loadingExecutionRows: 'Ładowanie staged execution data dla tej daty...',
    noPredictions: 'Brak predykcji dla tej daty; wzbogacony widok pojawi się później.',
    noExecutionData: 'Brak danych wykonania dla tej daty; staged auction path pojawi się później.',
    executionPathTitle: 'Plan vs wykonanie',
      executionPathBody: 'Podsumowanie etapow z aktywnej sciezki Goose IDA2. W godzinach pokazujemy Kup/Sprzedaj, gdy strona jest jednoznaczna; w przeciwnym razie neutralne zamkniecie wolumenu. Fix I jest finansowo rozliczany razem z Fix II, wiec addytywny wynik Fix I/II pokazujemy na karcie Fix II.',
    executionPathDetails: 'Godziny wykonania',
    executionPathShow: 'Pokaż godziny',
    executionPathHide: 'Ukryj godziny',
    executionNoRows: 'Brak wykonanych wierszy',
    executionPending: 'Czeka',
    executionEmpty: 'Brak wykonania',
    executionDone: 'Gotowe',
    executionPreview: 'Preview',
    executionPreviewPnl: 'PnL preview',
    executionFix2FallbackNoticeTitle: 'Wyniki dzienne zatrzymały się na Fix II',
    executionFix2FallbackNoticeBody: 'Karty IDA1/IDA2 poniżej są tylko runtime/order-plan preview. Oficjalny daily settle został sfinalizowany na Fix II, więc PnL i wolumen IDA1/IDA2 nie wchodzą do Wyników dziennych.',
    executionSettledDetailsMissingTitle: 'Ścieżka czeka na day-details',
    executionSettledDetailsMissingBody: 'Dzień ma już zapisany official settle, więc nie pokazujemy order-plan preview jako wykonanej ścieżki. Odśwież status albo poczekaj na day-details.',
    fixIExecutionTitle: 'Fix I execution audit',
    fixIExecutionBody: 'Szybkie porównanie złożonych orderów Fix I z realnym filleem Fix I. To nie jest settle i nie udaje finalnego PnL.',
    fixIExecutionReady: 'Fix I wykonany',
    fixIExecutionRefreshing: 'Odświeżanie fillu Fix I',
    fixIExecutionWaiting: 'Czeka na Fix I',
    fixIExecutionRefreshButton: 'Odśwież fill Fix I',
    fixIExecutionRefreshSuccess: 'Fill Fix I odświeżony',
    fixIExecutionNoFill: 'Fix I gotowy, brak wykonania',
    fixIExecutionOpenPosition: 'czeka na settle Fix II',
    executionAvg: 'Śr.',
    executionRows: 'wiersze',
    executionCarry: 'residual',
    executionPnl: 'PnL etapu',
    executionPnlOpen: 'PnL: czeka na zamknięcie Fix II',
    executionPnlClosedInFixII: 'PnL rozliczony razem z Fix II',
    executionFixIIPnl: 'PnL Fix I/II',
    executionTableHeaders: ['Godzina', 'BASE', 'Fix I', 'Fix II', 'IDA1', 'IDA2', 'PnL godziny'],
    executionCloseLabel: 'Zamknięcie',
    executionStageFill: 'Zamknięcie',
    executionCloseFixI: 'Domknięcie Fix I',
    executionFinalClose: 'Finalne domknięcie',
    executionHandoffTo: (stage) => `Do ${stage}`,
    executionOrderSide: 'Strona orderu',
    executionFixIIActivity: 'Zamknięcie',
    executionResidualClose: 'domknięcie residualu',
    executionFixIILadder: 'wykonanie matrix Fix II',
    executionFixIILadderClose: 'domknięcie matrix Fix II',
    executionFixIPnlNote: 'Rozliczane przez Fix II',
    hedgeExplainButton: 'Dlaczego?',
    hedgeExplanationTitle: 'Dlaczego hedge tak zadziałał',
    executionBaseSetup: 'Setup BASE',
    executionBaseClose: 'Zamknięcie BASE',
    executionBaseHedgeClose: 'Hedge zamknięcie BASE',
    executionBaseCashflow: 'Cashflow BASE',
    executionBaseCloseCashflow: 'Cashflow zamknięcia BASE',
    favorableSignal: 'Korzystny arbitraż Fix I wobec następnej dostępnej nogi',
    unfavorableSignal: 'Niekorzystny arbitraż Fix I wobec następnej dostępnej nogi',
    neutralSignal: 'Brak aktywnego sygnału arbitrażu Fix I',
    baseCarryChip: 'pokazuje staged carry actions rozbite po venue, gdy istnieją.',
    auctionCellsChip: 'pokazują realną akcję, wolumen i cenę venue dla staged path.',
    pricesRefsChip: 'łączą ceny rynkowe z q50 / kwantylami modelu do manualnej kontroli.',
    noExecution: 'Brak wykonania',
    noClose: 'Brak close',
    showDetails: 'Pokaż detale',
    hideDetails: 'Ukryj detale',
    noBaseCarry: 'Brak BASE carry',
    moreBaseStages: (count) => `+${count} więcej etapów BASE w detalach`,
    baseCarryPath: (hour) => `Ścieżka BASE carry dla ${hour}`,
    noBaseCarryActivity: 'Brak aktywności BASE carry dla tej godziny.',
    pricesAndRefs: 'Ceny i referencje modelu',
    fixIMarket: 'Rynek Fix I',
    fixIIMarket: 'Rynek Fix II',
    ida1Market: 'Rynek IDA1',
    ida2Market: 'Rynek IDA2',
    fixIIModel: 'Model q50 Fix II',
    ida1Model: 'Model q50 IDA1',
    ida2Model: 'Model q50 IDA2',
    fixIIQuantiles: 'Kwantyle Fix II',
    quarterPathTitle: (hour) => `Ścieżka 15m dla ${hour}`,
    noQuarterPath: 'Brak rozbicia 15-minutowego dla tej godziny.',
    curveVolume: 'Krzywa',
    pathLabel: 'Ścieżka',
    orderPreviewTitle: 'Preview orderów',
    orderPreviewBody: (fmt) => fmt === 'BOŚ'
      ? 'Ta sama drabinka v5 sim, ułożona sekcja-per-etap z kolumnami K/S w konwencji BOŚ. Do weryfikacji lub składania koszyków w formacie BOŚ.'
      : 'Drabinka limitów + ordery zamknięcia per etap z v5 sim. Format Noble = jeden wiersz na order z osobnymi stronami K/S.',
    exportXlsx: 'Pobierz Noble XLSX',
    exportBosXlsx: 'Pobierz BOŚ XLSX',
    loadingOrderPlan: 'Ładowanie staged prediction rows dla preview...',
    noOrderPlan: 'Brak staged plan; preview orderów Noble pojawi się później.',
    loadingFixI: 'Ładowanie preview Fix I z Goose...',
    loadingIda1: 'Ładowanie preview IDA1 z Goose...',
    loadingIda2: 'Ładowanie preview IDA2 z Goose...',
    failedFixI: 'Nie udało się załadować preview Fix I z Goose.',
    failedIda1: 'Nie udało się załadować preview IDA1 z Goose.',
    failedIda2: 'Nie udało się załadować preview IDA2 z Goose.',
    retryFixI: 'Ponów preview Fix I',
    retryIda1: 'Ponów preview IDA1',
    retryIda2: 'Ponów preview IDA2',
    previewSource: 'Źródło preview',
    limitPriceNote: 'Wiersze z referencją/margin używają limit prices Noble. Hedge Fix I z risk management używa Buy q60 + 25 / Sell q40 - 25; IDA używa swojego staged margin.',
    orderColumns: {
      instrument: 'Instrument',
      side: 'Strona',
      price: 'Cena',
      limitPrice: 'Limit price',
      referenceQ50: 'Referencja',
      margin: 'Margin',
      volume: 'Wolumen',
    },
    predictionColumns: {
      hour: 'Godzina',
      executed: 'Wykonanie',
      executedVolFixI: 'Wolumen @ Fix I',
      closedFixII: 'Close @ Fix II',
      closedIda1: 'Close @ IDA1',
      residualIda2: 'Residual do IDA2',
      closedIda2: 'Close @ IDA2',
      finalVolume: 'Finalny wkład wolumenu',
      loadBand: 'Load band',
      capacitySource: 'Źródło capacity',
      importLimit: 'Limit importu PL',
      exportLimit: 'Limit eksportu PL',
      importCapacity: 'Zdolność importu',
      exportCapacity: 'Zdolność eksportu',
      planIssue: 'Issue planu',
      fixIAuction: 'Aukcja Fix I',
      fixIIAuction: 'Aukcja Fix II',
      ida1Auction: 'Aukcja IDA1',
      ida2Auction: 'Aukcja IDA2',
      pricesRefs: 'Ceny i referencje modelu',
    },
  },
};

const RESULTS_UI_COPY = {
  en: {
    rangeTitle: 'Results range',
    rangeHelper: 'Affects Summary and Daily Results. Modified simulation updates this view in memory only.',
    simulationVariantLabel: 'Simulation variant',
    simulationVariantOptions: {
      gooseV5: 'Goose v5.0.0',
      gooseV3: 'Goose v3.1.0',
      compare: 'Compare',
    },
    from: 'From',
    to: 'To',
    loading: 'Loading...',
    refreshingResults: 'Refreshing results...',
    refreshResults: 'Refresh Results',
    refreshTitle: 'Reload only the results summary and daily tables for the selected date range',
    comparing: 'Comparing...',
    loadingResultsTitle: 'Goose is refreshing results.',
    loadingResultsBody: 'Reloading summary cards and compact chart rows for the selected range.',
    loadingDailyBody: 'Reloading full day-by-day rows for the selected range.',
    loadingCompareTitle: 'Goose is comparing model variants.',
    loadingCompareBody: 'Comparing Goose logic with probabilistic v5.0.0 against v3.1.0 for the selected range.',
    loadingReplayTitle: 'Goose is replaying this preset.',
    loadingReplayBody: 'Running an in-memory replay for the selected range. Saved Results stay unchanged.',
    summaryTitle: 'Results Summary',
    accumulatedChart: {
      title: 'Accumulated PnL',
      body: 'The legend below shows cumulative PnL for the live Goose simulation path and benchmark closes.',
      currentResult: 'Current simulation result',
      mooseIda1Stop: 'Goose IDA1 Stop',
      fix2StopBenchmark: 'Fix II Stop Benchmark',
      tooltipFix2Stop: 'Acc. PnL (Fix II Stop Benchmark)',
      tooltipGooseV3: 'Acc. PnL (Goose v3 IDA1 reference)',
      tooltipGooseV5: 'Acc. PnL (Goose v5 IDA1 reference)',
      tooltipGooseIda1: 'Acc. PnL (Goose IDA1 Stop)',
      tooltipCurrent: 'Acc. PnL (current simulation result)',
    },
    strategyProfitable: 'Strategy result is profitable for the selected range.',
    strategyNegative: 'Strategy result is negative for the selected range.',
    dailyTitle: 'Daily Results',
    dailySubtitle: 'Day-by-day realised flow shown in gross PnL, with actual `IDA1` / `IDA2` outcomes and the `Fix II` benchmark. After-fees values are shown in the summary cards and day details.',
    dailySortBy: 'Sort by',
    dailySortDirection: 'Order',
    dailySortHighestFirst: 'Highest first',
    dailySortLowestFirst: 'Lowest first',
    dailySortNewestFirst: 'Newest first',
    dailySortOldestFirst: 'Oldest first',
    dailySortOptions: {
      date: 'Date',
      result: 'Day result',
      basePnl: 'Base PnL',
      fixPnl: 'Fix I / II PnL',
      ida1Pnl: 'IDA1 PnL',
      ida2Pnl: 'IDA2 PnL',
      volume: 'Volume',
    },
    liveTotalLabel: 'Live provisional total incl. partial settle',
    modifiedTotalLabel: 'Modified simulation total incl. partial settle',
    afterFees: 'After fees',
    fees: 'Fees',
    liveTotalInfo: 'This live total rolls each realised day onto the furthest currently available close: `Fix II` partial, `IDA1` fallback, or final `IDA2`. The benchmark section below keeps separate stop and final bases for apples-to-apples comparison.',
    mixedRangeInfo: ({ realizedDays, partialDays, comparableDays }) => `The live Goose total above includes ${realizedDays} realised day${realizedDays === 1 ? '' : 's'}, including ${partialDays} partial/fallback day${partialDays === 1 ? '' : 's'}. IDA2 overlay uses the ${comparableDays} day${comparableDays === 1 ? '' : 's'} already comparable through IDA2.`,
    strategyProfitPerMwh: 'Strategy Profit per MWh',
    strategyProfitHelper: 'Live provisional average profit per executed MWh across the selected range.',
    strategyProfitInfo: 'This is the clean unit-economics view of the current strategy. Read it as the average realised PLN captured on every traded MWh before comparing the larger benchmark totals below.',
    columns: {
      details: 'Details',
      date: 'Date',
      status: 'Status',
      result: 'Result',
      baseSetup: 'Base setup',
      basePnl: 'Base PnL',
      fixPnl: 'Fix I / II PnL',
      ida1Pnl: 'IDA1 PnL',
      ida2Pnl: 'IDA2 PnL',
      volume: 'Volume',
    },
    view: 'View',
    selectedRange: (days) => `Selected range: ${days} realised day${days === 1 ? '' : 's'}`,
    rangeSnapshot: 'Range Snapshot',
    rangeSnapshotBody: 'Quick operating view for the selected range: profitable days, current BASE posture, and actual strategy-leg PnL for all settled days.',
    byStrategyLeg: 'By strategy leg',
    legPnl: 'Leg PnL (PLN)',
    grossVolume: 'Gross volume (MWh)',
    componentsNote: 'These are actual leg/component PnLs for all settled days — not stop benchmarks. The Benchmark section below shows counterfactual stop-benchmarks and is computed only on IDA2-comparable days (days where fix2, IDA1-benchmark, and IDA2-close data are all available). Snapshot covers every settled day; Benchmark covers the comparable subset — this is why their numbers differ.',
    metric: 'Metric',
    total: 'Total',
    profitable: 'Profitable',
    flat: 'Flat',
    loss: 'Loss',
    days: 'Days',
    basePnlDays: 'Base PnL days',
    manualOverrideDays: 'Manual override days',
    noOverrides: 'No overrides',
    baseSetupDirection: 'BASE context direction',
    manualOverrides: 'Manual overrides',
    baseProfitableDays: 'Base profitable days',
    avgDailyPnl: 'Avg daily PnL',
    totalVolume: 'Total Volume',
    totalVolumeBody: 'Total traded volume across the selected range, summed over the live realised execution path.',
    venueChipsNote: 'The venue chips on the right are gross activity by leg and can overlap, so they are not expected to add up to this live-path total.',
    benchmarkStopComparison: 'Benchmark stop comparison',
    ida2ComparableDays: (days) => `${days} IDA2-comparable day${days === 1 ? '' : 's'}`,
    collapseBenchmark: 'Collapse benchmark',
    expandBenchmark: 'Expand benchmark',
    totalVolumeTooltip: 'Total volume',
    profitPerMwh: 'Profit / MWh',
    finalMaeTitle: 'Final MAE on settled prices',
    finalMaeSubtitle: 'q50 forecast vs realised settled prices',
    maeRefreshWarning: 'MAE refresh warning',
    collapseFinalMae: 'Collapse final MAE',
    expandFinalMae: 'Expand final MAE',
    finalMae: 'Final MAE',
    maeValue: 'MAE value',
    bias: 'Bias',
    matchedHours: 'Matched hours',
    matchedDays: 'Matched days',
    avgForecast: 'Avg forecast',
    avgSettled: 'Avg settled',
    noSettledPrices: 'No settled prices matched this leg in the selected range yet.',
    pnlLegsActivity: 'PnL legs and activity',
    tradingDaysSubtitle: ({ tradingDays, ida2CompleteDays }) => `${tradingDays} trading day${Number(tradingDays) === 1 ? '' : 's'} / ${ida2CompleteDays} IDA2-complete`,
    collapsePnlActivity: 'Collapse PnL activity',
    expandPnlActivity: 'Expand PnL activity',
    tradingFinalisedDays: 'Trading / Finalised Days',
    ida2CompleteDays: 'IDA2-complete Days',
    ida1CompleteDays: 'IDA1-complete Days',
    historicalFix2CloseDays: 'Historical Fix II-close Days',
    historicalIda1CloseDays: 'Historical IDA1-close Days',
    ida1FallbackDays: 'IDA1-fallback Days',
    ida2NoIda1Days: 'IDA2-no-IDA1 Days',
    fix2FallbackDays: 'Fix II fallback Days',
    partialFix2SettleDays: 'Partial Fix II settle Days',
    fixIExecutionStatus: 'Fix I filled / waiting for Fix II settle',
    fixIExecutionStatusShort: 'Fix I filled',
    fix2BetterThanIda1Days: 'Fix II better-than-IDA1 Days',
    ida1BetterThanFix2Days: 'IDA1 better-than-Fix II Days',
    loadingDayDetails: 'Loading day details...',
    noDayDetails: 'No hourly drill-down available for this day.',
    noSimulationResults: 'No simulation results available for this range yet.',
    settledFromPlan: 'Settled from predictions_sim.fix2_hourly_plan using v5.0.0 Goose flow through Fix II, IDA1, and IDA2.',
    dayResult: 'Strategy result',
    vol: 'Vol',
    avgQ50Forecast: 'Avg q50 forecast',
    avgActualPrices: 'Avg actual prices',
    baseSetupCard: 'Base setup',
    runtimePrice: 'Runtime price',
    source: 'Source',
    venue: 'Venue',
    settledClosed: 'Settled/closed',
    storedOverridePrice: 'Stored override price',
    informationalNotPnl: 'informational, not yet used in PnL',
    fixLegPnl: 'Fix I / Fix II leg PnL',
    ida1LegPnl: 'IDA1 leg PnL',
    ida2LegPnl: 'IDA2 leg PnL',
    mooseIda2Overlay: 'Goose IDA2 overlay',
    candidates: 'Candidates',
    selected: 'Selected',
    mooseRows: 'Goose rows',
    mooseVolume: 'Goose volume',
    mooseOverlayNote: 'This badge describes only the Goose carry overlay. Baseline IDA2 execution can still exist even when the overlay is disabled. The production Goose tabs keep their existing logic.',
    totalBuyValue: 'Total Buy Value',
    totalSellValue: 'Total Sell Value',
    volumeTransactionFees: 'Volume / Transaction Fees (IDA1 flow)',
    activeHours: 'Active Hours',
    positiveNegativeHours: 'Positive / Negative Hours',
    accMooseIda1Stop: 'Acc. Goose IDA1 Stop',
    accMooseIda2Final: 'Acc. Goose IDA2 Final',
    accFixIIStopBenchmark: 'Acc. Fix II Stop Benchmark',
    accIda1StopGoose: 'Acc. Goose IDA1 Stop',
    accFixIIStopGoose: 'Acc. Fix II Stop (Goose live stop)',
    venueBase: 'Venue Base',
    venueFixI: 'Venue Fix I',
    venueFixII: 'Venue Fix II',
    venueIda1: 'Venue IDA1',
    venueIda2: 'Venue IDA2',
    capacitySource: 'Capacity Source',
    issueDate: 'Issue Date',
    loadBands: 'Load Bands',
    blendAlphas: 'Blend Alphas',
    hourlyBreakdown: 'Hourly breakdown',
    hourlyBreakdownBody: 'Hourly forecast, auction actions, volumes, and PnL. Open a single hour when you need the 15-minute path.',
    rows: 'rows',
    collapseHourlyBreakdown: 'Collapse hourly breakdown',
    expandHourlyBreakdown: 'Expand hourly breakdown',
    expand15mPath: 'Show 15m',
    collapse15mPath: 'Hide 15m',
    path15mFor: (hour) => `15m path for ${hour}`,
    path15mEmpty: 'No 15-minute path for this hour.',
    modified: 'Modified',
    modifiedDelta: 'Modified delta',
    modifiedReplayDeltaTitle: 'In-memory replay delta vs saved/default',
    monitorDerivedOverride: 'Monitor-derived override',
    manualOverwrite: 'Manual overwrite',
    baseAction: 'Base action',
    sizing: 'sizing',
    basePrice: 'base price',
    updatedBy: 'Updated by',
    unknown: 'unknown',
    note: 'Note',
    manualRecalcNote: 'Manual override is still recalculating. PnL cards below remain provisional until the staged predict and settle chain completes.',
    editOverride: 'Edit override',
    baseSourceLabels: {
      monitorOverride: 'Overridden from monitor data',
      manualOverride: 'Manual BASE override',
      postBaseExecutionInput: 'BASE execution overridden',
      settledReplay: 'Settled replay',
      settledActivity: 'Venue BASE activity without saved setup',
      simulationDefault: 'Model / simulation default',
      modifiedSimulation: 'Modified simulation',
      requestInput: 'Unsaved request input',
      persistedBaseAction: 'Production BASE signal',
      modelGuard: 'Goose risk',
      baseSimulationSkip: 'Model skipped BASE',
      override: 'Override',
      automatic: 'Model / automatic',
    },
    hourlyHeaders: [
      'Hour',
      'Fix I',
      'Fix II (Q50)',
      'IDA1',
      'IDA2',
      'Q10',
      'Q30',
      'Q50',
      'Q70',
      'Q90',
      'Fix I Action',
      'Fix II Action',
      'IDA1 Action',
      'IDA2 Action',
      'Fix I Vol',
      'Fix II Vol',
      'IDA1 Vol',
      'IDA2 Vol',
      'Final Volume',
      'Bought',
      'Sold',
      'Arb Fix II PnL',
      'Arb IDA1 PnL',
      'Arb IDA2 PnL',
      'Arb Goose IDA1 Stop',
      'Arb Goose IDA2 Final',
      'Goose IDA2 Final vs Goose IDA1 Stop',
      'Fix II Stop Benchmark',
      'Fix II Stop vs Goose IDA1 Stop',
      'Load Band',
      'Alpha',
      'Capacity Source',
    ],
    loadingLower: 'loading...',
  },
  pl: {
    rangeTitle: 'Zakres wyników',
    rangeHelper: 'Wpływa na Podsumowanie i Wyniki dzienne. Zmodyfikowana symulacja odświeża ten widok tylko w pamięci.',
    simulationVariantLabel: 'Wariant symulacji',
    simulationVariantOptions: {
      gooseV5: 'Goose v5.0.0',
      gooseV3: 'Goose v3.1.0',
      compare: 'Porównaj',
    },
    from: 'Od',
    to: 'Do',
    loading: 'Ładowanie...',
    refreshingResults: 'Odświeżanie wyników...',
    refreshResults: 'Odśwież wyniki',
    refreshTitle: 'Odśwież tylko podsumowanie i tabelę dzienną dla wybranego zakresu dat',
    comparing: 'Porównywanie...',
    loadingResultsTitle: 'Goose odświeża wyniki.',
    loadingResultsBody: 'Ładowanie kart podsumowania i kompaktowych wierszy wykresu dla wybranego zakresu.',
    loadingDailyBody: 'Ładowanie pełnych wierszy dzień po dniu dla wybranego zakresu.',
    loadingCompareTitle: 'Goose porównuje warianty modeli.',
    loadingCompareBody: 'Porównanie logiki Goose z modelem probabilistycznym v5.0.0 przeciwko v3.1.0 dla wybranego zakresu.',
    loadingReplayTitle: 'Goose odtwarza ten preset.',
    loadingReplayBody: 'Trwa odtwarzanie w pamięci dla wybranego zakresu. Zapisane wyniki pozostają bez zmian.',
    summaryTitle: 'Podsumowanie wyników',
    accumulatedChart: {
      title: 'Skumulowany PnL',
      body: 'Legenda pokazuje skumulowany PnL dla aktualnej ścieżki Goose live oraz benchmarkowych domknięć.',
      currentResult: 'Aktualny wynik symulacji',
      mooseIda1Stop: 'Goose IDA1 Stop',
      fix2StopBenchmark: 'Benchmark Fix II Stop',
      tooltipFix2Stop: 'Skum. PnL (benchmark Fix II Stop)',
      tooltipGooseV3: 'Skum. PnL (referencja Goose v3 IDA1)',
      tooltipGooseV5: 'Skum. PnL (referencja Goose v5 IDA1)',
      tooltipGooseIda1: 'Skum. PnL (Goose IDA1 Stop)',
      tooltipCurrent: 'Skum. PnL (aktualny wynik symulacji)',
    },
    strategyProfitable: 'Wynik strategii jest dodatni dla wybranego zakresu.',
    strategyNegative: 'Wynik strategii jest ujemny dla wybranego zakresu.',
    dailyTitle: 'Wyniki dzienne',
    dailySubtitle: 'Dzień po dniu: zrealizowany przepływ w PnL brutto, realne wyniki `IDA1` / `IDA2` i benchmark `Fix II`. Wartości po kosztach są na kartach podsumowania i w detalach dnia.',
    dailySortBy: 'Sortuj po',
    dailySortDirection: 'Kolejność',
    dailySortHighestFirst: 'Najwyższe najpierw',
    dailySortLowestFirst: 'Najniższe najpierw',
    dailySortNewestFirst: 'Najnowsze najpierw',
    dailySortOldestFirst: 'Najstarsze najpierw',
    dailySortOptions: {
      date: 'Data',
      result: 'Wynik dnia',
      basePnl: 'BASE PnL',
      fixPnl: 'Fix I / II PnL',
      ida1Pnl: 'IDA1 PnL',
      ida2Pnl: 'IDA2 PnL',
      volume: 'Wolumen',
    },
    liveTotalLabel: 'Wynik live z częściowym rozliczeniem',
    modifiedTotalLabel: 'Zmodyfikowana symulacja z częściowym rozliczeniem',
    afterFees: 'Po kosztach',
    fees: 'Koszty',
    liveTotalInfo: 'Ten wynik live zlicza każdy rozliczony dzień do najdalszego aktualnie dostępnego zamknięcia: częściowy `Fix II`, fallback `IDA1` albo finalne `IDA2`. Sekcja benchmarków niżej trzyma osobno bazy stop i final, żeby porównanie było spójne.',
    mixedRangeInfo: ({ realizedDays, partialDays, comparableDays }) => `Wynik Goose live powyżej obejmuje ${realizedDays} rozliczone dni, w tym ${partialDays} dni częściowe/fallback. IDA2 overlay używa ${comparableDays} dni porównywalnych do IDA2.`,
    strategyProfitPerMwh: 'Zysk strategii na MWh',
    strategyProfitHelper: 'Live średni zysk na wykonany MWh w wybranym zakresie.',
    strategyProfitInfo: 'To czysty widok ekonomiki aktualnej strategii. Czytaj go jako średni zrealizowany PLN złapany na każdym handlowanym MWh przed porównaniem większych benchmarków niżej.',
    columns: {
      details: 'Detale',
      date: 'Data',
      status: 'Status',
      result: 'Wynik',
      baseSetup: 'Setup BASE',
      basePnl: 'BASE PnL',
      fixPnl: 'Fix I / II PnL',
      ida1Pnl: 'IDA1 PnL',
      ida2Pnl: 'IDA2 PnL',
      volume: 'Wolumen',
    },
    view: 'Pokaż',
    selectedRange: (days) => `Wybrany zakres: ${days} rozliczone dni`,
    rangeSnapshot: 'Snapshot zakresu',
    rangeSnapshotBody: 'Szybki widok operacyjny dla zakresu: dni zysku, aktualna postawa BASE i rzeczywisty PnL nóg strategii dla wszystkich rozliczonych dni.',
    byStrategyLeg: 'Po nodze strategii',
    legPnl: 'PnL nogi (PLN)',
    grossVolume: 'Wolumen brutto (MWh)',
    componentsNote: 'To rzeczywiste PnL nóg/komponentów dla wszystkich rozliczonych dni — nie benchmarki stop. Sekcja Benchmark poniżej pokazuje kontrfaktyczne benchmarki stop i jest obliczana wyłącznie na dniach porównywalnych z IDA2 (dni, dla których dostępne są dane fix2, benchmark IDA1 i IDA2 close). Snapshot obejmuje wszystkie rozliczone dni; Benchmark — tylko podzbiór porównywalny. Stąd różnica w liczbach.',
    metric: 'Metryka',
    total: 'Suma',
    profitable: 'Zyskowne',
    flat: 'Flat',
    loss: 'Strata',
    days: 'Dni',
    basePnlDays: 'Dni BASE PnL',
    manualOverrideDays: 'Dni z manual override',
    noOverrides: 'Brak override',
    baseSetupDirection: 'Kierunek setupu BASE',
    manualOverrides: 'Manual override',
    baseProfitableDays: 'Zyskowne dni BASE',
    avgDailyPnl: 'Średni dzienny PnL',
    totalVolume: 'Łączny wolumen',
    totalVolumeBody: 'Łączny wolumen handlu w wybranym zakresie, zsumowany po aktualnej ścieżce realizacji.',
    venueChipsNote: 'Chipy po prawej pokazują aktywność brutto po nogach i mogą się nakładać, więc nie muszą sumować się do tego łącznego wolumenu.',
    benchmarkStopComparison: 'Porównanie benchmarków stop',
    ida2ComparableDays: (days) => `${days} dni porównywalne z IDA2`,
    collapseBenchmark: 'Zwiń benchmark',
    expandBenchmark: 'Rozwiń benchmark',
    totalVolumeTooltip: 'Łączny wolumen',
    profitPerMwh: 'Zysk / MWh',
    finalMaeTitle: 'Finalne MAE na cenach rozliczonych',
    finalMaeSubtitle: 'forecast q50 vs realne ceny rozliczenia',
    maeRefreshWarning: 'Ostrzeżenie odświeżenia MAE',
    collapseFinalMae: 'Zwiń finalne MAE',
    expandFinalMae: 'Rozwiń finalne MAE',
    finalMae: 'Finalne MAE',
    maeValue: 'Wartość MAE',
    bias: 'Bias',
    matchedHours: 'Dopasowane godziny',
    matchedDays: 'Dopasowane dni',
    avgForecast: 'Średni forecast',
    avgSettled: 'Średnia cena rozliczona',
    noSettledPrices: 'Brak dopasowanych cen rozliczenia dla tej nogi w wybranym zakresie.',
    pnlLegsActivity: 'PnL nóg i aktywność',
    tradingDaysSubtitle: ({ tradingDays, ida2CompleteDays }) => `${tradingDays} dni handlu / ${ida2CompleteDays} dni domknięte do IDA2`,
    collapsePnlActivity: 'Zwiń aktywność PnL',
    expandPnlActivity: 'Rozwiń aktywność PnL',
    tradingFinalisedDays: 'Dni handlu / finalne',
    ida2CompleteDays: 'Dni domknięte do IDA2',
    ida1CompleteDays: 'Dni domknięte do IDA1',
    historicalFix2CloseDays: 'Historyczne dni close Fix II',
    historicalIda1CloseDays: 'Historyczne dni close IDA1',
    ida1FallbackDays: 'Dni fallback IDA1',
    ida2NoIda1Days: 'Dni IDA2 bez IDA1',
    fix2FallbackDays: 'Dni fallback Fix II',
    partialFix2SettleDays: 'Dni częściowego settle Fix II',
    fixIExecutionStatus: 'Fix I wykonany / czeka na settle Fix II',
    fixIExecutionStatusShort: 'Fix I wykonany',
    fix2BetterThanIda1Days: 'Dni Fix II lepszy niż IDA1',
    ida1BetterThanFix2Days: 'Dni IDA1 lepszy niż Fix II',
    loadingDayDetails: 'Ładowanie detali dnia...',
    noDayDetails: 'Brak godzinowego drill-down dla tego dnia.',
    noSimulationResults: 'Brak wyników symulacji dla tego zakresu.',
    settledFromPlan: 'Rozliczone z predictions_sim.fix2_hourly_plan przez flow Goose v5.0.0 przez Fix II, IDA1 i IDA2.',
    dayResult: 'Wynik strategii',
    vol: 'Vol',
    avgQ50Forecast: 'Średni forecast q50',
    avgActualPrices: 'Średnie ceny realne',
    baseSetupCard: 'Setup BASE',
    runtimePrice: 'Cena runtime',
    source: 'Źródło',
    venue: 'Venue',
    settledClosed: 'Rozliczone/zamknięte',
    storedOverridePrice: 'Zapisana cena override',
    informationalNotPnl: 'informacyjnie, jeszcze nie użyte w PnL',
    fixLegPnl: 'PnL nogi Fix I / Fix II',
    ida1LegPnl: 'PnL nogi IDA1',
    ida2LegPnl: 'PnL nogi IDA2',
    mooseIda2Overlay: 'Goose IDA2 overlay',
    candidates: 'Kandydaci',
    selected: 'Wybrane',
    mooseRows: 'Wiersze Goose',
    mooseVolume: 'Wolumen Goose',
    mooseOverlayNote: 'Ten badge opisuje tylko overlay carry Goose. Bazowe wykonanie IDA2 może nadal istnieć nawet wtedy, gdy overlay jest wyłączony. Produkcyjne zakładki Goose zachowują obecną logikę.',
    totalBuyValue: 'Łączna wartość kupna',
    totalSellValue: 'Łączna wartość sprzedaży',
    volumeTransactionFees: 'Wolumen / opłaty transakcyjne (flow IDA1)',
    activeHours: 'Aktywne godziny',
    positiveNegativeHours: 'Godziny pozytywne / negatywne',
    accMooseIda1Stop: 'Skum. Goose IDA1 Stop',
    accMooseIda2Final: 'Skum. Goose IDA2 Final',
    accFixIIStopBenchmark: 'Skum. benchmark Fix II Stop',
    accIda1StopGoose: 'Skum. IDA1 Stop (kalkulacja Goose)',
    accFixIIStopGoose: 'Skum. Fix II Stop (kalkulacja Goose)',
    venueBase: 'Wolumen BASE',
    venueFixI: 'Wolumen Fix I',
    venueFixII: 'Wolumen Fix II',
    venueIda1: 'Wolumen IDA1',
    venueIda2: 'Wolumen IDA2',
    capacitySource: 'Źródło capacity',
    issueDate: 'Data issue',
    loadBands: 'Pasma load',
    blendAlphas: 'Alfy blend',
    hourlyBreakdown: 'Rozbicie godzinowe',
    hourlyBreakdownBody: 'Godzinowy forecast, akcje aukcyjne, wolumeny i PnL. Rozwiń pojedynczą godzinę tylko wtedy, gdy chcesz zobaczyć ścieżkę 15m.',
    rows: 'wierszy',
    collapseHourlyBreakdown: 'Zwiń rozbicie godzinowe',
    expandHourlyBreakdown: 'Rozwiń rozbicie godzinowe',
    expand15mPath: 'Pokaż 15m',
    collapse15mPath: 'Ukryj 15m',
    path15mFor: (hour) => `Ścieżka 15m dla ${hour}`,
    path15mEmpty: 'Brak ścieżki 15-minutowej dla tej godziny.',
    modified: 'Zmodyfikowane',
    modifiedDelta: 'Delta modyfikacji',
    modifiedReplayDeltaTitle: 'Delta replay w pamięci vs zapis/default',
    monitorDerivedOverride: 'Override z monitora',
    manualOverwrite: 'Manual overwrite',
    baseAction: 'Akcja BASE',
    sizing: 'rozmiar',
    basePrice: 'cena BASE',
    updatedBy: 'Zaktualizowane przez',
    unknown: 'nieznany',
    note: 'Notatka',
    manualRecalcNote: 'Manual override nadal się przelicza. Karty PnL niżej są tymczasowe do czasu zakończenia staged predict i settle chain.',
    editOverride: 'Edytuj override',
    baseSourceLabels: {
      monitorOverride: 'Nadpisane z danych monitora',
      manualOverride: 'Manual override',
      postBaseExecutionInput: 'Nadpisano wykonanie BASE',
      settledReplay: 'Settled replay',
      settledActivity: 'Aktywność BASE na venue bez zapisanego setupu',
      simulationDefault: 'Model / domyślna symulacja',
      modifiedSimulation: 'Zmodyfikowana symulacja',
      requestInput: 'Input roboczy bez zapisu',
      persistedBaseAction: 'Sygnał BASE z produkcji',
        modelGuard: 'Goose risk',
      baseSimulationSkip: 'Model pominął BASE',
      override: 'Override',
      automatic: 'Model / automatycznie',
    },
    hourlyHeaders: [
      'Godzina',
      'Fix I',
      'Fix II (Q50)',
      'IDA1',
      'IDA2',
      'Q10',
      'Q30',
      'Q50',
      'Q70',
      'Q90',
      'Akcja Fix I',
      'Akcja Fix II',
      'Akcja IDA1',
      'Akcja IDA2',
      'Wol. Fix I',
      'Wol. Fix II',
      'Wol. IDA1',
      'Wol. IDA2',
      'Wolumen finalny',
      'Kupione',
      'Sprzedane',
      'Arb Fix II PnL',
      'Arb IDA1 PnL',
      'Arb IDA2 PnL',
      'Arb Goose IDA1 Stop',
      'Arb Goose IDA2 Final',
      'Goose IDA2 Final vs Goose IDA1 Stop',
      'Benchmark Fix II Stop',
      'Fix II Stop vs Goose IDA1 Stop',
      'Load band',
      'Alpha',
      'Źródło capacity',
    ],
    loadingLower: 'ładowanie...',
  },
};

const BASE_HEDGE_GUARD_COPY = {
  en: {
    softLabel: 'Goose risk · soft',
    hardLabel: 'Goose risk · hard',
    spikeLabel: 'Goose spike risk',
    spikeSuppressedLabel: 'Spike risk, no hedge',
    fixIBuyGuardLabel: 'Goose Fix I risk control',
    buyBlockedShort: 'BUY Fix I blocked',
    buyCappedShort: 'BUY Fix I capped',
    buyGuardInfo: 'Goose risk management blocked or capped a normal Fix I BUY order. This is an active risk intervention, not a hedge order or hedge PnL.',
    softShort: 'Risk soft',
    hardShort: 'Risk hard',
    spikeShort: 'Spike',
    spikeSuppressedShort: 'Spike held',
    softInfo: 'Goose soft mode keeps a larger BASE exposure, usually 35/45 MW, while still limiting negative-price risk.',
    hardInfo: 'Goose hard mode cuts negative-price risk more aggressively and can reduce exposure to 20/10/0 MW when the model sees collapse-pricing risk.',
    spikeInfo: 'protects a Sell BASE position when the model sees expensive spike hours and buy-back risk on Fix I.',
    spikeSuppressedInfo: 'detected expensive spike hours, but the Fix I buy-back had worse economics than keeping the BASE exposure, so no hedge was placed.',
    hybridSoft: 'hybrid activated soft',
    policy: 'Policy',
    title: 'Goose risk management',
    base: 'BASE',
    fixIHedge: 'Fix I risk-management order',
    guardedHours: 'Risk signal hours',
    hedgeOrders: 'Risk-management orders',
    hedgeExecuted: 'Executed risk-management volume',
    hedgeSubmittedOnly: 'Submitted without execution',
    hedgePnl: 'Risk-management PnL',
    profileBeforeHedge: 'BASE profile before risk management',
    executableBasePnl: 'BASE result after risk management',
    hedgeHours: 'Risk-management hours',
    interventionHours: 'Risk intervention hours',
    executedBadge: 'EXEC',
    submittedBadge: 'SUB',
    blockedBadge: 'BLOCKED',
    cappedBadge: 'CAPPED',
    suppressedBadge: 'SUPPRESSED',
  },
  pl: {
    softLabel: 'Ryzyko Goose · soft',
    hardLabel: 'Ryzyko Goose · hard',
    spikeLabel: 'Goose spike risk',
    spikeSuppressedLabel: 'Ryzyko spike, bez hedge',
    fixIBuyGuardLabel: 'Goose kontrola BUY Fix I',
    buyBlockedShort: 'BUY Fix I zablokowany',
    buyCappedShort: 'BUY Fix I limit',
    buyGuardInfo: 'Goose ryzyko zablokował albo ograniczył zwykły order BUY na Fix I. To aktywna interwencja ryzyka, nie order hedge ani hedge PnL.',
    softShort: 'Ryzyko soft',
    hardShort: 'Ryzyko hard',
    spikeShort: 'Spike',
    spikeSuppressedShort: 'Spike bez hedge',
    softInfo: 'Goose soft ogranicza ryzyko cen ujemnych, ale zostawia większą ekspozycję BASE, zwykle 35/45 MW, żeby nie tracić całego upside.',
    hardInfo: 'Goose hard mocniej tnie ryzyko cen ujemnych i może zejść do 20/10/0 MW, gdy model widzi ryzyko collapse pricingu.',
    spikeInfo: 'chroni pozycję Sell BASE, gdy model widzi drogie godziny spike i ryzyko odkupienia drożej na Fix I.',
    spikeSuppressedInfo: 'wykrył drogie godziny spike, ale odkup na Fix I miał gorszą ekonomię niż zostawienie ekspozycji BASE, więc hedge nie został wystawiony.',
    hybridSoft: 'hybrid aktywował soft',
    policy: 'Policy',
    title: 'Goose risk management',
    base: 'BASE',
    fixIHedge: 'Order risk management Fix I',
    guardedHours: 'Godziny sygnału ryzyka',
    hedgeOrders: 'Ordery risk management',
    hedgeExecuted: 'Wykonany wolumen risk management',
    hedgeSubmittedOnly: 'Wystawione bez wykonania',
    hedgePnl: 'PnL risk management',
    profileBeforeHedge: 'Profil BASE przed risk management',
    executableBasePnl: 'Wynik BASE po risk management',
    hedgeHours: 'Godziny risk management',
    interventionHours: 'Godziny interwencji ryzyka',
    executedBadge: 'EXEC',
    submittedBadge: 'SUB',
    blockedBadge: 'BLOKADA',
    cappedBadge: 'LIMIT',
    suppressedBadge: 'POMINIĘTY',
  },
};

const XLSX_ORDER_THEME = {
  title: {
    fill: { fgColor: { rgb: '0F172A' } },
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 14 },
    alignment: { vertical: 'center' },
  },
  subtitle: {
    fill: { fgColor: { rgb: 'E2E8F0' } },
    font: { bold: true, color: { rgb: '334155' } },
    alignment: { vertical: 'center' },
  },
  header: {
    fill: { fgColor: { rgb: '1E293B' } },
    font: { bold: true, color: { rgb: 'FFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
  buy: { fill: { fgColor: { rgb: 'DCFCE7' } } },
  sell: { fill: { fgColor: { rgb: 'FFE4E6' } } },
  neutral: { fill: { fgColor: { rgb: 'F8FAFC' } } },
  alt: { fill: { fgColor: { rgb: 'F1F5F9' } } },
  active: { fill: { fgColor: { rgb: 'ECFEFF' } } },
  muted: { font: { color: { rgb: '64748B' } } },
  positiveFont: { font: { color: { rgb: '047857' }, bold: true } },
  negativeFont: { font: { color: { rgb: 'BE123C' }, bold: true } },
  border: {
    top: { style: 'thin', color: { rgb: 'CBD5E1' } },
    bottom: { style: 'thin', color: { rgb: 'CBD5E1' } },
    left: { style: 'thin', color: { rgb: 'CBD5E1' } },
    right: { style: 'thin', color: { rgb: 'CBD5E1' } },
  },
};

const mergeXlsxStyle = (...styles) => styles.reduce((acc, style) => {
  if (!style) return acc;
  Object.entries(style).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      acc[key] = { ...(acc[key] || {}), ...value };
    } else {
      acc[key] = value;
    }
  });
  return acc;
}, {});

const sanitizeXlsxSheetName = (name, usedNames = new Set()) => {
  const base = String(name || 'Sheet')
    .replace(/[\\/?*[\]:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 31) || 'Sheet';
  let candidate = base;
  let index = 2;
  while (usedNames.has(candidate)) {
    const suffix = ` ${index}`;
    candidate = `${base.slice(0, Math.max(1, 31 - suffix.length))}${suffix}`;
    index += 1;
  }
  usedNames.add(candidate);
  return candidate;
};

const loadStyledXlsx = async () => {
  const module = await import('xlsx-js-style');
  return module.default || module;
};

const styleXlsxCell = (ws, address, style, numberFormat = null) => {
  if (!ws[address]) {
    ws[address] = { t: 's', v: '' };
  }
  ws[address].s = mergeXlsxStyle(ws[address].s || {}, style);
  if (numberFormat) {
    ws[address].z = numberFormat;
  }
};

const styleXlsxRange = (XLSX, ws, rangeRef, style, numberFormat = null) => {
  const range = XLSX.utils.decode_range(rangeRef);
  for (let row = range.s.r; row <= range.e.r; row += 1) {
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      styleXlsxCell(ws, XLSX.utils.encode_cell({ r: row, c: col }), style, numberFormat);
    }
  }
};

const applyOrderSheetPolish = (XLSX, ws, {
  title,
  subtitle,
  headerRow = 2,
  dataStartRow = 3,
  rowCount = 0,
  colCount = 1,
  sideColIndex = null,
  buyColumnIndexes = [],
  sellColumnIndexes = [],
  activeRowPredicate = null,
  numberColumnIndexes = [],
  volumeColumnIndexes = [],
} = {}) => {
  if (title) {
    styleXlsxRange(XLSX, ws, `A1:${XLSX.utils.encode_col(Math.max(0, colCount - 1))}1`, XLSX_ORDER_THEME.title);
  }
  if (subtitle) {
    styleXlsxRange(XLSX, ws, `A2:${XLSX.utils.encode_col(Math.max(0, colCount - 1))}2`, XLSX_ORDER_THEME.subtitle);
  }
  styleXlsxRange(
    XLSX,
    ws,
    `A${headerRow}:${XLSX.utils.encode_col(Math.max(0, colCount - 1))}${headerRow}`,
    mergeXlsxStyle(XLSX_ORDER_THEME.header, { border: XLSX_ORDER_THEME.border }),
  );
  for (let rowOffset = 0; rowOffset < rowCount; rowOffset += 1) {
    const excelRow = dataStartRow + rowOffset;
    const sideCell = sideColIndex !== null
      ? ws[XLSX.utils.encode_cell({ r: excelRow - 1, c: sideColIndex })]
      : null;
    const sideValue = String(sideCell?.v || '').trim().toUpperCase();
    const rowTone = sideValue === 'K'
      ? XLSX_ORDER_THEME.buy
      : sideValue === 'S'
        ? XLSX_ORDER_THEME.sell
        : activeRowPredicate?.(rowOffset)
          ? XLSX_ORDER_THEME.active
          : rowOffset % 2 === 1
            ? XLSX_ORDER_THEME.alt
            : XLSX_ORDER_THEME.neutral;
    styleXlsxRange(
      XLSX,
      ws,
      `A${excelRow}:${XLSX.utils.encode_col(Math.max(0, colCount - 1))}${excelRow}`,
      mergeXlsxStyle(rowTone, { border: XLSX_ORDER_THEME.border }),
    );
    numberColumnIndexes.forEach((colIndex) => {
      styleXlsxCell(ws, XLSX.utils.encode_cell({ r: excelRow - 1, c: colIndex }), {}, '#,##0.00');
    });
    volumeColumnIndexes.forEach((colIndex) => {
      styleXlsxCell(ws, XLSX.utils.encode_cell({ r: excelRow - 1, c: colIndex }), {}, '#,##0.0');
    });
  }
  for (let rowOffset = 0; rowOffset < rowCount; rowOffset += 1) {
    const excelRow = dataStartRow + rowOffset;
    buyColumnIndexes.forEach((colIndex) => {
      styleXlsxCell(ws, XLSX.utils.encode_cell({ r: excelRow - 1, c: colIndex }), XLSX_ORDER_THEME.buy, '#,##0.00');
    });
    sellColumnIndexes.forEach((colIndex) => {
      styleXlsxCell(ws, XLSX.utils.encode_cell({ r: excelRow - 1, c: colIndex }), XLSX_ORDER_THEME.sell, '#,##0.00');
    });
  }
  ws['!freeze'] = { xSplit: 0, ySplit: headerRow };
  ws['!autofilter'] = {
    ref: `A${headerRow}:${XLSX.utils.encode_col(Math.max(0, colCount - 1))}${Math.max(headerRow, dataStartRow + rowCount - 1)}`,
  };
};

const applyFix2MatrixSheetPolish = (XLSX, ws) => {
  if (!ws || !ws['!ref']) return;
  const range = XLSX.utils.decode_range(ws['!ref']);
  const colCount = Math.max(1, range.e.c + 1);
  const lastCol = XLSX.utils.encode_col(colCount - 1);
  const titleRef = `A1:${lastCol}1`;
  const subtitleRef = `A2:${lastCol}2`;
  const headerRef = `A3:${lastCol}3`;

  styleXlsxRange(XLSX, ws, titleRef, XLSX_ORDER_THEME.title);
  styleXlsxRange(XLSX, ws, subtitleRef, XLSX_ORDER_THEME.subtitle);
  styleXlsxRange(XLSX, ws, headerRef, mergeXlsxStyle(XLSX_ORDER_THEME.header, { border: XLSX_ORDER_THEME.border }));

  for (let col = 1; col < colCount; col += 1) {
    styleXlsxCell(ws, XLSX.utils.encode_cell({ r: 2, c: col }), {}, '#,##0.00');
  }

  for (let row = 3; row <= range.e.r; row += 1) {
    const rowTone = Math.floor((row - 3) / 4) % 2 === 0 ? XLSX_ORDER_THEME.neutral : XLSX_ORDER_THEME.alt;
    styleXlsxRange(
      XLSX,
      ws,
      `A${row + 1}:${lastCol}${row + 1}`,
      mergeXlsxStyle(rowTone, { border: XLSX_ORDER_THEME.border }),
    );
    styleXlsxCell(
      ws,
      XLSX.utils.encode_cell({ r: row, c: 0 }),
      mergeXlsxStyle(XLSX_ORDER_THEME.subtitle, { border: XLSX_ORDER_THEME.border }),
    );
    for (let col = 1; col < colCount; col += 1) {
      const address = XLSX.utils.encode_cell({ r: row, c: col });
      const value = Number(ws[address]?.v);
      if (Number.isFinite(value) && Math.abs(value) > 0.0001) {
        styleXlsxCell(
          ws,
          address,
          mergeXlsxStyle(
            value > 0 ? XLSX_ORDER_THEME.buy : XLSX_ORDER_THEME.sell,
            { border: XLSX_ORDER_THEME.border },
            value > 0 ? XLSX_ORDER_THEME.positiveFont : XLSX_ORDER_THEME.negativeFont,
          ),
          '#,##0.0',
        );
      } else {
        styleXlsxCell(ws, address, mergeXlsxStyle(rowTone, { border: XLSX_ORDER_THEME.border }), '#,##0.0');
      }
    }
  }

  ws['!cols'] = [
    { wch: 10 },
    ...Array.from({ length: Math.max(0, colCount - 1) }, () => ({ wch: 8 })),
  ];
  ws['!freeze'] = { xSplit: 1, ySplit: 3 };
  ws['!autofilter'] = { ref: `A3:${lastCol}${Math.max(3, range.e.r + 1)}` };
};

const createSummarySheet = (XLSX, title, subtitle, rows) => {
  const data = [
    [title],
    [subtitle],
    [],
    ['Arkusz', 'Opis', 'Deadline', 'Wiersze'],
    ...rows.map((row) => [row.sheetName, row.description, row.deadline || '-', Number(row.rowCount || 0)]),
  ];
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 24 }, { wch: 54 }, { wch: 14 }, { wch: 10 }];
  styleXlsxRange(XLSX, ws, 'A1:D1', XLSX_ORDER_THEME.title);
  styleXlsxRange(XLSX, ws, 'A2:D2', XLSX_ORDER_THEME.subtitle);
  styleXlsxRange(XLSX, ws, 'A4:D4', XLSX_ORDER_THEME.header);
  if (rows.length > 0) {
    styleXlsxRange(XLSX, ws, `A5:D${rows.length + 4}`, mergeXlsxStyle(XLSX_ORDER_THEME.neutral, { border: XLSX_ORDER_THEME.border }));
  }
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
  ];
  ws['!freeze'] = { xSplit: 0, ySplit: 4 };
  return ws;
};

function Fix2V5Sim({ darkMode = false }) {
  const formatHourLabel = formatZeroBasedDeliveryHourLabel;
  const formatOneBasedHourLabel = formatOneBasedDeliveryHourLabel;
  const getHourSlotKey = (row = {}, fallbackPrefix = 'hour') => {
    const targetTs = row?.target_ts || row?.targetTs;
    if (targetTs) return String(targetTs);
    const slotKey = row?.slot_key || row?.slotKey;
    if (slotKey) return String(slotKey);
    return `${fallbackPrefix}:${row?.target_hour_waw ?? row?.hour ?? 0}`;
  };
  const compareHourSlots = (left = {}, right = {}) => {
    const leftTs = left?.target_ts || left?.targetTs;
    const rightTs = right?.target_ts || right?.targetTs;
    if (leftTs && rightTs && leftTs !== rightTs) return String(leftTs).localeCompare(String(rightTs));
    return Number(left?.target_hour_waw ?? left?.hour ?? 0) - Number(right?.target_hour_waw ?? right?.hour ?? 0);
  };
  const { fetchGet, fetchPostJson, fetchDelete, axiosPost, isAuthenticated } = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  const getWarsawDateOffset = (days = 0) => {
    const warsawNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Warsaw' }));
    warsawNow.setDate(warsawNow.getDate() + days);
    return warsawNow.toLocaleDateString('sv-SE');
  };

  const getWarsawMonthStart = (days = 0) => {
    const warsawNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Warsaw' }));
    warsawNow.setDate(warsawNow.getDate() + days);
    warsawNow.setDate(1);
    return warsawNow.toLocaleDateString('sv-SE');
  };

  const getMonthStartForDate = (dateValue) => {
    const normalizedDate = String(dateValue || '').slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)
      ? `${normalizedDate.slice(0, 7)}-01`
      : getWarsawMonthStart(1);
  };

  const [date, setDate] = useState(getWarsawDateOffset(1));
  const [startDate, setStartDate] = useState(getWarsawMonthStart(1));
  const [endDate, setEndDate] = useState(getWarsawDateOffset(1));
  const [simulationVariant, setSimulationVariant] = useState('goose-v3');
  const [letsTradeDeliveryDate, setLetsTradeDeliveryDate] = useState(getWarsawDateOffset(1));
  const [statusData, setStatusData] = useState(null);
  const [orderPlan, setOrderPlan] = useState([]);
  const [orderPlanMeta, setOrderPlanMeta] = useState(null);
  const [predictionInputData, setPredictionInputData] = useState(null);
  const [predictionInputLoading, setPredictionInputLoading] = useState(false);
  const [predictionInputError, setPredictionInputError] = useState('');
  const [orderAuditData, setOrderAuditData] = useState(null);
  const [orderAuditLoading, setOrderAuditLoading] = useState(false);
  const [orderAuditError, setOrderAuditError] = useState('');
  const [expandedOrderAuditHours, setExpandedOrderAuditHours] = useState(() => new Set());
  const [orderAuditBasisFilter, setOrderAuditBasisFilter] = useState('official');
  const [dayRadarData, setDayRadarData] = useState(null);
  const [dayRadarLoading, setDayRadarLoading] = useState(false);
  const [dayRadarRunning, setDayRadarRunning] = useState(false);
  const [dayRadarError, setDayRadarError] = useState('');
  const [letsTradeOrderPlan, setLetsTradeOrderPlan] = useState([]);
  const [letsTradeOrderPlanMeta, setLetsTradeOrderPlanMeta] = useState(null);
  const [letsTradeOrderPlanDate, setLetsTradeOrderPlanDate] = useState('');
  const [letsTradeOrderPlanLoading, setLetsTradeOrderPlanLoading] = useState(false);
  const [letsTradeOrderPlanError, setLetsTradeOrderPlanError] = useState('');
  const [resultsData, setResultsData] = useState(null);
  const [baseEntryRiskData, setBaseEntryRiskData] = useState(null);
  const [baseEntryRiskRowsByDate, setBaseEntryRiskRowsByDate] = useState({});
  const [baseEntryRiskRowsLoading, setBaseEntryRiskRowsLoading] = useState(false);
  const [baseEntryRiskLoading, setBaseEntryRiskLoading] = useState(false);
  const [basePretradeData, setBasePretradeData] = useState(null);
  const [basePretradeLoading, setBasePretradeLoading] = useState(false);
  const [baseHedgeDate] = useState(getWarsawDateOffset(1));
  const [baseQuickInputForm] = useState({
    baseAction: 'Buy',
    volume: '50',
    basePrice: '',
  });
  const [parityReport, setParityReport] = useState(null);
  const [parityReportLoading, setParityReportLoading] = useState(false);
  const [isParityReportExpanded, setIsParityReportExpanded] = useState(false);
  const [quickReplayForm, setQuickReplayForm] = useState(() => getDefaultQuickReplayFormForVariant('goose-v3'));
  const [quickReplayResult, setQuickReplayResult] = useState(null);
  const [quickReplayLoading, setQuickReplayLoading] = useState(false);
  const [dayDetailsByDate, setDayDetailsByDate] = useState({});
  const [expandedDate, setExpandedDate] = useState('');
  const [dailyResultsSortKey, setDailyResultsSortKey] = useState('date');
  const [dailyResultsSortDirection, setDailyResultsSortDirection] = useState('desc');
  const [detailsLoadingDate, setDetailsLoadingDate] = useState('');
  const [dailyBaseHedgeTooltip, setDailyBaseHedgeTooltip] = useState(null);
  const [overridesData, setOverridesData] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [overrideFormLoading, setOverrideFormLoading] = useState(false);
  const [overrideSaveStatus, setOverrideSaveStatus] = useState(null);
  const [overrideFormState, setOverrideFormState] = useState({
    mode: 'create',
    date: getWarsawDateOffset(1),
    baseAction: 'Idle',
    volume: '0',
    basePrice: '',
    note: '',
    triggerRecalc: true,
    fixIBasePrice: null,
    updatedAt: null,
    updatedBy: null,
    recalcStatus: null,
  });
  const [loading, setLoading] = useState(false);
  const [orderPlanLoading, setOrderPlanLoading] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);

  const getSimulationVariantLabel = useCallback((variantKey = simulationVariant) => {
    if (variantKey === 'goose-v5') return 'Goose v5.0.0';
    return 'Goose v3.1.0';
  }, [simulationVariant]);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [runPanelMode, setRunPanelMode] = useState({ train: 'latest', predict: 'latest', settle: 'latest' });
  const [selectedRunIds, setSelectedRunIds] = useState({ train: null, predict: null, settle: null });
  const [runDetailsByKey, setRunDetailsByKey] = useState({});
  const [settleResultMode, setSettleResultMode] = useState('ida2');
  const [predictResultMode, setPredictResultMode] = useState('fix2');
  const [, setTrainResultMode] = useState('v3');
  const [pipelineTrainTarget, setPipelineTrainTarget] = useState('goose-v3');
  const [pipelinePredictTarget, setPipelinePredictTarget] = useState('goose-v3');
  const [orderPreviewMode, setOrderPreviewMode] = useState('fix_i');
  const [orderPreviewFormat, setOrderPreviewFormat] = useState('noble');
  const [bosSectionKey, setBosSectionKey] = useState('base_setup');
  const [bosXlsxDownloading, setBosXlsxDownloading] = useState(false);
  const [isOrderPreviewCollapsed, setIsOrderPreviewCollapsed] = useState(true);
  const handleBosSectionClick = useCallback((sectionKey) => {
    setBosSectionKey((prev) => {
      if (prev === sectionKey) {
        setIsOrderPreviewCollapsed((c) => !c);
        return prev;
      }
      setIsOrderPreviewCollapsed(false);
      return sectionKey;
    });
  }, []);
  const [isLetsTradeEntryExplanationOpen, setIsLetsTradeEntryExplanationOpen] = useState(false);
  const [isLetsTradeHedgeExplanationOpen, setIsLetsTradeHedgeExplanationOpen] = useState('');
  const [isLetsTradeYachtPopoverOpen, setIsLetsTradeYachtPopoverOpen] = useState(false);
  const [isLetsTradeExecutionPathPopoverOpen, setIsLetsTradeExecutionPathPopoverOpen] = useState(false);
  const [isLetsTradeExecutionPathExpanded, setIsLetsTradeExecutionPathExpanded] = useState(false);
  const [isPipelineAuditExecutionPathExpanded, setIsPipelineAuditExecutionPathExpanded] = useState(true);
  const [externalFixIPreviewRows, setExternalFixIPreviewRows] = useState([]);
  const [externalFixIPreviewLoading, setExternalFixIPreviewLoading] = useState(false);
  const [externalFixIPreviewError, setExternalFixIPreviewError] = useState('');
  const [externalFixIPreviewFailedKey, setExternalFixIPreviewFailedKey] = useState('');
  const [externalIda1PreviewRows, setExternalIda1PreviewRows] = useState([]);
  const [externalIda1PreviewLoading, setExternalIda1PreviewLoading] = useState(false);
  const [externalIda1PreviewError, setExternalIda1PreviewError] = useState('');
  const [externalIda1PreviewFailedKey, setExternalIda1PreviewFailedKey] = useState('');
  const [externalIda2PreviewRows, setExternalIda2PreviewRows] = useState([]);
  const [externalIda2PreviewLoading, setExternalIda2PreviewLoading] = useState(false);
  const [externalIda2PreviewError, setExternalIda2PreviewError] = useState('');
  const [externalIda2PreviewFailedKey, setExternalIda2PreviewFailedKey] = useState('');
  const [howItWorksLanguage, setHowItWorksLanguage] = useState(getInitialSimulationLanguage);
  const [isBenchmarkSectionExpanded, setIsBenchmarkSectionExpanded] = useState(false);
  const [isFinalMaeSectionExpanded, setIsFinalMaeSectionExpanded] = useState(false);
  const [isPnlComponentsSectionExpanded, setIsPnlComponentsSectionExpanded] = useState(false);
  const [isQuickReplayExpanded, setIsQuickReplayExpanded] = useState(true);
  const [isSimulationModifierOpen, setIsSimulationModifierOpen] = useState(false);
  const [isHourlyBreakdownExpanded, setIsHourlyBreakdownExpanded] = useState(false);
  const [expandedExecutionQuarterPaths, setExpandedExecutionQuarterPaths] = useState({});
  const [expandedDailyQuarterPaths, setExpandedDailyQuarterPaths] = useState({});
  const hasUserSelectedOrderPreviewModeRef = useRef(false);
  const orderPlanHeaderRef = useRef(null);
  const orderPlanScrollBodyRef = useRef(null);
  const dailyBaseHedgeTooltipCloseTimerRef = useRef(null);
  const predictionInputHeaderRef = useRef(null);
  const predictionInputScrollBodyRef = useRef(null);
  const isSyncingOrderPlanScrollRef = useRef(false);
  const dailyResultsSectionRef = useRef(null);
  const dailyResultsHeaderRef = useRef(null);
  const dailyResultsScrollBodyRef = useRef(null);
  const dailyResultsStickyWrapperRef = useRef(null);
  const detailResultsHeaderRef = useRef(null);
  const detailResultsScrollBodyRef = useRef(null);
  const expandedDetailRegionRef = useRef(null);
  const isSyncingDailyScrollRef = useRef(false);
  const isSyncingDetailScrollRef = useRef(false);
  const orderPlanCacheRef = useRef(new Map());
  const predictionInputCacheRef = useRef(new Map());
  const orderAuditCacheRef = useRef(new Map());
  const orderAuditLoadedForDateRef = useRef('');
  const statusCacheRef = useRef(new Map());
  const runDetailsByKeyRef = useRef({});
  const latestStatusRequestRef = useRef(0);
  const latestOrderPlanSummaryRequestRef = useRef(0);
  const latestOrderPlanRequestRef = useRef(0);
  const latestPredictionInputRequestRef = useRef(0);
  const latestOrderAuditRequestRef = useRef(0);
  const latestDayRadarRequestRef = useRef(0);
  const latestLetsTradeOrderPlanRequestRef = useRef(0);
  const fix1OrderPlanRefreshedRef = useRef(null);
  const dayDetailsLoadedForStatusRef = useRef({});
  const latestNoblePreviewRequestRef = useRef({ fix_i: 0, ida1: 0, ida2: 0 });
  const resultsCacheRef = useRef(new Map());
  const baseEntryRiskCacheRef = useRef(new Map());
  const basePretradeCacheRef = useRef(new Map());
  const noblePreviewCacheRef = useRef(new Map());
  const latestResultsRequestRef = useRef(0);
  const latestBaseEntryRiskRequestRef = useRef(0);
  const latestBaseEntryRiskRowsRequestRef = useRef(0);
  const latestBasePretradeRequestRef = useRef(0);
  const latestParityRequestRef = useRef(0);
  const latestQuickReplayRequestRef = useRef(0);
  useEffect(() => () => {
    if (dailyBaseHedgeTooltipCloseTimerRef.current) {
      clearTimeout(dailyBaseHedgeTooltipCloseTimerRef.current);
    }
  }, []);
  const [hideDailyFloatingScroll, setHideDailyFloatingScroll] = useState(false);
  const activeSection = useMemo(() => {
    const pathname = String(location.pathname || '').replace(/\/+$/, '');
    const section = pathname.split('/').pop();
    if (section === 'pipeline') {
      return 'pipeline';
    }
    if (section === 'predictions') {
      return 'predictions';
    }
    if (section === 'data') {
      return 'data';
    }
    if (section === 'audit') {
      return 'audit';
    }
    if (section === 'orders') {
      return 'orders';
    }
    if (section === 'results') {
      return 'results';
    }
    if (section === 'daily-results') {
      return 'daily-results';
    }
    if (section === 'radar') {
      return 'radar';
    }
    if (section === 'lets-trade') {
      return 'lets-trade';
    }
    if (section === 'guard' || section === 'risk-management' || section === 'base-simulation') {
      return 'base-simulation';
    }
    if (section === 'quick-replay') {
      return 'results';
    }
    if (section === 'how-it-works') {
      return 'how-it-works';
    }
    return 'lets-trade';
  }, [location.pathname]);
  const overrideQueryDate = useMemo(() => new URLSearchParams(location.search || '').get('date') || '', [location.search]);
  const isResultsSection = activeSection === 'results';
  const isDailyResultsSection = activeSection === 'daily-results';
  const isDayRadarSection = activeSection === 'radar';
  const isLetsTradeSection = activeSection === 'lets-trade';
  const isBaseSimulationSection = activeSection === 'base-simulation';
  const isQuickReplaySection = activeSection === 'quick-replay';
  const isResultsDataSection = isResultsSection || isDailyResultsSection || isLetsTradeSection || isBaseSimulationSection || isQuickReplaySection;
  const isOverridesSection = false;
  const isPipelineSection = activeSection === 'pipeline';
  const isPipelinePredictionsSection = activeSection === 'predictions';
  const isPipelineDataSection = activeSection === 'data';
  const isPipelineAuditSection = activeSection === 'audit';
  const isPipelineOrdersSection = activeSection === 'orders';
  const isPipelineFamilySection = isPipelineSection || isPipelinePredictionsSection || isPipelineDataSection || isPipelineAuditSection || isPipelineOrdersSection;
  const isPipelineDetailSection = isPipelinePredictionsSection || isPipelineDataSection || isPipelineAuditSection || isPipelineOrdersSection;
  const isHowSection = activeSection === 'how-it-works';
  useEffect(() => {
    if (isLetsTradeSection) {
      setIsLetsTradeExecutionPathExpanded(false);
      setIsLetsTradeExecutionPathPopoverOpen(false);
    }
  }, [isLetsTradeSection, letsTradeDeliveryDate]);
  const basePretradeTargetDate = isBaseSimulationSection
    ? baseHedgeDate
    : isLetsTradeSection
      ? letsTradeDeliveryDate
      : endDate;
  const isHeavyResultsRange = useMemo(
    () => getInclusiveDaySpan(startDate, endDate) > MAX_AUTO_REFRESH_RESULTS_RANGE_DAYS,
    [endDate, startDate],
  );
  const howItWorksCopy = getHowItWorksCopy(howItWorksLanguage);
  const simCopy = SIM_UI_COPY[howItWorksLanguage] || SIM_UI_COPY.en;
  const baseHedgeCopy = simCopy.baseHedge || SIM_UI_COPY.en.baseHedge;
  const pipelineCopy = PIPELINE_TECH_COPY[howItWorksLanguage] || PIPELINE_TECH_COPY.en;
  const resultsCopy = RESULTS_UI_COPY[howItWorksLanguage] || RESULTS_UI_COPY.en;
  const baseGuardCopy = BASE_HEDGE_GUARD_COPY[howItWorksLanguage] || BASE_HEDGE_GUARD_COPY.en;
  const formatBaseHedgeActionLabel = useCallback((action) => {
    const normalized = String(action || '').trim().toLowerCase();
    if (normalized === 'buy') return baseHedgeCopy.actionLabels.Buy;
    if (normalized === 'sell') return baseHedgeCopy.actionLabels.Sell;
    if (normalized === 'idle') return baseHedgeCopy.actionLabels.Idle;
    return action ? String(action).toUpperCase() : '-';
  }, [baseHedgeCopy]);
  useEffect(() => {
    setSimulationLanguageCookie(howItWorksLanguage);
  }, [howItWorksLanguage]);
  useEffect(() => {
    runDetailsByKeyRef.current = runDetailsByKey;
  }, [runDetailsByKey]);
  const simSectionTabs = useMemo(() => ([
    {
      key: 'radar',
      label: simCopy.tabs.radar.label,
      path: '/fix2-v5-sim/radar',
      helper: simCopy.tabs.radar.helper,
      icon: Radar,
    },
    {
      key: 'lets-trade',
      label: simCopy.tabs['lets-trade'].label,
      path: '/fix2-v5-sim/lets-trade',
      helper: simCopy.tabs['lets-trade'].helper,
      icon: Play,
    },
    {
      key: 'results',
      label: simCopy.tabs.results.label,
      path: '/fix2-v5-sim/results',
      helper: simCopy.tabs.results.helper,
      icon: BarChart3,
    },
    {
      key: 'daily-results',
      label: simCopy.tabs['daily-results'].label,
      path: '/fix2-v5-sim/daily-results',
      helper: simCopy.tabs['daily-results'].helper,
      icon: CalendarDays,
    },
    {
      key: 'base-simulation',
      label: simCopy.tabs['base-simulation'].label,
      path: '/fix2-v5-sim/risk-management',
      helper: simCopy.tabs['base-simulation'].helper,
      icon: ShieldCheck,
    },
    {
      key: 'pipeline',
      label: simCopy.tabs.pipeline.label,
      path: '/fix2-v5-sim/pipeline',
      helper: simCopy.tabs.pipeline.helper,
      icon: Activity,
    },
    {
      key: 'predictions',
      label: simCopy.tabs.predictions.label,
      path: '/fix2-v5-sim/predictions',
      helper: simCopy.tabs.predictions.helper,
      icon: SlidersHorizontal,
    },
    {
      key: 'data',
      label: simCopy.tabs.data.label,
      path: '/fix2-v5-sim/data',
      helper: simCopy.tabs.data.helper,
      icon: FileSpreadsheet,
    },
    {
      key: 'audit',
      label: simCopy.tabs.audit.label,
      path: '/fix2-v5-sim/audit',
      helper: simCopy.tabs.audit.helper,
      icon: ShieldCheck,
    },
    {
      key: 'orders',
      label: simCopy.tabs.orders.label,
      path: '/fix2-v5-sim/orders',
      helper: simCopy.tabs.orders.helper,
      icon: FileSpreadsheet,
    },
    {
      key: 'how-it-works',
      label: simCopy.tabs['how-it-works'].label,
      path: '/fix2-v5-sim/how-it-works',
      helper: simCopy.tabs['how-it-works'].helper,
      icon: BookOpen,
    },
  ].filter(Boolean)), [simCopy]);
  const primarySimSectionTabs = useMemo(
    () => simSectionTabs.filter((tab) => ['results', 'daily-results', 'lets-trade', 'radar'].includes(tab.key)),
    [simSectionTabs],
  );
  const technicalSimSectionTabs = useMemo(
    () => {
      const tabs = simSectionTabs.filter((tab) => !['results', 'daily-results', 'lets-trade', 'radar', 'overrides'].includes(tab.key));
      return tabs.sort((a, b) => (a.key === 'pipeline' ? -1 : b.key === 'pipeline' ? 1 : 0));
    },
    [simSectionTabs],
  );

  const formatNumber = useCallback((value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }, []);

  const toRoundedMoney = useCallback((value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
    const numeric = Number(value);
    return Math.round((numeric + Math.sign(numeric) * Number.EPSILON) * 100) / 100;
  }, []);

  const formatOrderVolume = useCallback((value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }, []);

  const formatPriceOrDash = useCallback((value) => (
    value === null || value === undefined || Number.isNaN(Number(value))
      ? '-'
      : `${formatNumber(value)} PLN/MWh`
  ), [formatNumber]);

  const formatSignedNumber = (value, suffix = '') => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
    const numeric = Number(value);
    const sign = numeric > 0 ? '+' : '';
    return `${sign}${formatNumber(numeric)}${suffix}`;
  };

  const formatInteger = (value) => Math.round(Number(value || 0)).toLocaleString('en-US');
  const toFiniteNumberValue = useCallback((value, fallback = 0) => {
    if (value === null || value === undefined || value === '') return fallback;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
  }, []);

  const getEntryRiskHoursLabel = useCallback((rows = []) => {
    const hours = (Array.isArray(rows) ? rows : [])
      .map((row) => row?.hour)
      .filter((hour) => hour !== null && hour !== undefined)
      .map((hour) => formatOneBasedHourLabel(hour));
    if (!hours.length) return '-';
    return hours.join(', ');
  }, [formatOneBasedHourLabel]);

  const buildEntryRiskExplanationLines = useCallback((entryRisk = {}, copy = simCopy.letsTrade) => {
    if (!entryRisk?.ready) {
      return [copy.entryMissing, ...((entryRisk?.missing_inputs || []).slice(0, 4))];
    }
    const buy = entryRisk.buy || {};
    const sell = entryRisk.sell || {};
    const buyHours = Number(buy.risk_hours || 0);
    const sellHours = Number(sell.risk_hours || 0);
    const buyCharge = Number(buy.risk_charge_pln_mwh || 0);
    const sellCharge = Number(sell.risk_charge_pln_mwh || 0);
    const buyWeight = buyHours * 10 + Number(buy.full_block_hours || 0) * 20 + buyCharge;
    const sellWeight = sellHours * 10 + Number(sell.strong_spike_hours || 0) * 20 + sellCharge;
    if (buyWeight <= 0 && sellWeight <= 0) {
      return [copy.entryNoRiskHumanExplain, copy.entryRadarObserveOnly];
    }
    if (sellWeight >= buyWeight) {
      return copy.entrySellRiskHumanExplain({
        hours: sellHours,
        strong: Number(sell.strong_spike_hours || 0),
        charge: formatNumber(sellCharge),
        hoursLabel: getEntryRiskHoursLabel(sell.hourly_risk || []),
      });
    }
    return copy.entryBuyRiskHumanExplain({
      hours: buyHours,
      fullBlock: Number(buy.full_block_hours || 0),
      charge: formatNumber(buyCharge),
      hoursLabel: getEntryRiskHoursLabel(buy.hourly_risk || []),
    });
  }, [formatNumber, getEntryRiskHoursLabel, simCopy.letsTrade]);

  const buildQuickReplayParams = useCallback((form) => Object.fromEntries(
    Object.entries(form || {}).map(([key, value]) => {
      if (QUICK_REPLAY_BOOLEAN_FIELDS.has(key)) {
        return [key, Boolean(value)];
      }
      if (QUICK_REPLAY_STRING_FIELDS.has(key)) {
        return [key, String(value || '').trim()];
      }
      return [key, Number(value)];
    })
  ), []);

  const getQuickReplayParamSignature = useCallback((params = {}) => JSON.stringify(
    Object.entries(params || {})
      .map(([key, value]) => {
        if (QUICK_REPLAY_BOOLEAN_FIELDS.has(key)) {
          return [key, Boolean(value)];
        }
        if (QUICK_REPLAY_STRING_FIELDS.has(key)) {
          return [key, String(value || '').trim()];
        }
        return [key, Number(value)];
      })
      .filter(([key, value]) => QUICK_REPLAY_BOOLEAN_FIELDS.has(key) || QUICK_REPLAY_STRING_FIELDS.has(key) || Number.isFinite(value))
      .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
  ), []);

  const formatQuickReplayParamsLabel = useCallback((params = {}) => {
    const pieces = [
      ['Engine', 'Goose live', ''],
      ['BASE', params.base_volume, 'MW'],
      ['Fix I spread', params.fix_i_model_spread, 'PLN'],
      ['IDA1 cap', params.max_position, 'MW'],
      ['avg cap', params.max_avg_volume_pct, '%'],
      ['IDA1 margin', params.ida1_margin, 'PLN'],
      ['IDA2 edge', params.ida2_min_edge_per_mwh, 'PLN/MWh'],
    ];
    return pieces
      .filter(([, value]) => typeof value === 'string' || Number.isFinite(Number(value)))
      .map(([label, value, suffix]) => (
        typeof value === 'string'
          ? `${label} ${value}`
          : `${label} ${formatNumber(value)} ${suffix}`
      ))
      .join(' · ');
  }, [formatNumber]);

  const calculateDelta = (value, baseline) => {
    const numericValue = Number(value);
    const numericBaseline = Number(baseline);
    if (
      value === null || value === undefined || Number.isNaN(numericValue)
      || baseline === null || baseline === undefined || Number.isNaN(numericBaseline)
    ) {
      return null;
    }
    return Number((numericValue - numericBaseline).toFixed(2));
  };

  const formatWarsawDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);

    const datePart = parsed.toLocaleDateString('sv-SE', {
      timeZone: 'Europe/Warsaw',
    });
    const timePart = parsed.toLocaleTimeString('sv-SE', {
      timeZone: 'Europe/Warsaw',
      hour12: false,
    });
    const zonePart = parsed.toLocaleTimeString('en-US', {
      timeZone: 'Europe/Warsaw',
      timeZoneName: 'short',
    }).split(' ').pop();

    return `${datePart} ${timePart} ${zonePart}`;
  };

  const formatNobleDate = useCallback((value) => {
    if (!value) return '00-00-0000';
    const [year, month, day] = String(value).split('-');
    if (!year || !month || !day) return '00-00-0000';
    return `${day}-${month}-${year}`;
  }, []);

  const getValueColor = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return 'text-gray-500';
    if (Number(value) > 0) return 'text-green-600';
    if (Number(value) < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  const getBenchmarkDeltaColor = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return 'text-gray-500';
    if (Number(value) > 0) return 'text-green-600';
    if (Number(value) < 0) return 'text-red-600';
    return 'text-gray-500';
  };

  const pathComponentMeta = [
    { key: 'base', label: 'Base', colorClass: 'bg-sky-400', textClass: 'text-sky-300' },
    { key: 'fix', label: 'Fix', colorClass: 'bg-violet-400', textClass: 'text-violet-300' },
    { key: 'ida1', label: 'IDA1', colorClass: 'bg-fuchsia-400', textClass: 'text-fuchsia-300' },
    { key: 'ida2', label: 'IDA2', colorClass: 'bg-emerald-400', textClass: 'text-emerald-300' },
  ];

  const renderPathComposition = (components, label = 'PnL mix') => {
    const entries = pathComponentMeta
      .map((item) => ({
        ...item,
        value: Number(components?.[item.key] || 0),
      }))
      .filter((item) => Number.isFinite(item.value) && Math.abs(item.value) > 0.005);
    if (!entries.length) return null;
    const absTotal = entries.reduce((sum, item) => sum + Math.abs(item.value), 0);
    if (!absTotal) return null;
    const tooltip = entries
      .map((item) => `${item.label}: ${formatSignedNumber(item.value, ' PLN')} (${formatNumber((Math.abs(item.value) / absTotal) * 100)}%)`)
      .join('\n');
    return (
      <div className="mt-3" title={`${label}\n${tooltip}`}>
        <div className={`mb-1 flex items-center justify-between text-[10px] uppercase ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
          <span>{label}</span>
          <span>{entries.length} legs</span>
        </div>
        <div className={`flex h-2 overflow-hidden rounded-full ${darkMode ? 'bg-slate-800' : 'bg-slate-200'}`}>
          {entries.map((item) => (
            <span
              key={item.key}
              className={item.colorClass}
              style={{ width: `${Math.max((Math.abs(item.value) / absTotal) * 100, 2)}%` }}
            />
          ))}
        </div>
        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px]">
          {entries.map((item) => (
            <span key={item.key} className={darkMode ? item.textClass : 'text-gray-500'}>
              {item.label} {formatNumber((Math.abs(item.value) / absTotal) * 100)}%
            </span>
          ))}
        </div>
      </div>
    );
  };

  const getStatusLabel = (status) => {
    const rawLabel = String(status || '').trim();
    if (!rawLabel || rawLabel === '-') return '-';
    return rawLabel
      .replace(/_/g, ' ')
      .replace(/\b[a-z]/g, (match) => match.toUpperCase());
  };

  const getDisplayedDailyResultValue = (row) => getLiveProvisionalDailyResultValue(row);

  const getRowClass = (row) => {
    if (row.status === 'partial_fix2_settle') return 'bg-yellow-50 hover:bg-yellow-100';
    if (row.status === 'settled_fix2_fallback') return 'bg-amber-50 hover:bg-amber-100';
    if (row.status === 'settled_ida1_fallback') return 'bg-sky-50 hover:bg-sky-100';
    if (row.status === 'settled_ida2_no_ida1') return 'bg-emerald-50 hover:bg-emerald-100';
    if (row.status !== 'settled') return 'bg-gray-50 hover:bg-gray-100';
    const displayedValue = Number(getDisplayedDailyResultValue(row) || 0);
    if (displayedValue > 0) return 'bg-green-50 hover:bg-green-100';
    if (displayedValue < 0) return 'bg-red-50 hover:bg-red-100';
    return 'bg-gray-50 hover:bg-gray-100';
  };

  const formatDayStatus = (status) => {
    if (status === 'partial_fix2_settle') return 'Fix II';
    if (status === 'settled') return 'IDA2';
    if (status === 'settled_ida1_fallback') return 'IDA1';
    if (status === 'settled_ida1_fallback_no_settlement') return 'IDA1';
    if (status === 'settled_ida2_no_ida1') return 'IDA2';
    if (status === 'settled_fix2_fallback') return 'Fix II';
    if (status === 'missing_data') return 'Missing Data';
    if (status === 'attempted') return 'Attempted, No Result Saved';
    if (status === 'not_run_yet') return 'Not Run Yet';
    return status || '-';
  };

  const isFixIExecutionWaitingForFixII = (row = {}) => {
    const status = String(row?.status || '').trim().toLowerCase();
    if (status === 'partial_fix2_settle' || status === 'settled_fix2_fallback' || status.startsWith('settled')) {
      return false;
    }
    return Number(row?.venue_fix_i_mwh || row?.executed_volume_fix1 || 0) > 0.0001;
  };

  const getDisplayedDayStatusLabel = (row = {}) => (
    isFixIExecutionWaitingForFixII(row)
      ? resultsCopy.fixIExecutionStatus
      : formatDayStatus(row?.status)
  );

  const getDisplayedDayStatusBadgeClass = (row = {}) => (
    isFixIExecutionWaitingForFixII(row)
      ? (darkMode
        ? 'border-cyan-400/60 bg-cyan-400/15 text-cyan-50 shadow-[0_0_0_1px_rgba(34,211,238,0.18)]'
        : 'border-cyan-200 bg-cyan-100 text-cyan-900')
      : getDayStatusBadgeClass(row?.status)
  );

  const formatIda2PolicyLabel = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized || normalized === 'disabled') return 'Goose carry off';
    if (normalized.startsWith('moose_model') || normalized === 'moose') return 'Goose Model';
    if (normalized.startsWith('decision_model')) return 'Decision model';
    if (normalized === 'carry_policy') return 'Carry policy';
    if (normalized === 'model') return 'Carry model';
    if (normalized === 'fallback') return 'Legacy fallback';
    return getStatusLabel(normalized);
  };

  const getIda2PolicyBadgeClass = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized.startsWith('moose_model') || normalized === 'moose') {
      return 'border-indigo-200 bg-indigo-50 text-indigo-800';
    }
    if (normalized.startsWith('decision_model')) {
      return 'border-emerald-200 bg-emerald-50 text-emerald-800';
    }
    if (normalized === 'disabled' || normalized === 'fallback') {
      return 'border-gray-200 bg-gray-50 text-gray-700';
    }
    return 'border-sky-200 bg-sky-50 text-sky-800';
  };

  const getDayStatusBadgeClass = (status) => {
    const normalized = String(status || '');
    if (normalized === 'settled' || normalized === 'settled_ida2_no_ida1' || normalized === 'settled_ida2_no_ida2') {
      return darkMode
        ? 'border-emerald-400/60 bg-emerald-400/15 text-emerald-50 shadow-[0_0_0_1px_rgba(16,185,129,0.18)]'
        : 'border-emerald-200 bg-emerald-100 text-emerald-900';
    }
    if (normalized === 'settled_ida1_fallback' || normalized === 'settled_ida1_fallback_no_settlement') {
      return darkMode
        ? 'border-cyan-400/60 bg-cyan-400/15 text-cyan-50 shadow-[0_0_0_1px_rgba(34,211,238,0.18)]'
        : 'border-sky-200 bg-sky-100 text-sky-900';
    }
    if (normalized === 'partial_fix2_settle' || normalized === 'settled_fix2_fallback') {
      return darkMode
        ? 'border-amber-400/60 bg-amber-400/15 text-amber-50 shadow-[0_0_0_1px_rgba(251,191,36,0.18)]'
        : 'border-amber-200 bg-amber-100 text-amber-900';
    }
    if (normalized === 'missing_data' || normalized === 'attempted') {
      return darkMode
        ? 'border-rose-400/60 bg-rose-400/15 text-rose-50 shadow-[0_0_0_1px_rgba(251,113,133,0.18)]'
        : 'border-rose-200 bg-rose-100 text-rose-900';
    }
    return darkMode
      ? 'border-slate-500/80 bg-slate-800/80 text-slate-100'
      : 'border-gray-200 bg-gray-100 text-gray-700';
  };

  const getBaseDailyVenueVolume = (baseContext, rowLike = {}) => {
    const candidates = [
      baseContext?.venue_base_mwh,
      baseContext?.daily_volume_mwh,
      rowLike?.venue_base_mwh,
    ];
    const value = candidates.find((candidate) => candidate !== null && candidate !== undefined);
    return Number(value || 0);
  };

  const getBaseSettledVolume = (baseContext, rowLike = {}) => {
    const candidates = [
      baseContext?.settled_base_mwh,
      rowLike?.base_volume,
    ];
    const value = candidates.find((candidate) => candidate !== null && candidate !== undefined);
    return Number(value || 0);
  };

  const shouldShowBaseSettledVolume = (baseContext, rowLike = {}) => (
    getBaseSettledVolume(baseContext, rowLike) > 0
    && Math.abs(getBaseSettledVolume(baseContext, rowLike) - getBaseDailyVenueVolume(baseContext, rowLike)) > 0.01
  );

  const hasSettledBaseActivityWithoutSetup = (baseContext, rowLike = {}) => (
    Boolean(baseContext?.has_settled_activity_without_setup)
    || (
      String(baseContext?.base_action || 'Idle').toLowerCase() === 'idle'
      && Number(baseContext?.volume || 0) <= 0
      && getBaseDailyVenueVolume(baseContext, rowLike) > 0
    )
  );

  const getBaseDisplayAction = (baseContext, rowLike = {}) => (
    baseContext?.base_action || rowLike?.base_action || 'Idle'
  );

  const getBaseDisplayVolume = (baseContext, rowLike = {}) => {
    return Number(baseContext?.volume || 0);
  };

  const getBaseDisplayUnit = (baseContext, rowLike = {}) => (
    baseContext?.display_volume_kind === 'avg_venue_mw' || hasSettledBaseActivityWithoutSetup(baseContext, rowLike)
      ? 'MW'
      : 'MW'
  );

  const getBaseSetupLabel = (baseContext, rowLike = {}) => (
    `${formatBaseHedgeActionLabel(getBaseDisplayAction(baseContext, rowLike))} · ${formatNumber(getBaseDisplayVolume(baseContext, rowLike))}\u00A0${getBaseDisplayUnit(baseContext, rowLike)}`
  );

  const normalizeBaseSourceValue = (value) => String(value || '').trim().toLowerCase();
  const getBaseSourceLabelForSource = (source, sourceLabels) => {
    const normalizedSource = normalizeBaseSourceValue(source);
    if (normalizedSource === 'monitor_override' || normalizedSource === 'monitor') return sourceLabels.monitorOverride;
    if (normalizedSource === 'manual_override' || normalizedSource === 'manual') return sourceLabels.manualOverride;
    if (normalizedSource === 'post_base_execution_input') return sourceLabels.postBaseExecutionInput;
    if (normalizedSource === 'settled_replay') return sourceLabels.settledReplay;
    if (normalizedSource === 'settled_activity') return sourceLabels.settledActivity;
    if (normalizedSource === 'simulation_default') return sourceLabels.simulationDefault;
    if (normalizedSource === 'modified_simulation') return sourceLabels.modifiedSimulation;
    if (normalizedSource === 'request_input') return sourceLabels.requestInput;
    if (normalizedSource === 'persisted_base_action') return sourceLabels.persistedBaseAction;
    if (normalizedSource === 'model_guard') return sourceLabels.modelGuard;
    if (normalizedSource === 'base_simulation_skip') return sourceLabels.baseSimulationSkip;
    if (normalizedSource === 'override') return sourceLabels.override;
    return sourceLabels.automatic;
  };
  const getBaseEffectiveSource = (baseContext, rowLike = {}) => {
    if (hasSettledBaseActivityWithoutSetup(baseContext, rowLike)) {
      return 'settled_activity';
    }
    const source = normalizeBaseSourceValue(baseContext?.source);
    const runtimeSource = normalizeBaseSourceValue(baseContext?.runtime_source);
    if (source === 'settled_replay' && runtimeSource && runtimeSource !== 'settled_replay') {
      return runtimeSource;
    }
    return source || runtimeSource || 'automatic';
  };
  const getBaseSourceLabel = (baseContext, rowLike = {}) => {
    const sourceLabels = resultsCopy.baseSourceLabels || RESULTS_UI_COPY.en.baseSourceLabels;
    const source = normalizeBaseSourceValue(baseContext?.source);
    const runtimeSource = normalizeBaseSourceValue(baseContext?.runtime_source);
    if (source === 'settled_replay' && runtimeSource && runtimeSource !== 'settled_replay') {
      if (runtimeSource === 'simulation_default') {
        return getBaseSourceLabelForSource(runtimeSource, sourceLabels);
      }
      return `${sourceLabels.settledReplay} · ${getBaseSourceLabelForSource(runtimeSource, sourceLabels)}`;
    }
    if (baseContext?.source === 'settled_activity' || hasSettledBaseActivityWithoutSetup(baseContext, rowLike)) {
      return sourceLabels.settledActivity;
    }
    return getBaseSourceLabelForSource(source || runtimeSource, sourceLabels);
  };
  const shouldShowBaseSourceBadge = (baseContext, rowLike = {}) => {
    const effectiveSource = getBaseEffectiveSource(baseContext, rowLike);
    return !['automatic', 'simulation_default'].includes(effectiveSource);
  };
  const getBaseSourceBadgeClass = (baseContext, rowLike = {}) => {
    const effectiveSource = getBaseEffectiveSource(baseContext, rowLike);
    if (['manual_override', 'manual', 'monitor_override', 'monitor', 'post_base_execution_input', 'request_input', 'persisted_base_action', 'override'].includes(effectiveSource)) {
      return darkMode
        ? 'border-amber-300/45 bg-amber-500/15 text-amber-100'
        : 'border-amber-200 bg-amber-50 text-amber-900';
    }
    if (effectiveSource === 'modified_simulation') {
      return darkMode
        ? 'border-emerald-300/40 bg-emerald-500/10 text-emerald-100'
        : 'border-emerald-200 bg-emerald-50 text-emerald-800';
    }
    if (effectiveSource === 'settled_activity') {
      return darkMode
        ? 'border-cyan-300/40 bg-cyan-500/10 text-cyan-100'
        : 'border-cyan-200 bg-cyan-50 text-cyan-900';
    }
    return darkMode
      ? 'border-slate-600 bg-slate-900 text-slate-300'
      : 'border-gray-200 bg-gray-50 text-gray-700';
  };

  const buildBaseSourceContext = (baseContext = {}, rowLike = {}) => ({
    ...(baseContext || {}),
    source: baseContext?.source || rowLike?.source,
    runtime_source: baseContext?.runtime_source || rowLike?.runtime_source || rowLike?.base_action_source,
  });

  const buildBaseContextFromReplay = (baseSimulation = {}) => {
    const setup = baseSimulation?.base_setup || {};
    const rawAction = (
      baseSimulation?.recommended_action
      || setup?.base_action
      || baseSimulation?.model_action
      || 'Idle'
    );
    const normalizedAction = String(rawAction || '').toLowerCase() === 'none' ? 'Idle' : rawAction;
    const setupVolume = baseSimulation?.base_block_volume_mw ?? setup?.volume ?? 0;
    const venueVolume = (
      baseSimulation?.base_block_volume_mwh
      ?? baseSimulation?.full_block_volume_mwh
      ?? setup?.daily_volume_mwh
      ?? setup?.venue_base_mwh
    );
    return {
      ...setup,
      base_action: normalizedAction,
      display_action: normalizedAction,
      volume: setupVolume,
      runtime_base_price: baseSimulation?.base_price ?? setup?.runtime_base_price ?? setup?.base_price,
      base_price: baseSimulation?.base_price ?? setup?.base_price ?? setup?.runtime_base_price,
      daily_volume_mwh: venueVolume,
      venue_base_mwh: setup?.venue_base_mwh ?? venueVolume,
      source: baseSimulation?.source || setup?.source || baseSimulation?.base_action_source,
      runtime_source: setup?.runtime_source || baseSimulation?.runtime_source || baseSimulation?.base_action_source || baseSimulation?.source,
    };
  };

  const getBaseActionBadgeClass = (baseContext, rowLike = {}) => {
    const action = String(baseContext?.base_action || 'Idle').toLowerCase();
    if (action === 'buy') {
      return darkMode
        ? 'border-emerald-400/50 bg-emerald-400/15 text-emerald-50'
        : 'border-emerald-200 bg-emerald-50 text-emerald-800';
    }
    if (action === 'sell') {
      return darkMode
        ? 'border-rose-400/50 bg-rose-400/15 text-rose-50'
        : 'border-rose-200 bg-rose-50 text-rose-800';
    }
    return darkMode
      ? 'border-slate-500/80 bg-slate-800/80 text-slate-100'
      : 'border-gray-200 bg-gray-100 text-gray-700';
  };

  const getBaseSetupHint = (baseContext, rowLike = {}) => {
    if (hasSettledBaseActivityWithoutSetup(baseContext, rowLike)) {
      return 'This result has BASE venue activity, but no explicit runtime BASE context or manual override is attached to the run.';
    }
    return Number(baseContext?.volume || 0) > 0
      ? 'BASE context shows the strategy sizing for this day. Later auctions can still move residual flow independently.'
      : 'Idle BASE means this run did not attach a BASE context. Later auctions can still trade if executable residual exists.';
  };

  const getStatusBadgeMeta = (status, displayLabel = null) => {
    const rawStatus = String(status || '').trim();
    const normalized = rawStatus.toLowerCase().replace(/_/g, ' ');
    const label = displayLabel || getStatusLabel(rawStatus);
    let tooltip = null;

    if (!rawStatus || rawStatus === '-') {
      tooltip = 'No status value available yet.';

      return {
        label,
        badgeClass: darkMode
          ? 'bg-slate-700/80 text-slate-100 border-slate-500'
          : 'bg-gray-100 text-gray-700 border-gray-200',
        tooltip,
      };
    }

    if (
      normalized === 'success'
      || normalized === 'ready'
      || normalized === 'settled'
      || normalized === 'selected'
      || normalized.startsWith('full settle')
      || normalized.startsWith('final fallback')
    ) {
      tooltip = normalized === 'selected'
        ? 'Data for this view are active in the current selection.'
        : 'Completed successfully and a valid result is available.';

      return {
        label,
        badgeClass: darkMode
          ? 'bg-emerald-500/15 text-emerald-100 border-emerald-400/50'
          : 'bg-emerald-100 text-emerald-900 border-emerald-200',
        tooltip,
      };
    }

    if (
      normalized === 'running'
      || normalized === 'requested'
      || normalized === 'provisioning'
      || normalized === 'available in plan'
    ) {
      tooltip = 'The stage is running/in progress or staged for selection, but final output is not marked complete.';

      return {
        label,
        badgeClass: darkMode
          ? 'bg-sky-500/15 text-sky-100 border-sky-400/50'
          : 'bg-sky-100 text-sky-900 border-sky-200',
        tooltip,
      };
    }

    if (
      normalized === 'pending'
      || normalized === 'attempted'
      || normalized.startsWith('partial settle')
    ) {
      tooltip = 'Partially completed; this step may still require a later stage to reach final result.';

      return {
        label,
        badgeClass: darkMode
          ? 'bg-amber-500/15 text-amber-100 border-amber-400/50'
          : 'bg-amber-100 text-amber-900 border-amber-200',
        tooltip,
      };
    }

    if (
      normalized === 'failed'
      || normalized === 'sync error'
      || normalized.includes('error')
    ) {
      tooltip = 'Execution ended with an error; check the warning/error details and consider rerunning this stage.';

      return {
        label,
        badgeClass: darkMode
          ? 'bg-rose-500/15 text-rose-100 border-rose-400/50'
          : 'bg-rose-100 text-rose-900 border-rose-200',
        tooltip,
      };
    }

    if (
      normalized === 'missing'
      || normalized === 'not observed'
      || normalized === 'not run yet'
      || normalized === 'not observed'
      || normalized === 'cancelled'
      || normalized === 'missing data'
      || normalized === 'not run'
    ) {
      tooltip = 'No run output exists yet for this status.';

      return {
        label,
        badgeClass: darkMode
          ? 'bg-slate-700/80 text-slate-100 border-slate-500'
          : 'bg-slate-100 text-slate-700 border-slate-200',
        tooltip,
      };
    }

    tooltip = `Execution status: ${rawStatus}`;

    return {
      label,
      badgeClass: darkMode
        ? 'bg-violet-500/15 text-violet-100 border-violet-400/50'
        : 'bg-violet-100 text-violet-900 border-violet-200',
      tooltip,
    };
  };

  const renderStatusBadge = (statusMeta, options = {}) => {
    const { className = '' } = options;
    if (!statusMeta) {
      return null;
    }
    return (
      <span
        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusMeta.badgeClass} ${className}`}
        title={statusMeta.tooltip || statusMeta.label}
      >
        {statusMeta.label}
      </span>
    );
  };

  const normalizeBaseHedgeGuardMode = (value) => {
    const normalized = String(value || '').trim().toLowerCase().replaceAll('-', '_');
    if (normalized.includes('soft')) return 'soft';
    if (normalized.includes('hard')) return 'hard';
    return '';
  };

  const hasExecutedBaseHedge = (baseSimulation = {}) => {
    const hedgeVolume = Number(baseSimulation?.hedge_volume_mwh || 0);
    if (hedgeVolume > 0.001) return true;
    const hourlyPlan = Array.isArray(baseSimulation?.hourly_plan) ? baseSimulation.hourly_plan : [];
    return hourlyPlan.some((hour) => Number(hour?.hedge_volume_mw || 0) > 0.001);
  };

  const hasActualBasePosition = ({ action, volume }) => {
    const normalizedAction = String(action || '').trim().toLowerCase();
    const numericVolume = Number(volume);
    return ['buy', 'sell'].includes(normalizedAction)
      && Number.isFinite(numericVolume)
      && Math.abs(numericVolume) > 0.0001;
  };

  const getNegativeFixIBuyGuardAction = (hour = {}) => {
    const guard = hour?.negative_price_guard || {};
    const hasVolumePayload = [
      hour?.negative_price_guard_original_volume,
      hour?.negative_price_guard_post_volume,
      hour?.negative_price_guard_affected_volume,
      guard.negative_price_guard_original_volume,
      guard.negative_price_guard_post_volume,
      guard.negative_price_guard_affected_volume,
      guard.original_volume,
      guard.original_volume_mw,
      guard.post_volume,
      guard.post_volume_mw,
      guard.affected_volume,
      guard.affected_volume_mw,
    ].some((value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)));
    const reason = String(
      guard.reason
      || guard.action
      || guard.base_action
      || hour?.negative_price_guard_reason
      || hour?.negative_price_guard_action
      || hour?.negative_price_guard_base_action
      || '',
    ).trim().toLowerCase();
    const explicitAction = String(
      hour?.negative_price_guard_action
      || guard.action
      || hour?.negative_price_guard_base_action
      || guard.base_action
      || '',
    ).trim().toLowerCase();
    const baseGuardAction = String(
      hour?.negative_price_guard_base_action
      || guard.base_action
      || '',
    ).trim().toLowerCase();
    const isAllowedBuy = explicitAction.includes('allow_buy') || explicitAction === 'allow';
    const actionForClassification = isAllowedBuy ? baseGuardAction : explicitAction;
    if (isAllowedBuy && !['shrink_base_buy', 'block_base_buy'].includes(baseGuardAction)) {
      return null;
    }
    if (!actionForClassification && !hasVolumePayload) {
      return null;
    }
    if (
      actionForClassification.includes('block_buy')
      || actionForClassification.includes('block')
      || reason.includes('block_buy')
      || reason.includes('block')
    ) {
      return 'block';
    }
    if (
      actionForClassification.includes('cap_buy')
      || actionForClassification.includes('limit_buy')
      || actionForClassification.includes('shrink_base_buy')
      || actionForClassification.includes('cap')
      || actionForClassification.includes('limit')
      || reason.includes('limit_buy')
      || reason.includes('cap_buy')
      || reason.includes('limit')
    ) {
      return 'cap';
    }
    return null;
  };

  const getFirstFiniteGuardNumber = useCallback((...values) => {
    for (const value of values) {
      if (value === null || value === undefined || value === '') continue;
      const number = Number(value);
      if (Number.isFinite(number)) return number;
    }
    return null;
  }, []);

  const getNegativeFixIBuyGuardCapMw = useCallback((hour = {}) => {
    const guard = hour?.negative_price_guard || {};
    const explicitCap = getFirstFiniteGuardNumber(
      hour?.negative_price_guard_buy_cap_mw,
      hour?.negative_price_guard_cap_mw,
      hour?.negative_price_guard_cap_volume_mw,
      guard.negative_price_guard_buy_cap_mw,
      guard.buy_cap_mw,
      guard.cap_mw,
      guard.cap_volume_mw,
    );
    if (Number.isFinite(explicitCap)) return explicitCap;
    const rawReason = String(
      hour?.negative_price_guard_reason
      || hour?.negative_price_guard_action
      || guard.reason
      || guard.action
      || '',
    ).toLowerCase();
    const match = rawReason.match(/(?:limit|cap)_buy_([0-9]+(?:\.[0-9]+)?)\s*mw/);
    return match ? Number(match[1]) : null;
  }, [getFirstFiniteGuardNumber]);

  const getNegativeFixIBuyGuardSummary = (baseSimulation = {}) => {
    const hourlyPlan = Array.isArray(baseSimulation?.hourly_plan) ? baseSimulation.hourly_plan : [];
    const hasTradeContext = hasActualBasePosition({
      action: baseSimulation?.execution_action || baseSimulation?.base_action || baseSimulation?.model_action,
      volume: baseSimulation?.base_block_volume_mw ?? baseSimulation?.base_setup?.volume,
    });
    const rows = hourlyPlan
      .map((hour) => {
        const hasPostBaseHedgeOrder = Number(hour?.hedge_order_volume_mw ?? hour?.hedge_volume_mw ?? 0) > 0.001;
        if (hasPostBaseHedgeOrder) return null;
        const action = getNegativeFixIBuyGuardAction(hour);
        if (!action) return null;
        const guard = hour?.negative_price_guard || {};
        const originalVolume = getFirstFiniteGuardNumber(
          hour?.negative_price_guard_original_volume,
          guard.negative_price_guard_original_volume,
          guard.original_volume,
          guard.original_volume_mw,
          guard.pre_guard_volume_mw,
          hour?.original_volume_mw,
        );
        const capVolume = getFirstFiniteGuardNumber(
          getNegativeFixIBuyGuardCapMw(hour),
          action === 'block' ? 0 : null,
        );
        const postVolume = getFirstFiniteGuardNumber(
          hour?.negative_price_guard_post_volume,
          guard.negative_price_guard_post_volume,
          guard.post_volume,
          guard.post_volume_mw,
          guard.post_guard_volume_mw,
        );
        const explicitAffectedVolume = getFirstFiniteGuardNumber(
          hour?.negative_price_guard_affected_volume,
          guard.negative_price_guard_affected_volume,
          guard.affected_volume,
          guard.affected_volume_mw,
          guard.reduced_volume_mw,
        );
        const orderVolume = getFirstFiniteGuardNumber(
          hour?.executed_volume_fix1,
          hour?.submitted_volume_fix1,
          hour?.order_volume_mw,
        );
        const hasVolumeEvidence = (
          (Number.isFinite(originalVolume) && originalVolume > 0.001)
          || (Number.isFinite(orderVolume) && Math.abs(orderVolume) > 0.001)
        );
        if (!hasTradeContext && !hasVolumeEvidence) return null;
        const volumeAfterGuard = Number.isFinite(postVolume)
          ? postVolume
          : Number.isFinite(capVolume)
            ? Math.min(originalVolume || 0, capVolume)
            : orderVolume;
        const affectedVolume = Number.isFinite(explicitAffectedVolume)
          ? explicitAffectedVolume
          : Number.isFinite(originalVolume) && originalVolume > 0.001 && Number.isFinite(volumeAfterGuard)
            ? Math.max(0, originalVolume - volumeAfterGuard)
            : null;
        const reason = String(
          guard.reason
          || hour?.negative_price_guard_reason
          || hour?.negative_price_guard_action
          || '',
        ).trim();
        return {
          hour,
          hourNumber: Number(hour?.hour),
          action,
          reason,
          originalVolume: Number.isFinite(originalVolume) ? originalVolume : 0,
          capVolume: Number.isFinite(capVolume) ? capVolume : null,
          postVolume: Number.isFinite(postVolume) ? postVolume : null,
          affectedVolume: Number.isFinite(affectedVolume) ? affectedVolume : 0,
          missingVolumeData: !Number.isFinite(originalVolume) || !Number.isFinite(affectedVolume),
          price: hour?.fix_i_price ?? hour?.hedge_price ?? null,
        };
      })
      .filter(Boolean);
    const blockedRows = rows.filter((row) => row.action === 'block');
    const cappedRows = rows.filter((row) => row.action === 'cap');
    const potentialVolume = rows.reduce((sum, row) => sum + Number(row.originalVolume || 0), 0);
    const affectedVolume = rows.reduce((sum, row) => sum + Number(row.affectedVolume || 0), 0);
    const missingVolumeRows = rows.filter((row) => row.missingVolumeData).length;
    const capValues = Array.from(new Set(
      rows
        .map((row) => row.capVolume)
        .filter((value) => Number.isFinite(value))
        .map((value) => Number(value.toFixed(2))),
    )).sort((a, b) => a - b);
    return {
      rows,
      blockedRows,
      cappedRows,
      hasIntervention: rows.length > 0,
      blockedHours: blockedRows.length,
      cappedHours: cappedRows.length,
      hours: rows.length,
      potentialVolume: Number(potentialVolume.toFixed(2)),
      affectedVolume: Number(affectedVolume.toFixed(2)),
      missingVolumeRows,
      hasMissingVolumeData: missingVolumeRows > 0,
      capValues,
      hourLabels: rows.map((row) => formatHourLabel(row.hourNumber)).filter(Boolean),
      blockedHourLabels: blockedRows.map((row) => formatHourLabel(row.hourNumber)).filter(Boolean),
      cappedHourLabels: cappedRows.map((row) => formatHourLabel(row.hourNumber)).filter(Boolean),
    };
  };

	  const formatFixIBuyGuardLabel = (
	    summary = {},
	    { includePrefix = true, includeCounts = false } = {},
	  ) => {
	    const blockedHours = Number(summary?.blockedHours || 0);
	    const cappedHours = Number(summary?.cappedHours || 0);
	    const statusParts = [
	      blockedHours > 0 ? `${baseGuardCopy.blockedBadge}${includeCounts ? ` ${formatInteger(blockedHours)}h` : ''}` : null,
	      cappedHours > 0 ? `${baseGuardCopy.cappedBadge}${includeCounts ? ` ${formatInteger(cappedHours)}h` : ''}` : null,
	    ];
    return [
      includePrefix ? 'Goose risk' : null,
      'BUY Fix I',
      ...statusParts,
    ].filter(Boolean).join(' · ');
  };

  const getBaseCarryGuardSummary = useCallback((summary = {}, hourlyRows = []) => {
    const baseSimulation = summary?.base_simulation || summary || {};
    const rows = Array.isArray(hourlyRows) && hourlyRows.length
      ? hourlyRows
      : Array.isArray(summary?.hourly) && summary.hourly.length
        ? summary.hourly
        : Array.isArray(baseSimulation?.hourly_plan)
          ? baseSimulation.hourly_plan
          : [];
    const sumRows = (fieldNames = []) => rows.reduce((sum, row) => {
      const value = fieldNames
        .map((fieldName) => Number(row?.[fieldName]))
        .find((number) => Number.isFinite(number) && Math.abs(number) > 0.001);
      return sum + (Number.isFinite(value) ? Math.abs(value) : 0);
    }, 0);
    const pickCarryVolume = (...values) => {
      const candidates = values
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && Math.abs(value) > 0.001)
        .map((value) => Math.abs(value));
      if (!candidates.length) return 0;
      return Math.max(...candidates);
    };
    const forcedFix2 = pickCarryVolume(
      summary?.base_forced_fix2_volume_mwh,
      summary?.base_forced_to_fix2_volume_mwh,
      summary?.base_carry_forced_fix2_volume_mwh,
      baseSimulation?.base_forced_fix2_volume_mwh,
      baseSimulation?.base_forced_to_fix2_volume_mwh,
      baseSimulation?.base_carry_forced_fix2_volume_mwh,
      sumRows(['base_forced_fix2_volume_mwh', 'base_forced_to_fix2_volume_mwh', 'base_carry_forced_fix2_volume_mwh']),
    );
    const blockedIda1 = pickCarryVolume(
      summary?.base_blocked_from_ida1_volume_mwh,
      summary?.base_blocked_ida1_volume_mwh,
      summary?.base_carry_blocked_ida1_volume_mwh,
      baseSimulation?.base_blocked_from_ida1_volume_mwh,
      baseSimulation?.base_blocked_ida1_volume_mwh,
      baseSimulation?.base_carry_blocked_ida1_volume_mwh,
      sumRows(['base_blocked_from_ida1_volume_mwh', 'base_blocked_ida1_volume_mwh', 'base_carry_blocked_ida1_volume_mwh']),
    );
    const blockedIda2 = pickCarryVolume(
      summary?.base_blocked_from_ida2_volume_mwh,
      summary?.base_blocked_ida2_volume_mwh,
      summary?.base_carry_blocked_ida2_volume_mwh,
      baseSimulation?.base_blocked_from_ida2_volume_mwh,
      baseSimulation?.base_blocked_ida2_volume_mwh,
      baseSimulation?.base_carry_blocked_ida2_volume_mwh,
      sumRows(['base_blocked_from_ida2_volume_mwh', 'base_blocked_ida2_volume_mwh', 'base_carry_blocked_ida2_volume_mwh']),
    );
    const hasPositiveCarryValue = (row, fieldNames = []) => fieldNames.some((fieldName) => {
      const value = Number(row?.[fieldName]);
      return Number.isFinite(value) && Math.abs(value) > 0.001;
    });
    const hourRows = rows.filter((row) => (
      hasPositiveCarryValue(row, ['base_forced_fix2_volume_mwh', 'base_forced_to_fix2_volume_mwh', 'base_carry_forced_fix2_volume_mwh'])
      || hasPositiveCarryValue(row, ['base_blocked_from_ida1_volume_mwh', 'base_blocked_ida1_volume_mwh', 'base_carry_blocked_ida1_volume_mwh'])
      || hasPositiveCarryValue(row, ['base_blocked_from_ida2_volume_mwh', 'base_blocked_ida2_volume_mwh', 'base_carry_blocked_ida2_volume_mwh'])
    ));
    return {
      hasIntervention: [forcedFix2, blockedIda1, blockedIda2].some((value) => Number.isFinite(value) && value > 0.001),
      forcedFix2: Number.isFinite(forcedFix2) ? Number(forcedFix2.toFixed(2)) : 0,
      blockedIda1: Number.isFinite(blockedIda1) ? Number(blockedIda1.toFixed(2)) : 0,
      blockedIda2: Number.isFinite(blockedIda2) ? Number(blockedIda2.toFixed(2)) : 0,
      hours: hourRows.length,
      hourLabels: hourRows.map((row) => formatHourLabel(row?.target_hour_waw ?? row?.hour)).filter(Boolean),
    };
  }, [formatHourLabel]);

  const getBaseHedgeGuardMeta = (baseSimulation = {}) => {
    const hourlyPlan = Array.isArray(baseSimulation?.hourly_plan) ? baseSimulation.hourly_plan : [];
    const guardSummary = baseSimulation?.guard_summary || {};
    const buyGuardSummary = getNegativeFixIBuyGuardSummary(baseSimulation);
    if (buyGuardSummary.hasIntervention) {
      const policyParts = [
        buyGuardSummary.blockedHours > 0 ? `${formatInteger(buyGuardSummary.blockedHours)} blocked h` : null,
        buyGuardSummary.cappedHours > 0 ? `${formatInteger(buyGuardSummary.cappedHours)} capped h` : null,
        buyGuardSummary.affectedVolume > 0.001 ? `${formatNumber(buyGuardSummary.affectedVolume)} MW affected` : null,
      ].filter(Boolean);
      return {
        mode: 'fix_i_buy_guard',
        label: formatFixIBuyGuardLabel(buyGuardSummary),
        shortLabel: formatFixIBuyGuardLabel(buyGuardSummary, { includePrefix: false }),
        info: baseGuardCopy.buyGuardInfo,
        policyInfo: policyParts.join(' · '),
        badgeClass: darkMode
          ? 'border-cyan-300/55 bg-cyan-500/15 text-cyan-100'
          : 'border-cyan-200 bg-cyan-50 text-cyan-900',
        panelClass: darkMode
          ? 'border-cyan-400/35 bg-cyan-500/10 text-cyan-50'
          : 'border-cyan-200 bg-cyan-50 text-cyan-950',
      };
    }
    const suppressedSpikeRows = hourlyPlan.filter((hour) => (
      Boolean(hour?.sell_spike_guard?.suppressed)
      || String(hour?.sell_spike_guard?.suppressed_reason || '').trim()
      || String(hour?.sell_spike_guard?.reason || '').trim() === 'sell_spike_hedge_not_economic'
      || String(hour?.sell_spike_guard_reason || '').trim() === 'sell_spike_hedge_not_economic'
    ));
    const sellSpikeRows = hourlyPlan.filter((hour) => (
      Number(hour?.hedge_volume_mw || 0) > 0.001
      && (
        hour?.sell_spike_guard?.active
        || hour?.sell_spike_guard_active
        || String(hour?.sell_spike_guard_reason || '').trim()
      )
    ));
    const suppressedSpikeHours = Number(guardSummary.sell_spike_suppressed_hours || 0) || suppressedSpikeRows.length;
    if (suppressedSpikeHours > 0 && sellSpikeRows.length === 0 && Number(guardSummary.sell_spike_active_hours || 0) === 0) {
      const strongHours = Number(guardSummary.sell_spike_suppressed_strong_hours || 0)
        || suppressedSpikeRows.filter((hour) => Boolean(hour?.sell_spike_guard?.strong || hour?.sell_spike_guard_strong)).length;
      const policyParts = [
        `${formatInteger(suppressedSpikeHours)} suppressed spike h`,
        strongHours > 0 ? `${formatInteger(strongHours)} strong spike h` : null,
      ].filter(Boolean);
      return {
        mode: 'spike_suppressed',
        label: baseGuardCopy.spikeSuppressedLabel,
        shortLabel: baseGuardCopy.spikeSuppressedShort,
        info: baseGuardCopy.spikeSuppressedInfo,
        policyInfo: policyParts.join(' · '),
        badgeClass: darkMode
          ? 'border-sky-300/55 bg-sky-500/15 text-sky-100'
          : 'border-sky-200 bg-sky-50 text-sky-900',
        panelClass: darkMode
          ? 'border-sky-400/35 bg-sky-500/10 text-sky-50'
          : 'border-sky-200 bg-sky-50 text-sky-950',
      };
    }
    if (sellSpikeRows.length > 0 || Number(guardSummary.sell_spike_active_hours || 0) > 0) {
      const strongHours = Number(guardSummary.sell_spike_strong_hours || 0)
        || sellSpikeRows.filter((hour) => Boolean(hour?.sell_spike_guard?.strong || hour?.sell_spike_guard_strong)).length;
      const policyParts = [
        strongHours > 0 ? `${formatInteger(strongHours)} strong spike h` : null,
        sellSpikeRows.length > 0 ? `${formatInteger(sellSpikeRows.length)} hedge h` : null,
      ].filter(Boolean);
      return {
        mode: 'spike',
        label: baseGuardCopy.spikeLabel,
        shortLabel: baseGuardCopy.spikeShort,
        info: baseGuardCopy.spikeInfo,
        policyInfo: policyParts.join(' · '),
        badgeClass: darkMode
          ? 'border-amber-300/55 bg-amber-500/15 text-amber-100'
          : 'border-amber-200 bg-amber-50 text-amber-900',
        panelClass: darkMode
          ? 'border-amber-400/35 bg-amber-500/10 text-amber-50'
          : 'border-amber-200 bg-amber-50 text-amber-950',
      };
    }
    const firstGuard = hourlyPlan
      .map((hour) => hour?.negative_price_guard)
      .find(Boolean) || {};
    const policy = (
      guardSummary.base_shrink_policy
      || baseSimulation?.negative_price_base_shrink_policy
      || firstGuard.base_shrink_policy
      || {}
    );
    const rawMode = (
      guardSummary.base_shrink_mode
      || policy.effective_mode
      || baseSimulation?.negative_price_base_shrink_mode
      || firstGuard.base_shrink_mode
      || ''
    );
    let mode = normalizeBaseHedgeGuardMode(rawMode);

    if (!mode) {
      const hedgeRows = hourlyPlan.filter((hour) => Number(hour?.hedge_volume_mw || 0) > 0.001);
      const targetVolumes = hedgeRows
        .map((hour) => Number(hour?.target_volume_mw))
        .filter((value) => Number.isFinite(value));
      mode = targetVolumes.some((value) => value >= 34 && value <= 46) ? 'soft' : 'hard';
    }

    const isSoft = mode === 'soft';
    const policyParts = [];
    if (policy?.hybrid_active) {
      policyParts.push(baseGuardCopy.hybridSoft);
    } else if (policy?.configured_mode && policy?.effective_mode && policy.configured_mode !== policy.effective_mode) {
      policyParts.push(`${policy.configured_mode} -> ${policy.effective_mode}`);
    }
    if (policy?.hard_full_block_hours !== null && policy?.hard_full_block_hours !== undefined) {
      policyParts.push(`${formatInteger(policy.hard_full_block_hours)} hard full-block h`);
    }
    if (policy?.base_price !== null && policy?.base_price !== undefined) {
      policyParts.push(`BASE ${formatNumber(policy.base_price)} PLN/MWh`);
    }

    return {
      mode,
      label: isSoft ? baseGuardCopy.softLabel : baseGuardCopy.hardLabel,
      shortLabel: isSoft ? baseGuardCopy.softShort : baseGuardCopy.hardShort,
      info: isSoft ? baseGuardCopy.softInfo : baseGuardCopy.hardInfo,
      policyInfo: policyParts.join(' · '),
      badgeClass: isSoft
        ? darkMode
          ? 'border-cyan-300/50 bg-cyan-500/15 text-cyan-100'
          : 'border-cyan-200 bg-cyan-50 text-cyan-800'
        : darkMode
          ? 'border-rose-300/50 bg-rose-500/15 text-rose-100'
          : 'border-rose-200 bg-rose-50 text-rose-800',
      panelClass: isSoft
        ? darkMode
          ? 'border-cyan-400/35 bg-cyan-500/10 text-cyan-50'
          : 'border-cyan-200 bg-cyan-50 text-cyan-950'
        : darkMode
          ? 'border-rose-400/35 bg-rose-500/10 text-rose-50'
          : 'border-rose-200 bg-rose-50 text-rose-950',
    };
  };

  const getPostBaseSellSpikeBreakdown = (baseSimulation = {}, copy = simCopy.letsTrade) => {
    const hourlyPlan = Array.isArray(baseSimulation?.hourly_plan) ? baseSimulation.hourly_plan : [];
    const guardSummary = baseSimulation?.guard_summary || {};
    const spikeRows = hourlyPlan
      .map((hour) => {
        const guard = hour?.sell_spike_guard || {};
        const active = Boolean(guard.active || hour?.sell_spike_guard_active);
        const suppressed = Boolean(
          guard.suppressed
          || hour?.sell_spike_guard_suppressed
          || guard.suppressed_reason
          || String(guard.reason || hour?.sell_spike_guard_reason || '').trim() === 'sell_spike_hedge_not_economic'
        );
        if (!active && !suppressed) {
          return null;
        }
        const rawHour = Number(hour?.hour);
        const auctionHour = formatHourLabel(rawHour);
        const edgeText = suppressed && guard.hedge_edge !== null && guard.hedge_edge !== undefined && guard.target_edge !== null && guard.target_edge !== undefined
          ? copy.postBaseGuardEdge({
              hedgeEdge: formatNumber(guard.hedge_edge),
              targetEdge: formatNumber(guard.target_edge),
            })
          : '';
        return {
          hour: rawHour,
          active,
          suppressed,
          label: auctionHour,
          edgeText,
        };
      })
      .filter(Boolean)
      .sort((left, right) => Number(left.hour || 0) - Number(right.hour || 0));
    const activeRows = spikeRows.filter((row) => row.active);
    const suppressedRows = spikeRows.filter((row) => row.suppressed);
    const detected = Number(guardSummary.sell_spike_raw_hours || 0) || spikeRows.length;
    const hedged = Number(guardSummary.sell_spike_active_hours || 0) || activeRows.length;
    const skipped = Number(guardSummary.sell_spike_suppressed_hours || 0) || suppressedRows.length;
    return {
      detected,
      hedged,
      skipped,
      activeRows,
      suppressedRows,
      hasSpike: detected > 0 || activeRows.length > 0 || suppressedRows.length > 0,
    };
  };

  const getHourlyPnlForAvailability = (row, availability = {}) => {
    if (availability.ida2 && row?.hourly_pnl_ida2_close !== null && row?.hourly_pnl_ida2_close !== undefined) {
      return row.hourly_pnl_ida2_close;
    }
    if (availability.ida1 && row?.hourly_pnl !== null && row?.hourly_pnl !== undefined) {
      return row.hourly_pnl;
    }
    if (availability.fix_ii && row?.hourly_pnl_fix2_close !== null && row?.hourly_pnl_fix2_close !== undefined) {
      return row.hourly_pnl_fix2_close;
    }
    if (availability.fix_i && row?.hourly_pnl_fix1_mark !== null && row?.hourly_pnl_fix1_mark !== undefined) {
      return row.hourly_pnl_fix1_mark;
    }
    return null;
  };

  const getHourlyRowClass = (row, availability = {}) => {
    const displayPnl = getHourlyPnlForAvailability(row, availability) ?? 0;
    if (displayPnl > 0) return 'bg-green-50';
    if (displayPnl < 0) return 'bg-red-50';
    return 'bg-white';
  };

  const getAuctionActionBadgeClass = (action) => {
    if (action === 'buy') {
      return darkMode
        ? 'border-emerald-300/50 bg-emerald-500/15 text-emerald-100'
        : 'border-emerald-200 bg-emerald-50 text-emerald-700';
    }
    if (action === 'sell') {
      return darkMode
        ? 'border-rose-300/50 bg-rose-500/15 text-rose-100'
        : 'border-rose-200 bg-rose-50 text-rose-700';
    }
    return darkMode
      ? 'border-slate-600 bg-slate-800 text-slate-300'
      : 'border-gray-200 bg-gray-50 text-gray-500';
  };

  const getAuctionStageToneClass = () => (
    darkMode
      ? 'border-slate-700 bg-slate-950/35'
      : 'border-slate-200 bg-white'
  );

  const getAuctionStageLabelClass = () => (
    darkMode ? 'text-slate-300' : 'text-slate-600'
  );

  const getAuctionStageCardClass = (action, pnl = null, stageLabel = '') => {
    if (action === 'buy') {
      return darkMode
        ? 'border-emerald-400/70 bg-emerald-500/15'
        : 'border-emerald-300 bg-emerald-50';
    }
    if (action === 'sell') {
      return darkMode
        ? 'border-rose-400/70 bg-rose-500/15'
        : 'border-rose-300 bg-rose-50';
    }
    if (Number(pnl || 0) > 0.001) {
      return darkMode
        ? 'border-emerald-400/45 bg-emerald-500/10'
        : 'border-emerald-200 bg-emerald-50/70';
    }
    if (Number(pnl || 0) < -0.001) {
      return darkMode
        ? 'border-rose-400/45 bg-rose-500/10'
        : 'border-rose-200 bg-rose-50/70';
    }
    if (stageLabel) {
      return getAuctionStageToneClass(stageLabel);
    }
    return darkMode ? 'border-slate-700 bg-slate-900/35' : 'border-slate-200 bg-white';
  };

  const normalizeExecutionActionValue = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'buy' || normalized === 'sell' ? normalized : null;
  };

  const getExecutionPnlBridge = (summary = {}, availability = {}) => {
    const toFinite = (value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    };
    const hasValue = (value) => value !== null && value !== undefined;
    const hasIda2Basis = availability.ida2 && hasValue(summary?.daily_pnl_ida2_close);
    const hasIda1Basis = availability.ida1 && hasValue(summary?.daily_pnl);
    const hasFix2Basis = availability.fix_ii && hasValue(summary?.daily_pnl_fix2_close);
    const hasFix1Basis = availability.fix_i && hasValue(summary?.daily_pnl_fix1_mark);

    if (hasIda2Basis) {
      return {
        key: 'ida2',
        targetPnl: toFinite(summary.daily_pnl_ida2_close),
        contributions: {
          base: toFinite(summary.base_pnl),
          fix_ii: toFinite(summary.probabilistic_pnl),
          ida1: toFinite(summary.ida1_pnl),
          ida2: toFinite(summary.ida2_pnl),
        },
      };
    }
    if (hasIda1Basis) {
      return {
        key: 'ida1',
        targetPnl: toFinite(summary.daily_pnl),
        contributions: {
          base: toFinite(summary.base_pnl),
          fix_ii: toFinite(summary.probabilistic_pnl),
          ida1: toFinite(summary.ida1_pnl),
          ida2: null,
        },
      };
    }
    if (hasFix2Basis) {
      const fixICloseRealizedFix2 = toFinite(summary.fix_i_close_realized_pnl_fix2_close);
      return {
        key: 'fix_ii',
        targetPnl: toFinite(summary.daily_pnl_fix2_close),
        contributions: {
          base: toFinite(summary.base_pnl_fix2_close ?? summary.base_pnl),
          fix_i: fixICloseRealizedFix2,
          fix_ii: toFinite(summary.probabilistic_pnl_fix2_close ?? summary.probabilistic_pnl),
          ida1: null,
          ida2: null,
        },
      };
    }
    if (hasFix1Basis) {
      // BASE tile shows the residual mark (= what BASE residual would earn if
      // closed at Fix I right now). Fix I tile shows the realised hedge fill.
      // Together they equal `base_pnl_fix1_mark` (full BASE × edge sum), so
      // Wynik strategii (= sum of stage contributions) stays consistent with
      // the legacy total without double-counting the hedge.
      const residualMarkBase = toFinite(summary.base_open_mark_pnl_fix1 ?? summary.base_pnl_fix1_mark ?? summary.base_pnl);
      const realizedFix1 = toFinite(summary.daily_pnl_fix1_mark);
      const a = Number.isFinite(residualMarkBase) ? residualMarkBase : 0;
      const b = Number.isFinite(realizedFix1) ? realizedFix1 : 0;
      return {
        key: 'fix_i',
        targetPnl: Number((a + b).toFixed(2)),
        contributions: {
          base: residualMarkBase,
          fix_i: realizedFix1,
          fix_ii: null,
          ida1: null,
          ida2: null,
        },
      };
    }
    return {
      key: null,
      targetPnl: null,
      contributions: {},
    };
  };

  const buildExecutionPathSummary = (rows = [], availability = {}, summary = {}) => {
    const pnlBridge = getExecutionPnlBridge(summary, availability);
    const hasBridgeContribution = (key) => {
      const bridgeValue = pnlBridge.contributions?.[key];
      return bridgeValue !== null && bridgeValue !== undefined && Number.isFinite(Number(bridgeValue));
    };
    const getBridgeContribution = (key, fallbackValue = null) => {
      if (hasBridgeContribution(key)) {
        return Number(pnlBridge.contributions[key]);
      }
      return fallbackValue;
    };
    const baseContext = summary?.base_context || {};
    const baseAction = String(baseContext?.base_action || summary?.base_action || 'Idle').trim().toLowerCase();
    const baseSetupVolumeMw = Number(baseContext?.volume ?? 0);
    const baseVenueVolumeMwh = getBaseDailyVenueVolume(baseContext, summary);
    const baseFallbackVolumeMwh = Number(summary?.base_volume ?? summary?.venue_base_mwh ?? 0);
    const baseTotalVolumeMwh = baseVenueVolumeMwh > 0.0001
      ? baseVenueVolumeMwh
      : baseFallbackVolumeMwh > 0.0001
        ? baseFallbackVolumeMwh
        : baseSetupVolumeMw * 24.0;
    const getLegCashflow = (action, price, volume) => {
      const normalizedAction = normalizeExecutionActionValue(action);
      const numericPrice = Number(price);
      const numericVolume = Number(volume);
      if (!normalizedAction || !Number.isFinite(numericPrice) || !Number.isFinite(numericVolume) || Math.abs(numericVolume) <= 0.0001) {
        return null;
      }
      return normalizedAction === 'sell'
        ? numericPrice * numericVolume
        : -numericPrice * numericVolume;
    };
    const basePnlValue = getBridgeContribution('base', summary?.base_pnl);
    const basePnl = basePnlValue !== null && basePnlValue !== undefined ? Number(basePnlValue) : null;
    const hasBaseStage = hasActualBasePosition({
      action: baseAction,
      volume: baseSetupVolumeMw > 0.0001 ? baseSetupVolumeMw : baseTotalVolumeMwh,
    });
    const baseStage = {
      key: 'base',
      label: 'BASE',
      unit: 'MWh',
      ready: true,
      totalVolume: hasBaseStage && Number.isFinite(baseTotalVolumeMwh) ? baseTotalVolumeMwh : 0,
      setupVolumeMw: hasBaseStage && Number.isFinite(baseSetupVolumeMw) ? baseSetupVolumeMw : 0,
      avgPrice: Number.isFinite(Number(baseContext?.runtime_base_price))
        ? Number(baseContext.runtime_base_price)
        : Number.isFinite(Number(baseContext?.base_price))
          ? Number(baseContext.base_price)
          : Number.isFinite(Number(summary?.base_price))
            ? Number(summary.base_price)
            : null,
      pnl: Number.isFinite(basePnl) ? basePnl : null,
      cashflow: hasBaseStage
        ? getLegCashflow(baseAction, baseContext?.runtime_base_price ?? baseContext?.base_price ?? summary?.base_price, baseTotalVolumeMwh)
        : null,
      pnlIsBridgeContribution: hasBridgeContribution('base'),
      buyVolume: hasBaseStage && baseAction === 'buy' ? baseTotalVolumeMwh : 0,
      sellVolume: hasBaseStage && baseAction === 'sell' ? baseTotalVolumeMwh : 0,
      carryVolume: 0,
      details: hasBaseStage ? [{ action: baseAction, volume: baseTotalVolumeMwh }] : [],
      status: hasBaseStage ? 'done' : 'empty',
      baseAction,
      sourceLabel: getBaseSourceLabel(baseContext, summary),
      sourceBadgeClass: getBaseSourceBadgeClass(baseContext, summary),
    };
    const stageConfigs = [
      baseStage,
	      {
	        key: 'fix_i',
	        label: 'Fix I',
        actionKey: 'executed_action',
        volumeKey: 'executed_volume_fix1',
	        priceKey: 'fix_i_price',
	        unit: 'MW',
	        ready: Boolean(availability.fix_i),
	        previewReady: Boolean(availability.preview?.fix_i),
	      },
	      {
	        key: 'fix_ii',
        label: 'Fix II',
        actionKey: 'fix2_close_action',
        volumeKey: 'closed_volume_fix2',
	        priceKey: 'fix_ii_price',
	        pnlKey: 'fix_ii_pnl',
	        unit: 'MWh',
	        ready: Boolean(availability.fix_ii),
	        previewReady: Boolean(availability.preview?.fix_ii),
	        carryVolume: rows.reduce((acc, row) => acc + Number(row?.closed_volume_ida1 || 0) + Number(row?.residual_to_ida2 || 0), 0),
	      },
      {
        key: 'ida1',
        label: 'IDA1',
        actionKey: 'ida1_close_action',
        volumeKey: 'closed_volume_ida1',
	        priceKey: 'ida1_price',
	        pnlKey: 'ida1_pnl',
	        unit: 'MWh',
	        ready: Boolean(availability.ida1),
	        previewReady: Boolean(availability.preview?.ida1),
	        carryVolume: rows.reduce((acc, row) => acc + Number(row?.residual_to_ida2 || 0), 0),
	      },
      {
        key: 'ida2',
        label: 'IDA2',
        actionKey: 'ida2_close_action',
        volumeKey: 'closed_volume_ida2',
	        priceKey: 'ida2_price',
	        pnlKey: 'ida2_pnl',
	        unit: 'MWh',
	        ready: Boolean(availability.ida2),
	        previewReady: Boolean(availability.preview?.ida2),
	      },
	    ];

    const getDirectPnl = (row, stage) => {
      if (stage.key === 'fix_i') {
        return null;
      }
      if (!stage.pnlKey || row?.[stage.pnlKey] === null || row?.[stage.pnlKey] === undefined) {
        return null;
      }
      const directPnl = Number(row?.[stage.pnlKey]);
      return Number.isFinite(directPnl) ? directPnl : null;
    };

    return stageConfigs.map((stage) => {
      if (stage.key === 'base') {
        return stage;
      }
	      if (!stage.ready && !stage.previewReady) {
	        return {
	          ...stage,
          totalVolume: 0,
          avgPrice: null,
          pnl: null,
          pnlIsBridgeContribution: false,
          buyVolume: 0,
          sellVolume: 0,
          carryVolume: 0,
          details: [],
          baseCloseVolume: 0,
          baseCloseCashflow: null,
          cashflow: null,
          baseCloseBuyVolume: 0,
          baseCloseSellVolume: 0,
	          status: 'pending',
	        };
	      }
      const buildDirectDetail = (row) => {
          const volume = Number(row?.[stage.volumeKey] || 0);
          const action = normalizeExecutionActionValue(row?.[stage.actionKey]);
          const price = Number(row?.[stage.priceKey]);
          const pnl = getDirectPnl(row, stage);
          const hasVolume = Number.isFinite(volume) && Math.abs(volume) > 0.0001;
          const hasPnl = Number.isFinite(pnl) && Math.abs(pnl) > 0.001;
          if (!hasVolume && !hasPnl) return null;
          return {
            hour: Number(row?.target_hour_waw ?? row?.hour ?? 0),
            action,
            volume: Number.isFinite(volume) ? volume : 0,
            price: Number.isFinite(price) ? price : null,
            pnl: Number.isFinite(pnl) ? pnl : null,
            cashflow: getLegCashflow(action, price, volume),
          };
      };
      const buildComponentDetails = (row) => {
        const components = Array.isArray(row?.execution_components?.[stage.key])
          ? row.execution_components[stage.key]
          : [];
        if (!components.length) return [];
        const hour = Number(row?.target_hour_waw ?? row?.hour ?? 0);
        return components
          .map((component) => {
            const volume = Number(component?.volume_mwh || 0);
            const action = normalizeExecutionActionValue(component?.action || row?.[stage.actionKey]);
            const price = Number(component?.price ?? row?.[stage.priceKey]);
            const pnl = Number(component?.pnl);
            const hasVolume = Number.isFinite(volume) && Math.abs(volume) > 0.0001;
            const hasPnl = Number.isFinite(pnl) && Math.abs(pnl) > 0.001;
            if (!hasVolume && !hasPnl) return null;
            return {
              hour,
              action,
              volume: Number.isFinite(volume) ? volume : 0,
              price: Number.isFinite(price) ? price : null,
              pnl: Number.isFinite(pnl) ? pnl : null,
              cashflow: getLegCashflow(action, price, volume),
              isComponent: true,
            };
          })
          .filter(Boolean);
      };
      const details = (rows || [])
        .flatMap((row) => {
          const directDetail = buildDirectDetail(row);
          if (directDetail) return [directDetail];
          return buildComponentDetails(row);
        })
        .filter(Boolean)
        .sort((left, right) => left.hour - right.hour);
      const baseCloseDetails = stage.key === 'fix_i' ? (rows || [])
        .flatMap((row) => {
          const hour = Number(row?.target_hour_waw ?? row?.hour ?? 0);
          const fixIComponents = Array.isArray(row?.execution_components?.fix_i)
            ? row.execution_components.fix_i
            : [];
          const officialBaseComponents = fixIComponents
            .filter((component) => ['base_close', 'base_hedge_close'].includes(component?.role))
            .map((component) => {
              const volume = Number(component?.volume_mwh || 0);
              const action = normalizeExecutionActionValue(component?.action || row?.base_fix_i_action);
              const price = Number(component?.price ?? row?.fix_i_price);
              const pnl = Number(component?.pnl);
              const hasVolume = Number.isFinite(volume) && Math.abs(volume) > 0.0001;
              const hasPnl = Number.isFinite(pnl) && Math.abs(pnl) > 0.001;
              if (!hasVolume && !hasPnl) return null;
              return {
                hour,
                action,
                volume: Number.isFinite(volume) ? volume : 0,
                price: Number.isFinite(price) ? price : null,
                pnl: Number.isFinite(pnl) ? pnl : null,
                cashflow: getLegCashflow(action, price, volume),
                isBaseClose: true,
                isComponent: true,
              };
            })
            .filter(Boolean);
          if (officialBaseComponents.length || row?.execution_components) {
            return officialBaseComponents;
          }

          // Legacy fallback only for rows that do not expose execution_components
          // at all. Runtime replay can carry intended risk-management hedge volume in
          // base_fix_i_volume even when the order did not execute, so component
          // rows are the source of truth whenever they exist.
          const volume = Number(row?.base_fix_i_volume || row?.base_hedge_volume_mwh || 0);
          const rawAction = String(row?.base_fix_i_action || row?.base_hedge_action || '').trim().toLowerCase();
          const action = rawAction === 'buy' || rawAction === 'sell' ? rawAction : null;
          const price = Number(row?.fix_i_price);
          const pnl = Number(row?.base_fix_i_pnl ?? row?.base_hedge_pnl_fix_i);
          const hasVolume = Number.isFinite(volume) && Math.abs(volume) > 0.0001;
          const hasPnl = Number.isFinite(pnl) && Math.abs(pnl) > 0.001;
          if (!hasVolume && !hasPnl) return [];
          return [{
            hour,
            action,
            volume: Number.isFinite(volume) ? volume : 0,
            price: Number.isFinite(price) ? price : null,
            pnl: Number.isFinite(pnl) ? pnl : null,
            cashflow: getLegCashflow(action, price, volume),
            isBaseClose: true,
          }];
        })
        .filter(Boolean)
        .sort((left, right) => left.hour - right.hour) : [];
      const allDetails = [...details, ...baseCloseDetails];
      const volumeDetails = allDetails;
      const pnlDetails = stage.key === 'fix_i' ? details : allDetails;
      const totalVolume = volumeDetails.reduce((acc, item) => acc + item.volume, 0);
      const weightedPriceVolume = volumeDetails.reduce((acc, item) => acc + (item.price !== null ? item.volume : 0), 0);
      const weightedPriceValue = volumeDetails.reduce((acc, item) => acc + (item.price !== null ? item.price * item.volume : 0), 0);
      const avgPrice = weightedPriceVolume > 0 ? weightedPriceValue / weightedPriceVolume : null;
      const pnl = pnlDetails.some((item) => item.pnl !== null)
        ? pnlDetails.reduce((acc, item) => acc + Number(item.pnl || 0), 0)
        : null;
      const buyVolume = volumeDetails
        .filter((item) => item.action === 'buy')
        .reduce((acc, item) => acc + item.volume, 0);
      const sellVolume = volumeDetails
        .filter((item) => item.action === 'sell')
        .reduce((acc, item) => acc + item.volume, 0);
      const baseCloseVolume = baseCloseDetails.reduce((acc, item) => acc + item.volume, 0);
      const baseCloseCashflow = baseCloseDetails.some((item) => item.cashflow !== null)
        ? baseCloseDetails.reduce((acc, item) => acc + Number(item.cashflow || 0), 0)
        : null;
      const cashflow = allDetails.some((item) => item.cashflow !== null)
        ? allDetails.reduce((acc, item) => acc + Number(item.cashflow || 0), 0)
        : null;
      const baseCloseBuyVolume = baseCloseDetails
        .filter((item) => item.action === 'buy')
        .reduce((acc, item) => acc + item.volume, 0);
      const baseCloseSellVolume = baseCloseDetails
        .filter((item) => item.action === 'sell')
        .reduce((acc, item) => acc + item.volume, 0);
      const bridgePnl = (
        pnlBridge.key === 'fix_ii'
        && stage.key === 'fix_i'
        && !hasBridgeContribution('fix_i')
      )
        ? null
        : getBridgeContribution(stage.key, pnl);
      const isFixIFinanciallyCarriedForward = stage.key === 'fix_i' && Boolean(availability.fix_ii || availability.ida1 || availability.ida2);
      const displayPnl = !isFixIFinanciallyCarriedForward && bridgePnl !== null && bridgePnl !== undefined && Number.isFinite(Number(bridgePnl))
        ? Number(bridgePnl)
        : null;
      return {
        ...stage,
        totalVolume,
        avgPrice,
        pnl: displayPnl,
        pnlIsBridgeContribution: hasBridgeContribution(stage.key),
        buyVolume,
        sellVolume,
        carryVolume: Number(stage.carryVolume || 0),
        details: allDetails,
        baseCloseVolume,
        baseCloseCashflow,
        cashflow,
        baseCloseBuyVolume,
        baseCloseSellVolume,
	        status: totalVolume > 0.0001 || baseCloseVolume > 0.0001 || (displayPnl !== null && Math.abs(displayPnl) > 0.001)
	          ? (stage.ready ? 'done' : 'preview')
	          : stage.ready ? 'empty' : stage.previewReady ? 'preview' : 'pending',
	      };
    });
  };

  const getExecutionPnlBridgeFromStages = (stages = [], availability = {}) => {
    const stageByKey = Object.fromEntries((stages || []).map((stage) => [stage.key, stage]));
    const toFinite = (value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    };
    const latestKey = availability.ida2
      ? 'ida2'
      : availability.ida1
        ? 'ida1'
        : availability.fix_ii
          ? 'fix_ii'
          : availability.fix_i
            ? 'fix_i'
            : null;
    if (!latestKey) {
      return { key: null, targetPnl: null, contributions: {} };
    }
    const orderedKeys = latestKey === 'ida2'
      ? ['base', 'fix_ii', 'ida1', 'ida2']
      : latestKey === 'ida1'
        ? ['base', 'fix_ii', 'ida1']
        : latestKey === 'fix_ii'
          ? ['base', 'fix_ii']
          : ['base', 'fix_i'];
    const contributions = {};
    let hasAnyPnl = false;
    let targetPnl = 0.0;
    orderedKeys.forEach((key) => {
      const pnl = toFinite(stageByKey[key]?.pnl);
      contributions[key] = pnl;
      if (pnl !== null) {
        hasAnyPnl = true;
        targetPnl += pnl;
      }
    });
    return {
      key: hasAnyPnl ? latestKey : null,
      targetPnl: hasAnyPnl ? Number(targetPnl.toFixed(2)) : null,
      contributions,
    };
  };

  const getExecutionStageStatusMeta = (stage, copy) => {
    if (stage.status === 'done') {
      return {
        label: copy.executionDone,
        className: darkMode
          ? 'border-emerald-300/50 bg-emerald-500/15 text-emerald-100'
          : 'border-emerald-200 bg-emerald-50 text-emerald-800',
      };
    }
    if (stage.status === 'preview') {
      return {
        label: copy.executionPreview || 'Preview',
        className: darkMode
          ? 'border-cyan-300/45 bg-cyan-500/15 text-cyan-100'
          : 'border-cyan-200 bg-cyan-50 text-cyan-800',
      };
    }
    if (stage.status === 'empty') {
      return {
        label: copy.executionEmpty,
        className: darkMode
          ? 'border-slate-600 bg-slate-800 text-slate-300'
          : 'border-gray-200 bg-gray-50 text-gray-600',
      };
    }
    return {
      label: copy.executionPending,
      className: darkMode
        ? 'border-amber-300/45 bg-amber-500/15 text-amber-100'
        : 'border-amber-200 bg-amber-50 text-amber-800',
    };
  };

  const renderExecutionPathPanel = ({
    rows = [],
    summary = {},
    availability = {},
    copy,
    compact = false,
    expanded = true,
    onToggle = null,
    quarterPathKeyPrefix = 'execution',
    showHeader = true,
    notice = null,
  }) => {
    const stages = buildExecutionPathSummary(rows, availability, summary);
    const executionPnlBridge = getExecutionPnlBridge(summary, availability);
    const toFiniteNumber = (value) => {
      if (value === null || value === undefined || value === '') return null;
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    };
    const normalizeAuctionAction = (value) => {
      const normalized = String(value || '').trim().toLowerCase();
      return normalized === 'buy' || normalized === 'sell' ? normalized : null;
    };
    const summaryBaseContext = summary?.base_context || {};
    const summaryBaseAction = normalizeAuctionAction(summaryBaseContext?.base_action || summary?.base_action);
    const summaryBaseVolumeMw = toFiniteNumber(summaryBaseContext?.volume);
    const summaryBasePrice = toFiniteNumber(
      summaryBaseContext?.runtime_base_price
      ?? summaryBaseContext?.base_price
      ?? summary?.base_price
    );
    const hasHourlyBaseLeg = hasActualBasePosition({
      action: summaryBaseAction,
      volume: summaryBaseVolumeMw || 0,
    });
    const buildLeg = ({
      action,
      volume,
      unit,
      price,
      pnl,
      fallbackLabel = null,
      roleLabel = null,
      roleTone = null,
      guardMeta = null,
      pnlNote = null,
      orderLimitPrice = null,
      referencePrice = null,
      limitMargin = null,
      explanation = null,
      popoverKey = null,
    }) => ({
      action: normalizeAuctionAction(action),
      volume: toFiniteNumber(volume) || 0,
      unit,
      price: toFiniteNumber(price),
      pnl: toFiniteNumber(pnl),
      fallbackLabel,
      roleLabel,
      roleTone,
      guardMeta,
      pnlNote,
      orderLimitPrice: toFiniteNumber(orderLimitPrice),
      referencePrice: toFiniteNumber(referencePrice),
      limitMargin: toFiniteNumber(limitMargin),
      explanation,
      popoverKey,
    });
    const hasLegActivity = (leg) => (
      Math.abs(Number(leg?.volume || 0)) > 0.0001
      || Math.abs(Number(leg?.pnl || 0)) > 0.001
    );
    const getHourlyPostBaseGuardMeta = (row) => {
      const hour = Number(row?.target_hour_waw ?? row?.hour);
      const planRow = (basePretradeDecision?.hourly_plan || []).find((item) => Number(item?.hour) === hour) || {};
      const guardSource = {
        hourly_plan: [planRow],
        guard_summary: basePretradeDecision?.guard_summary || {},
      };
      const meta = getBaseHedgeGuardMeta(guardSource);
      return meta?.mode ? meta : getBaseHedgeGuardMeta({});
    };
    const getExecutionComponentRoleLabel = (role) => {
      switch (role) {
        case 'fix_i_close':
          return copy.executionCloseFixI || copy.executionCloseLabel || 'Fix I close';
        case 'base_close':
          return copy.executionBaseClose || 'BASE close';
        case 'base_hedge_close':
          return copy.executionBaseHedgeClose || copy.executionBaseClose || 'BASE hedge close';
        case 'fix_ii_ladder':
          return copy.executionFixIILadder || copy.executionFixIIActivity || 'Fix II ladder';
        case 'fix_ii_ladder_close':
          return copy.executionFixIILadderClose || copy.executionFinalClose || 'Fix II ladder close';
        case 'residual_close':
          return copy.executionResidualClose || copy.executionFinalClose || copy.executionCloseLabel || 'residual close';
        default:
          return role ? String(role).replaceAll('_', ' ') : null;
      }
    };
    const getExecutionComponentRoleTone = (role) => {
      if (role === 'base_hedge_close') return 'guardBaseClose';
      if (role === 'base_close') return 'baseClose';
      return null;
    };
    const buildComponentLegs = (row, stageKey, fallbackLeg, defaultAction = null) => {
      const stageComponents = row?.execution_components?.[stageKey];
      if (!Array.isArray(stageComponents) || !stageComponents.length) {
        return [fallbackLeg];
      }
      const componentLegs = stageComponents
        .map((component, componentIndex) => {
          const role = component?.role;
          const isHedgeRole = role === 'base_hedge_close';
          const componentGuardMeta = isHedgeRole ? getHourlyPostBaseGuardMeta(row) : null;
          return buildLeg({
            action: (component?.action === 'buy' || component?.action === 'sell')
              ? component.action
              : defaultAction,
            volume: component?.volume_mwh,
            unit: 'MWh',
            price: component?.price,
            pnl: component?.pnl,
            roleLabel: getExecutionComponentRoleLabel(role),
            roleTone: getExecutionComponentRoleTone(role),
            guardMeta: componentGuardMeta,
            explanation: isHedgeRole
              ? [
                  componentGuardMeta?.info,
                  componentGuardMeta?.policyInfo,
                ].filter(Boolean).join(' ')
              : null,
            popoverKey: isHedgeRole ? `${quarterPathKeyPrefix}:${getHourSlotKey(row)}:${stageKey}-hedge-${componentIndex}` : null,
          });
        })
        .filter(hasLegActivity);
      return componentLegs.length ? componentLegs : [fallbackLeg];
    };
    const hourlyExecutionDetails = (rows || [])
      .map((row) => {
        const rowHour = Number(row?.target_hour_waw ?? row?.hour ?? 0);
        const rowSlotKey = getHourSlotKey(row);
        const postBasePlanRow = (basePretradeDecision?.hourly_plan || [])
          .find((item) => Number(item?.hour) === rowHour) || {};
        const postBaseHedgeVolume = toFiniteNumber(postBasePlanRow?.hedge_volume_mw) || 0;
        const postBaseHedgeAction = normalizeAuctionAction(postBasePlanRow?.hedge_action);
        const postBaseHedgeLimitPrice = toFiniteNumber(postBasePlanRow?.hedge_limit_price);
        const postBaseHedgeAuctionPrice = toFiniteNumber(row?.fix_i_price);
        const hasPostBaseHedgeIntent = Math.abs(postBaseHedgeVolume) > 0.0001;
        const postBaseHedgeLimitExecutes = (
          hasPostBaseHedgeIntent
          && postBaseHedgeAction
          && postBaseHedgeLimitPrice !== null
          && postBaseHedgeAuctionPrice !== null
        )
          ? (
            postBaseHedgeAction === 'buy'
              ? postBaseHedgeAuctionPrice <= postBaseHedgeLimitPrice + 0.0001
              : postBaseHedgeAuctionPrice >= postBaseHedgeLimitPrice - 0.0001
          )
          : false;
        const hasPostBaseHedge = hasPostBaseHedgeIntent && (
          postBaseHedgeLimitExecutes
          || (
            postBaseHedgeLimitPrice === null
            && Math.abs(Number(row?.base_hedge_volume_mwh ?? row?.base_fix_i_volume ?? 0)) > 0.0001
          )
        );
        const suppressBaseFixIFallback = hasPostBaseHedgeIntent && !hasPostBaseHedge;
        const postBaseHedgePrice = hasPostBaseHedge
          ? (toFiniteNumber(row?.fix_i_price) ?? toFiniteNumber(postBasePlanRow?.hedge_price))
          : null;
        const postBaseHedgePnlFromPlan = toFiniteNumber(postBasePlanRow?.hedge_pnl);
        const postBaseHedgeBaseAction = normalizeAuctionAction(
          postBasePlanRow?.base_action
          || basePretradeDecision?.base_setup?.base_action
          || summaryBaseAction
        );
        const postBaseHedgeBasePrice = toFiniteNumber(
          postBasePlanRow?.base_price
          ?? basePretradeDecision?.base_price
          ?? basePretradeDecision?.base_setup?.runtime_base_price
          ?? summaryBasePrice
        );
        const computedPostBaseHedgePnl = (
          hasPostBaseHedge
          && postBaseHedgePnlFromPlan === null
          && postBaseHedgePrice !== null
          && postBaseHedgeBasePrice !== null
          && postBaseHedgeBaseAction
        )
          ? (
            postBaseHedgeBaseAction === 'sell'
              ? (postBaseHedgeBasePrice - postBaseHedgePrice) * postBaseHedgeVolume
              : (postBaseHedgePrice - postBaseHedgeBasePrice) * postBaseHedgeVolume
          )
          : postBaseHedgePnlFromPlan;
        const postBaseHedgeGuardMeta = hasPostBaseHedge ? getHourlyPostBaseGuardMeta(row) : null;
        const rowFixIVolume = toFiniteNumber(row?.executed_volume_fix1) || 0;
        const rowFixIIVolume = toFiniteNumber(row?.closed_volume_fix2) || 0;
        const rowIda1Volume = toFiniteNumber(row?.closed_volume_ida1) || 0;
        const rowIda2Volume = toFiniteNumber(row?.closed_volume_ida2) || 0;
        const rowOppositeAction = (value) => (value === 'buy' ? 'sell' : value === 'sell' ? 'buy' : null);
        const rowDirectionalSource = String(row?.executed_action || summaryBaseAction || '').trim().toLowerCase();
        const rowDayCloseAction = rowOppositeAction(
          rowDirectionalSource === 'buy' || rowDirectionalSource === 'sell' ? rowDirectionalSource : null
        );
        const base = buildLeg({
          action: summaryBaseAction,
          volume: hasHourlyBaseLeg ? summaryBaseVolumeMw : 0,
          unit: 'MW',
          price: summaryBasePrice,
          pnl: null,
        });
        const fixI = buildLeg({
          action: row?.executed_action,
          volume: rowFixIVolume,
          unit: 'MW',
          price: row?.fix_i_price,
          pnl: null,
        });
        const baseFixI = buildLeg({
          action: hasPostBaseHedge
            ? postBasePlanRow?.hedge_action
            : suppressBaseFixIFallback
              ? null
              : (row?.base_fix_i_action || row?.base_hedge_action),
          volume: hasPostBaseHedge
            ? postBaseHedgeVolume
            : suppressBaseFixIFallback
              ? 0
              : (row?.base_fix_i_volume || row?.base_hedge_volume_mwh),
          unit: 'MWh',
          price: hasPostBaseHedge
            ? postBaseHedgePrice
            : suppressBaseFixIFallback
              ? null
              : row?.fix_i_price,
          pnl: hasPostBaseHedge
            ? computedPostBaseHedgePnl
            : suppressBaseFixIFallback
              ? null
              : row?.base_fix_i_pnl,
          roleLabel: hasPostBaseHedge
            ? (copy.executionBaseHedgeClose || copy.executionBaseClose || 'BASE hedge close')
            : !suppressBaseFixIFallback && (row?.base_fix_i_action || row?.base_hedge_action)
              ? (copy.executionBaseClose || 'BASE close')
              : null,
          roleTone: hasPostBaseHedge
            ? 'guardBaseClose'
            : !suppressBaseFixIFallback && (row?.base_fix_i_action || row?.base_hedge_action)
              ? 'baseClose'
              : null,
          guardMeta: postBaseHedgeGuardMeta,
          explanation: hasPostBaseHedge
            ? [
                postBaseHedgeGuardMeta?.info,
                postBaseHedgeGuardMeta?.policyInfo,
              ].filter(Boolean).join(' ')
            : null,
          popoverKey: hasPostBaseHedge ? `${quarterPathKeyPrefix}:${rowSlotKey}:base-hedge` : null,
          orderLimitPrice: hasPostBaseHedge
            ? postBasePlanRow?.hedge_limit_price
            : (row?.base_fix_i_limit_price ?? postBasePlanRow?.hedge_limit_price),
          referencePrice: hasPostBaseHedge
            ? (postBasePlanRow?.hedge_reference_price ?? postBasePlanRow?.hedge_price)
            : (row?.base_fix_i_reference_price ?? postBasePlanRow?.hedge_reference_price ?? postBasePlanRow?.hedge_price),
          limitMargin: hasPostBaseHedge
            ? postBasePlanRow?.hedge_limit_margin_pln_mwh
            : (row?.base_fix_i_limit_margin_pln_mwh ?? postBasePlanRow?.hedge_limit_margin_pln_mwh),
        });
        const fixII = buildLeg({
          action: row?.fix2_close_action || rowDayCloseAction,
          volume: rowFixIIVolume,
          unit: 'MWh',
          price: row?.fix_ii_price,
          pnl: row?.fix_ii_pnl,
        });
        const ida1 = buildLeg({
          action: row?.ida1_close_action || rowDayCloseAction,
          volume: rowIda1Volume,
          unit: 'MWh',
          price: row?.ida1_price,
          pnl: row?.ida1_pnl,
        });
        const ida2 = buildLeg({
          action: row?.ida2_close_action || rowDayCloseAction,
          volume: rowIda2Volume,
          unit: 'MWh',
          price: row?.ida2_price,
          pnl: row?.ida2_pnl,
        });
        const fixIILegs = availability.fix_ii ? buildComponentLegs(row, 'fix_ii', fixII, rowDayCloseAction) : [];
        const ida1Legs = availability.ida1 ? buildComponentLegs(row, 'ida1', ida1, rowDayCloseAction) : [];
        const ida2Legs = availability.ida2 ? buildComponentLegs(row, 'ida2', ida2, rowDayCloseAction) : [];
        const hasFixIComponentRows = Array.isArray(row?.execution_components?.fix_i);
        const fixIComponents = hasFixIComponentRows
          ? row.execution_components.fix_i
          : [];
        const fixILegs = !availability.fix_i ? [] : hasFixIComponentRows
          ? fixIComponents.map((component, componentIndex) => {
              const role = component?.role;
              const isHedgeRole = role === 'base_hedge_close';
              const componentGuardMeta = isHedgeRole ? getHourlyPostBaseGuardMeta(row) : null;
              return buildLeg({
                action: (component?.action === 'buy' || component?.action === 'sell')
                  ? component.action
                  : rowDayCloseAction,
                volume: component?.volume_mwh,
                unit: 'MWh',
                price: component?.price,
                pnl: component?.pnl,
                roleLabel: isHedgeRole
                  ? (copy.executionBaseHedgeClose || copy.executionBaseClose || 'BASE hedge close')
                  : null,
                roleTone: isHedgeRole ? 'guardBaseClose' : null,
                guardMeta: componentGuardMeta,
                explanation: isHedgeRole
                  ? [
                      componentGuardMeta?.info,
                      componentGuardMeta?.policyInfo,
                    ].filter(Boolean).join(' ')
                  : null,
                popoverKey: isHedgeRole ? `${quarterPathKeyPrefix}:${rowSlotKey}:fix-i-hedge-${componentIndex}` : null,
                orderLimitPrice: isHedgeRole ? postBasePlanRow?.hedge_limit_price : null,
                referencePrice: isHedgeRole
                  ? (postBasePlanRow?.hedge_reference_price ?? postBasePlanRow?.hedge_price)
                  : null,
                limitMargin: isHedgeRole ? postBasePlanRow?.hedge_limit_margin_pln_mwh : null,
              });
            }).filter(hasLegActivity)
          : (hasLegActivity(baseFixI) ? [baseFixI] : []);
        const rawTotalPnl = availability.ida2
          ? toFiniteNumber(row?.hourly_pnl_ida2_close)
          : availability.ida1
            ? toFiniteNumber(row?.hourly_pnl)
            : availability.fix_ii
              ? toFiniteNumber(row?.hourly_pnl_fix2_close)
              : availability.fix_i
                ? toFiniteNumber(row?.hourly_pnl_fix1_mark)
                : null;
        const sumLegPnl = (legs) => (legs || [])
          .map((leg) => toFiniteNumber(leg?.pnl))
          .filter((value) => value !== null)
          .reduce((total, value) => total + value, 0);
        const computedPathPnl = sumLegPnl([
          base,
          fixI,
          ...fixILegs,
          ...fixIILegs,
          ...ida1Legs,
          ...ida2Legs,
        ]);
        const totalPnl = (
          rawTotalPnl === null
          || (Math.abs(Number(rawTotalPnl || 0)) <= 0.001 && Math.abs(computedPathPnl) > 0.001)
        )
          ? computedPathPnl
          : rawTotalPnl;
        const hasActivity = [base, fixI, ...fixILegs, ...fixIILegs, ...ida1Legs, ...ida2Legs].some(hasLegActivity)
          || Math.abs(Number(totalPnl || 0)) > 0.001;
        if (!hasActivity) return null;
        return {
          hour: rowHour,
          slotKey: rowSlotKey,
          slotSortKey: row?.target_ts || row?.targetTs || null,
          quarterPathKey: `${quarterPathKeyPrefix}:${rowSlotKey}`,
          quarterPaths: Array.isArray(row?.quarter_paths) ? row.quarter_paths : [],
          base,
          fixI,
          baseFixI,
          fixILegs,
          fixIILegs,
          ida1Legs,
          ida2Legs,
          totalPnl,
        };
      })
      .filter(Boolean)
      .sort((left, right) => (
        left.slotSortKey && right.slotSortKey
          ? String(left.slotSortKey).localeCompare(String(right.slotSortKey))
          : left.hour - right.hour
      ));
    const renderExecutionLegCell = (leg) => {
      if (!hasLegActivity(leg)) {
        return <span className={darkMode ? 'text-slate-500' : 'text-gray-400'}>-</span>;
      }
      const actionLabel = leg.action ? formatBaseHedgeActionLabel(leg.action) : null;
      const roleLabel = leg.roleLabel || leg.fallbackLabel || null;
      const volumeLabel = Math.abs(Number(leg.volume || 0)) > 0.0001 ? `${formatOrderVolume(leg.volume)} ${leg.unit}` : null;
      const sideLabel = actionLabel || roleLabel;
      const lineOne = [sideLabel, volumeLabel].filter(Boolean).join(' ');
      const renderOrderLimitNote = () => {
        if (leg.orderLimitPrice === null || leg.orderLimitPrice === undefined) return null;
        const parts = [`Limit orderu ${formatNumber(leg.orderLimitPrice)} PLN/MWh`];
        if (leg.referencePrice !== null && leg.referencePrice !== undefined) {
          parts.push(`ref ${formatNumber(leg.referencePrice)}`);
        }
        if (leg.limitMargin !== null && leg.limitMargin !== undefined) {
          parts.push(`margin ${formatNumber(leg.limitMargin)}`);
        }
        return (
          <div className={darkMode ? 'text-[11px] text-cyan-300/90' : 'text-[11px] text-cyan-700'}>
            {parts.join(' · ')}
          </div>
        );
      };
      const renderRoleLabel = () => {
        if (!(roleLabel && actionLabel)) return null;
        if (leg.roleTone === 'guardBaseClose') {
          const explanation = leg.explanation || leg.guardMeta?.info || '';
          const popoverKey = leg.popoverKey || `execution-hedge-${roleLabel}-${leg.action}-${leg.volume}-${leg.price}`;
          const isOpen = Boolean(explanation && isLetsTradeHedgeExplanationOpen === popoverKey);
          const panelClass = leg.guardMeta?.panelClass || (
            darkMode
              ? 'border-cyan-400/30 bg-slate-950 text-cyan-50 shadow-black/40'
              : 'border-cyan-200 bg-white text-cyan-900 shadow-cyan-950/15'
          );
          return (
            <div className="flex flex-wrap items-center gap-1 text-[11px]" title={explanation || undefined}>
              <span className={`inline-flex rounded-full border px-1.5 py-0.5 font-extrabold uppercase leading-none ${leg.guardMeta?.badgeClass || getBaseHedgeGuardMeta({}).badgeClass}`}>
                Guard
              </span>
              <span className={darkMode ? 'font-extrabold uppercase tracking-wide text-cyan-300' : 'font-extrabold uppercase tracking-wide text-cyan-700'}>
                {roleLabel}
              </span>
              {explanation ? (
                <span className="relative inline-flex">
                  <button
                    type="button"
                    onClick={() => setIsLetsTradeHedgeExplanationOpen((prev) => (prev === popoverKey ? '' : popoverKey))}
                    title={explanation}
                    className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 font-bold normal-case transition ${
                      isOpen
                        ? (leg.guardMeta?.badgeClass || (darkMode ? 'border-cyan-300/55 bg-cyan-500/15 text-cyan-100' : 'border-cyan-200 bg-cyan-50 text-cyan-800'))
                        : darkMode
                          ? 'border-slate-600 text-slate-300 hover:bg-slate-900'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                    aria-expanded={isOpen}
                  >
                    <Info className="h-3 w-3" />
                    <span>{copy.hedgeExplainButton || simCopy?.letsTrade?.hedgeExplainButton || 'Dlaczego?'}</span>
                  </button>
                  {isOpen ? (
                    <span className={`absolute left-0 top-full z-50 mt-2 block w-[min(82vw,360px)] rounded-xl border p-3 text-left text-xs normal-case leading-relaxed shadow-2xl ${panelClass}`}>
                      <span className={`absolute -top-1.5 left-7 h-3 w-3 rotate-45 border-l border-t ${
                        leg.guardMeta?.panelClass
                          ? ''
                          : darkMode ? 'border-cyan-400/30 bg-slate-950' : 'border-cyan-200 bg-white'
                      }`} />
                      <span className="mb-1 block font-extrabold">{copy.hedgeExplanationTitle || simCopy?.letsTrade?.hedgeExplanationTitle || roleLabel}</span>
                      <span className="block">{explanation}</span>
                    </span>
                  ) : null}
                </span>
              ) : null}
            </div>
          );
        }
        if (leg.roleTone === 'baseClose') {
          return (
            <div className={darkMode ? 'text-[11px] font-extrabold uppercase tracking-wide text-cyan-300' : 'text-[11px] font-extrabold uppercase tracking-wide text-cyan-700'}>
              {roleLabel}
            </div>
          );
        }
        return (
          <div className={darkMode ? 'text-slate-500' : 'text-gray-400'}>
            {roleLabel}
          </div>
        );
      };
      return (
        <div className="space-y-1">
          <div className="font-semibold">{lineOne || '-'}</div>
          {leg.price !== null ? (
            <div className={darkMode ? 'text-slate-400' : 'text-gray-500'}>@ {formatNumber(leg.price)} PLN/MWh</div>
          ) : null}
          {renderOrderLimitNote()}
          {renderRoleLabel()}
          {leg.pnl !== null && Math.abs(Number(leg.pnl || 0)) > 0.001 ? (
            <div className={getValueColor(leg.pnl)}>{formatSignedNumber(leg.pnl, ' PLN')}</div>
          ) : leg.pnlNote ? (
            <div className={darkMode ? 'text-slate-500' : 'text-gray-400'}>{leg.pnlNote}</div>
          ) : null}
        </div>
      );
    };
    const renderExecutionLegListCell = (legs = []) => {
      const activeLegs = (legs || []).filter(hasLegActivity);
      if (!activeLegs.length) {
        return renderExecutionLegCell(null);
      }
      return (
        <div className="space-y-3">
          {activeLegs.map((leg, index) => (
            <div
              key={`${leg.roleLabel || leg.fallbackLabel || leg.action || 'leg'}-${index}`}
              className={index > 0 ? `border-t pt-2 ${darkMode ? 'border-slate-800' : 'border-gray-200'}` : ''}
            >
              {renderExecutionLegCell(leg)}
            </div>
          ))}
        </div>
      );
    };
    const renderExecutionFixICell = (detail) => {
      const hasFixIActivity = hasLegActivity(detail.fixI);
      const baseFixILegs = (Array.isArray(detail.fixILegs) ? detail.fixILegs : []).filter(hasLegActivity);
      if (!hasFixIActivity && baseFixILegs.length === 0) {
        return <span className={darkMode ? 'text-slate-500' : 'text-gray-400'}>-</span>;
      }
      return (
        <div className="space-y-3">
          {hasFixIActivity ? renderExecutionLegCell(detail.fixI) : null}
          {baseFixILegs.map((leg, index) => (
            <div
              key={`base-fix-i-leg-${index}`}
              className={`border-t pt-2 ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}
            >
              {renderExecutionLegCell(leg)}
            </div>
          ))}
        </div>
      );
    };
    const renderQuarterPathLeg = (label, leg) => {
      const hasLeg = Boolean(leg?.action) || Math.abs(Number(leg?.volume_mwh || 0)) > 0.0001;
      const volumeMwh = Number(leg?.volume_mwh || 0);
      const volumeMw = Number(leg?.volume_mw || 0);
      const volumeText = hasLeg
        ? `${formatOrderVolume(volumeMwh)} MWh${Math.abs(volumeMw) > 0.0001 ? ` (${formatOrderVolume(volumeMw)} MW)` : ''}`
        : '-';
      const legPnl = Number(leg?.pnl || 0);
      return (
        <div className={`rounded-md border border-l-4 px-2 py-1.5 ${
          hasLeg
            ? getAuctionStageCardClass(leg?.action, leg?.pnl, label)
            : darkMode ? 'border-slate-700 bg-slate-950/40' : 'border-gray-200 bg-white/70'
        }`}>
          <div className="flex items-center justify-between gap-2">
            <span className={`text-[11px] font-semibold uppercase ${getAuctionStageLabelClass(label)}`}>{label}</span>
            <span className={`inline-flex rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${getAuctionActionBadgeClass(leg?.action)}`}>
              {leg?.action ? formatBaseHedgeActionLabel(leg.action) : '-'}
            </span>
          </div>
          <div className={`mt-1 text-[11px] ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
            {volumeText}{hasLeg && leg?.price !== null && leg?.price !== undefined ? ` @ ${formatNumber(leg.price)} PLN/MWh` : ''}
          </div>
          {hasLeg || Math.abs(legPnl) > 0.001 ? (
            <div className={`text-[11px] ${getValueColor(leg?.pnl)}`}>
              {formatNumber(leg?.pnl)} PLN
            </div>
          ) : null}
        </div>
      );
    };
    const renderQuarterPathDetails = (detail) => {
      const quarterPaths = Array.isArray(detail?.quarterPaths) ? detail.quarterPaths : [];
      if (!quarterPaths.length) {
        return (
          <div className={`rounded-lg border border-dashed px-3 py-3 text-xs ${
            darkMode ? 'border-slate-700 bg-slate-950/40 text-slate-400' : 'border-gray-200 bg-gray-50 text-gray-500'
          }`}>
            {copy.noQuarterPath || 'No quarter-hour breakdown for this hour.'}
          </div>
        );
      }
      return (
        <div className={`rounded-xl border px-3 py-3 ${
          darkMode ? 'border-slate-700 bg-slate-950/55' : 'border-gray-200 bg-white/80'
        }`}>
          <div className="mb-2 flex items-center justify-between gap-3">
            <div className={`text-[11px] font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {typeof copy.quarterPathTitle === 'function'
                ? copy.quarterPathTitle(formatHourLabel(detail.hour))
                : `15m path for ${formatHourLabel(detail.hour)}`}
            </div>
            <div className={`text-[11px] font-bold ${getValueColor(detail.totalPnl)}`}>
              {detail.totalPnl === null ? '-' : formatSignedNumber(detail.totalPnl, ' PLN')}
            </div>
          </div>
          <div className="grid gap-2 lg:grid-cols-2 2xl:grid-cols-4">
            {quarterPaths.map((quarterPath) => (
              <div
                key={`${detail.quarterPathKey}-${quarterPath.quarter_label}`}
                className={`rounded-lg border px-2.5 py-2 text-[11px] ${
                  darkMode ? 'border-slate-700 bg-slate-900/40' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className={`font-bold ${darkMode ? 'text-slate-100' : 'text-gray-900'}`}>{quarterPath.quarter_label || '-'}</span>
                  <span className={`font-semibold ${getValueColor(quarterPath.quarterly_pnl)}`}>{formatNumber(quarterPath.quarterly_pnl)} PLN</span>
                </div>
                <div className="grid gap-1.5">
                  {renderQuarterPathLeg('Fix II', quarterPath.fix2_leg)}
                  {renderQuarterPathLeg('IDA1', quarterPath.ida1_leg)}
                  {renderQuarterPathLeg('IDA2', quarterPath.ida2_leg)}
                </div>
                <div className={`mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  <span>{copy.curveVolume || 'Curve'}: {formatNumber(quarterPath.curve_net_volume)}</span>
                  <span>{copy.pathLabel || 'Path'}: {quarterPath.base_carry_path || '-'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };
    const panelClass = darkMode
      ? 'border-slate-700 bg-slate-950/55'
      : 'border-gray-200 bg-white';
    const pathGuardSource = summary?.base_simulation || basePretradeDecision || {};
    const pathBuyGuardSummary = getNegativeFixIBuyGuardSummary(pathGuardSource);
    const pathBaseCarrySummary = getBaseCarryGuardSummary(summary || {}, rows || []);
    const hasPathGuardIntervention = pathBuyGuardSummary.hasIntervention || pathBaseCarrySummary.hasIntervention;
    return (
      <section className={`mb-4 rounded-xl border p-4 ${panelClass}`}>
        {showHeader ? (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className={`text-base font-extrabold ${darkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                {copy.executionPathTitle}
              </div>
              <div className={`mt-1 max-w-4xl text-sm ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                {copy.executionPathBody}
              </div>
            </div>
            {onToggle ? (
              <button
                type="button"
                onClick={onToggle}
                className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                  darkMode
                    ? 'border-slate-600 bg-slate-900 text-slate-200 hover:bg-slate-800'
                    : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
              >
                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {expanded ? copy.executionPathHide : copy.executionPathShow}
              </button>
            ) : null}
          </div>
        ) : onToggle ? (
          <div className="mb-3 flex justify-end">
            <button
              type="button"
              onClick={onToggle}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                darkMode
                  ? 'border-slate-600 bg-slate-900 text-slate-200 hover:bg-slate-800'
                  : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {expanded ? copy.executionPathHide : copy.executionPathShow}
            </button>
          </div>
        ) : null}
        {notice ? (
          <div className={`mt-3 flex gap-2 rounded-xl border px-3 py-2 text-xs leading-5 ${
            darkMode
              ? 'border-cyan-300/30 bg-cyan-500/10 text-cyan-100'
              : 'border-cyan-200 bg-cyan-50 text-cyan-900'
          }`}>
            <Info size={15} className="mt-0.5 shrink-0" />
            <div>
              <div className="font-extrabold">{notice.title}</div>
              <div className={darkMode ? 'text-cyan-100/80' : 'text-cyan-900/75'}>{notice.body}</div>
            </div>
          </div>
        ) : null}
        {hasPathGuardIntervention ? (
          <div className={`mt-3 rounded-xl border px-3 py-2 text-xs leading-5 ${
            darkMode
              ? 'border-cyan-300/30 bg-cyan-500/10 text-cyan-100'
              : 'border-cyan-200 bg-cyan-50 text-cyan-900'
          }`}>
            <div className="flex items-start gap-2">
              <ShieldCheck size={15} className="mt-0.5 shrink-0" />
              <div>
                <div className="font-extrabold">{baseHedgeCopy.guardInterventionTitle}</div>
                {pathBuyGuardSummary.hasIntervention ? (
                  <div className={darkMode ? 'text-cyan-100/85' : 'text-cyan-900/80'}>
                    Fix I: {baseHedgeCopy.fixIBuyGuardLabel} · {baseHedgeCopy.guardInterventionHours(pathBuyGuardSummary.blockedHours, pathBuyGuardSummary.cappedHours)}
                    {pathBuyGuardSummary.hourLabels.length ? ` · ${pathBuyGuardSummary.hourLabels.join(', ')}` : ''}
                  </div>
                ) : null}
                {pathBaseCarrySummary.hasIntervention ? (
                  <div className={darkMode ? 'text-cyan-100/85' : 'text-cyan-900/80'}>
                    {baseHedgeCopy.baseCarryGuardLabel}: {baseHedgeCopy.forcedFix2Label} {formatNumber(pathBaseCarrySummary.forcedFix2)} MWh · {baseHedgeCopy.blockedIda1Label} {formatNumber(pathBaseCarrySummary.blockedIda1)} MWh
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
        <div className={`grid gap-3 ${showHeader || notice || hasPathGuardIntervention ? 'mt-4' : ''} ${compact ? 'md:grid-cols-5' : 'md:grid-cols-2 xl:grid-cols-5'}`}>
          {stages.map((stage) => {
            const statusMeta = getExecutionStageStatusMeta(stage, copy);
            const pnlLabel = stage.key === 'base'
              ? (
                executionPnlBridge.key === 'fix_i'
                  ? (copy.executionBaseMarkFixI || copy.columns?.basePnl || 'BASE PnL')
                  : (copy.columns?.basePnl || 'BASE PnL')
              )
              : stage.status === 'preview'
              ? (copy.executionPreviewPnl || copy.executionPnl)
              : stage.key === 'fix_ii'
              ? (copy.executionFixIIPnl || copy.executionPnl)
              : copy.executionPnl;
            return (
              <div key={stage.key} className={`rounded-xl border border-l-4 p-3 ${getAuctionStageCardClass(null, stage.pnl, stage.label)}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className={`text-sm font-extrabold ${darkMode ? 'text-slate-100' : 'text-gray-900'}`}>{stage.label}</div>
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-bold ${statusMeta.className}`}>
                    {statusMeta.label}
                  </span>
                </div>
                <div className={`mt-3 text-xl font-extrabold tabular-nums ${stage.status === 'done' ? (darkMode ? 'text-slate-50' : 'text-gray-900') : 'text-gray-500'}`}>
                  {stage.totalVolume > 0.0001 ? `${formatNumber(stage.totalVolume)} ${stage.unit}` : '-'}
                </div>
                <div className={`mt-2 grid gap-1 text-[11px] ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  <div>{copy.executionAvg}: {stage.avgPrice === null ? '-' : `${formatNumber(stage.avgPrice)} PLN/MWh`}</div>
                  <div>{copy.executionRows}: {formatInteger(stage.details.length)}</div>
                  {stage.carryVolume > 0.0001 ? (
                    <div>{copy.executionCarry}: {formatNumber(stage.carryVolume)} MWh</div>
                  ) : null}
                  {stage.key === 'base' && stage.setupVolumeMw > 0.0001 ? (
                    <div>{copy.executionBaseSetup || 'BASE context'}: {formatBaseHedgeActionLabel(stage.baseAction)} {formatNumber(stage.setupVolumeMw)} MW</div>
                  ) : null}
                  {stage.key === 'base' && stage.sourceLabel ? (
                    <div>
                      <span className={`inline-flex max-w-full items-center rounded-full border px-2 py-0.5 font-semibold ${stage.sourceBadgeClass}`}>
                        {stage.sourceLabel}
                      </span>
                    </div>
                  ) : null}
                  {stage.pnl !== null ? (
                    <div className={getValueColor(stage.pnl)}>{pnlLabel}: {formatSignedNumber(stage.pnl, ' PLN')}</div>
                  ) : stage.key === 'fix_i' && stage.status === 'done' ? (
                    <div className={darkMode ? 'text-slate-400' : 'text-gray-500'}>
                      {availability.fix_ii
                        ? (copy.executionPnlClosedInFixII || 'PnL: included in Fix II')
                        : (copy.executionPnlOpen || 'PnL: waiting for Fix II close')}
                    </div>
                  ) : null}
                  {stage.key === 'fix_i' && stage.baseCloseVolume > 0.0001 ? (
                    <div>
                      {copy.executionBaseClose || 'BASE close'}: {formatBaseHedgeActionLabel('buy')} {formatNumber(stage.baseCloseBuyVolume)} / {formatBaseHedgeActionLabel('sell')} {formatNumber(stage.baseCloseSellVolume)} MWh
                    </div>
                  ) : null}
                  {(stage.buyVolume > 0.0001 || stage.sellVolume > 0.0001) ? (
                    <div>
                      {formatBaseHedgeActionLabel('buy')}: {formatNumber(stage.buyVolume)} / {formatBaseHedgeActionLabel('sell')}: {formatNumber(stage.sellVolume)}
                    </div>
                  ) : null}
	                </div>
	              </div>
	            );
	          })}
                </div>
        {expanded ? (
          <div className={`mt-4 rounded-xl border ${darkMode ? 'border-slate-700 bg-slate-950/40' : 'border-gray-200 bg-gray-50/80'}`}>
            <div className={`border-b px-3 py-2 text-xs font-bold uppercase ${darkMode ? 'border-slate-700 text-slate-300' : 'border-gray-200 text-gray-600'}`}>
              {copy.executionPathDetails}
            </div>
            {hourlyExecutionDetails.length ? (
              <div className="max-h-[360px] overflow-auto">
                <table className="w-full min-w-[1120px] text-sm">
                  <thead className={darkMode ? 'bg-slate-900 text-slate-400' : 'bg-white text-gray-500'}>
                    <tr>
                      {(copy.executionTableHeaders || ['Hour', 'BASE', 'Fix I', 'Fix II', 'IDA1', 'IDA2', 'Hour PnL']).map((label) => (
                        <th key={label} className="px-3 py-2 text-left text-[11px] font-semibold uppercase">{label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {hourlyExecutionDetails.map((detail) => {
                      const hasQuarterPaths = Array.isArray(detail.quarterPaths) && detail.quarterPaths.length > 0;
                      const isQuarterPathExpanded = Boolean(expandedExecutionQuarterPaths[detail.quarterPathKey]);
                      return (
                        <React.Fragment key={`execution-hour-${detail.quarterPathKey}`}>
                          <tr className={`border-t align-top ${darkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                            <td className="px-3 py-3 font-semibold">
                              <div>{formatHourLabel(detail.hour)}</div>
                              {hasQuarterPaths ? (
                                <button
                                  type="button"
                                  onClick={() => toggleExecutionQuarterPathExpanded(detail.quarterPathKey)}
                                  className={`mt-1 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition ${
                                    isQuarterPathExpanded
                                      ? darkMode
                                        ? 'border-cyan-400/50 bg-cyan-500/15 text-cyan-100'
                                        : 'border-cyan-200 bg-cyan-50 text-cyan-800'
                                      : darkMode
                                        ? 'border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800'
                                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                                  }`}
                                >
                                  {isQuarterPathExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                  {isQuarterPathExpanded
                                    ? (copy.collapse15mPath || 'Hide 15m')
                                    : (copy.expand15mPath || 'Show 15m')}
                                </button>
                              ) : null}
                            </td>
                            <td className="px-3 py-3 tabular-nums">{renderExecutionLegCell(detail.base)}</td>
                            <td className="px-3 py-3 tabular-nums">{renderExecutionFixICell(detail)}</td>
                            <td className="px-3 py-3 tabular-nums">{renderExecutionLegListCell(detail.fixIILegs)}</td>
                            <td className="px-3 py-3 tabular-nums">{renderExecutionLegListCell(detail.ida1Legs)}</td>
                            <td className="px-3 py-3 tabular-nums">{renderExecutionLegListCell(detail.ida2Legs)}</td>
                            <td className={`px-3 py-3 font-bold tabular-nums ${getValueColor(detail.totalPnl)}`}>
                              {detail.totalPnl === null ? '-' : formatSignedNumber(detail.totalPnl, ' PLN')}
                            </td>
                          </tr>
                          {isQuarterPathExpanded ? (
                            <tr className={`border-t ${darkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                              <td colSpan={(copy.executionTableHeaders || []).length || 6} className="px-3 py-3">
                                {renderQuarterPathDetails(detail)}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={`px-3 py-4 text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                {copy.executionNoRows}
              </div>
            )}
          </div>
        ) : null}
      </section>
    );
  };

  const hourlyBreakdownHeaders = resultsCopy.hourlyHeaders || RESULTS_UI_COPY.en.hourlyHeaders;

  const formatErrorPreview = useCallback((rawText, fallbackLabel) => {
    if (!rawText) return fallbackLabel;

    const titleMatch = rawText.match(/<title>(.*?)<\/title>/i);
    const h1Match = rawText.match(/<h1[^>]*>(.*?)<\/h1>/i);
    const preferred = titleMatch?.[1] || h1Match?.[1] || rawText;

    const cleaned = preferred
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    return cleaned || fallbackLabel;
  }, []);

  const parseJsonResponse = useCallback(async (response, label) => {
    const rawText = await response.text();
    if (!response.ok) {
      const preview = formatErrorPreview(rawText, `HTTP ${response.status}`);
      throw new Error(`${label} failed: ${preview}`);
    }
    try {
      return rawText ? JSON.parse(rawText) : {};
    } catch (err) {
      const preview = formatErrorPreview(rawText, 'empty response');
      throw new Error(`${label} returned non-JSON response: ${preview}`);
    }
  }, [formatErrorPreview]);

  const getResultsRangeKey = useCallback((view = 'all') => `${SIM_V5_CLIENT_CACHE_VERSION}:${startDate}:${endDate}:${view}:${simulationVariant}`, [endDate, simulationVariant, startDate]);
  const getOrderPlanCacheKey = (kind = 'detail') => `${SIM_V5_CLIENT_CACHE_VERSION}:${kind}:${simulationVariant}:${date}`;
  const getStatusCacheKey = (targetDate = date) => `${SIM_V5_CLIENT_CACHE_VERSION}:${targetDate}`;
  const getNoblePreviewCacheKey = useCallback((mode = 'ida1', targetDate = date) => `${SIM_V5_CLIENT_CACHE_VERSION}:${simulationVariant}:${mode}:${targetDate}`, [date, simulationVariant]);
  const resetExternalNoblePreview = useCallback((mode = null, { clearCache = false } = {}) => {
    const modes = mode ? [mode] : ['fix_i', 'ida1', 'ida2'];
    latestNoblePreviewRequestRef.current = modes.reduce((acc, modeKey) => ({
      ...acc,
      [modeKey]: (acc[modeKey] || 0) + 1,
    }), { ...latestNoblePreviewRequestRef.current });
    if (clearCache && date) {
      modes.forEach((modeKey) => noblePreviewCacheRef.current.delete(getNoblePreviewCacheKey(modeKey)));
    }
    if (!mode || mode === 'fix_i') {
      setExternalFixIPreviewRows([]);
      setExternalFixIPreviewLoading(false);
      setExternalFixIPreviewError('');
      setExternalFixIPreviewFailedKey('');
    }
    if (!mode || mode === 'ida1') {
      setExternalIda1PreviewRows([]);
      setExternalIda1PreviewLoading(false);
      setExternalIda1PreviewError('');
      setExternalIda1PreviewFailedKey('');
    }
    if (!mode || mode === 'ida2') {
      setExternalIda2PreviewRows([]);
      setExternalIda2PreviewLoading(false);
      setExternalIda2PreviewError('');
      setExternalIda2PreviewFailedKey('');
    }
  }, [date, getNoblePreviewCacheKey]);

  const applyStatusPayload = (statusJson, { preserveOrderPlanMeta = false, preserveSectionState = false } = {}) => {
    setStatusData(statusJson.data || null);
    if (!preserveSectionState) {
      setOrderPlan([]);
      resetExternalNoblePreview(null, { clearCache: true });
    }
    if (!preserveOrderPlanMeta) {
      setOrderPlanMeta(null);
    }
    if (!preserveSectionState) {
      runDetailsByKeyRef.current = {};
      setRunDetailsByKey({});
      setSelectedRunIds({ train: null, predict: null, settle: null });
      setRunPanelMode({ train: 'latest', predict: 'latest', settle: 'latest' });
      setSettleResultMode('ida2');
      setPredictResultMode('fix2');
    }
  };

  const readCachedStatus = (cacheKey) => {
    const inMemory = statusCacheRef.current.get(cacheKey);
    if (inMemory) {
      return inMemory;
    }
    try {
      const raw = window.sessionStorage.getItem(`fix2-v5-sim-status:${cacheKey}`);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.status === 'success') {
        statusCacheRef.current.set(cacheKey, parsed);
        return parsed;
      }
    } catch (err) {
      console.warn('Failed to read cached Simulation v5 status', err);
    }
    return null;
  };

  const writeCachedStatus = (cacheKey, payload) => {
    statusCacheRef.current.set(cacheKey, payload);
    try {
      window.sessionStorage.setItem(`fix2-v5-sim-status:${cacheKey}`, JSON.stringify(payload));
    } catch (err) {
      console.warn('Failed to cache Simulation v5 status', err);
    }
  };

  const readCachedOrderPlan = (cacheKey) => {
    const inMemory = orderPlanCacheRef.current.get(cacheKey);
    if (inMemory) {
      return inMemory;
    }
    try {
      const raw = window.sessionStorage.getItem(`fix2-v5-sim-order-plan:${cacheKey}`);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.status === 'success') {
        orderPlanCacheRef.current.set(cacheKey, parsed);
        return parsed;
      }
    } catch (err) {
      console.warn('Failed to read cached Simulation v5 order plan', err);
    }
    return null;
  };

  const writeCachedOrderPlan = (cacheKey, payload) => {
    orderPlanCacheRef.current.set(cacheKey, payload);
    try {
      window.sessionStorage.setItem(`fix2-v5-sim-order-plan:${cacheKey}`, JSON.stringify(payload));
    } catch (err) {
      console.warn('Failed to cache Simulation v5 order plan', err);
    }
  };

  const readCachedResults = (rangeKey) => {
    const inMemory = resultsCacheRef.current.get(rangeKey);
    if (inMemory) {
      return inMemory;
    }
    try {
      const raw = window.sessionStorage.getItem(`fix2-v5-sim-results:${rangeKey}`);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      if (parsed?.status === 'success') {
        resultsCacheRef.current.set(rangeKey, parsed);
        return parsed;
      }
    } catch (err) {
      console.warn('Failed to read cached Simulation v5 results', err);
    }
    return null;
  };

  const writeCachedResults = (rangeKey, payload) => {
    resultsCacheRef.current.set(rangeKey, payload);
    try {
      window.sessionStorage.setItem(`fix2-v5-sim-results:${rangeKey}`, JSON.stringify(payload));
    } catch (err) {
      console.warn('Failed to cache Simulation v5 results', err);
    }
  };

  const clearResultsCache = () => {
    resultsCacheRef.current.clear();
    try {
      Object.keys(window.sessionStorage)
        .filter((key) => key.startsWith('fix2-v5-sim-results:'))
        .forEach((key) => window.sessionStorage.removeItem(key));
    } catch (err) {
      console.warn('Failed to clear cached Simulation v5 results', err);
    }
  };

  const clearStatusCache = () => {
    statusCacheRef.current.clear();
    try {
      Object.keys(window.sessionStorage)
        .filter((key) => key.startsWith('fix2-v5-sim-status:'))
        .forEach((key) => window.sessionStorage.removeItem(key));
    } catch (err) {
      console.warn('Failed to clear cached Simulation v5 status', err);
    }
  };

  const clearOrderPlanCache = () => {
    orderPlanCacheRef.current.clear();
    orderAuditCacheRef.current.clear();
    orderAuditLoadedForDateRef.current = '';
    try {
      Object.keys(window.sessionStorage)
        .filter((key) => key.startsWith('fix2-v5-sim-order-plan:'))
        .forEach((key) => window.sessionStorage.removeItem(key));
    } catch (err) {
      console.warn('Failed to clear cached Simulation v5 order plan', err);
    }
  };

  const getStageRuns = (stage) => (statusData?.[`${stage}_runs`] || []);

  const loadRunDetails = useCallback(async (stage, runId) => {
    const key = `${stage}:${runId}`;
    const cached = runDetailsByKeyRef.current[key];
    if (cached) return cached;
    const response = await fetchGet(`/v5-sim/run-details?stage=${stage}&run_id=${runId}`);
    const payload = await parseJsonResponse(response, `${stage} run details`);
    if (payload.status !== 'success') {
      throw new Error(payload.message || `Failed to load ${stage} run details`);
    }
    setRunDetailsByKey((prev) => {
      if (prev[key]) {
        runDetailsByKeyRef.current = prev;
        return prev;
      }
      const next = { ...prev, [key]: payload };
      runDetailsByKeyRef.current = next;
      return next;
    });
    return payload;
  }, [fetchGet, parseJsonResponse]);

  const loadCoreData = async ({ force = false, preserveOrderPlanMeta = false, targetDate = date } = {}) => {
    const statusDate = String(targetDate || date || '').slice(0, 10);
    const cacheKey = getStatusCacheKey(statusDate);
    const cachedStatus = readCachedStatus(cacheKey);
    const shouldBypassCachedStatus = Boolean(statusDate && statusDate >= getWarsawDateOffset(0));

    if (cachedStatus && !force && !shouldBypassCachedStatus) {
      applyStatusPayload(cachedStatus, { preserveOrderPlanMeta, preserveSectionState: true });
      return cachedStatus;
    }

    if (cachedStatus && !force && shouldBypassCachedStatus) {
      applyStatusPayload(cachedStatus, { preserveOrderPlanMeta, preserveSectionState: true });
    }

    const requestId = latestStatusRequestRef.current + 1;
    latestStatusRequestRef.current = requestId;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date: statusDate });
      if (force) {
        params.set('force_refresh', 'true');
      }
      params.set('_', String(Date.now()));
      const statusResp = await fetchGet(`/v5-sim/status?${params.toString()}`);
      const statusJson = await parseJsonResponse(statusResp, 'V5 status');

      if (statusJson.status !== 'success') {
        throw new Error(statusJson.message || 'Failed to load status');
      }
      writeCachedStatus(cacheKey, statusJson);
      if (requestId === latestStatusRequestRef.current) {
        applyStatusPayload(statusJson, { preserveOrderPlanMeta, preserveSectionState: !force });
      }
      return statusJson;
    } finally {
      if (requestId === latestStatusRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const loadOrderPlan = async ({ force = false } = {}) => {
    const cacheKey = getOrderPlanCacheKey('detail');
    const cachedPlan = readCachedOrderPlan(cacheKey);
    const cachedRows = Array.isArray(cachedPlan?.rows) ? cachedPlan.rows : [];
    const summarySaysFix2PlanReady = (
      Number(statusData?.predict?.plan_rows || 0) > 0
      || Boolean(orderPlanMeta?.market_readiness?.fix_ii?.ready)
      || (Array.isArray(orderPlanMeta?.fix2_issue_date) && orderPlanMeta.fix2_issue_date.length > 0)
    );
    const shouldBypassEmptyCachedPlan = Boolean(
      cachedPlan
      && cachedRows.length === 0
      && !force
      && summarySaysFix2PlanReady
    );

    if (cachedPlan && !shouldBypassEmptyCachedPlan && (!orderPlanMeta || force)) {
      startTransition(() => {
        setOrderPlan(cachedRows);
        setOrderPlanMeta(cachedPlan.metadata || null);
      });
    }

    if (cachedPlan && !force && !shouldBypassEmptyCachedPlan) {
      return cachedPlan;
    }

    const requestId = latestOrderPlanRequestRef.current + 1;
    latestOrderPlanRequestRef.current = requestId;
    setOrderPlanLoading(true);
    try {
      const planQuery = new URLSearchParams({ date, simulation_variant: simulationVariant });
      if (force) {
        planQuery.set('force_refresh', 'true');
        planQuery.set('_', String(Date.now()));
      }
      const planResp = await fetchGet(`/v5-sim/order-plan?${planQuery.toString()}`);
      let planJson = { status: 'success', rows: [], metadata: null };
      try {
        const parsedPlan = await parseJsonResponse(planResp, 'V5 order plan');
        if (parsedPlan.status === 'success') {
          planJson = parsedPlan;
        } else {
          const message = String(parsedPlan.message || '').toLowerCase();
          const isExpectedMissingPlan =
            message.includes('no order-plan analysis available')
            || message.includes('failed to fetch predictions data')
            || message.includes('no prediction')
            || message.includes('missing');
          if (!isExpectedMissingPlan) {
            throw new Error(parsedPlan.message || 'Failed to load order plan');
          }
        }
      } catch (planErr) {
        const message = String(planErr?.message || '').toLowerCase();
        const isExpectedMissingPlan =
          message.includes('failed to fetch predictions data')
          || message.includes('no order-plan analysis available')
          || message.includes('no prediction')
          || message.includes('missing');
        if (!isExpectedMissingPlan) {
          throw planErr;
        }
      }

      writeCachedOrderPlan(cacheKey, planJson);
      if (requestId === latestOrderPlanRequestRef.current) {
        startTransition(() => {
          setOrderPlan(planJson.rows || []);
          setOrderPlanMeta(planJson.metadata || null);
        });
      }
      return planJson;
    } finally {
      if (requestId === latestOrderPlanRequestRef.current) {
        setOrderPlanLoading(false);
      }
    }
  };

  const loadOrderPlanSummary = async ({ force = false } = {}) => {
    const cacheKey = getOrderPlanCacheKey('summary');
    const cachedPlan = readCachedOrderPlan(cacheKey);

    if (cachedPlan && (!orderPlanMeta || force)) {
      startTransition(() => {
        setOrderPlanMeta(cachedPlan.metadata || null);
      });
    }

    if (cachedPlan && !force) {
      return cachedPlan;
    }

    const requestId = latestOrderPlanSummaryRequestRef.current + 1;
    latestOrderPlanSummaryRequestRef.current = requestId;
    try {
      const planResp = await fetchGet(`/v5-sim/order-plan-summary?date=${date}`);
      const planJson = await parseJsonResponse(planResp, 'V5 order plan summary');
      if (planJson.status !== 'success') {
        throw new Error(planJson.message || 'Failed to load order plan summary');
      }

      writeCachedOrderPlan(cacheKey, planJson);
      if (requestId === latestOrderPlanSummaryRequestRef.current) {
        startTransition(() => {
          setOrderPlanMeta(planJson.metadata || null);
        });
      }
      return planJson;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const loadPredictionInputData = useCallback(async ({ force = false, targetDate = date } = {}) => {
    const dataDate = targetDate || date;
    if (!dataDate) {
      return null;
    }
    const cacheKey = dataDate;
    const cachedPayload = predictionInputCacheRef.current.get(cacheKey);
    if (cachedPayload && !force) {
      setPredictionInputData(cachedPayload);
      setPredictionInputError('');
      return cachedPayload;
    }

    const requestId = latestPredictionInputRequestRef.current + 1;
    latestPredictionInputRequestRef.current = requestId;
    setPredictionInputLoading(true);
    setPredictionInputError('');
    try {
      const query = new URLSearchParams({ date: dataDate });
      if (force) {
        query.set('force_refresh', 'true');
        query.set('_', String(Date.now()));
      }
      const response = await fetchGet(`/v5-sim/prediction-input-data?${query.toString()}`);
      const payload = await parseJsonResponse(response, 'V5 prediction input data');
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to load prediction input data');
      }
      predictionInputCacheRef.current.set(cacheKey, payload);
      if (requestId === latestPredictionInputRequestRef.current) {
        setPredictionInputData(payload);
        setPredictionInputError('');
      }
      return payload;
    } catch (err) {
      console.error(err);
      if (requestId === latestPredictionInputRequestRef.current) {
        setPredictionInputError(err.message || 'Failed to load prediction input data');
      }
      return null;
    } finally {
      if (requestId === latestPredictionInputRequestRef.current) {
        setPredictionInputLoading(false);
      }
    }
  }, [date, fetchGet, parseJsonResponse]);

  const loadOrderAudit = useCallback(async ({ force = false, targetDate = date } = {}) => {
    const auditDate = targetDate || date;
    if (!auditDate) {
      return null;
    }
    const auditCacheKey = `${simulationVariant}:${auditDate}`;
    const cachedPayload = orderAuditCacheRef.current.get(auditCacheKey);
    if (cachedPayload && !force) {
      setOrderAuditData(cachedPayload);
      setOrderAuditError('');
    }

    const requestId = latestOrderAuditRequestRef.current + 1;
    latestOrderAuditRequestRef.current = requestId;
    setOrderAuditLoading(true);
    setOrderAuditError('');
    try {
      const query = new URLSearchParams({ date: auditDate });
      query.set('simulation_variant', simulationVariant);
      if (force) {
        query.set('force_refresh', 'true');
      }
      query.set('_', String(Date.now()));
      const response = await fetchGet(`/v5-sim/order-audit?${query.toString()}`, {
        timeout: NOBLE_PREVIEW_TIMEOUT_MS,
      });
      const payload = await parseJsonResponse(response, 'V5 order audit');
    if (payload.status !== 'success') {
      throw new Error(payload.message || 'Failed to load order audit');
    }
      orderAuditCacheRef.current.set(auditCacheKey, payload);
      orderAuditLoadedForDateRef.current = auditCacheKey;
      if (requestId === latestOrderAuditRequestRef.current) {
        setOrderAuditData(payload);
        setOrderAuditError('');
      }
      return payload;
    } catch (err) {
      console.error(err);
      if (requestId === latestOrderAuditRequestRef.current) {
        setOrderAuditError(err.message || 'Failed to load order audit');
      }
      return null;
    } finally {
      if (requestId === latestOrderAuditRequestRef.current) {
        setOrderAuditLoading(false);
      }
    }
  }, [date, fetchGet, parseJsonResponse, simulationVariant]);

  const loadDayRadar = useCallback(async ({ force = false, targetDate = date } = {}) => {
    const radarDate = targetDate || date;
    if (!radarDate) {
      return null;
    }

    const requestId = latestDayRadarRequestRef.current + 1;
    latestDayRadarRequestRef.current = requestId;
    setDayRadarLoading(true);
    setDayRadarError('');
    try {
      const query = new URLSearchParams({ date: radarDate, simulation_variant: simulationVariant });
      if (force) {
        query.set('force_refresh', 'true');
      }
      query.set('_', String(Date.now()));
      const response = await fetchGet(`/v5-sim/day-radar?${query.toString()}`);
      const payload = await parseJsonResponse(response, 'Simulation day radar');
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to load day radar');
      }
      if (requestId === latestDayRadarRequestRef.current) {
        setDayRadarData(payload);
        setDayRadarError('');
        const runStatus = String(payload.pipeline_run?.status || payload.snapshot?.status || '').toLowerCase();
        setDayRadarRunning(['requested', 'pending', 'running', 'provisioning'].includes(runStatus));
      }
      return payload;
    } catch (err) {
      console.error(err);
      if (requestId === latestDayRadarRequestRef.current) {
        setDayRadarError(err.message || 'Failed to load day radar');
      }
      return null;
    } finally {
      if (requestId === latestDayRadarRequestRef.current) {
        setDayRadarLoading(false);
      }
    }
  }, [date, fetchGet, parseJsonResponse, simulationVariant]);

  const runDayRadar = useCallback(async () => {
    if (!date) return;
    const activeSnapshot = String(dayRadarData?.date || '').slice(0, 10) === date ? dayRadarData?.snapshot : null;
    const activePayload = activeSnapshot?.payload || {};
    const activeStatus = String(activeSnapshot?.status || '').toLowerCase();
    if (activeStatus === 'blocked_missing_predict' || activePayload.status === 'blocked_missing_predict') return;
    setDayRadarRunning(true);
    setDayRadarError('');
    setActionMessage('');
    try {
      const response = await fetchPostJson('/v5-sim/day-radar/run', {
        date,
        simulation_variant: simulationVariant,
        force_refresh: true,
        include_web: true,
      });
      const payload = await parseJsonResponse(response, 'Simulation day radar run');
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to start day radar');
      }
      setActionMessage('Goose risk został uruchomiony. Odświeżam status w tle.');
      await loadDayRadar({ force: true, targetDate: date });
    } catch (err) {
      console.error(err);
      setDayRadarRunning(false);
      setDayRadarError(err.message || 'Failed to start day radar');
    }
  }, [date, dayRadarData, fetchPostJson, loadDayRadar, parseJsonResponse, simulationVariant]);

  const loadNoblePreview = useCallback(async ({ mode = 'ida1', force = false, targetDate = date } = {}) => {
    const previewDate = targetDate || date;
    if (!previewDate || !['fix_i', 'ida1', 'ida2'].includes(mode)) {
      return { status: 'success', rows: [] };
    }

    const cacheKey = getNoblePreviewCacheKey(mode, previewDate);
    const cachedPreview = noblePreviewCacheRef.current.get(cacheKey);
    const cachedPreviewRows = Array.isArray(cachedPreview?.rows) ? cachedPreview.rows : [];
    if (cachedPreview && !force) {
      startTransition(() => {
        if (mode === 'fix_i') {
          setExternalFixIPreviewRows(cachedPreviewRows);
          setExternalFixIPreviewError('');
          setExternalFixIPreviewFailedKey('');
        } else if (mode === 'ida1') {
          setExternalIda1PreviewRows(cachedPreviewRows);
          setExternalIda1PreviewError('');
          setExternalIda1PreviewFailedKey('');
        } else {
          setExternalIda2PreviewRows(cachedPreviewRows);
          setExternalIda2PreviewError('');
          setExternalIda2PreviewFailedKey('');
        }
      });
      return cachedPreview;
    }

    const requestId = (latestNoblePreviewRequestRef.current[mode] || 0) + 1;
    latestNoblePreviewRequestRef.current = {
      ...latestNoblePreviewRequestRef.current,
      [mode]: requestId,
    };
    if (mode === 'fix_i') {
      setExternalFixIPreviewLoading(true);
      setExternalFixIPreviewError('');
    } else if (mode === 'ida1') {
      setExternalIda1PreviewLoading(true);
      setExternalIda1PreviewError('');
    } else {
      setExternalIda2PreviewLoading(true);
      setExternalIda2PreviewError('');
    }
    if (force) {
      if (mode === 'fix_i') {
        setExternalFixIPreviewFailedKey('');
      } else if (mode === 'ida1') {
        setExternalIda1PreviewFailedKey('');
      } else {
        setExternalIda2PreviewFailedKey('');
      }
    }
    try {
      const query = new URLSearchParams({ date: previewDate, mode, simulation_variant: simulationVariant });
      if (force) {
        query.set('force_refresh', 'true');
        query.set('_', String(Date.now()));
      }
      const response = await fetchGet(`/v5-sim/noble-preview?${query.toString()}`, {
        timeout: NOBLE_PREVIEW_TIMEOUT_MS,
      });
      const payload = await parseJsonResponse(response, 'V5 Noble preview');
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to load Noble preview');
      }

      noblePreviewCacheRef.current.set(cacheKey, payload);
      if (requestId === latestNoblePreviewRequestRef.current[mode]) {
        startTransition(() => {
          if (mode === 'fix_i') {
            setExternalFixIPreviewRows(payload.rows || []);
            setExternalFixIPreviewError('');
            setExternalFixIPreviewFailedKey('');
          } else if (mode === 'ida1') {
            setExternalIda1PreviewRows(payload.rows || []);
            setExternalIda1PreviewError('');
            setExternalIda1PreviewFailedKey('');
          } else {
            setExternalIda2PreviewRows(payload.rows || []);
            setExternalIda2PreviewError('');
            setExternalIda2PreviewFailedKey('');
          }
        });
      }
      return payload;
    } catch (err) {
      if (requestId === latestNoblePreviewRequestRef.current[mode]) {
        const timeoutMessage = mode === 'ida2'
          ? 'IDA2 preview request timed out. Retry to fetch the latest Goose rows.'
          : mode === 'fix_i'
          ? 'Fix I preview request timed out. Retry to fetch the latest Goose rows.'
          : 'IDA1 preview request timed out. Retry to fetch the latest Goose rows.';
        const fallbackMessage = err?.name === 'AbortError'
          ? timeoutMessage
          : mode === 'ida2'
          ? 'Failed to load IDA2 preview rows from Goose'
          : mode === 'fix_i'
          ? 'Failed to load Fix I preview rows from Goose'
          : 'Failed to load IDA1 preview rows from Goose';
        if (mode === 'fix_i') {
          setExternalFixIPreviewError(String(err?.message || fallbackMessage));
          setExternalFixIPreviewFailedKey(cacheKey);
        } else if (mode === 'ida1') {
          setExternalIda1PreviewError(String(err?.message || fallbackMessage));
          setExternalIda1PreviewFailedKey(cacheKey);
        } else {
          setExternalIda2PreviewError(String(err?.message || fallbackMessage));
          setExternalIda2PreviewFailedKey(cacheKey);
        }
      }
      throw err;
    } finally {
      if (requestId === latestNoblePreviewRequestRef.current[mode]) {
        if (mode === 'fix_i') {
          setExternalFixIPreviewLoading(false);
        } else if (mode === 'ida1') {
          setExternalIda1PreviewLoading(false);
        } else {
          setExternalIda2PreviewLoading(false);
        }
      }
    }
  }, [date, fetchGet, getNoblePreviewCacheKey, parseJsonResponse, simulationVariant]);

  const loadLetsTradeOrderPlan = useCallback(async ({ targetDate = endDate, force = false } = {}) => {
    if (!targetDate) {
      return { status: 'success', rows: [], metadata: null };
    }
	    const requestId = latestLetsTradeOrderPlanRequestRef.current + 1;
	    latestLetsTradeOrderPlanRequestRef.current = requestId;
	    const cacheKey = `${SIM_V5_CLIENT_CACHE_VERSION}:lets-trade:${simulationVariant}:${targetDate}`;
	    const cachedPlan = readCachedOrderPlan(cacheKey);
	    const shouldBypassCachedLetsTradePlan = Boolean(targetDate && targetDate >= getWarsawDateOffset(0));
	    if (cachedPlan && !force && !shouldBypassCachedLetsTradePlan) {
	      if (requestId === latestLetsTradeOrderPlanRequestRef.current) {
	        startTransition(() => {
          setLetsTradeOrderPlan(cachedPlan.rows || []);
          setLetsTradeOrderPlanMeta(cachedPlan.metadata || null);
          setLetsTradeOrderPlanDate(targetDate);
          setLetsTradeOrderPlanError('');
        });
      }
      return cachedPlan;
    }

    setLetsTradeOrderPlanLoading(true);
    setLetsTradeOrderPlanError('');
    try {
	      const planQuery = new URLSearchParams({ date: targetDate, simulation_variant: simulationVariant });
	      if (force || shouldBypassCachedLetsTradePlan) {
	        planQuery.set('_', String(Date.now()));
	      }
	      if (force) {
	        planQuery.set('force_refresh', 'true');
	      }
      const planResp = await fetchGet(`/v5-sim/order-plan?${planQuery.toString()}`);
      let planJson = { status: 'success', rows: [], metadata: null };
      try {
        const parsedPlan = await parseJsonResponse(planResp, 'V5 trader order plan');
        if (parsedPlan.status === 'success') {
          planJson = parsedPlan;
        } else {
          throw new Error(parsedPlan.message || 'Failed to load trader order plan');
        }
      } catch (planErr) {
        const message = String(planErr?.message || '').toLowerCase();
        const isExpectedMissingPlan =
          message.includes('failed to fetch predictions data')
          || message.includes('no order-plan analysis available')
          || message.includes('no prediction')
          || message.includes('missing');
        if (!isExpectedMissingPlan) {
          throw planErr;
        }
      }

      writeCachedOrderPlan(cacheKey, planJson);
      if (requestId === latestLetsTradeOrderPlanRequestRef.current) {
        startTransition(() => {
          setLetsTradeOrderPlan(planJson.rows || []);
          setLetsTradeOrderPlanMeta(planJson.metadata || null);
          setLetsTradeOrderPlanDate(targetDate);
          setLetsTradeOrderPlanError('');
        });
      }
      return planJson;
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Failed to load trader order plan';
      const isNetwork = isClientNetworkErrorMessage(msg);
      if (requestId === latestLetsTradeOrderPlanRequestRef.current) {
        setLetsTradeOrderPlanError(isNetwork ? 'Connection error while loading trader order plan.' : msg);
      }
      return { status: 'error', rows: [], metadata: null, message: msg };
    } finally {
      if (requestId === latestLetsTradeOrderPlanRequestRef.current) {
        setLetsTradeOrderPlanLoading(false);
      }
    }
  }, [endDate, fetchGet, parseJsonResponse, simulationVariant]);

  const loadResults = useCallback(async ({ force = false, view = 'all' } = {}) => {
    const normalizedView = ['summary', 'daily', 'all'].includes(view) ? view : 'all';
    const rangeKey = getResultsRangeKey(normalizedView);
    const cachedResults = readCachedResults(rangeKey);

    if (cachedResults && !force && !resultsData) {
      startTransition(() => {
        setResultsData(cachedResults);
      });
    }

    if (cachedResults && !force) {
      return cachedResults;
    }

    const requestId = latestResultsRequestRef.current + 1;
    latestResultsRequestRef.current = requestId;
    setResultsLoading(true);
    if (force) {
      startTransition(() => {
        setResultsData(null);
      });
    }
    try {
      const query = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        force_refresh: force ? 'true' : 'false',
        view: normalizedView,
        simulation_variant: simulationVariant,
      });
      const resultsResp = await fetchGet(`/v5-sim/results?${query.toString()}`);
      const resultsJson = await parseJsonResponse(resultsResp, 'V5 results');
      if (resultsJson.status !== 'success') {
        throw new Error(resultsJson.message || 'Failed to load results');
      }
      writeCachedResults(rangeKey, resultsJson);
      if (requestId === latestResultsRequestRef.current) {
        startTransition(() => {
          setResultsData(resultsJson);
        });
      }
      return resultsJson;
    } finally {
      if (requestId === latestResultsRequestRef.current) {
        setResultsLoading(false);
      }
    }
  }, [endDate, fetchGet, getResultsRangeKey, parseJsonResponse, resultsData, simulationVariant, startDate]);

  const loadBaseEntryRisk = useCallback(async ({ targetDate = endDate, force = false, baseVolume = 50 } = {}) => {
    if (!targetDate) return null;
    const normalizedVolume = Number(String(baseVolume || 50).replace(',', '.'));
    const safeVolume = Number.isFinite(normalizedVolume) ? normalizedVolume : 50;
    const cacheKey = `${simulationVariant}:${targetDate}:${safeVolume}`;
    const cachedPayload = baseEntryRiskCacheRef.current.get(cacheKey);
    if (cachedPayload && !force) {
      setBaseEntryRiskData(cachedPayload);
      return cachedPayload;
    }

    const requestId = latestBaseEntryRiskRequestRef.current + 1;
    latestBaseEntryRiskRequestRef.current = requestId;
    setBaseEntryRiskLoading(true);
    try {
      const query = new URLSearchParams({
        date: targetDate,
        base_volume: String(safeVolume),
        simulation_variant: simulationVariant,
      });
      if (force) {
        query.set('force_refresh', '1');
      }
      const response = await fetchGet(`/v5-sim/base-entry-risk?${query.toString()}`);
      const payload = await parseJsonResponse(response, 'V5 BASE entry risk');
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to load BASE entry risk');
      }
      baseEntryRiskCacheRef.current.set(cacheKey, payload);
      if (requestId === latestBaseEntryRiskRequestRef.current) {
        setBaseEntryRiskData(payload);
      }
      return payload;
    } catch (err) {
      console.error(err);
      if (requestId === latestBaseEntryRiskRequestRef.current) {
        const msg = err.message || 'Failed to load BASE entry risk';
        const isNetwork = isClientNetworkErrorMessage(msg);
        setError(isNetwork ? 'Connection error – check your network and click Refresh Results to retry.' : msg);
      }
      return null;
    } finally {
      if (requestId === latestBaseEntryRiskRequestRef.current) {
        setBaseEntryRiskLoading(false);
      }
    }
  }, [endDate, fetchGet, parseJsonResponse, simulationVariant]);

  const loadBaseEntryRiskRows = useCallback(async ({ dates = [], force = false, baseVolume = 50 } = {}) => {
    const uniqueDates = [...new Set((dates || []).map((value) => String(value || '').slice(0, 10)).filter(Boolean))];
    if (!uniqueDates.length) return {};
    const normalizedVolume = Number(String(baseVolume || 50).replace(',', '.'));
    const safeVolume = Number.isFinite(normalizedVolume) ? normalizedVolume : 50;
    const requestId = latestBaseEntryRiskRowsRequestRef.current + 1;
    latestBaseEntryRiskRowsRequestRef.current = requestId;
    setBaseEntryRiskRowsLoading(true);
    const nextRowsByDate = {};
    try {
      await Promise.all(uniqueDates.map(async (targetDate) => {
        const cacheKey = `${simulationVariant}:${targetDate}:${safeVolume}`;
        const cachedPayload = baseEntryRiskCacheRef.current.get(cacheKey);
        if (cachedPayload && !force) {
          nextRowsByDate[targetDate] = cachedPayload;
          return;
        }
        const query = new URLSearchParams({
          date: targetDate,
          base_volume: String(safeVolume),
          simulation_variant: simulationVariant,
        });
        if (force) {
          query.set('force_refresh', '1');
        }
        const response = await fetchGet(`/v5-sim/base-entry-risk?${query.toString()}`);
        const payload = await parseJsonResponse(response, 'V5 BASE entry risk');
        if (payload.status !== 'success') {
          throw new Error(payload.message || 'Failed to load BASE entry risk');
        }
        baseEntryRiskCacheRef.current.set(cacheKey, payload);
        nextRowsByDate[targetDate] = payload;
      }));
      if (requestId === latestBaseEntryRiskRowsRequestRef.current) {
        setBaseEntryRiskRowsByDate((prev) => ({ ...prev, ...nextRowsByDate }));
      }
      return nextRowsByDate;
    } catch (err) {
      console.error(err);
      if (requestId === latestBaseEntryRiskRowsRequestRef.current) {
        const msg = err.message || 'Failed to load BASE entry risk rows';
        const isNetwork = isClientNetworkErrorMessage(msg);
        setError(isNetwork ? 'Connection error – check your network and click Refresh Results to retry.' : msg);
      }
      return nextRowsByDate;
    } finally {
      if (requestId === latestBaseEntryRiskRowsRequestRef.current) {
        setBaseEntryRiskRowsLoading(false);
      }
    }
  }, [fetchGet, parseJsonResponse, simulationVariant]);

  const loadBasePretrade = useCallback(async ({ targetDate = endDate, force = false, quickInput = false } = {}) => {
    if (!targetDate) return null;
    const selectedQuickAction = String(baseQuickInputForm.baseAction || 'Idle');
    const normalizedQuickVolume = selectedQuickAction === 'Idle' ? 0 : Number(String(baseQuickInputForm.volume || '').replace(',', '.'));
    const normalizedBasePrice = String(baseQuickInputForm.basePrice || '').trim().replace(',', '.');
    const parsedBasePrice = normalizedBasePrice === '' ? null : Number(normalizedBasePrice);
    const hasBaseTradePrice = parsedBasePrice !== null && Number.isFinite(parsedBasePrice);
    const quickKey = quickInput
      ? `${selectedQuickAction}:${Number.isFinite(normalizedQuickVolume) ? normalizedQuickVolume : 'bad-volume'}:${hasBaseTradePrice ? parsedBasePrice : 'no-base-trade-price'}`
      : 'fallback-context';
    const cacheKey = `${simulationVariant}:${targetDate}:${quickKey}`;
    const cachedPayload = basePretradeCacheRef.current.get(cacheKey);
    if (cachedPayload && !force) {
      setBasePretradeData(cachedPayload);
      return cachedPayload;
    }

    const requestId = latestBasePretradeRequestRef.current + 1;
    latestBasePretradeRequestRef.current = requestId;
    setBasePretradeLoading(true);
    try {
      const query = new URLSearchParams({ date: targetDate, simulation_variant: simulationVariant });
      if (quickInput) {
        query.set('base_action', selectedQuickAction);
        query.set('base_volume', String(Number.isFinite(normalizedQuickVolume) ? normalizedQuickVolume : 0));
      }
      if (hasBaseTradePrice) {
        query.set('base_price', String(parsedBasePrice));
      }
      if (force) {
        query.set('_', String(Date.now()));
      }
      const response = await fetchGet(`/v5-sim/base-pretrade?${query.toString()}`);
      const payload = await parseJsonResponse(response, 'V5 BASE hedge decision');
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to load BASE hedge decision');
      }
      basePretradeCacheRef.current.set(cacheKey, payload);
      if (requestId === latestBasePretradeRequestRef.current) {
        setBasePretradeData(payload);
      }
      return payload;
    } catch (err) {
      console.error(err);
      if (requestId === latestBasePretradeRequestRef.current) {
        const msg = err.message || 'Failed to load BASE hedge decision';
        const isNetwork = isClientNetworkErrorMessage(msg);
        setError(isNetwork ? 'Connection error – check your network and click Refresh Results to retry.' : msg);
      }
      return null;
    } finally {
      if (requestId === latestBasePretradeRequestRef.current) {
        setBasePretradeLoading(false);
      }
    }
  }, [baseQuickInputForm, endDate, fetchGet, parseJsonResponse, simulationVariant]);

  const loadParityReport = useCallback(async ({
    force = true,
    forceReplay = false,
    modifiedParams,
    autoExpand = false,
  } = {}) => {
    const requestId = latestParityRequestRef.current + 1;
    latestParityRequestRef.current = requestId;
    const requestedStartDate = startDate;
    const requestedEndDate = endDate;
    const parityParams = modifiedParams !== undefined ? modifiedParams : null;
    setParityReportLoading(true);
    setError('');
    try {
      const query = new URLSearchParams({
        start_date: requestedStartDate,
        end_date: requestedEndDate,
        force_refresh: force ? 'true' : 'false',
        force_replay_refresh: forceReplay ? 'true' : 'false',
      });
      if (parityParams && Object.keys(parityParams).length > 0) {
        query.set('params_json', JSON.stringify(parityParams));
      }
      if (force) {
        query.set('_', String(Date.now()));
      }
      const response = await fetchGet(`/v5-sim/parity-report?${query.toString()}`);
      const json = await parseJsonResponse(response, 'V5 parity report');
      if (json.status !== 'success') {
        throw new Error(json.message || 'Failed to load parity report');
      }
      if (requestId === latestParityRequestRef.current) {
        setParityReport({
          ...json,
          requested_start_date: requestedStartDate,
          requested_end_date: requestedEndDate,
        });
        if (autoExpand) {
          setIsParityReportExpanded(true);
        }
      }
      return json;
    } catch (err) {
      console.error(err);
      if (requestId === latestParityRequestRef.current) {
        setError(err.message || 'Failed to load parity report');
      }
      return null;
    } finally {
      if (requestId === latestParityRequestRef.current) {
        setParityReportLoading(false);
      }
    }
  }, [endDate, fetchGet, parseJsonResponse, startDate]);

  const runQuickReplay = useCallback(async () => {
    const requestId = latestQuickReplayRequestRef.current + 1;
    latestQuickReplayRequestRef.current = requestId;
    const requestedStartDate = startDate;
    const requestedEndDate = endDate;
    const params = buildQuickReplayParams(quickReplayForm);
    const replayVariant = simulationVariant === 'goose-v3' ? 'goose-v3' : 'goose-v5';

    const invalidParam = Object.entries(params).find(([key, value]) => (
      !QUICK_REPLAY_BOOLEAN_FIELDS.has(key)
      && !QUICK_REPLAY_STRING_FIELDS.has(key)
      && !Number.isFinite(value)
    ));
    if (invalidParam) {
      setError(`${invalidParam[0]} must be a number.`);
      return null;
    }

    setQuickReplayLoading(true);
    setError('');
    try {
      const response = await fetchPostJson('/v5-sim/quick-replay', {
        start_date: requestedStartDate,
        end_date: requestedEndDate,
        variant: replayVariant,
        params,
      }, { timeout: 600000 });
      const json = await parseJsonResponse(response, 'V5 quick replay');
      if (json.status !== 'success') {
        throw new Error(json.message || 'Failed to run quick replay');
      }
      if (requestId === latestQuickReplayRequestRef.current) {
        setQuickReplayResult({
          ...json,
          requested_start_date: requestedStartDate,
          requested_end_date: requestedEndDate,
        });
        setParityReport(null);
        setIsParityReportExpanded(false);
        loadResults({ force: false, view: 'daily' }).catch((loadErr) => {
          console.warn('Failed to pre-load Daily Results rows after modified simulation replay', loadErr);
        });
      }
      return json;
    } catch (err) {
      console.error(err);
      if (requestId === latestQuickReplayRequestRef.current) {
        setError(err.message || 'Failed to run quick replay');
      }
      return null;
    } finally {
      if (requestId === latestQuickReplayRequestRef.current) {
        setQuickReplayLoading(false);
      }
    }
  }, [buildQuickReplayParams, endDate, fetchPostJson, loadResults, parseJsonResponse, quickReplayForm, simulationVariant, startDate]);

  const resetToSavedResults = useCallback(async () => {
    latestQuickReplayRequestRef.current += 1;
    setQuickReplayLoading(false);
    setQuickReplayResult(null);
    setParityReport(null);
    setIsParityReportExpanded(false);
    setError('');
    await loadResults({ force: true, view: 'all' });
  }, [loadResults]);

  const downloadParityReportCsv = useCallback(() => {
    const rows = parityReport?.daily || [];
    if (!rows.length) return;
    const csvCell = (value) => {
      if (value === null || value === undefined) return '';
      const text = Array.isArray(value) ? value.join('; ') : String(value);
      return `"${text.replace(/"/g, '""')}"`;
    };
    const headers = [
      { label: 'date', key: 'date' },
      { label: 'Goose v3 IDA1 PnL', key: 'bison_ida1_pnl' },
      { label: 'Goose v5 IDA1 PnL', key: 'v5_ida1_pnl' },
      { label: 'Goose v3 minus Goose v5 IDA1', key: 'delta_bison_vs_v5_ida1' },
      { label: 'Goose v5 IDA1 minus Goose v3 IDA1', key: 'delta_v5_vs_bison_ida1' },
      { label: 'Goose IDA1 Stop PnL', key: 'simulation_v5_ida1_pnl' },
      { label: 'Goose IDA2 Final PnL', key: 'moose_final_ida2_pnl' },
      { label: 'Goose IDA2 Final vs Goose v5 IDA1', key: 'delta_moose_vs_v5_ida1' },
      { label: 'Goose v3 base action', key: 'bison_base_action' },
      { label: 'Goose v5 base action', key: 'v5_base_action' },
      { label: 'Goose base action', key: 'simulation_v5_base_action' },
      { label: 'Goose v3 base PnL', key: 'bison_base_pnl' },
      { label: 'Goose v5 base PnL', key: 'v5_base_pnl' },
      { label: 'Goose base PnL', key: 'simulation_v5_base_pnl' },
      { label: 'Goose IDA2 Final base PnL', key: 'moose_base_pnl' },
      { label: 'Goose v3 Fix PnL', key: 'bison_fix_pnl' },
      { label: 'Goose v5 Fix PnL', key: 'v5_fix_pnl' },
      { label: 'Goose Fix PnL', key: 'simulation_v5_fix_pnl' },
      { label: 'Goose IDA2 Final Fix PnL', key: 'moose_fix_pnl' },
      { label: 'Goose v3 IDA1 leg PnL', key: 'bison_ida1_component_pnl' },
      { label: 'Goose v5 IDA1 leg PnL', key: 'v5_ida1_component_pnl' },
      { label: 'Goose IDA1 leg PnL', key: 'simulation_v5_ida1_component_pnl' },
      { label: 'Goose IDA2 Final IDA1 leg PnL', key: 'moose_ida1_component_pnl' },
      { label: 'Goose IDA2 leg PnL', key: 'moose_ida2_component_pnl' },
      { label: 'reason flags', key: 'reason_flags' },
    ];
    const csv = [
      headers.map((header) => csvCell(header.label)).join(','),
      ...rows.map((row) => headers.map((header) => csvCell(row[header.key])).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bison-v5-parity-${startDate}-to-${endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [endDate, parityReport, startDate]);

  const loadPipelineData = async ({ forceOrderPlan = false } = {}) => {
    setError('');
    setActionMessage('');
    const summaryPromise = loadOrderPlanSummary({ force: forceOrderPlan }).catch((err) => {
      console.error(err);
      const msg = err.message || 'Failed to load v5 simulation order plan summary';
      const isNetwork = isClientNetworkErrorMessage(msg);
      setError(isNetwork ? `Connection error – check your network and click Refresh All to retry.` : msg);
      return null;
    });
    try {
      const statusPayload = await loadCoreData({ force: forceOrderPlan, preserveOrderPlanMeta: true });
      const summaryPayload = await summaryPromise;

      const summaryMeta = summaryPayload?.metadata || null;
      const statusPredict = statusPayload?.data?.predict || null;
      const summaryIndicatesPlanReady = Boolean(
        Number(statusPredict?.plan_rows || 0) > 0
        || summaryMeta?.market_readiness?.fix_ii?.ready
        || (Array.isArray(summaryMeta?.fix2_issue_date) && summaryMeta.fix2_issue_date.length > 0)
      );
      const shouldLoadDetailAfterRefresh = Boolean(
        isPipelineDetailSection
        && summaryIndicatesPlanReady
        && (forceOrderPlan || !orderPlan.length)
      );

      if (shouldLoadDetailAfterRefresh) {
        await loadOrderPlan({ force: forceOrderPlan || !orderPlan.length });
      }
      if (isPipelineDataSection) {
        await loadPredictionInputData({ force: forceOrderPlan });
      }
      if (isPipelineAuditSection) {
        await loadLetsTradeOrderPlan({ targetDate: date, force: true });
        await loadOrderAudit({ force: forceOrderPlan });
      }
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Failed to load v5 simulation pipeline data';
      const isNetwork = isClientNetworkErrorMessage(msg);
      setError(isNetwork ? `Connection error – check your network and click Refresh Status to retry.` : msg);
    }
  };

  const loadResultsData = useCallback(async ({ forceResults = true, view = null } = {}) => {
    setError('');
    setActionMessage('');
    if (forceResults) {
      setParityReport(null);
      if (isBaseSimulationSection) {
        latestBaseEntryRiskRowsRequestRef.current += 1;
        setBaseEntryRiskRowsByDate({});
      }
    }
    try {
      const preferredView = view || (isDailyResultsSection ? 'daily' : 'summary');
      let resultsLoadError = null;
      try {
        await loadResults({ force: forceResults, view: preferredView });
      } catch (err) {
        resultsLoadError = err;
        if (!isLetsTradeSection) {
          throw err;
        }
      }
      if (isLetsTradeSection || isBaseSimulationSection) {
        const baseContextDate = isBaseSimulationSection ? baseHedgeDate : letsTradeDeliveryDate;
        await loadBaseEntryRisk({ targetDate: baseContextDate, force: forceResults, baseVolume: 50 });
        await loadBasePretrade({ targetDate: baseContextDate, force: forceResults });
      }
      if (isLetsTradeSection) {
        await loadLetsTradeOrderPlan({ targetDate: letsTradeDeliveryDate, force: forceResults });
      }
      if (resultsLoadError) {
        const msg = resultsLoadError.message || 'Failed to load v5 simulation results';
        if (!isExpectedPreSettlementDataGapMessage(msg)) {
          throw resultsLoadError;
        }
      }
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Failed to load v5 simulation results';
      const isNetwork = isClientNetworkErrorMessage(msg);
      setError(isNetwork ? `Connection error – check your network and click Refresh Results to retry.` : msg);
    }
  }, [baseHedgeDate, isBaseSimulationSection, isDailyResultsSection, isLetsTradeSection, letsTradeDeliveryDate, loadBaseEntryRisk, loadBasePretrade, loadLetsTradeOrderPlan, loadResults]);

  const buildOverrideFormState = useCallback((targetDate = date, payload = {}) => ({
    mode: payload.mode || 'create',
    date: targetDate,
    source: payload.source || 'manual',
    baseAction: payload.baseAction || 'Idle',
    volume: payload.volume ?? '0',
    basePrice: payload.basePrice ?? '',
    note: payload.note ?? '',
    triggerRecalc: payload.triggerRecalc ?? true,
    fixIBasePrice: payload.fixIBasePrice ?? null,
    updatedAt: payload.updatedAt ?? null,
    updatedBy: payload.updatedBy ?? null,
    recalcStatus: payload.recalcStatus ?? null,
    monitorCandidate: payload.monitorCandidate ?? null,
  }), [date]);

  const resetOverrideForm = useCallback((targetDate = overrideQueryDate || date) => {
    setOverrideFormState(buildOverrideFormState(targetDate));
    setOverrideSaveStatus(null);
  }, [buildOverrideFormState, date, overrideQueryDate]);

  const loadOverrides = useCallback(async () => {
    setOverridesLoading(true);
    try {
      const response = await fetchGet(`/v5-sim/base-overrides?start_date=${startDate}&end_date=${endDate}`);
      const payload = await parseJsonResponse(response, 'V5 base overrides');
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to load manual overrides');
      }
      setOverridesData(Array.isArray(payload.overrides) ? payload.overrides : []);
      return payload;
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Failed to load manual overrides';
      const isNetwork = isClientNetworkErrorMessage(msg);
      setError(isNetwork ? 'Connection error – check your network and click Refresh to retry.' : msg);
      return null;
    } finally {
      setOverridesLoading(false);
    }
  }, [endDate, fetchGet, parseJsonResponse, startDate]);

  const loadOverrideEditor = useCallback(async (targetDate) => {
    if (!targetDate) {
      resetOverrideForm(date);
      return null;
    }

    setOverrideFormLoading(true);
    try {
      const response = await fetchGet(`/v5-sim/base-override?date=${targetDate}`);
      const payload = await parseJsonResponse(response, 'V5 base override');
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to load manual override');
      }
      const override = payload.override || null;
      const current = payload.current || null;
      const monitorCandidate = payload.monitor_candidate || null;
      setOverrideFormState(buildOverrideFormState(targetDate, {
        mode: override ? 'edit' : 'create',
        source: override?.source === 'monitor_data' ? 'monitor' : 'manual',
        baseAction: override?.base_action || current?.base_action || 'Idle',
        volume: String(override?.volume ?? current?.volume ?? 0),
        basePrice: override?.base_price ?? '',
        note: override?.note || '',
        triggerRecalc: true,
        fixIBasePrice: override?.fix_i_base_price ?? current?.fix_i_base_price ?? null,
        updatedAt: override?.updated_at || null,
        updatedBy: override?.updated_by || null,
        recalcStatus: override?.recalc_status ?? payload.recalc_status ?? null,
        monitorCandidate,
      }));
      return payload;
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Failed to load manual override';
      const isNetwork = isClientNetworkErrorMessage(msg);
      setError(isNetwork ? 'Connection error – check your network and click Refresh to retry.' : msg);
      return null;
    } finally {
      setOverrideFormLoading(false);
    }
  }, [buildOverrideFormState, date, fetchGet, parseJsonResponse, resetOverrideForm]);

  const refreshAfterOverrideChange = useCallback(async () => {
    clearStatusCache();
    clearOrderPlanCache();
    clearResultsCache();
    setDayDetailsByDate({});
    setResultsData(null);
    setParityReport(null);
    await Promise.all([
      loadOverrides(),
      loadResultsData({ forceResults: true }),
    ]);
  }, [loadOverrides, loadResultsData]);

  const handleOverrideFieldChange = useCallback((field, value) => {
    setOverrideFormState((prev) => {
      if (field === 'source') {
        return {
          ...prev,
          source: value,
          basePrice: value === 'monitor' ? '' : prev.basePrice,
        };
      }
      if (field === 'baseAction' && value === 'Idle') {
        return {
          ...prev,
          baseAction: value,
          volume: '0',
          basePrice: '',
        };
      }
      return {
        ...prev,
        [field]: value,
      };
    });
    if (overrideSaveStatus) {
      setOverrideSaveStatus(null);
    }
  }, [overrideSaveStatus]);

  const handleSaveOverride = useCallback(async (event) => {
    event.preventDefault();
    setError('');
    setActionMessage('');
    setOverrideSaveStatus(null);

    const selectedSource = String(overrideFormState.source || 'manual');
    const monitorCandidate = overrideFormState.monitorCandidate || null;
    const selectedAction = String(overrideFormState.baseAction || 'Idle');
    const numericVolume = selectedSource === 'monitor'
      ? Number(monitorCandidate?.volume || 0)
      : (selectedAction === 'Idle' ? 0 : Number(overrideFormState.volume || 0));
    const normalizedBasePrice = selectedSource === 'monitor'
      ? null
      : (selectedAction === 'Idle'
      ? null
      : (overrideFormState.basePrice === '' || overrideFormState.basePrice === null
        ? null
        : Number(overrideFormState.basePrice)));

    if (!['manual', 'monitor'].includes(selectedSource)) {
      setError('Override source must be manual input or monitor data.');
      return;
    }
    if (selectedSource === 'monitor' && !monitorCandidate?.available) {
      setError(monitorCandidate?.error || 'No monitor-derived BASE data is available for this date.');
      return;
    }
    if (selectedSource === 'manual' && !['Buy', 'Sell', 'Idle'].includes(selectedAction)) {
      setError('Base action must be Buy, Sell, or Idle.');
      return;
    }
    if (Number.isNaN(numericVolume) || numericVolume < 0) {
      setError(selectedSource === 'monitor' ? 'Monitor-derived volume is invalid.' : 'Volume must be a non-negative number.');
      return;
    }
    if (selectedSource === 'manual' && selectedAction !== 'Idle' && !Number.isInteger(numericVolume)) {
      setError('Volume must be a whole number of MW for manual overrides.');
      return;
    }
    if (normalizedBasePrice !== null && (Number.isNaN(normalizedBasePrice) || normalizedBasePrice < 0)) {
      setError('Base price must be empty or a non-negative number.');
      return;
    }

    setOverrideFormLoading(true);
    try {
      const response = await axiosPost('/v5-sim/base-override', {
        date: overrideFormState.date,
        source: selectedSource,
        base_action: selectedSource === 'monitor' ? monitorCandidate?.base_action : selectedAction,
        volume: numericVolume,
        base_price: normalizedBasePrice,
        note: overrideFormState.note || '',
        trigger_recalc: Boolean(overrideFormState.triggerRecalc),
      });
      const payload = response?.data || {};
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to save manual override');
      }

      setOverrideSaveStatus({
        kind: 'success',
        action: overrideFormState.mode === 'edit' ? 'updated' : 'created',
        triggeredRuns: Array.isArray(payload.triggered_runs) ? payload.triggered_runs : [],
        recalcStatus: payload.recalc_status || null,
      });

      const savedDate = overrideFormState.date;
      navigate(`/fix2-v5-sim/overrides?date=${savedDate}`, { replace: true });
      await refreshAfterOverrideChange();
      await loadOverrideEditor(savedDate);
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Failed to save manual override';
      const isNetwork = isClientNetworkErrorMessage(msg);
      setError(isNetwork ? 'Connection error – check your network and try again.' : msg);
    } finally {
      setOverrideFormLoading(false);
    }
  }, [axiosPost, loadOverrideEditor, navigate, overrideFormState, refreshAfterOverrideChange]);

  const handleDeleteOverride = useCallback(async (
    targetDate,
    { skipConfirm = false, triggerRecalc = true } = {},
  ) => {
    if (!targetDate) {
      return;
    }
    if (!skipConfirm && !window.confirm(`Delete manual override for ${targetDate}?`)) {
      return;
    }

    setError('');
    setActionMessage('');
    setOverrideSaveStatus(null);
    setOverrideFormLoading(true);
    try {
      const response = await fetchDelete(
        `/v5-sim/base-override?date=${targetDate}&trigger_recalc=${triggerRecalc ? 'true' : 'false'}`
      );
      const payload = await parseJsonResponse(response, 'Delete V5 base override');
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to delete manual override');
      }

      setOverrideSaveStatus({
        kind: 'deleted',
        action: 'deleted',
        triggeredRuns: Array.isArray(payload.triggered_runs) ? payload.triggered_runs : [],
        recalcStatus: payload.recalc_status || null,
      });

      await refreshAfterOverrideChange();
      navigate('/fix2-v5-sim/overrides', { replace: true });
      resetOverrideForm(date);
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Failed to delete manual override';
      const isNetwork = isClientNetworkErrorMessage(msg);
      setError(isNetwork ? 'Connection error – check your network and try again.' : msg);
    } finally {
      setOverrideFormLoading(false);
    }
  }, [date, fetchDelete, navigate, parseJsonResponse, refreshAfterOverrideChange, resetOverrideForm]);

  const orderPlanReadyHint = Boolean(
    Number(statusData?.predict?.plan_rows || 0) > 0
    || Boolean(orderPlanMeta?.market_readiness?.fix_ii?.ready)
    || (Array.isArray(orderPlanMeta?.fix2_issue_date) && orderPlanMeta.fix2_issue_date.length > 0)
    || Boolean(orderPlanMeta?.fix_ii_plan?.available)
  );

  useEffect(() => {
    if (!isAuthenticated || !isPipelineDetailSection) return;
    if (!orderPlanReadyHint || orderPlanLoading || orderPlan.length > 0) return;

    let cancelled = false;

    (async () => {
      try {
        await loadOrderPlan({ force: true });
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        const msg = err.message || 'Failed to load detailed prediction rows';
        const isNetwork = isClientNetworkErrorMessage(msg);
        setError(isNetwork ? `Connection error – check your network and click Refresh All to retry.` : msg);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isPipelineDetailSection,
    orderPlanReadyHint,
    orderPlanLoading,
    orderPlan.length,
    date,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !isPipelineDataSection) {
      return;
    }
    if (predictionInputData?.date === date || predictionInputLoading) {
      return;
    }
    loadPredictionInputData({ force: false }).catch((err) => {
      console.error(err);
    });
  }, [
    date,
    isAuthenticated,
    isPipelineDataSection,
    loadPredictionInputData,
    predictionInputData,
    predictionInputLoading,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !isPipelineAuditSection) {
      return;
    }
    if (orderAuditLoading) {
      return;
    }
    const auditLoadedKey = `${simulationVariant}:${date}`;
    const activeAuditVariant = orderAuditData?.summary?.variant || simulationVariant;
    if (orderAuditLoadedForDateRef.current === auditLoadedKey && orderAuditData?.date === date && activeAuditVariant === simulationVariant) {
      return;
    }
    orderAuditLoadedForDateRef.current = auditLoadedKey;
    loadOrderAudit({ force: false }).catch((err) => {
      console.error(err);
      orderAuditLoadedForDateRef.current = '';
    });
  }, [
    date,
    isAuthenticated,
    isPipelineAuditSection,
    loadOrderAudit,
    orderAuditData,
    orderAuditLoading,
    simulationVariant,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !isPipelineAuditSection || !date) {
      return;
    }
    if (letsTradeOrderPlanLoading || (letsTradeOrderPlanDate === date && letsTradeOrderPlan.length > 0)) {
      return;
    }
    loadLetsTradeOrderPlan({ targetDate: date, force: false }).catch((err) => {
      console.error(err);
    });
  }, [
    date,
    isAuthenticated,
    isPipelineAuditSection,
    letsTradeOrderPlan.length,
    letsTradeOrderPlanDate,
    letsTradeOrderPlanLoading,
    loadLetsTradeOrderPlan,
  ]);

  const loadDayDetails = useCallback(async (selectedDate) => {
    setDetailsLoadingDate(selectedDate);
    try {
      const query = new URLSearchParams({
        date: selectedDate,
        simulation_variant: simulationVariant,
        _: String(Date.now()),
      });
      const response = await fetchGet(`/v5-sim/day-details?${query.toString()}`);
      const payload = await parseJsonResponse(response, 'V5 day details');
      if (payload.status !== 'success') {
        throw new Error(payload.message || 'Failed to load day details');
      }
      setDayDetailsByDate((prev) => ({
        ...prev,
        [selectedDate]: payload.data,
      }));
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Failed to load day details';
      if (isLetsTradeSection && isExpectedPreSettlementDataGapMessage(msg)) {
        return;
      }
      const isNetwork = isClientNetworkErrorMessage(msg);
      setError(isNetwork ? 'Connection error – check your network and click Refresh All to retry.' : msg);
    } finally {
      setDetailsLoadingDate('');
    }
  }, [fetchGet, isLetsTradeSection, parseJsonResponse, simulationVariant]);

  useEffect(() => {
    if (!isAuthenticated || !isPipelineAuditSection || !date) {
      return;
    }
    if (detailsLoadingDate === date) {
      return;
    }
    if (dayDetailsLoadedForStatusRef.current[`audit:${date}`]) {
      return;
    }
    dayDetailsLoadedForStatusRef.current[`audit:${date}`] = true;
    loadDayDetails(date).catch((err) => {
      console.error(err);
    });
  }, [
    date,
    detailsLoadingDate,
    isAuthenticated,
    isPipelineAuditSection,
    loadDayDetails,
  ]);

  useEffect(() => {
    setDayDetailsByDate({});
    dayDetailsLoadedForStatusRef.current = {};
    setExpandedDate('');
    setDetailsLoadingDate('');
    setIsHourlyBreakdownExpanded(false);
    setExpandedDailyQuarterPaths({});
    setResultsData(null);
    setQuickReplayResult(null);
    setQuickReplayForm((prev) => ({
      ...getDefaultQuickReplayFormForVariant(simulationVariant),
      ...prev,
      apply_ridge_shift: simulationVariant === 'goose-v3',
    }));
  }, [simulationVariant]);

  const toggleDay = async (selectedDate) => {
    if (expandedDate === selectedDate) {
      setExpandedDate('');
      setIsHourlyBreakdownExpanded(false);
      setExpandedDailyQuarterPaths({});
      return;
    }
    setExpandedDate(selectedDate);
    setIsHourlyBreakdownExpanded(false);
    setExpandedDailyQuarterPaths({});
    if (!dayDetailsByDate[selectedDate]) {
      await loadDayDetails(selectedDate);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (isResultsDataSection) {
      loadResultsData({ forceResults: false });
      return;
    }
    if (isPipelineFamilySection) {
      loadPipelineData({ forceOrderPlan: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    date,
    isAuthenticated,
    isPipelineFamilySection,
    isResultsDataSection,
    isHeavyResultsRange,
    isDailyResultsSection,
    startDate,
    endDate,
    simulationVariant,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !isDayRadarSection) return;
    loadDayRadar({ force: true });
  }, [date, isAuthenticated, isDayRadarSection, loadDayRadar]);

  useEffect(() => {
    if (!isAuthenticated || !isLetsTradeSection || !letsTradeDeliveryDate) return;
    loadDayRadar({ force: true, targetDate: letsTradeDeliveryDate });
  }, [isAuthenticated, isLetsTradeSection, letsTradeDeliveryDate, loadDayRadar]);

  useEffect(() => {
    if (!isAuthenticated || !isDayRadarSection || !dayRadarRunning) return undefined;
    const timer = window.setInterval(() => {
      loadDayRadar({ force: true });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [dayRadarRunning, isAuthenticated, isDayRadarSection, loadDayRadar]);

  const syncDailyResultsScroll = (sourceRef, targetRefs) => {
    if (!sourceRef.current || isSyncingDailyScrollRef.current) return;
    isSyncingDailyScrollRef.current = true;
    targetRefs.forEach((targetRef) => {
      if (targetRef.current) {
        targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
      }
    });
    window.requestAnimationFrame(() => {
      isSyncingDailyScrollRef.current = false;
    });
  };

  const syncOrderPlanScroll = (sourceRef, targetRefs) => {
    if (!sourceRef.current || isSyncingOrderPlanScrollRef.current) return;
    isSyncingOrderPlanScrollRef.current = true;
    targetRefs.forEach((targetRef) => {
      if (targetRef.current) {
        targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
      }
    });
    window.requestAnimationFrame(() => {
      isSyncingOrderPlanScrollRef.current = false;
    });
  };

  const syncDetailResultsScroll = (sourceRef, targetRefs) => {
    if (!sourceRef.current || isSyncingDetailScrollRef.current) return;
    isSyncingDetailScrollRef.current = true;
    targetRefs.forEach((targetRef) => {
      if (targetRef.current) {
        targetRef.current.scrollLeft = sourceRef.current.scrollLeft;
      }
    });
    window.requestAnimationFrame(() => {
      isSyncingDetailScrollRef.current = false;
    });
  };

  useEffect(() => {
    const updateDailyFloatingScrollbarVisibility = () => {
      if (!expandedDate || !expandedDetailRegionRef.current || !dailyResultsStickyWrapperRef.current) {
        setHideDailyFloatingScroll(false);
        return;
      }

      const stickyRect = dailyResultsStickyWrapperRef.current.getBoundingClientRect();
      const detailRect = expandedDetailRegionRef.current.getBoundingClientRect();
      const overlapsDetail =
        detailRect.top <= stickyRect.bottom + 4 && detailRect.bottom >= stickyRect.top + 4;

      setHideDailyFloatingScroll(overlapsDetail);
    };

    updateDailyFloatingScrollbarVisibility();
    window.addEventListener('scroll', updateDailyFloatingScrollbarVisibility, { passive: true });
    window.addEventListener('resize', updateDailyFloatingScrollbarVisibility);

    return () => {
      window.removeEventListener('scroll', updateDailyFloatingScrollbarVisibility);
      window.removeEventListener('resize', updateDailyFloatingScrollbarVisibility);
    };
  }, [expandedDate, dayDetailsByDate]);

  const handleRun = async (endpoint, payload = { date }, display = {}) => {
    setRunPanelMode({ train: 'latest', predict: 'latest', settle: 'latest' });
    setSelectedRunIds({ train: null, predict: null, settle: null });
    setSettleResultMode(display.settleResultMode || 'ida2');
    setPredictResultMode(display.predictResultMode || 'fix2');
    setTrainResultMode(display.trainResultMode || 'v5');
    setLoading(true);
    setError('');
    setActionMessage('');
    try {
      const response = await axiosPost(endpoint, payload);
      if (response.data.status === 'success') {
        const successMessage = response.data.message || `Started task successfully: ${response.data.task_definition || endpoint}`;
        clearStatusCache();
        clearOrderPlanCache();
        clearResultsCache();
        setResultsData(null);
        setParityReport(null);
        await loadPipelineData({ forceOrderPlan: true });
        setActionMessage(successMessage);
      } else {
        throw new Error(response.data.message || 'Failed to start task');
      }
    } catch (err) {
      console.error(err);
      const msg = err.message || 'Failed to start task';
      const isNetwork = isClientNetworkErrorMessage(msg);
      setError(isNetwork ? 'Connection error – check your network and try again.' : msg);
    } finally {
      setLoading(false);
    }
  };

  const summary = useMemo(() => resultsData?.summary || {}, [resultsData]);
  const paritySummary = useMemo(() => parityReport?.summary || {}, [parityReport]);
  const parityRows = useMemo(() => parityReport?.daily || [], [parityReport]);
  const parityRowsByDate = useMemo(() => new Map(
    [...parityRows]
      .filter((row) => row?.date)
      .map((row) => [String(row.date).slice(0, 10), row]),
  ), [parityRows]);
  const parityTopDiscrepancyRows = useMemo(() => (
    [...parityRows]
      .sort((a, b) => Math.abs(Number(b.delta_moose_vs_v5_ida1 || 0)) - Math.abs(Number(a.delta_moose_vs_v5_ida1 || 0)))
      .slice(0, 12)
  ), [parityRows]);
  const parityTopMoosePositiveRows = useMemo(() => (
    [...parityRows]
      .filter((row) => Number.isFinite(Number(row.delta_moose_vs_v5_ida1)))
      .sort((a, b) => Number(b.delta_moose_vs_v5_ida1) - Number(a.delta_moose_vs_v5_ida1))
      .slice(0, 5)
  ), [parityRows]);
  const parityTopMooseNegativeRows = useMemo(() => (
    [...parityRows]
      .filter((row) => Number.isFinite(Number(row.delta_moose_vs_v5_ida1)))
      .sort((a, b) => Number(a.delta_moose_vs_v5_ida1) - Number(b.delta_moose_vs_v5_ida1))
      .slice(0, 5)
  ), [parityRows]);
  const quickReplaySummary = useMemo(() => quickReplayResult?.summary || {}, [quickReplayResult]);
  const quickReplayDefaultSummary = quickReplaySummary.default || {};
  const quickReplayCustomSummary = quickReplaySummary.custom || {};
  const quickReplayDeltaSummary = quickReplaySummary.delta_custom_vs_default || {};
  const quickReplayRows = useMemo(() => quickReplayResult?.daily || [], [quickReplayResult]);
  const quickReplayFormParams = useMemo(
    () => buildQuickReplayParams(quickReplayForm),
    [buildQuickReplayParams, quickReplayForm],
  );
  const quickReplayFormSignature = useMemo(
    () => getQuickReplayParamSignature(quickReplayFormParams),
    [getQuickReplayParamSignature, quickReplayFormParams],
  );
  const quickReplayDefaultParams = useMemo(
    () => buildQuickReplayParams(getDefaultQuickReplayFormForVariant(simulationVariant)),
    [buildQuickReplayParams, simulationVariant],
  );
  const quickReplayDefaultSignature = useMemo(
    () => getQuickReplayParamSignature(quickReplayDefaultParams),
    [getQuickReplayParamSignature, quickReplayDefaultParams],
  );
  const quickReplayResultParams = useMemo(
    () => quickReplayResult?.params || {},
    [quickReplayResult],
  );
  const quickReplayResultSignature = useMemo(
    () => getQuickReplayParamSignature(quickReplayResultParams),
    [getQuickReplayParamSignature, quickReplayResultParams],
  );
  const quickReplayActiveParamsLabel = useMemo(
    () => formatQuickReplayParamsLabel(quickReplayResultParams),
    [formatQuickReplayParamsLabel, quickReplayResultParams],
  );
  const isQuickReplayForActiveRange = !quickReplayResult || (
    (quickReplayResult.start_date || quickReplayResult.requested_start_date) === startDate
    && (quickReplayResult.end_date || quickReplayResult.requested_end_date) === endDate
  );
  const isQuickReplayPendingSettings = Boolean(
    quickReplayResult
    && isQuickReplayForActiveRange
    && quickReplayFormSignature !== quickReplayResultSignature
  );
  const isModifiedSimulationActive = Boolean(
    quickReplayResult
    && isQuickReplayForActiveRange
    && !isQuickReplayPendingSettings
  );
  const hasVariantOverride = simulationVariant !== 'goose-v3';
  const hasPendingReplayOverrides = quickReplayFormSignature !== quickReplayDefaultSignature;
  const hasAppliedReplayOverrides = isModifiedSimulationActive && quickReplayResultSignature !== quickReplayDefaultSignature;
  const pendingModifiedParamsLabel = useMemo(
    () => formatQuickReplayParamsLabel(quickReplayFormParams),
    [formatQuickReplayParamsLabel, quickReplayFormParams],
  );
  const quickReplayRowsByDate = useMemo(() => {
    const rowsByDate = new Map();
    quickReplayRows.forEach((row) => {
      if (row?.date) {
        rowsByDate.set(String(row.date).slice(0, 10), row);
      }
    });
    return rowsByDate;
  }, [quickReplayRows]);
  const quickReplayFieldSections = useMemo(() => {
    const fieldsCopy = simCopy.modifier.fields;
    return [
      {
        key: 'goose',
        title: howItWorksLanguage === 'pl' ? 'Goose risk i model' : 'Goose risk and model',
        fields: [
          { key: 'base_volume', label: fieldsCopy.base_volume.label, suffix: 'MW', hint: fieldsCopy.base_volume.hint },
          { key: 'fix_i_model_spread', label: fieldsCopy.fix_i_model_spread.label, suffix: 'PLN', hint: fieldsCopy.fix_i_model_spread.hint },
          { key: 'prediction_filter_margin', label: fieldsCopy.prediction_filter_margin.label, suffix: 'PLN', hint: fieldsCopy.prediction_filter_margin.hint },
          { key: 'apply_risk_score', type: 'checkbox', label: fieldsCopy.apply_risk_score.label, hint: fieldsCopy.apply_risk_score.hint },
          {
            key: 'risk_score_model',
            type: 'select',
            label: fieldsCopy.risk_score_model.label,
            hint: fieldsCopy.risk_score_model.hint,
            disabled: !quickReplayForm.apply_risk_score,
            options: [
              { value: 'prod-v2.0.0', label: 'prod-v2.0.0' },
              { value: 'collapse_logreg_v1_wf_pse_till_500', label: 'collapse_logreg_v1_wf_pse_till_500' },
            ],
          },
          { key: 'apply_ridge_shift', type: 'checkbox', label: fieldsCopy.apply_ridge_shift.label, hint: fieldsCopy.apply_ridge_shift.hint },
        ],
      },
      {
        key: 'fix_i',
        title: howItWorksLanguage === 'pl' ? 'Bucket Fix I / Fix II' : 'Fix I / Fix II buckets',
        fields: [
          { key: 'bucket1_volume', label: fieldsCopy.bucket1_volume.label, suffix: 'MW', hint: fieldsCopy.bucket1_volume.hint },
          { key: 'bucket2_volume', label: fieldsCopy.bucket2_volume.label, suffix: 'MW', hint: fieldsCopy.bucket2_volume.hint },
          { key: 'bucket3_volume', label: fieldsCopy.bucket3_volume.label, suffix: 'MW', hint: fieldsCopy.bucket3_volume.hint },
          { key: 'np_constraint_bucket1', label: fieldsCopy.np_constraint_bucket1.label, suffix: 'MWh', hint: fieldsCopy.np_constraint_bucket1.hint },
          { key: 'np_constraint_bucket2', label: fieldsCopy.np_constraint_bucket2.label, suffix: 'MWh', hint: fieldsCopy.np_constraint_bucket2.hint },
          { key: 'np_constraint_bucket3', label: fieldsCopy.np_constraint_bucket3.label, suffix: 'MWh', hint: fieldsCopy.np_constraint_bucket3.hint },
          { key: 'margin_tier1', label: fieldsCopy.margin_tier1.label, suffix: 'PLN', hint: fieldsCopy.margin_tier1.hint },
          { key: 'margin_tier2', label: fieldsCopy.margin_tier2.label, suffix: 'PLN', hint: fieldsCopy.margin_tier2.hint },
          { key: 'margin_tier3', label: fieldsCopy.margin_tier3.label, suffix: 'PLN', hint: fieldsCopy.margin_tier3.hint },
          { key: 'margin_tier4', label: fieldsCopy.margin_tier4.label, suffix: 'PLN', hint: fieldsCopy.margin_tier4.hint },
        ],
      },
      {
        key: 'ida1',
        title: 'IDA1',
        fields: [
          { key: 'fix_ii_ida1_bucket1_volume', label: fieldsCopy.fix_ii_ida1_bucket1_volume.label, suffix: 'MW', hint: fieldsCopy.fix_ii_ida1_bucket1_volume.hint },
          { key: 'fix_ii_ida1_bucket2_volume', label: fieldsCopy.fix_ii_ida1_bucket2_volume.label, suffix: 'MW', hint: fieldsCopy.fix_ii_ida1_bucket2_volume.hint },
          { key: 'fix_ii_ida1_bucket3_volume', label: fieldsCopy.fix_ii_ida1_bucket3_volume.label, suffix: 'MW', hint: fieldsCopy.fix_ii_ida1_bucket3_volume.hint },
          { key: 'max_avg_volume_pct', label: fieldsCopy.max_avg_volume_pct.label, suffix: '%', hint: fieldsCopy.max_avg_volume_pct.hint },
          { key: 'max_position', label: fieldsCopy.max_position.label, suffix: 'MW', hint: fieldsCopy.max_position.hint },
          { key: 'ida1_margin', label: fieldsCopy.ida1_margin.label, suffix: 'PLN', hint: fieldsCopy.ida1_margin.hint },
        ],
      },
      {
        key: 'ida2',
        title: howItWorksLanguage === 'pl' ? 'IDA2 overlay' : 'IDA2 overlay',
        fields: [
          { key: 'ida2_margin', label: fieldsCopy.ida2_margin.label, suffix: 'PLN', hint: fieldsCopy.ida2_margin.hint },
          { key: 'ida2_min_edge_per_mwh', label: fieldsCopy.ida2_min_edge_per_mwh.label, suffix: 'PLN/MWh', hint: fieldsCopy.ida2_min_edge_per_mwh.hint },
          {
            key: 'base_carry_mode',
            type: 'select',
            label: fieldsCopy.base_carry_mode.label,
            hint: fieldsCopy.base_carry_mode.hint,
            options: [{ value: 'strict_v4', label: 'strict_v4' }],
          },
        ],
      },
    ];
  }, [howItWorksLanguage, quickReplayForm.apply_risk_score, simCopy]);
  const bisonParityPreset = parityReport?.presets?.bison_ida1_tab || {};
  const simParityPreset = parityReport?.presets?.bison_ida1_v5_tab || parityReport?.presets?.simulation_v5 || {};
  const mooseIda2Preset = parityReport?.presets?.moose_final_ida2 || parityReport?.presets?.simulation_v5 || simParityPreset;
  const bisonParityLabel = `Goose v3 IDA1 (${bisonParityPreset.probabilistic_model || 'v3.1.0'} + ${bisonParityPreset.ida1_model || 'ida1-v1.0.3'})`;
  const simParityLabel = `Goose v5 IDA1 (${simParityPreset.probabilistic_model || 'v5.0.0'} + ${simParityPreset.ida1_model || 'ida1-v1.0.3'})`;
  const mooseIda1ParityLabel = `Goose IDA1 (${simParityPreset.probabilistic_model || 'v5.0.0'} + ${simParityPreset.ida1_model || 'ida1-v1.0.3'})`;
  const mooseIda2ParityLabel = `Goose IDA2 (${mooseIda2Preset.probabilistic_model || 'v5.0.0'} + ${mooseIda2Preset.ida1_model || 'ida1-v1.0.3'} + ${mooseIda2Preset.ida2_model || 'ida2-v1.0.0'})`;
  const parityComparableDays = paritySummary.comparable_v5_vs_moose_days || paritySummary.comparable_bison_vs_v5_days || 0;
  const bisonV3CardPnl = paritySummary.bison_ida1_full_pnl ?? paritySummary.bison_ida1_pnl;
  const bisonV5CardPnl = paritySummary.v5_ida1_full_pnl ?? paritySummary.v5_ida1_pnl;
  const bisonV3CardComponents = paritySummary.bison_ida1_full_components || paritySummary.bison_ida1_components;
  const bisonV5CardComponents = paritySummary.v5_ida1_full_components || paritySummary.v5_ida1_components;
  const parityStopBenchmarkRows = useMemo(() => {
    const metric = (prefix) => ({
      pnl: paritySummary[`${prefix}_pnl`],
      afterFees: paritySummary[`${prefix}_after_fees`],
      fees: paritySummary[`${prefix}_fees`],
      volume: paritySummary[`${prefix}_volume_mwh`],
      profitPerMwh: paritySummary[`${prefix}_profit_per_mwh`],
      days: paritySummary[`${prefix}_days`] || 0,
    });
    return [
      {
        key: 'bison-v3',
        label: 'Goose v3',
        bisonFix2: metric('bison_v3_fix2_bison_calc'),
        mooseFix2: metric('bison_v3_fix2_moose_calc'),
        bisonIda1: metric('bison_v3_ida1_bison_calc'),
        mooseIda1: metric('bison_v3_ida1_moose_calc'),
      },
      {
        key: 'bison-v5',
        label: 'Goose v5',
        bisonFix2: metric('bison_v5_fix2_bison_calc'),
        mooseFix2: metric('bison_v5_fix2_moose_calc'),
        bisonIda1: metric('bison_v5_ida1_bison_calc'),
        mooseIda1: metric('bison_v5_ida1_moose_calc'),
      },
      {
        key: 'moose',
        label: 'Goose',
        bisonFix2: metric('moose_fix2_bison_calc'),
        mooseFix2: metric('moose_fix2_moose_calc'),
        bisonIda1: metric('moose_ida1_bison_calc'),
        mooseIda1: metric('moose_ida1_moose_calc'),
      },
    ];
  }, [paritySummary]);
  const parityReportStartDate = parityReport?.start_date || parityReport?.requested_start_date;
  const parityReportEndDate = parityReport?.end_date || parityReport?.requested_end_date;
  const parityReportGeneratedLabel = useMemo(() => {
    if (!parityReport?.generated_at) return '';
    const parsed = new Date(parityReport.generated_at);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString();
  }, [parityReport?.generated_at]);
  const isParityReportForActiveRange = !parityReport || (
    parityReportStartDate === startDate && parityReportEndDate === endDate
  );
  const isParityGooseCardsRefreshing = quickReplayLoading || parityReportLoading;
  const parityComparisonParameterRows = useMemo(() => {
    const bisonV3 = parityReport?.presets?.bison_ida1_tab || {};
    const bisonV5 = parityReport?.presets?.bison_ida1_v5_tab || parityReport?.presets?.simulation_v5 || {};
    const simulationV5 = parityReport?.presets?.simulation_v5 || bisonV5;
    const formatPresetValue = (value, suffix = '') => {
      if (value === null || value === undefined || value === '') return '-';
      if (typeof value === 'boolean') return value ? 'on' : 'off';
      if (typeof value === 'number') return `${formatNumber(value)}${suffix}`;
      return `${value}${suffix}`;
    };
    const firstPresentValue = (preset, keys) => {
      for (const key of keys) {
        if (preset?.[key] !== null && preset?.[key] !== undefined && preset?.[key] !== '') {
          return preset[key];
        }
      }
      return null;
    };
    const formatBuckets = (preset, keys, suffix = '') => keys
      .map((keyOptions) => formatPresetValue(
        firstPresentValue(preset, Array.isArray(keyOptions) ? keyOptions : [keyOptions]),
        suffix,
      ))
      .join(' / ');
    const formatMarginTiers = (preset) => [
      firstPresentValue(preset, ['margin_tier1', 'margin_price_lt_500']),
      firstPresentValue(preset, ['margin_tier2', 'margin_price_500_700']),
      firstPresentValue(preset, ['margin_tier3', 'margin_price_700_1000']),
      firstPresentValue(preset, ['margin_tier4', 'margin_price_gt_1000']),
    ].map((value) => formatPresetValue(value, ' PLN')).join(' / ');
    const formatModels = (preset) => `${preset?.fix_i_model || '-'} / ${preset?.fix_ii_model || '-'}`;
    const row = (label, getter, suffix = '') => ({
      label,
      bisonV3: formatPresetValue(getter(bisonV3), suffix),
      bisonV5: formatPresetValue(getter(bisonV5), suffix),
      simulationV5: formatPresetValue(getter(simulationV5), suffix),
    });
    return [
      row('Probabilistic model', (preset) => preset.probabilistic_model),
      row('Fix I / Fix II models', formatModels),
      row('IDA1 model', (preset) => preset.ida1_model),
      {
        label: 'IDA2 model',
        bisonV3: 'not used in IDA1 replay',
        bisonV5: 'not used in IDA1 replay',
        simulationV5: simulationV5?.ida2_model || '-',
      },
      row('Base volume', (preset) => preset.base_volume, ' MW'),
      row('Preds diff cutoff', (preset) => preset.preds_diff_cutoff, '%'),
      row('Trend/filter margin', (preset) => preset.prediction_filter_margin, ' PLN'),
      row('Fix I model spread', (preset) => preset.fix_i_model_spread),
      row('IDA1 margin', (preset) => preset.ida1_margin),
      {
        label: 'IDA2 margin',
        bisonV3: 'not used in IDA1 replay',
        bisonV5: 'not used in IDA1 replay',
        simulationV5: formatPresetValue(simulationV5?.ida2_margin),
      },
      {
        label: 'IDA2 min edge',
        bisonV3: 'not used in IDA1 replay',
        bisonV5: 'not used in IDA1 replay',
        simulationV5: formatPresetValue(simulationV5?.ida2_min_edge_per_mwh, ' PLN/MWh'),
      },
      {
        label: 'Goose mode',
        bisonV3: 'not used in IDA1 replay',
        bisonV5: 'not used in IDA1 replay',
        simulationV5: simulationV5?.moose_mode === 'full_strategy_experimental'
          ? 'full_strategy_experimental'
          : simulationV5?.moose_mode === 'overlay_only'
            ? 'overlay_only'
            : 'overlay_handoff',
      },
      {
        label: 'Goose sell-carry guard',
        bisonV3: 'not used in IDA1 replay',
        bisonV5: 'not used in IDA1 replay',
        simulationV5: 'Fix I / Fix II bucket, hours 18-23, IDA2 q50 vs Fix II >= 20 PLN/MWh, BASE blocked',
      },
      {
        label: 'Fix I buckets',
        bisonV3: formatBuckets(bisonV3, [['bucket1_volume', 'fix_i_vs_fix_ii_bucket_1_volume'], ['bucket2_volume', 'fix_i_vs_fix_ii_bucket_2_volume'], ['bucket3_volume', 'fix_i_vs_fix_ii_bucket_3_volume']], ' MW'),
        bisonV5: formatBuckets(bisonV5, [['bucket1_volume', 'fix_i_vs_fix_ii_bucket_1_volume'], ['bucket2_volume', 'fix_i_vs_fix_ii_bucket_2_volume'], ['bucket3_volume', 'fix_i_vs_fix_ii_bucket_3_volume']], ' MW'),
        simulationV5: formatBuckets(simulationV5, [['bucket1_volume', 'fix_i_vs_fix_ii_bucket_1_volume'], ['bucket2_volume', 'fix_i_vs_fix_ii_bucket_2_volume'], ['bucket3_volume', 'fix_i_vs_fix_ii_bucket_3_volume']], ' MW'),
      },
      {
        label: 'Fix II -> IDA1 buckets',
        bisonV3: formatBuckets(bisonV3, [['fix_ii_ida1_bucket1_volume', 'fix_ii_vs_ida1_bucket_1_volume'], ['fix_ii_ida1_bucket2_volume', 'fix_ii_vs_ida1_bucket_2_volume'], ['fix_ii_ida1_bucket3_volume', 'fix_ii_vs_ida1_bucket_3_volume']], ' MW'),
        bisonV5: formatBuckets(bisonV5, [['fix_ii_ida1_bucket1_volume', 'fix_ii_vs_ida1_bucket_1_volume'], ['fix_ii_ida1_bucket2_volume', 'fix_ii_vs_ida1_bucket_2_volume'], ['fix_ii_ida1_bucket3_volume', 'fix_ii_vs_ida1_bucket_3_volume']], ' MW'),
        simulationV5: formatBuckets(simulationV5, [['fix_ii_ida1_bucket1_volume', 'fix_ii_vs_ida1_bucket_1_volume'], ['fix_ii_ida1_bucket2_volume', 'fix_ii_vs_ida1_bucket_2_volume'], ['fix_ii_ida1_bucket3_volume', 'fix_ii_vs_ida1_bucket_3_volume']], ' MW'),
      },
      row('Max avg IDA1 volume', (preset) => preset.max_avg_volume_pct, '%'),
      row('Max IDA1 Position', (preset) => preset.max_position, ' MW'),
      {
        label: 'Price margin tiers',
        bisonV3: formatMarginTiers(bisonV3),
        bisonV5: formatMarginTiers(bisonV5),
        simulationV5: formatMarginTiers(simulationV5),
      },
      row('Ridge shift', (preset) => preset.apply_ridge_shift),
    ];
  }, [formatNumber, parityReport]);
  const rawDailyRows = useMemo(() => resultsData?.daily || [], [resultsData]);
  const dailyRows = useMemo(() => {
    if (!isModifiedSimulationActive || !quickReplayRowsByDate.size) {
      return rawDailyRows;
    }

    const numberOrNull = (value) => {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
    };
    const applyExistingFeeSpread = (nextGross, previousGross, previousAfterFees) => {
      const nextGrossNumber = numberOrNull(nextGross);
      if (nextGrossNumber === null) {
        return null;
      }
      const previousGrossNumber = numberOrNull(previousGross);
      const previousAfterFeesNumber = numberOrNull(previousAfterFees);
      const feeSpread = previousGrossNumber !== null && previousAfterFeesNumber !== null
        ? previousGrossNumber - previousAfterFeesNumber
        : 0;
      return Number((nextGrossNumber - feeSpread).toFixed(2));
    };
    const setNumeric = (target, key, value) => {
      const numericValue = numberOrNull(value);
      if (numericValue !== null) {
        target[key] = numericValue;
      }
    };
    const normalizeBaseAction = (value) => {
      const text = String(value || '').trim().toLowerCase();
      if (text === 'buy') return 'Buy';
      if (text === 'sell') return 'Sell';
      return 'Idle';
    };

    return rawDailyRows.map((row) => {
      const replayRow = quickReplayRowsByDate.get(String(row?.date || '').slice(0, 10));
      const custom = replayRow?.custom || null;
      if (!custom || !Object.keys(custom).length) {
        return row;
      }

      const nextRow = {
        ...row,
        is_modified_simulation: true,
        modified_replay_delta_ida2_close_pnl: replayRow.delta_ida2_close_pnl,
        modified_replay_delta_ida1_stop_pnl: replayRow.delta_ida1_stop_pnl,
      };

      [
        "daily_pnl",
        "daily_pnl_ida2_close",
        "daily_pnl_fix2_close",
        "daily_pnl_ida1_benchmark",
        "base_pnl",
        "base_pnl_fix2_close",
        "probabilistic_pnl",
        "ida1_pnl",
        "ida2_pnl",
        "total_volume",
        "total_volume_traded",
        "total_volume_fix2_close",
        "total_volume_ida1_benchmark",
        "path_volume_mwh",
        "base_volume",
        "base_volume_mwh",
        "probabilistic_volume_mwh",
        "ida1_volume_mwh",
        "ida2_volume_mwh",
        "venue_base_mwh",
        "venue_fix_i_mwh",
        "venue_fix_ii_mwh",
        "venue_ida1_mwh",
        "venue_ida2_mwh",
        "base_price",
      ].forEach((key) => setNumeric(nextRow, key, custom[key]));

      if (custom.total_volume_traded !== undefined) {
        setNumeric(nextRow, 'total_volume', custom.total_volume_traded);
        setNumeric(nextRow, 'path_volume_mwh', custom.total_volume_traded);
      }
      if (custom.daily_pnl !== undefined) {
        nextRow.daily_pnl_after_fees = applyExistingFeeSpread(
          custom.daily_pnl,
          row.daily_pnl,
          row.daily_pnl_after_fees,
        );
      }
      if (custom.daily_pnl_ida2_close !== undefined) {
        nextRow.daily_pnl_after_fees_ida2_close = applyExistingFeeSpread(
          custom.daily_pnl_ida2_close,
          row.daily_pnl_ida2_close,
          row.daily_pnl_after_fees_ida2_close,
        );
      }
      if (custom.daily_pnl_fix2_close !== undefined) {
        nextRow.daily_pnl_after_fees_fix2_close = applyExistingFeeSpread(
          custom.daily_pnl_fix2_close,
          row.daily_pnl_fix2_close,
          row.daily_pnl_after_fees_fix2_close,
        );
      }
      if (custom.daily_pnl_ida1_benchmark !== undefined) {
        nextRow.daily_pnl_after_fees_ida1_benchmark = applyExistingFeeSpread(
          custom.daily_pnl_ida1_benchmark,
          row.daily_pnl_ida1_benchmark,
          row.daily_pnl_after_fees_ida1_benchmark,
        );
      }
      const modifiedIda1Pnl = numberOrNull(nextRow.daily_pnl);
      const modifiedIda2Pnl = numberOrNull(nextRow.daily_pnl_ida2_close);
      const modifiedFix2Pnl = numberOrNull(nextRow.daily_pnl_fix2_close);
      if (modifiedIda1Pnl !== null && modifiedIda2Pnl !== null) {
        nextRow.delta_pnl_ida2_close = Number((modifiedIda2Pnl - modifiedIda1Pnl).toFixed(2));
      }
      if (modifiedIda1Pnl !== null && modifiedFix2Pnl !== null) {
        nextRow.delta_pnl_fix2_close = Number((modifiedFix2Pnl - modifiedIda1Pnl).toFixed(2));
      }
      if (custom.status !== undefined) {
        nextRow.status = custom.status;
      }
      if (custom.settlement_status !== undefined) {
        nextRow.settlement_status = custom.settlement_status;
        nextRow.status = custom.status || custom.settlement_status;
      }
      if (custom.base_action !== undefined) {
        nextRow.base_action = normalizeBaseAction(custom.base_action);
      }
      if (
        custom.base_action !== undefined
        || custom.base_volume !== undefined
        || custom.base_volume_mwh !== undefined
        || custom.base_price !== undefined
        || custom.venue_base_mwh !== undefined
      ) {
        const modifiedBaseAction = normalizeBaseAction(custom.base_action ?? nextRow.base_action ?? row?.base_context?.base_action);
        nextRow.base_context = {
          ...(row.base_context || {}),
          base_action: modifiedBaseAction,
          display_action: modifiedBaseAction,
          volume: numberOrNull(custom.base_volume) ?? numberOrNull(custom.base_volume_mwh) ?? row?.base_context?.volume ?? 0,
          runtime_base_price: numberOrNull(custom.base_price) ?? row?.base_context?.runtime_base_price,
          venue_base_mwh: numberOrNull(custom.venue_base_mwh) ?? row?.base_context?.venue_base_mwh,
          daily_volume_mwh: numberOrNull(custom.venue_base_mwh) ?? row?.base_context?.daily_volume_mwh,
          has_settled_activity_without_setup: false,
          source: 'modified_simulation',
        };
      }

      return nextRow;
    });
  }, [isModifiedSimulationActive, quickReplayRowsByDate, rawDailyRows]);

  useEffect(() => {
    if (!isAuthenticated || !isLetsTradeSection || !letsTradeDeliveryDate) {
      return;
    }
    if (detailsLoadingDate === letsTradeDeliveryDate) {
      return;
    }
    const deliveryRow = dailyRows.find((row) => String(row?.date || '').slice(0, 10) === letsTradeDeliveryDate);
    if (!deliveryRow || !isRealizedSimulationRow(deliveryRow)) {
      return;
    }
    const currentStatus = String(deliveryRow.status || '');
    const loadedForStatus = dayDetailsLoadedForStatusRef.current[letsTradeDeliveryDate];
    if (loadedForStatus === currentStatus) {
      return;
    }
    dayDetailsLoadedForStatusRef.current[letsTradeDeliveryDate] = currentStatus;
    loadDayDetails(letsTradeDeliveryDate).catch((err) => {
      console.error(err);
    });
  }, [dailyRows, detailsLoadingDate, isAuthenticated, isLetsTradeSection, letsTradeDeliveryDate, loadDayDetails]);

  const resultsRefreshLabel = loading
    ? resultsCopy.loading
    : resultsLoading
      ? resultsCopy.refreshingResults
      : resultsCopy.refreshResults;
  const getLoadingStyle = (variant) => variant === 'blue'
    ? {
      border: darkMode ? 'border-blue-900/70' : 'border-blue-200',
      background: darkMode ? 'bg-blue-950/40' : 'bg-blue-50',
      text: darkMode ? 'text-blue-100' : 'text-blue-900',
      spinner: darkMode ? 'border-blue-800/80 border-t-blue-200' : 'border-blue-200 border-t-blue-700',
    }
    : {
      border: darkMode ? 'border-emerald-900/70' : 'border-emerald-200',
      background: darkMode ? 'bg-emerald-950/40' : 'bg-emerald-50',
      text: darkMode ? 'text-emerald-100' : 'text-emerald-900',
      spinner: darkMode
        ? 'border-emerald-800/80 border-t-emerald-200'
        : 'border-emerald-200 border-t-emerald-700',
    };
  const renderSectionLoading = (text, variant) => {
    const style = getLoadingStyle(variant);
    return (
      <div className={`mx-6 -mt-2 rounded-xl border ${style.border} ${style.background} px-4 py-4 text-sm ${style.text}`}>
        <div className="flex items-center gap-3">
          <div className={`h-6 w-6 rounded-full border-2 ${style.spinner} animate-spin`} />
          <span>{text}</span>
        </div>
      </div>
    );
  };
  const renderInlineLoading = (text, variant) => {
    const style = getLoadingStyle(variant);
    return (
      <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${style.border} ${style.background} ${style.text}`}>
        <div className={`h-4 w-4 rounded-full border ${style.spinner} border-2 animate-spin`} />
        <span>{text}</span>
      </div>
    );
  };
  const renderMooseLoading = (text, detail) => (
    <div className={`sim-moose-loading ${darkMode ? 'sim-moose-loading-dark' : ''}`} role="status" aria-live="polite">
      <div className="sim-moose-stage" aria-hidden="true">
        <span className="sim-moose-dancer" role="img" aria-label="Dancing duck">🦆</span>
        <span className="sim-moose-shadow" />
      </div>
      <div>
        <div className="sim-moose-loading-title">{text}</div>
        {detail && <div className="sim-moose-loading-detail">{detail}</div>}
      </div>
    </div>
  );
  const getFix2ClosePathVolume = useCallback((row) => {
    if (!row) {
      return null;
    }
    const benchmarkFix2 = Number(row?.total_volume_fix2_close_benchmark);
    return Number.isFinite(benchmarkFix2) && benchmarkFix2 > 0 ? benchmarkFix2 : null;
  }, []);
  const getIda2ClosePathVolume = useCallback((row) => {
    if (!row) {
      return null;
    }
    const pathVolume = Number(row?.path_volume_mwh);
    if (Number.isFinite(pathVolume) && pathVolume > 0) return pathVolume;
    const totalVolume = Number(row?.total_volume);
    return Number.isFinite(totalVolume) && totalVolume > 0 ? totalVolume : null;
  }, []);
  const getIda1ClosePathVolume = useCallback((row) => {
    if (!row) {
      return null;
    }
    const benchmarkVolume = Number(row?.total_volume_ida1_benchmark);
    if (Number.isFinite(benchmarkVolume) && benchmarkVolume > 0) {
      return benchmarkVolume;
    }
    const totalVolume = Number(row?.total_volume);
    if (!Number.isFinite(totalVolume) || totalVolume <= 0) {
      return null;
    }
    const ida2Volume = Number(row?.ida2_volume);
    if (!Number.isFinite(ida2Volume) || ida2Volume <= 0) {
      return totalVolume;
    }
    return Number(Math.max(totalVolume - ida2Volume, 0).toFixed(2));
  }, []);
  const getIda1BenchmarkPnl = useCallback((row) => {
    const benchmarkValue = Number(row?.daily_pnl_ida1_benchmark);
    if (Number.isFinite(benchmarkValue)) {
      return benchmarkValue;
    }
    if (row?.ida1_benchmark_basis) {
      return Number(row?.daily_pnl || 0);
    }
    const status = String(row?.status || '');
    if (status === 'settled_ida1_fallback' || status === 'settled_ida1_fallback_no_settlement') {
      return Number(row?.daily_pnl || 0);
    }
    if (row?.daily_pnl_ida2_close !== null && row?.daily_pnl_ida2_close !== undefined) {
      return null;
    }
    return Number(row?.daily_pnl || 0);
  }, []);
  const getIda1BenchmarkAfterFees = useCallback((row) => {
    const benchmarkValue = Number(row?.daily_pnl_after_fees_ida1_benchmark);
    if (Number.isFinite(benchmarkValue)) {
      return benchmarkValue;
    }
    if (getIda1BenchmarkPnl(row) === null) {
      return null;
    }
    return Number(row?.daily_pnl_after_fees || 0);
  }, [getIda1BenchmarkPnl]);
  const getPathVolume = useCallback((row) => {
    if (!row) {
      return 0;
    }
    const explicitPathVolume = Number(row?.path_volume_mwh);
    if (Number.isFinite(explicitPathVolume) && explicitPathVolume > 0) {
      return explicitPathVolume;
    }
    const totalVolume = Number(row?.total_volume);
    return Number.isFinite(totalVolume) && totalVolume > 0 ? totalVolume : 0;
  }, []);
  const getBaseStopPathVolume = useCallback((row) => {
    if (!row) {
      return null;
    }
    const benchmarkBase = Number(row?.venue_base_mwh_benchmark);
    return Number.isFinite(benchmarkBase) && benchmarkBase > 0 ? benchmarkBase : null;
  }, []);
  const calculateProfitPerMwh = useCallback((pnlValue, volumeValue) => {
    const pnl = Number(pnlValue);
    const volume = Number(volumeValue);
    if (!Number.isFinite(pnl) || !Number.isFinite(volume) || volume <= 0) {
      return null;
    }
    return Number((pnl / volume).toFixed(2));
  }, []);
  const comparableSummary = useMemo(() => {
    const realizedRows = dailyRows.filter((row) => String(row?.status || '').startsWith('settled'));
    const fullyComparableRows = realizedRows.filter(
      (row) =>
        row?.daily_pnl !== null && row?.daily_pnl !== undefined
        && getIda1BenchmarkPnl(row) !== null
        && row?.daily_pnl_fix2_close !== null && row?.daily_pnl_fix2_close !== undefined
        && row?.daily_pnl_ida2_close !== null && row?.daily_pnl_ida2_close !== undefined
        && row?.available_stages?.ida2 !== false,
    );

    if (!fullyComparableRows.length) {
      return {
        total_combined_pnl: summary.total_combined_pnl,
        total_combined_pnl_after_fees: summary.total_combined_pnl_after_fees,
        total_combined_fees: summary.total_combined_fees,
        total_combined_pnl_ida2_close: summary.total_combined_pnl_ida2_close,
        total_combined_pnl_ida2_close_after_fees: summary.total_combined_pnl_ida2_close_after_fees,
        total_combined_fees_ida2_close: summary.total_combined_fees_ida2_close,
        total_combined_pnl_historical_cutover: summary.total_combined_pnl_historical_cutover,
        total_combined_pnl_historical_cutover_after_fees: summary.total_combined_pnl_historical_cutover_after_fees,
        total_combined_fees_historical_cutover: summary.total_combined_fees_historical_cutover,
        total_combined_pnl_fix2_close: summary.total_combined_pnl_fix2_close,
        total_combined_pnl_fix2_close_after_fees: summary.total_combined_pnl_fix2_close_after_fees,
        total_combined_fees_fix2_close: summary.total_combined_fees_fix2_close,
        total_combined_pnl_base_stop: summary.total_base_pnl,
        total_volume_base_stop: summary.venue_base_mwh,
        avg_profit_per_mwh_base_stop: calculateProfitPerMwh(summary.total_base_pnl, summary.venue_base_mwh),
        total_volume_ida1_close: null,
        total_volume_ida2_close: null,
        total_volume_fix2_close: null,
        avg_profit_per_mwh_ida1_close: null,
        avg_profit_per_mwh_ida2_close: null,
        avg_profit_per_mwh_fix2_close: null,
        fix_layer_pnl: null,
        fix_layer_pnl_after_fees: null,
        ida1_layer_pnl: null,
        ida1_layer_pnl_after_fees: null,
        ida2_layer_pnl: null,
        ida2_layer_pnl_after_fees: null,
        total_volume_fix_layer: null,
        total_volume_ida1_layer: null,
        total_volume_ida2_layer: null,
        avg_profit_per_mwh_fix_layer: null,
        avg_profit_per_mwh_ida1_layer: null,
        avg_profit_per_mwh_ida2_layer: null,
        realizedDays: realizedRows.length,
        comparableDays: realizedRows.length,
      };
    }

    const ida1Gross = fullyComparableRows.reduce((acc, row) => acc + Number(getIda1BenchmarkPnl(row) || 0), 0);
    const ida1AfterFees = fullyComparableRows.reduce((acc, row) => acc + Number(getIda1BenchmarkAfterFees(row) || 0), 0);
    const ida2Gross = fullyComparableRows.reduce((acc, row) => {
      const val = row.daily_pnl_ida2_close != null ? row.daily_pnl_ida2_close : getIda1BenchmarkPnl(row);
      return acc + Number(val ?? 0);
    }, 0);
    const ida2AfterFees = fullyComparableRows.reduce((acc, row) => {
      const val = row.daily_pnl_after_fees_ida2_close != null ? row.daily_pnl_after_fees_ida2_close : getIda1BenchmarkAfterFees(row);
      return acc + Number(val ?? 0);
    }, 0);
    const fix2Gross = fullyComparableRows.reduce((acc, row) => acc + Number(row.daily_pnl_fix2_close_benchmark ?? 0), 0);
    const fix2AfterFees = fullyComparableRows.reduce((acc, row) => acc + Number(row.daily_pnl_after_fees_fix2_close || 0), 0);
    const ida1Volume = fullyComparableRows.reduce((acc, row) => acc + Number(getIda1ClosePathVolume(row) || 0), 0);
    const ida2Volume = fullyComparableRows.reduce((acc, row) => {
      const vol = row.daily_pnl_ida2_close != null ? getIda2ClosePathVolume(row) : getIda1ClosePathVolume(row);
      return acc + Number(vol || 0);
    }, 0);
    const fix2Volume = fullyComparableRows.reduce((acc, row) => acc + Number(getFix2ClosePathVolume(row) || 0), 0);
    const baseGross = fullyComparableRows.reduce((acc, row) => acc + Number(row.base_pnl_benchmark ?? 0), 0);
    const baseVolume = fullyComparableRows.reduce((acc, row) => acc + Number(getBaseStopPathVolume(row) || 0), 0);
    const fixLayerVolume = Math.max(0, fix2Volume - baseVolume);
    const ida1LayerVolume = Math.max(0, ida1Volume - fix2Volume);
    const ida2LayerVolume = fullyComparableRows.reduce((acc, row) => acc + Number(row.venue_ida2_mwh || 0), 0);
    const historicalGross = fullyComparableRows.reduce((acc, row) => {
      const useFix2Close = String(row.date || '') < (summary.ida1_cutover_date || '2025-12-01');
      return acc + Number(useFix2Close ? row.daily_pnl_fix2_close || 0 : getIda1BenchmarkPnl(row) || 0);
    }, 0);
    const historicalAfterFees = fullyComparableRows.reduce((acc, row) => {
      const useFix2Close = String(row.date || '') < (summary.ida1_cutover_date || '2025-12-01');
      return acc + Number(useFix2Close ? row.daily_pnl_after_fees_fix2_close || 0 : getIda1BenchmarkAfterFees(row) || 0);
    }, 0);

    return {
      total_combined_pnl: Number(ida1Gross.toFixed(2)),
      total_combined_pnl_after_fees: Number(ida1AfterFees.toFixed(2)),
      total_combined_fees: Number((ida1Gross - ida1AfterFees).toFixed(2)),
      total_combined_pnl_ida2_close: Number(ida2Gross.toFixed(2)),
      total_combined_pnl_ida2_close_after_fees: Number(ida2AfterFees.toFixed(2)),
      total_combined_fees_ida2_close: Number((ida2Gross - ida2AfterFees).toFixed(2)),
      total_combined_pnl_historical_cutover: Number(historicalGross.toFixed(2)),
      total_combined_pnl_historical_cutover_after_fees: Number(historicalAfterFees.toFixed(2)),
      total_combined_fees_historical_cutover: Number((historicalGross - historicalAfterFees).toFixed(2)),
      total_combined_pnl_fix2_close: Number(fix2Gross.toFixed(2)),
      total_combined_pnl_fix2_close_after_fees: Number(fix2AfterFees.toFixed(2)),
      total_combined_fees_fix2_close: Number((fix2Gross - fix2AfterFees).toFixed(2)),
      total_combined_pnl_base_stop: Number(baseGross.toFixed(2)),
      total_volume_base_stop: Number(baseVolume.toFixed(2)),
      avg_profit_per_mwh_base_stop: baseVolume > 0 ? Number((baseGross / baseVolume).toFixed(2)) : null,
      total_volume_ida1_close: Number(ida1Volume.toFixed(2)),
      total_volume_ida2_close: Number(ida2Volume.toFixed(2)),
      total_volume_fix2_close: Number(fix2Volume.toFixed(2)),
      avg_profit_per_mwh_ida1_close: ida1Volume > 0 ? Number((ida1Gross / ida1Volume).toFixed(2)) : null,
      avg_profit_per_mwh_ida2_close: ida2Volume > 0 ? Number((ida2Gross / ida2Volume).toFixed(2)) : null,
      avg_profit_per_mwh_fix2_close: fix2Volume > 0 ? Number((fix2Gross / fix2Volume).toFixed(2)) : null,
      fix_layer_pnl: Number((fix2Gross - baseGross).toFixed(2)),
      fix_layer_pnl_after_fees: fix2Gross !== 0
        ? Number(((fix2Gross - baseGross) * (fix2AfterFees / fix2Gross)).toFixed(2))
        : null,
      total_combined_pnl_base_stop_after_fees: fix2Gross !== 0
        ? Number((baseGross * (fix2AfterFees / fix2Gross)).toFixed(2))
        : null,
      ida1_layer_pnl: Number((ida1Gross - fix2Gross).toFixed(2)),
      ida1_layer_pnl_after_fees: Number((ida1AfterFees - fix2AfterFees).toFixed(2)),
      ida2_layer_pnl: Number((ida2Gross - ida1Gross).toFixed(2)),
      ida2_layer_pnl_after_fees: Number((ida2AfterFees - ida1AfterFees).toFixed(2)),
      total_volume_fix_layer: Number(fixLayerVolume.toFixed(2)),
      total_volume_ida1_layer: Number(ida1LayerVolume.toFixed(2)),
      total_volume_ida2_layer: Number(ida2LayerVolume.toFixed(2)),
      avg_profit_per_mwh_fix_layer: fixLayerVolume > 0 ? Number(((fix2Gross - baseGross) / fixLayerVolume).toFixed(2)) : null,
      avg_profit_per_mwh_ida1_layer: ida1LayerVolume > 0 ? Number(((ida1Gross - fix2Gross) / ida1LayerVolume).toFixed(2)) : null,
      avg_profit_per_mwh_ida2_layer: ida2LayerVolume > 0 ? Number(((ida2Gross - ida1Gross) / ida2LayerVolume).toFixed(2)) : null,
      realizedDays: realizedRows.length,
      comparableDays: fullyComparableRows.length,
    };
  }, [
    dailyRows,
    summary,
    calculateProfitPerMwh,
    getBaseStopPathVolume,
    getFix2ClosePathVolume,
    getIda1BenchmarkAfterFees,
    getIda1BenchmarkPnl,
    getIda1ClosePathVolume,
    getIda2ClosePathVolume,
  ]);
  const ida1BenchmarkSummary = useMemo(() => {
    const comparableDays = Number(comparableSummary.comparableDays || 0);
    if (!isModifiedSimulationActive && Number.isFinite(comparableDays) && comparableDays > 0) {
      return {
        totalValue: Number(comparableSummary.total_combined_pnl || 0),
        afterFeesValue: Number(comparableSummary.total_combined_pnl_after_fees || 0),
        feesValue: Number(comparableSummary.total_combined_fees || 0),
        totalVolume: Number(comparableSummary.total_volume_ida1_close || 0),
        profitPerMwh: comparableSummary.avg_profit_per_mwh_ida1_close ?? null,
        benchmarkDays: comparableDays,
      };
    }
    const benchmarkRows = dailyRows.filter(
      (row) => isIda1BenchmarkRow(row)
        && getIda1BenchmarkPnl(row) !== null,
    );
    const gross = benchmarkRows.reduce((acc, row) => acc + Number(getIda1BenchmarkPnl(row) || 0), 0);
    const afterFees = benchmarkRows.reduce((acc, row) => acc + Number(getIda1BenchmarkAfterFees(row) || 0), 0);
    const volume = benchmarkRows.reduce((acc, row) => acc + Number(getIda1ClosePathVolume(row) || 0), 0);
    return {
      totalValue: Number(gross.toFixed(2)),
      afterFeesValue: Number(afterFees.toFixed(2)),
      feesValue: Number((gross - afterFees).toFixed(2)),
      totalVolume: Number(volume.toFixed(2)),
      profitPerMwh: volume > 0 ? Number((gross / volume).toFixed(2)) : null,
      benchmarkDays: benchmarkRows.length,
    };
  }, [comparableSummary, dailyRows, getIda1BenchmarkAfterFees, getIda1BenchmarkPnl, getIda1ClosePathVolume, isModifiedSimulationActive]);
  const benchmarkComparisonRows = [
    {
      key: 'base_layer',
      label: 'Base',
      totalValue: comparableSummary.total_combined_pnl_base_stop,
      afterFeesValue: comparableSummary.total_combined_pnl_base_stop_after_fees,
      feesValue: comparableSummary.total_combined_pnl_base_stop != null && comparableSummary.total_combined_pnl_base_stop_after_fees != null
        ? Number((comparableSummary.total_combined_pnl_base_stop - comparableSummary.total_combined_pnl_base_stop_after_fees).toFixed(2))
        : null,
      totalVolume: comparableSummary.total_volume_base_stop,
      profitPerMwh: comparableSummary.avg_profit_per_mwh_base_stop,
      helper: 'Cumulative PnL from the BASE auction only (50 MW base position). Volume = venue_base_mwh. After-fees estimated proportionally from Fix II stop fee ratio.',
    },
    {
      key: 'fixing_layer',
      label: 'Fixing',
      totalValue: comparableSummary.fix_layer_pnl,
      afterFeesValue: comparableSummary.fix_layer_pnl_after_fees,
      feesValue: comparableSummary.fix_layer_pnl != null && comparableSummary.fix_layer_pnl_after_fees != null
        ? Number((comparableSummary.fix_layer_pnl - comparableSummary.fix_layer_pnl_after_fees).toFixed(2))
        : null,
      totalVolume: comparableSummary.total_volume_fix_layer,
      profitPerMwh: comparableSummary.avg_profit_per_mwh_fix_layer,
      helper: 'Incremental PnL added by Fix I + Fix II on top of Base (Fix II Stop − Base Stop). Volume = venue_fix_i_mwh + venue_fix_ii_mwh. After-fees estimated proportionally from Fix II stop fee ratio.',
    },
    {
      key: 'ida1_layer',
      label: 'IDA1 Stop',
      totalValue: comparableSummary.total_combined_pnl,
      afterFeesValue: comparableSummary.total_combined_pnl_after_fees,
      feesValue: comparableSummary.total_combined_pnl != null && comparableSummary.total_combined_pnl_after_fees != null
        ? Number((comparableSummary.total_combined_pnl - comparableSummary.total_combined_pnl_after_fees).toFixed(2))
        : null,
      totalVolume: comparableSummary.total_volume_ida1_close,
      profitPerMwh: comparableSummary.avg_profit_per_mwh_ida1_close,
      helper: `Cumulative strategy PnL if all residual positions were closed at IDA1 — no carry (BASE + Fixing + IDA1). Comparable across ${ida1BenchmarkSummary.benchmarkDays} day${ida1BenchmarkSummary.benchmarkDays === 1 ? '' : 's'} where IDA2 data was available.`,
    },
    {
      key: 'ida2_layer',
      label: 'IDA2 Final',
      totalValue: comparableSummary.total_combined_pnl_ida2_close,
      afterFeesValue: comparableSummary.total_combined_pnl_ida2_close_after_fees,
      feesValue: comparableSummary.total_combined_pnl_ida2_close != null && comparableSummary.total_combined_pnl_ida2_close_after_fees != null
        ? Number((comparableSummary.total_combined_pnl_ida2_close - comparableSummary.total_combined_pnl_ida2_close_after_fees).toFixed(2))
        : null,
      totalVolume: comparableSummary.total_volume_ida2_close,
      profitPerMwh: comparableSummary.avg_profit_per_mwh_ida2_close,
      carryBenefit: comparableSummary.ida2_layer_pnl,
      helper: 'Cumulative strategy PnL with IDA2 carry active (BASE + Fixing + IDA1 + IDA2 Carry). Compare with IDA1 Stop to see carry value.',
    },
  ];
  const selectedPredictDetails = selectedRunIds.predict ? runDetailsByKey[`predict:${selectedRunIds.predict}`] : null;
  const selectedSettleDetails = selectedRunIds.settle ? runDetailsByKey[`settle:${selectedRunIds.settle}`]?.data : null;
  const activePredictRows = useMemo(() => (
    runPanelMode.predict === 'history' && selectedPredictDetails
      ? (selectedPredictDetails.rows || [])
      : orderPlan
  ), [orderPlan, runPanelMode.predict, selectedPredictDetails]);
  const activePredictMeta = useMemo(() => (
    runPanelMode.predict === 'history' && selectedPredictDetails
      ? (selectedPredictDetails.metadata || null)
      : orderPlanMeta
  ), [orderPlanMeta, runPanelMode.predict, selectedPredictDetails]);
  const letsTradePlanMatchesDate = isLetsTradeSection && letsTradeOrderPlanDate === letsTradeDeliveryDate;
  const previewPredictRows = letsTradePlanMatchesDate ? letsTradeOrderPlan : activePredictRows;
  const previewPredictMeta = letsTradePlanMatchesDate ? letsTradeOrderPlanMeta : activePredictMeta;
  const previewDeliveryDate = isLetsTradeSection ? letsTradeDeliveryDate : date;
  const activeSettleSummary = runPanelMode.settle === 'history' && selectedSettleDetails
    ? selectedSettleDetails.summary
    : null;
  const predictStageStatuses = statusData?.predict_stage_statuses || {};
  const nonNordpoolPlan = activePredictRows.some((row) => row.capacity_source && row.capacity_source !== 'nordpool')
    || (Array.isArray(activePredictMeta?.capacity_source)
      ? activePredictMeta.capacity_source.some((source) => source && source !== 'nordpool')
      : Boolean(activePredictMeta?.capacity_source && activePredictMeta.capacity_source !== 'nordpool'));
  const settleRuns = getStageRuns('settle');
  const settleResultModeOptions = useMemo(() => ([
    {
      key: 'fix1',
      label: 'Fix I',
      cardLabel: 'Fill Fix I',
      rowValueKeys: [],
      afterFeesKeys: [],
      runMatches: () => false,
      isAvailableFromSummary: () => false,
      sourceKind: 'fix1_execution',
    },
    {
      key: 'fix2',
      label: 'Fix II',
      cardLabel: 'Fix II Stop Benchmark',
      rowValueKeys: ['daily_pnl_fix2_close', 'pnl_fix2_close'],
      afterFeesKeys: ['daily_pnl_after_fees_fix2_close', 'pnl_after_fees_fix2_close'],
      runMatches: (run) => {
        const status = String(run?.status || '');
        const variant = String(run?.stage_variant || '');
        return variant === 'fix2_partial' || status === 'partial_fix2_settle' || status === 'settled_fix2_fallback';
      },
      isAvailableFromSummary: (row) => {
        const value = row?.daily_pnl_fix2_close ?? row?.pnl_fix2_close;
        return value !== null && value !== undefined;
      },
    },
    {
      key: 'ida1',
      label: 'IDA1',
      cardLabel: 'Goose IDA1 Stop',
      rowValueKeys: ['daily_pnl', 'pnl_ida1_close'],
      afterFeesKeys: ['daily_pnl_after_fees', 'pnl_after_fees_ida1_close'],
      runMatches: (run) => {
        const status = String(run?.status || '');
        const variant = String(run?.stage_variant || '');
        return variant === 'ida1_partial' || status === 'settled_ida1_fallback';
      },
      isAvailableFromSummary: (row) => {
        if (row?.available_stages?.ida1?.ready !== undefined) {
          return Boolean(row.available_stages.ida1.ready);
        }
        const value = row?.daily_pnl ?? row?.pnl_ida1_close;
        return value !== null && value !== undefined;
      },
    },
    {
      key: 'ida2',
      label: 'IDA2',
      cardLabel: 'Goose IDA2 Final',
      rowValueKeys: ['daily_pnl_ida2_close', 'pnl_ida2_close'],
      afterFeesKeys: ['daily_pnl_after_fees_ida2_close', 'pnl_after_fees_ida2_close'],
      runMatches: (run) => {
        const status = String(run?.status || '');
        const variant = String(run?.stage_variant || '');
        return variant === 'final_ida2' || status === 'settled' || status === 'settled_ida2_no_ida1';
      },
      isAvailableFromSummary: (row) => {
        if (row?.available_stages?.ida2?.ready !== undefined) {
          return Boolean(row.available_stages.ida2.ready);
        }
        const value = row?.daily_pnl_ida2_close;
        return value !== null && value !== undefined;
      },
    },
  ]), []);
  const settleDisplayModes = settleResultModeOptions;
  const predictResultModeOptions = useMemo(() => ([
    {
      key: 'fix2',
      label: 'Fix II',
      cardLabel: 'Fix II plan',
      isAvailableFromMeta: (meta) => (
        Array.isArray(meta?.fix2_issue_date) && meta.fix2_issue_date.length > 0
      ) || Number(meta?.plan_rows || 0) > 0,
      getIssueDate: (meta) => (
        Array.isArray(meta?.fix2_issue_date) && meta.fix2_issue_date.length
          ? meta.fix2_issue_date[meta.fix2_issue_date.length - 1]
          : meta?.latest_issue_date || null
      ),
      isReady: (meta) => (
        Array.isArray(meta?.fix2_issue_date) && meta.fix2_issue_date.length > 0
      ) || Number(meta?.plan_rows || 0) > 0,
      statusLabel: 'Fix II plan',
    },
    {
      key: 'ida1',
      label: 'IDA1',
      cardLabel: 'IDA1 enriched',
      isAvailableFromMeta: (meta) => Boolean(meta?.ida1_prediction?.available),
      getIssueDate: (meta) => meta?.ida1_prediction?.latest_issue_date || null,
      isReady: (meta) => Boolean(meta?.ida1_prediction?.available),
      statusLabel: 'IDA1-enriched plan',
    },
    {
      key: 'ida2',
      label: 'IDA2',
      cardLabel: 'IDA2 enriched',
      isAvailableFromMeta: (meta) => Boolean(meta?.ida2_prediction?.available),
      getIssueDate: (meta) => meta?.ida2_prediction?.latest_issue_date || null,
      isReady: (meta) => Boolean(meta?.ida2_prediction?.available),
      statusLabel: 'IDA2-enriched plan',
    },
  ]), []);
  const predictDisplayModes = predictResultModeOptions;
  const getModeValue = (row, keys) => {
    if (!row) return null;
    for (const key of keys) {
      const value = row?.[key];
      if (value !== null && value !== undefined) {
        return value;
      }
    }
    return null;
  };
  const settleModeRuns = useMemo(() => settleResultModeOptions.reduce((acc, mode) => {
    acc[mode.key] = settleRuns.find((run) => mode.runMatches(run)) || null;
    return acc;
  }, {}), [settleResultModeOptions, settleRuns]);
  const settleModeMap = useMemo(() => {
    const settleDefaults = statusData?.settle || {};
    return settleResultModeOptions.reduce((acc, mode) => {
      if (mode.sourceKind === 'fix1_execution') {
        const execStatus = statusData?.fix1_execution || null;
        const execSummary = execStatus?.summary || {};
        const execRuns = Array.isArray(statusData?.fix1_execution_runs) ? statusData.fix1_execution_runs : [];
        const execRun = execStatus?.pipeline_run || execRuns[0] || null;
        const execRunStatus = String(execRun?.status || '').trim().toLowerCase();
        const isRefreshing = ['requested', 'running', 'provisioning', 'pending'].includes(execRunStatus);
        const isFailed = execRunStatus === 'failed';
        const filled = execStatus?.execution_status === 'filled';
        const attempted = Boolean(execStatus);
        const totalVolumeMw = execSummary?.total_volume_mw ?? null;
        const avgPrice = execSummary?.avg_price ?? null;
        const normalizedStatus = isRefreshing
          ? 'running'
          : isFailed && !execStatus?.ready
            ? 'failed'
            : filled
              ? 'ready'
              : execStatus?.execution_status === 'ready_no_fill'
                ? 'missing'
                : 'pending';
        acc[mode.key] = {
          ...mode,
          run: execRun,
          summary: execSummary,
          attempted,
          available: filled,
          pnl: filled ? avgPrice : null,
          afterFees: null,
          issueDate: execRun?.started_at || null,
          updatedAt: execRun?.finished_at || null,
          warningMessage: !attempted ? 'Fix I refresh has not been run yet for this date.' : null,
          errorMessage: execStatus?.error_message || execRun?.error_message || null,
          capacitySource: execSummary?.capacity_source || null,
          statusValue: normalizedStatus,
          fix1Volume: totalVolumeMw,
          fix1AvgPrice: avgPrice,
        };
        return acc;
      }
      const modeRun = settleModeRuns[mode.key] || null;
      const currentModeSummary = mode.isAvailableFromSummary(settleDefaults) ? settleDefaults : null;
      const modeSummary = modeRun
        ? (runDetailsByKey[`settle:${modeRun.run_id}`]?.data?.summary || null)
        : currentModeSummary;
      const modeAvailable = Boolean(modeSummary && mode.isAvailableFromSummary(modeSummary));
      const modeAttempted = Boolean(modeRun || currentModeSummary);
      const modeWarningMessage = modeSummary?.warning_message
        || modeRun?.warning_message
        || (modeAttempted && !modeAvailable ? 'Stage was attempted, but no live result was saved for this mode.' : null)
        || (!modeAttempted ? 'This execution stage has not been calculated yet for this date.' : null)
        || null;
      const modeErrorMessage = modeSummary?.error_message
        || modeRun?.error_message
        || null;
      acc[mode.key] = {
        ...mode,
        run: modeRun,
        summary: modeSummary,
        attempted: modeAttempted,
        available: modeAvailable,
        pnl: modeAvailable ? getModeValue(modeSummary, mode.rowValueKeys) : null,
        afterFees: modeAvailable ? getModeValue(modeSummary, mode.afterFeesKeys) : null,
        issueDate: modeSummary?.issue_date || modeRun?.started_at || null,
        updatedAt: modeSummary?.updated_at || modeRun?.finished_at || null,
        warningMessage: modeWarningMessage,
        errorMessage: modeErrorMessage,
        capacitySource: modeSummary?.capacity_source || null,
        statusValue: modeSummary?.status || modeRun?.status || (modeAttempted ? 'attempted' : 'not_run_yet'),
      };
      return acc;
    }, {});
  }, [runDetailsByKey, settleModeRuns, settleResultModeOptions, statusData]);
  const predictModeMap = useMemo(() => predictResultModeOptions.reduce((acc, mode) => {
    const modeMeta = activePredictMeta || {};
    acc[mode.key] = {
      ...mode,
      meta: modeMeta,
      available: mode.isAvailableFromMeta(modeMeta),
      source: 'prediction',
      issueDate: mode.getIssueDate(modeMeta),
      ready: mode.isReady(modeMeta),
    };
    return acc;
  }, {}), [activePredictMeta, predictResultModeOptions]);
  const defaultSettleResultMode = (
    settleModeMap.ida2?.available
      ? 'ida2'
      : settleModeMap.ida1?.available
        ? 'ida1'
        : settleModeMap.fix2?.available
          ? 'fix2'
          : settleModeMap.fix1?.available
            ? 'fix1'
            : settleModeMap.ida2?.attempted
              ? 'ida2'
              : settleModeMap.ida1?.attempted
                ? 'ida1'
                : settleModeMap.fix2?.attempted
                  ? 'fix2'
                  : settleModeMap.fix1?.attempted
                    ? 'fix1'
                    : 'ida2'
  );
  const defaultPredictResultMode = (
    predictModeMap.ida2?.available
      ? 'ida2'
      : predictModeMap.ida1?.available
        ? 'ida1'
        : 'fix2'
  );
  const activePredictMode = (
    predictModeMap[predictResultMode]
    || predictModeMap.fix2
    || predictResultModeOptions[0]
  );
  const activePredictVariantLabel = getSimulationVariantLabel();
  const activeFix2IssueDates = useMemo(() => {
    if (Array.isArray(activePredictMeta?.fix2_issue_date) && activePredictMeta.fix2_issue_date.length) {
      return activePredictMeta.fix2_issue_date;
    }
    const fallbackIssueDates = Array.from(new Set(
      (activePredictRows || [])
        .map((row) => row?.issue_date)
        .filter(Boolean),
    ));
    if (fallbackIssueDates.length) {
      return fallbackIssueDates;
    }
    if (activePredictMeta?.latest_issue_date) {
      return [activePredictMeta.latest_issue_date];
    }
    return [];
  }, [activePredictMeta, activePredictRows]);
  const getStagePredictStatusLabel = (stageKey, isActive = false) => {
    if (stageKey === 'fix_ii') {
      const hasFixIIForecast = (
        activePredictRows.length > 0
        || Boolean(activePredictMeta?.market_readiness?.fix_ii?.ready)
        || Boolean(activePredictMeta?.latest_issue_date)
        || activeFix2IssueDates.length > 0
      );
      if (hasFixIIForecast) {
        return 'ready';
      }
    }
    const meta = stageKey === 'ida1' ? activePredictMeta?.ida1_prediction : activePredictMeta?.ida2_prediction;
    const stageStatus = String(predictStageStatuses?.[stageKey]?.status || 'pending').toLowerCase();
    if (meta?.available || ['ready', 'completed', 'success', 'available'].includes(stageStatus)) {
      return 'ready';
    }
    return stageStatus;
  };
  const getStagePredictStatusBadge = (stageKey, isActive = false) =>
    getStatusBadgeMeta(getStagePredictStatusLabel(stageKey, isActive));
  const getStagePredictChipClass = (stageKey) => {
    return getStagePredictStatusBadge(stageKey).badgeClass;
  };
  const isFixIIPredictReadyForPreview = getStagePredictStatusLabel('fix_ii') === 'ready';
  const isIda1PredictReadyForPreview = getStagePredictStatusLabel('ida1') === 'ready';
  const isIda2PredictReadyForPreview = getStagePredictStatusLabel('ida2') === 'ready';
  const activePredictFix2PlanBadge = getStatusBadgeMeta(
    activePredictMode?.key === 'fix2'
      ? 'selected'
      : (isFixIIPredictReadyForPreview ? 'ready' : 'pending'),
  );
  const activePredictIda1Badge = getStagePredictStatusBadge('ida1', activePredictMode?.key === 'ida1');
  const activePredictIda2Badge = getStagePredictStatusBadge('ida2', activePredictMode?.key === 'ida2');
  const fix1PartialStatus = statusData?.fix1_partial || null;
  const fix1PartialSummary = fix1PartialStatus?.summary || {};
  const fix1PartialRows = Array.isArray(fix1PartialStatus?.rows) ? fix1PartialStatus.rows : [];
  const hasFix1PartialMark = Boolean(fix1PartialStatus?.ready && fix1PartialSummary?.daily_pnl_fix1_mark !== null && fix1PartialSummary?.daily_pnl_fix1_mark !== undefined);
  const pipelineTrainCards = [
    ...['goose-v3', 'goose-v5'].map((variantKey) => {
      const monitor = statusData?.pipeline_train_model_statuses?.[variantKey] || null;
      const statusValue = String(monitor?.status || 'not observed');
      const statusBadge = getStatusBadgeMeta(statusValue);
      return {
        variantKey,
        label: monitor?.label || getSimulationVariantLabel(variantKey),
        sourceLabel: howItWorksLanguage === 'pl' ? 'monitoring treningu modelu' : 'training monitor',
        statusBadge,
        latestAt: monitor?.finished_at || monitor?.started_at || null,
        modelVersion: monitor?.model_version || (variantKey === 'goose-v3' ? 'v3.1.0' : 'v5.0.0'),
        artifactPath: monitor?.s3_model_prefix || null,
        warning: monitor?.warning_message || null,
        error: monitor?.error_message || null,
        colorKey: variantKey,
      };
    }),
    ...['ida1', 'ida2'].map((stageKey) => {
      const monitor = statusData?.train_stage_statuses?.[stageKey] || null;
      const statusValue = String(monitor?.status || 'not observed');
      const statusBadge = getStatusBadgeMeta(statusValue);
      return {
        variantKey: stageKey,
        label: stageKey === 'ida1' ? 'IDA1 ida1-v1.0.3' : 'IDA2 ida2-v1.0.0',
        sourceLabel: howItWorksLanguage === 'pl' ? 'monitoring treningu modelu' : 'training monitor',
        statusBadge,
        latestAt: monitor?.finished_at || monitor?.started_at || null,
        modelVersion: stageKey === 'ida1' ? 'ida1-v1.0.3' : 'ida2-v1.0.0',
        artifactPath: null,
        warning: monitor?.warning_message || null,
        error: monitor?.error_message || null,
        colorKey: stageKey,
      };
    }),
  ];
  const pipelinePredictCards = [
    ...['goose-v3', 'goose-v5'].map((variantKey) => {
      const monitor = statusData?.pipeline_predict_model_statuses?.[variantKey] || null;
      const statusValue = String(monitor?.status || 'not observed');
      const statusBadge = getStatusBadgeMeta(statusValue);
      return {
        variantKey,
        label: monitor?.label || getSimulationVariantLabel(variantKey),
        sourceLabel: howItWorksLanguage === 'pl' ? 'monitoring outputu predykcji' : 'prediction output monitor',
        statusBadge,
        latestAt: monitor?.latest_issue_date || monitor?.finished_at || monitor?.started_at || null,
        rowsCount: monitor?.rows_count ?? 0,
        modelVersion: monitor?.model_version || (variantKey === 'goose-v3' ? 'v3.1.0' : 'v5.0.0'),
        warning: monitor?.warning_message || null,
        error: monitor?.error_message || null,
        colorKey: variantKey,
      };
    }),
    ...['ida1', 'ida2'].map((stageKey) => {
      const monitor = statusData?.predict_stage_statuses?.[stageKey] || null;
      const statusValue = String(monitor?.status || 'not observed');
      const statusBadge = getStatusBadgeMeta(statusValue);
      return {
        variantKey: stageKey,
        label: stageKey === 'ida1' ? 'IDA1 ida1-v1.0.3' : 'IDA2 ida2-v1.0.0',
        sourceLabel: howItWorksLanguage === 'pl' ? 'monitoring outputu predykcji' : 'prediction output monitor',
        statusBadge,
        latestAt: monitor?.latest_issue_date || monitor?.finished_at || monitor?.started_at || null,
        rowsCount: monitor?.rows_count ?? 0,
        modelVersion: stageKey === 'ida1' ? 'ida1-v1.0.3' : 'ida2-v1.0.0',
        warning: monitor?.warning_message || null,
        error: monitor?.error_message || null,
        colorKey: stageKey,
      };
    }),
  ];

  useEffect(() => {
    if (!isAuthenticated || !isLetsTradeSection || hasFix1PartialMark) return;
    if (!letsTradeDeliveryDate || letsTradeDeliveryDate < getWarsawDateOffset(0)) return;
    const currentDeliveryRow = dailyRows.find((row) => String(row?.date || '').slice(0, 10) === letsTradeDeliveryDate);
    if (currentDeliveryRow && isRealizedSimulationRow(currentDeliveryRow)) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      loadCoreData({ force: true, targetDate: letsTradeDeliveryDate, preserveOrderPlanMeta: true }).catch((err) => {
        console.error('Fix I partial auto-refresh failed', err);
      });
    }, 45000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFix1PartialMark, isAuthenticated, isLetsTradeSection, letsTradeDeliveryDate, dailyRows]);

  useEffect(() => {
    if (!isAuthenticated || !isLetsTradeSection || !hasFix1PartialMark) return;
    if (!letsTradeDeliveryDate || letsTradeDeliveryDate < getWarsawDateOffset(0)) return;
    const cacheKey = `${letsTradeDeliveryDate}:${fix1PartialStatus?.date}`;
    if (fix1OrderPlanRefreshedRef.current === cacheKey) return;
    fix1OrderPlanRefreshedRef.current = cacheKey;
    loadLetsTradeOrderPlan({ targetDate: letsTradeDeliveryDate, force: true }).catch((err) => {
      console.error('Fix I order plan refresh failed', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFix1PartialMark, isAuthenticated, isLetsTradeSection, letsTradeDeliveryDate]);

  const getPredictTablePlaceholder = (stageKey) => {
    const stageStatus = predictStageStatuses?.[stageKey]?.status || 'pending';
    if (stageStatus === 'failed' || stageStatus === 'sync error') {
      return 'error';
    }
    if (stageStatus === 'not observed' || stageStatus === 'missing') {
      return '-';
    }
    return 'pending';
  };
  useEffect(() => {
    if (!runPanelMode.settle || runPanelMode.settle === 'history') {
      return;
    }
    Object.values(settleModeRuns).forEach((run) => {
      if (!run?.run_id) return;
      const detailKey = `settle:${run.run_id}`;
      if (!runDetailsByKeyRef.current[detailKey]?.data?.summary) {
        loadRunDetails('settle', run.run_id).catch(() => {
          // Leave mode unavailable until metadata is available in status payload.
        });
      }
    });
  }, [runPanelMode.settle, settleModeRuns, loadRunDetails]);

  useEffect(() => {
    if (runPanelMode.settle === 'history') {
      return;
    }
    const currentMode = settleModeMap[settleResultMode];
    const defaultMode = settleModeMap[defaultSettleResultMode];
    const currentHasSignal = Boolean(currentMode?.available || currentMode?.attempted);
    const defaultHasSignal = Boolean(defaultMode?.available || defaultMode?.attempted);
    if (
      !settleDisplayModes.some((mode) => mode.key === settleResultMode)
      || (!currentHasSignal && defaultHasSignal && settleResultMode !== defaultSettleResultMode)
    ) {
      setSettleResultMode(defaultSettleResultMode);
    }
  }, [settleDisplayModes, settleResultMode, defaultSettleResultMode, runPanelMode.settle, settleModeMap]);

  useEffect(() => {
    if (!predictDisplayModes.some((mode) => mode.key === predictResultMode)) {
      setPredictResultMode(defaultPredictResultMode);
    }
  }, [defaultPredictResultMode, predictDisplayModes, predictResultMode]);

  const marketReadiness = activePredictMeta?.market_readiness || {};
  const hasPendingFixReadiness = ['fix_i', 'fix_ii'].some((key) => marketReadiness?.[key]?.state === 'pending');

  const getActualResultLabel = (status) => {
    switch (status) {
      case 'partial_fix2_settle':
        return 'Fix II';
      case 'settled':
        return 'IDA2';
      case 'settled_ida1_fallback':
        return 'IDA1';
      case 'settled_ida1_fallback_no_settlement':
        return 'IDA1';
      case 'settled_ida2_no_ida1':
        return 'IDA2';
      case 'settled_fix2_fallback':
        return 'Fix II';
      default:
        return 'Current result';
    }
  };

  const getDailyBaseHedgeInfo = (summaryRow = {}) => {
    const baseSimulation = summaryRow?.base_simulation || {};
    const baseCarrySummary = getBaseCarryGuardSummary(summaryRow, summaryRow?.hourly || []);
    if (!summaryRow?.base_simulation && !baseCarrySummary.hasIntervention) {
      return {
        available: false,
        title: baseGuardCopy.title,
        lines: [howItWorksLanguage === 'pl' ? 'Brak danych hedge dla tej daty.' : 'No hedge data for this date.'],
        rows: [],
        extraRows: 0,
      };
    }

    const hourlyPlan = Array.isArray(baseSimulation.hourly_plan) ? baseSimulation.hourly_plan : [];
    const isGuardHour = (hour) => Boolean(
      hour?.negative_price_guard?.active
      || hour?.negative_price_guard_active
      || hour?.sell_spike_guard?.active
      || hour?.sell_spike_guard_active
      || hour?.sell_spike_guard?.suppressed
      || hour?.sell_spike_guard_suppressed
      || String(hour?.negative_price_guard_reason || '').trim()
      || String(hour?.sell_spike_guard_reason || '').trim(),
    );
    const hedgeOrderRows = hourlyPlan.filter((hour) => (
      Number(hour?.hedge_order_volume_mw ?? hour?.hedge_volume_mw ?? 0) > 0.001
    ));
    const hedgeRows = hourlyPlan.filter((hour) => Number(hour?.hedge_volume_mw || 0) > 0.001);
    const submittedOnlyRows = hedgeOrderRows.filter((hour) => Number(hour?.hedge_volume_mw || 0) <= 0.001);
    const guardRows = hourlyPlan.filter(isGuardHour);
    const baseAction = String(
      summaryRow?.base_context?.base_action
      || summaryRow?.base_action
      || baseSimulation.base_setup?.base_action
      || baseSimulation.model_action
      || 'Idle',
    );
    const baseVolume = Number(
      summaryRow?.base_context?.volume
      ?? summaryRow?.base_volume
      ?? baseSimulation.base_block_volume_mw
      ?? baseSimulation.base_setup?.volume
      ?? 0,
    );
    const hedgeVolume = Number(baseSimulation.hedge_volume_mwh || 0);
    const hedgeOrderVolume = hedgeOrderRows.reduce(
      (sum, hour) => sum + Number(hour?.hedge_order_volume_mw ?? hour?.hedge_volume_mw ?? 0),
      0,
    );
    const hedgePnl = baseSimulation.hedge_pnl_fix_i ?? baseSimulation.hedge_expected_pnl;
    const profileBeforeHedge = baseSimulation.guarded_profile_base_pnl_fix2_close ?? baseSimulation.target_profile_expected_pnl;
    const executablePnl = baseSimulation.executable_base_pnl_fix2_close ?? baseSimulation.expected_executable_pnl;
    const guardHours = Number(baseSimulation.guard_summary?.active_hours ?? guardRows.length);
    const normalizedBaseAction = baseAction.trim().toLowerCase();
    const hasActiveBasePosition = hasActualBasePosition({ action: normalizedBaseAction, volume: baseVolume });
    const hasHedge = hasActiveBasePosition && hasExecutedBaseHedge(baseSimulation);
    const buyGuardSummary = getNegativeFixIBuyGuardSummary(baseSimulation);
    const guardMeta = getBaseHedgeGuardMeta(baseSimulation);
    const hedgeGuardMeta = hedgeOrderRows.length > 0
      ? getBaseHedgeGuardMeta({
          ...baseSimulation,
          hourly_plan: hourlyPlan.filter((hour) => Number(hour?.hedge_order_volume_mw ?? hour?.hedge_volume_mw ?? 0) > 0.001),
        })
      : guardMeta;
    const hedgeAction = baseAction.toLowerCase() === 'buy'
      ? 'SELL'
      : baseAction.toLowerCase() === 'sell'
        ? 'BUY'
        : 'HEDGE';
    const spikeBreakdown = getPostBaseSellSpikeBreakdown(baseSimulation, simCopy.letsTrade);
    const hasGuardActivity = hasActiveBasePosition && (
      guardHours > 0
      || guardRows.length > 0
      || hedgeOrderRows.length > 0
      || spikeBreakdown.hasSpike
      || buyGuardSummary.hasIntervention
      || baseCarrySummary.hasIntervention
    );
    const spikeBreakdownLines = spikeBreakdown.hasSpike ? [
      simCopy.letsTrade.postBaseGuardBreakdownSummary({
        detected: spikeBreakdown.detected,
        hedged: spikeBreakdown.hedged,
        skipped: spikeBreakdown.skipped,
      }),
      spikeBreakdown.activeRows.length
        ? `${simCopy.letsTrade.postBaseGuardHedgedRows}: ${spikeBreakdown.activeRows.map((row) => row.label).join(', ')}`
        : null,
      spikeBreakdown.suppressedRows.length
        ? `${simCopy.letsTrade.postBaseGuardSkippedRows}: ${spikeBreakdown.suppressedRows.map((row) => (
            row.edgeText ? `${row.label} (${row.edgeText})` : row.label
          )).join(', ')}`
        : null,
    ].filter(Boolean) : [];
    const activeGuardChips = [];
    const addGuardChip = (label, className, title = '') => {
      const cleanLabel = String(label || '').trim();
      if (!cleanLabel || activeGuardChips.some((chip) => chip.label === cleanLabel)) return;
      activeGuardChips.push({ label: cleanLabel, className, title });
    };
    if (hedgeOrderVolume > 0.001 && hedgeGuardMeta?.mode !== 'spike_suppressed') {
      addGuardChip(hedgeGuardMeta.label, hedgeGuardMeta.badgeClass, `${hedgeGuardMeta.label} · hedge`);
    }
    const spikeHedgeHours = Number(spikeBreakdown.hedged || spikeBreakdown.activeRows?.length || 0);
    if (spikeHedgeHours > 0) {
      addGuardChip(
        `${baseGuardCopy.spikeLabel} · ${formatInteger(spikeHedgeHours)}h`,
        darkMode ? 'border-amber-300/55 bg-amber-500/15 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900',
        `${baseGuardCopy.spikeLabel}: ${formatInteger(spikeHedgeHours)}h`,
      );
    }
    if (buyGuardSummary.hasIntervention) {
      addGuardChip(
        formatFixIBuyGuardLabel(buyGuardSummary, { includeCounts: true }),
        darkMode ? 'border-cyan-300/55 bg-cyan-500/15 text-cyan-100' : 'border-cyan-200 bg-cyan-50 text-cyan-900',
        `${baseHedgeCopy.fixIBuyGuardLabel}: ${baseHedgeCopy.guardInterventionHours(buyGuardSummary.blockedHours, buyGuardSummary.cappedHours)}`,
      );
    }
    if (baseCarrySummary.hasIntervention) {
      const baseCarryVolume = Math.max(
        Number(baseCarrySummary.forcedFix2 || 0),
        Number(baseCarrySummary.blockedIda1 || 0),
        Number(baseCarrySummary.blockedIda2 || 0),
      );
      addGuardChip(
        `${baseHedgeCopy.baseCarryGuardLabel}: ${formatNumber(baseCarryVolume)} MWh`,
        darkMode ? 'border-indigo-300/55 bg-indigo-500/15 text-indigo-100' : 'border-indigo-200 bg-indigo-50 text-indigo-900',
        `${baseHedgeCopy.baseCarryGuardLabel}: ${baseHedgeCopy.forcedFix2Label} ${formatNumber(baseCarrySummary.forcedFix2 || 0)} MWh · ${baseHedgeCopy.blockedIda1Label} ${formatNumber(baseCarrySummary.blockedIda1 || 0)} MWh · ${baseHedgeCopy.blockedIda2Label} ${formatNumber(baseCarrySummary.blockedIda2 || 0)} MWh`,
      );
    }

    return {
      available: true,
      hasHedge,
      hasGuardDetails: hasHedge || hasGuardActivity || spikeBreakdown.hasSpike,
      guardMeta,
      hedgeGuardMeta,
      buyGuardSummary,
      baseCarrySummary,
      spikeBreakdown,
      hedgeOrderVolume,
      activeGuardChips,
      title: activeGuardChips.length > 1
        ? (howItWorksLanguage === 'pl' ? 'Guardy dnia' : 'Day guards')
        : baseGuardCopy.title,
      lines: [
        `${baseGuardCopy.base}: ${baseAction.toUpperCase()} ${formatNumber(baseVolume)} MW`,
        `${guardMeta.label}: ${guardMeta.info}`,
        guardMeta.policyInfo ? `${baseGuardCopy.policy}: ${guardMeta.policyInfo}` : null,
        `${baseGuardCopy.guardedHours}: ${formatInteger(guardHours)}`,
        `${baseGuardCopy.hedgeOrders}: ${hedgeAction} ${formatNumber(hedgeOrderVolume)} MWh / ${formatInteger(hedgeOrderRows.length)} h`,
        `${baseGuardCopy.hedgeExecuted}: ${hedgeAction} ${formatNumber(hedgeVolume)} MWh / ${formatInteger(hedgeRows.length)} h`,
        submittedOnlyRows.length > 0
          ? `${baseGuardCopy.hedgeSubmittedOnly}: ${submittedOnlyRows.map((hour) => formatHourLabel(hour.hour)).join(', ')}`
          : null,
        buyGuardSummary.hasIntervention
          ? `${baseHedgeCopy.fixIBuyGuardLabel}: ${baseHedgeCopy.guardInterventionHours(buyGuardSummary.blockedHours, buyGuardSummary.cappedHours)}${buyGuardSummary.affectedVolume > 0.001 ? ` · ${formatNumber(buyGuardSummary.affectedVolume)} MW` : ''}`
          : null,
        baseCarrySummary.hasIntervention
          ? `${baseHedgeCopy.baseCarryGuardLabel}: ${baseHedgeCopy.forcedFix2Label} ${formatNumber(baseCarrySummary.forcedFix2)} MWh · ${baseHedgeCopy.blockedIda1Label} ${formatNumber(baseCarrySummary.blockedIda1)} MWh`
          : null,
        ...spikeBreakdownLines,
        `${baseGuardCopy.hedgePnl}: ${hedgePnl === null || hedgePnl === undefined ? '-' : formatSignedNumber(hedgePnl, ' PLN')}`,
        `${baseGuardCopy.profileBeforeHedge}: ${profileBeforeHedge === null || profileBeforeHedge === undefined ? '-' : formatSignedNumber(profileBeforeHedge, ' PLN')}`,
        `${baseGuardCopy.executableBasePnl}: ${executablePnl === null || executablePnl === undefined ? '-' : formatSignedNumber(executablePnl, ' PLN')}`,
      ].filter(Boolean),
      rows: [
        ...buyGuardSummary.rows.map((row) => (
          `${formatHourLabel(row.hourNumber)} ${row.action === 'block' ? baseGuardCopy.buyBlockedShort : baseGuardCopy.buyCappedShort} ${row.affectedVolume > 0.001 ? `${formatNumber(row.affectedVolume)} MW ` : ''}${row.action === 'block' ? baseGuardCopy.blockedBadge : baseGuardCopy.cappedBadge}`
        )),
        ...hedgeOrderRows.map((hour) => {
          const executedVolume = Number(hour?.hedge_volume_mw || 0);
          const submittedVolume = Number(hour?.hedge_order_volume_mw ?? hour?.hedge_volume_mw ?? 0);
          const status = executedVolume > 0.001 ? baseGuardCopy.executedBadge : baseGuardCopy.submittedBadge;
          return `${formatHourLabel(hour.hour)} ${String(hour.hedge_action || hedgeAction).toUpperCase()} ${formatNumber(executedVolume > 0.001 ? executedVolume : submittedVolume)} MW ${status}`;
        }),
      ].slice(0, 10),
      extraRows: Math.max(0, buyGuardSummary.rows.length + hedgeOrderRows.length - 10),
    };
  };

  const openDailyBaseHedgeTooltip = (event, dailyBaseHedgeInfo, { align = 'left' } = {}) => {
    if (!dailyBaseHedgeInfo?.hasGuardDetails || typeof window === 'undefined') return;
    if (dailyBaseHedgeTooltipCloseTimerRef.current) {
      clearTimeout(dailyBaseHedgeTooltipCloseTimerRef.current);
      dailyBaseHedgeTooltipCloseTimerRef.current = null;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const width = Math.min(460, Math.max(300, window.innerWidth - 32));
    const preferredLeft = align === 'right' ? rect.right - width : rect.left;
    const left = Math.min(Math.max(12, preferredLeft), Math.max(12, window.innerWidth - width - 12));
    const viewportPadding = 12;
    const spaceAbove = Math.max(180, rect.top - viewportPadding - 10);
    const spaceBelow = Math.max(180, window.innerHeight - rect.bottom - viewportPadding - 10);
    const showBelow = spaceBelow >= 360 || spaceBelow >= spaceAbove;
    const maxHeight = Math.min(560, showBelow ? spaceBelow : spaceAbove);
    const top = showBelow ? Math.min(window.innerHeight - viewportPadding, rect.bottom + 10) : Math.max(viewportPadding, rect.top - 10);
    setDailyBaseHedgeTooltip({
      info: dailyBaseHedgeInfo,
      guardMeta: dailyBaseHedgeInfo.guardMeta || getBaseHedgeGuardMeta({}),
      left,
      top,
      width,
      maxHeight,
      placement: showBelow ? 'below' : 'above',
    });
  };

  const cancelDailyBaseHedgeTooltipClose = () => {
    if (dailyBaseHedgeTooltipCloseTimerRef.current) {
      clearTimeout(dailyBaseHedgeTooltipCloseTimerRef.current);
      dailyBaseHedgeTooltipCloseTimerRef.current = null;
    }
  };

  const closeDailyBaseHedgeTooltip = ({ immediate = false } = {}) => {
    cancelDailyBaseHedgeTooltipClose();
    if (immediate) {
      setDailyBaseHedgeTooltip(null);
      return;
    }
    dailyBaseHedgeTooltipCloseTimerRef.current = setTimeout(() => {
      setDailyBaseHedgeTooltip(null);
      dailyBaseHedgeTooltipCloseTimerRef.current = null;
    }, 180);
  };

  const renderDailyBaseHedgeFloatingTooltip = () => {
    if (!dailyBaseHedgeTooltip?.info?.hasGuardDetails) {
      return null;
    }
    const { info, guardMeta, left, top, width, maxHeight, placement } = dailyBaseHedgeTooltip;
    const shellClass = darkMode
      ? 'border-slate-500 bg-slate-950 text-slate-50'
      : 'border-slate-300 bg-white text-slate-950';
    const accentClass = guardMeta.mode === 'soft'
      ? darkMode ? 'border-cyan-300 bg-cyan-300 text-slate-950' : 'border-cyan-600 bg-cyan-600 text-white'
      : guardMeta.mode === 'fix_i_buy_guard'
        ? darkMode ? 'border-cyan-300 bg-cyan-300 text-slate-950' : 'border-cyan-600 bg-cyan-600 text-white'
      : guardMeta.mode === 'spike'
        ? darkMode ? 'border-amber-300 bg-amber-300 text-slate-950' : 'border-amber-600 bg-amber-600 text-white'
        : guardMeta.mode === 'spike_suppressed'
          ? darkMode ? 'border-sky-300 bg-sky-300 text-slate-950' : 'border-sky-600 bg-sky-600 text-white'
          : darkMode ? 'border-rose-300 bg-rose-300 text-slate-950' : 'border-rose-600 bg-rose-600 text-white';
    const guardChips = Array.isArray(info.activeGuardChips) && info.activeGuardChips.length
      ? info.activeGuardChips
      : [{ label: guardMeta.label, className: accentClass, title: guardMeta.label }];
    return (
      <div
        className={`pointer-events-auto fixed z-[9999] overscroll-contain rounded-xl border p-4 text-xs normal-case leading-relaxed shadow-2xl ${placement === 'below' ? '' : '-translate-y-full'} ${shellClass}`}
        style={{ left, top, width, maxHeight, overflowY: 'auto' }}
        onMouseEnter={cancelDailyBaseHedgeTooltipClose}
        onMouseLeave={() => closeDailyBaseHedgeTooltip({ immediate: true })}
      >
        <div className="flex flex-wrap items-center gap-2">
          <div className="font-extrabold">{info.title}</div>
          {guardChips.map((chip) => (
            <span
              key={`daily-guard-tooltip-chip-${chip.label}`}
              className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-extrabold ${chip.className || accentClass}`}
              title={chip.title || chip.label}
            >
              {chip.label}
            </span>
          ))}
        </div>
        <div className={`mt-3 space-y-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
          {info.lines.map((line) => (
            <div key={line} className={guardChips.some((chip) => line.includes(String(chip.label || '').split(' · ')[0])) ? 'font-semibold' : ''}>
              {line}
            </div>
          ))}
        </div>
        {info.rows.length > 0 ? (
          <div className={`mt-3 border-t pt-3 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="mb-2 font-extrabold">{info.buyGuardSummary?.hasIntervention ? baseGuardCopy.interventionHours : baseGuardCopy.hedgeHours}</div>
            <div className="flex flex-wrap gap-1.5">
              {info.rows.map((line) => (
                <span key={line} className={`rounded-md border px-2 py-1 font-extrabold ${darkMode ? 'border-slate-600 bg-slate-900 text-slate-50' : 'border-slate-200 bg-slate-100 text-slate-900'}`}>
                  {line}
                </span>
              ))}
              {info.extraRows > 0 ? (
                <span className={`rounded-md border px-2 py-1 font-semibold ${darkMode ? 'border-slate-600 bg-slate-900 text-slate-200' : 'border-slate-200 bg-slate-100 text-slate-700'}`}>
                  +{info.extraRows}
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderBaseHedgeGuardTooltip = (
    dailyBaseHedgeInfo,
    {
      align = 'left',
      label = null,
      labelPrefix = '',
      guardMetaOverride = null,
      ariaLabel = 'Goose risk management details',
      className = '',
    } = {},
  ) => {
    if (!dailyBaseHedgeInfo?.hasGuardDetails) {
      return null;
    }
    const guardMeta = guardMetaOverride || dailyBaseHedgeInfo.guardMeta || getBaseHedgeGuardMeta({});
    const displayLabel = label || `${labelPrefix}${guardMeta.shortLabel}`;
    return (
      <div className="relative inline-flex">
        <button
          type="button"
          onMouseEnter={(event) => openDailyBaseHedgeTooltip(event, dailyBaseHedgeInfo, { align })}
          onMouseLeave={closeDailyBaseHedgeTooltip}
          onFocus={(event) => openDailyBaseHedgeTooltip(event, dailyBaseHedgeInfo, { align })}
          onBlur={() => closeDailyBaseHedgeTooltip({ immediate: true })}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold outline-none transition ${guardMeta.badgeClass} ${className}`}
          aria-label={ariaLabel}
        >
          <Info className="h-3 w-3" />
          {displayLabel}
        </button>
      </div>
    );
  };

  const renderSetupBaseActivityBadges = (
    activityRow,
    {
      stacked = false,
      compact = false,
    } = {},
  ) => {
    if (!activityRow) return null;
    const riskShiftHours = Number(activityRow.riskShiftHours || 0);
    const fixIOrderHours = Number(activityRow.fixIOrderHours || 0);
    const fixIOrderVolume = Number(activityRow.fixIOrderVolume || 0);
    const selectedCarryVolume = Number(activityRow.selectedCarryVolume || 0);
    const carryForced = Number(activityRow.carryForced || 0);
    const carryBlockedIda1 = Number(activityRow.carryBlockedIda1 || 0);
    const carryBlockedIda2 = Number(activityRow.carryBlockedIda2 || 0);

    const badgeBaseClass = compact
      ? 'inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-full border px-2 text-[10px] font-extrabold leading-none'
      : 'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none';
    const wrapperClass = stacked
      ? 'flex min-w-0 max-w-full flex-col items-start gap-1'
      : 'flex min-w-0 max-w-full flex-wrap items-center gap-1';

    const badges = [];
    if (riskShiftHours > 0) {
      badges.push(
        <span
          key="risk-shift"
          className={`${badgeBaseClass} ${
            darkMode ? 'border-amber-300/50 bg-amber-500/15 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900'
          }`}
        >
          {`Risk shift · ${riskShiftHours}h`}
        </span>,
      );
    }
    if (fixIOrderHours > 0 || fixIOrderVolume > 0.001) {
      badges.push(
        <span
          key="goose-fix-i"
          className={`${badgeBaseClass} ${
            darkMode ? 'border-cyan-300/45 bg-cyan-500/15 text-cyan-100' : 'border-cyan-200 bg-cyan-50 text-cyan-900'
          }`}
        >
          {`Goose Fix I · ${fixIOrderHours}h · ${formatNumber(fixIOrderVolume)} MWh`}
        </span>,
      );
    }
    if (selectedCarryVolume > 0.001 || carryForced > 0.001 || carryBlockedIda1 > 0.001 || carryBlockedIda2 > 0.001) {
      badges.push(
        <span
          key="ida2-carry"
          className={`${badgeBaseClass} ${
            darkMode ? 'border-indigo-300/45 bg-indigo-500/15 text-indigo-100' : 'border-indigo-200 bg-indigo-50 text-indigo-900'
          }`}
        >
          {`IDA2 carry · ${formatNumber(selectedCarryVolume)} MWh`}
        </span>,
      );
    }

    if (!badges.length) return null;
    return <div className={wrapperClass}>{badges}</div>;
  };

  const getActualDailyResultValue = useCallback((row) => getDisplayedDailyResultValue(row), []);

  const getActualDailyAfterFeesValue = useCallback((row) => getLiveProvisionalDailyAfterFeesValue(row), []);

  const isPartialFix2Settle = useCallback((row) => isPartialFix2SettleRow(row), []);
  const isIda2CloseUnavailable = useCallback((row) => {
    if (!row) return true;
    if (row?.available_stages?.ida2?.ready !== undefined) {
      return !row.available_stages.ida2.ready;
    }
    return isPartialFix2SettleRow(row)
      || isIda1PendingPartialSettleRow(row)
      || isIda1FinalFallbackSettleRow(row)
      || row.daily_pnl_ida2_close === null
      || row.daily_pnl_ida2_close === undefined;
  }, []);

  const legacyGooseComparableAccByDate = useMemo(() => {
    let acc = 0.0;
    const values = {};
    [...dailyRows]
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))
      .forEach((row) => {
        if (isRealizedSimulationRow(row)) {
          acc += Number(getLegacyGooseComparablePnl(row) || 0);
        }
        values[String(row.date)] = Number(acc.toFixed(2));
      });
    return values;
  }, [dailyRows]);
  const bisonV5Ida1AccByDate = useMemo(() => {
    let acc = 0.0;
    let hasReference = false;
    const values = {};
    [...dailyRows]
      .sort((a, b) => String(a.date).localeCompare(String(b.date)))
      .forEach((row) => {
        const parityRow = parityRowsByDate.get(String(row.date).slice(0, 10));
        const value = getGooseV5Ida1ReferencePnl(parityRow);
        if (value !== null) {
          hasReference = true;
          acc += Number(value || 0);
        }
        values[String(row.date)] = hasReference ? Number(acc.toFixed(2)) : null;
      });
    return values;
  }, [dailyRows, parityRowsByDate]);

  const liveProvisionalSummary = useMemo(() => {
    const realizedRows = dailyRows.filter((row) => isRealizedSimulationRow(row));

    const totalPnl = realizedRows.reduce((acc, row) => acc + Number(getLiveProvisionalDailyResultValue(row) || 0), 0);
    const totalAfterFees = realizedRows.reduce((acc, row) => acc + Number(getLiveProvisionalDailyAfterFeesValue(row) || 0), 0);
    const totalVolume = realizedRows.reduce((acc, row) => acc + getPathVolume(row), 0);
    const venueBaseMwh = realizedRows.reduce((acc, row) => acc + Number(row?.venue_base_mwh || 0), 0);
    const venueFixIMwh = realizedRows.reduce((acc, row) => acc + Number(row?.venue_fix_i_mwh || 0), 0);
    const venueFixIIMwh = realizedRows.reduce((acc, row) => acc + Number(row?.venue_fix_ii_mwh || 0), 0);
    const venueIda1Mwh = realizedRows.reduce((acc, row) => acc + Number(row?.venue_ida1_mwh || 0), 0);
    const venueIda2Mwh = realizedRows.reduce((acc, row) => acc + Number(row?.venue_ida2_mwh || 0), 0);
    const partialDays = realizedRows.filter((row) => {
      const status = String(row?.status || '');
      return status === 'partial_fix2_settle'
        || status === 'settled_fix2_fallback'
        || status === 'settled_ida1_fallback'
        || status === 'settled_ida1_fallback_no_settlement';
    }).length;

    return {
      total_pnl: Number(totalPnl.toFixed(2)),
      total_after_fees: Number(totalAfterFees.toFixed(2)),
      total_fees: Number((totalPnl - totalAfterFees).toFixed(2)),
      total_volume: Number(totalVolume.toFixed(2)),
      venue_base_mwh: Number(venueBaseMwh.toFixed(2)),
      venue_fix_i_mwh: Number(venueFixIMwh.toFixed(2)),
      venue_fix_ii_mwh: Number(venueFixIIMwh.toFixed(2)),
      venue_ida1_mwh: Number(venueIda1Mwh.toFixed(2)),
      venue_ida2_mwh: Number(venueIda2Mwh.toFixed(2)),
      avg_profit_per_mwh: totalVolume > 0 ? Number((totalPnl / totalVolume).toFixed(2)) : 0,
      realizedDays: realizedRows.length,
      partialDays,
    };
  }, [dailyRows, getPathVolume]);

  const settledBaseSimulationRows = useMemo(() => {
    return dailyRows
      .map((row) => ({
        date: String(row?.date || '').slice(0, 10),
        status: row?.status,
        rowKind: 'settled',
        baseSimulation: row?.base_simulation || null,
        monitorStatus: row?.monitor_status || null,
        summaryRow: row,
      }))
      .filter((row) => row.date && row.baseSimulation)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [dailyRows]);

  const baseEntryRiskDecision = baseEntryRiskData?.date === basePretradeTargetDate ? baseEntryRiskData : null;
  const basePretradeDecision = basePretradeData?.date === basePretradeTargetDate ? basePretradeData : null;

  const liveBaseSimulationRow = useMemo(() => {
    const liveDate = String(basePretradeTargetDate || '').slice(0, 10);
    if (!isBaseSimulationSection || !/^\d{4}-\d{2}-\d{2}$/.test(liveDate)) {
      return null;
    }
    if (liveDate < startDate || liveDate > endDate) {
      return null;
    }
    if (settledBaseSimulationRows.some((row) => row.date === liveDate)) {
      return null;
    }
    if (!basePretradeDecision && !baseEntryRiskDecision) {
      return null;
    }

    const pretrade = basePretradeDecision || {};
    const setup = pretrade.base_setup || {};
    const fallbackBasePrice = pretrade.base_price ?? baseEntryRiskDecision?.avg_q50_forecast ?? null;
    const fallbackSource = pretrade.source || setup.source || 'live_pre_base';
    const candidateVolume = Number(
      pretrade.candidate_base_block_volume_mw
      ?? pretrade.base_block_volume_mw
      ?? setup.volume
      ?? 0,
    );
    const baseSimulation = {
      ...pretrade,
      date: liveDate,
      source: fallbackSource,
      runtime_source: pretrade.runtime_source || setup.runtime_source || fallbackSource,
      base_setup: {
        ...setup,
        source: setup.source || fallbackSource,
        runtime_source: setup.runtime_source || pretrade.runtime_source || fallbackSource,
        base_action: setup.base_action || (pretrade.model_action === 'buy' ? 'Buy' : pretrade.model_action === 'sell' ? 'Sell' : 'Idle'),
        volume: setup.volume ?? candidateVolume,
        runtime_base_price: setup.runtime_base_price ?? fallbackBasePrice,
        base_price: setup.base_price ?? fallbackBasePrice,
      },
      model_action: pretrade.model_action || 'none',
      recommended_action: pretrade.recommended_action || 'none',
      base_block_volume_mw: Number(pretrade.base_block_volume_mw || 0),
      base_price: fallbackBasePrice,
      target_profile_volume_mwh: Number(pretrade.target_profile_volume_mwh || 0),
      hedge_volume_mwh: Number(pretrade.hedge_volume_mwh || 0),
      hedge_pnl_fix_i: Number(pretrade.hedge_expected_pnl || 0),
      executable_base_pnl_fix2_close: Number(pretrade.expected_executable_pnl || 0),
      guarded_profile_base_pnl_fix2_close: Number(pretrade.target_profile_expected_pnl || 0),
      hourly_plan: Array.isArray(pretrade.hourly_plan) ? pretrade.hourly_plan : [],
      guard_summary: pretrade.guard_summary || {},
    };

    return {
      date: liveDate,
      status: 'live_pre_settle',
      rowKind: 'live',
      baseSimulation,
      monitorStatus: null,
      summaryRow: null,
    };
  }, [
    baseEntryRiskDecision,
    basePretradeDecision,
    basePretradeTargetDate,
    endDate,
    isBaseSimulationSection,
    settledBaseSimulationRows,
    startDate,
  ]);

  const sortedDailyRows = useMemo(() => {
    const getSortValue = (row) => {
      switch (dailyResultsSortKey) {
        case 'result':
          return getActualDailyResultValue(row);
        case 'basePnl':
          return Number(row?.base_pnl);
        case 'fixPnl':
          return Number(row?.probabilistic_pnl);
        case 'ida1Pnl':
          return isPartialFix2Settle(row) ? null : Number(row?.ida1_pnl);
        case 'ida2Pnl':
          return isIda2CloseUnavailable(row) ? null : Number(row?.ida2_pnl);
        case 'volume':
          return Number(getPathVolume(row));
        case 'date':
        default:
          return String(row?.date || '');
      }
    };

    return [...dailyRows].sort((left, right) => {
      const leftValue = getSortValue(left);
      const rightValue = getSortValue(right);

      if (dailyResultsSortKey === 'date') {
        const comparison = String(leftValue).localeCompare(String(rightValue));
        return dailyResultsSortDirection === 'asc' ? comparison : -comparison;
      }

      const leftNumber = Number(leftValue);
      const rightNumber = Number(rightValue);
      const leftValid = Number.isFinite(leftNumber);
      const rightValid = Number.isFinite(rightNumber);

      if (!leftValid && !rightValid) {
        return String(right?.date || '').localeCompare(String(left?.date || ''));
      }
      if (!leftValid) return 1;
      if (!rightValid) return -1;

      const comparison = leftNumber - rightNumber;
      if (comparison === 0) {
        return String(right?.date || '').localeCompare(String(left?.date || ''));
      }
      return dailyResultsSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [
    dailyResultsSortDirection,
    dailyResultsSortKey,
    dailyRows,
    getActualDailyResultValue,
    getPathVolume,
    isPartialFix2Settle,
    isIda2CloseUnavailable,
  ]);

  const baseSimulationRows = useMemo(() => {
    const rows = liveBaseSimulationRow
      ? [liveBaseSimulationRow, ...settledBaseSimulationRows]
      : settledBaseSimulationRows;
    return [...rows].sort((a, b) => b.date.localeCompare(a.date));
  }, [liveBaseSimulationRow, settledBaseSimulationRows]);

  const baseEntryRiskRowsForDisplayByDate = useMemo(() => {
    const rowsByDate = { ...baseEntryRiskRowsByDate };
    if (baseEntryRiskDecision?.date) {
      rowsByDate[baseEntryRiskDecision.date] = baseEntryRiskDecision;
    }
    return rowsByDate;
  }, [baseEntryRiskDecision, baseEntryRiskRowsByDate]);

  const getReplayBaseRiskSide = useCallback((baseSimulation) => {
    const recommendedAction = String(baseSimulation?.recommended_action || '').trim().toLowerCase();
    const recommendedVolume = Number(baseSimulation?.base_block_volume_mw || 0);
    if ((recommendedAction === 'buy' || recommendedAction === 'sell') && recommendedVolume > 0.0001) {
      return recommendedAction;
    }
    const setupAction = String(
      baseSimulation?.base_setup?.base_action
      || baseSimulation?.model_action
      || '',
    ).trim().toLowerCase();
    if (setupAction === 'buy' || setupAction === 'sell') {
      return setupAction;
    }
    // model_guard can override recommended_action="none" and still execute a buy/sell;
    // fall back to execution_action so the base risk level is shown correctly
    if (baseSimulation?.execution_applied) {
      const executionAction = String(baseSimulation?.execution_action || '').trim().toLowerCase();
      if (executionAction === 'buy' || executionAction === 'sell') {
        return executionAction;
      }
    }
    return null;
  }, []);

  const getReplayBaseRiskLevel = useCallback((risk, baseSimulation) => {
    if (!risk?.ready) {
      return 'not_ready';
    }
    const side = getReplayBaseRiskSide(baseSimulation);
    if (!side) {
      return 'no_base';
    }
    const sideRisk = side === 'buy' || side === 'sell'
      ? String(risk?.[side]?.risk_level || '').trim().toLowerCase()
      : '';
    return sideRisk || String(risk?.risk_level || 'low').trim().toLowerCase();
  }, [getReplayBaseRiskSide]);

  const basePreBaseReplaySummary = useMemo(() => {
    const rows = baseSimulationRows
      .map((row) => ({
        risk: baseEntryRiskRowsForDisplayByDate[row.date],
        baseSimulation: row.baseSimulation,
      }))
      .filter((row) => row.risk?.ready);
    const displayLevels = rows.map((row) => getReplayBaseRiskLevel(row.risk, row.baseSimulation));
    return {
      comparableDays: rows.length,
      highOrBlockedDays: displayLevels.filter((level) => ['high', 'blocked'].includes(level)).length,
      mediumDays: displayLevels.filter((level) => level === 'medium').length,
      lowDays: displayLevels.filter((level) => level === 'low').length,
      buyRiskHours: rows.reduce((acc, row) => acc + Number(row.risk?.buy?.risk_hours || 0), 0),
      sellRiskHours: rows.reduce((acc, row) => acc + Number(row.risk?.sell?.risk_hours || 0), 0),
    };
  }, [baseEntryRiskRowsForDisplayByDate, baseSimulationRows, getReplayBaseRiskLevel]);

  const guardCalibrationSummary = useMemo(() => {
    const modeLabels = {
      goose_fix_i: howItWorksLanguage === 'pl' ? 'Goose Fix I execution' : 'Goose Fix I execution',
      goose_risk_shift: howItWorksLanguage === 'pl' ? 'Goose risk shift' : 'Goose risk shift',
      spike: baseGuardCopy.spikeLabel || 'Spike Guard',
      negative_hard: baseGuardCopy.hardLabel || 'Goose risk · hard',
      negative_soft: baseGuardCopy.softLabel || 'Goose risk · soft',
    };
    const variantDefs = [
      { key: 'current', label: baseHedgeCopy.variantCurrent },
      { key: 'plus10', label: baseHedgeCopy.variantPlus(10), delta: 10 },
      { key: 'plus25', label: baseHedgeCopy.variantPlus(25), delta: 25 },
      { key: 'plus50', label: baseHedgeCopy.variantPlus(50), delta: 50 },
      { key: 'marketable', label: baseHedgeCopy.variantMarketable },
      { key: 'no_hedge', label: baseHedgeCopy.variantNoHedge },
    ];
    const createStats = (modeKey = 'all') => ({
      modeKey,
      label: modeKey === 'all' ? 'All guards' : modeLabels[modeKey] || modeKey,
      daysSet: new Set(),
      orderDaysSet: new Set(),
      zeroFillDaysSet: new Set(),
      signalHours: 0,
      orderHours: 0,
      orderVolume: 0,
      executedVolume: 0,
      executedPnl: 0,
      marketableUnfilledPnl: 0,
      marketableBetterHours: 0,
      marketableWorseHours: 0,
      buyGuardDaysSet: new Set(),
      buyGuardBlockedHours: 0,
      buyGuardCappedHours: 0,
      buyGuardAffectedVolume: 0,
      buyGuardPotentialVolume: 0,
      buyGuardMissingVolumeRows: 0,
      buyGuardCapValuesSet: new Set(),
      riskOnlySpikeDaysSet: new Set(),
      riskOnlySpikeHours: 0,
      baseCarryDaysSet: new Set(),
      baseForcedFix2Volume: 0,
      baseBlockedIda1Volume: 0,
      baseBlockedIda2Volume: 0,
      gooseFixIOrderHours: 0,
      gooseRiskShiftHours: 0,
      gooseRiskShiftAbsSum: 0,
    });
    const modeStats = new Map();
    const variantStats = new Map(variantDefs.map((variant) => [variant.key, {
      ...variant,
      orderVolume: 0,
      executedVolume: 0,
      pnl: 0,
    }]));
    const getStats = (modeKey) => {
      if (!modeStats.has(modeKey)) {
        modeStats.set(modeKey, createStats(modeKey));
      }
      return modeStats.get(modeKey);
    };
    const totals = createStats('all');
    const getNegativeGuardMode = (baseSimulation = {}, hour = {}) => {
      const guardSummary = baseSimulation.guard_summary || {};
      const negativeGuard = hour?.negative_price_guard || {};
      const policy = (
        negativeGuard.base_shrink_policy
        || guardSummary.base_shrink_policy
        || baseSimulation?.negative_price_base_shrink_policy
        || {}
      );
      const rawMode = String(
        negativeGuard.base_shrink_mode
        || policy.effective_mode
        || guardSummary.base_shrink_mode
        || baseSimulation?.negative_price_base_shrink_mode
        || '',
      ).trim().toLowerCase().replaceAll('-', '_');
      if (rawMode.includes('soft')) return 'negative_soft';
      if (rawMode.includes('hard')) return 'negative_hard';
      const targetVolume = toFiniteNumberValue(hour?.target_volume_mw, null);
      return Number.isFinite(targetVolume) && targetVolume >= 34 && targetVolume <= 46
        ? 'negative_soft'
        : 'negative_hard';
    };
    const getHourGuardMode = (baseSimulation = {}, hour = {}) => {
      const negativeGuard = hour?.negative_price_guard || {};
      if (negativeGuard.active || hour?.negative_price_guard_active) {
        return getNegativeGuardMode(baseSimulation, hour);
      }
      const spikeGuard = hour?.sell_spike_guard || {};
      const spikeReason = String(spikeGuard.reason || hour?.sell_spike_guard_reason || '').trim();
      const hasSuppressedSpike = Boolean(
        spikeGuard.suppressed
        || hour?.sell_spike_guard_suppressed
        || spikeGuard.suppressed_reason
        || spikeReason === 'sell_spike_hedge_not_economic',
      );
      if (hasSuppressedSpike) return 'risk_only_spike';
      if (spikeGuard.active || hour?.sell_spike_guard_active) return 'spike';
      return 'none';
    };
    const isExecutableGuardMode = (modeKey) => modeKey !== 'none' && modeKey !== 'risk_only_spike';
    const hedgePnlFor = (baseAction, basePrice, auctionPrice, volume) => {
      if (!Number.isFinite(basePrice) || !Number.isFinite(auctionPrice) || !Number.isFinite(volume)) return 0;
      if (baseAction === 'sell') return (basePrice - auctionPrice) * volume;
      if (baseAction === 'buy') return (auctionPrice - basePrice) * volume;
      return 0;
    };
    const variantExecutes = (side, auctionPrice, limitPrice, delta = 0) => {
      if (!side || !Number.isFinite(auctionPrice) || !Number.isFinite(limitPrice)) return false;
      if (side === 'buy') return auctionPrice <= limitPrice + delta + 0.0001;
      if (side === 'sell') return auctionPrice >= limitPrice - delta - 0.0001;
      return false;
    };

    baseSimulationRows.forEach(({ date: rowDate, baseSimulation, summaryRow }) => {
      if (!baseSimulation) return;
      const hourlyPlan = Array.isArray(baseSimulation.hourly_plan) ? baseSimulation.hourly_plan : [];
      const gooseRisk = baseSimulation.risk_management || {};
      const isGooseLiveRisk = String(gooseRisk.engine || baseSimulation.engine || '').trim() === 'goose_live_backtest';
      if (isGooseLiveRisk) {
        const fixIOrderHours = Number(gooseRisk.fix_i_order_hours || 0);
        const fixIOrderVolume = Number(gooseRisk.fix_i_order_volume_mwh || 0);
        const fixIExecutedVolume = Number(gooseRisk.fix_i_executed_volume_mwh ?? fixIOrderVolume);
        const riskShiftHours = Number(gooseRisk.risk_shift_hours || 0);
        const riskShiftAbsSum = Number(gooseRisk.risk_shift_abs_sum || 0);
        if (
          hasActualBasePosition({
            action: gooseRisk.base_action || baseSimulation.execution_action || baseSimulation.base_action,
            volume: gooseRisk.base_volume_mw || baseSimulation.base_block_volume_mw,
          })
          || fixIOrderHours > 0
          || riskShiftHours > 0
        ) {
          totals.daysSet.add(rowDate);
        }
        if (fixIOrderHours > 0 || fixIOrderVolume > 0.001) {
          const gooseFixIStats = getStats('goose_fix_i');
          [gooseFixIStats, totals].forEach((bucket) => {
            bucket.daysSet.add(rowDate);
            bucket.orderDaysSet.add(rowDate);
            bucket.orderHours += fixIOrderHours;
            bucket.orderVolume += fixIOrderVolume;
            bucket.executedVolume += fixIExecutedVolume;
            bucket.gooseFixIOrderHours += fixIOrderHours;
          });
        }
        if (riskShiftHours > 0) {
          const gooseShiftStats = getStats('goose_risk_shift');
          [gooseShiftStats, totals].forEach((bucket) => {
            bucket.daysSet.add(rowDate);
            bucket.signalHours += riskShiftHours;
            bucket.gooseRiskShiftHours += riskShiftHours;
            bucket.gooseRiskShiftAbsSum += riskShiftAbsSum;
          });
        }
      }
      const hasDayTradeContext = hasActualBasePosition({
        action: baseSimulation.execution_action || baseSimulation.base_action || baseSimulation.model_action || summaryRow?.base_context?.base_action || summaryRow?.base_action,
        volume: baseSimulation.base_block_volume_mw ?? summaryRow?.base_context?.volume ?? summaryRow?.base_volume,
      });
      const dayModeStats = new Map();
      const getDayModeStats = (modeKey) => {
        if (!dayModeStats.has(modeKey)) {
          dayModeStats.set(modeKey, { orderVolume: 0, executedVolume: 0 });
        }
        return dayModeStats.get(modeKey);
      };
      let totalDayOrderVolume = 0;
      let totalDayExecutedVolume = 0;
      hourlyPlan.forEach((hour) => {
        const signalModeKey = getHourGuardMode(baseSimulation, hour);
        if (isExecutableGuardMode(signalModeKey)) {
          [getStats(signalModeKey), totals].forEach((bucket) => {
            bucket.daysSet.add(rowDate);
            bucket.signalHours += 1;
          });
        } else if (signalModeKey === 'risk_only_spike') {
          totals.riskOnlySpikeDaysSet.add(rowDate);
          totals.riskOnlySpikeHours += 1;
        }
        const fixIBuyAction = Number(hour.hedge_order_volume_mw ?? hour.hedge_volume_mw ?? 0) > 0.001
          ? null
          : getNegativeFixIBuyGuardAction(hour);
        if (fixIBuyAction) {
          const guard = hour?.negative_price_guard || {};
          const originalVolume = getFirstFiniteGuardNumber(
            hour?.negative_price_guard_original_volume,
            guard.negative_price_guard_original_volume,
            guard.original_volume,
            guard.original_volume_mw,
          );
          const orderVolumeEvidence = getFirstFiniteGuardNumber(hour?.executed_volume_fix1, hour?.submitted_volume_fix1, hour?.order_volume_mw);
          const originalVolumeValue = Number.isFinite(originalVolume) ? originalVolume : 0;
          const orderVolumeEvidenceValue = Number.isFinite(orderVolumeEvidence) ? orderVolumeEvidence : 0;
          if (!hasDayTradeContext && originalVolumeValue <= 0.001 && orderVolumeEvidenceValue <= 0.001) {
            return;
          }
          const modeKey = signalModeKey === 'negative_soft' ? 'negative_soft' : 'negative_hard';
          const interventionStats = getStats(modeKey);
          const capVolume = getFirstFiniteGuardNumber(
            getNegativeFixIBuyGuardCapMw(hour),
            fixIBuyAction === 'block' ? 0 : null,
          );
          const postVolume = getFirstFiniteGuardNumber(
            hour?.negative_price_guard_post_volume,
            guard.negative_price_guard_post_volume,
            guard.post_volume,
            guard.post_volume_mw,
          );
          const explicitAffectedVolume = getFirstFiniteGuardNumber(
            hour?.negative_price_guard_affected_volume,
            guard.negative_price_guard_affected_volume,
            guard.affected_volume,
            guard.affected_volume_mw,
          );
          const volumeAfterGuard = Number.isFinite(postVolume)
            ? postVolume
            : Number.isFinite(capVolume)
              ? Math.min(originalVolumeValue, capVolume)
              : orderVolumeEvidenceValue;
          const affectedVolume = Number.isFinite(explicitAffectedVolume)
            ? explicitAffectedVolume
            : originalVolumeValue > 0.001
              ? Math.max(0, originalVolumeValue - volumeAfterGuard)
              : 0;
          const missingVolumeData = !Number.isFinite(originalVolume) || !Number.isFinite(affectedVolume);
          [interventionStats, totals].forEach((bucket) => {
            bucket.daysSet.add(rowDate);
            bucket.buyGuardDaysSet.add(rowDate);
            if (fixIBuyAction === 'block') bucket.buyGuardBlockedHours += 1;
            if (fixIBuyAction === 'cap') bucket.buyGuardCappedHours += 1;
            if (Number.isFinite(capVolume)) bucket.buyGuardCapValuesSet.add(Number(capVolume.toFixed(2)));
            if (missingVolumeData) bucket.buyGuardMissingVolumeRows += 1;
            bucket.buyGuardAffectedVolume += affectedVolume;
            bucket.buyGuardPotentialVolume += originalVolumeValue > 0.001 ? originalVolumeValue : 0;
          });
        }
        const orderVolume = toFiniteNumberValue(hour.hedge_order_volume_mw ?? hour.hedge_volume_mw);
        if (orderVolume <= 0.001) return;
        const modeKey = isExecutableGuardMode(signalModeKey) ? signalModeKey : 'unclassified_order';
        const stats = getStats(modeKey);
        const executedVolume = toFiniteNumberValue(hour.hedge_volume_mw);
        const currentPnl = toFiniteNumberValue(hour.hedge_pnl);
        const baseAction = String(baseSimulation.execution_action || baseSimulation.base_action || baseSimulation.model_action || '').trim().toLowerCase();
        const basePrice = toFiniteNumberValue(baseSimulation.base_price, null);
        const auctionPrice = toFiniteNumberValue(hour.hedge_price ?? hour.fix_i_price, null);
        const limitPrice = toFiniteNumberValue(hour.hedge_limit_price, null);
        const side = String(hour.hedge_action || '').trim().toLowerCase();
        const unfilledVolume = Math.max(0, orderVolume - executedVolume);
        const unfilledMarketablePnl = hedgePnlFor(baseAction, basePrice, auctionPrice, unfilledVolume);
        const marketablePnl = hedgePnlFor(baseAction, basePrice, auctionPrice, orderVolume);
        const dayStats = getDayModeStats(modeKey);
        dayStats.orderVolume += orderVolume;
        dayStats.executedVolume += executedVolume;
        totalDayOrderVolume += orderVolume;
        totalDayExecutedVolume += executedVolume;
        [stats, totals].forEach((bucket) => {
          bucket.daysSet.add(rowDate);
          bucket.orderDaysSet.add(rowDate);
          bucket.orderHours += 1;
          bucket.orderVolume += orderVolume;
          bucket.executedVolume += executedVolume;
          bucket.executedPnl += currentPnl;
          bucket.marketableUnfilledPnl += unfilledMarketablePnl;
          if (unfilledVolume > 0.001 && unfilledMarketablePnl > currentPnl + 0.01) {
            bucket.marketableBetterHours += 1;
          } else if (unfilledVolume > 0.001 && unfilledMarketablePnl < currentPnl - 0.01) {
            bucket.marketableWorseHours += 1;
          }
        });
        variantDefs.forEach((variant) => {
          const variantBucket = variantStats.get(variant.key);
          let variantVolume = 0;
          let variantPnl = 0;
          if (variant.key === 'current') {
            variantVolume = executedVolume;
            variantPnl = currentPnl;
          } else if (variant.key === 'marketable') {
            variantVolume = orderVolume;
            variantPnl = marketablePnl;
          } else if (variant.key === 'no_hedge') {
            variantVolume = 0;
            variantPnl = 0;
          } else if (variantExecutes(side, auctionPrice, limitPrice, variant.delta || 0)) {
            variantVolume = orderVolume;
            variantPnl = marketablePnl;
          }
          variantBucket.orderVolume += orderVolume;
          variantBucket.executedVolume += variantVolume;
          variantBucket.pnl += variantPnl;
        });
      });
      dayModeStats.forEach((dayStats, modeKey) => {
        if (dayStats.orderVolume > 0.001 && dayStats.executedVolume <= 0.001) {
          getStats(modeKey).zeroFillDaysSet.add(rowDate);
        }
      });
      if (totalDayOrderVolume > 0.001 && totalDayExecutedVolume <= 0.001) {
        totals.zeroFillDaysSet.add(rowDate);
      }
      const carrySummary = getBaseCarryGuardSummary(
        summaryRow || baseSimulation,
        summaryRow?.hourly || baseSimulation?.hourly_plan || [],
      );
      if (carrySummary.hasIntervention) {
        totals.baseCarryDaysSet.add(rowDate);
        totals.baseForcedFix2Volume += carrySummary.forcedFix2;
        totals.baseBlockedIda1Volume += carrySummary.blockedIda1;
        totals.baseBlockedIda2Volume += carrySummary.blockedIda2;
      }
    });
    const finalizeStats = (stats) => ({
      modeKey: stats.modeKey,
      label: stats.label,
      days: stats.daysSet.size,
      orderDays: stats.orderDaysSet.size,
      zeroFillDays: stats.zeroFillDaysSet.size,
      signalHours: stats.signalHours,
      orderHours: stats.orderHours,
      orderVolume: Number(stats.orderVolume.toFixed(2)),
      executedVolume: Number(stats.executedVolume.toFixed(2)),
      fillRate: stats.orderVolume > 0 ? Number(((stats.executedVolume / stats.orderVolume) * 100).toFixed(1)) : null,
      executedPnl: Number(stats.executedPnl.toFixed(2)),
      marketableUnfilledPnl: Number(stats.marketableUnfilledPnl.toFixed(2)),
      marketableBetterHours: stats.marketableBetterHours,
      marketableWorseHours: stats.marketableWorseHours,
      buyGuardDays: stats.buyGuardDaysSet.size,
      buyGuardBlockedHours: stats.buyGuardBlockedHours,
      buyGuardCappedHours: stats.buyGuardCappedHours,
      buyGuardAffectedVolume: Number(stats.buyGuardAffectedVolume.toFixed(2)),
      buyGuardPotentialVolume: Number(stats.buyGuardPotentialVolume.toFixed(2)),
      buyGuardMissingVolumeRows: stats.buyGuardMissingVolumeRows,
      buyGuardCapValues: Array.from(stats.buyGuardCapValuesSet).sort((a, b) => a - b),
      riskOnlySpikeDays: stats.riskOnlySpikeDaysSet.size,
      riskOnlySpikeHours: stats.riskOnlySpikeHours,
      baseCarryDays: stats.baseCarryDaysSet.size,
      baseForcedFix2Volume: Number(stats.baseForcedFix2Volume.toFixed(2)),
      baseBlockedIda1Volume: Number(stats.baseBlockedIda1Volume.toFixed(2)),
      baseBlockedIda2Volume: Number(stats.baseBlockedIda2Volume.toFixed(2)),
      gooseFixIOrderHours: stats.gooseFixIOrderHours,
      gooseRiskShiftHours: stats.gooseRiskShiftHours,
      gooseRiskShiftAbsSum: Number(stats.gooseRiskShiftAbsSum.toFixed(2)),
      note: baseHedgeCopy.guardModeNotes?.[stats.modeKey] || '',
    });
    const currentPnl = variantStats.get('current')?.pnl || 0;
    return {
      totals: finalizeStats(totals),
      modes: Array.from(modeStats.values()).map(finalizeStats).sort((a, b) => b.days - a.days),
      variants: Array.from(variantStats.values()).map((variant) => ({
        key: variant.key,
        label: variant.label,
        orderVolume: Number(variant.orderVolume.toFixed(2)),
        executedVolume: Number(variant.executedVolume.toFixed(2)),
        fillRate: variant.orderVolume > 0 ? Number(((variant.executedVolume / variant.orderVolume) * 100).toFixed(1)) : null,
        pnl: Number(variant.pnl.toFixed(2)),
        delta: Number((variant.pnl - currentPnl).toFixed(2)),
      })),
    };
  }, [baseGuardCopy, baseHedgeCopy, baseSimulationRows, getBaseCarryGuardSummary, getFirstFiniteGuardNumber, getNegativeFixIBuyGuardCapMw, howItWorksLanguage, toFiniteNumberValue]);

  const formatBuyGuardVolumeSummary = useCallback((stats = {}) => {
    const affectedVolume = Number(stats.buyGuardAffectedVolume || 0);
    if (affectedVolume > 0.001) return `${formatNumber(affectedVolume)} MW`;
    if (Number(stats.buyGuardDays || 0) > 0 && Number(stats.buyGuardMissingVolumeRows || 0) > 0) {
      return howItWorksLanguage === 'pl' ? 'brak danych wolumenu' : 'volume data missing';
    }
    return '-';
  }, [formatNumber, howItWorksLanguage]);

  const formatBuyGuardCapSummary = useCallback((stats = {}) => {
    const values = Array.isArray(stats.buyGuardCapValues) ? stats.buyGuardCapValues : [];
    if (!values.length) return '-';
    return values
      .map((value) => {
        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) return null;
        if (numericValue <= 0.001) {
          return howItWorksLanguage === 'pl' ? 'blokada: 0 MW' : 'block: 0 MW';
        }
        return `${howItWorksLanguage === 'pl' ? 'limit' : 'cap'}: ${formatNumber(numericValue)} MW`;
      })
      .filter(Boolean)
      .join(' / ') || '-';
  }, [formatNumber, howItWorksLanguage]);

  const guardMechanismActivityRows = useMemo(() => {
    const modesByKey = new Map((guardCalibrationSummary.modes || []).map((mode) => [mode.modeKey, mode]));
    const totals = guardCalibrationSummary.totals || {};
    const hardMode = modesByKey.get('negative_hard') || {};
    const softMode = modesByKey.get('negative_soft') || {};
    const spikeMode = modesByKey.get('spike') || {};
    const gooseFixIMode = modesByKey.get('goose_fix_i') || {};
    const gooseRiskShiftMode = modesByKey.get('goose_risk_shift') || {};
    const hiddenStatus = howItWorksLanguage === 'pl'
      ? 'ukryty w hedge stats, bo nie jest hedge order'
      : 'hidden from hedge stats because it is not a hedge order';
    const contextStatus = howItWorksLanguage === 'pl' ? 'tylko kontekst' : 'context only';
    const usedStatus = howItWorksLanguage === 'pl' ? 'użyty' : 'used';
    const missingStatus = howItWorksLanguage === 'pl' ? 'nie wystąpił w zakresie' : 'not present in range';
    const dayLabel = (count) => baseHedgeCopy.dayCount(Number(count || 0));
    const hedgeDetail = (mode) => (
      mode?.days > 0
        ? `${dayLabel(mode.days)} · ${baseHedgeCopy.hedgeOrderVolume}: ${formatNumber(mode.orderVolume || 0)} MWh · ${baseHedgeCopy.hedgeExecutedVolume}: ${formatNumber(mode.executedVolume || 0)} MWh`
        : (howItWorksLanguage === 'pl' ? 'Brak aktywnych dni w wybranym zakresie.' : 'No active days in the selected range.')
    );
    return [
      {
        key: 'prebase',
        mechanism: baseHedgeCopy.preBaseSummaryTitle,
        status: basePreBaseReplaySummary.comparableDays > 0 ? contextStatus : missingStatus,
        tone: basePreBaseReplaySummary.comparableDays > 0 ? 'context' : 'idle',
        detail: basePreBaseReplaySummary.comparableDays > 0
          ? `${dayLabel(basePreBaseReplaySummary.comparableDays)} · ${baseHedgeCopy.preBaseSummaryWorse}: ${basePreBaseReplaySummary.buyRiskHours}h · ${baseHedgeCopy.preBaseSummarySkipped}: ${basePreBaseReplaySummary.sellRiskHours}h`
          : (howItWorksLanguage === 'pl' ? 'Radar nie został załadowany dla tego zakresu.' : 'Radar is not loaded for this range.'),
      },
      {
        key: 'goose_fix_i',
        mechanism: 'Goose Fix I execution',
        status: gooseFixIMode.days > 0 ? usedStatus : missingStatus,
        tone: gooseFixIMode.days > 0 ? 'used' : 'idle',
        detail: gooseFixIMode.days > 0
          ? `${dayLabel(gooseFixIMode.days)} · ${baseHedgeCopy.hedgeOrderVolume}: ${formatNumber(gooseFixIMode.orderVolume || 0)} MWh · ${baseHedgeCopy.hedgeExecutedVolume}: ${formatNumber(gooseFixIMode.executedVolume || 0)} MWh`
          : (howItWorksLanguage === 'pl' ? 'Brak wykonanych orderów Fix I w Goose live.' : 'No executed Fix I orders in Goose live.'),
      },
      {
        key: 'goose_risk_shift',
        mechanism: 'Goose risk shift',
        status: gooseRiskShiftMode.days > 0 ? usedStatus : missingStatus,
        tone: gooseRiskShiftMode.days > 0 ? 'used' : 'idle',
        detail: gooseRiskShiftMode.days > 0
          ? `${dayLabel(gooseRiskShiftMode.days)} · ${howItWorksLanguage === 'pl' ? 'Godziny shiftu' : 'Shift hours'}: ${gooseRiskShiftMode.gooseRiskShiftHours || 0}h · ${howItWorksLanguage === 'pl' ? 'Suma abs shift' : 'Abs shift sum'}: ${formatNumber(gooseRiskShiftMode.gooseRiskShiftAbsSum || 0)} PLN/MWh`
          : (howItWorksLanguage === 'pl' ? 'Brak aktywnego collapse-risk shift w zakresie.' : 'No active collapse-risk shift in this range.'),
      },
      {
        key: 'post_base_hedge',
        mechanism: 'Risk-management hedge',
        status: totals.orderDays > 0 ? usedStatus : missingStatus,
        tone: totals.orderDays > 0 ? 'used' : 'idle',
        detail: totals.orderDays > 0
          ? `${dayLabel(totals.orderDays)} · ${baseHedgeCopy.hedgeOrderVolume}: ${formatNumber(totals.orderVolume || 0)} MWh · ${baseHedgeCopy.fillRate}: ${totals.fillRate === null || totals.fillRate === undefined ? '-' : `${formatNumber(totals.fillRate)}%`}`
          : (howItWorksLanguage === 'pl' ? 'Nie było wystawionych orderów hedge Fix I.' : 'No Fix I hedge orders were submitted.'),
      },
      {
        key: 'negative_hard',
        mechanism: baseGuardCopy.hardLabel,
        status: hardMode.days > 0 ? usedStatus : missingStatus,
        tone: hardMode.days > 0 ? 'used' : 'idle',
        detail: hedgeDetail(hardMode),
      },
      {
        key: 'negative_soft',
        mechanism: baseGuardCopy.softLabel,
        status: softMode.days > 0 ? usedStatus : missingStatus,
        tone: softMode.days > 0 ? 'used' : 'idle',
        detail: hedgeDetail(softMode),
      },
      {
        key: 'fix_i_buy_guard',
        mechanism: baseHedgeCopy.fixIBuyGuardLabel,
        status: totals.buyGuardDays > 0 ? hiddenStatus : missingStatus,
        tone: totals.buyGuardDays > 0 ? 'hidden' : 'idle',
        detail: totals.buyGuardDays > 0
          ? `${dayLabel(totals.buyGuardDays)} · ${baseHedgeCopy.buyBlockedHours}: ${totals.buyGuardBlockedHours}h · ${baseHedgeCopy.buyCappedHours}: ${totals.buyGuardCappedHours}h · ${howItWorksLanguage === 'pl' ? 'Limit/blokada' : 'Cap/block'}: ${formatBuyGuardCapSummary(totals)} · ${baseHedgeCopy.buyGuardVolume}: ${formatBuyGuardVolumeSummary(totals)}`
          : (howItWorksLanguage === 'pl' ? 'Brak blokad lub limitów BUY Fix I w zakresie.' : 'No Fix I BUY blocks or caps in this range.'),
      },
      {
        key: 'base_carry_guard',
        mechanism: baseHedgeCopy.baseCarryGuardLabel,
        status: totals.baseCarryDays > 0 ? hiddenStatus : missingStatus,
        tone: totals.baseCarryDays > 0 ? 'hidden' : 'idle',
        detail: totals.baseCarryDays > 0
          ? `${dayLabel(totals.baseCarryDays)} · ${baseHedgeCopy.forcedFix2Label}: ${formatNumber(totals.baseForcedFix2Volume || 0)} MWh · ${baseHedgeCopy.blockedIda1Label}: ${formatNumber(totals.baseBlockedIda1Volume || 0)} MWh`
          : (howItWorksLanguage === 'pl' ? 'Brak wymuszonego carry/domknięcia residualu w zakresie.' : 'No forced carry/residual close in this range.'),
      },
      {
        key: 'spike',
        mechanism: baseGuardCopy.spikeLabel,
        status: spikeMode.days > 0 ? usedStatus : missingStatus,
        tone: spikeMode.days > 0 ? 'used' : 'idle',
        detail: hedgeDetail(spikeMode),
      },
      {
        key: 'spike_no_hedge',
        mechanism: baseGuardCopy.spikeSuppressedLabel,
        status: totals.riskOnlySpikeDays > 0 ? hiddenStatus : missingStatus,
        tone: totals.riskOnlySpikeDays > 0 ? 'hidden' : 'idle',
        detail: totals.riskOnlySpikeDays > 0
          ? `${dayLabel(totals.riskOnlySpikeDays)} · ${baseGuardCopy.spikeSuppressedShort}: ${totals.riskOnlySpikeHours}h`
          : (howItWorksLanguage === 'pl' ? 'Brak spike risk bez hedge w zakresie.' : 'No spike risk without hedge in this range.'),
      },
    ];
  }, [
    baseGuardCopy.hardLabel,
    baseGuardCopy.softLabel,
    baseGuardCopy.spikeLabel,
    baseGuardCopy.spikeSuppressedLabel,
    baseGuardCopy.spikeSuppressedShort,
    baseHedgeCopy,
    basePreBaseReplaySummary,
    formatBuyGuardCapSummary,
    formatBuyGuardVolumeSummary,
    formatNumber,
    guardCalibrationSummary,
    howItWorksLanguage,
  ]);

  const guardModeSummaryByKey = useMemo(
    () => new Map((guardCalibrationSummary.modes || []).map((mode) => [mode.modeKey, mode])),
    [guardCalibrationSummary.modes],
  );
  const gooseFixIModeSummary = guardModeSummaryByKey.get('goose_fix_i') || {};
  const gooseRiskShiftModeSummary = guardModeSummaryByKey.get('goose_risk_shift') || {};
  const guardSpikeModeSummary = guardModeSummaryByKey.get('spike') || {};
  const guardTotalsSummary = guardCalibrationSummary.totals || {};

  const gooseRiskDayRows = useMemo(() => {
    const normalizeAction = (value) => {
      const action = String(value || '').trim().toLowerCase();
      return action === 'buy' || action === 'sell' ? action : 'idle';
    };
    const stagePnl = (summaryRow, baseSimulation) => {
      const value = getLiveProvisionalDailyResultValue(summaryRow);
      if (Number.isFinite(Number(value))) return Number(value);
      return Number(
        baseSimulation?.daily_pnl_ida2_close
        ?? baseSimulation?.daily_pnl
        ?? baseSimulation?.executable_base_pnl_fix2_close
        ?? 0,
      );
    };
    return baseSimulationRows.map(({ date: rowDate, status, rowKind, baseSimulation = {}, summaryRow = {} }) => {
      const hourlyPlan = Array.isArray(baseSimulation.hourly_plan) ? baseSimulation.hourly_plan : [];
      const risk = baseSimulation.risk_management || {};
      const action = normalizeAction(risk.base_action || baseSimulation.execution_action || baseSimulation.base_action || baseSimulation.model_action || summaryRow?.base_context?.base_action);
      const baseVolume = action === 'idle'
        ? 0
        : Number(risk.base_volume_mw ?? baseSimulation.base_block_volume_mw ?? summaryRow?.base_context?.volume ?? 0);
      const riskShiftRows = hourlyPlan.filter((hour) => Math.abs(Number(hour.risk_shift || 0)) > 0.001);
      const fixIRows = hourlyPlan.filter((hour) => Number(hour.fix_i_order_volume_mw || hour.fix_i_order_volume_mwh || 0) > 0.001);
      const riskShiftHours = Number(risk.risk_shift_hours ?? riskShiftRows.length);
      const riskShiftAbsSum = Number(risk.risk_shift_abs_sum ?? riskShiftRows.reduce((sum, hour) => sum + Math.abs(Number(hour.risk_shift || 0)), 0));
      const fixIOrderHours = Number(risk.fix_i_order_hours ?? fixIRows.length);
      const fixIOrderVolume = Number(risk.fix_i_order_volume_mwh ?? fixIRows.reduce((sum, hour) => sum + Number(hour.fix_i_order_volume_mw || hour.fix_i_order_volume_mwh || 0), 0));
      const fixIExecutedVolume = Number(risk.fix_i_executed_volume_mwh ?? fixIOrderVolume);
      const selectedCarryVolume = Number(
        risk.ida2_selected_carry_volume_mwh
        ?? baseSimulation.ida2_policy_selected_carry_volume_mwh
        ?? summaryRow?.ida2_policy_selected_carry_volume_mwh
        ?? 0,
      );
      const carryForced = Number(baseSimulation.base_forced_fix2_volume_mwh ?? summaryRow?.base_forced_fix2_volume_mwh ?? 0);
      const carryBlockedIda1 = Number(baseSimulation.base_blocked_from_ida1_volume_mwh ?? summaryRow?.base_blocked_from_ida1_volume_mwh ?? 0);
      const carryBlockedIda2 = Number(baseSimulation.base_blocked_from_ida2_volume_mwh ?? summaryRow?.base_blocked_from_ida2_volume_mwh ?? 0);
      const hourlySignals = hourlyPlan
        .filter((hour) => (
          Number.isFinite(Number(hour.risk_score))
          || Math.abs(Number(hour.risk_shift || 0)) > 0.001
          || Number(hour.fix_i_order_volume_mw || hour.fix_i_order_volume_mwh || 0) > 0.001
          || Number(hour.base_forced_fix2_volume_mwh || 0) > 0.001
          || Number(hour.base_blocked_from_ida1_volume_mwh || 0) > 0.001
          || Number(hour.base_blocked_from_ida2_volume_mwh || 0) > 0.001
        ))
        .map((hour) => ({
          hour,
          hourLabel: formatOneBasedHourLabel(hour.hour),
          riskScore: hour.risk_score,
          riskShift: Number(hour.risk_shift || 0),
          fixIAction: hour.fix_i_action || hour.action || '',
          fixIVolume: Number(hour.fix_i_order_volume_mw || hour.fix_i_order_volume_mwh || 0),
          fixIPrice: hour.fix_i_price,
          carryVolume: Number(hour.base_forced_fix2_volume_mwh || 0)
            + Number(hour.base_blocked_from_ida1_volume_mwh || 0)
            + Number(hour.base_blocked_from_ida2_volume_mwh || 0),
        }));
      return {
        date: rowDate,
        status,
        rowKind,
        action,
        baseVolume,
        pnl: stagePnl(summaryRow, baseSimulation),
        riskEngine: risk.risk_score_model || baseSimulation.risk_score_model || '-',
        riskShiftHours,
        riskShiftAbsSum,
        fixIOrderHours,
        fixIOrderVolume,
        fixIExecutedVolume,
        selectedCarryVolume,
        carryForced,
        carryBlockedIda1,
        carryBlockedIda2,
        hourlySignals,
        hasActivity: riskShiftHours > 0 || fixIOrderHours > 0 || fixIOrderVolume > 0.001 || selectedCarryVolume > 0.001 || carryForced > 0.001 || carryBlockedIda1 > 0.001 || carryBlockedIda2 > 0.001,
      };
    });
  }, [baseSimulationRows, formatOneBasedHourLabel]);

  const gooseRiskRangeSummary = useMemo(() => {
    const rows = gooseRiskDayRows;
    const activeRows = rows.filter((row) => row.hasActivity);
    const sum = (getter) => rows.reduce((acc, row) => acc + Number(getter(row) || 0), 0);
    return {
      days: rows.length,
      activeDays: activeRows.length,
      buyDays: rows.filter((row) => row.action === 'buy').length,
      sellDays: rows.filter((row) => row.action === 'sell').length,
      idleDays: rows.filter((row) => row.action === 'idle').length,
      riskShiftDays: rows.filter((row) => row.riskShiftHours > 0).length,
      riskShiftHours: sum((row) => row.riskShiftHours),
      riskShiftAbsSum: sum((row) => row.riskShiftAbsSum),
      fixIDays: rows.filter((row) => row.fixIOrderHours > 0 || row.fixIOrderVolume > 0.001).length,
      fixIHours: sum((row) => row.fixIOrderHours),
      fixIOrderVolume: sum((row) => row.fixIOrderVolume),
      fixIExecutedVolume: sum((row) => row.fixIExecutedVolume),
      carryDays: rows.filter((row) => row.selectedCarryVolume > 0.001 || row.carryForced > 0.001 || row.carryBlockedIda1 > 0.001 || row.carryBlockedIda2 > 0.001).length,
      selectedCarryVolume: sum((row) => row.selectedCarryVolume),
      carryForced: sum((row) => row.carryForced),
      carryBlockedIda1: sum((row) => row.carryBlockedIda1),
      carryBlockedIda2: sum((row) => row.carryBlockedIda2),
      pnl: sum((row) => row.pnl),
    };
  }, [gooseRiskDayRows]);
  const gooseRiskDayRowByDate = useMemo(
    () => new Map(gooseRiskDayRows.map((row) => [String(row.date).slice(0, 10), row])),
    [gooseRiskDayRows],
  );

  useEffect(() => {
    if (!isBaseSimulationSection) {
      return;
    }
    const rowDates = baseSimulationRows.map((row) => row.date).filter(Boolean);
    const missingDates = rowDates.filter((rowDate) => !baseEntryRiskRowsForDisplayByDate[rowDate]);
    if (!missingDates.length || baseEntryRiskRowsLoading) {
      return;
    }
    loadBaseEntryRiskRows({ dates: missingDates, baseVolume: 50 }).catch((err) => {
      console.error(err);
    });
  }, [baseEntryRiskRowsForDisplayByDate, baseEntryRiskRowsLoading, baseSimulationRows, isBaseSimulationSection, loadBaseEntryRiskRows]);

  const rangeSnapshotSummary = useMemo(() => {
    const realizedRows = dailyRows.filter((row) => isRealizedSimulationRow(row));
    const classifySignedDays = (rows, valueGetter) => {
      const profitable = rows.filter((row) => Number(valueGetter(row) || 0) > 0.005).length;
      const loss = rows.filter((row) => Number(valueGetter(row) || 0) < -0.005).length;
      return {
        total: rows.length,
        profitable,
        loss,
        flat: Math.max(0, rows.length - profitable - loss),
      };
    };
    const safePct = (value, total) => (total > 0 ? Number(((value / total) * 100).toFixed(1)) : 0);
    const dayStats = classifySignedDays(realizedRows, (row) => getLiveProvisionalDailyResultValue(row));
    const profitableDays = realizedRows.filter((row) => Number(getLiveProvisionalDailyResultValue(row) || 0) > 0.005).length;
    const lossDays = realizedRows.filter((row) => Number(getLiveProvisionalDailyResultValue(row) || 0) < -0.005).length;
    const flatDays = Math.max(0, realizedRows.length - profitableDays - lossDays);
    const baseActions = realizedRows.reduce((acc, row) => {
      if (row?.is_manual_overwrite) {
        acc.manual += 1;
      }
      const baseContext = row?.base_context || {};
      const dailyBaseVolume = getBaseDailyVenueVolume(baseContext, row);
      const hasSettledActivity = (
        Boolean(baseContext.has_settled_activity_without_setup)
        || (
          String(baseContext.base_action || 'Idle').toLowerCase() === 'idle'
          && Number(baseContext.volume || 0) <= 0
          && dailyBaseVolume > 0
        )
      );
      if (hasSettledActivity) {
        acc.settled += 1;
        return acc;
      }
      const normalizedAction = String(baseContext.base_action || '').trim().toLowerCase();
      if (normalizedAction === 'buy') {
        acc.buy += 1;
      } else if (normalizedAction === 'sell') {
        acc.sell += 1;
      } else {
        acc.idle += 1;
      }
      return acc;
    }, { buy: 0, sell: 0, idle: 0, settled: 0, manual: 0 });
    const baseDayStats = classifySignedDays(realizedRows, (row) => Number(row?.base_pnl || 0));
    const manualOverrideRows = realizedRows.filter((row) => row?.is_manual_overwrite);
    const manualOverrideStats = classifySignedDays(manualOverrideRows, (row) => getLiveProvisionalDailyResultValue(row));
    const componentTotals = {
      basePnl: realizedRows.reduce((acc, row) => acc + Number(row?.base_pnl || 0), 0),
      fixingPnl: realizedRows.reduce((acc, row) => acc + Number(row?.probabilistic_pnl || 0), 0),
      ida1Pnl: realizedRows.reduce((acc, row) => acc + Number(row?.ida1_pnl || 0), 0),
      ida2Pnl: realizedRows.reduce((acc, row) => acc + Number(row?.ida2_pnl || 0), 0),
      baseVolume: realizedRows.reduce((acc, row) => acc + Number(row?.venue_base_mwh || 0), 0),
      fixingVolume: realizedRows.reduce((acc, row) => acc + Number(row?.venue_fix_i_mwh || 0) + Number(row?.venue_fix_ii_mwh || 0), 0),
      ida1Volume: realizedRows.reduce((acc, row) => acc + Number(row?.venue_ida1_mwh || 0), 0),
      ida2Volume: realizedRows.reduce((acc, row) => acc + Number(row?.venue_ida2_mwh || 0), 0),
    };

    const componentColumns = [
      {
        key: 'base',
        label: 'BASE leg',
        pnl: componentTotals.basePnl,
        volume: componentTotals.baseVolume,
      },
      {
        key: 'fixing',
        label: 'Fix I / Fix II legs',
        pnl: componentTotals.fixingPnl,
        volume: componentTotals.fixingVolume,
      },
      {
        key: 'ida1',
        label: 'IDA1 leg',
        pnl: componentTotals.ida1Pnl,
        volume: componentTotals.ida1Volume,
      },
      {
        key: 'ida2',
        label: 'IDA2 leg',
        pnl: componentTotals.ida2Pnl,
        volume: componentTotals.ida2Volume,
      },
    ].map((column) => ({
      ...column,
      pnlPerMwh: column.volume > 0 ? Number((column.pnl / column.volume).toFixed(2)) : null,
    }));

    return {
      totalDays: realizedRows.length,
      profitableDays,
      profitablePct: safePct(profitableDays, realizedRows.length),
      lossDays,
      lossPct: safePct(lossDays, realizedRows.length),
      flatDays,
      flatPct: safePct(flatDays, realizedRows.length),
      avgDailyPnl: realizedRows.length > 0 ? Number((liveProvisionalSummary.total_pnl / realizedRows.length).toFixed(2)) : 0,
      baseActions,
      dayStats: {
        ...dayStats,
        profitablePct: safePct(dayStats.profitable, dayStats.total),
        flatPct: safePct(dayStats.flat, dayStats.total),
        lossPct: safePct(dayStats.loss, dayStats.total),
      },
      baseDayStats: {
        ...baseDayStats,
        profitablePct: safePct(baseDayStats.profitable, baseDayStats.total),
        flatPct: safePct(baseDayStats.flat, baseDayStats.total),
        lossPct: safePct(baseDayStats.loss, baseDayStats.total),
      },
      manualOverrideStats: {
        ...manualOverrideStats,
        profitablePct: safePct(manualOverrideStats.profitable, manualOverrideStats.total),
        flatPct: safePct(manualOverrideStats.flat, manualOverrideStats.total),
        lossPct: safePct(manualOverrideStats.loss, manualOverrideStats.total),
      },
      componentColumns,
      componentTotals,
    };
  }, [dailyRows, liveProvisionalSummary.total_pnl]);
  const rangeSnapshotComponentStyles = useMemo(() => ({
    base: darkMode
      ? {
          header: 'bg-sky-950/70 text-sky-200 border-l border-sky-500/30',
          cell: 'bg-sky-950/30 text-slate-100 border-l border-sky-500/20',
        }
      : {
          header: 'bg-sky-50 text-sky-800 border-l border-sky-200',
          cell: 'bg-sky-50/80 text-slate-900 border-l border-sky-100',
        },
    fixing: darkMode
      ? {
          header: 'bg-indigo-950/70 text-indigo-200 border-l border-indigo-500/30',
          cell: 'bg-indigo-950/30 text-slate-100 border-l border-indigo-500/20',
        }
      : {
          header: 'bg-indigo-50 text-indigo-800 border-l border-indigo-200',
          cell: 'bg-indigo-50/80 text-slate-900 border-l border-indigo-100',
        },
    ida1: darkMode
      ? {
          header: 'bg-fuchsia-950/70 text-fuchsia-200 border-l border-fuchsia-500/30',
          cell: 'bg-fuchsia-950/30 text-slate-100 border-l border-fuchsia-500/20',
        }
      : {
          header: 'bg-fuchsia-50 text-fuchsia-800 border-l border-fuchsia-200',
          cell: 'bg-fuchsia-50/80 text-slate-900 border-l border-fuchsia-100',
        },
    ida2: darkMode
      ? {
          header: 'bg-emerald-950/70 text-emerald-200 border-l border-emerald-500/30',
          cell: 'bg-emerald-950/30 text-slate-100 border-l border-emerald-500/20',
        }
      : {
          header: 'bg-emerald-50 text-emerald-800 border-l border-emerald-200',
          cell: 'bg-emerald-50/80 text-slate-900 border-l border-emerald-100',
        },
  }), [darkMode]);
  const mooseIda1CardPnl = paritySummary.simulation_v5_ida1_full_pnl ?? ida1BenchmarkSummary.totalValue ?? paritySummary.simulation_v5_ida1_pnl;
  const mooseIda1CardComponents = paritySummary.simulation_v5_ida1_full_components || paritySummary.simulation_v5_ida1_components;
  const mooseIda2CardPnl = paritySummary.moose_final_ida2_pnl;
  const mooseIda2CardComponents = paritySummary.moose_final_ida2_components;
  const mooseIda2PureUplift = paritySummary.delta_moose_vs_simulation_v5_ida1 ?? mooseIda2CardComponents?.ida2;
  const paritySelectedRangeDays = paritySummary.days || liveProvisionalSummary?.realizedDays || 0;
  const ida2ComparableBasisLabel = `${parityComparableDays || 0} IDA2 day${Number(parityComparableDays || 0) === 1 ? '' : 's'}`;
  const parityHeadlineDeltaRows = [
    {
      label: 'Goose v3 IDA1 vs Goose v5 IDA1',
      value: calculateDelta(bisonV5CardPnl, bisonV3CardPnl),
      basis: 'selected range',
    },
    {
      label: 'Goose v3 IDA1 vs Goose IDA1',
      value: calculateDelta(mooseIda1CardPnl, bisonV3CardPnl),
      basis: 'selected range',
    },
    {
      label: 'Goose v5 IDA1 vs Goose IDA1',
      value: calculateDelta(mooseIda1CardPnl, bisonV5CardPnl),
      basis: 'selected range',
    },
    {
      label: 'Goose IDA1 vs Goose IDA2',
      value: calculateDelta(paritySummary.moose_final_ida2_pnl, paritySummary.simulation_v5_ida1_pnl),
      basis: ida2ComparableBasisLabel,
    },
    {
      label: 'Goose v5 IDA1 vs Goose IDA2',
      value: calculateDelta(paritySummary.moose_final_ida2_pnl, paritySummary.v5_ida1_pnl),
      basis: ida2ComparableBasisLabel,
    },
    {
      label: 'Goose v3 IDA1 vs Goose IDA2',
      value: calculateDelta(paritySummary.moose_final_ida2_pnl, paritySummary.bison_ida1_pnl),
      basis: ida2ComparableBasisLabel,
    },
  ];
  const finalMaeSummary = useMemo(() => summary.final_mae_on_settled_prices || {}, [summary]);
  const finalMaeRows = useMemo(() => ([
    {
      key: 'fix_ii',
      label: 'Fix II',
      helper: 'Stored V5 / Fix II q50 vs settled Fix II hourly market average.',
      data: finalMaeSummary.fix_ii || {},
    },
    {
      key: 'ida1',
      label: 'IDA1',
      helper: 'Stored IDA1 q50 vs settled IDA1 hourly market average.',
      data: finalMaeSummary.ida1 || {},
    },
    {
      key: 'ida2',
      label: 'IDA2',
      helper: 'Stored IDA2 q50 vs settled IDA2 hourly market average.',
      data: finalMaeSummary.ida2 || {},
    },
  ]), [finalMaeSummary]);

  const activePredictionInputData = predictionInputData?.date === date ? predictionInputData : null;
  const activeOrderAuditData = orderAuditData?.date === date
    && (!String(simulationVariant || '').startsWith('goose-') || orderAuditData?.summary?.variant === simulationVariant)
    ? orderAuditData
    : null;
  const orderAuditSummary = activeOrderAuditData?.summary || {};
  const orderAuditHours = useMemo(
    () => (isPipelineAuditSection && Array.isArray(activeOrderAuditData?.hours) ? activeOrderAuditData.hours : []),
    [activeOrderAuditData, isPipelineAuditSection],
  );
  const orderAuditEntries = useMemo(
    () => (isPipelineAuditSection
      ? orderAuditHours.flatMap((hour) => (hour.entries || []).map((entry) => ({ ...entry, hour_label: hour.hour_label })))
      : []),
    [isPipelineAuditSection, orderAuditHours],
  );
  const toggleOrderAuditHour = useCallback((hourKey) => {
    setExpandedOrderAuditHours((prev) => {
      const next = new Set(prev);
      if (next.has(hourKey)) {
        next.delete(hourKey);
      } else {
        next.add(hourKey);
      }
      return next;
    });
  }, []);
  const orderAuditBasisOptions = useMemo(() => ([
    { key: 'official', label: 'Oficjalne' },
    { key: 'all', label: 'Oficjalne + check' },
    { key: 'preview', label: 'Runtime / replay' },
  ]), []);
  const isOrderAuditEntryVisible = useCallback((entry) => {
    const basis = String(entry?.basis || '').trim().toLowerCase();
    const status = String(entry?.status || '').trim().toLowerCase();
    if (basis === 'q50_model_preview' || status === 'preview_only') {
      return false;
    }
    const isInfoOnly = ['preview_only', 'historical_replay_diff'].includes(status)
      || ['q50_model_preview', 'runtime_preview', 'historical_replay_diff'].includes(basis);
    if (orderAuditBasisFilter === 'official') {
      return basis === 'official_settle' || basis === 'goose_live';
    }
    if (orderAuditBasisFilter === 'preview') {
      return basis !== 'official_settle' && basis !== 'goose_live';
    }
    return basis === 'official_settle' || basis === 'goose_live' || !isInfoOnly;
  }, [orderAuditBasisFilter]);
  const orderAuditHourSummaries = useMemo(() => {
    if (!isPipelineAuditSection) {
      return [];
    }
    return orderAuditHours.map((hour) => {
      const entries = (Array.isArray(hour.entries) ? hour.entries : []).filter(isOrderAuditEntryVisible);
      const officialEntries = entries.filter((entry) => String(entry?.basis || '').trim().toLowerCase() === 'official_settle');
      const summaryEntries = orderAuditBasisFilter === 'all' && officialEntries.length ? officialEntries : entries;
    const orderCount = summaryEntries.reduce((acc, entry) => acc + Number(entry?.order_count || 0), 0);
    const executedOrderCount = summaryEntries.reduce((acc, entry) => acc + Number(entry?.executed_order_count || 0), 0);
    const submittedVolume = summaryEntries.reduce((acc, entry) => acc + Number(entry?.submitted_volume_mwh || 0), 0);
    const expectedVolume = summaryEntries.reduce((acc, entry) => acc + Number(entry?.expected_execution_mwh || 0), 0);
    const calculationVolume = summaryEntries.reduce((acc, entry) => acc + Number(entry?.calculation_volume_mwh || 0), 0);
    const componentPnl = toRoundedMoney(summaryEntries.reduce((acc, entry) => acc + Number(entry?.calculation_pnl || 0), 0)) || 0;
    const canonicalPnl = toRoundedMoney(hour.pnl);
    const pnl = canonicalPnl === null ? componentPnl : canonicalPnl;
    const pnlComponentDelta = canonicalPnl === null ? 0 : (toRoundedMoney(canonicalPnl - componentPnl) || 0);
    const mismatchCount = entries.filter((entry) => !['ok', 'preview_only', 'calculation_only', 'historical_replay_diff'].includes(String(entry?.status || ''))).length;
    const previewCount = entries.filter((entry) => ['preview_only', 'calculation_only', 'historical_replay_diff'].includes(String(entry?.status || ''))).length;
    return {
      ...hour,
      entries,
      orderCount,
      executedOrderCount,
      submittedVolume,
      expectedVolume,
      calculationVolume,
      pnl,
      componentPnl,
      pnlComponentDelta,
      mismatchCount,
      previewCount,
      };
    }).filter((hour) => hour.entries.length > 0);
  }, [isPipelineAuditSection, orderAuditHours, isOrderAuditEntryVisible, orderAuditBasisFilter, toRoundedMoney]);
  const getOrderAuditHourLabel = useCallback((hour) => formatHourLabel(hour?.hour), [formatHourLabel]);
  const orderAuditAllHoursExpanded = orderAuditHourSummaries.length > 0
    && orderAuditHourSummaries.every((hour) => expandedOrderAuditHours.has(getOrderAuditHourLabel(hour)));
  const getOrderAuditBasisMeta = useCallback((basis) => {
    const normalized = String(basis || '').trim().toLowerCase();
    if (normalized === 'official_settle') {
      return {
        label: 'official settle',
        className: darkMode ? 'border-emerald-400/60 bg-emerald-400/10 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700',
      };
    }
    if (normalized === 'goose_live') {
      return {
        label: 'Goose live',
        className: darkMode ? 'border-cyan-400/60 bg-cyan-400/10 text-cyan-200' : 'border-cyan-200 bg-cyan-50 text-cyan-800',
      };
    }
    if (normalized === 'historical_replay_diff') {
      return {
        label: 'replay historyczny',
        className: darkMode ? 'border-sky-400/60 bg-sky-400/10 text-sky-200' : 'border-sky-200 bg-sky-50 text-sky-800',
      };
    }
    if (normalized === 'q50_model_preview') {
      return {
        label: 'preview q50',
        className: darkMode ? 'border-cyan-400/60 bg-cyan-400/10 text-cyan-200' : 'border-cyan-200 bg-cyan-50 text-cyan-800',
      };
    }
    return {
      label: normalized ? normalized.replace(/_/g, ' ') : 'preview runtime',
      className: darkMode ? 'border-slate-500 bg-slate-500/10 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600',
    };
  }, [darkMode]);
  const predictionInputSummary = activePredictionInputData?.summary || {};
  const predictionInputRows = useMemo(() => {
    if (!(isPipelinePredictionsSection || isPipelineDataSection)) {
      return [];
    }
    const datasetRows = Array.isArray(activePredictionInputData?.rows) ? activePredictionInputData.rows : [];
    if (datasetRows.length) {
      return datasetRows;
    }
    return activePredictRows.map((row) => ({
      hour: row.target_hour_waw ?? row.hour,
      input: {},
      signals: {},
      prediction: {
        fix_i_price: row.fix_i_price,
        fix_ii_q10: row.stacked_q10,
        fix_ii_q30: row.stacked_q30,
        fix_ii_q50: row.stacked_q50,
        fix_ii_q70: row.stacked_q70,
        fix_ii_q90: row.stacked_q90,
        ida1_q50: row.ida1_price,
        ida2_q50: row.ida2_price,
      },
      plan: {
        load_band: row.load_band,
        blend_alpha: row.blend_alpha,
        capacity_source: row.capacity_source,
        issue_date: row.issue_date,
      },
    }));
  }, [activePredictRows, activePredictionInputData, isPipelineDataSection, isPipelinePredictionsSection]);
  const predictionInputSourceCards = useMemo(() => (
    (isPipelinePredictionsSection || isPipelineDataSection)
      ? (Array.isArray(activePredictionInputData?.sources) ? activePredictionInputData.sources : [])
      : []
  ), [activePredictionInputData, isPipelineDataSection, isPipelinePredictionsSection]);
  const formatDatasetValue = useCallback((value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : '-';
    }
    if (typeof value === 'object') {
      return Object.keys(value).length ? Object.entries(value).map(([key, entryValue]) => `${key}: ${entryValue}`).join(', ') : '-';
    }
    if (typeof value === 'number') {
      return formatNumber(value);
    }
    const numeric = Number(value);
    if (!Number.isNaN(numeric) && String(value).trim() !== '') {
      return formatNumber(numeric);
    }
    return String(value);
  }, [formatNumber]);
  const getDatasetCellTone = useCallback((columnKey) => {
    if (columnKey === 'hour') {
      return darkMode ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900';
    }
    if (columnKey.startsWith('input:')) {
      return darkMode ? 'bg-sky-950/20 text-sky-50' : 'bg-sky-50/55 text-slate-900';
    }
    if (columnKey.startsWith('signal:')) {
      return darkMode ? 'bg-emerald-950/20 text-emerald-50' : 'bg-emerald-50/50 text-slate-900';
    }
    return darkMode ? 'text-slate-100' : 'text-slate-800';
  }, [darkMode]);
  const formatSourceStatusLabel = useCallback((status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'ready') return pipelineCopy.readySource;
    if (normalized === 'error') return pipelineCopy.errorSource;
    return pipelineCopy.missingSource;
  }, [pipelineCopy]);
  const getPredictionDatasetCoverage = useCallback((group, columnName) => {
    const coverageValue = activePredictionInputData?.coverage?.[group]?.[columnName];
    if (coverageValue !== null && coverageValue !== undefined) {
      return Number(coverageValue || 0);
    }
    return predictionInputRows.reduce((acc, row) => {
      const value = row?.[group]?.[columnName];
      return value !== null && value !== undefined && value !== '' && !(Array.isArray(value) && value.length === 0)
        ? acc + 1
        : acc;
    }, 0);
  }, [activePredictionInputData, predictionInputRows]);
  const predictionDatasetColumns = useMemo(() => {
    if (!(isPipelinePredictionsSection || isPipelineDataSection)) {
      return [];
    }
    const seen = new Set();
    const addColumn = (columns, column) => {
      if (!column?.key || seen.has(column.key)) {
        return;
      }
      seen.add(column.key);
      columns.push(column);
    };
    const columns = [];
    [
      { key: 'hour', label: pipelineCopy.predictionColumns.hour, group: '', value: (row) => formatHourLabel(row.hour) },
      { key: 'fix_i_price', label: 'Fix I', group: pipelineCopy.marketContext, value: (row) => row.prediction?.fix_i_price },
      {
        key: 'fix_ii_q50',
        label: isModifiedSimulationActive
          ? `Fix II ${simulationVariant === 'goose-v5' ? 'v5' : 'v3'} q50`
          : 'Fix II q50',
        group: pipelineCopy.modelOutputs,
        value: (row) => row.prediction?.fix_ii_q50,
      },
      { key: 'fix_ii_tail', label: 'Q10 / Q90', group: pipelineCopy.modelOutputs, value: (row) => `${formatDatasetValue(row.prediction?.fix_ii_q10)} / ${formatDatasetValue(row.prediction?.fix_ii_q90)}` },
      { key: 'ida1_q50', label: 'IDA1 q50', group: pipelineCopy.modelOutputs, value: (row) => row.prediction?.ida1_q50 },
      { key: 'ida2_q50', label: 'IDA2 q50', group: pipelineCopy.modelOutputs, value: (row) => row.prediction?.ida2_q50 },
      { key: 'load_band', label: pipelineCopy.predictionColumns.loadBand, group: pipelineCopy.modelOutputs, value: (row) => row.plan?.load_band },
      { key: 'capacity_source', label: pipelineCopy.predictionColumns.capacitySource, group: pipelineCopy.modelOutputs, value: (row) => row.plan?.capacity_source || predictionInputSummary.capacity_source },
    ].forEach((column) => addColumn(columns, column));

    PREDICTION_INPUT_CAPACITY_COLUMNS.forEach(({ column: columnName, labelKey, fallbackGroup }) => {
      const inputCoverage = getPredictionDatasetCoverage('input', columnName);
      const fallbackCoverage = fallbackGroup ? getPredictionDatasetCoverage(fallbackGroup, columnName) : 0;
      const coverage = Math.max(inputCoverage, fallbackCoverage);
      if (coverage <= 0) {
        return;
      }
      addColumn(columns, {
        key: `capacity:${columnName}`,
        label: pipelineCopy.predictionColumns[labelKey] || columnName.replace(/_/g, ' '),
        group: pipelineCopy.marketContext,
        coverage,
        value: (row) => row.input?.[columnName] ?? (fallbackGroup ? row[fallbackGroup]?.[columnName] : undefined),
      });
    });

    (activePredictionInputData?.columns?.input || []).forEach((columnName) => {
      if (PINNED_PREDICTION_INPUT_COLUMNS.has(columnName)) {
        return;
      }
      const coverage = getPredictionDatasetCoverage('input', columnName);
      if (coverage <= 0) {
        return;
      }
      addColumn(columns, {
        key: `input:${columnName}`,
        label: columnName.replace(/_/g, ' '),
        group: pipelineCopy.baseDataset,
        coverage,
        value: (row) => row.input?.[columnName],
      });
    });
    (activePredictionInputData?.columns?.signals || []).forEach((columnName) => {
      if (PINNED_PREDICTION_INPUT_COLUMNS.has(columnName)) {
        return;
      }
      const coverage = getPredictionDatasetCoverage('signals', columnName);
      if (coverage <= 0) {
        return;
      }
      addColumn(columns, {
        key: `signal:${columnName}`,
        label: columnName.replace(/_/g, ' '),
        group: pipelineCopy.inputSignals,
        coverage,
        value: (row) => row.signals?.[columnName],
      });
    });
    return columns;
  }, [activePredictionInputData, formatDatasetValue, formatHourLabel, getPredictionDatasetCoverage, isModifiedSimulationActive, isPipelineDataSection, isPipelinePredictionsSection, pipelineCopy, predictionInputSummary.capacity_source, simulationVariant]);
  const predictionDatasetHiddenEmptyColumns = useMemo(() => {
    if (!(isPipelinePredictionsSection || isPipelineDataSection)) {
      return 0;
    }
    const inputColumns = activePredictionInputData?.columns?.input || [];
    const signalColumns = activePredictionInputData?.columns?.signals || [];
    return [
      ...inputColumns.map((columnName) => ['input', columnName]),
      ...signalColumns.map((columnName) => ['signals', columnName]),
    ].filter(([group, columnName]) => getPredictionDatasetCoverage(group, columnName) <= 0).length;
  }, [activePredictionInputData, getPredictionDatasetCoverage, isPipelineDataSection, isPipelinePredictionsSection]);
  const predictionDatasetTableWidth = Math.max(1280, predictionDatasetColumns.length * 118);

  const strategyResultLabel = isModifiedSimulationActive
    ? resultsCopy.modifiedTotalLabel
    : resultsCopy.liveTotalLabel;
  const strategyResultValue = liveProvisionalSummary.total_pnl;
  const strategyResultAfterFees = liveProvisionalSummary.total_after_fees;
  const strategyResultFees = liveProvisionalSummary.total_fees;
  const mixedSummaryRange = liveProvisionalSummary.realizedDays > comparableSummary.comparableDays;
  const fixIIForecastColumnLabel = isModifiedSimulationActive
    ? `Fix II (${simulationVariant === 'goose-v5' ? 'v5' : 'v3'} Q50)`
    : 'Fix II (Q50)';
  const orderPlanColumns = [
    { label: pipelineCopy.predictionColumns.hour, widthClass: 'w-[72px] min-w-[72px] max-w-[72px]' },
    { label: 'Fix I', widthClass: 'w-[92px] min-w-[92px] max-w-[92px]' },
    { label: fixIIForecastColumnLabel, widthClass: 'w-[132px] min-w-[132px] max-w-[132px]' },
    { label: 'IDA1', widthClass: 'w-[92px] min-w-[92px] max-w-[92px]' },
    { label: 'IDA2', widthClass: 'w-[92px] min-w-[92px] max-w-[92px]' },
    { label: 'Q10', widthClass: 'w-[92px] min-w-[92px] max-w-[92px]' },
    { label: 'Q30', widthClass: 'w-[92px] min-w-[92px] max-w-[92px]' },
    { label: 'Q50', widthClass: 'w-[92px] min-w-[92px] max-w-[92px]' },
    { label: 'Q70', widthClass: 'w-[92px] min-w-[92px] max-w-[92px]' },
    { label: 'Q90', widthClass: 'w-[92px] min-w-[92px] max-w-[92px]' },
    { label: pipelineCopy.predictionColumns.executed, widthClass: 'w-[168px] min-w-[168px] max-w-[168px]' },
    { label: pipelineCopy.predictionColumns.executedVolFixI, widthClass: 'w-[128px] min-w-[128px] max-w-[128px]' },
    { label: pipelineCopy.predictionColumns.closedFixII, widthClass: 'w-[124px] min-w-[124px] max-w-[124px]' },
    { label: pipelineCopy.predictionColumns.closedIda1, widthClass: 'w-[124px] min-w-[124px] max-w-[124px]' },
    { label: pipelineCopy.predictionColumns.residualIda2, widthClass: 'w-[138px] min-w-[138px] max-w-[138px]' },
    { label: pipelineCopy.predictionColumns.closedIda2, widthClass: 'w-[124px] min-w-[124px] max-w-[124px]' },
    { label: pipelineCopy.predictionColumns.finalVolume, widthClass: 'w-[176px] min-w-[176px] max-w-[176px]' },
    { label: pipelineCopy.predictionColumns.loadBand, widthClass: 'w-[108px] min-w-[108px] max-w-[108px]' },
    { label: 'Alpha', widthClass: 'w-[84px] min-w-[84px] max-w-[84px]' },
    { label: pipelineCopy.predictionColumns.capacitySource, widthClass: 'w-[130px] min-w-[130px] max-w-[130px]' },
    { label: pipelineCopy.predictionColumns.planIssue, widthClass: 'w-[180px] min-w-[180px] max-w-[180px]' },
  ];
  const orderPlanTableMinWidthClass = 'min-w-[2464px]';
  const toggleDailyQuarterPathExpanded = useCallback((rowKey) => {
    setExpandedDailyQuarterPaths((current) => ({
      ...current,
      [rowKey]: !current[rowKey],
    }));
  }, []);
  const toggleExecutionQuarterPathExpanded = useCallback((rowKey) => {
    setExpandedExecutionQuarterPaths((current) => ({
      ...current,
      [rowKey]: !current[rowKey],
    }));
  }, []);
  const dailyResultsColumns = [
    { label: resultsCopy.columns.details, widthClass: 'w-[74px] min-w-[74px] max-w-[74px]' },
    { label: resultsCopy.columns.date, widthClass: 'w-[108px] min-w-[108px] max-w-[108px]' },
    { label: resultsCopy.columns.status, widthClass: 'w-[92px] min-w-[92px] max-w-[92px]' },
    { label: resultsCopy.columns.result, widthClass: 'w-[170px] min-w-[170px] max-w-[170px]' },
    { label: resultsCopy.columns.baseSetup, widthClass: 'w-[340px] min-w-[340px] max-w-[340px]' },
    { label: resultsCopy.columns.basePnl, widthClass: 'w-[112px] min-w-[112px] max-w-[112px]' },
    { label: resultsCopy.columns.fixPnl, widthClass: 'w-[132px] min-w-[132px] max-w-[132px]' },
    { label: resultsCopy.columns.ida1Pnl, widthClass: 'w-[108px] min-w-[108px] max-w-[108px]' },
    { label: resultsCopy.columns.ida2Pnl, widthClass: 'w-[108px] min-w-[108px] max-w-[108px]' },
    { label: resultsCopy.columns.volume, widthClass: 'w-[102px] min-w-[102px] max-w-[102px]' },
  ];
  const dailyResultsTableMinWidthClass = 'min-w-[1274px]';
  const splitQuarterVolumes = useCallback((totalVolume) => {
    const total = Number(totalVolume || 0);
    if (!Number.isFinite(total) || total <= 0) {
      return [0, 0, 0, 0];
    }
    const first = Number((total / 4).toFixed(2));
    const second = Number((total / 4).toFixed(2));
    const third = Number((total / 4).toFixed(2));
    const fourth = Number((total - first - second - third).toFixed(2));
    return [first, second, third, fourth];
  }, []);
  const getPreviewHourIndex = useCallback((row) => {
    const explicitHour = Number(row?.target_hour_waw);
    if (Number.isFinite(explicitHour)) {
      return explicitHour;
    }
    const fallbackHour = Number(row?.hour);
    return Number.isFinite(fallbackHour) ? fallbackHour : 0;
  }, []);
  const parseNullableNumber = useCallback((value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }, []);
  const getDailyQ50Averages = useCallback((hourlyRows = [], metadata = null) => {
    const avgForKeys = (keys, metadataKey) => {
      const values = (hourlyRows || [])
        .map((hourRow) => {
          const matchedKey = keys.find((key) => hourRow?.[key] !== null && hourRow?.[key] !== undefined);
          return matchedKey ? parseNullableNumber(hourRow?.[matchedKey]) : null;
        })
        .filter((value) => value !== null);
      if (!values.length) {
        return parseNullableNumber(metadata?.average_prices?.[metadataKey]);
      }
      return Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(2));
    };
    return {
      fix2: avgForKeys(['stacked_q50'], 'fix2'),
      ida1: avgForKeys(['ida1_q50'], 'ida1'),
      ida2: avgForKeys(['ida2_q50'], 'ida2'),
    };
  }, [parseNullableNumber]);
  const getDailyActualPriceAverages = useCallback((hourlyRows = [], metadata = null) => {
    const avgForKey = (key, metadataKey = null) => {
      const values = (hourlyRows || [])
        .map((hourRow) => parseNullableNumber(hourRow?.[key]))
        .filter((value) => value !== null);
      if (!values.length) {
        return metadataKey ? parseNullableNumber(metadata?.average_prices?.[metadataKey]) : null;
      }
      return Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(2));
    };
    return {
      fix_i: avgForKey('fix_i_price', 'fix_i'),
      fix2: avgForKey('fix_ii_price'),
      ida1: avgForKey('ida1_price'),
      ida2: avgForKey('ida2_price'),
    };
  }, [parseNullableNumber]);
  const getDailyMarketPriceAverages = useCallback((hourlyRows = []) => {
    const avgForKey = (key) => {
      const values = (hourlyRows || [])
        .map((hourRow) => parseNullableNumber(hourRow?.[key]))
        .filter((value) => value !== null);
      if (!values.length) {
        return null;
      }
      return Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(2));
    };
    return {
      fix2: avgForKey('fix_ii_market_price'),
      ida1: avgForKey('ida1_market_price'),
      ida2: avgForKey('ida2_market_price'),
    };
  }, [parseNullableNumber]);
  const normalizeAction = useCallback((value) => {
    const action = String(value || '').toLowerCase();
    return action === 'buy' || action === 'sell' ? action : null;
  }, []);
  const deriveFixIExecution = useCallback((row) => {
    const explicitAction = normalizeAction(row?.executed_action);
    const explicitVolume = Number(row?.executed_volume_fix1 || 0);
    if (explicitAction && Number.isFinite(explicitVolume) && explicitVolume > 0) {
      return {
        action: explicitAction,
        totalVolume: Number(explicitVolume.toFixed(2)),
        source: 'stored',
      };
    }

    const fixIPrice = parseNullableNumber(row?.fix_i_price);
    if (!Number.isFinite(fixIPrice)) {
      return {
        action: null,
        totalVolume: 0,
        source: null,
      };
    }

    const buyVolume = [
      { trigger: Number(row?.buy_trigger_1), volume: Number(row?.buy_volume_1 || 0) },
      { trigger: Number(row?.buy_trigger_2), volume: Number(row?.buy_volume_2 || 0) },
      { trigger: Number(row?.buy_trigger_3), volume: Number(row?.buy_volume_3 || 0) },
    ].reduce((sum, order) => (
      Number.isFinite(order.trigger) && Number.isFinite(order.volume) && order.volume > 0 && fixIPrice <= order.trigger
        ? sum + order.volume
        : sum
    ), 0);
    const sellVolume = [
      { trigger: Number(row?.sell_trigger_1), volume: Number(row?.sell_volume_1 || 0) },
      { trigger: Number(row?.sell_trigger_2), volume: Number(row?.sell_volume_2 || 0) },
      { trigger: Number(row?.sell_trigger_3), volume: Number(row?.sell_volume_3 || 0) },
    ].reduce((sum, order) => (
      Number.isFinite(order.trigger) && Number.isFinite(order.volume) && order.volume > 0 && fixIPrice >= order.trigger
        ? sum + order.volume
        : sum
    ), 0);

    if (buyVolume > 0 && sellVolume <= 0) {
      return {
        action: 'buy',
        totalVolume: Number(buyVolume.toFixed(2)),
        source: 'derived',
      };
    }
    if (sellVolume > 0 && buyVolume <= 0) {
      return {
        action: 'sell',
        totalVolume: Number(sellVolume.toFixed(2)),
        source: 'derived',
      };
    }

    return {
      action: null,
      totalVolume: 0,
      source: null,
    };
  }, [normalizeAction, parseNullableNumber]);
  const inferCloseAction = useCallback((row, volumeKey) => {
    const explicitAction = normalizeAction(row?.executed_action);
    if (explicitAction) {
      return explicitAction;
    }

    const closeVolume = Number(row?.[volumeKey] || 0);
    if (!Number.isFinite(closeVolume) || closeVolume <= 0) {
      return null;
    }

    const soldValue = Number(row?.sold_value || 0);
    const boughtValue = Number(row?.bought_value || 0);
    const hasValueSignal = Number.isFinite(soldValue) && Number.isFinite(boughtValue);
    if (hasValueSignal && soldValue !== boughtValue) {
      return soldValue > boughtValue ? 'buy' : 'sell';
    }

    // Fallback keeps historical behavior for rows where action is missing but only close
    // volume is still available (common on Fix II forced-close rows).
    return 'buy';
  }, [normalizeAction]);
  const inferQuarterCloseSide = useCallback((row, volumeKey, actionKey) => {
    const explicitCloseAction = normalizeAction(row?.[actionKey]);
    if (explicitCloseAction === 'buy') {
      return 'K';
    }
    if (explicitCloseAction === 'sell') {
      return 'S';
    }

    const legacyAction = inferCloseAction(row, volumeKey);
    if (legacyAction === 'buy') {
      return 'S';
    }
    if (legacyAction === 'sell') {
      return 'K';
    }
    return null;
  }, [inferCloseAction, normalizeAction]);
  const getArbitrageSignal = useCallback((row) => {
    const execution = deriveFixIExecution(row);
    const entryPrice = parseNullableNumber(row?.fix_i_price);
    if (!execution.action || !Number.isFinite(entryPrice)) {
      return {
        state: 'neutral',
        label: 'No trigger hit',
        rowClass: 'bg-white',
        badgeClass: 'bg-gray-100 text-gray-600 border-gray-200',
        nextLegLabel: null,
        nextLegPrice: null,
        executedAction: execution.action,
        executedVolume: execution.totalVolume,
        executionSource: execution.source,
      };
    }

    const downstreamCandidates = [
      {
        label: 'IDA2',
        price: parseNullableNumber(row?.ida2_price),
        usable: Boolean(row?.ida2_prediction_available) && Number.isFinite(parseNullableNumber(row?.ida2_price)),
      },
      {
        label: 'IDA1',
        price: parseNullableNumber(row?.ida1_price),
        usable: Boolean(row?.ida1_prediction_available) && Number.isFinite(parseNullableNumber(row?.ida1_price)),
      },
      {
        label: 'Fix II',
        price: parseNullableNumber(row?.fix_ii_price),
        usable: Number.isFinite(parseNullableNumber(row?.fix_ii_price)),
      },
    ];

    const nextLeg = downstreamCandidates.find((candidate) => candidate.usable);
    if (!nextLeg) {
      return {
        state: 'pending',
        label: 'Next leg pending',
        rowClass: 'bg-white',
        badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
        nextLegLabel: null,
        nextLegPrice: null,
        executedAction: execution.action,
        executedVolume: execution.totalVolume,
        executionSource: execution.source,
      };
    }

    const favorable = execution.action === 'buy'
      ? nextLeg.price > entryPrice
      : nextLeg.price < entryPrice;

    return {
      state: favorable ? 'favorable' : 'unfavorable',
      label: favorable ? 'Favorable' : 'Unfavorable',
      rowClass: favorable ? 'bg-emerald-50/40' : 'bg-rose-50/40',
      badgeClass: favorable ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200',
      nextLegLabel: nextLeg.label,
      nextLegPrice: nextLeg.price,
      executedAction: execution.action,
      executedVolume: execution.totalVolume,
      executionSource: execution.source,
    };
  }, [deriveFixIExecution, parseNullableNumber]);
  const buildQuarterInstrument = useCallback((prefix, targetDate, hourIndex, quarterIndex) => {
    const formattedDate = formatNobleDate(targetDate);
    const oneBasedHour = Number(hourIndex || 0) + 1;
    const previousHour = String(Math.max(oneBasedHour - 1, 0)).padStart(2, '0');
    const currentHour = String(oneBasedHour).padStart(2, '0');
    const quarterLabel = quarterIndex === 3
      ? `${currentHour}:00`
      : `${previousHour}:${String((quarterIndex + 1) * 15).padStart(2, '0')}`;
    return `${prefix}_${formattedDate}_Q${quarterLabel}`;
  }, [formatNobleDate]);
  const buildQuarterInstrumentFromLabel = useCallback((prefix, targetDate, quarterLabel) => {
    const formattedDate = formatNobleDate(targetDate);
    return `${prefix}_${formattedDate}_Q${String(quarterLabel || '').trim()}`;
  }, [formatNobleDate]);
  const collapseQuarterPreviewRows = useCallback((rows = [], modeKey = '') => {
    if (modeKey !== 'ida2' || !Array.isArray(rows) || rows.length <= 1) {
      return rows;
    }
    const collapsed = [];
    rows.forEach((row) => {
      const price = Number(row?.price);
      const referencePrice = row?.reference_price === null || row?.reference_price === undefined
        ? null
        : Number(row.reference_price);
      const limitMargin = row?.limit_margin === null || row?.limit_margin === undefined
        ? null
        : Number(row.limit_margin);
      const sourceLabel = row?.source || '';
      const key = [
        row?.side || '',
        Number.isFinite(price) ? price.toFixed(2) : 'nan',
        Number.isFinite(referencePrice) ? referencePrice.toFixed(2) : 'na',
        Number.isFinite(limitMargin) ? limitMargin.toFixed(2) : 'na',
        row?.price_type || '',
        sourceLabel,
      ].join('|');
      const previous = collapsed[collapsed.length - 1];
      if (previous && previous._collapseKey === key) {
        previous.volume = Number((Number(previous.volume || 0) + Number(row?.volume || 0)).toFixed(2));
        previous._rowCount += 1;
        previous.instrument = `${previous._firstInstrument} +${previous._rowCount - 1}`;
        return;
      }
      collapsed.push({
        ...row,
        _collapseKey: key,
        _firstInstrument: row?.instrument || 'IDA2',
        _rowCount: 1,
      });
    });
    return collapsed.map(({ _collapseKey, _firstInstrument, _rowCount, ...row }) => row);
  }, []);
  const parseQuarterLabelMeta = useCallback((quarterLabel) => {
    const match = String(quarterLabel || '').trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
      return null;
    }
    const hourLabel = Number(match[1]);
    const minuteLabel = Number(match[2]);
    if (!Number.isFinite(hourLabel) || !Number.isFinite(minuteLabel)) {
      return null;
    }
    if (minuteLabel === 0) {
      return {
        hourIndex: Math.max(hourLabel - 1, 0),
        qh: 3,
      };
    }
    const qhMap = { 15: 0, 30: 1, 45: 2 };
    if (!(minuteLabel in qhMap)) {
      return null;
    }
    return {
      hourIndex: hourLabel,
      qh: qhMap[minuteLabel],
    };
  }, []);
  const stagedNoblePreviewRows = useMemo(() => {
    if (!(isPipelineOrdersSection || isLetsTradeSection) || previewPredictRows.length === 0) {
      return {
        fix_i: [],
        fix_ii: [],
        ida1: [],
        ida2: [],
      };
    }
    const targetDate = String(previewDeliveryDate || '');
    const buildFixIExecutedOrders = (prefix) => previewPredictRows.flatMap((row) => {
      const execution = deriveFixIExecution(row);
      const action = execution.action;
      const price = parseNullableNumber(row?.fix_i_price);
      const volume = Number(execution.totalVolume || 0);
      if (!action || !Number.isFinite(price) || !Number.isFinite(volume) || volume <= 0) {
        return [];
      }
      const instrument = `${prefix}_${formatNobleDate(targetDate)}_H${String(getPreviewHourIndex(row) + 1).padStart(2, '0')}`;
      return [{
        instrument,
        side: action === 'buy' ? 'K' : 'S',
        price,
        volume: Number(volume.toFixed(2)),
      }];
    });
    const buildHourlyLadderOrders = (prefix) => previewPredictRows.flatMap((row) => {
      const instrument = `${prefix}_${formatNobleDate(targetDate)}_H${String(getPreviewHourIndex(row) + 1).padStart(2, '0')}`;
      return [
        { side: 'K', price: Number(row?.buy_trigger_1), volume: Number(row?.buy_volume_1 || 0) },
        { side: 'K', price: Number(row?.buy_trigger_2), volume: Number(row?.buy_volume_2 || 0) },
        { side: 'K', price: Number(row?.buy_trigger_3), volume: Number(row?.buy_volume_3 || 0) },
        { side: 'S', price: Number(row?.sell_trigger_1), volume: Number(row?.sell_volume_1 || 0) },
        { side: 'S', price: Number(row?.sell_trigger_2), volume: Number(row?.sell_volume_2 || 0) },
        { side: 'S', price: Number(row?.sell_trigger_3), volume: Number(row?.sell_volume_3 || 0) },
      ]
        .filter((order) => Number.isFinite(order.price) && Number.isFinite(order.volume) && order.volume > 0)
        .map((order) => ({
          instrument,
          side: order.side,
          price: order.price,
          volume: Number(order.volume.toFixed(2)),
          stageKey: 'fix_ii',
          hour: getPreviewHourIndex(row),
          price_type: 'limit',
        }));
    });

    const buildQuarterCloseOrders = (
        stageKey,
        priceKey,
        volumeKey,
        actionKey,
        prefix,
        previewPriceKey = null,
        previewReferencePriceKey = null,
        previewMarginKey = null,
      ) => previewPredictRows.flatMap((row) => {
        const side = inferQuarterCloseSide(row, volumeKey, actionKey);
        const totalVolume = Number(row?.[volumeKey] || 0);
        const previewPrice = previewPriceKey ? parseNullableNumber(row?.[previewPriceKey]) : null;
        const price = Number.isFinite(previewPrice) ? previewPrice : Number(row?.[priceKey]);
        const referencePrice = previewReferencePriceKey ? parseNullableNumber(row?.[previewReferencePriceKey]) : null;
        const limitMargin = previewMarginKey ? parseNullableNumber(row?.[previewMarginKey]) : null;
        const quarterPaths = Array.isArray(row?.quarter_paths) ? row.quarter_paths : [];
        const quarterLegKey = stageKey === 'ida1' ? 'ida1_leg' : 'ida2_leg';
        const quarterOrders = quarterPaths
          .map((path) => {
            const leg = path?.[quarterLegKey] || {};
            const legAction = inferQuarterCloseSide(leg, 'volume_mwh', 'action');
            const legVolume = Number(leg?.volume_mwh || 0);
            const quarterLabel = String(path?.quarter_label || '').trim();
            const quarterMeta = parseQuarterLabelMeta(quarterLabel);
            const legPrice = parseNullableNumber(leg?.price);
            const quarterReferencePrice = parseNullableNumber(path?.[priceKey]);
            const finalPrice = Number.isFinite(previewPrice)
              ? previewPrice
              : (legPrice ?? quarterReferencePrice ?? price);
            if (
              !quarterMeta
              || !quarterLabel
              || !legAction
              || !['K', 'S'].includes(legAction)
              || !Number.isFinite(legVolume)
              || legVolume <= 0
              || !Number.isFinite(finalPrice)
            ) {
              return null;
            }
            return {
              instrument: buildQuarterInstrumentFromLabel(prefix, targetDate, quarterLabel),
              side: legAction,
              price: finalPrice,
              volume: Number(legVolume.toFixed(2)),
              stageKey,
              hour: quarterMeta.hourIndex,
              qh: quarterMeta.qh,
              reference_price: Number.isFinite(referencePrice)
                ? Number(referencePrice.toFixed(2))
                : (quarterReferencePrice !== null ? Number(quarterReferencePrice.toFixed(2)) : null),
              limit_margin: Number.isFinite(limitMargin) ? Number(limitMargin.toFixed(2)) : null,
              price_type: Number.isFinite(previewPrice) ? 'limit' : 'reference',
            };
          })
          .filter((order) => order && order.volume > 0);
        if (quarterOrders.length > 0) {
          return quarterOrders;
        }
        if (!side || !['K', 'S'].includes(side) || !Number.isFinite(totalVolume) || totalVolume <= 0 || !Number.isFinite(price)) {
          return [];
        }
        const splitVolumes = splitQuarterVolumes(totalVolume);
        return splitVolumes
          .map((volume, quarterIndex) => ({
            instrument: buildQuarterInstrument(prefix, targetDate, getPreviewHourIndex(row), quarterIndex),
            side,
            price,
            volume,
            stageKey,
            reference_price: Number.isFinite(referencePrice) ? Number(referencePrice.toFixed(2)) : null,
            limit_margin: Number.isFinite(limitMargin) ? Number(limitMargin.toFixed(2)) : null,
            price_type: Number.isFinite(previewPrice) ? 'limit' : 'reference',
          }))
          .filter((order) => order.volume > 0);
      });

      return {
        fix_i: buildFixIExecutedOrders('RDN'),
        fix_ii: buildHourlyLadderOrders('DAM'),
        ida1: buildQuarterCloseOrders('ida1', 'ida1_price', 'closed_volume_ida1', 'ida1_close_action', 'IDA1', 'ida1_preview_price', 'ida1_preview_reference_price', 'ida1_limit_margin'),
        ida2: buildQuarterCloseOrders('ida2', 'ida2_price', 'closed_volume_ida2', 'ida2_close_action', 'IDA2', 'ida2_preview_price', 'ida2_preview_reference_price', 'ida2_limit_margin'),
      };
    }, [buildQuarterInstrument, buildQuarterInstrumentFromLabel, deriveFixIExecution, formatNobleDate, getPreviewHourIndex, inferQuarterCloseSide, isLetsTradeSection, isPipelineOrdersSection, parseNullableNumber, parseQuarterLabelMeta, previewDeliveryDate, previewPredictRows, splitQuarterVolumes]);
  const hasStagedIda1PreviewRows = stagedNoblePreviewRows.ida1.length > 0;
  const hasStagedIda2PreviewRows = stagedNoblePreviewRows.ida2.length > 0;
  const hasExternalIda1PreviewRows = externalIda1PreviewRows.length > 0;
  const hasExternalIda2PreviewRows = externalIda2PreviewRows.length > 0;
  const isIda1OrderPreviewReady = isIda1PredictReadyForPreview || hasStagedIda1PreviewRows || hasExternalIda1PreviewRows;
  const isIda2OrderPreviewReady = isIda2PredictReadyForPreview || hasStagedIda2PreviewRows || hasExternalIda2PreviewRows;
  const pickPreferredLatePreviewRows = useCallback((modeKey, stagedRows = [], externalRows = []) => {
    // IDA1/IDA2 runtime preview is the closer venue-ready representation.
    // Keep staged rows only as a fallback while runtime preview is still unavailable.
    if (['ida1', 'ida2'].includes(modeKey) && externalRows.length > 0) {
      return externalRows;
    }
    if (stagedRows.length > 0) {
      return stagedRows;
    }
    return externalRows;
  }, []);
  const nobleOrderPreviewRows = useMemo(() => {
    const preferredIda1Rows = isIda1OrderPreviewReady
      ? pickPreferredLatePreviewRows('ida1', stagedNoblePreviewRows.ida1, externalIda1PreviewRows)
      : [];
    const preferredIda2Rows = isIda2OrderPreviewReady
      ? pickPreferredLatePreviewRows('ida2', stagedNoblePreviewRows.ida2, externalIda2PreviewRows)
      : [];
    return {
      ...stagedNoblePreviewRows,
      fix_i: externalFixIPreviewRows,
      ida1: preferredIda1Rows,
      ida2: collapseQuarterPreviewRows(preferredIda2Rows, 'ida2'),
    };
  }, [
    collapseQuarterPreviewRows,
    externalFixIPreviewRows,
    externalIda1PreviewRows,
    externalIda2PreviewRows,
    isIda1OrderPreviewReady,
    isIda2OrderPreviewReady,
    pickPreferredLatePreviewRows,
    stagedNoblePreviewRows,
  ]);
  const activeNoblePreviewRows = nobleOrderPreviewRows[orderPreviewMode] || [];
  const noblePackageHasRows = Object.values(nobleOrderPreviewRows || {}).some((rows) => (rows || []).length > 0);
  const fixINobleOrderRows = nobleOrderPreviewRows.fix_i;
  const isActiveFixIIPreviewAwaitingIda1 = orderPreviewMode === 'fix_ii' && !isIda1OrderPreviewReady;
  const activeNoblePreviewHasReferencePrice = activeNoblePreviewRows.some((order) => (
    order.reference_price !== null
    && order.reference_price !== undefined
    && Number.isFinite(Number(order.reference_price))
  ));
  const activeNoblePreviewShowsLimitPrice = ['ida1', 'ida2'].includes(orderPreviewMode)
    || (orderPreviewMode === 'fix_i' && activeNoblePreviewHasReferencePrice);
  const getNoblePreviewSourceLabel = useCallback((order) => {
    const source = String(order?.source || '').trim().toLowerCase();
    if (source !== 'simulation_v5_post_base_hedge') {
      return '';
    }
    const guardType = String(order?.guard_type || '').trim().toLowerCase();
    const rawGuardLabel = String(order?.guard_label || '').trim();
    const guardLabel = rawGuardLabel
      || (guardType === 'spike'
        ? baseGuardCopy.spikeLabel
        : guardType === 'negative_price'
          ? 'Goose risk'
          : '');
    return guardLabel ? `Goose risk · ${guardLabel}` : 'Goose risk management';
  }, [baseGuardCopy.spikeLabel]);
  const activeNoblePreviewHasSourceLabel = activeNoblePreviewRows.some((order) => getNoblePreviewSourceLabel(order));
  const activeNoblePreviewSourceMeta = useMemo(() => {
    if (!isPipelineOrdersSection) return null;

    const sourceCopy = howItWorksLanguage === 'pl'
      ? {
          stagedLabel: 'Źródło: staged current plan',
          stagedLegend: 'Legenda: staged current plan oznacza, ze preview pochodzi bezposrednio z aktywnej sciezki symulacji Goose IDA2 dla tej daty.',
          fixIStagedBody: 'Preview Fix I w koszyku Noble używa Goose live staged orders, BASE close i hedge z risk management, tak samo jak Let’s trade.',
          fixIGooseLabel: 'Źródło: Goose live Fix I + risk management',
          fixIGooseBody: 'Te wiersze Fix I pochodza z live adaptera Goose oraz z hedge risk management naszej symulacji. Hedge uzywa limitu z kwantyli: Buy q60 + 25 / Sell q40 - 25. PRICE jest limitem Noble, a REFERENCE cena modelowa.',
          fixIGooseLegend: 'Legenda: Goose live Fix I + risk management = projekcja koszyka Fix I przed live fillami Fix I. Wiersze hedge naszej symulacji uzywaja limitu Buy q60 + 25 / Sell q40 - 25.',
          fixIIStagedBody: 'Preview Fix II pochodzi ze staged godzinowej drabinki zapisanej w aktualnym planie Goose IDA2 simulation.',
          stagedIda1Body: 'Te wiersze IDA1 pochodza ze staged path Goose IDA2 simulation. Widoczny wolumen IDA1 jest juz netowany o residual zarezerwowany dla IDA2.',
          stagedIda2Body: 'Te wiersze IDA2 pochodza ze staged path Goose IDA2 simulation i pokazuja tylko residual przekazany do IDA2.',
          stagedIda1Legend: 'Legenda: staged current plan = wiersze IDA1 są już pomniejszone o późniejszy staged handoff do IDA2.',
          stagedIda2Legend: 'Legenda: staged current plan = wiersze IDA2 pokazują downstream residual jawnie przekazany do IDA2 w aktualnej ścieżce.',
          mooseLabel: 'Źródło: Goose staged preview',
          mooseIda1Body: 'Te wiersze IDA1 pochodza z intencji runtime Goose IDA2 overlay. Wolumen preview to staged close IDA1 po odjeciu residuala zarezerwowanego juz dla IDA2.',
          mooseIda2Body: 'Te wiersze IDA2 pochodza z intencji runtime Goose IDA2 overlay. Wolumen preview to staged residual jawnie zarezerwowany do handoffu IDA2 przed aukcja.',
          mooseIda1Legend: 'Legenda: Goose staged preview = wiersze IDA1 pokazują runtime carry split, nie fallback zamykający pełny residual.',
          mooseIda2Legend: 'Legenda: Goose staged preview = wiersze IDA2 pokazują rezerwację handoff runtime, nie rekonstrukcję Goose po pełnym close IDA1.',
          bisonLabel: 'Źródło: fallback preview',
          bisonIda1Body: 'Te wiersze IDA1 pochodzą z fallback preview, bo staged quarter rows nie są jeszcze tutaj wystawione. To preview domknięcia residualu na IDA1 do momentu, gdy runtime zbuduje staged split/order IDA2. Wtedy IDA1 może zostać zastąpione przez staged/Goose IDA1 pomniejszone o handoff do IDA2, jeśli stanie się to przed bramką IDA1.',
          bisonIda2Body: 'Te wiersze IDA2 pochodzą z fallback preview, bo staged quarter rows nie są jeszcze tutaj wystawione. Fallback odbudowuje close IDA2 z quarter positions, używając najpierw splitter data, a potem zrekonstruowanych pozycji IDA1 jako backupu.',
          bisonIda1Legend: 'Legenda: fallback preview = pełne preview close IDA1, nie wolumen staged pomniejszony o ewentualny handoff do IDA2. Po złożeniu koszyka IDA1 nie aktualizujemy go późniejszą predykcją IDA2.',
          bisonIda2Legend: 'Legenda: fallback preview = wiersze IDA2 sa rekonstruowane z quarter positions zamiast staged path Goose IDA2 simulation.',
        }
      : {
          stagedLabel: 'Source: staged current plan',
          stagedLegend: 'Legend: staged current plan means the preview comes directly from the active Goose IDA2 simulation path for this date.',
          fixIStagedBody: 'Fix I Noble preview uses Goose live staged orders, BASE close rows, and risk-management hedge, the same as Let’s trade.',
          fixIGooseLabel: 'Source: Goose live Fix I + risk management',
          fixIGooseBody: 'These Fix I rows come from the Goose live adapter plus risk-management hedge rows from our simulation layer. Hedge rows use quantile limits: Buy q60 + 25 / Sell q40 - 25. PRICE is the Noble limit, REFERENCE is the model price.',
          fixIGooseLegend: 'Legend: Goose live Fix I + risk management = projected Fix I order basket before live Fix I fills. Hedge rows from our simulation layer use Buy q60 + 25 / Sell q40 - 25.',
          fixIIStagedBody: 'Fix II preview comes from the staged hourly ladder stored in the current Goose IDA2 simulation plan.',
          stagedIda1Body: 'These IDA1 rows come from the staged Goose IDA2 simulation path. The visible IDA1 volume is already net of residual reserved for IDA2.',
          stagedIda2Body: 'These IDA2 rows come from the staged Goose IDA2 simulation path and reflect only the residual volume already handed off into IDA2.',
          stagedIda1Legend: 'Legend: staged current plan = IDA1 rows are already reduced by any later staged handoff to IDA2.',
          stagedIda2Legend: 'Legend: staged current plan = IDA2 rows show only the downstream residual explicitly handed off to IDA2 in the current staged path.',
          mooseLabel: 'Source: Goose staged preview',
          mooseIda1Body: 'These IDA1 rows come from the Goose IDA2 overlay runtime intent. The preview volume is the staged IDA1 close after subtracting residual already reserved for IDA2.',
          mooseIda2Body: 'These IDA2 rows come from the Goose IDA2 overlay runtime intent. The preview volume is the staged residual explicitly reserved for handoff into IDA2 before the auction.',
          mooseIda1Legend: 'Legend: Goose staged preview = IDA1 rows reflect the runtime carry split, not a fallback that closes the full residual.',
          mooseIda2Legend: 'Legend: Goose staged preview = IDA2 rows reflect the runtime handoff reservation, not a Goose live reconstruction after full IDA1 close.',
          bisonLabel: 'Source: fallback preview',
          bisonIda1Body: 'These IDA1 rows come from fallback preview because staged quarter rows are not exposed here yet. This is an IDA1 residual-close preview until the runtime builds the staged IDA2 split/order. Then IDA1 can be replaced by staged/Goose IDA1 reduced by the IDA2 handoff, if that happens before the IDA1 gate.',
          bisonIda2Body: 'These IDA2 rows come from fallback preview because staged quarter rows are not exposed here yet. The fallback rebuilds the IDA2 close from quarter positions, using splitter data first and reconstructed IDA1 positions as a backup.',
          bisonIda1Legend: 'Legend: fallback preview = full close preview for IDA1, not staged volume reduced by a possible IDA2 handoff. Once the IDA1 basket is submitted, it is not updated by the later IDA2 prediction.',
          bisonIda2Legend: 'Legend: fallback preview = IDA2 rows are reconstructed from quarter positions instead of the staged Goose IDA2 simulation path.',
        };

    const externalRows = orderPreviewMode === 'ida1' ? externalIda1PreviewRows : externalIda2PreviewRows;
    const hasMooseRuntimePreview = ['ida1', 'ida2'].includes(orderPreviewMode)
      && externalRows.length > 0
      && externalRows.every((row) => String(row?.source || '').startsWith('simulation_v5_moose_'));

    if (orderPreviewMode === 'fix_i' && externalFixIPreviewRows.length > 0) {
      return {
        label: sourceCopy.fixIGooseLabel,
        badgeClass: 'border-sky-200 bg-sky-50 text-sky-800',
        panelClass: darkMode
          ? 'border-sky-800/70 bg-sky-950/30 text-sky-100'
          : 'border-sky-200 bg-sky-50/90 text-sky-900',
        body: sourceCopy.fixIGooseBody,
        legend: sourceCopy.fixIGooseLegend,
      };
    }

    if (orderPreviewMode === 'fix_ii') {
      return {
        label: sourceCopy.stagedLabel,
        badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        panelClass: darkMode
          ? 'border-emerald-800/70 bg-emerald-950/30 text-emerald-100'
          : 'border-emerald-200 bg-emerald-50/80 text-emerald-900',
        body: sourceCopy.fixIIStagedBody,
        legend: sourceCopy.stagedLegend,
      };
    }

    if (hasMooseRuntimePreview) {
      return {
        label: sourceCopy.mooseLabel,
        badgeClass: 'border-cyan-200 bg-cyan-50 text-cyan-800',
        panelClass: darkMode
          ? 'border-cyan-800/70 bg-cyan-950/30 text-cyan-100'
          : 'border-cyan-200 bg-cyan-50/90 text-cyan-900',
        body: orderPreviewMode === 'ida1' ? sourceCopy.mooseIda1Body : sourceCopy.mooseIda2Body,
        legend: orderPreviewMode === 'ida1' ? sourceCopy.mooseIda1Legend : sourceCopy.mooseIda2Legend,
      };
    }

    if (externalRows.length > 0) {
      return {
        label: sourceCopy.bisonLabel,
        badgeClass: 'border-amber-200 bg-amber-50 text-amber-800',
        panelClass: darkMode
          ? 'border-amber-800/70 bg-amber-950/30 text-amber-100'
          : 'border-amber-200 bg-amber-50/90 text-amber-900',
        body: orderPreviewMode === 'ida1' ? sourceCopy.bisonIda1Body : sourceCopy.bisonIda2Body,
        legend: orderPreviewMode === 'ida1' ? sourceCopy.bisonIda1Legend : sourceCopy.bisonIda2Legend,
      };
    }

    const stagedRows = stagedNoblePreviewRows[orderPreviewMode] || [];
    if (stagedRows.length > 0) {
      return {
        label: sourceCopy.stagedLabel,
        badgeClass: 'border-emerald-200 bg-emerald-50 text-emerald-800',
        panelClass: darkMode
          ? 'border-emerald-800/70 bg-emerald-950/30 text-emerald-100'
          : 'border-emerald-200 bg-emerald-50/80 text-emerald-900',
        body: orderPreviewMode === 'ida1' ? sourceCopy.stagedIda1Body : sourceCopy.stagedIda2Body,
        legend: orderPreviewMode === 'ida1' ? sourceCopy.stagedIda1Legend : sourceCopy.stagedIda2Legend,
      };
    }

    return null;
  }, [
    darkMode,
    externalFixIPreviewRows,
    externalIda1PreviewRows,
    externalIda2PreviewRows,
    howItWorksLanguage,
    isPipelineOrdersSection,
    orderPreviewMode,
    stagedNoblePreviewRows,
  ]);
  const stagedCloseVolumes = useMemo(() => ({
    ida1: previewPredictRows.reduce((acc, row) => acc + Number(row?.closed_volume_ida1 || 0), 0),
    ida2: previewPredictRows.reduce((acc, row) => acc + Number(row?.closed_volume_ida2 || 0), 0),
  }), [previewPredictRows]);
  const selectedDateResultRow = useMemo(
    () => (resultsData?.daily || []).find((row) => String(row?.date || '').slice(0, 10) === date) || null,
    [date, resultsData],
  );
  const activeNoblePreviewEmptyMessage = (() => {
    const isPl = howItWorksLanguage === 'pl';
    if (orderPreviewMode === 'fix_i') {
      if (externalFixIPreviewRows.length > 0) {
        return isPl
          ? 'Preview Fix I jest załadowane z tego samego koszyka Noble co Let’s trade: Goose live Fix I + risk management.'
          : 'Fix I preview is loaded from the same Noble basket as Let’s trade: Goose live Fix I + risk management.';
      }
      if (!activePredictMeta?.market_readiness?.fix_i?.ready) {
        return isPl
          ? 'Preview Fix I jest puste, bo nie udało się jeszcze zbudować koszyka Noble z Goose live Fix I + risk management.'
          : 'Fix I preview is empty because the Noble basket from Goose live Fix I + risk management is not available yet.';
      }
      return isPl
        ? 'Fix I jest ready, ale koszyk Noble z Goose live Fix I + risk management nie zwrócił wierszy dla tej daty.'
        : 'Fix I is ready, but the Noble basket from Goose live Fix I + risk management returned no rows for this date.';
    }
    if (orderPreviewMode === 'ida2' && isIda2OrderPreviewReady) {
      if (stagedCloseVolumes.ida2 > 0) {
        return isPl
          ? `Predykcja IDA2 jest gotowa i aktualny staged plan przenosi ${formatNumber(stagedCloseVolumes.ida2)} MWh do IDA2, ale dla tej daty nie zbudowano quarter-hour preview rows.`
          : `IDA2 prediction is ready and the current staged plan carries ${formatNumber(stagedCloseVolumes.ida2)} MWh into IDA2, but no quarter-hour preview rows were built for this date.`;
      }
      return isPl
        ? 'Predykcja IDA2 jest gotowa, ale aktualny staged plan przenosi 0 MWh do IDA2 dla tej daty, więc nie ma orderów IDA2 do pokazania.'
        : 'IDA2 prediction is ready, but the current staged plan carries 0 MWh into IDA2 for this date, so there are no IDA2 orders to show.';
    }
    if (orderPreviewMode === 'ida1' && isIda1OrderPreviewReady) {
      if (stagedCloseVolumes.ida1 <= 0) {
        if (selectedDateResultRow?.is_manual_overwrite) {
          return isPl
            ? 'Predykcja IDA1 jest gotowa, ale aktualny manual override / base path nie zostawia widocznego staged residual volume dla IDA1. Fallback preview ładuje się automatycznie, gdy dostępne są aktualne pliki wykonania.'
            : 'IDA1 prediction is ready, but the current manual override / base path does not leave visible staged residual volume for IDA1 on this date. A fallback preview is loaded automatically when current execution files are available.';
        }
        return isPl
          ? 'Predykcja IDA1 jest gotowa, ale aktualny staged plan przenosi 0 MWh do IDA1 dla tej daty, więc nie ma orderów IDA1 do pokazania.'
          : 'IDA1 prediction is ready, but the current staged plan carries 0 MWh into IDA1 for this date, so there are no IDA1 orders to show.';
      }
      return isPl
        ? `Predykcja IDA1 jest gotowa, a aktualny staged plan ma ${formatNumber(stagedCloseVolumes.ida1)} MWh zaplanowane dla IDA1 close rows. Jeśli quarter-hour Noble rows nadal tu brakuje, fallback preview ładuje się automatycznie, gdy jest dostępny.`
        : `IDA1 prediction is ready, but the current staged plan has ${formatNumber(stagedCloseVolumes.ida1)} MWh scheduled for IDA1 close rows. If quarter-hour Noble rows are still missing here, the fallback preview is loaded automatically when available.`;
    }
    return isPl
      ? 'Brak preview rows dla tej nogi w aktualnym staged planie. Jeśli to możliwe, automatycznie wybrano inną nogę z dostępnymi staged orders.'
      : 'No preview rows for this leg in the current staged plan. Another leg with available staged orders has been selected automatically when possible.';
  })();
  const preferredNoblePreviewMode = useMemo(() => (
    externalFixIPreviewRows.length > 0 || externalFixIPreviewLoading
      ? 'fix_i'
      : (['fix_i', 'fix_ii', 'ida1', 'ida2'].find((modeKey) => (nobleOrderPreviewRows[modeKey] || []).length > 0) || null)
  ), [externalFixIPreviewLoading, externalFixIPreviewRows.length, nobleOrderPreviewRows]);

  useEffect(() => {
    hasUserSelectedOrderPreviewModeRef.current = false;
    setOrderPreviewMode('fix_i');
    resetExternalNoblePreview(null, { clearCache: true });
  }, [date, resetExternalNoblePreview]);

  useEffect(() => {
    if (!isPipelineOrdersSection) {
      return;
    }
    if (hasUserSelectedOrderPreviewModeRef.current) {
      return;
    }
    if (!preferredNoblePreviewMode) {
      return;
    }
    if (preferredNoblePreviewMode !== orderPreviewMode) {
      setOrderPreviewMode(preferredNoblePreviewMode);
    }
  }, [isPipelineOrdersSection, orderPreviewMode, preferredNoblePreviewMode]);

  const handleOrderPreviewModeSelect = useCallback((modeKey) => {
    hasUserSelectedOrderPreviewModeRef.current = true;
    setOrderPreviewMode(modeKey);
    if (modeKey === 'fix_i' && externalFixIPreviewRows.length === 0 && !externalFixIPreviewLoading) {
      loadNoblePreview({ mode: 'fix_i', force: true }).catch((err) => {
        console.error(err);
      });
    }
    if (modeKey === 'ida1' && externalIda1PreviewRows.length === 0 && !externalIda1PreviewLoading) {
      loadNoblePreview({ mode: 'ida1', force: true }).catch((err) => {
        console.error(err);
      });
    }
    if (modeKey === 'ida2' && externalIda2PreviewRows.length === 0 && !externalIda2PreviewLoading) {
      loadNoblePreview({ mode: 'ida2', force: true }).catch((err) => {
        console.error(err);
      });
    }
  }, [
    externalFixIPreviewLoading,
    externalFixIPreviewRows.length,
    externalIda1PreviewLoading,
    externalIda1PreviewRows.length,
    externalIda2PreviewLoading,
    externalIda2PreviewRows.length,
    loadNoblePreview,
  ]);

  useEffect(() => {
    if (!isPipelineOrdersSection) {
      return;
    }
    if (externalFixIPreviewFailedKey === getNoblePreviewCacheKey('fix_i')) {
      return;
    }
    if (externalFixIPreviewRows.length > 0 || externalFixIPreviewLoading) {
      return;
    }

    loadNoblePreview({ mode: 'fix_i' }).catch((err) => {
      console.error(err);
    });
  }, [
    externalFixIPreviewFailedKey,
    externalFixIPreviewLoading,
    externalFixIPreviewRows.length,
    getNoblePreviewCacheKey,
    isPipelineOrdersSection,
    loadNoblePreview,
  ]);

  useEffect(() => {
    if (!isPipelineOrdersSection) {
      return;
    }
    if (orderPreviewFormat !== 'bos' && orderPreviewMode !== 'ida1') {
      return;
    }
    const hasPredictPlanContext = (
      activePredictRows.length > 0
      || Boolean(activePredictMeta?.latest_issue_date)
      || activeFix2IssueDates.length > 0
    );
    if (!hasPredictPlanContext) {
      return;
    }
    if (externalIda1PreviewFailedKey === getNoblePreviewCacheKey('ida1')) {
      return;
    }
    if (externalIda1PreviewRows.length > 0 || externalIda1PreviewLoading) {
      return;
    }

    loadNoblePreview({ mode: 'ida1' }).catch((err) => {
      console.error(err);
    });
  }, [
    activeFix2IssueDates.length,
    activePredictMeta?.latest_issue_date,
    activePredictRows.length,
    externalIda1PreviewLoading,
    externalIda1PreviewFailedKey,
    externalIda1PreviewRows.length,
    getNoblePreviewCacheKey,
    isPipelineOrdersSection,
    loadNoblePreview,
    orderPreviewFormat,
    orderPreviewMode,
  ]);

  useEffect(() => {
    if (!isPipelineOrdersSection) {
      return;
    }
    if (orderPreviewFormat !== 'bos' && orderPreviewMode !== 'ida2') {
      return;
    }
    const hasPredictPlanContext = (
      activePredictRows.length > 0
      || Boolean(activePredictMeta?.latest_issue_date)
      || activeFix2IssueDates.length > 0
    );
    if (!hasPredictPlanContext) {
      return;
    }
    if (externalIda2PreviewFailedKey === getNoblePreviewCacheKey('ida2')) {
      return;
    }
    if (externalIda2PreviewRows.length > 0 || externalIda2PreviewLoading) {
      return;
    }

    loadNoblePreview({ mode: 'ida2' }).catch((err) => {
      console.error(err);
    });
  }, [
    activeFix2IssueDates.length,
    activePredictMeta?.latest_issue_date,
    activePredictRows.length,
    externalIda2PreviewFailedKey,
    externalIda2PreviewLoading,
    externalIda2PreviewRows.length,
    getNoblePreviewCacheKey,
    isPipelineOrdersSection,
    loadNoblePreview,
    orderPreviewFormat,
    orderPreviewMode,
  ]);

  const handleRetryExternalIda1Preview = useCallback(() => {
    resetExternalNoblePreview('ida1', { clearCache: true });
    loadNoblePreview({ mode: 'ida1', force: true }).catch((err) => {
      console.error(err);
    });
  }, [loadNoblePreview, resetExternalNoblePreview]);

  const handleRetryExternalFixIPreview = useCallback(() => {
    resetExternalNoblePreview('fix_i', { clearCache: true });
    loadNoblePreview({ mode: 'fix_i', force: true }).catch((err) => {
      console.error(err);
    });
  }, [loadNoblePreview, resetExternalNoblePreview]);

  const handleRetryExternalIda2Preview = useCallback(() => {
    resetExternalNoblePreview('ida2', { clearCache: true });
    loadNoblePreview({ mode: 'ida2', force: true }).catch((err) => {
      console.error(err);
    });
  }, [loadNoblePreview, resetExternalNoblePreview]);

  useEffect(() => {
    if (!isLetsTradeSection) {
      return;
    }
    if (letsTradeOrderPlanDate !== letsTradeDeliveryDate && !letsTradeOrderPlanLoading) {
      loadLetsTradeOrderPlan({ targetDate: letsTradeDeliveryDate }).catch((err) => {
        console.error(err);
      });
    }
    if (externalFixIPreviewRows.length > 0 || externalFixIPreviewLoading) {
      return;
    }
    if (externalFixIPreviewFailedKey === getNoblePreviewCacheKey('fix_i', letsTradeDeliveryDate)) {
      return;
    }
    loadNoblePreview({ mode: 'fix_i', targetDate: letsTradeDeliveryDate }).catch((err) => {
      console.error(err);
    });
  }, [externalFixIPreviewFailedKey, externalFixIPreviewLoading, externalFixIPreviewRows.length, getNoblePreviewCacheKey, isLetsTradeSection, letsTradeDeliveryDate, letsTradeOrderPlanDate, letsTradeOrderPlanLoading, loadLetsTradeOrderPlan, loadNoblePreview]);

  useEffect(() => {
    if (!isLetsTradeSection) {
      return;
    }
    if (orderPreviewMode !== 'ida1') {
      return;
    }
    const hasLetsTradePlanContext = (
      letsTradeOrderPlanDate === letsTradeDeliveryDate
      && (
        letsTradeOrderPlan.length > 0
        || Boolean(letsTradeOrderPlanMeta?.latest_issue_date)
        || Boolean(letsTradeOrderPlanMeta?.market_readiness?.fix_ii?.ready)
      )
    );
    if (!hasLetsTradePlanContext) {
      return;
    }
    if (externalIda1PreviewFailedKey === getNoblePreviewCacheKey('ida1', letsTradeDeliveryDate)) {
      return;
    }
    if (externalIda1PreviewRows.length > 0 || externalIda1PreviewLoading) {
      return;
    }
    loadNoblePreview({ mode: 'ida1', targetDate: letsTradeDeliveryDate }).catch((err) => {
      console.error(err);
    });
  }, [
    externalIda1PreviewFailedKey,
    externalIda1PreviewLoading,
    externalIda1PreviewRows.length,
    getNoblePreviewCacheKey,
    isLetsTradeSection,
    letsTradeDeliveryDate,
    letsTradeOrderPlan.length,
    letsTradeOrderPlanDate,
    letsTradeOrderPlanMeta?.latest_issue_date,
    letsTradeOrderPlanMeta?.market_readiness?.fix_ii?.ready,
    loadNoblePreview,
    orderPreviewMode,
  ]);

  useEffect(() => {
    if (!isLetsTradeSection) {
      return;
    }
    if (orderPreviewMode !== 'ida2') {
      return;
    }
    const hasLetsTradePlanContext = (
      letsTradeOrderPlanDate === letsTradeDeliveryDate
      && (
        letsTradeOrderPlan.length > 0
        || Boolean(letsTradeOrderPlanMeta?.latest_issue_date)
        || Boolean(letsTradeOrderPlanMeta?.market_readiness?.fix_ii?.ready)
      )
    );
    if (!hasLetsTradePlanContext) {
      return;
    }
    if (externalIda2PreviewFailedKey === getNoblePreviewCacheKey('ida2', letsTradeDeliveryDate)) {
      return;
    }
    if (externalIda2PreviewRows.length > 0 || externalIda2PreviewLoading) {
      return;
    }
    loadNoblePreview({ mode: 'ida2', targetDate: letsTradeDeliveryDate }).catch((err) => {
      console.error(err);
    });
  }, [
    externalIda2PreviewFailedKey,
    externalIda2PreviewLoading,
    externalIda2PreviewRows.length,
    getNoblePreviewCacheKey,
    isLetsTradeSection,
    letsTradeDeliveryDate,
    letsTradeOrderPlan.length,
    letsTradeOrderPlanDate,
    letsTradeOrderPlanMeta?.latest_issue_date,
    letsTradeOrderPlanMeta?.market_readiness?.fix_ii?.ready,
    loadNoblePreview,
    orderPreviewMode,
  ]);

  const exportNoblePreviewXlsx = async () => {
    const exportDate = isLetsTradeSection ? letsTradeDeliveryDate : date;
    const nobleSections = [
      {
        key: 'fix_i',
        sheetName: 'Fix I',
        title: 'Fix I Noble basket',
        description: 'Goose live Fix I + BASE close + risk-management hedge',
        deadline: '10:30',
        rows: nobleOrderPreviewRows.fix_i || [],
      },
      {
        key: 'fix_ii',
        sheetName: 'Fix II',
        title: 'Fix II Noble basket',
        description: 'Drabinka orderów Fix II',
        deadline: '12:00',
        rows: nobleOrderPreviewRows.fix_ii || [],
      },
      {
        key: 'ida1',
        sheetName: 'IDA1',
        title: 'IDA1 Noble basket',
        description: 'Quarter close rows z runtime preview',
        deadline: '15:00',
        rows: nobleOrderPreviewRows.ida1 || [],
      },
      {
        key: 'ida2',
        sheetName: 'IDA2',
        title: 'IDA2 Noble basket',
        description: 'Quarter close rows z runtime preview',
        deadline: '22:00',
        rows: nobleOrderPreviewRows.ida2 || [],
      },
    ];
    if (!exportDate || !nobleSections.some((section) => section.rows.length > 0)) {
      return;
    }

    const XLSX = await loadStyledXlsx();
    const wb = XLSX.utils.book_new();
    const usedSheetNames = new Set();
    XLSX.utils.book_append_sheet(
      wb,
      createSummarySheet(
        XLSX,
        `Noble order package — delivery ${exportDate}`,
        'Każdy etap aukcyjny jest w osobnej zakładce. Kolor wiersza: K = kupno, S = sprzedaż.',
        nobleSections.map((section) => ({
          sheetName: section.sheetName,
          description: section.description,
          deadline: section.deadline,
          rowCount: section.rows.length,
        })),
      ),
      sanitizeXlsxSheetName('README', usedSheetNames),
    );
    nobleSections.forEach((section) => {
      const header = ['Instrument', 'Side', 'Limit price', 'Volume'];
      const rows = section.rows.map((order) => [
        order.instrument || '',
        order.side || '',
        Number.isFinite(Number(order.price)) ? Number(order.price) : '',
        Number.isFinite(Number(order.volume)) ? Number(Number(order.volume).toFixed(1)) : '',
      ]);
      const wsData = [
        [section.title],
        [`${section.description} · deadline ${section.deadline}`],
        header,
        ...(rows.length ? rows : [['-', '-', '', '']]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [
        { wch: 26 },
        { wch: 8 },
        { wch: 13 },
        { wch: 11 },
      ];
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 3 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 3 } },
      ];
      applyOrderSheetPolish(XLSX, ws, {
        title: section.title,
        subtitle: section.description,
        headerRow: 3,
        dataStartRow: 4,
        rowCount: Math.max(1, rows.length),
        colCount: header.length,
        sideColIndex: 1,
        numberColumnIndexes: [2],
        volumeColumnIndexes: [3],
      });
      XLSX.utils.book_append_sheet(wb, ws, sanitizeXlsxSheetName(section.sheetName, usedSheetNames));
    });
    XLSX.writeFile(wb, `noble-preview-${exportDate}.xlsx`);
  };

  const bosLadderData = useMemo(() => {
    const exportDate = isLetsTradeSection ? letsTradeDeliveryDate : date;
    const fixIOrderRows = fixINobleOrderRows
      .map((order, index) => ({
        id: `${order.instrument || 'fix-i'}-${order.side || ''}-${order.price || ''}-${index}`,
        instrument: order.instrument || '',
        side: order.side || '',
        price: Number.isFinite(Number(order.price)) ? Number(order.price) : null,
        volume: Number.isFinite(Number(order.volume)) ? Number(order.volume) : null,
        hour: Number.isFinite(Number(order.hour)) ? Number(order.hour) + 1 : null,
        source: getNoblePreviewSourceLabel(order) || order.source || '',
      }))
      .filter((order) => order.instrument && order.side && order.volume !== null && Math.abs(order.volume) > 0.0001)
      .sort((a, b) => {
        const hourDelta = Number(a.hour || 0) - Number(b.hour || 0);
        if (hourDelta !== 0) return hourDelta;
        const sideDelta = String(a.side).localeCompare(String(b.side));
        if (sideDelta !== 0) return sideDelta;
        return Number(a.price || 0) - Number(b.price || 0);
      });
    const buildBosOrderRows = (orders = [], stageKey = 'orders') => orders
      .map((order, index) => ({
        id: `${order.instrument || stageKey}-${order.side || ''}-${order.price || ''}-${index}`,
        instrument: order.instrument || '',
        side: order.side || '',
        price: Number.isFinite(Number(order.price)) ? Number(order.price) : null,
        volume: Number.isFinite(Number(order.volume)) ? Number(order.volume) : null,
        hour: Number.isFinite(Number(order.hour)) ? Number(order.hour) + 1 : null,
        qh: Number.isFinite(Number(order.qh)) ? Number(order.qh) : null,
        source: getNoblePreviewSourceLabel(order) || order.source || '',
      }))
      .filter((order) => order.instrument && order.side && order.volume !== null && Math.abs(order.volume) > 0.0001)
      .sort((a, b) => {
        const hourDelta = Number(a.hour || 0) - Number(b.hour || 0);
        if (hourDelta !== 0) return hourDelta;
        const qhDelta = Number(a.qh ?? -1) - Number(b.qh ?? -1);
        if (qhDelta !== 0) return qhDelta;
        const instrumentDelta = String(a.instrument).localeCompare(String(b.instrument));
        if (instrumentDelta !== 0) return instrumentDelta;
        const sideDelta = String(a.side).localeCompare(String(b.side));
        if (sideDelta !== 0) return sideDelta;
        return Number(a.price || 0) - Number(b.price || 0);
      });
    const buildBosFixIILadderRows = (orders = []) => {
      const asFinite = (value) => {
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? numberValue : null;
      };
      const groupedByHour = new Map();
      orders.forEach((order) => {
        const hourIndex = Number.isFinite(Number(order?.hour))
          ? Number(order.hour)
          : null;
        if (hourIndex === null || hourIndex < 0 || hourIndex > 23) {
          return;
        }
        const side = String(order?.side || '');
        const price = asFinite(order?.price);
        const volume = asFinite(order?.volume);
        if (!['K', 'S'].includes(side) || !Number.isFinite(price) || !Number.isFinite(volume) || volume <= 0.0001) {
          return;
        }
        const bucket = groupedByHour.get(hourIndex) || { buy: [], sell: [] };
        if (side === 'K') {
          bucket.buy.push({ price, volume });
        } else {
          bucket.sell.push({ price, volume });
        }
        groupedByHour.set(hourIndex, bucket);
      });
      return Array.from({ length: 24 }, (_, hourIndex) => {
        const bucket = groupedByHour.get(hourIndex) || { buy: [], sell: [] };
        const buyOrders = [...bucket.buy].sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
        const sellOrders = [...bucket.sell].sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
        const buckets = [0, 1, 2].map((index) => ({
          qK: buyOrders[index]?.volume ?? null,
          lK: buyOrders[index]?.price ?? null,
          lS: sellOrders[index]?.price ?? null,
          qS: sellOrders[index]?.volume ?? null,
        }));
        return { hour: hourIndex + 1, buckets };
      });
    };
    const ida1OrderRows = buildBosOrderRows(nobleOrderPreviewRows.ida1, 'ida1');
    const ida2OrderRows = buildBosOrderRows(nobleOrderPreviewRows.ida2, 'ida2');
    if ((!previewPredictRows.length && !fixIOrderRows.length && !ida1OrderRows.length && !ida2OrderRows.length) || !exportDate) {
      return null;
    }
    const byHour = {};
    previewPredictRows.forEach((r) => {
      const h = getPreviewHourIndex(r);
      if (Number.isFinite(h) && h >= 0 && h <= 23) {
        byHour[h] = r;
      }
    });
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const pickAction = (value) => {
      const norm = String(value || '').trim().toLowerCase();
      return norm === 'buy' || norm === 'sell' ? norm : null;
    };
    const finiteOrNull = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const buildKs = ({ action, volume, price }) => {
      const act = pickAction(action);
      const vol = Number(volume || 0);
      const pr = finiteOrNull(price);
      if (!act || !(Math.abs(vol) > 0.0001)) {
        return { qK: null, lK: null, lS: null, qS: null };
      }
      if (act === 'buy') {
        return { qK: vol, lK: pr, lS: null, qS: null };
      }
      return { qK: null, lK: null, lS: pr, qS: vol };
    };

    const baseSetupMeta = basePretradeDecision?.base_setup || {};
    const exportDayDetails = dayDetailsByDate?.[exportDate] || null;
    const baseContext = exportDayDetails?.summary?.base_context || null;
    const fallbackBaseAction = pickAction(
      baseSetupMeta.base_action
      || basePretradeDecision?.model_action
      || baseContext?.base_action
    );
    const totalBaseVolume = Number(
      baseSetupMeta.volume
      || baseContext?.daily_volume_mwh
      || 0
    );
    const fallbackBaseVolume = Number(
      baseSetupMeta.base_block_volume_mw
      ?? basePretradeDecision?.base_block_volume_mw
      ?? baseContext?.volume
      ?? (totalBaseVolume > 0 ? totalBaseVolume / 24 : 0)
    );
    const fallbackBasePrice = Number(
      baseSetupMeta.runtime_base_price
      ?? baseSetupMeta.base_price
      ?? basePretradeDecision?.base_price
      ?? baseContext?.runtime_base_price
      ?? baseContext?.stored_override_base_price
    );

    const buildSimple = (key, tabLabel, title, helper, deadline, mapper) => {
      const rows = hours.map((h) => ({ hour: h + 1, ...mapper(byHour[h] || {}, h) }));
      const activeRowsCount = rows.filter((r) => r.qK !== null || r.qS !== null).length;
      return { key, tabLabel, kind: 'simple', title, helper, deadline, rows, activeRowsCount };
    };

    const sections = [
      buildSimple('base_setup', 'BASE context', 'BASE context', 'Setup BASE OTC', '-', (r) => {
        const action = pickAction(r.base_action) || fallbackBaseAction;
        const volume = Number(r.base_volume_mw || 0) || fallbackBaseVolume;
        const price = Number.isFinite(Number(r.base_price)) ? Number(r.base_price) : fallbackBasePrice;
        return buildKs({ action, volume, price });
      }),
      {
        key: 'fix_i_orders',
        tabLabel: 'Fix I',
        kind: 'orders',
        title: 'Fix I order basket',
        helper: 'Goose live Fix I + risk-management hedge',
        deadline: '10:30',
        rows: fixIOrderRows,
        activeRowsCount: fixIOrderRows.length,
      },
      (() => {
        const rows = buildBosFixIILadderRows(nobleOrderPreviewRows.fix_ii || []);
        const activeRowsCount = rows.filter((r) => r.buckets.some((b) => b.qK !== null || b.qS !== null)).length;
        return {
          key: 'fix_ii_ladder',
          tabLabel: 'Fix II intent',
          kind: 'ladder',
          title: 'Fix II 3-bucket intent (model input)',
          helper: 'Modelowy szkic orderów. Finalny koszyk Noble/SAPRI to zoptymalizowany matrix Fix II z osobnej zakładki w eksporcie BOŚ/Noble.',
          deadline: '12:00',
          rows,
          activeRowsCount,
        };
      })(),
      buildSimple('base_close_fix_ii', 'BASE → Fix II', 'BASE close on Fix II', 'Close BASE na Fix II', '12:00', (r) => {
        const action = pickAction(r.base_fix_ii_action);
        const volume = Number(r.base_fix_ii_volume || 0);
        const price = finiteOrNull(r.fix_ii_price);
        return buildKs({ action, volume, price });
      }),
      {
        key: 'ida1_close',
        tabLabel: 'IDA1',
        kind: 'orders',
        title: 'IDA1 close (probabilistic, excl. BASE)',
        helper: 'Quarter rows z runtime preview',
        deadline: '15:00',
        rows: ida1OrderRows,
        activeRowsCount: ida1OrderRows.length,
      },
      buildSimple('base_close_ida1', 'BASE → IDA1', 'BASE close on IDA1', 'Close BASE na IDA1', '15:00', (r) => {
        const action = pickAction(r.base_ida1_action);
        const volume = Number(r.base_ida1_volume || 0);
        const previewPrice = finiteOrNull(r.ida1_preview_price);
        const price = previewPrice ?? finiteOrNull(r.ida1_price);
        return buildKs({ action, volume, price });
      }),
      {
        key: 'ida2_close',
        tabLabel: 'IDA2',
        kind: 'orders',
        title: 'IDA2 close (probabilistic, excl. BASE)',
        helper: 'Quarter rows z runtime preview',
        deadline: '22:00',
        rows: ida2OrderRows,
        activeRowsCount: ida2OrderRows.length,
      },
      buildSimple('base_close_ida2', 'BASE → IDA2', 'BASE close on IDA2', 'Close BASE na IDA2', '22:00', (r) => {
        const action = pickAction(r.base_ida2_action);
        const volume = Number(r.base_ida2_volume || 0);
        const previewPrice = finiteOrNull(r.ida2_preview_price);
        const price = previewPrice ?? finiteOrNull(r.ida2_price);
        return buildKs({ action, volume, price });
      }),
    ];

    return { exportDate, sections };
  }, [
    previewPredictRows,
    fixINobleOrderRows,
    nobleOrderPreviewRows,
    basePretradeDecision,
    dayDetailsByDate,
    isLetsTradeSection,
    letsTradeDeliveryDate,
    date,
    getPreviewHourIndex,
    getNoblePreviewSourceLabel,
  ]);
  const bosLadderHasRows = Boolean(
    bosLadderData?.sections?.some((section) => Number(section?.activeRowsCount || 0) > 0)
  );

  const exportBosLadderXlsx = async () => {
    if (!bosLadderData || bosXlsxDownloading) return;
    const { exportDate, sections } = bosLadderData;
    if (!exportDate) return;
    setBosXlsxDownloading(true);
    setError('');
    setActionMessage('');
    try {
      const XLSX = await loadStyledXlsx();
      const wb = XLSX.utils.book_new();
      const usedSheetNames = new Set();
      XLSX.utils.book_append_sheet(
        wb,
        createSummarySheet(
          XLSX,
          `BOŚ order package — delivery ${exportDate}`,
          'Każda tabela BOŚ jest w osobnej zakładce. Kolumny K są zielone, kolumny S czerwone.',
          sections.map((section) => ({
            sheetName: section.tabLabel,
            description: section.helper || section.title,
            deadline: section.deadline,
            rowCount: section.activeRowsCount,
          })),
        ),
        sanitizeXlsxSheetName('README', usedSheetNames),
      );
      sections.forEach((sec) => {
        let sheet = [
          [sec.title],
          [`${sec.helper || ''}${sec.deadline && sec.deadline !== '-' ? ` · deadline ${sec.deadline}` : ''}`],
        ];
        let colCount = 5;
        let polishOptions = {};
        if (sec.kind === 'simple') {
          sheet.push(['Hour', 'Ilość K', 'Limit K', 'Limit S', 'Ilość S']);
          sec.rows.forEach((row) => sheet.push([
            row.hour,
            row.qK ?? '', row.lK ?? '', row.lS ?? '', row.qS ?? '',
          ]));
          colCount = 5;
          polishOptions = {
            activeRowPredicate: (rowIndex) => {
              const row = sec.rows[rowIndex] || {};
              return row.qK !== null || row.qS !== null;
            },
            buyColumnIndexes: [1, 2],
            sellColumnIndexes: [3, 4],
            numberColumnIndexes: [2, 3],
            volumeColumnIndexes: [1, 4],
          };
        } else if (sec.kind === 'orders') {
          sheet.push(['Instrument', 'K/S', 'Cena', 'Wolumen']);
          const rows = sec.rows.length ? sec.rows : [{
            instrument: '-',
            side: '-',
            price: '',
            volume: '',
          }];
          rows.forEach((row) => sheet.push([
            row.instrument,
            row.side,
            row.price ?? '',
            row.volume ?? '',
          ]));
          colCount = 4;
          polishOptions = {
            sideColIndex: 1,
            numberColumnIndexes: [2],
            volumeColumnIndexes: [3],
          };
        } else if (sec.kind === 'ladder') {
          sheet.push([
            'Hour',
            'B1 Ilość K', 'B1 Limit K', 'B1 Limit S', 'B1 Ilość S',
            'B2 Ilość K', 'B2 Limit K', 'B2 Limit S', 'B2 Ilość S',
            'B3 Ilość K', 'B3 Limit K', 'B3 Limit S', 'B3 Ilość S',
          ]);
          sec.rows.forEach((row) => {
            const cells = [row.hour];
            row.buckets.forEach((b) => cells.push(b.qK ?? '', b.lK ?? '', b.lS ?? '', b.qS ?? ''));
            sheet.push(cells);
          });
          colCount = 13;
          polishOptions = {
            activeRowPredicate: (rowIndex) => {
              const row = sec.rows[rowIndex] || {};
              return (row.buckets || []).some((bucket) => bucket.qK !== null || bucket.qS !== null);
            },
            buyColumnIndexes: [1, 2, 5, 6, 9, 10],
            sellColumnIndexes: [3, 4, 7, 8, 11, 12],
            numberColumnIndexes: [2, 3, 6, 7, 10, 11],
            volumeColumnIndexes: [1, 4, 5, 8, 9, 12],
          };
        }
        const ws = XLSX.utils.aoa_to_sheet(sheet);
        ws['!cols'] = sec.kind === 'orders'
          ? [{ wch: 28 }, { wch: 7 }, { wch: 12 }, { wch: 11 }]
          : sec.kind === 'ladder'
            ? [{ wch: 7 }, ...Array.from({ length: 12 }, (_, idx) => ({ wch: idx % 2 === 0 ? 11 : 12 }))]
            : [{ wch: 7 }, { wch: 11 }, { wch: 12 }, { wch: 12 }, { wch: 11 }];
        ws['!merges'] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
          { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
        ];
        applyOrderSheetPolish(XLSX, ws, {
          title: sec.title,
          subtitle: sec.helper,
          headerRow: 3,
          dataStartRow: 4,
          rowCount: Math.max(1, sheet.length - 3),
          colCount,
          ...polishOptions,
        });
        XLSX.utils.book_append_sheet(wb, ws, sanitizeXlsxSheetName(sec.tabLabel, usedSheetNames));
      });

      const query = new URLSearchParams({ date: exportDate, force_refresh: 'true' });
      const response = await fetchGet(`/v5-sim/fix2-matrix?${query.toString()}`, {
        timeout: NOBLE_PREVIEW_TIMEOUT_MS,
      });
      const payload = await parseJsonResponse(response, 'V5 Fix II matrix');
      if (payload.status === 'success' && payload.file_data) {
        const byteChars = window.atob(payload.file_data);
        const byteNumbers = new Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i += 1) {
          byteNumbers[i] = byteChars.charCodeAt(i);
        }
        const matrixWorkbook = XLSX.read(new Uint8Array(byteNumbers), { type: 'array' });
        const matrixSheetName = matrixWorkbook.SheetNames[0];
        if (matrixSheetName && matrixWorkbook.Sheets[matrixSheetName]) {
          const matrixSheet = matrixWorkbook.Sheets[matrixSheetName];
          applyFix2MatrixSheetPolish(XLSX, matrixSheet);
          XLSX.utils.book_append_sheet(wb, matrixSheet, sanitizeXlsxSheetName('Fix_II_Matrix', usedSheetNames));
        }
      } else {
        const matrixNote = XLSX.utils.aoa_to_sheet([
          ['Fix II matrix unavailable'],
          [payload.message || 'No optimized Fix II matrix was returned for this delivery date.'],
        ]);
        matrixNote['!cols'] = [{ wch: 28 }, { wch: 80 }];
        styleXlsxRange(XLSX, matrixNote, 'A1:B1', XLSX_ORDER_THEME.title);
        XLSX.utils.book_append_sheet(wb, matrixNote, sanitizeXlsxSheetName('Fix_II_Matrix', usedSheetNames));
      }

      XLSX.writeFile(wb, `v5-bos-${exportDate}.xlsx`);
      setActionMessage(`BOŚ XLSX ready for ${exportDate}.`);
    } catch (err) {
      setError(String(err?.message || 'Failed to download BOŚ XLSX'));
    } finally {
      setBosXlsxDownloading(false);
    }
  };

  const renderBosLadderPreview = ({ forceExpand = false } = {}) => {
    if (!bosLadderData) {
      return (
        <div className={`px-3 py-4 text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
          —
        </div>
      );
    }
    const formatCell = (v) => (v === null || v === undefined ? '-' : formatNumber(v));
    const thBase = `px-2 py-1.5 text-left text-[11px] font-semibold uppercase ${darkMode ? 'text-slate-400' : 'text-gray-500'}`;
    const tdBase = `px-2 py-1.5 tabular-nums ${darkMode ? 'text-slate-100' : 'text-gray-900'}`;
    const activeSection = bosLadderData.sections.find((s) => s.key === bosSectionKey) || bosLadderData.sections[0];
    const baseDeadlineLabel = (simCopy?.letsTrade?.deadlineLabel) || 'Noble do';
    const deadlineLabel = baseDeadlineLabel.replace(/Noble/i, 'BOŚ');
    return (
      <div className="space-y-3">
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
          {bosLadderData.sections.map((sec) => {
            const active = activeSection.key === sec.key;
            const muted = !active && sec.activeRowsCount === 0;
            const hasDeadline = sec.deadline && sec.deadline !== '-';
            return (
              <button
                key={sec.key}
                type="button"
                onClick={() => {
                  if (forceExpand) {
                    setBosSectionKey(sec.key);
                  } else {
                    handleBosSectionClick(sec.key);
                  }
                }}
                className={`rounded-lg border p-3 text-left transition ${
                  active
                    ? (darkMode ? 'border-emerald-400 bg-emerald-500/20 text-emerald-50' : 'border-emerald-500 bg-emerald-50 text-emerald-900')
                    : muted
                      ? (darkMode ? 'border-slate-800 bg-slate-950/50 text-slate-500 opacity-70 hover:bg-slate-900/70' : 'border-gray-200 bg-gray-100 text-gray-500 opacity-70 hover:bg-gray-50')
                      : (darkMode ? 'border-slate-700 bg-slate-950/70 text-slate-200 hover:bg-slate-900' : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-white')
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-bold">{sec.tabLabel}</span>
                  <span className="text-xs font-semibold tabular-nums">{sec.activeRowsCount} rows</span>
                </div>
                <div className={`mt-1 text-[11px] ${active ? '' : darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{sec.helper}{active && !forceExpand ? (isOrderPreviewCollapsed ? ' · kliknij aby rozwinąć' : ' · kliknij aby zwinąć') : ''}</div>
                {hasDeadline && (
                  <div className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold tabular-nums ${
                    active
                      ? (darkMode ? 'border-emerald-300/50 bg-emerald-300/10 text-emerald-50' : 'border-emerald-200 bg-white text-emerald-700')
                      : muted
                        ? (darkMode ? 'border-slate-800 bg-slate-900/60 text-slate-500' : 'border-gray-200 bg-white/70 text-gray-400')
                        : (darkMode ? 'border-slate-700 bg-slate-900 text-slate-400' : 'border-gray-200 bg-white text-gray-500')
                  }`}>
                    <Clock3 className="h-3 w-3" />
                    <span>{deadlineLabel} {sec.deadline}</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>
        {activeSection && (forceExpand || !isOrderPreviewCollapsed) && (
          <div>
            <div className={`mb-1.5 text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              {activeSection.title}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-xs">
                <thead className={darkMode ? 'border-b border-slate-700' : 'border-b border-gray-200'}>
                  {activeSection.kind === 'simple' ? (
                    <tr>
                      <th className={thBase}>Hour</th>
                      <th className={thBase}>Ilość K</th>
                      <th className={thBase}>Limit K</th>
                      <th className={thBase}>Limit S</th>
                      <th className={thBase}>Ilość S</th>
                    </tr>
                  ) : activeSection.kind === 'orders' ? (
                    <tr>
                      <th className={thBase}>Instrument</th>
                      <th className={thBase}>K/S</th>
                      <th className={thBase}>Cena</th>
                      <th className={thBase}>Wolumen</th>
                      <th className={thBase}>Źródło</th>
                    </tr>
                  ) : (
                    <tr>
                      <th className={thBase}>Hour</th>
                      {[1, 2, 3].map((b) => (
                        <React.Fragment key={`th-b${b}`}>
                          <th className={thBase}>B{b} Ilość K</th>
                          <th className={thBase}>B{b} Limit K</th>
                          <th className={thBase}>B{b} Limit S</th>
                          <th className={thBase}>B{b} Ilość S</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {activeSection.rows.map((row) => activeSection.kind === 'simple' ? (
                    <tr key={`${activeSection.key}-${row.hour}`} className={darkMode ? 'border-b border-slate-800' : 'border-b border-gray-100'}>
                      <td className={`${tdBase} font-semibold`}>{formatOneBasedHourLabel(row.hour)}</td>
                      <td className={tdBase}>{formatCell(row.qK)}</td>
                      <td className={tdBase}>{formatCell(row.lK)}</td>
                      <td className={tdBase}>{formatCell(row.lS)}</td>
                      <td className={tdBase}>{formatCell(row.qS)}</td>
                    </tr>
                  ) : activeSection.kind === 'orders' ? (
                    <tr key={`${activeSection.key}-${row.id}`} className={darkMode ? 'border-b border-slate-800' : 'border-b border-gray-100'}>
                      <td className={`${tdBase} font-semibold`}>{row.instrument}</td>
                      <td className={tdBase}>{row.side || '-'}</td>
                      <td className={tdBase}>{formatCell(row.price)}</td>
                      <td className={tdBase}>{formatCell(row.volume)}</td>
                      <td className={`${tdBase} whitespace-normal tabular-nums`}>{row.source || '-'}</td>
                    </tr>
                  ) : (
                    <tr key={`${activeSection.key}-${row.hour}`} className={darkMode ? 'border-b border-slate-800' : 'border-b border-gray-100'}>
                      <td className={`${tdBase} font-semibold`}>{formatOneBasedHourLabel(row.hour)}</td>
                      {row.buckets.flatMap((b, i) => [
                        <td key={`${row.hour}-b${i}-qk`} className={tdBase}>{formatCell(b.qK)}</td>,
                        <td key={`${row.hour}-b${i}-lk`} className={tdBase}>{formatCell(b.lK)}</td>,
                        <td key={`${row.hour}-b${i}-ls`} className={tdBase}>{formatCell(b.lS)}</td>,
                        <td key={`${row.hour}-b${i}-qs`} className={tdBase}>{formatCell(b.qS)}</td>,
                      ])}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };
  const getDayPathVolume = (summaryRow, pathKey) => {
    if (!summaryRow) {
      return null;
    }
    if (pathKey === 'fix2') {
      return getFix2ClosePathVolume(summaryRow);
    }
    if (pathKey === 'ida2') {
      if (isIda2CloseUnavailable(summaryRow)) {
        return null;
      }
      return getIda2ClosePathVolume(summaryRow);
    }
    if (pathKey === 'ida1') {
      if (isPartialFix2Settle(summaryRow)) {
        return null;
      }
      return getIda1ClosePathVolume(summaryRow);
    }
    return null;
  };
  const getDayPathProfitPerMwh = (summaryRow, pathKey) => {
    if (!summaryRow) {
      return null;
    }
    if (pathKey === 'fix2') {
      return calculateProfitPerMwh(summaryRow.daily_pnl_fix2_close, getDayPathVolume(summaryRow, 'fix2'));
    }
    if (pathKey === 'ida2') {
      return calculateProfitPerMwh(summaryRow.daily_pnl_ida2_close, getDayPathVolume(summaryRow, 'ida2'));
    }
    if (pathKey === 'ida1') {
      return calculateProfitPerMwh(summaryRow.daily_pnl, getDayPathVolume(summaryRow, 'ida1'));
    }
    return null;
  };
  const pipelineTrainTargets = [
    {
      key: 'goose-v3',
      label: 'Goose v3.1.0',
      helper: 'Run the Goose v3.1.0 train stack manually for this delivery date.',
      payload: { date, simulation_variant: 'goose-v3' },
      displayMode: 'v3',
    },
    {
      key: 'goose-v5',
      label: 'Goose v5.0.0',
      helper: 'Run the Goose v5.0.0 train stack manually for this delivery date.',
      payload: { date, simulation_variant: 'goose-v5' },
      displayMode: 'v5',
    },
    {
      key: 'ida1',
      label: 'IDA1 ida1-v1.0.3',
      helper: 'Run the IDA1 model train stack (train-ida1-v103) manually for this delivery date.',
      payload: { date, stage_variant: 'ida1' },
      displayMode: 'ida1',
    },
    {
      key: 'ida2',
      label: 'IDA2 ida2-v1.0.0',
      helper: 'Run the IDA2 model train stack (train-ida2-v100) manually for this delivery date.',
      payload: { date, stage_variant: 'ida2' },
      displayMode: 'ida2',
    },
  ];
  const pipelinePredictTargets = [
    {
      key: 'goose-v3',
      label: 'Goose v3.1.0 Fix II',
      helper: 'Run the Goose v3.1.0 Fix II predict path.',
      payload: { date, simulation_variant: 'goose-v3' },
      displayMode: 'fix2',
    },
    {
      key: 'goose-v5-fix2',
      label: 'Goose v5.0.0 Fix II',
      helper: 'Run the Goose v5.0.0 Fix II predict path.',
      payload: { date, simulation_variant: 'goose-v5' },
      displayMode: 'fix2',
    },
    {
      key: 'goose-v5-ida1',
      label: 'IDA1 ida1-v1.0.3',
      helper: 'Refresh Goose v5.0.0 staged path with IDA1 prediction (model ida1-v1.0.3).',
      payload: { date, simulation_variant: 'goose-v5', stage_variant: 'ida1' },
      displayMode: 'ida1',
    },
    {
      key: 'goose-v5-ida2',
      label: 'IDA2 ida2-v1.0.0',
      helper: 'Refresh Goose v5.0.0 staged path with IDA2 prediction (model ida2-v1.0.0).',
      payload: { date, simulation_variant: 'goose-v5', stage_variant: 'ida2' },
      displayMode: 'ida2',
    },
  ];
  const selectedPipelineTrainTarget = pipelineTrainTargets.find((target) => target.key === pipelineTrainTarget) || pipelineTrainTargets[0];
  const selectedPipelinePredictTarget = pipelinePredictTargets.find((target) => target.key === pipelinePredictTarget) || pipelinePredictTargets[0];
  const renderModifiedSimulationStatus = () => {
    if (isQuickReplayPendingSettings) {
      return (
        <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${
          darkMode
            ? 'border-amber-300/40 bg-amber-400/10 text-amber-50'
            : 'border-amber-300 bg-amber-50 text-amber-950'
        }`}>
          <div className="inline-flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4" />
            <span className="font-semibold">{simCopy.modifier.pendingTitle}</span>
            <span className={darkMode ? 'text-amber-100/75' : 'text-amber-900/70'}>
              {simCopy.modifier.pendingBody}
            </span>
          </div>
          <button
            type="button"
            onClick={runQuickReplay}
            disabled={quickReplayLoading}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed ${
              darkMode
                ? 'border-amber-200/50 bg-slate-950/40 text-amber-50 hover:bg-slate-900 disabled:text-slate-500'
                : 'border-amber-300 bg-white text-amber-900 hover:bg-amber-100 disabled:text-gray-400'
            }`}
          >
            {quickReplayLoading ? simCopy.modifier.replaying : simCopy.modifier.run}
          </button>
        </div>
      );
    }
    if (!isModifiedSimulationActive) {
      return null;
    }
    return (
      <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${
        darkMode
          ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-50'
          : 'border-emerald-200 bg-emerald-50 text-emerald-950'
      }`}>
        <div className="inline-flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4" />
          <span className="font-semibold">{simCopy.modifier.activeTitle}</span>
          <span className={darkMode ? 'text-emerald-100/75' : 'text-emerald-900/70'}>
            {simCopy.modifier.activeBody}
          </span>
          {quickReplayActiveParamsLabel ? (
            <span className={`rounded-full border px-2 py-0.5 text-[11px] ${
              darkMode
                ? 'border-emerald-300/30 bg-slate-950/35 text-emerald-100'
                : 'border-emerald-200 bg-white/70 text-emerald-900'
            }`}>
              {quickReplayActiveParamsLabel}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={resetToSavedResults}
          className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
            darkMode
              ? 'border-emerald-300/40 bg-slate-950/40 text-emerald-50 hover:bg-slate-900'
              : 'border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-100'
          }`}
        >
          {simCopy.modifier.reset}
        </button>
      </div>
    );
  };
  const renderQuickReplayField = (field) => {
    const fieldValue = quickReplayForm[field.key];
    const inputClass = `mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 ${
      darkMode
        ? 'border-slate-700 bg-slate-900 text-slate-100'
        : 'border-gray-300 bg-white text-gray-900'
    } ${field.disabled ? 'cursor-not-allowed opacity-60' : ''}`;
    return (
      <label key={field.key} className="block">
        <span className={`text-xs font-semibold ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{field.label}</span>
        {field.type === 'checkbox' ? (
          <button
            type="button"
            onClick={() => setQuickReplayForm((prev) => ({ ...prev, [field.key]: !prev[field.key] }))}
            className={`mt-1 flex h-10 w-full items-center justify-between rounded-lg border px-3 text-left text-sm font-semibold transition ${
              fieldValue
                ? darkMode
                  ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-100'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : darkMode
                  ? 'border-slate-700 bg-slate-900 text-slate-300'
                  : 'border-gray-300 bg-white text-gray-600'
            }`}
          >
            <span>{fieldValue ? 'On' : 'Off'}</span>
            <span className={`h-5 w-9 rounded-full p-0.5 transition ${fieldValue ? 'bg-emerald-500' : darkMode ? 'bg-slate-700' : 'bg-slate-300'}`}>
              <span className={`block h-4 w-4 rounded-full bg-white transition ${fieldValue ? 'translate-x-4' : ''}`} />
            </span>
          </button>
        ) : field.type === 'select' ? (
          <select
            value={fieldValue}
            disabled={field.disabled}
            onChange={(event) => setQuickReplayForm((prev) => ({ ...prev, [field.key]: event.target.value }))}
            className={inputClass}
          >
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        ) : (
          <div className="mt-1 flex items-stretch">
            <input
              type="number"
              value={fieldValue}
              onChange={(event) => setQuickReplayForm((prev) => ({ ...prev, [field.key]: event.target.value }))}
              className={`min-w-0 flex-1 rounded-l-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 ${
                darkMode
                  ? 'border-slate-700 bg-slate-900 text-slate-100'
                  : 'border-gray-300 bg-white text-gray-900'
              }`}
            />
            <span className={`rounded-r-lg border border-l-0 px-2 py-2 text-xs ${
              darkMode
                ? 'border-slate-700 bg-slate-800 text-slate-300'
                : 'border-gray-300 bg-gray-50 text-gray-500'
            }`}>
              {field.suffix}
            </span>
          </div>
        )}
        <span className={`mt-1 block text-[11px] ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{field.hint}</span>
      </label>
    );
  };
  const renderSimulationModifierFloatingPanel = () => {
    if (!isSimulationModifierOpen) {
      return null;
    }
    return (
      <div className="fixed right-8 top-28 z-50 max-w-[calc(100vw-4rem)]">
        <div className={`w-[min(760px,calc(100vw-4rem))] rounded-2xl border p-4 shadow-2xl ${
            darkMode
              ? 'border-slate-700 bg-slate-950 text-slate-100'
              : 'border-slate-200 bg-white text-slate-900'
          }`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <SlidersHorizontal className="h-4 w-4" />
                  {simCopy.modifySimulation}
                </div>
                <div className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {isQuickReplayPendingSettings
                    ? simCopy.modifier.pendingText
                    : simCopy.modifier.rangeText(startDate, endDate)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsSimulationModifierOpen(false)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                  darkMode
                    ? 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'
                    : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
              >
                {simCopy.modifier.close}
              </button>
            </div>
            <div className="mt-4 max-h-[min(70vh,720px)] space-y-4 overflow-y-auto pr-1">
              <section className={`rounded-xl border p-3 ${darkMode ? 'border-slate-800 bg-slate-900/60' : 'border-slate-200 bg-slate-50/70'}`}>
                <div className="grid gap-3 sm:grid-cols-[minmax(0,180px)_1fr] sm:items-center">
                  <label className={`text-xs font-extrabold uppercase tracking-[0.14em] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {simCopy.modifier.modelLabel}
                  </label>
                  <select
                    value={simulationVariant}
                    onChange={(event) => {
                      const nextVariant = event.target.value;
                      latestParityRequestRef.current += 1;
                      latestQuickReplayRequestRef.current += 1;
                      setSimulationVariant(nextVariant);
                      setParityReport(null);
                      setQuickReplayResult(null);
                      setParityReportLoading(false);
                      setQuickReplayLoading(false);
                      setDayDetailsByDate({});
                      setExpandedDate('');
                      setDetailsLoadingDate('');
                      setIsHourlyBreakdownExpanded(false);
                      setExpandedDailyQuarterPaths({});
                    }}
                    className={`rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                      darkMode
                        ? 'border-slate-700 bg-slate-950 text-slate-100'
                        : 'border-gray-300 bg-white text-slate-900'
                    }`}
                  >
                    {MODIFY_SIMULATION_VARIANTS.map((variant) => (
                      <option key={variant.key} value={variant.key}>{variant.label}</option>
                    ))}
                  </select>
                </div>
              </section>
              {quickReplayFieldSections.map((section) => (
                <section key={section.key} className={`rounded-xl border p-3 ${darkMode ? 'border-slate-800 bg-slate-900/60' : 'border-slate-200 bg-slate-50/70'}`}>
                  <h3 className={`mb-3 text-xs font-extrabold uppercase tracking-[0.14em] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{section.title}</h3>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {section.fields.map(renderQuickReplayField)}
                  </div>
                </section>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <div className={`flex flex-wrap gap-2 text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <span>{getSimulationVariantLabel()}</span>
                <span>{simCopy.modifier.baseSummary(quickReplayForm.base_volume)}</span>
                <span>{simCopy.modifier.ida1CapSummary(quickReplayForm.max_position)}</span>
                <span>{simCopy.modifier.ida1MarginSummary(quickReplayForm.ida1_margin)}</span>
                  {isQuickReplayPendingSettings ? (
                    <span className={darkMode ? 'text-amber-200' : 'text-amber-700'}>{simCopy.modifier.pendingBadge}</span>
                  ) : null}
              </div>
              <button
                type="button"
                onClick={async () => {
                  const replay = await runQuickReplay();
                  if (replay) setIsSimulationModifierOpen(false);
                }}
                disabled={quickReplayLoading}
                className={`inline-flex items-center gap-2 rounded-lg border px-5 py-2 text-sm font-semibold disabled:cursor-not-allowed ${
                  darkMode
                    ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25 disabled:border-slate-700 disabled:bg-slate-900 disabled:text-slate-500'
                    : 'border-emerald-200 bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-100 disabled:text-gray-400'
                }`}
              >
                <Play className="h-4 w-4" />
                {quickReplayLoading ? simCopy.modifier.replaying : simCopy.modifier.run}
              </button>
            </div>
          </div>
        </div>
    );
  };
  const renderModifiedSimulationHomeBadge = () => {
    const showPendingState = hasPendingReplayOverrides && (!isModifiedSimulationActive || isQuickReplayPendingSettings);
    const showActiveState = hasVariantOverride || hasAppliedReplayOverrides;
    if (!showPendingState && !showActiveState) {
      return null;
    }

    const title = showPendingState
      ? (howItWorksLanguage === 'pl' ? 'Modyfikacja symulacji oczekuje' : 'Modified simulation pending')
      : (howItWorksLanguage === 'pl' ? 'Modyfikacja symulacji aktywna' : 'Modified simulation active');
    const body = showPendingState
      ? (howItWorksLanguage === 'pl'
          ? 'Formularz odbiega od defaultu. Wyniki zmienią się po uruchomieniu modified simulation.'
          : 'The form differs from default settings. Results will change after you run modified simulation.')
      : (howItWorksLanguage === 'pl'
          ? 'Widok działa na niestandardowych ustawieniach zamiast na domyślnej konfiguracji Goose v3.'
          : 'This view is using non-default settings instead of the default Goose v3 configuration.');
    const toneClass = showPendingState
      ? (darkMode
          ? 'border-amber-300/55 bg-amber-400/18 text-amber-50 shadow-[0_16px_40px_rgba(245,158,11,0.22)]'
          : 'border-amber-300 bg-amber-50 text-amber-950 shadow-[0_16px_40px_rgba(245,158,11,0.12)]')
      : (darkMode
          ? 'border-cyan-300/45 bg-cyan-400/16 text-cyan-50 shadow-[0_16px_40px_rgba(34,211,238,0.18)]'
          : 'border-cyan-200 bg-cyan-50 text-cyan-950 shadow-[0_16px_40px_rgba(34,211,238,0.12)]');
    const iconClass = showPendingState
      ? (darkMode ? 'border-amber-200/50 bg-slate-950/35 text-amber-100' : 'border-amber-200 bg-white text-amber-900')
      : (darkMode ? 'border-cyan-200/50 bg-slate-950/35 text-cyan-100' : 'border-cyan-200 bg-white text-cyan-900');
    const chipClass = showPendingState
      ? (darkMode ? 'border-amber-200/40 bg-slate-950/30 text-amber-100' : 'border-amber-200 bg-white/80 text-amber-900')
      : (darkMode ? 'border-cyan-200/40 bg-slate-950/30 text-cyan-100' : 'border-cyan-200 bg-white/80 text-cyan-900');
    const modifierChips = [];
    if (hasVariantOverride) {
      modifierChips.push(getSimulationVariantLabel(simulationVariant));
    }
    if (showPendingState && pendingModifiedParamsLabel) {
      modifierChips.push(pendingModifiedParamsLabel);
    } else if (hasAppliedReplayOverrides && quickReplayActiveParamsLabel) {
      modifierChips.push(quickReplayActiveParamsLabel);
    }

    return (
      <div className="pointer-events-none fixed left-1/2 top-5 z-40 w-[min(920px,calc(100vw-2rem))] -translate-x-1/2">
        <div className={`pointer-events-auto rounded-2xl border px-4 py-3 backdrop-blur-sm ${toneClass}`}>
          <div className="flex items-start gap-3">
            <div className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border ${iconClass}`}>
              <Home className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-sm font-extrabold">{title}</div>
                {modifierChips.map((chip) => (
                  <span key={chip} className={`inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-[11px] font-bold ${chipClass}`}>
                    <span className="truncate">{chip}</span>
                  </span>
                ))}
              </div>
              <div className={`mt-1 text-xs leading-5 ${showPendingState ? (darkMode ? 'text-amber-100/85' : 'text-amber-900/80') : (darkMode ? 'text-cyan-100/85' : 'text-cyan-900/80')}`}>
                {body}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsSimulationModifierOpen(true)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${
                darkMode
                  ? 'border-slate-700 bg-slate-950/35 text-slate-100 hover:bg-slate-900'
                  : 'border-white/80 bg-white/80 text-slate-900 hover:bg-white'
              }`}
            >
              {simCopy.modifySimulation}
            </button>
          </div>
        </div>
      </div>
    );
  };
  const renderScrollToTopButton = () => (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      title="Scroll to top"
      className={`fixed bottom-6 right-6 z-50 inline-flex h-11 w-11 items-center justify-center rounded-full border shadow-xl transition ${
        darkMode
          ? 'border-slate-600 bg-slate-900 text-slate-100 hover:bg-slate-800'
          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
      }`}
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );

  const renderGooseRiskManagementSection = () => {
    const summary = gooseRiskRangeSummary;
    const cardClass = darkMode ? 'border-slate-700 bg-slate-950/45' : 'border-gray-200 bg-white';
    const labelClass = darkMode ? 'text-slate-500' : 'text-gray-500';
    const mutedClass = darkMode ? 'text-slate-400' : 'text-gray-500';
    const hasCarryBreakdown = Number(summary.carryForced || 0) > 0.001
      || Number(summary.carryBlockedIda1 || 0) > 0.001
      || Number(summary.carryBlockedIda2 || 0) > 0.001;
    const carryBreakdownParts = [
      Number(summary.carryForced || 0) > 0.001 ? `Fix II forced ${formatNumber(summary.carryForced)} MWh` : null,
      Number(summary.carryBlockedIda1 || 0) > 0.001 ? `IDA1 block ${formatNumber(summary.carryBlockedIda1)} MWh` : null,
      Number(summary.carryBlockedIda2 || 0) > 0.001 ? `IDA2 block ${formatNumber(summary.carryBlockedIda2)} MWh` : null,
    ].filter(Boolean);
    const carryHelper = hasCarryBreakdown
      ? `${summary.carryDays} ${howItWorksLanguage === 'pl' ? 'dni' : 'days'} · ${carryBreakdownParts.join(' · ')}`
      : `${summary.carryDays} ${howItWorksLanguage === 'pl' ? 'dni z carry do IDA2' : 'carry-to-IDA2 days'}`;
    const summaryToneMap = {
      activity: darkMode
        ? 'border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-slate-950/80 to-slate-950/90'
        : 'border-cyan-200 bg-gradient-to-br from-cyan-50 via-white to-sky-50',
      shift: darkMode
        ? 'border-amber-400/30 bg-gradient-to-br from-amber-500/12 via-slate-950/80 to-slate-950/90'
        : 'border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50',
      fixi: darkMode
        ? 'border-sky-400/30 bg-gradient-to-br from-sky-500/12 via-slate-950/80 to-slate-950/90'
        : 'border-sky-200 bg-gradient-to-br from-sky-50 via-white to-cyan-50',
      ida2: darkMode
        ? 'border-indigo-400/30 bg-gradient-to-br from-indigo-500/12 via-slate-950/80 to-slate-950/90'
        : 'border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-violet-50',
      pnl: darkMode
        ? 'border-emerald-400/30 bg-gradient-to-br from-emerald-500/12 via-slate-950/80 to-slate-950/90'
        : 'border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-lime-50',
    };
    const summaryValueToneMap = {
      activity: darkMode ? 'text-cyan-100' : 'text-cyan-900',
      shift: darkMode ? 'text-amber-100' : 'text-amber-900',
      fixi: darkMode ? 'text-sky-100' : 'text-sky-900',
      ida2: darkMode ? 'text-indigo-100' : 'text-indigo-900',
      pnl: getValueColor(summary.pnl),
    };
    const actionLabel = (action) => (
      action === 'buy'
        ? (howItWorksLanguage === 'pl' ? 'BASE Buy' : 'BASE Buy')
        : action === 'sell'
          ? (howItWorksLanguage === 'pl' ? 'BASE Sell' : 'BASE Sell')
          : 'BASE Idle'
    );
    const actionClass = (action) => (
      action === 'buy'
        ? darkMode ? 'border-emerald-300/45 bg-emerald-500/15 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
        : action === 'sell'
          ? darkMode ? 'border-rose-300/45 bg-rose-500/15 text-rose-100' : 'border-rose-200 bg-rose-50 text-rose-800'
          : darkMode ? 'border-slate-600 bg-slate-900 text-slate-300' : 'border-gray-200 bg-gray-50 text-gray-600'
    );
    const activityBadgeClass = darkMode
      ? 'border-cyan-300/35 bg-cyan-500/10 text-cyan-100'
      : 'border-cyan-200 bg-cyan-50 text-cyan-900';
    const shiftBadgeClass = darkMode
      ? 'border-amber-300/35 bg-amber-500/10 text-amber-100'
      : 'border-amber-200 bg-amber-50 text-amber-900';
    const emptyBadgeClass = darkMode
      ? 'border-slate-700 bg-slate-900 text-slate-400'
      : 'border-gray-200 bg-gray-50 text-gray-500';
    const summaryCards = [
      {
        key: 'activity',
        title: howItWorksLanguage === 'pl' ? 'Dni z akcją Goose risk' : 'Goose risk action days',
        value: `${summary.activeDays}/${summary.days}`,
        helper: howItWorksLanguage === 'pl'
          ? `BASE: ${summary.buyDays} Buy / ${summary.sellDays} Sell / ${summary.idleDays} Idle`
          : `BASE: ${summary.buyDays} Buy / ${summary.sellDays} Sell / ${summary.idleDays} Idle`,
      },
      {
        key: 'shift',
        title: howItWorksLanguage === 'pl' ? 'Risk score / shift' : 'Risk score / shift',
        value: `${summary.riskShiftHours}h`,
        helper: `${summary.riskShiftDays} ${howItWorksLanguage === 'pl' ? 'dni' : 'days'} · ${formatNumber(summary.riskShiftAbsSum)} PLN/MWh abs`,
      },
      {
        key: 'fixi',
        title: 'Goose Fix I',
        value: `${formatNumber(summary.fixIExecutedVolume)} MWh`,
        helper: `${summary.fixIDays} ${howItWorksLanguage === 'pl' ? 'dni' : 'days'} · ${summary.fixIHours}h · order ${formatNumber(summary.fixIOrderVolume)} MWh`,
      },
      {
        key: 'ida2',
        title: howItWorksLanguage === 'pl' ? 'Nasze decyzje IDA2 carry' : 'Our IDA2 carry decisions',
        value: `${formatNumber(summary.selectedCarryVolume)} MWh`,
        helper: carryHelper,
      },
      {
        key: 'pnl',
        title: howItWorksLanguage === 'pl' ? 'Wynik zakresu' : 'Range result',
        value: formatSignedNumber(summary.pnl, ' PLN'),
        helper: howItWorksLanguage === 'pl' ? 'wynik widocznej ścieżki Goose IDA2' : 'visible Goose IDA2 path result',
        valueClass: getValueColor(summary.pnl),
      },
    ];
    return (
      <section className={`rounded-xl shadow-sm ${darkMode ? 'border border-slate-700 bg-slate-900/80' : 'bg-white'}`}>
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className={`text-xs font-extrabold uppercase tracking-[0.16em] ${darkMode ? 'text-cyan-200' : 'text-cyan-700'}`}>
                Goose risk management
              </div>
              <p className={`mt-2 max-w-5xl text-sm ${mutedClass}`}>
                {howItWorksLanguage === 'pl'
                  ? 'Ten widok pokazuje wyłącznie to, co wynika z Goose: poranny risk score/shift, decyzję BASE, ordery Fix I z live backtestu oraz nasz późniejszy overlay/carry do IDA2. Stary Guard/Negative Guard nie jest tu semantyką.'
                  : 'This view shows only Goose behaviour: morning risk score/shift, BASE decision, Fix I live-backtest orders, and our later IDA2 overlay/carry. Legacy Guard/Negative Guard semantics are not used here.'}
              </p>
            </div>
            <div className={`rounded-full border px-3 py-1 text-xs font-bold ${
              darkMode ? 'border-slate-700 bg-slate-950 text-slate-300' : 'border-gray-200 bg-gray-50 text-gray-700'
            }`}>
              {getSimulationVariantLabel()} · {baseHedgeCopy.dayCount(summary.days)}
            </div>
          </div>
        </div>
        <div className={`border-b px-6 py-5 ${darkMode ? 'border-slate-800 bg-slate-950/25' : 'border-gray-200 bg-gray-50/60'}`}>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            {summaryCards.map((card) => (
              <div
                key={`goose-risk-summary-${card.key}`}
                className={`relative overflow-hidden rounded-2xl border p-4 shadow-sm ${summaryToneMap[card.key] || cardClass}`}
              >
                <div className={`absolute right-0 top-0 h-20 w-20 rounded-full blur-2xl ${
                  card.key === 'activity'
                    ? (darkMode ? 'bg-cyan-400/15' : 'bg-cyan-200/40')
                    : card.key === 'shift'
                      ? (darkMode ? 'bg-amber-400/15' : 'bg-amber-200/45')
                      : card.key === 'fixi'
                        ? (darkMode ? 'bg-sky-400/15' : 'bg-sky-200/45')
                        : card.key === 'ida2'
                          ? (darkMode ? 'bg-indigo-400/15' : 'bg-indigo-200/45')
                          : (darkMode ? 'bg-emerald-400/15' : 'bg-emerald-200/45')
                }`} />
                <div className={`relative text-[11px] font-extrabold uppercase tracking-[0.16em] ${labelClass}`}>{card.title}</div>
                <div className={`relative mt-2 text-2xl font-extrabold tabular-nums ${card.valueClass || summaryValueToneMap[card.key] || (darkMode ? 'text-slate-50' : 'text-gray-900')}`}>
                  {card.value}
                </div>
                <div className={`relative mt-2 text-xs leading-5 ${mutedClass}`}>{card.helper}</div>
              </div>
            ))}
          </div>
        </div>
        <div className={`px-6 py-5 ${darkMode ? 'bg-slate-950/15' : 'bg-white'}`}>
          {gooseRiskDayRows.length === 0 ? (
            <div className={`rounded-xl border border-dashed px-4 py-8 text-center text-sm ${darkMode ? 'border-slate-700 text-slate-400' : 'border-gray-200 text-gray-500'}`}>
              {howItWorksLanguage === 'pl' ? 'Brak dni Goose risk management w wybranym zakresie.' : 'No Goose risk-management days in the selected range.'}
            </div>
          ) : (
            <div className="space-y-3">
              {gooseRiskDayRows.map((row) => {
                const pnlClass = getValueColor(row.pnl);
                return (
                  <details
                    key={`goose-risk-day-${row.date}`}
                    className={`group rounded-xl border shadow-sm ${
                      darkMode ? 'border-slate-700 bg-slate-900/75 text-slate-100' : 'border-gray-200 bg-white text-gray-900'
                    }`}
                  >
                    <summary className={`grid cursor-pointer list-none gap-3 px-4 py-3 marker:hidden md:grid-cols-[120px_190px_minmax(0,1fr)_170px_150px_24px] md:items-center ${
                      darkMode ? 'hover:bg-slate-950/35' : 'hover:bg-gray-50'
                    }`}>
                      <div>
                        <div className="text-sm font-extrabold tabular-nums">{row.date}</div>
                        <div className={`mt-1 text-[11px] ${mutedClass}`}>{row.rowKind === 'live' ? 'Live' : 'Replay'}</div>
                      </div>
                      <div>
                        <span className={`inline-flex w-max max-w-full whitespace-nowrap rounded-full border px-3 py-1 text-xs font-extrabold leading-none ${actionClass(row.action)}`}>
                          {actionLabel(row.action)} · {formatNumber(row.baseVolume)}{'\u00A0'}MW
                        </span>
                      </div>
                      <div className="flex min-w-0 flex-wrap gap-2">
                        {row.riskShiftHours > 0 ? (
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none ${shiftBadgeClass}`}>
                            Risk shift · {row.riskShiftHours}h
                          </span>
                        ) : null}
                        {row.fixIOrderHours > 0 || row.fixIOrderVolume > 0.001 ? (
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none ${activityBadgeClass}`}>
                            Goose Fix I · {row.fixIOrderHours}h · {formatNumber(row.fixIOrderVolume)} MWh
                          </span>
                        ) : null}
                        {row.selectedCarryVolume > 0.001 || row.carryForced > 0.001 || row.carryBlockedIda1 > 0.001 || row.carryBlockedIda2 > 0.001 ? (
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none ${darkMode ? 'border-indigo-300/40 bg-indigo-500/15 text-indigo-100' : 'border-indigo-200 bg-indigo-50 text-indigo-900'}`}>
                            IDA2 carry · {formatNumber(row.selectedCarryVolume)} MWh
                          </span>
                        ) : null}
                        {!row.hasActivity ? (
                          <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none ${emptyBadgeClass}`}>
                            {howItWorksLanguage === 'pl' ? 'Brak aktywnej akcji Goose risk' : 'No active Goose risk action'}
                          </span>
                        ) : null}
                      </div>
                      <div>
                        <div className={`text-[10px] font-extrabold uppercase tracking-wide ${labelClass}`}>Fix I executed</div>
                        <div className="mt-0.5 text-sm font-extrabold tabular-nums">{formatNumber(row.fixIExecutedVolume)} MWh</div>
                      </div>
                      <div className="md:text-right">
                        <div className={`text-[10px] font-extrabold uppercase tracking-wide ${labelClass}`}>{howItWorksLanguage === 'pl' ? 'Wynik dnia' : 'Day result'}</div>
                        <div className={`mt-0.5 text-base font-extrabold tabular-nums ${pnlClass}`}>{formatSignedNumber(row.pnl, ' PLN')}</div>
                      </div>
                      <ChevronDown size={16} className={`mt-1 transition group-open:rotate-180 md:mt-0 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                    </summary>
                    <div className={`border-t p-4 ${darkMode ? 'border-slate-800' : 'border-gray-100'}`}>
                      <div className="grid gap-3 lg:grid-cols-4">
                        {[
                          [howItWorksLanguage === 'pl' ? 'Risk engine' : 'Risk engine', row.riskEngine],
                          ['Risk shift', `${row.riskShiftHours}h · ${formatNumber(row.riskShiftAbsSum)} PLN/MWh abs`],
                          ['Goose Fix I', `${formatNumber(row.fixIOrderVolume)} MWh order / ${formatNumber(row.fixIExecutedVolume)} MWh exec`],
                          ['IDA2 carry', [
                            `${formatNumber(row.selectedCarryVolume)} MWh selected`,
                            Number(row.carryForced || 0) > 0.001 ? `${formatNumber(row.carryForced)} MWh forced Fix II` : null,
                            Number(row.carryBlockedIda1 || 0) > 0.001 ? `${formatNumber(row.carryBlockedIda1)} MWh block IDA1` : null,
                            Number(row.carryBlockedIda2 || 0) > 0.001 ? `${formatNumber(row.carryBlockedIda2)} MWh block IDA2` : null,
                          ].filter(Boolean).join(' · ')],
                        ].map(([label, value]) => (
                          <div key={`${row.date}-${label}`} className={`rounded-lg border px-3 py-2 ${cardClass}`}>
                            <div className={`text-[10px] font-extrabold uppercase tracking-wide ${labelClass}`}>{label}</div>
                            <div className="mt-1 text-sm font-extrabold tabular-nums">{value}</div>
                          </div>
                        ))}
                      </div>
                      <div className={`mt-4 overflow-hidden rounded-2xl border ${darkMode ? 'border-slate-700 bg-slate-950/40' : 'border-gray-200 bg-white'}`}>
                        <div className={`flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 ${darkMode ? 'border-slate-800 bg-slate-950/70' : 'border-gray-200 bg-gray-50/85'}`}>
                          <div>
                            <div className={`text-[11px] font-extrabold uppercase tracking-[0.16em] ${labelClass}`}>
                              {howItWorksLanguage === 'pl' ? 'Ślad godzinowy Goose risk' : 'Hourly Goose risk trace'}
                            </div>
                            <div className={`mt-1 text-xs ${mutedClass}`}>
                              {howItWorksLanguage === 'pl'
                                ? 'Risk score, shift, Goose Fix I i IDA2 carry w układzie godzina po godzinie.'
                                : 'Risk score, shift, Goose Fix I, and IDA2 carry hour by hour.'}
                            </div>
                          </div>
                          <div className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-extrabold ${darkMode ? 'border-slate-700 bg-slate-900 text-slate-200' : 'border-gray-200 bg-white text-gray-700'}`}>
                            {formatInteger(row.hourlySignals.length)}h
                          </div>
                        </div>
                        <div className="overflow-auto">
                        <table className="w-full min-w-[900px] text-left text-[12px]">
                          <thead className={darkMode ? 'bg-slate-950/80 text-slate-400' : 'bg-gray-50 text-gray-500'}>
                            <tr>
                              <th className="px-3 py-2.5 font-extrabold uppercase tracking-wide">H</th>
                              <th className="px-3 py-2.5 font-extrabold uppercase tracking-wide">Risk score</th>
                              <th className="px-3 py-2.5 text-right font-extrabold uppercase tracking-wide">Shift</th>
                              <th className="px-3 py-2.5 font-extrabold uppercase tracking-wide">Fix I action</th>
                              <th className="px-3 py-2.5 text-right font-extrabold uppercase tracking-wide">Fix I MWh</th>
                              <th className="px-3 py-2.5 text-right font-extrabold uppercase tracking-wide">Carry MWh</th>
                              <th className="px-3 py-2.5 text-right font-extrabold uppercase tracking-wide">Fix I price</th>
                            </tr>
                          </thead>
                          <tbody className={`divide-y ${darkMode ? 'divide-slate-800' : 'divide-gray-100'}`}>
                            {row.hourlySignals.length ? row.hourlySignals.map((hour, index) => (
                              <tr
                                key={`${row.date}-${hour.hourLabel}-${hour.riskShift}-${hour.fixIVolume}`}
                                className={darkMode
                                  ? index % 2 === 0 ? 'bg-slate-950/10' : 'bg-slate-900/10'
                                  : index % 2 === 0 ? 'bg-white' : 'bg-gray-50/45'}
                              >
                                <td className="whitespace-nowrap px-3 py-2.5 font-extrabold">{hour.hourLabel}</td>
                                <td className="px-3 py-2.5 font-mono tabular-nums">
                                  {hour.riskScore === null || hour.riskScore === undefined || hour.riskScore === ''
                                    ? '-'
                                    : (Number.isFinite(Number(hour.riskScore))
                                      ? Number(hour.riskScore).toFixed(4)
                                      : String(hour.riskScore))}
                                </td>
                                <td className="px-3 py-2.5 text-right tabular-nums">
                                  {hour.riskShift ? (
                                    <span className={`inline-flex min-w-[96px] justify-end rounded-full border px-2.5 py-1 text-[11px] font-bold ${
                                      Number(hour.riskShift || 0) < 0
                                        ? (darkMode ? 'border-cyan-300/35 bg-cyan-500/12 text-cyan-100' : 'border-cyan-200 bg-cyan-50 text-cyan-800')
                                        : (darkMode ? 'border-amber-300/35 bg-amber-500/12 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800')
                                    }`}>
                                      {formatSignedNumber(hour.riskShift, ' PLN/MWh')}
                                    </span>
                                  ) : (
                                    <span className={mutedClass}>-</span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5">
                                  {hour.fixIAction ? (
                                    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-extrabold leading-none ${actionClass(hour.fixIAction)}`}>
                                      {actionLabel(hour.fixIAction)}
                                    </span>
                                  ) : (
                                    <span className={mutedClass}>-</span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{hour.fixIVolume > 0.001 ? `${formatNumber(hour.fixIVolume)} MWh` : '-'}</td>
                                <td className="px-3 py-2.5 text-right tabular-nums">
                                  {hour.carryVolume > 0.001 ? (
                                    <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-extrabold ${darkMode ? 'border-indigo-300/35 bg-indigo-500/12 text-indigo-100' : 'border-indigo-200 bg-indigo-50 text-indigo-800'}`}>
                                      {formatNumber(hour.carryVolume)} MWh
                                    </span>
                                  ) : (
                                    <span className={mutedClass}>-</span>
                                  )}
                                </td>
                                <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatPriceOrDash(hour.fixIPrice)}</td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan={7} className={`px-3 py-5 text-center ${mutedClass}`}>
                                  {howItWorksLanguage === 'pl' ? 'Brak godzin z aktywnym risk score/shift, Fix I order albo carry.' : 'No hours with active risk score/shift, Fix I order, or carry.'}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    </div>
                  </details>
                );
              })}
            </div>
          )}
        </div>
      </section>
    );
  };

  const sectionPanelClass = darkMode
    ? 'border-slate-700/80 bg-[linear-gradient(180deg,rgba(15,23,42,0.92),rgba(2,6,23,0.94))] shadow-[0_18px_46px_rgba(2,6,23,0.28)]'
    : 'border-slate-200 bg-[linear-gradient(180deg,#ffffff,#f8fafc)] shadow-[0_14px_40px_rgba(15,23,42,0.08)]';
  const simulationShellClass = darkMode
    ? 'rounded-[30px] border border-slate-700/80 bg-[linear-gradient(180deg,rgba(15,23,42,0.94),rgba(2,6,23,0.96))] shadow-[0_32px_90px_rgba(2,6,23,0.35)]'
    : 'rounded-[30px] border border-slate-200 bg-white shadow-[0_28px_80px_rgba(15,23,42,0.10)]';
  const simulationBandClass = darkMode
    ? 'rounded-[28px] border border-cyan-500/25 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.18),_transparent_34%),linear-gradient(135deg,rgba(8,47,73,0.72),rgba(15,23,42,0.95))] shadow-[0_24px_70px_rgba(2,6,23,0.30)]'
    : 'rounded-[28px] border border-cyan-200 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.12),_transparent_32%),linear-gradient(135deg,#f0fdfa,#eff6ff)] shadow-[0_20px_50px_rgba(14,116,144,0.10)]';
  const simulationInsetCardClass = darkMode
    ? 'rounded-2xl border border-slate-700/70 bg-slate-950/45 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]'
    : 'rounded-2xl border border-slate-200 bg-white/90 shadow-sm';
  const simulationEyebrowClass = darkMode
    ? 'text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-200/75'
    : 'text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-800/75';

  const renderLetsTradeSection = () => {
    const tradeCopy = simCopy.letsTrade;
    const formatLabel = orderPreviewFormat === 'bos' ? 'BOŚ' : 'Noble';
    const formatTheme = orderPreviewFormat === 'bos'
      ? {
          accentBorder: darkMode ? 'border-emerald-400/30' : 'border-emerald-200',
          accentBg: darkMode ? 'bg-emerald-500/10' : 'bg-emerald-50',
          accentText: darkMode ? 'text-emerald-100' : 'text-emerald-900',
          accentMutedText: darkMode ? 'text-emerald-100/70' : 'text-emerald-800/70',
          numberBorder: darkMode ? 'border-emerald-300/40' : 'border-emerald-300',
          numberBg: darkMode ? 'bg-slate-950' : 'bg-white',
          numberText: darkMode ? 'text-emerald-100' : 'text-emerald-800',
          togglePillActive: darkMode ? 'bg-emerald-500 text-white shadow-md' : 'bg-emerald-600 text-white shadow-md',
        }
      : {
          accentBorder: darkMode ? 'border-amber-400/30' : 'border-amber-200',
          accentBg: darkMode ? 'bg-amber-500/10' : 'bg-amber-50',
          accentText: darkMode ? 'text-amber-100' : 'text-amber-900',
          accentMutedText: darkMode ? 'text-amber-100/70' : 'text-amber-800/70',
          numberBorder: darkMode ? 'border-amber-300/40' : 'border-amber-300',
          numberBg: darkMode ? 'bg-slate-950' : 'bg-white',
          numberText: darkMode ? 'text-amber-100' : 'text-amber-800',
          togglePillActive: darkMode ? 'bg-amber-500 text-slate-900 shadow-md' : 'bg-amber-500 text-white shadow-md',
        };
    const tradeDeliveryDate = letsTradeDeliveryDate;
    const deliveryRow = dailyRows.find((row) => String(row?.date || '').slice(0, 10) === tradeDeliveryDate) || null;
    const hasRealizedDeliveryRow = Boolean(deliveryRow && isRealizedSimulationRow(deliveryRow));
    const hasFinalReplayDeliveryRow = Boolean(hasRealizedDeliveryRow && !isPartialFix2SettleRow(deliveryRow));
    const deliveryStageAvailability = getSettleStageAvailabilityFromRow(deliveryRow);
    const letsTradeDayDetails = dayDetailsByDate[tradeDeliveryDate] || null;
    const letsTradeDayDetailRows = Array.isArray(letsTradeDayDetails?.hourly) ? letsTradeDayDetails.hourly : [];
    const hasSettledDayDetails = Boolean(hasFinalReplayDeliveryRow && letsTradeDayDetails?.summary && letsTradeDayDetailRows.length > 0);
    const isPartialFix2Live = Boolean(deliveryRow && isPartialFix2SettleRow(deliveryRow));
    const hasLiveFix1PartialMark = Boolean(
      !hasSettledDayDetails
      && String(fix1PartialStatus?.date || '').slice(0, 10) === tradeDeliveryDate
      && hasFix1PartialMark
    );
	    const liveFix1PartialRows = hasLiveFix1PartialMark ? fix1PartialRows : [];
	    const hasStagePrice = (value) => value !== null && value !== undefined;
	    const getExecutionRowHourKey = (row) => String(Number(row?.target_hour_waw ?? row?.hour ?? 0));
	    const mergeLiveFix1WithPreviewRows = (actualRows = [], previewRows = []) => {
	      if (!actualRows.length) return previewRows;
	      if (!previewRows.length) return actualRows;
	      const actualByHour = new Map(actualRows.map((row) => [getExecutionRowHourKey(row), row]));
	      const mergedRows = previewRows.map((previewRow) => {
	        const actualRow = actualByHour.get(getExecutionRowHourKey(previewRow));
	        if (!actualRow) return previewRow;
	        const previewComponents = previewRow?.execution_components || {};
	        const actualComponents = actualRow?.execution_components || {};
	        return {
	          ...previewRow,
	          ...actualRow,
	          closed_volume_fix2: previewRow.closed_volume_fix2,
	          fix2_close_action: previewRow.fix2_close_action,
	          fix_ii_price: previewRow.fix_ii_price,
	          fix_ii_pnl: previewRow.fix_ii_pnl,
	          closed_volume_ida1: previewRow.closed_volume_ida1,
	          ida1_close_action: previewRow.ida1_close_action,
	          ida1_price: previewRow.ida1_price,
	          ida1_pnl: previewRow.ida1_pnl,
	          residual_to_ida2: previewRow.residual_to_ida2,
	          closed_volume_ida2: previewRow.closed_volume_ida2,
	          ida2_close_action: previewRow.ida2_close_action,
	          ida2_price: previewRow.ida2_price,
	          ida2_pnl: previewRow.ida2_pnl,
	          quarter_paths: previewRow.quarter_paths ?? actualRow.quarter_paths,
	          execution_components: {
	            ...previewComponents,
	            ...actualComponents,
	            fix_ii: previewComponents.fix_ii ?? actualComponents.fix_ii,
	            ida1: previewComponents.ida1 ?? actualComponents.ida1,
	            ida2: previewComponents.ida2 ?? actualComponents.ida2,
	          },
	        };
	      });
	      const previewHours = new Set(previewRows.map(getExecutionRowHourKey));
	      const actualOnlyRows = actualRows.filter((row) => !previewHours.has(getExecutionRowHourKey(row)));
	      return [...mergedRows, ...actualOnlyRows].sort((left, right) => Number(getExecutionRowHourKey(left)) - Number(getExecutionRowHourKey(right)));
	    };
	    const hasPostFixIStageRows = (rows = []) => (rows || []).some((row) => (
	      Math.abs(Number(row?.closed_volume_fix2 || 0)) > 0.0001
	      || Math.abs(Number(row?.closed_volume_ida1 || 0)) > 0.0001
      || Math.abs(Number(row?.closed_volume_ida2 || 0)) > 0.0001
      || Math.abs(Number(row?.residual_to_ida2 || 0)) > 0.0001
      || hasStagePrice(row?.fix_ii_price)
      || hasStagePrice(row?.ida1_price)
      || hasStagePrice(row?.ida2_price)
    ));
	    const executionPathRows = hasSettledDayDetails
	      ? letsTradeDayDetailRows
	      : isPartialFix2Live
	        ? previewPredictRows
	        : hasLiveFix1PartialMark
	          ? mergeLiveFix1WithPreviewRows(liveFix1PartialRows, previewPredictRows)
	          : hasFinalReplayDeliveryRow
	            ? []
	            : previewPredictRows;
    const marketActualRows = hasSettledDayDetails
      ? letsTradeDayDetailRows
      : isPartialFix2Live
        ? previewPredictRows
        : hasLiveFix1PartialMark
          ? liveFix1PartialRows
          : hasFinalReplayDeliveryRow
            ? []
            : previewPredictRows;
    const activePreviewRows = activeNoblePreviewRows || [];
    const previewRowsForTable = activePreviewRows;
    const previewRowsHaveSourceLabel = previewRowsForTable.some((row) => getNoblePreviewSourceLabel(row));
    const action = String(basePretradeDecision?.base_setup?.base_action || basePretradeDecision?.model_action || baseQuickInputForm.baseAction || 'Idle');
    const deliveryStatus = String(deliveryRow?.status || '');
    const actionTone = action.toLowerCase() === 'buy'
      ? (darkMode ? 'text-emerald-300' : 'text-emerald-700')
      : action.toLowerCase() === 'sell'
        ? (darkMode ? 'text-rose-300' : 'text-rose-700')
        : (darkMode ? 'text-slate-300' : 'text-gray-600');
    const actualAverages = getDailyActualPriceAverages(marketActualRows, previewPredictMeta);
    const marketAverages = getDailyMarketPriceAverages(marketActualRows);
    const q50Averages = getDailyQ50Averages(marketActualRows, previewPredictMeta);
    const modeLabels = {
      fix_i: 'Fix I',
      fix_ii: 'Fix II',
      ida1: 'IDA1',
      ida2: 'IDA2',
    };
    const orderTiming = tradeCopy.orderTiming;
    const orderDeadlines = tradeCopy.orderDeadlines || {};
    const isSelectedFixIIPreviewAwaitingIda1 = orderPreviewMode === 'fix_ii' && !isIda1OrderPreviewReady;
    const isSelectedIda1ClosePreview = false;
    const isSelectedMutedPreview = isSelectedFixIIPreviewAwaitingIda1 || isSelectedIda1ClosePreview;
    const orderStageCards = [
      { key: 'fix_i', label: 'Fix I', rows: nobleOrderPreviewRows.fix_i || [], helper: tradeCopy.orderHelpers.fix_i, deadline: orderDeadlines.fix_i },
      {
        key: 'fix_ii',
        label: 'Fix II',
        rows: nobleOrderPreviewRows.fix_ii || [],
        helper: isIda1OrderPreviewReady ? tradeCopy.orderHelpers.fix_ii : tradeCopy.orderHelpers.fixIIWaitingIda1,
        deadline: orderDeadlines.fix_ii,
        muted: !isIda1OrderPreviewReady,
      },
      {
        key: 'ida1',
        label: 'IDA1',
        rows: nobleOrderPreviewRows.ida1 || [],
        helper: isIda1OrderPreviewReady
          ? tradeCopy.orderHelpers.ida1Ready
          : tradeCopy.orderHelpers.ida1Pending,
        deadline: orderDeadlines.ida1,
        muted: !isIda1OrderPreviewReady,
      },
      { key: 'ida2', label: 'IDA2', rows: nobleOrderPreviewRows.ida2 || [], helper: isIda2OrderPreviewReady ? tradeCopy.orderHelpers.ida2Ready : tradeCopy.orderHelpers.ida2Pending, deadline: orderDeadlines.ida2, muted: !isIda2OrderPreviewReady },
    ];
    const isSelectedPreviewLoading = (
      orderPreviewMode === 'fix_i'
        ? externalFixIPreviewLoading
        : orderPreviewMode === 'ida1'
          ? externalIda1PreviewLoading
          : orderPreviewMode === 'ida2'
            ? externalIda2PreviewLoading
            : letsTradeOrderPlanLoading
    );
    const selectedPreviewError = (
      orderPreviewMode === 'fix_i'
        ? externalFixIPreviewError
        : orderPreviewMode === 'ida1'
          ? externalIda1PreviewError
          : orderPreviewMode === 'ida2'
            ? externalIda2PreviewError
            : letsTradeOrderPlanError
    );
    const deliveryMonthStart = `${tradeDeliveryDate.slice(0, 7)}-01`;
    const deliveryMonthRows = dailyRows.filter((row) => {
      const rowDate = String(row?.date || '').slice(0, 10);
      return rowDate >= deliveryMonthStart && rowDate <= tradeDeliveryDate && isRealizedSimulationRow(row);
    });
    const deliveryMonthPnl = Number(deliveryMonthRows.reduce((acc, row) => acc + Number(getLiveProvisionalDailyResultValue(row) || 0), 0).toFixed(2));
    const deliveryMonthDays = deliveryMonthRows.length;
    const yachtMonthPnl = deliveryMonthPnl;
    const yachtMissingPnl = Math.max(0, LETS_TRADE_YACHT_TARGET_PLN - yachtMonthPnl);
    const hasYachtTarget = yachtMonthPnl >= LETS_TRADE_YACHT_TARGET_PLN;
    const yachtMoodFace = hasYachtTarget ? ':)' : ':(';
    const yachtMoodTitle = hasYachtTarget ? tradeCopy.yachtHappyTitle : tradeCopy.yachtSadTitle;
    const yachtMoodBody = hasYachtTarget
      ? tradeCopy.yachtHappyBody(formatSignedNumber(yachtMonthPnl, ' PLN'))
      : tradeCopy.yachtSadBody(formatSignedNumber(yachtMonthPnl, ' PLN'), `${formatNumber(yachtMissingPnl)} PLN`);
    const handleLetsTradeModeSelect = (modeKey) => {
      if (modeKey === orderPreviewMode) {
        setIsOrderPreviewCollapsed((prev) => !prev);
        return;
      }
      setIsOrderPreviewCollapsed(false);
      setOrderPreviewMode(modeKey);
      if (modeKey === 'fix_ii') {
        if (!letsTradeOrderPlan.length && !letsTradeOrderPlanLoading) {
          loadLetsTradeOrderPlan({ targetDate: tradeDeliveryDate }).catch((err) => console.error(err));
        }
        return;
      }
      const externalState = {
        fix_i: { rows: externalFixIPreviewRows, loading: externalFixIPreviewLoading },
        ida1: { rows: externalIda1PreviewRows, loading: externalIda1PreviewLoading },
        ida2: { rows: externalIda2PreviewRows, loading: externalIda2PreviewLoading },
      }[modeKey];
      if (externalState && externalState.rows.length === 0 && !externalState.loading) {
        loadNoblePreview({ mode: modeKey, targetDate: tradeDeliveryDate }).catch((err) => console.error(err));
      }
    };
    const refreshSelectedOrders = () => {
      loadLetsTradeOrderPlan({ targetDate: tradeDeliveryDate, force: true }).catch((err) => console.error(err));
      if (orderPreviewMode !== 'fix_ii') {
        loadNoblePreview({ mode: orderPreviewMode, force: true, targetDate: tradeDeliveryDate }).catch((err) => console.error(err));
      }
    };
    const refreshWholeTraderView = () => {
      clearStatusCache();
      clearOrderPlanCache();
      loadCoreData({ force: true, preserveOrderPlanMeta: true, targetDate: tradeDeliveryDate }).catch((err) => console.error(err));
      loadResultsData({ forceResults: true, view: 'summary' }).catch((err) => console.error(err));
      const deliveryRow = dailyRows.find((row) => String(row?.date || '').slice(0, 10) === tradeDeliveryDate);
      if (deliveryRow && isRealizedSimulationRow(deliveryRow)) {
        loadDayDetails(tradeDeliveryDate).catch((err) => console.error(err));
      }
      loadBasePretrade({ targetDate: tradeDeliveryDate, force: true }).catch((err) => console.error(err));
      loadDayRadar({ targetDate: tradeDeliveryDate, force: true }).catch((err) => console.error(err));
      loadLetsTradeOrderPlan({ targetDate: tradeDeliveryDate, force: true }).catch((err) => console.error(err));
      loadNoblePreview({ mode: 'fix_i', force: true, targetDate: tradeDeliveryDate }).catch((err) => console.error(err));
      if (orderPreviewMode === 'ida1' || orderPreviewMode === 'ida2') {
        loadNoblePreview({ mode: orderPreviewMode, force: true, targetDate: tradeDeliveryDate }).catch((err) => console.error(err));
      }
    };
    const handleLetsTradeDateChange = (nextDate) => {
      const normalizedDate = String(nextDate || '').slice(0, 10);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedDate)) return;
      const nextStartDate = getMonthStartForDate(normalizedDate);
      setError('');
      setActionMessage('');
      clearResultsCache();
      clearStatusCache();
      clearOrderPlanCache();
      latestParityRequestRef.current += 1;
      latestQuickReplayRequestRef.current += 1;
      latestResultsRequestRef.current += 1;
      latestBaseEntryRiskRequestRef.current += 1;
      latestLetsTradeOrderPlanRequestRef.current += 1;
      setLetsTradeDeliveryDate(normalizedDate);
      setDate(normalizedDate);
      setStartDate(nextStartDate);
      setEndDate(normalizedDate);
      setResultsData(null);
      setBaseEntryRiskData(null);
      setBaseEntryRiskLoading(false);
      setBasePretradeData(null);
      setBasePretradeLoading(false);
      setLetsTradeOrderPlan([]);
      setLetsTradeOrderPlanMeta(null);
      setLetsTradeOrderPlanDate('');
      setLetsTradeOrderPlanError('');
      setLetsTradeOrderPlanLoading(false);
      resetExternalNoblePreview(null);
      setParityReport(null);
      setQuickReplayResult(null);
      setParityReportLoading(false);
      setQuickReplayLoading(false);
      setOrderPreviewMode('fix_i');
      setIsLetsTradeEntryExplanationOpen(false);
      setIsLetsTradeHedgeExplanationOpen('');
      setIsLetsTradeYachtPopoverOpen(false);
      setIsLetsTradeExecutionPathPopoverOpen(false);
      setIsLetsTradeExecutionPathExpanded(false);
      setExpandedExecutionQuarterPaths({});
      loadCoreData({ force: true, preserveOrderPlanMeta: true, targetDate: normalizedDate }).catch((err) => console.error(err));
      loadBaseEntryRisk({ targetDate: normalizedDate, force: true, baseVolume: 50 }).catch((err) => console.error(err));
      loadBasePretrade({ targetDate: normalizedDate, force: true }).catch((err) => console.error(err));
      loadDayRadar({ targetDate: normalizedDate, force: true }).catch((err) => console.error(err));
      loadLetsTradeOrderPlan({ targetDate: normalizedDate, force: true }).catch((err) => console.error(err));
      loadNoblePreview({ mode: 'fix_i', force: true, targetDate: normalizedDate }).catch((err) => console.error(err));
    };
    const shiftLetsTradeDate = (dayDelta) => {
      const shiftedDate = shiftIsoDate(tradeDeliveryDate, dayDelta);
      if (!shiftedDate) return;
      handleLetsTradeDateChange(shiftedDate);
    };
    const renderFlowBubble = ({ icon: FlowIcon, step = null, title, body, tone = 'green' }) => (
      <div className={`inline-flex max-w-full items-center gap-3 rounded-xl border px-3 py-2 ${
        tone === 'red'
          ? darkMode
            ? 'border-rose-300/55 bg-rose-500/15 text-rose-50 shadow-[0_0_0_1px_rgba(251,113,133,0.10)]'
            : 'border-rose-200 bg-rose-50 text-rose-900'
          : tone === 'blue'
            ? darkMode
              ? 'border-sky-300/45 bg-sky-500/15 text-sky-50 shadow-[0_0_0_1px_rgba(56,189,248,0.10)]'
              : 'border-sky-200 bg-sky-50 text-sky-900'
          : tone === 'amber'
            ? darkMode
              ? 'border-amber-300/45 bg-amber-500/15 text-amber-50 shadow-[0_0_0_1px_rgba(251,191,36,0.10)]'
              : 'border-amber-200 bg-amber-50 text-amber-900'
          : darkMode
            ? 'border-slate-700 bg-slate-950/55 text-slate-200'
            : 'border-gray-200 bg-white text-gray-700'
      }`}
      >
        <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
          tone === 'red'
            ? darkMode ? 'border border-rose-300/40 bg-slate-950 text-rose-100' : 'border border-rose-200 bg-white text-rose-700'
            : tone === 'blue'
              ? darkMode ? 'border border-sky-300/40 bg-slate-950 text-sky-100' : 'border border-sky-200 bg-white text-sky-700'
            : tone === 'amber'
              ? darkMode ? 'border border-amber-300/40 bg-slate-950 text-amber-100' : 'border border-amber-200 bg-white text-amber-700'
            : darkMode ? 'bg-emerald-500/15 text-emerald-200' : 'bg-emerald-50 text-emerald-700'
        }`}
        >
          {step ? <span className="text-sm font-black leading-none">{step}</span> : <FlowIcon className="h-4 w-4" />}
        </span>
        <span className="min-w-0">
          <span className={`block text-sm font-extrabold ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>{title}</span>
          <span className={`block text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{body}</span>
        </span>
      </div>
    );
    const renderHedgeWhyPopoverButton = (card, popoverKey) => {
      if (!card.explanation) {
        return null;
      }
      const isOpen = isLetsTradeHedgeExplanationOpen === popoverKey;
      const activeClass = card.guardMeta?.badgeClass || (
        darkMode
          ? 'border-emerald-300/60 bg-emerald-500/15 text-emerald-100'
          : 'border-emerald-200 bg-emerald-50 text-emerald-800'
      );
      const inactiveClass = darkMode
        ? 'border-slate-600 text-slate-300 hover:bg-slate-900'
        : 'border-gray-300 text-gray-600 hover:bg-gray-50';
      const panelClass = card.guardMeta?.panelClass || (
        darkMode
          ? 'border-emerald-400/30 bg-slate-950 text-emerald-50 shadow-black/40'
          : 'border-emerald-200 bg-white text-emerald-900 shadow-emerald-950/15'
      );
      return (
        <span className="relative inline-flex">
          <button
            type="button"
            onClick={() => setIsLetsTradeHedgeExplanationOpen((prev) => (prev === popoverKey ? '' : popoverKey))}
            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold normal-case transition ${isOpen ? activeClass : inactiveClass}`}
            aria-expanded={isOpen}
          >
            <Info className="h-3 w-3" />
            <span>{tradeCopy.hedgeExplainButton}</span>
          </button>
          {isOpen ? (
            <span className={`absolute left-0 top-full z-50 mt-2 block w-[min(82vw,360px)] rounded-xl border p-3 text-left text-xs normal-case leading-relaxed shadow-2xl ${panelClass}`}>
              <span className={`absolute -top-1.5 left-7 h-3 w-3 rotate-45 border-l border-t ${
                card.guardMeta?.panelClass
                  ? ''
                  : darkMode ? 'border-emerald-400/30 bg-slate-950' : 'border-emerald-200 bg-white'
              }`} />
              <span className="mb-1 block font-extrabold">{tradeCopy.hedgeExplanationTitle}</span>
              <span className="block">{card.explanation}</span>
            </span>
          ) : null}
        </span>
      );
    };
    const entryRisk = baseEntryRiskDecision || {};
    const entryRiskLevel = entryRisk.ready ? String(entryRisk.risk_level || 'low') : 'not_ready';
    const entryRiskLabel = tradeCopy.entryRiskLabels?.[entryRiskLevel] || entryRiskLevel;
    const entryRiskToneClass = entryRiskLevel === 'blocked' || entryRiskLevel === 'high'
      ? darkMode ? 'border-rose-300/45 bg-rose-500/15 text-rose-100' : 'border-rose-200 bg-rose-50 text-rose-900'
      : entryRiskLevel === 'medium'
        ? darkMode ? 'border-amber-300/45 bg-amber-500/15 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900'
        : entryRiskLevel === 'not_ready'
          ? darkMode ? 'border-slate-600 bg-slate-900 text-slate-300' : 'border-gray-200 bg-gray-50 text-gray-700'
          : darkMode ? 'border-emerald-300/35 bg-emerald-500/10 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-900';
    const entryBuy = entryRisk.buy || {};
    const entrySell = entryRisk.sell || {};
    const entryBuyRiskHours = Number(entryBuy.risk_hours || 0);
    const entrySellRiskHours = Number(entrySell.risk_hours || 0);
    const entryRiskHours = entryBuyRiskHours + entrySellRiskHours;
    const entryHourChips = [
      ...(entryBuy.hourly_risk || []).map((row) => `B ${formatOneBasedHourLabel(row.hour)}`),
      ...(entrySell.hourly_risk || []).map((row) => `S ${formatOneBasedHourLabel(row.hour)}`),
    ];
    const entryBuyTriggerPrice = entryBuy.neutral_max_buy_price ?? entryBuy.risk_adjusted_max_buy_price;
    const entrySellTriggerPrice = entrySell.neutral_min_sell_price ?? entrySell.risk_adjusted_min_sell_price;
    const hasEntryTriggerPrices = entryRisk.ready
      && entryBuyTriggerPrice !== null
      && entryBuyTriggerPrice !== undefined
      && entrySellTriggerPrice !== null
      && entrySellTriggerPrice !== undefined;
    const entryExplainLines = buildEntryRiskExplanationLines(entryRisk, tradeCopy);
	    const hasExecutionRowsFor = (volumeKey, priceKey) => executionPathRows.some((row) => (
	      Number(row?.[volumeKey] || 0) > 0.0001
	      && row?.[priceKey] !== null
	      && row?.[priceKey] !== undefined
	    ));
	    const hasPreviewRowsFor = (volumeKey, priceKey) => !hasFinalReplayDeliveryRow && previewPredictRows.some((row) => (
	      Number(row?.[volumeKey] || 0) > 0.0001
	      && row?.[priceKey] !== null
	      && row?.[priceKey] !== undefined
	    ));
    const settledExecutionSummary = hasSettledDayDetails ? (letsTradeDayDetails?.summary || null) : null;
    const hasActualFixI = (
      Boolean(previewPredictMeta?.market_readiness?.fix_i?.ready)
      || hasExecutionRowsFor('executed_volume_fix1', 'fix_i_price')
    ) && actualAverages.fix_i !== null;
    const hasActualFixII = Boolean(deliveryStageAvailability.fix2 && (hasSettledDayDetails || isPartialFix2Live));
    const hasActualIda1 = Boolean(deliveryStageAvailability.ida1 && marketAverages.ida1 !== null);
    const hasActualIda2 = Boolean(deliveryStageAvailability.ida2 && marketAverages.ida2 !== null);
    const isOfficialSettleStoppedAtFix2 = Boolean(isPartialFix2Live);
	    const executionPathAvailability = {
	      fix_i: hasSettledDayDetails || hasLiveFix1PartialMark || hasActualFixI,
	      fix_ii: hasActualFixII,
	      ida1: isOfficialSettleStoppedAtFix2 ? false : hasActualIda1,
	      ida2: isOfficialSettleStoppedAtFix2 ? false : hasActualIda2,
	      preview: {
	        fix_ii: !hasActualFixII && hasPreviewRowsFor('closed_volume_fix2', 'fix_ii_price'),
	        ida1: !isOfficialSettleStoppedAtFix2 && !hasActualIda1 && hasPreviewRowsFor('closed_volume_ida1', 'ida1_price'),
	        ida2: !isOfficialSettleStoppedAtFix2 && !hasActualIda2 && hasPreviewRowsFor('closed_volume_ida2', 'ida2_price'),
	      },
	    };
    const executionPathNotice = isOfficialSettleStoppedAtFix2 && hasPostFixIStageRows(executionPathRows)
      ? {
          title: tradeCopy.executionFix2FallbackNoticeTitle,
          body: tradeCopy.executionFix2FallbackNoticeBody,
        }
      : hasFinalReplayDeliveryRow && !hasSettledDayDetails
        ? {
            title: tradeCopy.executionSettledDetailsMissingTitle,
            body: tradeCopy.executionSettledDetailsMissingBody,
          }
        : null;
    const basePretradeSourceContext = buildBaseSourceContext(
      {
        ...(basePretradeDecision?.base_setup || {}),
        base_action: action,
        volume: basePretradeDecision?.base_block_volume_mw ?? baseQuickInputForm.volume,
        runtime_base_price: basePretradeDecision?.base_price,
        source: basePretradeDecision?.base_setup?.source || basePretradeDecision?.source,
        runtime_source: basePretradeDecision?.base_setup?.runtime_source || basePretradeDecision?.runtime_source,
      },
      basePretradeDecision || {},
    );
    const basePretradeSourceLabel = getBaseSourceLabel(basePretradeSourceContext, basePretradeDecision || {});
    const basePretradeSourceBadgeClass = getBaseSourceBadgeClass(basePretradeSourceContext, basePretradeDecision || {});
    const pretradeBaseVolumeMw = Number(basePretradeDecision?.base_block_volume_mw ?? baseQuickInputForm.volume ?? 0);
    const pretradeBaseVenueMwh = Number(
      basePretradeDecision?.base_block_volume_mwh
      ?? basePretradeDecision?.full_block_volume_mwh
      ?? (Number.isFinite(pretradeBaseVolumeMw) ? pretradeBaseVolumeMw * 24 : 0)
    );
    const pretradeExecutionSummary = basePretradeDecision ? {
      date: tradeDeliveryDate,
      status: basePretradeDecision.status || deliveryStatus || 'pretrade',
      base_action: action,
      base_context: {
        ...(basePretradeDecision.base_setup || {}),
        base_action: action,
        display_action: action,
        volume: pretradeBaseVolumeMw,
        runtime_base_price: basePretradeDecision.base_price,
        base_price: basePretradeDecision.base_price,
        venue_base_mwh: pretradeBaseVenueMwh,
        daily_volume_mwh: pretradeBaseVenueMwh,
        source: basePretradeSourceContext.source || basePretradeDecision.source || 'post_base_hedge',
        runtime_source: basePretradeSourceContext.runtime_source || basePretradeDecision.runtime_source,
      },
      base_price: basePretradeDecision.base_price,
      base_pnl: null,
      base_volume: pretradeBaseVenueMwh,
      venue_base_mwh: pretradeBaseVenueMwh,
    } : null;
    const executionPathSummary = settledExecutionSummary
      || (hasRealizedDeliveryRow ? deliveryRow : null)
      || (hasLiveFix1PartialMark ? fix1PartialSummary : null)
      || pretradeExecutionSummary
      || {};
    const executionPathPnlBridge = getExecutionPnlBridge(executionPathSummary, executionPathAvailability);
    const executionPathStagesForPnl = buildExecutionPathSummary(
      executionPathRows,
      executionPathAvailability,
      executionPathSummary,
    );
    const executionPathStagePnlBridge = getExecutionPnlBridgeFromStages(
      executionPathStagesForPnl,
      executionPathAvailability,
    );
    const executionPathPnl = Number.isFinite(Number(executionPathPnlBridge?.targetPnl))
      ? Number(executionPathPnlBridge.targetPnl)
      : null;
    const executionPathStagePnl = Number.isFinite(Number(executionPathStagePnlBridge?.targetPnl))
      ? Number(executionPathStagePnlBridge.targetPnl)
      : null;
    const realizedDeliveryPnl = hasRealizedDeliveryRow ? getLiveProvisionalDailyResultValue(deliveryRow) : null;
    const fix1PartialDeliveryPnl = hasLiveFix1PartialMark && Number.isFinite(Number(fix1PartialSummary.daily_pnl_fix1_mark))
      ? Number(fix1PartialSummary.daily_pnl_fix1_mark)
      : null;
    const livePathDeliveryPnl = !hasFinalReplayDeliveryRow && executionPathStagePnl !== null
      ? executionPathStagePnl
      : executionPathPnl;
    const deliveryPnl = hasFinalReplayDeliveryRow && realizedDeliveryPnl !== null && realizedDeliveryPnl !== undefined
      ? realizedDeliveryPnl
      : livePathDeliveryPnl !== null
        ? livePathDeliveryPnl
        : realizedDeliveryPnl !== null && realizedDeliveryPnl !== undefined
          ? realizedDeliveryPnl
        : fix1PartialDeliveryPnl;
    const deliveryAfterFees = hasRealizedDeliveryRow ? getLiveProvisionalDailyAfterFeesValue(deliveryRow) : null;
    const deliveryPnlBridgeForLabel = !hasFinalReplayDeliveryRow && executionPathStagePnlBridge?.key
      ? executionPathStagePnlBridge
      : executionPathPnlBridge;
    const deliveryPnlStageLabel = deliveryPnlBridgeForLabel?.key ? modeLabels[deliveryPnlBridgeForLabel.key] : null;
    const deliveryPnlHelper = deliveryAfterFees === null || deliveryAfterFees === undefined
      ? (deliveryPnlStageLabel && deliveryPnl !== null && deliveryPnl !== undefined
        ? tradeCopy.dayPnlLiveStage(deliveryPnlStageLabel)
        : hasLiveFix1PartialMark ? tradeCopy.dayPnlFix1Mark : tradeCopy.dayPnlWaitingFixI(tradeDeliveryDate))
      : tradeCopy.dayPnlAfterFees(formatSignedNumber(deliveryAfterFees, ' PLN'));
    const actualCard = (label, value, ready) => ({
      label,
      value: ready ? value : null,
      helper: ready ? tradeCopy.marketCards.actualReady : tradeCopy.marketCards.actualUnavailable,
    });
    const isWaitingForFix1 = !deliveryAfterFees && !deliveryPnlStageLabel && !hasLiveFix1PartialMark && !hasFinalReplayDeliveryRow;
    const metricCards = [
      {
        label: tradeCopy.dayPnl,
        value: deliveryPnl === null || deliveryPnl === undefined ? '-' : `${formatSignedNumber(deliveryPnl, ' PLN')}`,
        helper: deliveryPnlHelper,
        valueClass: getValueColor(deliveryPnl),
        waiting: isWaitingForFix1 || resultsLoading,
      },
      {
        label: tradeCopy.monthPnl,
        value: `${formatSignedNumber(deliveryMonthPnl, ' PLN')}`,
        helper: tradeCopy.monthPnlHelper(deliveryMonthDays),
        valueClass: getValueColor(deliveryMonthPnl),
        waiting: resultsLoading,
      },
      {
        label: tradeCopy.baseAuction,
        value: formatPriceOrDash(basePretradeDecision?.base_price),
        helper: `${action.toUpperCase()} ${formatNumber(basePretradeDecision?.base_block_volume_mw ?? baseQuickInputForm.volume)} MW`,
        valueClass: actionTone,
        badge: basePretradeSourceLabel,
        badgeClass: basePretradeSourceBadgeClass,
        waiting: basePretradeLoading,
      },
      {
        label: tradeCopy.modelQ50Base,
        value: basePretradeDecision?.avg_q50_forecast == null ? '-' : `${formatNumber(basePretradeDecision.avg_q50_forecast)} PLN/MWh`,
        helper: tradeCopy.modelQ50Helper,
        valueClass: darkMode ? 'text-sky-200' : 'text-sky-700',
        waiting: basePretradeLoading,
      },
      {
        label: tradeCopy.shiftedQ50Goose,
        value: basePretradeDecision?.goose_shifted_avg_q50_forecast == null ? '-' : `${formatNumber(basePretradeDecision.goose_shifted_avg_q50_forecast)} PLN/MWh`,
        helper: tradeCopy.shiftedQ50GooseHelper,
        valueClass: darkMode ? 'text-cyan-200' : 'text-cyan-700',
        waiting: basePretradeLoading,
      },
    ];
    const marketCards = [
      actualCard(tradeCopy.marketCards.fixIReal, actualAverages.fix_i, hasActualFixI),
      actualCard(tradeCopy.marketCards.fixIIReal, marketAverages.fix2, hasActualFixII),
      actualCard(tradeCopy.marketCards.ida1Real, marketAverages.ida1, hasActualIda1),
      actualCard(tradeCopy.marketCards.ida2Real, marketAverages.ida2, hasActualIda2),
      { label: tradeCopy.marketCards.fixIIQ50, value: q50Averages.fix2, helper: tradeCopy.marketCards.storedForecast },
      { label: tradeCopy.marketCards.ida1Q50, value: q50Averages.ida1, helper: tradeCopy.marketCards.storedForecast },
      { label: tradeCopy.marketCards.ida2Q50, value: q50Averages.ida2, helper: tradeCopy.marketCards.storedForecast },
    ];
    const tradeDayRadarData = String(dayRadarData?.date || '').slice(0, 10) === tradeDeliveryDate ? dayRadarData : null;
    const tradeDayRadarSnapshot = tradeDayRadarData?.snapshot || null;
    const tradeDayRadarPayload = tradeDayRadarSnapshot?.payload || {};
    const tradeDayRadarSummary = tradeDayRadarPayload.summary || {};
    const tradeDayRadarSnapshotStatus = String(tradeDayRadarSnapshot?.status || '').toLowerCase();
    const tradeDayRadarPayloadStatus = String(tradeDayRadarPayload.status || '').toLowerCase();
    const tradeDayRadarPipelineStatus = String(tradeDayRadarData?.pipeline_run?.status || '').toLowerCase();
    const tradeDayRadarRunning = ['requested', 'pending', 'running', 'provisioning'].includes(tradeDayRadarPipelineStatus);
    const tradeDayRadarBlocked = tradeDayRadarSnapshotStatus === 'blocked_missing_predict' || tradeDayRadarPayloadStatus === 'blocked_missing_predict';
    const tradeDayRadarVerdict = tradeDayRadarSummary.verdict || (tradeDayRadarBlocked ? 'waiting' : '');
    const tradeDayRadarLabel = dayRadarLoading && !tradeDayRadarData
      ? tradeCopy.dayRadarLoading
      : tradeDayRadarRunning
        ? tradeCopy.dayRadarLoading
        : tradeDayRadarBlocked
          ? tradeCopy.dayRadarWaiting
          : tradeDayRadarSnapshot?.status === 'success'
            ? (tradeCopy.dayRadarVerdicts?.[tradeDayRadarVerdict] || tradeCopy.dayRadarReady)
            : dayRadarError
              ? tradeCopy.dayRadarError
              : tradeCopy.dayRadarMissing;
    const tradeDayRadarHelper = tradeDayRadarSnapshot?.status === 'success'
      ? (tradeDayRadarSummary.headline || tradeCopy.dayRadarReady)
      : tradeDayRadarBlocked
        ? tradeCopy.dayRadarWaiting
        : dayRadarError
          ? dayRadarError
          : tradeCopy.dayRadarOpen;
    const tradeDayRadarToneClass = tradeDayRadarVerdict === 'avoid'
      ? darkMode ? 'border-rose-300/45 bg-rose-500/15 text-rose-100 hover:bg-rose-500/25' : 'border-rose-200 bg-rose-50 text-rose-900 hover:bg-rose-100'
      : tradeDayRadarVerdict === 'cautious' || tradeDayRadarBlocked
        ? darkMode ? 'border-amber-300/45 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25' : 'border-amber-200 bg-amber-50 text-amber-900 hover:bg-amber-100'
        : tradeDayRadarVerdict === 'normal'
          ? darkMode ? 'border-emerald-300/35 bg-emerald-500/10 text-emerald-100 hover:bg-emerald-500/20' : 'border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-100'
          : darkMode ? 'border-slate-700 bg-slate-950/55 text-slate-200 hover:border-cyan-300/50' : 'border-gray-200 bg-white text-gray-800 hover:border-cyan-300';
    return (
      <div className={`rounded-xl shadow-sm ${darkMode ? 'border border-slate-700 bg-slate-900/80' : 'bg-white'}`}>
	        <div className="border-b border-gray-200 px-6 py-4">
	          <div className="flex items-start justify-between gap-3">
	            <div className="min-w-0">
	              <div className={`text-xs font-semibold uppercase ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>{tradeCopy.eyebrow}</div>
	              <h2 className={`mt-1 text-2xl font-bold ${darkMode ? 'text-slate-50' : 'text-gray-800'}`}>{tradeCopy.title}</h2>
	              <p className={`mt-1 max-w-xl text-sm ${darkMode ? 'text-slate-300' : 'text-gray-500'}`}>
	                {tradeCopy.subtitle(tradeDeliveryDate)}
	              </p>
	            </div>
	            <div className="flex shrink-0 flex-wrap items-end gap-3">
	              <div>
	                <label className={`block text-xs font-semibold uppercase ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
	                  {tradeCopy.deliveryDate}
	                </label>
	                <div className="mt-1 flex items-center gap-1.5">
	                  <button
	                    type="button"
	                    onClick={() => shiftLetsTradeDate(-1)}
	                    className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border transition ${
	                      darkMode
	                        ? 'border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800'
	                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
	                    }`}
	                    title="-1 dzień"
	                    aria-label="-1 dzień"
	                  >
	                    <ChevronLeft size={16} />
	                  </button>
	                  <input
	                    type="date"
	                    value={tradeDeliveryDate}
	                    onChange={(event) => handleLetsTradeDateChange(event.target.value)}
	                    className={`h-10 rounded-lg border px-3 text-sm font-semibold normal-case tabular-nums outline-none ${
	                      darkMode
	                        ? 'border-slate-700 bg-slate-950 text-slate-100 focus:border-emerald-400'
	                        : 'border-gray-200 bg-white text-gray-800 focus:border-emerald-500'
	                    }`}
	                  />
	                  <button
	                    type="button"
	                    onClick={() => shiftLetsTradeDate(1)}
	                    className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border transition ${
	                      darkMode
	                        ? 'border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800'
	                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
	                    }`}
	                    title="+1 dzień"
	                    aria-label="+1 dzień"
	                  >
	                    <ChevronRight size={16} />
	                  </button>
	                </div>
	              </div>
	              <button
	                type="button"
	                onClick={refreshWholeTraderView}
	                disabled={baseEntryRiskLoading || basePretradeLoading || externalFixIPreviewLoading || letsTradeOrderPlanLoading}
	                className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed ${
	                  darkMode
	                    ? 'border-sky-500/60 bg-sky-600/20 text-sky-200 hover:bg-sky-600/35 disabled:border-slate-700 disabled:bg-slate-900 disabled:text-slate-600'
	                    : 'border-sky-400 bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:border-gray-200 disabled:bg-white disabled:text-gray-400'
	                }`}
	              >
	                <RefreshCw size={14} className={(baseEntryRiskLoading || basePretradeLoading || externalFixIPreviewLoading || letsTradeOrderPlanLoading) ? 'animate-spin' : ''} />
	                {tradeCopy.refresh}
	              </button>
	            </div>
	          </div>
	        </div>

        <div className="space-y-5 px-6 py-5">
          <section className={`rounded-xl border p-4 ${sectionPanelClass}`}>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-3">
                {renderFlowBubble({ icon: BarChart3, step: 1, title: tradeCopy.step1Title, body: tradeCopy.flow.marketBody })}
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                {letsTradeOrderPlanLoading ? (
                  <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{renderInlineLoading(tradeCopy.loadingMarket, 'emerald')}</div>
                ) : null}
                <NavLink
                  to="/fix2-v5-sim/radar"
                  onClick={() => setDate(tradeDeliveryDate)}
                  className={`inline-flex min-h-[42px] max-w-[320px] items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm font-extrabold shadow-sm transition ${tradeDayRadarToneClass}`}
                  title={tradeDayRadarHelper}
                >
                  {dayRadarLoading && !tradeDayRadarData ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Radar className="h-4 w-4 shrink-0" />}
                  <span className="min-w-0">
                    <span className="block text-[10px] font-black uppercase tracking-[0.12em] opacity-65">{tradeCopy.dayRadarSummary}</span>
                    <span className="block truncate">{tradeDayRadarLabel}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 opacity-70" />
                </NavLink>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setIsLetsTradeExecutionPathPopoverOpen(false);
                      setIsLetsTradeYachtPopoverOpen((prev) => !prev);
                    }}
                    className={`inline-flex min-h-[42px] items-center rounded-xl border px-3 py-2 text-sm font-extrabold shadow-sm transition ${
                      isLetsTradeYachtPopoverOpen
                        ? darkMode
                          ? 'border-amber-300/70 bg-amber-400/15 text-amber-100'
                          : 'border-amber-300 bg-amber-50 text-amber-900'
                        : darkMode
                          ? 'border-slate-700 bg-slate-950/55 text-slate-100 hover:border-amber-300/60 hover:text-amber-100'
                          : 'border-gray-200 bg-white text-gray-800 hover:border-amber-300 hover:text-amber-900'
                    }`}
                    aria-expanded={isLetsTradeYachtPopoverOpen}
                  >
                    <Ship size={16} strokeWidth={2.4} className="mr-2 shrink-0" aria-hidden="true" />
                    {tradeCopy.yachtButton}
                  </button>
                  {isLetsTradeYachtPopoverOpen ? (
                    <div className={`absolute left-0 top-full z-40 mt-2 w-[min(88vw,380px)] overflow-hidden rounded-xl border shadow-2xl ${
                      darkMode
                        ? 'border-amber-300/30 bg-slate-950 text-slate-100'
                        : 'border-amber-200 bg-white text-gray-900'
                    }`}
                    >
                      <img
                        src={LETS_TRADE_YACHT_IMAGE_URL}
                        alt={tradeCopy.yachtImageAlt}
                        className="h-36 w-full object-cover"
                        loading="lazy"
                      />
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border text-lg font-black ${
                            hasYachtTarget
                              ? darkMode ? 'border-emerald-300/50 bg-emerald-500/15 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                              : darkMode ? 'border-rose-300/50 bg-rose-500/15 text-rose-100' : 'border-rose-200 bg-rose-50 text-rose-800'
                          }`}
                          >
                            {yachtMoodFace}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-extrabold">{yachtMoodTitle}</div>
                            <div className={`mt-1 text-xs leading-5 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{yachtMoodBody}</div>
                            <div className={`mt-2 inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold ${
                              darkMode ? 'border-amber-300/30 bg-amber-400/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900'
                            }`}
                            >
                              {tradeCopy.yachtTarget}
                            </div>
                          </div>
                        </div>
                        <p className={`mt-3 text-xs leading-5 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                          {tradeCopy.yachtDescription}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setIsLetsTradeYachtPopoverOpen(false);
                      setIsLetsTradeExecutionPathPopoverOpen((prev) => !prev);
                    }}
                    className={`inline-flex min-h-[42px] items-center gap-2 rounded-xl border px-3 py-2 text-sm font-extrabold shadow-sm transition ${
                      isLetsTradeExecutionPathPopoverOpen
                        ? darkMode
                          ? 'border-indigo-300/70 bg-indigo-400/20 text-indigo-100'
                          : 'border-indigo-400 bg-indigo-50 text-indigo-900'
                        : darkMode
                          ? 'border-indigo-400/45 bg-indigo-500/15 text-indigo-100 hover:bg-indigo-500/25'
                          : 'border-indigo-300 bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                    aria-expanded={isLetsTradeExecutionPathPopoverOpen}
                  >
                    <Activity className="h-4 w-4" />
                    {tradeCopy.executionPathButton || 'Ścieżka wykonania'}
                  </button>
                  {isLetsTradeExecutionPathPopoverOpen ? (
                    <div className={`absolute right-0 top-full z-50 mt-2 w-[min(92vw,1180px)] max-h-[78vh] overflow-auto rounded-2xl border p-3 shadow-2xl ${
                      darkMode
                        ? 'border-indigo-300/30 bg-slate-950 text-slate-100 shadow-black/40'
                        : 'border-indigo-200 bg-white text-gray-900 shadow-indigo-950/15'
                    }`}>
                      <div className={`absolute -top-1.5 right-10 h-3 w-3 rotate-45 border-l border-t ${
                        darkMode ? 'border-indigo-300/30 bg-slate-950' : 'border-indigo-200 bg-white'
                      }`} />
                      {renderExecutionPathPanel({
                        rows: executionPathRows,
                        summary: executionPathSummary,
                        availability: executionPathAvailability,
                        copy: tradeCopy,
                        compact: true,
                        expanded: isLetsTradeExecutionPathExpanded,
                        onToggle: () => setIsLetsTradeExecutionPathExpanded((prev) => !prev),
                        quarterPathKeyPrefix: `lets-trade:${tradeDeliveryDate}`,
                        notice: executionPathNotice,
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
	            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
	              {metricCards.map((card) => (
	                <div
	                  key={card.label}
	                  className={`flex min-h-[128px] flex-col justify-between rounded-lg border p-4 ${darkMode ? 'border-slate-700 bg-slate-950/70' : 'border-gray-200 bg-white'}`}
	                >
	                  <div>
	                    <div className={`flex flex-wrap items-center gap-1 text-xs font-semibold uppercase ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
	                      <span>{card.label}</span>
	                      {card.waiting ? <Loader2 size={12} className="animate-spin opacity-60" /> : null}
	                      {card.badge ? (
	                        <span className={`inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[10px] font-extrabold normal-case leading-none ${card.badgeClass || (darkMode ? 'border-slate-600 bg-slate-900 text-slate-300' : 'border-gray-200 bg-gray-50 text-gray-700')}`}>
	                          {card.badge}
	                        </span>
	                      ) : null}
	                      {renderHedgeWhyPopoverButton(card, `metric-${card.label}`)}
	                    </div>
	                    <div className={`mt-2 text-2xl font-extrabold tabular-nums ${card.valueClass}${card.waiting ? ' blur-sm select-none animate-pulse' : ''}`}>{card.value}</div>
	                  </div>
                  <div className={`mt-3 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{card.helper}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-7">
              {marketCards.map((card) => (
                <div key={card.label} className={`rounded-lg border p-3 ${darkMode ? 'border-slate-800 bg-slate-950/60' : 'border-gray-200 bg-white/80'}`}>
                  <div className={`text-[11px] font-semibold uppercase ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{card.label}</div>
                  <div className={`mt-1 text-base font-bold tabular-nums ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>{formatPriceOrDash(card.value)}</div>
                  <div className={`mt-1 text-[11px] ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{card.helper}</div>
                </div>
              ))}
            </div>
            {letsTradeOrderPlanError ? (
              <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${darkMode ? 'border-amber-500/40 bg-amber-500/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                {letsTradeOrderPlanError}
              </div>
            ) : null}
          </section>

          <section className={`rounded-xl border p-4 ${sectionPanelClass}`}>
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div>
                {renderFlowBubble({ icon: SlidersHorizontal, step: 2, title: tradeCopy.flow.entryTitle, body: tradeCopy.flow.entryBody, tone: 'blue' })}
              </div>
              <div className="flex flex-wrap items-start justify-end gap-2">
                <div className="relative inline-flex">
                  <button
                    type="button"
                    onClick={() => setIsLetsTradeEntryExplanationOpen((prev) => !prev)}
                    className={`inline-flex min-h-[40px] items-center gap-2 rounded-lg border px-3 py-2 text-xs font-extrabold shadow-sm transition ${
                      isLetsTradeEntryExplanationOpen
                        ? darkMode
                          ? 'border-sky-200/60 bg-sky-400/15 text-sky-50'
                          : 'border-sky-300 bg-sky-100 text-sky-950'
                        : darkMode
                          ? 'border-sky-300/35 bg-slate-950 text-sky-100 hover:bg-sky-500/10'
                          : 'border-sky-200 bg-sky-50 text-sky-900 hover:bg-sky-100'
                    }`}
                    aria-expanded={isLetsTradeEntryExplanationOpen}
                  >
                    <Info size={14} />
                    {tradeCopy.entryWhy}
                  </button>
                  {isLetsTradeEntryExplanationOpen ? (
                    <div className={`absolute right-0 top-full z-50 mt-3 w-[min(88vw,560px)] rounded-2xl border p-4 text-sm leading-6 shadow-2xl ${
                      darkMode
                        ? 'border-sky-300/35 bg-slate-950 text-sky-50 shadow-black/40'
                        : 'border-sky-200 bg-white text-sky-950 shadow-sky-950/15'
                    }`}
                    >
                      <div className={`absolute -top-2 right-8 h-4 w-4 rotate-45 border-l border-t ${
                        darkMode ? 'border-sky-300/35 bg-slate-950' : 'border-sky-200 bg-white'
                      }`} />
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-extrabold">{tradeCopy.entryWhyTitle}</div>
                        <span className={`rounded-full border px-2.5 py-1 text-[11px] font-extrabold ${entryRiskToneClass}`}>
                          {entryRiskLabel}
                        </span>
                      </div>
                      <div className={`mt-3 space-y-2 border-t pt-3 ${darkMode ? 'border-sky-300/20' : 'border-sky-100'}`}>
                        {entryExplainLines.map((line) => (
                          <div key={line} className={darkMode ? 'text-slate-100' : 'text-slate-800'}>
                            {line}
                          </div>
                        ))}
                      </div>
                      {entryHourChips.length ? (
                        <div className={`mt-3 border-t pt-3 ${darkMode ? 'border-sky-300/20' : 'border-sky-100'}`}>
                          <div className={`mb-2 text-[11px] font-bold uppercase ${darkMode ? 'text-sky-100/70' : 'text-sky-800/70'}`}>
                            {tradeCopy.entryRiskHours}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {entryHourChips.map((chip) => (
                              <span key={chip} className={`rounded-md border px-2 py-1 text-[11px] font-extrabold ${
                                darkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-sky-100 bg-sky-50 text-sky-950'
                              }`}>
                                {chip}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className={`mb-3 rounded-2xl border px-4 py-4 shadow-sm ${
              darkMode
                ? 'border-sky-300/25 bg-sky-500/10 text-sky-50'
                : 'border-sky-200 bg-sky-50 text-sky-950'
            }`}>
              {!entryRisk.ready || !hasEntryTriggerPrices ? (
                <div className={`text-sm leading-6 ${darkMode ? 'text-sky-50/85' : 'text-sky-950/80'}`}>
                  {entryRisk.ready ? tradeCopy.entryRadarObserveOnly : tradeCopy.entryMissing}
                </div>
              ) : (
                <>
                  <div className={`rounded-2xl border px-4 py-3 ${
                    darkMode
                      ? 'border-sky-300/30 bg-sky-500/12 text-sky-50'
                      : 'border-sky-200 bg-sky-50/90 text-sky-950'
                  }`}>
                    <div className="grid gap-2 lg:grid-cols-[104px_minmax(0,1fr)_minmax(0,1fr)] lg:items-center">
                      <div className="flex items-center justify-center lg:justify-start">
                        <div className={`flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl border ${
                          darkMode ? 'border-sky-300/30 bg-slate-950/45 text-sky-100' : 'border-sky-200 bg-white text-sky-800'
                        }`}>
                          <Radar className="h-11 w-11" />
                        </div>
                      </div>
                      <div className={`rounded-xl border px-4 py-2.5 ${
                        darkMode
                          ? 'border-emerald-300/30 bg-emerald-500/12 text-emerald-50'
                          : 'border-emerald-200 bg-emerald-50/90 text-emerald-950'
                      }`}>
                        <div className={`text-[11px] font-extrabold uppercase tracking-[0.16em] ${
                          darkMode ? 'text-emerald-200/80' : 'text-emerald-800/75'
                        }`}>
                          {howItWorksLanguage === 'pl' ? 'Kupuj tylko gdy' : 'Buy only when'}
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                          <span className="text-sm font-extrabold tabular-nums sm:text-[15px]">
                            {formatPriceOrDash(entryBuyTriggerPrice)}
                          </span>
                          <span className={`text-xs font-semibold ${darkMode ? 'text-emerald-100/85' : 'text-emerald-900/80'}`}>
                            {howItWorksLanguage === 'pl' ? 'lub niżej' : 'or lower'}
                          </span>
                        </div>
                      </div>
                      <div className={`rounded-xl border px-4 py-2.5 ${
                        darkMode
                          ? 'border-rose-300/30 bg-rose-500/12 text-rose-50'
                          : 'border-rose-200 bg-rose-50/90 text-rose-950'
                      }`}>
                        <div className={`text-[11px] font-extrabold uppercase tracking-[0.16em] ${
                          darkMode ? 'text-rose-200/80' : 'text-rose-800/75'
                        }`}>
                          {howItWorksLanguage === 'pl' ? 'Sprzedawaj tylko gdy' : 'Sell only when'}
                        </div>
                        <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                          <span className="text-sm font-extrabold tabular-nums sm:text-[15px]">
                            {formatPriceOrDash(entrySellTriggerPrice)}
                          </span>
                          <span className={`text-xs font-semibold ${darkMode ? 'text-rose-100/85' : 'text-rose-900/80'}`}>
                            {howItWorksLanguage === 'pl' ? 'lub wyżej' : 'or higher'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
              {[
                {
                  label: tradeCopy.entryRecommendation,
                  value: entryRisk.ready
                    ? (entryRiskHours > 0
                      ? entryRiskLabel
                      : (howItWorksLanguage === 'pl' ? 'Brak aktywnego shiftu' : 'No active shift'))
                    : '-',
                  helper: entryRisk.ready && tradeCopy.entryRadarSideSummary
                    ? tradeCopy.entryRadarSideSummary({ buyHours: entryBuyRiskHours, sellHours: entrySellRiskHours })
                    : entryRisk.ready
                      ? tradeCopy.entryRadarObserveOnly
                      : tradeCopy.entryMissing,
                  valueClass: entryRiskHours > 0
                    ? (entryRiskLevel === 'blocked' || entryRiskLevel === 'high' ? 'text-rose-300' : entryRiskLevel === 'medium' ? 'text-amber-200' : 'text-emerald-300')
                    : (darkMode ? 'text-slate-100' : 'text-slate-900'),
                },
                {
                  label: tradeCopy.entryBuyThreshold,
                  value: entryRisk.ready ? `${Number(entryBuy.risk_hours || 0)} h` : '-',
                  helper: `${tradeCopy.entryRiskCharge}: ${formatNumber(entryBuy.risk_charge_pln_mwh || 0)} PLN/MWh · ${tradeCopy.entryFullBlockHours(Number(entryBuy.full_block_hours || 0))}`,
                  valueClass: darkMode ? 'text-emerald-200' : 'text-emerald-700',
                },
                {
                  label: tradeCopy.entrySellThreshold,
                  value: entryRisk.ready ? `${Number(entrySell.risk_hours || 0)} h` : '-',
                  helper: `${tradeCopy.entryRiskCharge}: ${formatNumber(entrySell.risk_charge_pln_mwh || 0)} PLN/MWh · ${tradeCopy.entryStrongSpikeHours(Number(entrySell.strong_spike_hours || 0))}`,
                  valueClass: darkMode ? 'text-rose-200' : 'text-rose-700',
                },
                {
                  label: tradeCopy.entrySuggestedVolume,
                  value: entryRisk.ready && (entryRisk.goose_shifted_avg_q50_forecast ?? entryRisk.avg_q50_forecast) != null
                    ? `${formatNumber(entryRisk.goose_shifted_avg_q50_forecast ?? entryRisk.avg_q50_forecast)} PLN/MWh`
                    : '-',
                  helper: tradeCopy.entryNeutralBuffer(formatNumber(entryRisk.neutral_price_buffer_pln_mwh ?? 5)),
                  valueClass: darkMode ? 'text-sky-200' : 'text-sky-700',
                },
                {
                  label: tradeCopy.entryRiskHours,
                  value: entryRisk.ready ? String(entryRiskHours) : '-',
                  helper: entryHourChips.length ? entryHourChips.join(' · ') : '-',
                  valueClass: darkMode ? 'text-amber-100' : 'text-amber-800',
                },
              ].map((card) => (
                <div
                  key={`entry-risk-${card.label}`}
                  className={`flex min-h-[116px] flex-col justify-between rounded-lg border p-3 ${darkMode ? 'border-slate-700 bg-slate-950/65' : 'border-gray-200 bg-white'}`}
                >
                  <div className={`text-[11px] font-semibold uppercase ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{card.label}</div>
                  <div className={`mt-2 text-base font-extrabold tabular-nums ${card.valueClass}`}>{card.value}</div>
                  <div className={`mt-2 text-[11px] ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{card.helper}</div>
                </div>
              ))}
            </div>
          </section>

          <section className={`rounded-xl border ${darkMode ? 'border-slate-700 bg-slate-950/60' : 'border-gray-200 bg-white'}`}>
            <div className="border-b border-gray-200 px-4 py-4">
	              <div className="flex flex-wrap items-start justify-between gap-3">
	                <div className="flex-1 min-w-0">
	                  <div className="flex flex-wrap items-center gap-4">
	                    <div className={`inline-flex items-center gap-3 rounded-xl border px-3 py-2 transition-colors ${formatTheme.accentBorder} ${formatTheme.accentBg} ${formatTheme.accentText}`}
	                    >
	                      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border text-xs font-black tracking-tight ${formatTheme.numberBorder} ${formatTheme.numberBg} ${formatTheme.numberText}`}
	                      >
	                        3
	                      </span>
	                      <span>
	                        <span className="block text-sm font-extrabold">{tradeCopy.basketBadge ? tradeCopy.basketBadge(formatLabel) : tradeCopy.nobleBadge}</span>
	                        <span className={`block text-xs ${formatTheme.accentMutedText}`}>{tradeCopy.basketHelper ? tradeCopy.basketHelper(formatLabel) : tradeCopy.nobleHelper}</span>
	                      </span>
	                    </div>
	                    <div className={`inline-flex items-center rounded-xl border p-1 ${darkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-gray-50'}`}>
	                      {[{ key: 'noble', label: 'Noble' }, { key: 'bos', label: 'BOŚ' }].map((fmt) => {
	                        const active = orderPreviewFormat === fmt.key;
	                        const activeClass = active
	                          ? (fmt.key === 'bos'
	                              ? (darkMode ? 'bg-emerald-500 text-white shadow-sm' : 'bg-emerald-600 text-white shadow-sm')
	                              : (darkMode ? 'bg-amber-500 text-slate-900 shadow-sm' : 'bg-amber-500 text-white shadow-sm'))
	                          : (darkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-700 hover:bg-white');
	                        return (
	                          <button
	                            key={fmt.key}
	                            type="button"
	                            onClick={() => setOrderPreviewFormat(fmt.key)}
	                            className={`whitespace-nowrap rounded-lg px-4 py-1.5 text-sm font-bold transition-colors ${activeClass}`}
	                          >
	                            {fmt.label}
	                          </button>
	                        );
	                      })}
	                    </div>
	                  </div>
	                  <h3 className={`mt-2 text-lg font-bold ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>{tradeCopy.step3}</h3>
	                  <div className={`mt-1 text-sm font-semibold ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>{tradeCopy.orderPreview}: {modeLabels[orderPreviewMode] || orderPreviewMode}</div>
	                  <p className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
	                    {(typeof orderTiming[orderPreviewMode] === 'function' ? orderTiming[orderPreviewMode](formatLabel) : orderTiming[orderPreviewMode]) || ''}.
	                  </p>
                    {isSelectedIda1ClosePreview ? (
                      <div className={`mt-3 max-w-3xl rounded-lg border px-3 py-2 text-xs leading-5 ${
                        darkMode
                          ? 'border-amber-300/35 bg-amber-400/10 text-amber-100'
                          : 'border-amber-200 bg-amber-50 text-amber-900'
                      }`}
                      >
                        <div className="font-extrabold">{tradeCopy.ida1ClosePreviewTitle}</div>
                        <div className={darkMode ? 'text-amber-100/85' : 'text-amber-900/80'}>{tradeCopy.ida1ClosePreviewBody}</div>
                      </div>
                    ) : null}
                    {isSelectedFixIIPreviewAwaitingIda1 ? (
                      <div className={`mt-3 max-w-3xl rounded-lg border px-3 py-2 text-xs leading-5 ${
                        darkMode
                          ? 'border-amber-300/35 bg-amber-400/10 text-amber-100'
                          : 'border-amber-200 bg-amber-50 text-amber-900'
                      }`}
                      >
                        <div className="font-extrabold">{tradeCopy.fixIIPendingIda1Title}</div>
                        <div className={darkMode ? 'text-amber-100/85' : 'text-amber-900/80'}>{tradeCopy.fixIIPendingIda1Body}</div>
                      </div>
                    ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={refreshSelectedOrders}
                    disabled={isSelectedPreviewLoading}
                    className={`rounded-lg border px-3 py-2 text-xs font-semibold disabled:cursor-not-allowed ${
                      darkMode
                        ? 'border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800 disabled:text-slate-600'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:text-gray-400'
                    }`}
	                  >
	                    {isSelectedPreviewLoading ? tradeCopy.calculating : tradeCopy.refreshOrders}
	                  </button>
                  <button
                    type="button"
                    onClick={exportNoblePreviewXlsx}
                    disabled={!noblePackageHasRows}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${
                      darkMode
                        ? 'border-amber-500/50 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25'
                        : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
                    }`}
	                  >
	                    <FileSpreadsheet size={14} />
	                    {tradeCopy.downloadXlsx}
	                  </button>
                  <button
                    type="button"
                    onClick={exportBosLadderXlsx}
                    disabled={!bosLadderHasRows || bosXlsxDownloading}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${
                      darkMode
                        ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25'
                        : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    <FileSpreadsheet size={14} />
                    {bosXlsxDownloading ? tradeCopy.calculating : tradeCopy.downloadBosXlsx}
                  </button>
                </div>
              </div>
              {orderPreviewFormat === 'noble' && (
              <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                {orderStageCards.map((stage) => {
                  const active = orderPreviewMode === stage.key;
                  const mutedPreview = Boolean(stage.muted);
                  return (
                    <button
                      key={stage.key}
                      type="button"
                      onClick={() => handleLetsTradeModeSelect(stage.key)}
                      className={`rounded-lg border p-3 text-left transition ${
                        active && mutedPreview
                          ? darkMode
                            ? 'border-slate-500 bg-slate-800/45 text-slate-300 opacity-80'
                            : 'border-gray-300 bg-gray-100 text-gray-600 opacity-80'
                          : active
                          ? darkMode
                            ? 'border-amber-400 bg-amber-500/20 text-amber-50'
                            : 'border-amber-500 bg-amber-50 text-amber-900'
                          : mutedPreview
                            ? darkMode
                              ? 'border-slate-800 bg-slate-950/50 text-slate-500 opacity-70 hover:bg-slate-900/70'
                              : 'border-gray-200 bg-gray-100 text-gray-500 opacity-70 hover:bg-gray-50'
                          : darkMode
                            ? 'border-slate-700 bg-slate-950/70 text-slate-200 hover:bg-slate-900'
                            : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-bold">{stage.label}</span>
                        <span className="text-xs font-semibold tabular-nums">{stage.rows.length} rows</span>
                      </div>
                      <div className={`mt-1 text-[11px] ${active ? '' : darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{stage.helper}{active ? (isOrderPreviewCollapsed ? ' · kliknij aby rozwinąć' : ' · kliknij aby zwinąć') : ''}</div>
                      <div className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold tabular-nums ${
                        active && mutedPreview
                          ? darkMode
                            ? 'border-slate-600 bg-slate-950/40 text-slate-300'
                            : 'border-gray-300 bg-white/70 text-gray-500'
                          : active
                          ? darkMode
                            ? 'border-amber-300/50 bg-amber-300/10 text-amber-50'
                            : 'border-amber-200 bg-white text-amber-700'
                          : mutedPreview
                            ? darkMode
                              ? 'border-slate-800 bg-slate-900/60 text-slate-500'
                              : 'border-gray-200 bg-white/70 text-gray-400'
                          : darkMode
                            ? 'border-slate-700 bg-slate-900 text-slate-400'
                            : 'border-gray-200 bg-white text-gray-500'
                      }`}
                      >
                        <Clock3 className="h-3 w-3" />
                        <span>{tradeCopy.deadlineLabel} {stage.deadline}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              )}
            </div>
            <div className="overflow-x-auto">
              {orderPreviewFormat === 'bos' ? (
                <div className="px-4 py-4">{renderBosLadderPreview()}</div>
              ) : isOrderPreviewCollapsed ? null : isSelectedPreviewLoading && !activePreviewRows.length ? (
	                <div className={`px-4 py-6 text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
	                  {renderInlineLoading(tradeCopy.loadingPreview(modeLabels[orderPreviewMode] || 'order'), 'emerald')}
	                </div>
              ) : selectedPreviewError && !activePreviewRows.length ? (
                <div className={`px-4 py-6 text-sm ${darkMode ? 'text-amber-200' : 'text-amber-700'}`}>
                  {selectedPreviewError}
                </div>
              ) : !activePreviewRows.length ? (
	                <div className={`px-4 py-6 text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
	                  {tradeCopy.noPreview(modeLabels[orderPreviewMode] || orderPreviewMode)}
	                </div>
              ) : (
                <table className={`min-w-[760px] w-full text-sm ${isSelectedMutedPreview ? 'opacity-70 grayscale-[0.25]' : ''}`}>
                  <thead className={darkMode ? 'bg-slate-950 text-slate-300' : 'bg-gray-50 text-gray-500'}>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Instrument</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Side</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">{activeNoblePreviewShowsLimitPrice ? 'Limit price' : 'Price'}</th>
                      {activeNoblePreviewHasReferencePrice ? (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Reference</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Margin</th>
                        </>
                      ) : null}
                      {previewRowsHaveSourceLabel ? (
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Źródło</th>
                      ) : null}
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRowsForTable.map((row, index) => (
                      <tr
                        key={`${row.instrument}-${row.side}-${row.price}-${row.volume}-${row.source || 'staged'}-${index}`}
                        className={isSelectedMutedPreview
                          ? darkMode
                            ? 'border-t border-slate-800 bg-slate-950/40 text-slate-400'
                            : 'border-t border-gray-200 bg-gray-50 text-gray-500'
                          : darkMode
                            ? 'border-t border-slate-800 text-slate-100'
                            : 'border-t border-gray-200 text-gray-800'}
                      >
                        <td className="px-4 py-2 font-mono text-xs">{row.instrument}</td>
                        <td className="px-4 py-2 font-semibold">{row.side}</td>
                        <td className="px-4 py-2 tabular-nums">{formatNumber(row.price)}</td>
                        {activeNoblePreviewHasReferencePrice ? (
                          <>
                            <td className="px-4 py-2 tabular-nums">{formatNumber(row.reference_price)}</td>
                            <td className="px-4 py-2 tabular-nums">{formatNumber(row.limit_margin)}</td>
                          </>
                        ) : null}
                        {previewRowsHaveSourceLabel ? (
                          <td className="px-4 py-2">
                            {getNoblePreviewSourceLabel(row) ? (
                              <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${darkMode ? 'border-amber-400/40 bg-amber-400/10 text-amber-100' : 'border-amber-300 bg-amber-50 text-amber-800'}`}>
                                {getNoblePreviewSourceLabel(row)}
                              </span>
                            ) : (
                              <span className={darkMode ? 'text-slate-600' : 'text-gray-400'}>-</span>
                            )}
                          </td>
                        ) : null}
                        <td className="px-4 py-2 tabular-nums">{formatOrderVolume(row.volume)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </section>
        </div>
      </div>
    );
  };

  const shiftSimulationDate = (dayDelta) => {
    const shiftedDate = shiftIsoDate(date, dayDelta);
    if (!shiftedDate) return;
    setDate(shiftedDate);
  };

  const renderDayRadarSection = () => {
    const snapshot = dayRadarData?.snapshot || null;
    const payload = snapshot?.payload || {};
    const summaryPayload = payload.summary || {};
    const pipelineRun = dayRadarData?.pipeline_run || null;
    const snapshotStatus = String(snapshot?.status || pipelineRun?.status || 'not_generated');
    const isBlocked = snapshotStatus === 'blocked_missing_predict' || payload.status === 'blocked_missing_predict';
    const isFailed = snapshotStatus === 'failed';
    const isRegenerateDisabled = dayRadarRunning || isBlocked;
    const verdict = summaryPayload.verdict || (isBlocked ? 'waiting' : 'not ready');
    const verdictClass = {
      normal: darkMode ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-800',
      cautious: darkMode ? 'border-amber-400/40 bg-amber-400/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800',
      avoid: darkMode ? 'border-rose-400/40 bg-rose-400/10 text-rose-100' : 'border-rose-200 bg-rose-50 text-rose-800',
      waiting: darkMode ? 'border-slate-600 bg-slate-800 text-slate-200' : 'border-gray-200 bg-gray-50 text-gray-700',
    }[verdict] || (darkMode ? 'border-slate-600 bg-slate-800 text-slate-200' : 'border-gray-200 bg-gray-50 text-gray-700');
    const features = payload.features || {};
    const anomalies = payload.anomalies || [];
    const marketContext = payload.market_context || {};
    const notableSignals = payload.notable_signals || [];
    const historicalMatch = payload.similar_days?.historical_match || {};
    const marketNews = payload.market_news || {};
    const marketNewsView = parseDayRadarMarketNews(marketNews);
    const isDataReadBullet = (item) => /szybki read danych|read danych|dane/i.test(String(item?.section || ''));
    const isAnomalyMatchBullet = (item) => /dopasowanie anomalii|anomali|pow[oó]d rozjazdu/i.test(String(item?.section || ''));
    const dataReadBullets = marketNewsView.bullets.filter(isDataReadBullet);
    const anomalyMatchBullets = marketNewsView.bullets.filter(isAnomalyMatchBullet);
    const rawMarketNewsBullets = marketNewsView.bullets.filter((item) => !isDataReadBullet(item) && !isAnomalyMatchBullet(item));
    const marketTapeBullets = buildDayRadarMarketTape(anomalyMatchBullets, rawMarketNewsBullets);
    const topVerdictMatch = marketTapeBullets.find((item) => item.tapeKind === 'anomaly' && item.severity === 'high')
      || marketTapeBullets.find((item) => item.tapeKind === 'anomaly')
      || marketTapeBullets.find((item) => item.severity === 'high')
      || null;
    const getDataReadGroup = (item) => {
      const text = `${item?.section || ''} ${item?.text || ''}`.toLowerCase();
      if (/meteo|meteologica|de.?[-–]?\s*pl|pl.?[-–]?\s*de/.test(text)) return 'meteo';
      if (/q10|q50|q90|predict|prognoz|rozrzut|band/.test(text)) return 'predict';
      if (/atc|import|export|capacity|przepustowo/.test(text)) return 'capacity';
      if (/pv|wind|wiatr|res|load|obci[aą][żz]enie|pse/.test(text)) return 'res';
      return 'other';
    };
    const cleanDataReadText = (item) => String(item?.impactText || item?.factText || item?.text || '')
      .replace(/\s*Dane:\s*snapshot radaru\.?$/i, '')
      .trim();
    const dataReadByGroup = dataReadBullets.reduce((acc, item) => {
      const group = getDataReadGroup(item);
      acc[group] = acc[group] || [];
      acc[group].push(item);
      return acc;
    }, {});
    const radarShell = {
      panel: darkMode
        ? 'rounded-2xl border border-slate-800 bg-slate-900/80 shadow-[0_20px_60px_rgba(2,6,23,0.35)]'
        : 'rounded-2xl border border-slate-200 bg-white/95 shadow-[0_16px_40px_rgba(15,23,42,0.08)]',
      inset: darkMode
        ? 'rounded-xl border border-slate-800/80 bg-slate-950/70'
        : 'rounded-xl border border-slate-200 bg-slate-50/90',
      softPanel: darkMode
        ? 'rounded-xl border border-slate-800 bg-slate-900/70'
        : 'rounded-xl border border-slate-200 bg-white',
      heading: darkMode ? 'text-slate-100' : 'text-slate-900',
      muted: darkMode ? 'text-slate-400' : 'text-slate-600',
      eyebrow: darkMode ? 'text-slate-500' : 'text-slate-500',
    };
    const signalClass = (severity) => ({
      high: darkMode ? 'border-rose-400/35 bg-rose-400/10 text-rose-100' : 'border-rose-200 bg-rose-50 text-rose-800',
      medium: darkMode ? 'border-amber-400/35 bg-amber-400/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800',
      low: darkMode ? 'border-cyan-400/30 bg-cyan-400/10 text-cyan-100' : 'border-cyan-200 bg-cyan-50 text-cyan-800',
    }[String(severity || '').toLowerCase()] || (darkMode ? 'border-slate-700 bg-slate-900 text-slate-200' : 'border-gray-200 bg-white text-gray-700'));
    const newsClass = (severity) => ({
      high: {
        card: darkMode ? 'border-slate-800 bg-slate-950/45' : 'border-slate-200 bg-white',
        badge: darkMode ? 'border-rose-300/40 bg-rose-300/10 text-rose-100' : 'border-rose-200 bg-white text-rose-700',
        impact: darkMode ? 'border-slate-700 border-l-rose-300/70 bg-slate-950/55 text-slate-100' : 'border-slate-200 border-l-rose-400 bg-slate-50 text-slate-800',
      },
      medium: {
        card: darkMode ? 'border-slate-800 bg-slate-950/45' : 'border-slate-200 bg-white',
        badge: darkMode ? 'border-amber-300/40 bg-amber-300/10 text-amber-100' : 'border-amber-200 bg-white text-amber-700',
        impact: darkMode ? 'border-slate-700 border-l-amber-300/70 bg-slate-950/55 text-slate-100' : 'border-slate-200 border-l-amber-400 bg-slate-50 text-slate-800',
      },
      low: {
        card: darkMode ? 'border-slate-800 bg-slate-950/45' : 'border-slate-200 bg-white',
        badge: darkMode ? 'border-slate-600 bg-slate-950 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600',
        impact: darkMode ? 'border-slate-700 bg-slate-950/55 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-700',
      },
    }[String(severity || '').toLowerCase()] || {
      card: darkMode ? 'border-slate-800 bg-slate-950/45' : 'border-slate-200 bg-white',
      badge: darkMode ? 'border-slate-600 bg-slate-950 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600',
      impact: darkMode ? 'border-slate-700 bg-slate-950/55 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-700',
    });
    const signalByKey = notableSignals.reduce((acc, signal) => {
      if (signal?.key) acc[signal.key] = signal;
      return acc;
    }, {});
    const signalSeverityRank = { high: 3, medium: 2, low: 1 };
    const metricClass = (severity) => ({
      high: {
        card: darkMode ? 'border-slate-800 border-l-rose-300/70 bg-slate-950/55' : 'border-slate-200 border-l-rose-400 bg-white',
        label: darkMode ? 'text-slate-400' : 'text-slate-500',
        value: darkMode ? 'text-slate-50' : 'text-slate-950',
        helper: darkMode ? 'text-slate-400' : 'text-slate-500',
        badge: darkMode ? 'border-rose-300/40 bg-rose-300/10 text-rose-100' : 'border-rose-200 bg-white text-rose-700',
      },
      medium: {
        card: darkMode ? 'border-slate-800 border-l-amber-300/70 bg-slate-950/55' : 'border-slate-200 border-l-amber-400 bg-white',
        label: darkMode ? 'text-slate-400' : 'text-slate-500',
        value: darkMode ? 'text-slate-50' : 'text-slate-950',
        helper: darkMode ? 'text-slate-400' : 'text-slate-500',
        badge: darkMode ? 'border-amber-300/40 bg-amber-300/10 text-amber-100' : 'border-amber-200 bg-white text-amber-700',
      },
      low: {
        card: darkMode ? 'border-slate-800 bg-slate-950/55' : 'border-slate-200 bg-white',
        label: darkMode ? 'text-slate-400' : 'text-slate-500',
        value: darkMode ? 'text-slate-50' : 'text-slate-950',
        helper: darkMode ? 'text-slate-400' : 'text-slate-500',
        badge: darkMode ? 'border-cyan-300/30 bg-cyan-300/10 text-cyan-100' : 'border-cyan-200 bg-white text-cyan-700',
      },
    }[String(severity || '').toLowerCase()] || {
      card: radarShell.inset,
      label: radarShell.eyebrow,
      value: radarShell.heading,
      helper: radarShell.muted,
      badge: darkMode ? 'border-slate-700 bg-slate-950 text-slate-400' : 'border-slate-200 bg-slate-50 text-slate-500',
    });
    const interpretSignal = (signal) => {
      const key = signal?.key || '';
      const value = Number(signal?.value);
      const severity = String(signal?.severity || '').toLowerCase();
      const fallbackTag = severity === 'high' ? 'alert' : severity === 'medium' ? 'watch' : 'kontekst';
      if (key === 'import_capacity_avg' && Number.isFinite(value)) {
        if (value < 1000) {
          return {
            tag: 'watch',
            note: 'Ograniczona przepustowość importu. To jest słabszy bufor dla PL, więc napięcia z CEE/CWE mogą łatwiej przejść w polskie ceny.',
          };
        }
        if (value >= 1500) {
          return {
            tag: 'ok',
            note: 'OK / umiarkowany bufor importu. Samo w sobie nie jest alarmem, ale mówi ile miejsca ma PL na import, jeśli CEE/CWE zacznie ciągnąć energię.',
          };
        }
        return {
          tag: 'kontekst',
          note: 'Kontekst: import capacity nie jest skrajnie niska, ale nadal warto patrzeć, czy flow z regionu nie konkuruje z zapotrzebowaniem PL.',
        };
      }
      if (key === 'export_capacity_avg' && Number.isFinite(value)) {
        if (value < 2500) {
          return {
            tag: 'watch',
            note: 'Ograniczona przepustowość exportu. To może zatrzymywać nadwyżkę w PL i zmieniać reakcję cen przy wysokim PV/wiatrze.',
          };
        }
        if (value >= 4500) {
          return {
            tag: 'ok',
            note: 'OK / wysoka przepustowość exportu. Samo w sobie nie jest negatywne, ale oznacza, że nadwyżka PL może łatwiej wypływać i mocniej wiązać ceny z regionem.',
          };
        }
        return {
          tag: 'kontekst',
          note: 'Kontekst: export capacity jest w środku zakresu; ważne głównie razem z PV/wiatrem i spreadami regionalnymi.',
        };
      }
      if (key === 'pse_pv_avg' && Number.isFinite(value)) {
        return {
          tag: 'kontekst',
          note: value >= 3500
            ? 'Kontekst: wysoki PV. Kluczowe są godziny południowe, zachmurzenie i to, czy nadwyżka ma gdzie odpłynąć przez export.'
            : 'Kontekst: PV wpływa głównie na profil południa i ryzyko korekt przy zmianach zachmurzenia.',
        };
      }
      if (key === 'pse_wind_avg' && Number.isFinite(value)) {
        return {
          tag: 'kontekst',
          note: value >= 3000
            ? 'Kontekst: podwyższony wiatr. Może obniżać residual load, ale przy awariach/flow w regionie trzeba patrzeć na rampy i eksport.'
            : 'Kontekst: wiatr jest ważny dla spreadów godzinowych, ramp i potrzeby importu w słabszych godzinach.',
        };
      }
      if (key === 'de_minus_pl_avg' && Number.isFinite(value)) {
        return {
          tag: severity === 'high' ? 'alert' : 'watch',
          note: signal?.note || 'Spread Meteologica PL-DE jest kierunkowym sygnałem do ręcznego sprawdzenia rynku i flow.',
        };
      }
      if (key === 'q90_q10_spread_avg' && Number.isFinite(value)) {
        return {
          tag: 'watch',
          note: signal?.note || 'Szeroki przedział q10-q90 oznacza większą niepewność profilu i słabszą stabilność forecastu.',
        };
      }
      return {
        tag: fallbackTag,
        note: signal?.note || '',
      };
    };
    const signalTagClass = (tag) => ({
      alert: darkMode ? 'border-rose-300/40 bg-rose-300/10 text-rose-100' : 'border-rose-200 bg-white text-rose-700',
      watch: darkMode ? 'border-amber-300/40 bg-amber-300/10 text-amber-100' : 'border-amber-200 bg-white text-amber-700',
      ok: darkMode ? 'border-emerald-300/35 bg-emerald-300/10 text-emerald-100' : 'border-emerald-200 bg-white text-emerald-700',
      kontekst: darkMode ? 'border-cyan-300/30 bg-cyan-300/10 text-cyan-100' : 'border-cyan-200 bg-white text-cyan-700',
    }[tag] || (darkMode ? 'border-slate-700 bg-slate-950 text-slate-300' : 'border-slate-200 bg-white text-slate-600'));
    const configuredSignalGroups = [
      { key: 'meteo', label: 'Meteologica PL-DE', signalKeys: ['de_minus_pl_avg'] },
      { key: 'predict', label: 'Predict spread', signalKeys: ['q90_q10_spread_avg'] },
      { key: 'capacity', label: 'Import / export capacity', signalKeys: ['import_capacity_avg', 'export_capacity_avg'] },
      { key: 'res', label: 'PV / wind / load', signalKeys: ['pse_pv_avg', 'pse_wind_avg'] },
    ];
    const groupedSignalKeys = new Set(configuredSignalGroups.flatMap((group) => group.signalKeys));
    const signalGroups = [
      ...configuredSignalGroups.map((group) => {
        const signals = group.signalKeys.map((key) => signalByKey[key]).filter(Boolean);
        const read = dataReadByGroup[group.key]?.[0] || null;
        return { ...group, signals, read };
      }).filter((group) => group.signals.length || group.read),
      ...notableSignals
        .filter((signal) => signal?.key && !groupedSignalKeys.has(signal.key))
        .map((signal) => ({
          key: signal.key,
          label: signal.label,
          signalKeys: [signal.key],
          signals: [signal],
          read: null,
        })),
    ];
    const radarVariantLabel = simulationVariant === 'goose-v5' ? 'Goose v5' : 'Goose v3';
    const radarVariantShortLabel = simulationVariant === 'goose-v5' ? 'v5' : 'v3';
    const metricCards = [
      { key: 'q50_avg', label: 'q50 forecast', value: features.q50_avg, unit: 'PLN/MWh', helper: `${radarVariantShortLabel} daily avg` },
      { key: 'q90_q10_spread_avg', label: 'q10-q90 band', value: features.q90_q10_spread_avg, unit: 'PLN/MWh', helper: 'uncertainty' },
      { key: 'de_minus_pl_avg', label: 'DE - PL meteo', value: features.de_minus_pl_avg, unit: 'PLN/MWh', helper: 'regional spread' },
      { key: 'pse_wind_avg', label: 'Wind avg', value: features.pse_wind_avg, unit: 'MW', helper: 'PSE forecast' },
      { key: 'pse_pv_avg', label: 'PV avg', value: features.pse_pv_avg, unit: 'MW', helper: 'PSE forecast' },
      { key: 'import_capacity_avg', label: 'Import cap avg', value: features.import_capacity_avg, unit: 'MW', helper: 'Nord Pool' },
      { key: 'export_capacity_avg', label: 'Export cap avg', value: features.export_capacity_avg, unit: 'MW', helper: 'Nord Pool' },
      { key: 'ttf_gas_value', label: 'TTF latest', value: features.ttf_gas_value, unit: 'EUR/MWh', helper: features.ttf_gas_date || 'latest DB point' },
    ].filter(({ value }) => value !== null && value !== undefined);
    const verdictPanelClass = {
      normal: darkMode ? 'border-emerald-300/45 bg-emerald-300/10 shadow-[0_0_0_1px_rgba(110,231,183,0.08)]' : 'border-emerald-200 bg-emerald-50',
      cautious: darkMode ? 'border-amber-300/55 bg-amber-300/10 shadow-[0_0_0_1px_rgba(251,191,36,0.12)]' : 'border-amber-200 bg-amber-50',
      avoid: darkMode ? 'border-rose-300/55 bg-rose-300/10 shadow-[0_0_0_1px_rgba(251,113,133,0.12)]' : 'border-rose-200 bg-rose-50',
      waiting: radarShell.inset,
    }[verdict] || radarShell.inset;
    const verdictTextClass = {
      normal: darkMode ? 'text-emerald-50' : 'text-emerald-950',
      cautious: darkMode ? 'text-amber-50' : 'text-amber-950',
      avoid: darkMode ? 'text-rose-50' : 'text-rose-950',
      waiting: radarShell.heading,
    }[verdict] || radarShell.heading;
    const verdictMutedClass = {
      normal: darkMode ? 'text-emerald-100/80' : 'text-emerald-800/80',
      cautious: darkMode ? 'text-amber-100/85' : 'text-amber-800/85',
      avoid: darkMode ? 'text-rose-100/85' : 'text-rose-800/85',
      waiting: radarShell.muted,
    }[verdict] || radarShell.muted;
    const renderRadarReadCard = (item, index, prefix = 'read', options = {}) => {
      const classes = newsClass(item.severity);
      const sectionLabel = item.displaySection || item.section;
      const featured = Boolean(options.featured);
      const compact = Boolean(options.compact);
      const severityRailClass = {
        high: darkMode ? 'bg-rose-300/80' : 'bg-rose-500',
        medium: darkMode ? 'bg-amber-300/80' : 'bg-amber-500',
        low: darkMode ? 'bg-slate-500' : 'bg-slate-300',
      }[String(item.severity || '').toLowerCase()] || (darkMode ? 'bg-slate-600' : 'bg-slate-300');
      const typeLabel = item.tapeKind === 'anomaly' ? 'dopasowanie' : 'news';
      return (
        <div key={`${prefix}-${item.text}-${index}`} className={`relative flex h-full flex-col overflow-hidden rounded-xl border text-sm leading-relaxed ${compact ? 'p-3.5' : 'p-4'} ${classes.card} ${featured ? 'shadow-[0_12px_28px_rgba(2,6,23,0.22)]' : ''}`}>
          <div className={`absolute inset-y-0 left-0 w-1 ${severityRailClass}`} />
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            {sectionLabel ? (
              <div className={`pl-2 text-[11px] font-semibold uppercase tracking-[0.14em] ${radarShell.eyebrow}`}>{sectionLabel}</div>
            ) : <span />}
            <div className="flex items-center gap-2">
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em] ${darkMode ? 'border-slate-700 bg-slate-950/60 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600'}`}>
                {typeLabel}
              </span>
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em] ${classes.badge}`}>
                {item.severity || 'med'}
              </span>
            </div>
          </div>
          {item.factText ? (
            <div className={`pl-2 ${featured ? 'font-semibold' : ''} ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              {renderDayRadarInlineText(item.factText, darkMode)}
            </div>
          ) : null}
          {item.supportingNewsText ? (
            <div className={`ml-2 mt-3 rounded-lg border px-3 py-2 ${darkMode ? 'border-slate-700/80 bg-slate-950/30 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-700'}`}>
              <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.14em] opacity-60">Powiązany news</div>
              {renderDayRadarInlineText(item.supportingNewsText, darkMode)}
            </div>
          ) : null}
          {item.impactText ? (
            <div className={`ml-2 mt-3 rounded-lg border px-3 py-2 ${classes.impact}`}>
              <div className="text-[10px] font-extrabold uppercase tracking-[0.14em] opacity-70">Wpływ na nasz dzień</div>
              <div className="mt-1 font-semibold">{renderDayRadarInlineText(item.impactText, darkMode)}</div>
            </div>
          ) : null}
          {item.sourceText ? (
            <div className={`ml-2 mt-auto pt-3 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              {renderDayRadarInlineText(item.sourceText, darkMode)}
            </div>
          ) : null}
        </div>
      );
    };

    return (
      <section className={`${radarShell.panel} overflow-hidden`}>
        <div className={`border-b px-6 py-5 ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${darkMode ? 'border border-cyan-400/30 bg-cyan-400/10 text-cyan-200' : 'border border-cyan-300 bg-cyan-50 text-cyan-700'}`}>
                <Radar className="h-3.5 w-3.5" />
                Market read
              </div>
              <h2 className={`mt-3 text-2xl font-semibold tracking-tight ${radarShell.heading}`}>Market read dla aukcji D+1</h2>
              <p className={`mt-2 max-w-3xl text-sm leading-6 ${radarShell.muted}`}>
                Dzień obserwacji {marketContext.observation_date || '—'} → delivery {marketContext.delivery_date || date}. Fokus: co wydarzyło się dziś na rynku i czy zmienia ręczne nastawienie przed orderami.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => shiftSimulationDate(-1)} className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border ${darkMode ? 'border-slate-700 bg-slate-950 text-slate-200' : 'border-slate-300 bg-white text-slate-700'}`}>
                <ChevronLeft size={16} />
              </button>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`h-10 rounded-xl border px-3 text-sm font-semibold tabular-nums focus:ring-2 focus:ring-cyan-400 ${darkMode ? 'border-slate-700 bg-slate-950 text-slate-100' : 'border-slate-300 bg-white text-slate-800'}`}
              />
              <button type="button" onClick={() => shiftSimulationDate(1)} className={`inline-flex h-10 w-10 items-center justify-center rounded-xl border ${darkMode ? 'border-slate-700 bg-slate-950 text-slate-200' : 'border-slate-300 bg-white text-slate-700'}`}>
                <ChevronRight size={16} />
              </button>
              <button
                type="button"
                onClick={() => loadDayRadar({ force: true })}
                disabled={dayRadarLoading}
                className={`inline-flex h-10 items-center gap-2 rounded-xl border px-4 text-sm font-medium transition ${darkMode ? 'border-slate-700 bg-slate-950/80 text-slate-100 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-900 hover:bg-slate-50'} disabled:opacity-60`}
              >
                <RefreshCw size={15} className={dayRadarLoading ? 'animate-spin' : ''} />
                Status
              </button>
              <button
                type="button"
                onClick={runDayRadar}
                disabled={isRegenerateDisabled}
                title={isBlocked ? `Najpierw musi być gotowy predict ${radarVariantLabel} dla tej daty.` : undefined}
                className="inline-flex h-10 items-center gap-2 rounded-xl border border-cyan-300 bg-cyan-300 px-4 text-sm font-semibold text-slate-950 shadow-[0_8px_24px_rgba(34,211,238,0.22)] disabled:cursor-not-allowed disabled:opacity-45"
              >
                {dayRadarRunning ? <Loader2 size={15} className="animate-spin" /> : <Radar size={15} />}
                Regeneruj
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          {dayRadarError ? (
            <div className={`mb-5 rounded-2xl border px-4 py-3 text-sm ${darkMode ? 'border-rose-400/40 bg-rose-400/10 text-rose-100' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
              {dayRadarError}
            </div>
          ) : null}

          {!snapshot && !dayRadarLoading ? (
            <div className={`rounded-2xl border border-dashed px-5 py-8 text-sm ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-600'}`}>
              Brak zapisanego radaru dla tej daty.
            </div>
          ) : null}

          {isBlocked ? (
            <div className={`rounded-2xl border px-5 py-4 text-sm ${darkMode ? 'border-amber-400/40 bg-amber-400/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              <div className="font-bold">Czeka na predict {radarVariantShortLabel}</div>
              <div className="mt-1">Radar potrzebuje gotowego planu {radarVariantShortLabel} dla tej daty delivery.</div>
            </div>
          ) : null}

          {isFailed ? (
            <div className={`rounded-2xl border px-5 py-4 text-sm ${darkMode ? 'border-rose-400/40 bg-rose-400/10 text-rose-100' : 'border-rose-200 bg-rose-50 text-rose-800'}`}>
              {snapshot?.error_message || 'Generowanie radaru zakończyło się błędem.'}
            </div>
          ) : null}

          {payload.status === 'success' ? (
            <div className="space-y-6">
              <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
                <div className={`rounded-xl border p-5 ${verdictPanelClass}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${verdictMutedClass}`}>Verdict</div>
                      <div className={`mt-2 text-3xl font-semibold ${verdictTextClass}`}>{summaryPayload.headline || '-'}</div>
                    </div>
                    <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-extrabold uppercase ${verdictClass}`}>
                      {verdict}
                    </span>
                  </div>
                  <ul className="mt-4 space-y-2 text-sm">
                    {(summaryPayload.reasons || []).map((reason, index) => (
                      <li key={`${reason}-${index}`} className={verdictMutedClass}>{reason}</li>
                    ))}
                  </ul>
                  {topVerdictMatch ? (
                    <div className={`mt-5 rounded-xl border px-4 py-3 text-sm leading-relaxed ${darkMode ? 'border-slate-700/80 bg-slate-950/35 text-slate-100' : 'border-slate-200 bg-white/70 text-slate-800'}`}>
                      <div className={`mb-1 flex flex-wrap items-center justify-between gap-2 text-[10px] font-extrabold uppercase tracking-[0.14em] ${verdictMutedClass}`}>
                        <span>Główny driver z market tape</span>
                        <span className={`rounded-full border px-2 py-0.5 tracking-[0.12em] ${newsClass(topVerdictMatch.severity).badge}`}>
                          {topVerdictMatch.severity || 'med'}
                        </span>
                      </div>
                      <div className="font-semibold">{renderDayRadarInlineText(topVerdictMatch.factText || topVerdictMatch.text, darkMode)}</div>
                      <div className={`mt-2 text-xs ${verdictMutedClass}`}>Pełny wpływ i źródła są niżej w sekcji market tape.</div>
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  {metricCards.map((card) => {
                    const signal = signalByKey[card.key];
                    const classes = metricClass(signal?.severity);
                    const meaning = signal ? interpretSignal(signal) : null;
                    return (
                      <div key={card.label} className={`rounded-xl border border-l-4 p-4 transition ${classes.card}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${classes.label}`}>{card.label}</div>
                          {meaning ? (
                            <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.1em] ${classes.badge}`}>
                              {meaning.tag}
                            </span>
                          ) : null}
                        </div>
                        <div className={`mt-2 text-2xl font-semibold tabular-nums ${classes.value}`}>
                          {formatNumber(card.value)} <span className={`text-xs ${classes.helper}`}>{card.unit}</span>
                        </div>
                        <div className={`mt-2 text-xs font-semibold uppercase tracking-[0.08em] ${classes.helper}`}>{meaning?.tag || card.helper}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={`${radarShell.inset} p-5`}>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${radarShell.eyebrow}`}>Sygnały danych</div>
                    <div className={`mt-1 text-sm ${radarShell.muted}`}>Liczby z modelu, Meteologica, PSE i capacity scalone z krótkim readem GPT. Bez podobnych dni i bez post-factum PnL.</div>
                  </div>
                  <span className={`text-xs ${radarShell.muted}`}>{payload.generated_at || snapshot.generated_at || '-'}</span>
                </div>
                {signalGroups.length ? (
                  <div className="grid gap-3 lg:grid-cols-2">
                    {signalGroups.map((group) => {
                      const primarySignal = [...group.signals].sort((left, right) => (
                        (signalSeverityRank[String(right?.severity || '').toLowerCase()] || 0)
                        - (signalSeverityRank[String(left?.severity || '').toLowerCase()] || 0)
                      ))[0];
                      const primaryMeaning = primarySignal ? interpretSignal(primarySignal) : null;
                      const readText = group.read ? cleanDataReadText(group.read) : '';
                      const severity = primarySignal?.severity || group.read?.severity;
                      return (
                        <div key={group.key || group.label} className={`rounded-xl border p-4 ${signalClass(severity)}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="text-sm font-semibold">{group.label}</div>
                                {primaryMeaning ? (
                                  <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.1em] ${signalTagClass(primaryMeaning.tag)}`}>
                                    {primaryMeaning.tag}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                            {group.read ? (
                              <span className={`rounded-full border px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.1em] ${newsClass(group.read.severity).badge}`}>
                                GPT {group.read.severity || 'med'}
                              </span>
                            ) : null}
                          </div>
                          {group.signals.length ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {group.signals.map((signal) => {
                                const meaning = interpretSignal(signal);
                                return (
                                  <div key={signal.key || signal.label} className={`min-w-[180px] flex-1 rounded-lg border px-3 py-2 ${darkMode ? 'border-slate-700/70 bg-slate-950/25' : 'border-white/70 bg-white/45'}`}>
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0">
                                        <div className="truncate text-[11px] font-semibold uppercase tracking-[0.08em] opacity-75" title={signal.label}>
                                          {signal.label}
                                        </div>
                                        <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.08em] opacity-55">{meaning.tag}</div>
                                      </div>
                                      <div className="shrink-0 text-right">
                                        <div className="text-base font-semibold tabular-nums">
                                          {formatNumber(signal.value)}
                                        </div>
                                        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] opacity-65">{signal.unit || signal.severity}</div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : null}
                          {readText ? (
                            <div className={`mt-3 border-t pt-3 text-sm leading-relaxed ${darkMode ? 'border-slate-700/70 text-slate-200' : 'border-slate-200/80 text-slate-700'}`}>
                              <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.14em] opacity-55">Read GPT</div>
                              {renderDayRadarInlineText(readText, darkMode)}
                            </div>
                          ) : primaryMeaning?.note ? (
                            <div className="mt-3 text-xs leading-relaxed opacity-80">{primaryMeaning.note}</div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className={`rounded-xl border border-dashed px-4 py-5 text-sm ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-600'}`}>
                    Brak twardych odchyleń liczbowych. Decydujący może być dzisiejszy event/news flow.
                  </div>
                )}
                {anomalies.length ? (
                  <div className={`mt-4 text-xs ${radarShell.muted}`}>
                    Historyczne odchylenia: {anomalies.slice(0, 4).map((row) => `${row.feature} ${formatNumber(row.value)} / pctl ${formatNumber(row.percentile)}`).join(' · ')}
                  </div>
                ) : null}
              </div>

              <div className={`${radarShell.inset} p-5`}>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${radarShell.eyebrow}`}>Dzień analogiczny</div>
                    <div className={`mt-1 text-sm ${radarShell.muted}`}>Jeden starszy dzień o analogicznym układzie sygnałów. Jeśli próg nie przechodzi, pokazujemy brak zamiast dobierać na siłę.</div>
                  </div>
                  <span className={`rounded-full border px-2.5 py-1 text-[11px] font-extrabold uppercase ${historicalMatch.status === 'found' ? (darkMode ? 'border-cyan-400/35 bg-cyan-400/10 text-cyan-100' : 'border-cyan-200 bg-cyan-50 text-cyan-800') : (darkMode ? 'border-slate-700 bg-slate-950 text-slate-400' : 'border-slate-200 bg-white text-slate-500')}`}>
                    {historicalMatch.status === 'found' ? historicalMatch.date : 'brak'}
                  </span>
                </div>
                {historicalMatch.status === 'found' ? (
                  <div className="grid gap-3 lg:grid-cols-[1fr_0.9fr]">
                    <div className={`${radarShell.softPanel} p-4`}>
                      <div className={`text-lg font-semibold ${radarShell.heading}`}>{historicalMatch.date}</div>
                      <div className={`mt-2 text-sm ${radarShell.muted}`}>{historicalMatch.reason || 'Zbieżny układ sygnałów.'}</div>
                      <div className={`mt-3 flex flex-wrap gap-2 text-xs ${radarShell.muted}`}>
                        {(historicalMatch.matched_tags || []).map((tag) => (
                          <span key={tag.key || tag.label} className={`rounded-full border px-2 py-1 ${darkMode ? 'border-slate-700 bg-slate-950/60' : 'border-slate-200 bg-slate-50'}`}>{tag.label}</span>
                        ))}
                      </div>
                    </div>
                    <div className={`${radarShell.softPanel} grid gap-3 p-4 sm:grid-cols-3`}>
                      <div>
                        <div className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${radarShell.eyebrow}`}>Distance</div>
                        <div className={`mt-1 text-xl font-semibold tabular-nums ${radarShell.heading}`}>{formatNumber(historicalMatch.distance)}</div>
                      </div>
                      <div>
                        <div className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${radarShell.eyebrow}`}>DE-PL delta</div>
                        <div className={`mt-1 text-xl font-semibold tabular-nums ${radarShell.heading}`}>{formatNumber(historicalMatch.de_minus_pl_delta)}</div>
                      </div>
                      <div>
                        <div className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${radarShell.eyebrow}`}>Hist. PnL</div>
                        <div className={`mt-1 text-xl font-semibold tabular-nums ${getValueColor(historicalMatch.outcome?.daily_pnl)}`}>{formatNumber(historicalMatch.outcome?.daily_pnl)}</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`rounded-xl border border-dashed px-4 py-5 text-sm ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-600'}`}>
                    {historicalMatch.reason || 'Brak historycznych dni wystarczająco podobnych do dzisiejszego układu sygnałów.'}
                  </div>
                )}
              </div>

              <div className={`${radarShell.inset} p-5`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${radarShell.eyebrow}`}>Market tape i powody rozjazdu</div>
                    <div className={`mt-1 text-sm ${radarShell.muted}`}>Dopasowania anomalii i newsy z sieci w jednej liście, bez powtarzania tych samych tematów.</div>
                  </div>
                  <span className={`rounded-full border px-2.5 py-1 text-[11px] font-extrabold uppercase ${marketNews.status === 'success' ? (darkMode ? 'border-emerald-400/35 bg-emerald-400/10 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-800') : (darkMode ? 'border-amber-400/35 bg-amber-400/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800')}`}>
                    {marketNews.status || 'skipped'}
                  </span>
                </div>

                {marketNewsView.intro ? (
                  <div className={`mt-4 rounded-xl border px-4 py-3 text-sm leading-relaxed ${darkMode ? 'border-slate-800 bg-slate-950/55 text-slate-200' : 'border-slate-200 bg-white text-slate-700'}`}>
                    {renderDayRadarInlineText(marketNewsView.intro, darkMode)}
                  </div>
                ) : null}

                {marketTapeBullets.length ? (
                  <div className="mt-4 grid items-stretch gap-3 xl:grid-cols-2">
                    {marketTapeBullets.map((item, index) => (
                      <div key={`${item.topicKey || item.text}-${index}`} className={`h-full ${index === 0 && item.severity === 'high' ? 'xl:col-span-2' : ''}`}>
                        {renderRadarReadCard(item, index, 'market-tape', {
                          featured: index === 0 && item.severity === 'high',
                          compact: !(index === 0 && item.severity === 'high'),
                        })}
                      </div>
                    ))}
                    {!anomalyMatchBullets.length && anomalies.length ? (
                      <div className={`rounded-xl border border-dashed px-4 py-5 text-sm xl:col-span-2 ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-600'}`}>
                        Brak potwierdzonego newsowego wyjaśnienia dla największych anomalii. Traktuj sygnały jako liczbowy alert, nie jako zidentyfikowaną przyczynę.
                      </div>
                    ) : null}
                  </div>
                ) : marketNews.status !== 'success' ? (
                  <div className={`mt-4 rounded-xl border border-dashed px-4 py-5 text-sm ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-300 text-slate-600'}`}>
                    {getDayRadarMarketNewsText(marketNews)}
                  </div>
                ) : null}

                {marketNewsView.citations.length ? (
                  <div className={`mt-4 border-t pt-4 ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                    <div className="flex flex-wrap gap-2">
                      {marketNewsView.citations.map((source, index) => (
                        <a key={`${source.url || source}-${index}`} href={source.url || source} target="_blank" rel="noreferrer" className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${darkMode ? 'border-cyan-400/40 text-cyan-100 hover:bg-cyan-400/10' : 'border-cyan-200 text-cyan-700 hover:bg-cyan-50'}`}>
                          <span className="max-w-[220px] truncate">{getDayRadarCitationLabel(source)}</span>
                          <ExternalLink size={12} />
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </section>
    );
  };

  return (
    <div className={`sim-v5-page min-h-screen p-6 lg:p-8 ${
      darkMode
        ? 'sim-dark bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.10),_transparent_28%),linear-gradient(180deg,#020617,#082032_45%,#020617)]'
        : 'sim-light bg-[radial-gradient(circle_at_top,_rgba(34,211,238,0.08),_transparent_28%),linear-gradient(180deg,#f8fafc,#eef6ff_42%,#f8fafc)]'
    }`}>
      {renderModifiedSimulationHomeBadge()}
      {renderSimulationModifierFloatingPanel()}
      {renderScrollToTopButton()}
      {renderDailyBaseHedgeFloatingTooltip()}
      <div className="mx-auto max-w-[1800px] space-y-6">
	        <div className={simulationShellClass}>
            <div className={`rounded-t-[30px] border-b px-6 py-7 lg:px-8 lg:py-8 ${
              darkMode
                ? 'border-cyan-500/15 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.14),_transparent_30%),linear-gradient(135deg,rgba(8,47,73,0.55),rgba(15,23,42,0.92))]'
                : 'border-cyan-100 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.12),_transparent_32%),linear-gradient(135deg,#f0fdfa,#eff6ff)]'
            }`}>
	            <div className="flex items-center justify-between gap-4">
	              <div className="max-w-4xl">
                  <div className={simulationEyebrowClass}>Goose IDA2 simulation</div>
	              <h1 className={`mt-2 text-4xl font-bold tracking-tight lg:text-[2.75rem] ${darkMode ? 'text-slate-50' : 'text-slate-950'}`}>Goose IDA2 simulation</h1>
	              <p className={`mt-3 max-w-3xl text-sm leading-7 lg:text-base ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{simCopy.pageSubtitle}</p>
	            </div>
	          </div>
            </div>
	          <div className={`rounded-b-[30px] border-t-0 ${darkMode ? 'border-slate-700/70 bg-slate-950/30' : 'border-slate-200 bg-white/80'}`}>
	            <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">
	              <div className="flex min-w-0 flex-wrap items-center gap-2">
	                <span className={simulationEyebrowClass}>
	                  {simCopy.mainNavLabel}
	                </span>
	                <div className={`flex flex-wrap gap-1 rounded-2xl p-1 ${darkMode ? 'bg-slate-950/55 ring-1 ring-white/5' : 'bg-white/80 shadow-sm'}`}>
	                  {primarySimSectionTabs.map((tab) => {
	                    const TabIcon = tab.icon;
	                    return (
	                      <NavLink
	                        key={tab.key}
	                        to={tab.path}
	                        title={tab.helper}
	                        className={({ isActive }) => `inline-flex min-h-[38px] items-center gap-2 rounded-xl px-4 py-2 text-sm font-extrabold transition ${
	                          isActive || activeSection === tab.key
	                            ? (darkMode ? 'bg-sky-500/85 text-white shadow-sm ring-1 ring-sky-200/35' : 'bg-sky-600 text-white shadow-sm ring-1 ring-sky-200')
	                            : (darkMode ? 'text-slate-200 hover:bg-slate-800/90' : 'text-gray-800 hover:bg-white hover:shadow-sm')
	                        }`}
	                      >
	                        <TabIcon className="h-4 w-4" />
	                        <span>{tab.label}</span>
	                      </NavLink>
	                    );
		                  })}
	                </div>
	              </div>
	              <div className="flex flex-wrap items-center gap-2">
	                <div className={`inline-flex rounded-xl border p-1 ${darkMode ? 'border-slate-700/70 bg-slate-950/70' : 'border-slate-200 bg-white/90 shadow-sm'}`} title={simCopy.languageLabel}>
	                  {SIM_LANGUAGE_OPTIONS.map((option) => (
	                    <button
	                      key={`sim-lang-${option.key}`}
	                      type="button"
	                      onClick={() => setHowItWorksLanguage(option.key)}
	                      className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-bold transition ${
	                        howItWorksLanguage === option.key
	                          ? (darkMode ? 'bg-slate-700 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm')
	                          : (darkMode ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-100' : 'text-gray-600 hover:bg-white/80 hover:text-gray-900')
	                      }`}
	                    >
	                      <span>{option.flag}</span>
	                      <span>{option.shortLabel}</span>
	                    </button>
	                  ))}
	                </div>
	                <button
	                  type="button"
	                  onClick={() => setIsSimulationModifierOpen((prev) => !prev)}
	                  className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-bold shadow-sm transition ${
	                    isSimulationModifierOpen
	                      ? (darkMode
	                        ? 'border-amber-300 bg-amber-400 text-slate-950'
	                        : 'border-amber-600 bg-amber-500 text-slate-950')
	                      : (darkMode
	                        ? 'border-amber-300/70 bg-amber-500/20 text-amber-100 hover:bg-amber-500/30'
	                        : 'border-amber-500 bg-amber-100 text-amber-950 hover:bg-amber-200')
	                  }`}
	                >
	                  <SlidersHorizontal className="h-4 w-4" />
	                  {simCopy.modifySimulation}
	                </button>
	              </div>
	            </div>
	            <div className={`border-t px-3 py-3 ${darkMode ? 'border-slate-800 bg-slate-950/35' : 'border-slate-200 bg-slate-50/70'}`}>
	              <div className="flex flex-wrap items-center gap-2">
	                <span className={`${simulationEyebrowClass} mr-1`}>
	                  {simCopy.technicalNavLabel}
	                </span>
	                {technicalSimSectionTabs.map((tab) => {
	                  const TabIcon = tab.icon;
	                  return (
	                    <NavLink
	                      key={tab.key}
	                      to={tab.path}
	                      title={tab.helper}
	                        className={({ isActive }) => `inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition ${
	                          isActive || activeSection === tab.key
	                            ? (darkMode ? 'bg-slate-700/90 text-slate-50 shadow-sm ring-1 ring-cyan-300/15' : 'bg-white text-sky-700 shadow-sm ring-1 ring-sky-100')
	                            : (darkMode ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-100' : 'text-gray-600 hover:bg-white hover:text-gray-900')
	                        }`}
	                      >
	                      <TabIcon className="h-4 w-4" />
	                      <span>{tab.label}</span>
	                    </NavLink>
	                  );
	                })}
	              </div>
	            </div>
	          </div>
	        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 text-red-800">
            <AlertCircle className="w-5 h-5 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {actionMessage && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-800">
            {actionMessage}
          </div>
        )}

	        {/* ── Results Range section ─────────────────────────────── */}
	        {isResultsDataSection && (
	        <div className={`${simulationBandClass} p-1.5 space-y-6`}>
	          {!isLetsTradeSection && (
	          <div className={`${simulationInsetCardClass} p-6`}>
            <div className={`mb-1 flex flex-wrap items-center gap-2 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <Calendar size={16} className={darkMode ? 'text-slate-500' : 'text-gray-500'} />
              <span className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{resultsCopy.rangeTitle}</span>
              <span>{resultsCopy.rangeHelper}</span>
            </div>
	            <div className="flex items-center gap-4 mt-3">
	              <div className="flex items-center gap-2">
	                <label className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{resultsCopy.from}</label>
	                <input type="date" value={startDate} onChange={(e) => { latestParityRequestRef.current += 1; latestQuickReplayRequestRef.current += 1; setStartDate(e.target.value); setParityReport(null); setQuickReplayResult(null); setParityReportLoading(false); setQuickReplayLoading(false); }} className={`px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${darkMode ? 'border border-slate-700 bg-slate-950 text-slate-100' : 'border border-gray-300 bg-white text-gray-800'}`} />
	              </div>
	            <div className="flex items-center gap-2">
		                <label className={`text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{resultsCopy.to}</label>
		                <input type="date" value={endDate} onChange={(e) => { latestParityRequestRef.current += 1; latestQuickReplayRequestRef.current += 1; setEndDate(e.target.value); setParityReport(null); setQuickReplayResult(null); setParityReportLoading(false); setQuickReplayLoading(false); }} className={`px-3 py-2 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${darkMode ? 'border border-slate-700 bg-slate-950 text-slate-100' : 'border border-gray-300 bg-white text-gray-800'}`} />
		              </div>
              <button
                onClick={() => loadResultsData({ forceResults: true })}
                disabled={loading || resultsLoading}
                title={resultsCopy.refreshTitle}
                className="rounded-lg bg-emerald-600 px-5 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-400"
	              >
	                {resultsRefreshLabel}
              </button>
            </div>
		          </div>
	          )}

	          {(resultsLoading || parityReportLoading || quickReplayLoading) && renderMooseLoading(
            quickReplayLoading ? resultsCopy.loadingReplayTitle : parityReportLoading ? resultsCopy.loadingCompareTitle : resultsCopy.loadingResultsTitle,
            quickReplayLoading
              ? resultsCopy.loadingReplayBody
              : parityReportLoading
              ? resultsCopy.loadingCompareBody
              : isDailyResultsSection
              ? resultsCopy.loadingDailyBody
              : resultsCopy.loadingResultsBody,
          )}

          {(isResultsSection || isDailyResultsSection || isLetsTradeSection || isBaseSimulationSection) && renderModifiedSimulationStatus()}

          {isLetsTradeSection && renderLetsTradeSection()}

          {isBaseSimulationSection && renderGooseRiskManagementSection()}

          {false && isBaseSimulationSection && (
          <>
          <div className={`rounded-xl shadow-sm ${darkMode ? 'border border-slate-700 bg-slate-900/80' : 'bg-white'}`}>
            <div className="border-b border-gray-200 px-6 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-slate-50' : 'text-gray-800'}`}>{baseHedgeCopy.settledReplayTitle}</h2>
                  <p className={`mt-1 text-sm ${darkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                    {baseHedgeCopy.settledReplayBody}
                  </p>
                </div>
                <div className={`rounded-lg border px-3 py-2 text-sm ${darkMode ? 'border-slate-700 bg-slate-950 text-slate-200' : 'border-gray-200 bg-gray-50 text-gray-700'}`}>
                  {baseHedgeCopy.dayCount(baseSimulationRows.length)}
                </div>
              </div>
            </div>
            <div className={`border-b px-6 py-5 ${darkMode ? 'border-slate-800 bg-slate-950/25' : 'border-gray-200 bg-white'}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className={`text-sm font-extrabold ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>
                    {howItWorksLanguage === 'pl' ? 'Podsumowanie risk management' : 'Risk management summary'}
                  </div>
	                  <div className={`mt-1 max-w-5xl text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
	                    {howItWorksLanguage === 'pl'
	                      ? 'Jeden widok rozdziela Goose risk dla BASE, realne hedge ordery z risk management, blokady/limity BUY Fix I, BASE carry oraz spike risk. Tylko wykonane hedge ordery wchodzą do hedge PnL i fill rate.'
	                      : 'One view separates Goose risk context for BASE, real Fix I risk-management hedge orders, Fix I BUY blocks/caps, BASE carry, and spike risk. Only executed hedge orders enter hedge PnL and fill rate.'}
	                  </div>
                </div>
                <div className={`rounded-full border px-3 py-1 text-xs font-bold ${
                  darkMode ? 'border-slate-700 bg-slate-900 text-slate-300' : 'border-gray-200 bg-gray-50 text-gray-600'
                }`}>
                  {baseHedgeCopy.dayCount(baseSimulationRows.length)}
                </div>
              </div>

	              <div className="mt-4 grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                <div
                  className={`relative overflow-hidden rounded-xl border p-4 ${darkMode ? 'border-slate-700 bg-slate-950/45' : 'border-gray-200 bg-gray-50/70'}`}
                  aria-busy={loading || resultsLoading || baseEntryRiskRowsLoading}
                >
                  <div className={`text-[11px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                    {baseHedgeCopy.preBaseSummaryTitle}
                  </div>
                  <div className={`mt-1 text-[11px] ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {baseHedgeCopy.preBaseSummaryBody}
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-3">
                    {[
                      [baseHedgeCopy.preBaseSummaryDelta, String(basePreBaseReplaySummary.comparableDays), 'neutral'],
                      [baseHedgeCopy.preBaseSummaryCounterfactual, String(basePreBaseReplaySummary.highOrBlockedDays), 'loss'],
                      [baseHedgeCopy.preBaseSummaryBetter, String(basePreBaseReplaySummary.lowDays), 'gain'],
                    ].map(([label, value, tone]) => (
                      <div key={`guard-summary-prebase-${label}`} className={`flex min-h-[54px] items-center justify-between gap-3 rounded-lg border px-3 py-2 ${
                        tone === 'loss'
                          ? darkMode ? 'border-rose-400/25 bg-rose-500/10' : 'border-rose-200 bg-rose-50'
                          : tone === 'gain'
                            ? darkMode ? 'border-emerald-400/25 bg-emerald-500/10' : 'border-emerald-200 bg-emerald-50'
                            : darkMode ? 'border-slate-700 bg-slate-900/70' : 'border-gray-200 bg-white'
                      }`}>
                        <div className={`min-w-0 text-[10px] font-bold uppercase leading-3 tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{label}</div>
                        <div className={`shrink-0 text-lg font-extrabold tabular-nums ${
                          tone === 'loss'
                            ? darkMode ? 'text-rose-100' : 'text-rose-700'
                            : tone === 'gain'
                              ? darkMode ? 'text-emerald-100' : 'text-emerald-700'
                              : darkMode ? 'text-slate-100' : 'text-gray-900'
                        }`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className={`mt-3 grid gap-1.5 text-[11px] font-semibold ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                    {[
                      [baseHedgeCopy.preBaseSummaryWorse, `${basePreBaseReplaySummary.buyRiskHours}h`],
                      [baseHedgeCopy.preBaseSummarySkipped, `${basePreBaseReplaySummary.sellRiskHours}h`],
                      [baseHedgeCopy.preBaseSummaryActual, String(basePreBaseReplaySummary.mediumDays)],
                    ].map(([label, value]) => (
                      <div key={`guard-summary-prebase-inline-${label}`} className="flex items-center justify-between gap-3">
                        <span className="min-w-0">{label}</span>
                        <span className="shrink-0 font-extrabold tabular-nums">{value}</span>
                      </div>
                    ))}
                  </div>
                  {(loading || resultsLoading || baseEntryRiskRowsLoading) && (
                    <div className={`absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-xl text-xs font-bold backdrop-blur-sm ${
                      darkMode ? 'bg-slate-950/55 text-slate-100' : 'bg-white/65 text-gray-700'
                    }`}>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{baseHedgeCopy.preBaseLoading}</span>
                    </div>
                  )}
                </div>

                <div
                  className={`relative overflow-hidden rounded-xl border p-4 ${darkMode ? 'border-slate-700 bg-slate-950/45' : 'border-gray-200 bg-gray-50/70'}`}
                  aria-busy={loading || resultsLoading}
                >
                  <div className={`text-[11px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                    Goose Fix I execution
                  </div>
                  <div className={`mt-1 text-[11px] ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    {howItWorksLanguage === 'pl'
                      ? 'Goose live backtest: ordery Fix I wynikające z logiki Goose i historycznego fillu po cenie clearing.'
                      : 'Goose live backtest: Fix I orders from Goose logic and historical clearing-price fill.'}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {[
                      [baseHedgeCopy.guardDays, String(gooseFixIModeSummary.days || guardCalibrationSummary.totals.days || 0), 'neutral'],
                      [howItWorksLanguage === 'pl' ? 'Godziny Fix I' : 'Fix I hours', `${gooseFixIModeSummary.gooseFixIOrderHours || guardCalibrationSummary.totals.gooseFixIOrderHours || 0}h`, 'neutral'],
                      [baseHedgeCopy.hedgeOrderVolume, `${formatNumber(gooseFixIModeSummary.orderVolume || 0)} MWh`, 'neutral'],
                      [baseHedgeCopy.hedgeExecutedVolume, `${formatNumber(gooseFixIModeSummary.executedVolume || 0)} MWh`, 'gain'],
                    ].map(([label, value, tone]) => (
                      <div key={`guard-summary-hedge-${label}`} className={`rounded-lg border px-3 py-2 ${
                        tone === 'gain'
                          ? darkMode ? 'border-emerald-400/25 bg-emerald-500/10' : 'border-emerald-200 bg-emerald-50'
                          : tone === 'warn'
                            ? darkMode ? 'border-amber-400/25 bg-amber-500/10' : 'border-amber-200 bg-amber-50'
                            : darkMode ? 'border-slate-700 bg-slate-900/70' : 'border-gray-200 bg-white'
                      }`}>
                        <div className={`text-[10px] font-bold uppercase leading-3 tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{label}</div>
                        <div className={`mt-1 text-base font-extrabold tabular-nums sm:text-lg ${
                          tone === 'gain'
                            ? darkMode ? 'text-emerald-100' : 'text-emerald-700'
                            : tone === 'warn'
                              ? darkMode ? 'text-amber-100' : 'text-amber-700'
                              : darkMode ? 'text-slate-100' : 'text-gray-900'
                        }`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {(loading || resultsLoading) && (
                    <div className={`absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-xl text-xs font-bold backdrop-blur-sm ${
                      darkMode ? 'bg-slate-950/55 text-slate-100' : 'bg-white/65 text-gray-700'
                    }`}>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{resultsCopy.loading}</span>
                    </div>
                  )}
                </div>

                <div
                  className={`relative overflow-hidden rounded-xl border p-4 ${darkMode ? 'border-cyan-300/25 bg-cyan-500/10' : 'border-cyan-200 bg-cyan-50/80'}`}
                  aria-busy={loading || resultsLoading}
                >
                  <div className={`text-[11px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-cyan-200/70' : 'text-cyan-800/70'}`}>
                    Goose risk shift
                  </div>
                  <div className={`mt-1 text-[11px] ${darkMode ? 'text-cyan-50/80' : 'text-cyan-950/70'}`}>
                    {howItWorksLanguage === 'pl'
                      ? 'Collapse-risk shift przesuwa kwantyle Goose przed decyzją orderową. To jest risk management Goose, nie stary Negative Guard.'
                      : 'Collapse-risk shift moves Goose quantiles before order decisions. This is Goose risk management, not the old Negative Guard.'}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {[
                      [howItWorksLanguage === 'pl' ? 'Dni ze shiftem' : 'Shift days', String(gooseRiskShiftModeSummary.days || 0), 'neutral'],
                      [howItWorksLanguage === 'pl' ? 'Godziny shiftu' : 'Shift hours', `${gooseRiskShiftModeSummary.gooseRiskShiftHours || 0}h`, 'neutral'],
                      [howItWorksLanguage === 'pl' ? 'Suma abs shift' : 'Abs shift sum', `${formatNumber(gooseRiskShiftModeSummary.gooseRiskShiftAbsSum || 0)} PLN/MWh`, 'neutral'],
                      [howItWorksLanguage === 'pl' ? 'Risk score' : 'Risk score', gooseRiskShiftModeSummary.days > 0 ? 'aktywny' : '-', 'gain'],
                    ].map(([label, value, tone]) => (
                      <div key={`guard-summary-negative-${label}`} className={`rounded-lg border px-3 py-2 ${
                        tone === 'gain'
                          ? darkMode ? 'border-emerald-300/25 bg-emerald-500/10' : 'border-emerald-200 bg-white/80'
                          : darkMode ? 'border-cyan-300/20 bg-slate-950/45' : 'border-cyan-200 bg-white/80'
                      }`}>
                        <div className={`text-[10px] font-bold uppercase leading-3 tracking-wide ${darkMode ? 'text-cyan-200/70' : 'text-cyan-900/60'}`}>{label}</div>
                        <div className={`mt-1 text-base font-extrabold tabular-nums sm:text-lg ${
                          tone === 'gain'
                            ? darkMode ? 'text-emerald-100' : 'text-emerald-700'
                            : darkMode ? 'text-cyan-50' : 'text-cyan-950'
                        }`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className={`mt-2 text-[11px] font-semibold ${darkMode ? 'text-cyan-50/70' : 'text-cyan-950/70'}`}>
                    {howItWorksLanguage === 'pl' ? 'Model' : 'Model'}: <span className={darkMode ? 'text-cyan-50' : 'text-cyan-950'}>{getSimulationVariantLabel()}</span>
                  </div>
                  <div className={`mt-3 text-[11px] font-semibold ${darkMode ? 'text-cyan-50/70' : 'text-cyan-950/70'}`}>
                    {howItWorksLanguage === 'pl' ? 'Źródło' : 'Source'}: <span className={darkMode ? 'text-cyan-50' : 'text-cyan-950'}>predictions.risk_score.shift</span>
                  </div>
                  {(loading || resultsLoading) && (
                    <div className={`absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-xl text-xs font-bold backdrop-blur-sm ${
                      darkMode ? 'bg-slate-950/55 text-cyan-50' : 'bg-cyan-50/70 text-cyan-950'
                    }`}>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{resultsCopy.loading}</span>
                    </div>
                  )}
                </div>

                <div
                  className={`relative overflow-hidden rounded-xl border p-4 ${darkMode ? 'border-cyan-300/25 bg-cyan-500/10' : 'border-cyan-200 bg-cyan-50/80'}`}
                  aria-busy={loading || resultsLoading}
                >
                  <div className={`text-[11px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-cyan-200/70' : 'text-cyan-800/70'}`}>
                    {baseHedgeCopy.fixIBuyGuardLabel}
                  </div>
                    <div className={`mt-1 text-[11px] ${darkMode ? 'text-cyan-50/80' : 'text-cyan-950/70'}`}>
                      {howItWorksLanguage === 'pl'
                      ? 'Interwencja Goose ryzyka: blokada albo limit zwykłego BUY Fix I. Nie wchodzi do hedge fill rate ani hedge PnL.'
                      : 'Goose risk intervention: blocks or caps normal Fix I BUY. It does not enter hedge fill rate or hedge PnL.'}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {[
                      [baseHedgeCopy.buyGuardDays, String(guardCalibrationSummary.totals.buyGuardDays)],
                      [baseHedgeCopy.buyBlockedHours, `${guardCalibrationSummary.totals.buyGuardBlockedHours}h`],
                      [baseHedgeCopy.buyCappedHours, `${guardCalibrationSummary.totals.buyGuardCappedHours}h`],
                      [baseHedgeCopy.buyGuardVolume, formatBuyGuardVolumeSummary(guardCalibrationSummary.totals)],
                    ].map(([label, value]) => (
                      <div key={`guard-summary-buy-${label}`} className={`rounded-lg border px-3 py-2 ${darkMode ? 'border-cyan-300/20 bg-slate-950/45' : 'border-cyan-200 bg-white/80'}`}>
                        <div className={`text-[10px] font-bold uppercase leading-3 tracking-wide ${darkMode ? 'text-cyan-200/70' : 'text-cyan-900/60'}`}>{label}</div>
                        <div className={`mt-1 text-base font-extrabold tabular-nums sm:text-lg ${darkMode ? 'text-cyan-50' : 'text-cyan-950'}`}>{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className={`mt-2 text-[11px] font-semibold ${darkMode ? 'text-cyan-50/70' : 'text-cyan-950/70'}`}>
                    {howItWorksLanguage === 'pl' ? 'Limit/blokada' : 'Cap/block'}: <span className={darkMode ? 'text-cyan-50' : 'text-cyan-950'}>{formatBuyGuardCapSummary(guardCalibrationSummary.totals)}</span>
                  </div>
                  <div className={`mt-3 text-[11px] font-semibold ${darkMode ? 'text-cyan-50/70' : 'text-cyan-950/70'}`}>
                    {baseHedgeCopy.executedPnlColumn}: <span className={getValueColor(guardCalibrationSummary.totals.executedPnl)}>{formatSignedNumber(guardCalibrationSummary.totals.executedPnl, ' PLN')}</span>
                  </div>
	                  {(loading || resultsLoading) && (
	                    <div className={`absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-xl text-xs font-bold backdrop-blur-sm ${
	                      darkMode ? 'bg-slate-950/55 text-cyan-50' : 'bg-cyan-50/70 text-cyan-950'
                    }`}>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{resultsCopy.loading}</span>
	                    </div>
	                  )}
	                </div>
	                <div
	                  className={`relative overflow-hidden rounded-xl border p-4 ${darkMode ? 'border-indigo-300/25 bg-indigo-500/10' : 'border-indigo-200 bg-indigo-50/80'}`}
	                  aria-busy={loading || resultsLoading}
	                >
	                  <div className={`text-[11px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-indigo-200/70' : 'text-indigo-800/70'}`}>
	                    {baseHedgeCopy.baseCarryGuardLabel}
	                  </div>
	                  <div className={`mt-1 text-[11px] ${darkMode ? 'text-indigo-50/80' : 'text-indigo-950/70'}`}>
	                    {howItWorksLanguage === 'pl'
	                      ? 'Domyka albo blokuje residual BASE po fixingach. Wartość jest w ścieżce BASE/settle, poza PnL hedge Fix I.'
	                      : 'Closes or blocks BASE residual after fixings. Its value belongs to the BASE/settle path, outside Fix I hedge PnL.'}
	                  </div>
	                  <div className="mt-3 grid grid-cols-2 gap-2">
	                    {[
	                      [howItWorksLanguage === 'pl' ? 'Dni carry' : 'Carry days', String(guardTotalsSummary.baseCarryDays || 0)],
	                      [baseHedgeCopy.forcedFix2Label, `${formatNumber(guardTotalsSummary.baseForcedFix2Volume || 0)} MWh`],
	                      [baseHedgeCopy.blockedIda1Label, `${formatNumber(guardTotalsSummary.baseBlockedIda1Volume || 0)} MWh`],
	                      [baseHedgeCopy.blockedIda2Label, `${formatNumber(guardTotalsSummary.baseBlockedIda2Volume || 0)} MWh`],
	                    ].map(([label, value]) => (
	                      <div key={`guard-summary-carry-${label}`} className={`rounded-lg border px-3 py-2 ${darkMode ? 'border-indigo-300/20 bg-slate-950/45' : 'border-indigo-200 bg-white/80'}`}>
	                        <div className={`text-[10px] font-bold uppercase leading-3 tracking-wide ${darkMode ? 'text-indigo-200/70' : 'text-indigo-900/60'}`}>{label}</div>
	                        <div className={`mt-1 text-base font-extrabold tabular-nums sm:text-lg ${darkMode ? 'text-indigo-50' : 'text-indigo-950'}`}>{value}</div>
	                      </div>
	                    ))}
	                  </div>
	                  {(loading || resultsLoading) && (
	                    <div className={`absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-xl text-xs font-bold backdrop-blur-sm ${
	                      darkMode ? 'bg-slate-950/55 text-indigo-50' : 'bg-indigo-50/70 text-indigo-950'
	                    }`}>
	                      <Loader2 className="h-4 w-4 animate-spin" />
	                      <span>{resultsCopy.loading}</span>
	                    </div>
	                  )}
	                </div>
	                <div
	                  className={`relative overflow-hidden rounded-xl border p-4 ${darkMode ? 'border-amber-300/25 bg-amber-500/10' : 'border-amber-200 bg-amber-50/80'}`}
	                  aria-busy={loading || resultsLoading}
	                >
	                  <div className={`text-[11px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-amber-200/70' : 'text-amber-800/70'}`}>
	                    {baseGuardCopy.spikeLabel}
	                  </div>
	                  <div className={`mt-1 text-[11px] ${darkMode ? 'text-amber-50/80' : 'text-amber-950/70'}`}>
	                    {howItWorksLanguage === 'pl'
	                      ? 'Dla Sell BASE może wystawić BUY hedge na Fix I. Spike bez hedge zostaje sygnałem ryzyka i nie liczy się do fill rate.'
	                      : 'For Sell BASE it may submit a Fix I BUY hedge. Spike without hedge stays risk context and does not enter fill rate.'}
	                  </div>
	                  <div className="mt-3 grid grid-cols-2 gap-2">
	                    {[
	                      [howItWorksLanguage === 'pl' ? 'Dni spike hedge' : 'Spike hedge days', String(guardSpikeModeSummary.days || 0)],
	                      [baseHedgeCopy.hedgeOrderVolume, `${formatNumber(guardSpikeModeSummary.orderVolume || 0)} MWh`],
	                      [baseHedgeCopy.hedgeExecutedVolume, `${formatNumber(guardSpikeModeSummary.executedVolume || 0)} MWh`],
	                      [baseGuardCopy.spikeSuppressedShort, `${guardTotalsSummary.riskOnlySpikeHours || 0}h`],
	                    ].map(([label, value]) => (
	                      <div key={`guard-summary-spike-${label}`} className={`rounded-lg border px-3 py-2 ${darkMode ? 'border-amber-300/20 bg-slate-950/45' : 'border-amber-200 bg-white/80'}`}>
	                        <div className={`text-[10px] font-bold uppercase leading-3 tracking-wide ${darkMode ? 'text-amber-200/70' : 'text-amber-900/60'}`}>{label}</div>
	                        <div className={`mt-1 text-base font-extrabold tabular-nums sm:text-lg ${darkMode ? 'text-amber-50' : 'text-amber-950'}`}>{value}</div>
	                      </div>
	                    ))}
	                  </div>
	                  {(loading || resultsLoading) && (
	                    <div className={`absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-xl text-xs font-bold backdrop-blur-sm ${
	                      darkMode ? 'bg-slate-950/55 text-amber-50' : 'bg-amber-50/70 text-amber-950'
	                    }`}>
	                      <Loader2 className="h-4 w-4 animate-spin" />
	                      <span>{resultsCopy.loading}</span>
	                    </div>
	                  )}
	                </div>
	              </div>
                <details className={`group mt-4 rounded-xl border ${
                  darkMode ? 'border-slate-700 bg-slate-950/35' : 'border-gray-200 bg-gray-50/80'
                }`}>
                  <summary className={`flex cursor-pointer list-none items-center justify-between gap-3 rounded-xl px-4 py-3 marker:hidden ${
                    darkMode ? 'hover:bg-slate-900/70' : 'hover:bg-white'
                  }`}>
                    <div className="min-w-0">
                      <div className={`text-sm font-extrabold ${darkMode ? 'text-slate-100' : 'text-gray-900'}`}>
                        {howItWorksLanguage === 'pl' ? 'Porównanie mechanizmów risk management' : 'Risk-management mechanism comparison'}
                      </div>
                      <div className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        {howItWorksLanguage === 'pl'
                          ? 'Co jest tylko sygnałem ryzyka, co składa order, a co jest aktywną interwencją bez hedge PnL.'
                          : 'What is only a risk signal, what submits an order, and what is an active intervention without hedge PnL.'}
                      </div>
                    </div>
                    <ChevronDown size={16} className={`shrink-0 transition group-open:rotate-180 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                  </summary>
                  <div className={`border-t px-4 pb-4 pt-3 ${darkMode ? 'border-slate-800' : 'border-gray-200'}`}>
                    <div className={`overflow-x-auto rounded-lg border ${darkMode ? 'border-slate-700 bg-slate-950/45' : 'border-gray-200 bg-white'}`}>
                      <table className="min-w-[980px] text-left text-xs">
                        <thead className={darkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-50 text-gray-500'}>
                          <tr>
                            {(howItWorksLanguage === 'pl'
                              ? ['Mechanizm', 'Kiedy działa', 'Co zmienia', 'Order / execution', 'Jak czytać w statystykach']
                              : ['Mechanism', 'When it runs', 'What it changes', 'Order / execution', 'How to read stats']
                            ).map((label) => (
                              <th key={`guard-mechanism-head-${label}`} className="px-3 py-2 font-extrabold uppercase tracking-wide">{label}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${darkMode ? 'divide-slate-800' : 'divide-gray-100'}`}>
                          {(howItWorksLanguage === 'pl' ? [
                            {
                              mechanism: 'Goose risk dla BASE',
                              when: 'Przed aukcją BASE, przy liczeniu modelowego warunku Buy/Sell/Idle.',
                              changes: 'Goose czyta risk score/shift i koryguje q50 używane w warunku wejścia w BASE.',
                              order: 'Brak orderu. To wejściowy risk context modelu Goose, nie order hedge.',
                              stats: 'Pokazywać jako kontekst wejścia w BASE; nie liczyć jako hedge fill ani hedge PnL.',
                            },
                            {
                              mechanism: 'Risk-management hedge',
                              when: 'Po realnym wykonaniu BASE, gdy Goose risk management chce zdjąć część ekspozycji na Fix I.',
                              changes: 'Dodaje przeciwny order Fix I dla godzin ryzyka.',
                              order: 'SUB = order wystawiony, EXEC = faktyczny fill na Fix I.',
                              stats: 'Liczyć do dni z hedge order, fill rate i PnL wykonanego hedge.',
                            },
                            {
                              mechanism: baseGuardCopy.hardLabel,
                              when: 'Buy BASE, godziny 10-16, gdy q50/q90 albo q10 pokazują ryzyko cen ujemnych.',
                              changes: 'Mocno redukuje ekspozycję BASE: może zostawić 20/10/0 MW zamiast pełnego bloku.',
                              order: 'Tworzy Sell hedge na Fix I dla zdjętego wolumenu. Może mieć SUB albo EXEC.',
                              stats: 'Liczyć jako hedge z risk management: order, fill rate i PnL tylko dla faktycznie wykonanego hedge.',
                            },
                            {
                              mechanism: baseGuardCopy.softLabel,
                              when: 'Buy BASE, gdy hybrid_v1 wybiera łagodniejszy tryb: zwykle przy większej liczbie ryzykownych godzin i BASE poza neutralnym pasmem.',
                              changes: 'Redukuje ekspozycję mniej agresywnie, zwykle zostawia 35/45 MW, żeby nie wyciąć całego upside.',
                              order: 'Tworzy mniejszy Sell hedge na Fix I niż hard. Może mieć SUB albo EXEC.',
                              stats: 'Liczyć razem z hedge, ale interpretować osobno od hard, bo ma inną agresywność i mniejszą próbkę.',
                            },
                            {
                              mechanism: baseHedgeCopy.fixIBuyGuardLabel,
                              when: `${baseGuardCopy.hardLabel} wykrywa ryzyko cen ujemnych dla zwykłego BUY Fix I.`,
                              changes: 'Blokuje BUY Fix I albo ogranicza jego wolumen przed wysłaniem orderu.',
                              order: 'BLOKADA/LIMIT. To nie jest hedge order i nie trafia do koszyka Noble jako submitted order.',
                              stats: 'Liczyć jako interwencję ryzyka: dni/godziny blokady lub limitu, poza hedge fill rate i hedge PnL.',
                            },
                            {
                              mechanism: baseHedgeCopy.baseCarryGuardLabel,
                              when: 'Po Fix I/Fix II, gdy residual BASE może iść dalej do IDA.',
                              changes: 'Może wymusić domknięcie na Fix II albo zablokować przejście residualu do IDA1/IDA2.',
                              order: 'Pokazywać jako ścieżkę/carry, nie jako order hedge Fix I.',
                              stats: 'Wartość jest w ścieżce wyniku BASE/settle, nie w PnL hedge.',
                            },
                            {
                              mechanism: baseGuardCopy.spikeLabel,
                              when: 'Dla Sell BASE, gdy forecast widzi drogie godziny spike i ryzyko odkupienia drożej.',
                              changes: 'Może wystawić BUY hedge na Fix I, jeśli ekonomia hedge ma sens.',
                              order: 'Jeśli order jest wystawiony, działa jak hedge z risk management: SUB/EXEC.',
                              stats: 'Liczyć do hedge tylko wtedy, gdy istnieje rzeczywisty order hedge.',
                            },
                            {
                              mechanism: 'Ryzyko spike, bez hedge',
                              when: 'Spike risk został wykryty, ale hedge nie przeszedł ekonomii/limitu.',
                              changes: 'Nie zmienia ekspozycji, nie składa orderu.',
                              order: 'Brak orderu. Pokazywać po rozwinięciu dnia jako sygnał ryzyka.',
                              stats: 'Nie liczyć jako risk-management/hedge order, fill ani PnL hedge.',
                            },
                          ] : [
                            {
                              mechanism: 'Goose risk context',
                              when: 'Before BASE auction, as trader decision context.',
                              changes: 'Does not change BASE, target profile, or orders.',
                              order: 'No order. Risk signal only.',
                              stats: 'Do not count as hedge or risk-management execution.',
                            },
                            {
                              mechanism: 'Risk-management hedge',
                              when: 'After actual BASE execution, when Goose risk management wants to reduce part of the exposure on Fix I.',
                              changes: 'Adds an opposite Fix I order for risk hours.',
                              order: 'SUB = submitted order, EXEC = actual Fix I fill.',
                              stats: 'Count in hedge-order days, fill rate, and executed hedge PnL.',
                            },
                            {
                              mechanism: baseGuardCopy.hardLabel,
                              when: 'Buy BASE, hours 10-16, when q50/q90 or q10 show negative-price risk.',
                              changes: 'Cuts BASE exposure aggressively: may keep 20/10/0 MW instead of the full block.',
                              order: 'Creates a Fix I Sell hedge for the removed volume. Can be SUB or EXEC.',
                              stats: 'Count as risk-management hedge: order, fill rate, and PnL only for executed hedge.',
                            },
                            {
                              mechanism: baseGuardCopy.softLabel,
                              when: 'Buy BASE when hybrid_v1 picks the gentler mode: usually more risky hours and BASE outside the neutral band.',
                              changes: 'Reduces exposure less aggressively, usually keeping 35/45 MW so it does not kill the full upside.',
                              order: 'Creates a smaller Fix I Sell hedge than hard. Can be SUB or EXEC.',
                              stats: 'Count with hedge, but read separately from hard because it has different aggressiveness and a smaller sample.',
                            },
                            {
                              mechanism: baseHedgeCopy.fixIBuyGuardLabel,
                              when: `${baseGuardCopy.hardLabel} detects negative-price risk for a normal Fix I BUY.`,
                              changes: 'Blocks Fix I BUY or caps its volume before order submission.',
                              order: 'BLOCKED/CAPPED. Not a hedge order and not submitted to the Noble basket.',
                              stats: 'Count as risk intervention: blocked/capped days and hours, outside hedge fill rate and hedge PnL.',
                            },
                            {
                              mechanism: baseHedgeCopy.baseCarryGuardLabel,
                              when: 'After Fix I/Fix II, when BASE residual may continue to IDA.',
                              changes: 'Can force close on Fix II or block residual carry to IDA1/IDA2.',
                              order: 'Show as path/carry, not as a Fix I hedge order.',
                              stats: 'Value belongs to BASE/settle path, not hedge PnL.',
                            },
                            {
                              mechanism: baseGuardCopy.spikeLabel,
                              when: 'For Sell BASE when forecast sees expensive spike hours and buy-back risk.',
                              changes: 'May submit a Fix I BUY hedge if hedge economics make sense.',
                              order: 'If submitted, behaves like a risk-management hedge: SUB/EXEC.',
                              stats: 'Count as hedge only when an actual hedge order exists.',
                            },
                            {
                              mechanism: 'Spike risk, no hedge',
                              when: 'Spike risk was detected, but hedge economics/limits rejected the hedge.',
                              changes: 'Does not change exposure and does not submit an order.',
                              order: 'No order. Show only in expanded day details as risk context.',
                              stats: 'Do not count as risk-management/hedge order, fill, or hedge PnL.',
                            },
                          ]).map((row) => (
                            <tr key={`guard-mechanism-row-${row.mechanism}`} className={darkMode ? 'text-slate-200' : 'text-gray-800'}>
                              <td className="px-3 py-3 font-extrabold">{row.mechanism}</td>
                              <td className={`px-3 py-3 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{row.when}</td>
                              <td className={`px-3 py-3 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{row.changes}</td>
                              <td className={`px-3 py-3 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{row.order}</td>
                              <td className={`px-3 py-3 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{row.stats}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </details>
	                <details className={`group mt-4 rounded-xl border ${
	                  darkMode ? 'border-slate-700 bg-slate-950/35' : 'border-gray-200 bg-gray-50/80'
	                }`}>
	                  <summary className={`flex cursor-pointer list-none flex-wrap items-start justify-between gap-3 rounded-xl p-4 transition marker:hidden ${
	                    darkMode ? 'hover:bg-slate-900/70' : 'hover:bg-white'
	                  }`}>
	                    <div>
	                      <div className={`text-sm font-extrabold ${darkMode ? 'text-slate-100' : 'text-gray-900'}`}>
	                        {howItWorksLanguage === 'pl' ? 'Aktywność mechanizmów w zakresie' : 'Mechanism activity in range'}
	                      </div>
	                      <div className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
	                        {howItWorksLanguage === 'pl'
	                          ? 'Ta lista tłumaczy, które mechanizmy faktycznie wystąpiły, które są tylko kontekstem i które celowo nie wchodzą do hedge stats.'
	                          : 'This list explains which mechanisms actually appeared, which are context only, and which intentionally stay out of hedge stats.'}
	                      </div>
	                    </div>
	                    <div className="flex items-center gap-2">
	                      <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${
	                        darkMode ? 'border-slate-600 bg-slate-900 text-slate-300' : 'border-gray-200 bg-white text-gray-600'
	                      }`}>
	                        {baseHedgeCopy.dayCount(baseSimulationRows.length)}
	                      </span>
	                      <ChevronDown size={16} className={`shrink-0 transition group-open:rotate-180 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
	                    </div>
	                  </summary>
	                  <div className={`mx-4 mb-4 divide-y overflow-hidden rounded-lg border ${
	                    darkMode ? 'divide-slate-800 border-slate-700 bg-slate-950/45' : 'divide-gray-100 border-gray-200 bg-white'
	                  }`}>
	                    {guardMechanismActivityRows.map((row) => {
	                      const statusClass = row.tone === 'used'
	                        ? darkMode ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
	                        : row.tone === 'context'
	                          ? darkMode ? 'border-sky-300/30 bg-sky-500/10 text-sky-100' : 'border-sky-200 bg-sky-50 text-sky-800'
	                          : row.tone === 'hidden'
	                            ? darkMode ? 'border-cyan-300/35 bg-cyan-500/10 text-cyan-100' : 'border-cyan-200 bg-cyan-50 text-cyan-900'
	                            : darkMode ? 'border-slate-700 bg-slate-900 text-slate-400' : 'border-gray-200 bg-gray-50 text-gray-500';
	                      return (
	                        <div key={`guard-mechanism-activity-${row.key}`} className="grid gap-2 px-3 py-3 md:grid-cols-[220px_260px_minmax(0,1fr)] md:items-center">
	                          <div className={`text-sm font-extrabold ${darkMode ? 'text-slate-100' : 'text-gray-900'}`}>
	                            {row.mechanism}
	                          </div>
	                          <div>
	                            <span className={`inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-[11px] font-extrabold leading-4 ${statusClass}`}>
	                              {row.status}
	                            </span>
	                          </div>
	                          <div className={`text-xs leading-5 ${darkMode ? 'text-slate-400' : 'text-gray-600'}`}>
	                            {row.detail}
	                          </div>
	                        </div>
	                      );
	                    })}
	                  </div>
	                </details>
		              {guardCalibrationSummary.totals.days === 0 ? (
		                <div className={`mt-4 rounded-lg border px-4 py-3 text-sm ${darkMode ? 'border-slate-700 bg-slate-900/70 text-slate-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
		                  {baseHedgeCopy.noGuardCalibration}
		                </div>
		              ) : null}
	            </div>
	            <div className={`p-4 ${darkMode ? 'bg-slate-950/20' : 'bg-gray-50/70'}`}>
                  {baseSimulationRows.length === 0 ? (
                    <div className={`rounded-xl border px-4 py-8 text-center ${darkMode ? 'border-slate-700 text-slate-400' : 'border-gray-200 text-gray-500'}`}>
                      {baseHedgeCopy.noRows}
                    </div>
	                  ) : (
                    <div className="space-y-3">
                    {baseSimulationRows.map(({ date: rowDate, status, rowKind, baseSimulation, summaryRow }) => {
                      const isLiveBaseRow = rowKind === 'live';
                      const loadedDayDetails = dayDetailsByDate[rowDate] || null;
                      const loadedDaySummary = loadedDayDetails?.summary || null;
                      const loadedDayHourly = Array.isArray(loadedDayDetails?.hourly) ? loadedDayDetails.hourly : [];
	                    const riskContext = baseEntryRiskRowsForDisplayByDate[rowDate] || null;
	                      const riskContextReady = Boolean(riskContext?.ready);
	                      const riskContextSide = getReplayBaseRiskSide(baseSimulation);
	                      const riskContextLevel = riskContextReady ? getReplayBaseRiskLevel(riskContext, baseSimulation) : 'not_ready';
	                      const riskContextLabel = baseHedgeCopy.entryRiskLabels?.[riskContextLevel] || riskContextLevel;
	                      const riskContextClass = riskContextLevel === 'blocked' || riskContextLevel === 'high'
                        ? darkMode ? 'border-rose-300/45 bg-rose-500/15 text-rose-100' : 'border-rose-200 bg-rose-50 text-rose-900'
                        : riskContextLevel === 'medium'
                          ? darkMode ? 'border-amber-300/45 bg-amber-500/15 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900'
                          : riskContextLevel === 'not_ready' || riskContextLevel === 'no_base'
                            ? darkMode ? 'border-slate-600 bg-slate-900 text-slate-300' : 'border-gray-200 bg-gray-50 text-gray-700'
                            : darkMode ? 'border-emerald-300/35 bg-emerald-500/10 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-900';
	                      const riskContextExplainLines = riskContextReady ? buildEntryRiskExplanationLines(riskContext, simCopy.letsTrade) : [];
	                      const riskContextTitle = riskContextExplainLines.join('\n');
	                      const riskContextBuyTriggerPrice = riskContext?.buy?.neutral_max_buy_price ?? riskContext?.buy?.risk_adjusted_max_buy_price;
	                      const riskContextSellTriggerPrice = riskContext?.sell?.neutral_min_sell_price ?? riskContext?.sell?.risk_adjusted_min_sell_price;
	                    const hourlyPlan = Array.isArray(baseSimulation.hourly_plan) ? baseSimulation.hourly_plan : [];
                      const gooseRisk = baseSimulation.risk_management || {};
                      const isGooseLiveRisk = String(gooseRisk.engine || baseSimulation.engine || '').trim() === 'goose_live_backtest';
	                    const hedgeOrderRows = hourlyPlan.filter((hour) => Number(hour.hedge_order_volume_mw ?? hour.hedge_volume_mw ?? 0) > 0.001);
	                    const recommendedAction = String(baseSimulation.recommended_action || '').toLowerCase();
                    const guardExecutionAction = baseSimulation?.execution_applied
                      ? String(baseSimulation?.execution_action || '').trim().toLowerCase()
                      : '';
                    const effectiveAction = (recommendedAction === 'buy' || recommendedAction === 'sell')
                      ? recommendedAction
                      : (guardExecutionAction === 'buy' || guardExecutionAction === 'sell')
                        ? guardExecutionAction
                        : recommendedAction;
	                    const shouldPlay = hasActualBasePosition({
                      action: effectiveAction,
                      volume: baseSimulation.base_block_volume_mw,
                    });
	                    const visibleHedgeRows = shouldPlay ? hedgeOrderRows : [];
	                    const rowGuardMeta = getBaseHedgeGuardMeta(baseSimulation);
                      const rowHedgeInfo = getDailyBaseHedgeInfo({
                        ...(loadedDaySummary || summaryRow || {}),
	                      base_simulation: baseSimulation,
	                      base_action: effectiveAction,
	                      base_volume: baseSimulation.base_block_volume_mw,
	                    });
                      const rowBaseSourceContext = buildBaseContextFromReplay(baseSimulation);
                      const rowBaseSourceLabel = getBaseSourceLabel(rowBaseSourceContext, baseSimulation);
                      const rowBaseSourceBadgeClass = getBaseSourceBadgeClass(rowBaseSourceContext, baseSimulation);
		                    const setupAction = String(
	                      baseSimulation.base_setup?.base_action
	                      || baseSimulation.model_action
                      || '',
                    ).toLowerCase();
                    const isCurrentInputRow = baseSimulation.source === 'post_base_execution_input';
                    const isIdleInputRow = isCurrentInputRow && setupAction !== 'buy' && setupAction !== 'sell';
                    const decisionLabel = shouldPlay
                      ? effectiveAction === 'buy'
                        ? baseHedgeCopy.buyBase
                        : effectiveAction === 'sell'
                          ? baseHedgeCopy.sellBase
                          : `${effectiveAction.toUpperCase()} BASE`
                      : isIdleInputRow
                        ? baseHedgeCopy.idleBase
                        : baseHedgeCopy.skipBase;
                    const decisionAction = effectiveAction;
                      const decisionClass = shouldPlay && decisionAction === 'buy'
                      ? (darkMode ? 'border-emerald-300/70 bg-emerald-500/20 text-emerald-50 shadow-sm' : 'border-emerald-300 bg-emerald-100 text-emerald-900 shadow-sm')
                        : shouldPlay && decisionAction === 'sell'
                          ? (darkMode ? 'border-rose-300/70 bg-rose-500/20 text-rose-50 shadow-sm' : 'border-rose-300 bg-rose-100 text-rose-900 shadow-sm')
                        : (darkMode ? 'border-slate-500 bg-slate-700/70 text-slate-100 shadow-sm' : 'border-gray-300 bg-gray-100 text-gray-800 shadow-sm');
                    const hedgeOrderAuditRows = visibleHedgeRows.map((hour) => {
                      const submittedVolume = Number(hour.hedge_order_volume_mw ?? hour.hedge_volume_mw ?? 0);
                      const executedVolume = Number(hour.hedge_volume_mw || 0);
                      const isExecuted = executedVolume > 0.001;
                      const action = formatBaseHedgeActionLabel(hour.hedge_action);
                      const limitPrice = hour.hedge_limit_price ?? hour.hedge_price ?? null;
                      const executedPrice = hour.hedge_price ?? hour.fix_i_price ?? null;
                      return {
                        hour,
                        submittedVolume,
                        executedVolume,
                        isExecuted,
                        action,
                        limitPrice,
                        executedPrice,
                      };
                    });
                    const submittedHedgeVolume = hedgeOrderAuditRows.reduce(
                      (sum, row) => sum + row.submittedVolume,
                      0,
                    );
                    const executedHedgeVolume = hedgeOrderAuditRows.reduce(
                      (sum, row) => sum + row.executedVolume,
                      0,
                    );
                    const submittedOnlyHedgeRows = hedgeOrderAuditRows.filter((row) => !row.isExecuted);
                    const executedHedgeRows = hedgeOrderAuditRows.filter((row) => row.isExecuted);
                    const hedgeAuditHelpText = submittedHedgeVolume > 0.001
                      ? `${executedHedgeRows.length} ${baseGuardCopy.executedBadge} / ${submittedOnlyHedgeRows.length} ${baseGuardCopy.submittedBadge}`
                      : '';
                    const guardDecisionAuditRows = shouldPlay ? hourlyPlan
                      .map((hour) => {
                        if (isGooseLiveRisk) {
                          const gooseFixIVolume = Number(hour.fix_i_order_volume_mw || hour.fix_i_order_volume_mwh || 0);
                          const gooseRiskShift = Number(hour.risk_shift || 0);
                          const gooseCarryVolume = Number(hour.base_forced_fix2_volume_mwh || 0)
                            + Number(hour.base_blocked_from_ida1_volume_mwh || 0)
                            + Number(hour.base_blocked_from_ida2_volume_mwh || 0);
                          if (gooseFixIVolume <= 0.001 && Math.abs(gooseRiskShift) <= 0.001 && gooseCarryVolume <= 0.001) {
                            return null;
                          }
                          const actionLabel = hour.fix_i_action
                            ? formatBaseHedgeActionLabel(hour.fix_i_action)
                            : (Math.abs(gooseRiskShift) > 0.001 ? 'risk shift' : 'carry');
                          return {
                            hour,
                            mechanism: gooseFixIVolume > 0.001
                              ? 'Goose Fix I execution'
                              : Math.abs(gooseRiskShift) > 0.001
                                ? 'Goose risk shift'
                                : baseHedgeCopy.baseCarryGuardLabel,
                            decision: gooseFixIVolume > 0.001
                              ? `${actionLabel} Fix I`
                              : Math.abs(gooseRiskShift) > 0.001
                                ? `${gooseRiskShift > 0 ? '+' : ''}${formatNumber(gooseRiskShift)} PLN/MWh`
                                : baseHedgeCopy.baseCarryGuardLabel,
                            submittedVolume: gooseFixIVolume,
                            executedVolume: gooseFixIVolume,
                            isExecuted: gooseFixIVolume > 0.001,
                            hasHedgeOrder: false,
                            isRiskOnlySpike: false,
                            negativeBuyAction: null,
                            status: gooseFixIVolume > 0.001 ? baseGuardCopy.executedBadge : (howItWorksLanguage === 'pl' ? 'SYGNAŁ' : 'SIGNAL'),
                            reason: 'goose_live_risk_management',
                            guardCapVolume: null,
                            limitPrice: null,
                            price: hour.fix_i_price ?? null,
                          };
                        }
                        const submittedVolume = Number(hour.hedge_order_volume_mw ?? hour.hedge_volume_mw ?? 0);
                        const executedVolume = Number(hour.hedge_volume_mw || 0);
                        const hasHedgeOrder = submittedVolume > 0.001;
                        const negativeReason = String(
                          hour?.negative_price_guard?.reason
                          || hour?.negative_price_guard_reason
                          || '',
                        ).trim();
                        const spikeReason = String(
                          hour?.sell_spike_guard?.reason
                          || hour?.sell_spike_guard?.suppressed_reason
                          || hour?.sell_spike_guard_reason
                          || '',
                        ).trim();
                        const negativeActive = Boolean(
                          hour?.negative_price_guard?.active
                          || hour?.negative_price_guard_active
                          || negativeReason,
                        );
                        const spikeActive = Boolean(
                          hour?.sell_spike_guard?.active
                          || hour?.sell_spike_guard_active
                          || hour?.sell_spike_guard?.suppressed
                          || hour?.sell_spike_guard_suppressed
                          || spikeReason,
                        );
                        if (!hasHedgeOrder && !negativeActive && !spikeActive) {
                          return null;
                        }
                        const isExecuted = executedVolume > 0.001;
                        const negativeBuyAction = hasHedgeOrder ? null : getNegativeFixIBuyGuardAction(hour);
                        const isSuppressed = !hasHedgeOrder && spikeActive && !negativeBuyAction;
                        const isBlocked = !hasHedgeOrder && negativeBuyAction === 'block';
                        const isLimited = !hasHedgeOrder && negativeBuyAction === 'cap';
                        const status = hasHedgeOrder
                          ? (isExecuted ? baseGuardCopy.executedBadge : baseGuardCopy.submittedBadge)
                          : isBlocked
                            ? baseGuardCopy.blockedBadge
                            : isLimited
                              ? baseGuardCopy.cappedBadge
                              : isSuppressed
                                ? baseGuardCopy.suppressedBadge
                                : (howItWorksLanguage === 'pl' ? 'SYGNAŁ' : 'SIGNAL');
                        const mechanism = hasHedgeOrder
                          ? spikeActive && !negativeActive
                            ? baseGuardCopy.spikeLabel
                          : negativeActive
                              ? (rowGuardMeta.mode === 'soft' ? baseGuardCopy.softLabel : rowGuardMeta.mode === 'hard' ? baseGuardCopy.hardLabel : baseGuardCopy.hardLabel)
                              : 'Risk-management hedge'
                          : negativeBuyAction
                            ? formatFixIBuyGuardLabel({
                                blockedHours: negativeBuyAction === 'block' ? 1 : 0,
                                cappedHours: negativeBuyAction === 'cap' ? 1 : 0,
                              })
                            : spikeActive
                              ? (rowGuardMeta.mode === 'spike_suppressed' ? baseGuardCopy.spikeSuppressedLabel : baseGuardCopy.spikeLabel)
                            : (rowGuardMeta.mode === 'soft' ? baseGuardCopy.softLabel : baseGuardCopy.hardLabel);
                        const decision = hasHedgeOrder
                          ? formatBaseHedgeActionLabel(hour.hedge_action)
                          : negativeBuyAction === 'block'
                            ? (howItWorksLanguage === 'pl' ? 'blokuj BUY Fix I' : 'block Fix I BUY')
                            : negativeBuyAction === 'cap'
                              ? (howItWorksLanguage === 'pl' ? 'ogranicz BUY Fix I' : 'cap Fix I BUY')
                              : spikeActive
                                ? (howItWorksLanguage === 'pl' ? 'ryzyko spike, bez hedge' : 'spike risk, no hedge')
                                : (negativeReason || spikeReason || '-');
                        return {
                          hour,
                          mechanism,
                          decision,
                          submittedVolume,
                          executedVolume,
                          isExecuted,
                          hasHedgeOrder,
                          isRiskOnlySpike: spikeActive && !hasHedgeOrder && !negativeBuyAction,
                          negativeBuyAction,
                          status,
	                          reason: negativeReason || spikeReason || '',
                            guardCapVolume: negativeBuyAction ? getNegativeFixIBuyGuardCapMw(hour) : null,
	                          limitPrice: hour.hedge_limit_price ?? null,
	                          price: hour.hedge_price ?? hour.fix_i_price ?? null,
	                        };
                      })
                      .filter(Boolean) : [];
                    const guardDecisionChipClass = (mechanism, status = '') => {
                      const normalizedMechanism = String(mechanism || '').toLowerCase();
                      const normalizedStatus = String(status || '').toLowerCase();
                      if (normalizedMechanism.includes('post-base') || normalizedStatus.includes(baseGuardCopy.executedBadge.toLowerCase())) {
                        return darkMode
                          ? 'border-emerald-300/45 bg-emerald-500/15 text-emerald-100'
                          : 'border-emerald-200 bg-emerald-50 text-emerald-800';
                      }
                      if (normalizedMechanism.includes('spike')) {
                        return darkMode
                          ? 'border-amber-300/50 bg-amber-500/15 text-amber-100'
                          : 'border-amber-200 bg-amber-50 text-amber-900';
                      }
                      if (normalizedMechanism.includes('negative') || normalizedMechanism.includes('fix i buy')) {
                        return darkMode
                          ? 'border-cyan-300/45 bg-cyan-500/15 text-cyan-100'
                          : 'border-cyan-200 bg-cyan-50 text-cyan-900';
                      }
                      return darkMode
                        ? 'border-slate-600 bg-slate-900 text-slate-300'
                        : 'border-gray-200 bg-gray-50 text-gray-700';
                    };
                    const guardDecisionChips = Array.from(
                      guardDecisionAuditRows.filter((row) => !row.isRiskOnlySpike && !row.negativeBuyAction).reduce((acc, row) => {
                        const mechanism = row.mechanism || '-';
                        const previous = acc.get(mechanism) || { mechanism, count: 0, executed: 0, submitted: 0 };
                        previous.count += 1;
                        if (row.isExecuted) previous.executed += 1;
                        if (row.hasHedgeOrder) previous.submitted += 1;
                        acc.set(mechanism, previous);
                        return acc;
                      }, new Map()).values(),
                    );
	                    const fixIBuyGuardRows = guardDecisionAuditRows.filter((row) => row.negativeBuyAction);
	                    const fixIBuyGuardHourText = fixIBuyGuardRows.map((row) => formatOneBasedHourLabel(row.hour.hour)).join('-');
                      const fixIBuyBlockedRows = fixIBuyGuardRows.filter((row) => row.negativeBuyAction === 'block');
                      const fixIBuyCappedRows = fixIBuyGuardRows.filter((row) => row.negativeBuyAction === 'cap');
                      const fixIBuyGuardChipSummary = {
                        blockedHours: fixIBuyBlockedRows.length,
                        cappedHours: fixIBuyCappedRows.length,
                      };
	                      const fixIBuyGuardChipLabel = fixIBuyGuardRows.length > 0
	                        ? formatFixIBuyGuardLabel(fixIBuyGuardChipSummary, { includeCounts: true })
	                        : '';
                      const fixIBuyGuardLimitText = fixIBuyCappedRows
                        .map((row) => row.guardCapVolume)
                        .filter((value) => Number.isFinite(value))
                        .map((value) => `${formatNumber(value)} MW`)
                        .filter((value, index, arr) => arr.indexOf(value) === index)
                        .join(', ');
	                    const primaryGuardChip = guardDecisionChips.find((chip) => chip.submitted > 0 || chip.executed > 0) || guardDecisionChips[0] || null;
                      const gooseFixIHours = Number(gooseRisk.fix_i_order_hours || 0);
                      const gooseFixIVolume = Number(gooseRisk.fix_i_order_volume_mwh || 0);
                      const gooseRiskShiftHours = Number(gooseRisk.risk_shift_hours || 0);
                      const gooseRiskChipLabel = isGooseLiveRisk
                        ? gooseFixIHours > 0
                          ? `Goose Fix I · ${formatInteger(gooseFixIHours)}h · ${formatNumber(gooseFixIVolume)} MWh`
                          : gooseRiskShiftHours > 0
                            ? `Goose risk shift · ${formatInteger(gooseRiskShiftHours)}h`
                            : 'Goose risk model'
                        : '';
                      const gooseRiskChipTitle = isGooseLiveRisk
                        ? [
                            `Goose risk management: ${getSimulationVariantLabel()}`,
                            `BASE: ${(gooseRisk.base_action || effectiveAction || '-').toUpperCase()} ${formatNumber(gooseRisk.base_volume_mw ?? baseSimulation.base_block_volume_mw ?? 0)} MW`,
                            `Fix I executed: ${formatNumber(gooseFixIVolume)} MWh / ${formatInteger(gooseFixIHours)}h`,
                            `Risk shift hours: ${formatInteger(gooseRiskShiftHours)}h`,
                          ].join('\n')
                        : '';
                      const spikeGuardChipHours = Number(
                        rowHedgeInfo?.spikeBreakdown?.hedged
                        || rowHedgeInfo?.spikeBreakdown?.activeRows?.length
                        || 0,
                      );
                      const primaryGuardIsSpike = String(primaryGuardChip?.mechanism || '').toLowerCase().includes('spike');
                      const spikeGuardChipLabel = spikeGuardChipHours > 0 && !primaryGuardIsSpike
                        ? `${baseGuardCopy.spikeLabel} · ${formatInteger(spikeGuardChipHours)}h`
                        : '';
                      const baseCarryChipSummary = getBaseCarryGuardSummary(
                        {
                          ...(summaryRow || {}),
                          ...(loadedDaySummary || {}),
                          base_simulation: baseSimulation,
                        },
                        loadedDayHourly.length ? loadedDayHourly : (summaryRow?.hourly || baseSimulation?.hourly_plan || []),
                      );
                      const baseCarryChipVolume = Math.max(
                        Number(baseCarryChipSummary.forcedFix2 || 0),
                        Number(baseCarryChipSummary.blockedIda1 || 0),
                        Number(baseCarryChipSummary.blockedIda2 || 0),
                      );
                      const baseCarryChipLabel = baseCarryChipSummary.hasIntervention
                        ? `${baseHedgeCopy.baseCarryGuardLabel} · ${formatNumber(baseCarryChipVolume)} MWh`
                        : '';
                      const baseCarryDetailRows = baseCarryChipSummary.hasIntervention ? [
                        [baseHedgeCopy.forcedFix2Label, `${formatNumber(baseCarryChipSummary.forcedFix2 || 0)} MWh`],
                        [baseHedgeCopy.blockedIda1Label, `${formatNumber(baseCarryChipSummary.blockedIda1 || 0)} MWh`],
                        [baseHedgeCopy.blockedIda2Label, `${formatNumber(baseCarryChipSummary.blockedIda2 || 0)} MWh`],
                        [
                          howItWorksLanguage === 'pl' ? 'Godziny carry' : 'Carry hours',
                          Array.isArray(baseCarryChipSummary.hourLabels) && baseCarryChipSummary.hourLabels.length
                            ? baseCarryChipSummary.hourLabels.join(', ')
                            : (howItWorksLanguage === 'pl' ? 'Zapis dzienny' : 'Daily payload'),
                        ],
                      ] : [];
                      const baseCarryDetailText = baseCarryChipSummary.hasIntervention
                        ? (howItWorksLanguage === 'pl'
                          ? 'BASE carry control domyka lub blokuje residual BASE po fixingach. To jest element ścieżki BASE/settle, nie order hedge Fix I i nie wchodzi do hedge fill rate.'
                          : 'BASE carry control closes or blocks the remaining BASE residual after fixings. This is part of the BASE/settle path, not a Fix I hedge order, and it does not enter hedge fill rate.')
                        : '';
                      const hasSupplementalGuardChips = Boolean(spikeGuardChipLabel || baseCarryChipLabel || gooseRiskChipLabel);
	                    const guardKindLabel = primaryGuardChip
	                      ? `${primaryGuardChip.mechanism}${primaryGuardChip.count ? ` · ${primaryGuardChip.count}h` : ''}`
                      : '-';
                    const guardKindClass = primaryGuardChip
                      ? guardDecisionChipClass(primaryGuardChip.mechanism)
                      : darkMode
                        ? 'border-slate-700 bg-slate-950/40 text-slate-400'
                        : 'border-gray-200 bg-gray-50 text-gray-500';
                    const hedgeFillRate = submittedHedgeVolume > 0.001
                      ? (executedHedgeVolume / submittedHedgeVolume) * 100
                      : null;
                    const hedgeExecutionLabel = isGooseLiveRisk
                      ? gooseFixIVolume > 0.001
                        ? `${howItWorksLanguage === 'pl' ? 'Goose Fix I wykonany' : 'Goose Fix I executed'}: ${formatNumber(gooseFixIVolume)} MWh`
                        : (howItWorksLanguage === 'pl' ? 'Brak wykonania Fix I' : 'No Fix I fill')
                    : submittedHedgeVolume > 0.001
                      ? executedHedgeVolume > 0.001
                        ? `${baseHedgeCopy.hedgeExecutedVolume}: ${formatNumber(executedHedgeVolume)} / ${formatNumber(submittedHedgeVolume)} MWh`
                        : (howItWorksLanguage === 'pl' ? 'Nie wszedł' : 'Not filled')
                      : fixIBuyGuardRows.length > 0
                        ? (fixIBuyBlockedRows.length > 0 && fixIBuyCappedRows.length > 0
                            ? (howItWorksLanguage === 'pl' ? 'Blokada/limit BUY Fix I' : 'Fix I BUY block/cap')
                            : fixIBuyBlockedRows.length > 0
                              ? (howItWorksLanguage === 'pl' ? 'Blokada BUY Fix I' : 'Fix I BUY blocked')
                              : (howItWorksLanguage === 'pl' ? 'Limit BUY Fix I' : 'Fix I BUY capped'))
                      : primaryGuardChip
                        ? String(primaryGuardChip.mechanism || '').toLowerCase().includes('fix i buy')
                          ? (howItWorksLanguage === 'pl' ? 'Blokada BUY Fix I' : 'Fix I BUY blocked')
                          : (howItWorksLanguage === 'pl' ? 'Bez orderu hedge' : 'No hedge order')
                        : '-';
                    const hedgeExecutionClass = isGooseLiveRisk
                      ? gooseFixIVolume > 0.001
                        ? darkMode ? 'text-emerald-100' : 'text-emerald-800'
                        : darkMode ? 'text-slate-300' : 'text-slate-700'
                    : submittedHedgeVolume > 0.001
                      ? executedHedgeVolume > 0.001
                        ? (executedHedgeVolume + 0.001 >= submittedHedgeVolume
                            ? darkMode ? 'text-emerald-100' : 'text-emerald-800'
                            : darkMode ? 'text-amber-100' : 'text-amber-800')
                        : darkMode ? 'text-rose-100' : 'text-rose-800'
                      : (fixIBuyGuardRows.length > 0 || primaryGuardChip)
                        ? darkMode ? 'text-slate-300' : 'text-slate-700'
                        : darkMode ? 'text-slate-500' : 'text-gray-500';
                    const rowToneClass = shouldPlay
                      ? decisionAction === 'buy'
                        ? darkMode ? 'border-l-emerald-400' : 'border-l-emerald-500'
                        : decisionAction === 'sell'
                          ? darkMode ? 'border-l-rose-400' : 'border-l-rose-500'
                          : darkMode ? 'border-l-slate-500' : 'border-l-gray-300'
                      : darkMode ? 'border-l-slate-600' : 'border-l-gray-300';
	                    const metricTileClass = darkMode
	                      ? 'border-slate-700 bg-slate-950/45'
	                      : 'border-gray-200 bg-white';
	                    const rowPrimaryPnl = shouldPlay
	                      ? Number(baseSimulation.executable_base_pnl_fix2_close || 0)
	                      : 0;
	                    return (
	                      <details
	                        key={`${rowKind || 'settled'}-${rowDate}`}
                          onToggle={(event) => {
                            if (event.currentTarget.open && !isLiveBaseRow && !loadedDayDetails && detailsLoadingDate !== rowDate) {
                              loadDayDetails(rowDate).catch((err) => console.error(err));
                            }
                          }}
	                        className={`group rounded-xl border border-l-4 shadow-sm ${rowToneClass} ${
	                          darkMode ? 'border-slate-700 bg-slate-900/75 text-slate-100' : 'border-gray-200 bg-white text-gray-900'
	                        }`}
	                      >
	                        <summary className={`grid cursor-pointer list-none gap-3 px-4 py-3 marker:hidden md:grid-cols-[120px_minmax(0,1fr)_180px_130px_170px_24px] md:items-center ${
	                          darkMode ? 'hover:bg-slate-950/35' : 'hover:bg-gray-50'
	                        }`}>
	                          <div className="min-w-0">
	                            <div className="text-sm font-extrabold tabular-nums">{rowDate}</div>
	                            <div className={`mt-1 text-[11px] ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
	                              {isLiveBaseRow ? baseHedgeCopy.liveBadge : baseHedgeCopy.settledBadge} · {decisionLabel}
	                            </div>
	                          </div>
	                          <div className="min-w-0">
                              <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                {howItWorksLanguage === 'pl' ? 'Mechanizm ryzyka' : 'Risk mechanism'}
                              </div>
                              <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
                                {primaryGuardChip || (fixIBuyGuardRows.length === 0 && !hasSupplementalGuardChips) ? (
                                  <span
                                    className={`inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none ${guardKindClass}`}
                                    title={primaryGuardChip ? `${primaryGuardChip.mechanism}: ${primaryGuardChip.count} h${primaryGuardChip.submitted ? `, order ${primaryGuardChip.submitted} h` : ''}${primaryGuardChip.executed ? `, exec ${primaryGuardChip.executed} h` : ''}` : ''}
	                                  >
	                                    {guardKindLabel}
	                                  </span>
                                ) : null}
                                {spikeGuardChipLabel ? (
                                  <span
                                    className={`inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none ${guardDecisionChipClass(baseGuardCopy.spikeLabel)}`}
                                    title={`${baseGuardCopy.spikeLabel}: ${formatInteger(spikeGuardChipHours)}h`}
                                  >
                                    {spikeGuardChipLabel}
                                  </span>
                                ) : null}
                                {gooseRiskChipLabel ? (
                                  <span
                                    className={`inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none ${darkMode ? 'border-sky-300/45 bg-sky-500/15 text-sky-100' : 'border-sky-200 bg-sky-50 text-sky-900'}`}
                                    title={gooseRiskChipTitle}
                                  >
                                    {gooseRiskChipLabel}
                                  </span>
                                ) : null}
                                  {fixIBuyGuardRows.length > 0 ? (
                                    <span
                                      className={`inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none ${guardDecisionChipClass(baseGuardCopy.fixIBuyGuardLabel, baseGuardCopy.cappedBadge)}`}
                                      title={`${baseHedgeCopy.fixIBuyGuardLabel}: ${fixIBuyGuardHourText}`}
                                    >
                                      {fixIBuyGuardChipLabel}
                                    </span>
                                  ) : null}
                                {baseCarryChipLabel ? (
                                  <span
                                    className={`inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none ${darkMode ? 'border-indigo-300/45 bg-indigo-500/15 text-indigo-100' : 'border-indigo-200 bg-indigo-50 text-indigo-900'}`}
                                    title={`${baseHedgeCopy.baseCarryGuardLabel}: ${baseHedgeCopy.forcedFix2Label} ${formatNumber(baseCarryChipSummary.forcedFix2 || 0)} MWh · ${baseHedgeCopy.blockedIda1Label} ${formatNumber(baseCarryChipSummary.blockedIda1 || 0)} MWh · ${baseHedgeCopy.blockedIda2Label} ${formatNumber(baseCarryChipSummary.blockedIda2 || 0)} MWh`}
                                  >
                                    {baseCarryChipLabel}
                                  </span>
                                ) : null}
                                {shouldShowBaseSourceBadge(rowBaseSourceContext, baseSimulation) ? (
                                  <span className={`truncate text-[11px] ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                    {rowBaseSourceLabel}
                                  </span>
                                ) : null}
                              </div>
	                          </div>
	                          <div>
                              <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                {howItWorksLanguage === 'pl' ? 'Wykonanie' : 'Execution'}
                              </div>
	                            <div className={`mt-0.5 text-sm font-extrabold tabular-nums ${hedgeExecutionClass}`}>{shouldPlay ? hedgeExecutionLabel : '-'}</div>
                              {shouldPlay && hedgeAuditHelpText ? (
                                <div className={`mt-0.5 text-[11px] font-semibold ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                  {hedgeAuditHelpText}
                                </div>
                              ) : null}
	                          </div>
	                          <div>
	                            <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.columns.baseBlock}</div>
	                            <div className="mt-0.5 text-sm font-bold tabular-nums">{shouldPlay ? `${formatNumber(baseSimulation.base_block_volume_mw)} MW` : '0.00 MW'}</div>
	                          </div>
	                          <div className="md:text-right">
	                            <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.columns.executableBasePnl}</div>
	                            <div className={`mt-0.5 text-base font-extrabold tabular-nums ${shouldPlay ? getValueColor(rowPrimaryPnl) : darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
	                              {shouldPlay ? formatSignedNumber(rowPrimaryPnl, ' PLN') : '0.00 PLN'}
	                            </div>
	                          </div>
	                          <ChevronDown size={16} className={`mt-1 transition group-open:rotate-180 md:mt-0 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
	                        </summary>
	                        <div className={`border-t p-4 ${darkMode ? 'border-slate-800' : 'border-gray-100'}`}>
		                        <div className="grid gap-4 xl:grid-cols-[140px_minmax(0,1fr)]">
	                          <div>
	                            <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.dayDetails}</div>
	                            <div className="mt-2">
                            <span className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold leading-none ${
                              isLiveBaseRow
                                ? darkMode ? 'border-sky-300/45 bg-sky-500/15 text-sky-100' : 'border-sky-200 bg-sky-50 text-sky-900'
                                : darkMode ? 'border-slate-600 bg-slate-950 text-slate-300' : 'border-gray-200 bg-gray-50 text-gray-700'
                            }`}>
                              {isLiveBaseRow ? baseHedgeCopy.liveBadge : baseHedgeCopy.settledBadge}
                            </span>
                            </div>
                            {isLiveBaseRow ? (
                              <div className={`mt-2 text-xs leading-4 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                {baseHedgeCopy.liveStatus}
                              </div>
                            ) : null}
                          </div>

                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex min-w-[78px] items-center justify-center whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-bold leading-none ${decisionClass}`}>
                                {decisionLabel}
                              </span>
                              {shouldShowBaseSourceBadge(rowBaseSourceContext, baseSimulation) ? (
                                <span className={`inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-none ${rowBaseSourceBadgeClass}`}>
                                  {rowBaseSourceLabel}
                                </span>
                              ) : null}
                              {riskContext ? (
                                <span title={riskContextTitle} className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-extrabold leading-none ${riskContextClass}`}>
                                  {riskContextLabel}{riskContextSide ? ` · ${riskContextSide.toUpperCase()}` : ''}
                                </span>
                              ) : null}
                            </div>

                            <div className="mt-4 grid gap-3 md:grid-cols-2 2xl:grid-cols-4">
                              <div className={`rounded-lg border px-3 py-2 ${metricTileClass}`}>
	                                <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.columns.baseBlock}</div>
                                <div className="mt-1 font-bold tabular-nums">{shouldPlay ? `${formatNumber(baseSimulation.base_block_volume_mw)} MW` : '0.00 MW'}</div>
                                <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                  {shouldPlay
                                    ? `${formatNumber(baseSimulation.base_price)} PLN/MWh`
                                    : `${baseHedgeCopy.modelBlock} ${formatNumber(baseSimulation.base_block_volume_mw)} MW @ ${formatNumber(baseSimulation.base_price)}`}
                                </div>
                              </div>
                              <div className={`rounded-lg border px-3 py-2 ${metricTileClass}`}>
                                <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.targetProfile}</div>
                                <div className="mt-1 font-bold tabular-nums">{shouldPlay ? `${formatNumber(baseSimulation.target_profile_volume_mwh)} MWh` : '0.00 MWh'}</div>
                                {!shouldPlay ? (
                                  <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{baseHedgeCopy.counterfactualTarget} {formatNumber(baseSimulation.target_profile_volume_mwh)} MWh</div>
                                ) : null}
                              </div>
                              <div className={`rounded-lg border px-3 py-2 ${metricTileClass}`}>
                                <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.hedgeOrderVolume}</div>
                                <div className="mt-1 font-bold tabular-nums">{shouldPlay ? `${formatNumber(submittedHedgeVolume)} MWh` : '0.00 MWh'}</div>
                                <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                  {baseHedgeCopy.hedgeExecutedVolume}: {formatNumber(executedHedgeVolume)} MWh{hedgeFillRate !== null ? ` · ${formatNumber(hedgeFillRate)}%` : ''}
                                </div>
                              </div>
                              <div className={`rounded-lg border px-3 py-2 ${metricTileClass}`}>
                                <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.fixIHedge}</div>
                                <div className={`mt-1 font-bold tabular-nums ${shouldPlay ? getValueColor(baseSimulation.hedge_pnl_fix_i) : darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                                  {shouldPlay ? formatSignedNumber(baseSimulation.hedge_pnl_fix_i, ' PLN') : '0.00 PLN'}
                                </div>
                                {rowHedgeInfo.hasGuardDetails ? renderBaseHedgeGuardTooltip(rowHedgeInfo, {
                                  align: 'right',
                                  label: rowGuardMeta.label,
                                  ariaLabel: 'BASE replay risk-management hedge details',
                                  className: 'mt-1',
                                }) : null}
                              </div>
                            </div>

                            <div className="mt-3 grid gap-3">
	                              <div className={`min-w-0 rounded-lg border p-3 ${darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-gray-200 bg-gray-50'}`}>
                                <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.columns.preBase}</div>
                                {!riskContext ? (
                                  <div className={`mt-2 text-xs ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                    {baseEntryRiskRowsLoading ? baseHedgeCopy.preBaseLoading : baseHedgeCopy.preBaseMissing}
                                  </div>
                                ) : (
                                  <>
                                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                      <div className={`rounded-lg border px-2.5 py-2 ${darkMode ? 'border-slate-700 bg-slate-950/45' : 'border-gray-200 bg-white'}`}>
                                        <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-emerald-200' : 'text-emerald-700'}`}>{baseHedgeCopy.preBaseBuyRiskLabel}</div>
                                        <div className={`mt-0.5 text-xs tabular-nums ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                          {`${Number(riskContext?.buy?.risk_hours || 0)} h · ${formatNumber(riskContext?.buy?.risk_charge_pln_mwh || 0)} PLN/MWh`}
                                        </div>
                                      </div>
                                      <div className={`rounded-lg border px-2.5 py-2 ${darkMode ? 'border-slate-700 bg-slate-950/45' : 'border-gray-200 bg-white'}`}>
                                        <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-rose-200' : 'text-rose-700'}`}>{baseHedgeCopy.preBaseSellRiskLabel}</div>
                                        <div className={`mt-0.5 text-xs tabular-nums ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                          {`${Number(riskContext?.sell?.risk_hours || 0)} h · ${formatNumber(riskContext?.sell?.risk_charge_pln_mwh || 0)} PLN/MWh`}
                                        </div>
                                      </div>
                                    </div>
                                    {riskContextReady ? (
                                      <div className="mt-2 flex flex-wrap gap-1.5">
                                        <span className={`inline-flex rounded-md border px-2 py-1 text-[11px] font-bold ${
                                          darkMode ? 'border-emerald-300/25 bg-slate-950/55 text-emerald-100' : 'border-emerald-200 bg-white text-emerald-800'
                                        }`}>{`Buy <= ${formatPriceOrDash(riskContextBuyTriggerPrice)}`}</span>
                                        <span className={`inline-flex rounded-md border px-2 py-1 text-[11px] font-bold ${
                                          darkMode ? 'border-rose-300/25 bg-slate-950/55 text-rose-100' : 'border-rose-200 bg-white text-rose-800'
                                        }`}>{`Sell >= ${formatPriceOrDash(riskContextSellTriggerPrice)}`}</span>
                                      </div>
                                    ) : null}
                                  </>
                                )}
                              </div>
	                              <div className={`min-w-0 rounded-lg border p-3 ${darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-gray-200 bg-gray-50'}`}>
                                <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.columns.plan}</div>
                                {!shouldPlay ? (
                                  <div className={`mt-2 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{baseHedgeCopy.noBaseTrade}</div>
                                ) : guardDecisionAuditRows.length === 0 && !baseCarryChipSummary.hasIntervention ? (
                                  <div className={`mt-2 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{baseHedgeCopy.noHedge}</div>
                                ) : (
                                  <div className="mt-2 space-y-3">
                                    {baseCarryChipSummary.hasIntervention ? (
                                      <div className={`rounded-lg border p-3 ${
                                        darkMode
                                          ? 'border-indigo-300/35 bg-indigo-500/10 text-indigo-50'
                                          : 'border-indigo-200 bg-indigo-50 text-indigo-950'
                                      }`}>
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                          <div className="text-xs font-extrabold">{baseHedgeCopy.baseCarryGuardLabel}</div>
                                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-extrabold leading-none ${
                                            darkMode
                                              ? 'border-indigo-300/45 bg-indigo-400/10 text-indigo-100'
                                              : 'border-indigo-200 bg-white text-indigo-900'
                                          }`}>
                                            {formatNumber(baseCarryChipVolume)} MWh
                                          </span>
                                        </div>
                                        <div className={`mt-2 text-[11px] leading-4 ${darkMode ? 'text-indigo-100/80' : 'text-indigo-900/75'}`}>
                                          {baseCarryDetailText}
                                        </div>
                                        <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                                          {baseCarryDetailRows.map(([label, value]) => (
                                            <div key={`${rowDate}-base-carry-${label}`} className={`rounded-md border px-2.5 py-2 ${
                                              darkMode
                                                ? 'border-indigo-300/25 bg-slate-950/35'
                                                : 'border-indigo-100 bg-white/80'
                                            }`}>
                                              <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-indigo-200/70' : 'text-indigo-700/70'}`}>{label}</div>
                                              <div className="mt-1 text-sm font-extrabold tabular-nums">{value}</div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ) : null}
                                    {guardDecisionAuditRows.length > 0 ? (
                                    <>
                                    <div className={`text-[11px] leading-4 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                      {hedgeOrderAuditRows.length > 0
                                        ? `${baseHedgeCopy.hedgeOrderVolume} != ${baseHedgeCopy.hedgeExecutedVolume}. ${baseGuardCopy.executedBadge} oznacza fill na Fix I, ${baseGuardCopy.submittedBadge} oznacza wystawiony order bez fillu.`
	                                        : fixIBuyGuardRows.length > 0
                                          ? (howItWorksLanguage === 'pl'
                                              ? `Goose zablokował albo ograniczył BUY Fix I w ${fixIBuyGuardHourText}. LIMIT oznacza maksymalny BUY po risk control${fixIBuyGuardLimitText ? ` (${fixIBuyGuardLimitText})` : ''}, nie limit ceny. To nie jest hedge order.`
                                              : `Goose blocked or capped a Fix I BUY in ${fixIBuyGuardHourText}. LIMIT is the max BUY after risk control${fixIBuyGuardLimitText ? ` (${fixIBuyGuardLimitText})` : ''}, not a price limit. This is not a hedge order.`)
			                                        : (howItWorksLanguage === 'pl'
										                                          ? 'Goose risk management zadziałał jako filtr decyzji: wykrył ryzyko, ale nie wystawił orderu hedge.'
										                                          : 'Goose risk management acted as a decision filter: it detected risk but did not submit a hedge order.')}
                                    </div>
                                    <div className={`max-h-72 w-full overflow-auto rounded-lg border ${darkMode ? 'border-slate-700 bg-slate-950/40' : 'border-gray-200 bg-white'}`}>
                                      <table className="w-full min-w-[980px] text-left text-[11px]">
                                        <thead className={`sticky top-0 z-10 ${darkMode ? 'bg-slate-950 text-slate-400' : 'bg-gray-50 text-gray-500'}`}>
                                          <tr>
                                            <th className="px-2.5 py-2 font-extrabold uppercase tracking-wide">H</th>
                                            <th className="px-2.5 py-2 font-extrabold uppercase tracking-wide">{howItWorksLanguage === 'pl' ? 'Mechanizm' : 'Mechanism'}</th>
                                            <th className="px-2.5 py-2 font-extrabold uppercase tracking-wide">{howItWorksLanguage === 'pl' ? 'Decyzja' : 'Decision'}</th>
                                            <th className="px-2.5 py-2 text-right font-extrabold uppercase tracking-wide">Order</th>
                                            <th className="px-2.5 py-2 text-right font-extrabold uppercase tracking-wide">Exec</th>
                                            <th className="px-2.5 py-2 text-right font-extrabold uppercase tracking-wide">Limit</th>
                                            <th className="px-2.5 py-2 text-right font-extrabold uppercase tracking-wide">Cena</th>
                                            <th className="px-2.5 py-2 font-extrabold uppercase tracking-wide">Status</th>
                                          </tr>
                                        </thead>
                                        <tbody className={`divide-y ${darkMode ? 'divide-slate-800' : 'divide-gray-100'}`}>
                                          {guardDecisionAuditRows.map((row) => (
                                            <tr key={`${rowDate}-${row.hour.hour}-${row.mechanism}`} className={row.isExecuted ? (darkMode ? 'bg-emerald-500/5' : 'bg-emerald-50/40') : ''}>
                                              <td className="whitespace-nowrap px-2.5 py-2 font-bold tabular-nums">{formatOneBasedHourLabel(row.hour.hour)}</td>
                                              <td className="whitespace-nowrap px-2.5 py-2 font-semibold">{row.mechanism}</td>
                                              <td className="max-w-[190px] px-2.5 py-2">
                                                <div className="truncate font-semibold" title={row.reason || row.decision}>{row.decision}</div>
                                                {row.reason ? (
                                                  <div className={`truncate text-[10px] ${darkMode ? 'text-slate-500' : 'text-gray-500'}`} title={row.reason}>{row.reason}</div>
                                                ) : null}
                                              </td>
                                              <td className="whitespace-nowrap px-2.5 py-2 text-right font-semibold tabular-nums">{formatNumber(row.submittedVolume)} MW</td>
                                              <td className={`whitespace-nowrap px-2.5 py-2 text-right font-semibold tabular-nums ${row.isExecuted ? getValueColor(1) : darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                                                {formatNumber(row.executedVolume)} MW
                                              </td>
                                              <td className="whitespace-nowrap px-2.5 py-2 text-right tabular-nums">
                                                {row.negativeBuyAction
                                                  ? row.negativeBuyAction === 'block'
                                                    ? (howItWorksLanguage === 'pl' ? '0 MW max' : '0 MW max')
                                                    : Number.isFinite(row.guardCapVolume)
                                                      ? `${formatNumber(row.guardCapVolume)} MW max`
                                                      : '-'
                                                  : formatPriceOrDash(row.limitPrice)}
                                              </td>
                                              <td className="whitespace-nowrap px-2.5 py-2 text-right tabular-nums">{formatPriceOrDash(row.price)}</td>
                                              <td className="px-2.5 py-2">
                                                <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-extrabold leading-none ${
                                                  row.isExecuted
                                                    ? (darkMode ? 'border-emerald-400/50 bg-emerald-400/10 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-700')
                                                    : row.hasHedgeOrder
                                                      ? (darkMode ? 'border-slate-600 bg-slate-900 text-slate-300' : 'border-gray-200 bg-gray-50 text-gray-600')
                                                      : (darkMode ? 'border-amber-300/45 bg-amber-500/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800')
                                                }`}>
                                                  {row.status}
                                                </span>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                    </>
                                    ) : null}
                                  </div>
                                )}
	                              </div>
	                            </div>
	                          </div>

		                          <div className="grid min-w-0 gap-2 sm:grid-cols-2 xl:col-start-2">
                            <div className={`rounded-lg border px-3 py-2 ${metricTileClass}`}>
                              <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.columns.executableBasePnl}</div>
                              <div className={`mt-1 text-lg font-extrabold tabular-nums ${shouldPlay ? getValueColor(baseSimulation.executable_base_pnl_fix2_close) : darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                {shouldPlay ? `${formatNumber(baseSimulation.executable_base_pnl_fix2_close)} PLN` : '0.00 PLN'}
                              </div>
                              {!shouldPlay ? (
                                <div className={`mt-1 text-xs font-normal ${getValueColor(baseSimulation.executable_base_pnl_fix2_close)}`}>
                                  {baseHedgeCopy.counterfactual} {formatSignedNumber(baseSimulation.executable_base_pnl_fix2_close, ' PLN')}
                                </div>
                              ) : null}
                            </div>
                            <div className={`rounded-lg border px-3 py-2 ${metricTileClass}`}>
                              <div className={`text-[10px] font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{baseHedgeCopy.columns.profileBeforeHedge}</div>
                              <div className={`mt-1 text-lg font-extrabold tabular-nums ${shouldPlay ? getValueColor(baseSimulation.guarded_profile_base_pnl_fix2_close) : darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                {shouldPlay ? `${formatNumber(baseSimulation.guarded_profile_base_pnl_fix2_close)} PLN` : '-'}
                              </div>
	                              {!shouldPlay ? (
	                                <div className={`mt-1 text-xs ${getValueColor(baseSimulation.guarded_profile_base_pnl_fix2_close)}`}>
	                                  {baseHedgeCopy.counterfactual} {formatSignedNumber(baseSimulation.guarded_profile_base_pnl_fix2_close, ' PLN')}
	                                </div>
	                              ) : null}
	                            </div>
	                          </div>
	                        </div>
	                      </div>
	                    </details>
	                    );
                  })}
                    </div>
                  )}
            </div>
          </div>
          </>
          )}

          {isQuickReplaySection && (
          <>
          <div className={`rounded-xl shadow-sm p-6 ${darkMode ? 'border border-slate-700 bg-slate-900/80' : 'bg-white'}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className={`text-xs font-semibold uppercase ${darkMode ? 'text-sky-300' : 'text-indigo-700'}`}>Quick replay</div>
                <h2 className={`mt-1 text-xl font-bold ${darkMode ? 'text-slate-50' : 'text-gray-800'}`}>Modify Goose IDA2 simulation without saving</h2>
                <p className={`mt-2 max-w-4xl text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                  This runs a gross in-memory replay for the selected date range and updates Summary and Daily Results in memory. It does not write Results or launch AWS.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsQuickReplayExpanded((prev) => !prev)}
                className={`rounded-lg border px-5 py-2 text-sm font-semibold disabled:cursor-not-allowed ${
                  darkMode
                    ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25 disabled:border-slate-700 disabled:bg-slate-900 disabled:text-slate-500'
                    : 'border-emerald-200 bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-100 disabled:text-gray-400'
                }`}
              >
                {isQuickReplayExpanded ? 'Collapse quick replay' : 'Expand quick replay'}
              </button>
            </div>
            {isQuickReplayExpanded ? (
            <>
            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              {quickReplayFieldSections.map((section) => (
                <section
                  key={`quick-replay-inline-${section.key}`}
                  className={`rounded-xl border p-4 ${
                    darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-slate-200 bg-slate-50/80'
                  }`}
                >
                  <div className={`text-xs font-extrabold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {section.title}
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    {section.fields.map(renderQuickReplayField)}
                  </div>
                </section>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={runQuickReplay}
                disabled={quickReplayLoading}
                className={`rounded-lg border px-5 py-2 text-sm font-semibold disabled:cursor-not-allowed ${
                  darkMode
                    ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25 disabled:border-slate-700 disabled:bg-slate-900 disabled:text-slate-500'
                    : 'border-emerald-200 bg-emerald-600 text-white hover:bg-emerald-700 disabled:bg-gray-100 disabled:text-gray-400'
                }`}
              >
                {quickReplayLoading ? 'Replaying...' : 'Run quick replay'}
              </button>
            </div>
            {quickReplayResult && (
              <div className="mt-5">
                {!isQuickReplayForActiveRange && (
                  <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${
                    darkMode
                      ? 'border-amber-300/60 bg-amber-300/15 text-amber-50'
                      : 'border-amber-300 bg-amber-50 text-amber-950'
                  }`}>
                    This replay was generated for a previous date range. Run Quick replay again for the selected dates.
                  </div>
                )}
                {isQuickReplayPendingSettings && (
                  <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${
                    darkMode
                      ? 'border-amber-300/60 bg-amber-300/15 text-amber-50'
                      : 'border-amber-300 bg-amber-50 text-amber-950'
                  }`}>
                    The form has changed since this replay was generated. Run modified simulation again to update Summary and Daily Results.
                  </div>
                )}
                <div className="grid gap-3 md:grid-cols-3">
                  <div className={`rounded-lg border p-4 ${darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-gray-200 bg-slate-50'}`}>
                    <div className={`text-xs font-semibold uppercase ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Default Goose IDA2 Final</div>
                    <div className={`mt-1 text-xl font-bold ${getValueColor(quickReplayDefaultSummary.ida2_close_pnl)}`}>{formatNumber(quickReplayDefaultSummary.ida2_close_pnl)} PLN</div>
                    <div className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Gross in-memory replay. Goose IDA1 Stop: {formatNumber(quickReplayDefaultSummary.ida1_stop_pnl)} PLN</div>
                  </div>
                  <div className={`rounded-lg border p-4 ${darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-gray-200 bg-slate-50'}`}>
                    <div className={`text-xs font-semibold uppercase ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Custom Goose IDA2 Final</div>
                    <div className={`mt-1 text-xl font-bold ${getValueColor(quickReplayCustomSummary.ida2_close_pnl)}`}>{formatNumber(quickReplayCustomSummary.ida2_close_pnl)} PLN</div>
                    <div className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>Gross in-memory replay. Goose IDA1 Stop: {formatNumber(quickReplayCustomSummary.ida1_stop_pnl)} PLN</div>
                  </div>
                  <div className={`rounded-lg border p-4 ${darkMode ? 'border-sky-400/50 bg-sky-400/10' : 'border-indigo-200 bg-indigo-50'}`}>
                    <div className={`text-xs font-semibold uppercase ${darkMode ? 'text-sky-200' : 'text-indigo-700'}`}>Custom minus default</div>
                    <div className={`mt-1 text-xl font-bold ${getValueColor(quickReplayDeltaSummary.ida2_close_pnl)}`}>{formatSignedNumber(quickReplayDeltaSummary.ida2_close_pnl, ' PLN')}</div>
                    <div className={`mt-1 text-xs ${darkMode ? 'text-sky-100/80' : 'text-indigo-700'}`}>Goose IDA1 Stop delta: {formatSignedNumber(quickReplayDeltaSummary.ida1_stop_pnl, ' PLN')}</div>
                  </div>
                </div>
                <div className={`mt-3 rounded-lg border px-4 py-3 text-xs ${
                  darkMode
                    ? 'border-slate-700 bg-slate-950/30 text-slate-300'
                    : 'border-slate-200 bg-slate-50 text-slate-600'
                }`}>
                  The replay cards are gross counterfactuals over the selected range. The Results Summary below is the persisted live provisional path and can include partial/fallback days and fees.
                  {strategyResultValue !== null && strategyResultValue !== undefined && (
                    <span> Current visible live provisional total: <strong>{formatNumber(strategyResultValue)} PLN</strong> gross, <strong>{formatNumber(strategyResultAfterFees)} PLN</strong> after fees.</span>
                  )}
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-4">
                  {[
                    ['Base delta', quickReplayDeltaSummary.base_pnl],
                    ['Fix I / Fix II delta', quickReplayDeltaSummary.fix_pnl],
                    ['IDA1 delta', quickReplayDeltaSummary.ida1_pnl],
                    ['IDA2 delta', quickReplayDeltaSummary.ida2_pnl],
                  ].map(([label, value]) => (
                    <div key={label} className={`rounded-lg border px-4 py-3 ${
                      darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-gray-200 bg-slate-50'
                    }`}>
                      <div className={`text-[11px] font-semibold uppercase ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>{label}</div>
                      <div className={`mt-1 text-sm font-bold ${getValueColor(value)}`}>{formatSignedNumber(value, ' PLN')}</div>
                    </div>
                  ))}
                </div>
                {quickReplayRows.length > 0 && (
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-[760px] w-full text-sm">
                      <thead>
                        <tr className={`border-b text-left text-xs uppercase ${darkMode ? 'border-slate-700 text-slate-400' : 'border-gray-200 text-gray-500'}`}>
                          <th className="py-2 pr-4">Date</th>
                          <th className="py-2 pr-4">Default Goose IDA2 Final</th>
                          <th className="py-2 pr-4">Custom Goose IDA2 Final</th>
                          <th className="py-2 pr-4">Delta</th>
                          <th className="py-2 pr-4">Base</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quickReplayRows.slice(0, 8).map((row) => (
                          <tr key={row.date} className={`border-b last:border-b-0 ${darkMode ? 'border-slate-800' : 'border-gray-100'}`}>
                            <td className={`py-2 pr-4 font-medium ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>{String(row.date).slice(0, 10)}</td>
                            <td className={`py-2 pr-4 ${getValueColor(row.default_ida2_close_pnl)}`}>{formatNumber(row.default_ida2_close_pnl)}</td>
                            <td className={`py-2 pr-4 ${getValueColor(row.custom_ida2_close_pnl)}`}>{formatNumber(row.custom_ida2_close_pnl)}</td>
                            <td className={`py-2 pr-4 font-semibold ${getValueColor(row.delta_ida2_close_pnl)}`}>{formatSignedNumber(row.delta_ida2_close_pnl)}</td>
                            <td className={`py-2 pr-4 ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>{row.default_base_action || '-'} → {row.custom_base_action || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            </>
            ) : (
              <div className={`mt-5 flex flex-col gap-4 rounded-xl border border-dashed px-4 py-4 text-sm md:flex-row md:items-center md:justify-between ${
                darkMode
                  ? 'border-slate-600 bg-slate-950/30 text-slate-300'
                  : 'border-slate-200 bg-slate-50 text-slate-600'
              }`}>
                <div className="flex items-center gap-4">
                  <div className={`grid h-14 w-14 shrink-0 place-items-center rounded-lg border ${
                    darkMode
                      ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-100'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  }`}>
                    <SlidersHorizontal className="h-7 w-7" />
                  </div>
                  <div>
                    <div className={`font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                      Parameter controls are tucked away here.
                    </div>
                    <div className={`mt-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Expand this panel to edit BASE volume, IDA1 caps, margins, and edge filters without saving to settled Results.
                    </div>
                  </div>
                </div>
                <div className="grid min-w-[260px] grid-cols-3 gap-2 text-[11px]">
                  {[
                    ['BASE', `${quickReplayForm.base_volume || '-'} MW`],
                    ['IDA1 cap', `${quickReplayForm.max_position || '-'} MW`],
                    ['Margin', `${quickReplayForm.ida1_margin || '-'} PLN`],
                  ].map(([label, value]) => (
                    <div key={label} className={`rounded-lg border px-3 py-2 ${
                      darkMode
                        ? 'border-slate-700 bg-slate-900/70'
                        : 'border-slate-200 bg-white'
                    }`}>
                      <div className={darkMode ? 'text-slate-500' : 'text-slate-400'}>{label}</div>
                      <div className={`mt-0.5 font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          </>
          )}

          {isResultsSection && (
          <>
          {parityReport && false && (
            <div className={`rounded-xl shadow-sm ${isParityReportExpanded ? 'p-6' : 'px-6 py-4'} ${darkMode ? 'border border-slate-700 bg-slate-900/80' : 'bg-white'}`}>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className={`text-xs font-semibold uppercase ${darkMode ? 'text-sky-300' : 'text-indigo-700'}`}>Parity report</div>
                  <h2 className={`mt-1 font-bold ${isParityReportExpanded ? 'text-2xl' : 'text-lg'} ${darkMode ? 'text-slate-50' : 'text-gray-800'}`}>Goose v3 IDA1 vs Goose v5 IDA1 vs Goose IDA2 Overlay</h2>
                  {isParityReportExpanded && (
                    <p className={`mt-2 max-w-4xl text-sm ${darkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                      {isModifiedSimulationActive
                        ? 'In-memory diagnostic for the same range: Goose v3 IDA1, Goose v5 IDA1, and Goose IDA2 are all replayed with the active modified simulation parameters.'
                        : 'Settled diagnostic for the same days: Goose v3 IDA1 and Goose v5 IDA1 from explicit page presets, plus the official Goose IDA2 overlay result from persisted settle.'}
                    </p>
                  )}
                  <div className={`mt-2 flex flex-wrap gap-2 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    <span>Report range: {parityReportStartDate || '-'} to {parityReportEndDate || '-'}</span>
                    {parityReportGeneratedLabel && <span>Generated: {parityReportGeneratedLabel}</span>}
                    {parityReport?.bison_v3_mode && <span>Goose replay: {parityReport.bison_v3_mode}/{parityReport.bison_v5_mode || 'unknown'}</span>}
                  </div>
                  {isParityReportExpanded && !isParityReportForActiveRange && (
                    <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
                      darkMode
                        ? 'border-amber-300/60 bg-amber-300/15 text-amber-50'
                        : 'border-amber-300 bg-amber-50 text-amber-950'
                    }`}>
                      This report was generated for a previous date range. Run it again for the selected dates.
                    </div>
                  )}
                </div>
                <div className={`ml-auto flex shrink-0 flex-col items-end gap-2 text-right text-sm ${darkMode ? 'text-slate-300' : 'text-gray-500'}`}>
                  {isParityReportExpanded && (
                    <div className="flex flex-wrap justify-end gap-2">
                      <span className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        darkMode ? 'border-slate-700 bg-slate-900/70 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-700'
                      }`}>
                        Selected range: {liveProvisionalSummary?.realizedDays || 0} realised day{(liveProvisionalSummary?.realizedDays || 0) === 1 ? '' : 's'}
                      </span>
                      <span className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        darkMode ? 'border-slate-700 bg-slate-900/70 text-slate-200' : 'border-slate-200 bg-slate-50 text-slate-700'
                      }`}>
                        Final IDA2 comparable: {parityComparableDays} day{parityComparableDays === 1 ? '' : 's'}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => loadParityReport({
                        force: true,
                        autoExpand: true,
                        modifiedParams: isModifiedSimulationActive ? quickReplayResultParams : undefined,
                      })}
                      disabled={parityReportLoading}
                      title="Reload persisted Goose settle rows and rebuild the report. Cached Goose replays are reused when possible."
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed ${
                        darkMode
                          ? 'border-slate-500 bg-slate-800 text-slate-100 hover:bg-slate-700 disabled:border-slate-700 disabled:text-slate-500'
                          : 'border-indigo-200 bg-white text-indigo-800 hover:bg-indigo-50 disabled:bg-gray-100 disabled:text-gray-400'
                      }`}
                    >
                      {parityReportLoading ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsParityReportExpanded((prev) => !prev)}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                        darkMode
                          ? 'border-slate-500 bg-slate-800 text-slate-100 hover:bg-slate-700'
                          : 'border-indigo-200 bg-white text-indigo-800 hover:bg-indigo-50'
                      }`}
                    >
                      {isParityReportExpanded ? 'Collapse' : 'Expand'}
                    </button>
                    <button
                      type="button"
                      onClick={downloadParityReportCsv}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                        darkMode
                          ? 'border-slate-500 bg-slate-800 text-slate-100 hover:bg-slate-700'
                          : 'border-indigo-200 bg-white text-indigo-800 hover:bg-indigo-50'
                      }`}
                    >
                      Export CSV
                    </button>
                  </div>
                </div>
              </div>
              {isParityReportExpanded && (
                <>
              <details className={`mt-5 rounded-lg border ${
                darkMode
                  ? 'border-slate-700 bg-slate-950/30 text-slate-300'
                  : 'border-slate-200 bg-slate-50 text-slate-700'
              }`}>
                <summary className={`cursor-pointer select-none px-4 py-3 text-sm font-semibold ${
                  darkMode ? 'text-slate-100' : 'text-gray-800'
                }`}>
                  Parameters used for this comparison
                  <span className={`ml-2 text-xs font-normal ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    Max IDA1 Position, margins, filters, buckets, and model versions
                  </span>
                </summary>
                <div className={`border-t px-4 py-3 ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                  <p className={`mb-3 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    These values come from the parity payload presets, so the report documents the exact knobs used to replay Goose v3 IDA1, Goose v5 IDA1, and the official Goose overlay path.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-[920px] w-full text-xs">
                      <thead>
                        <tr className={`border-b text-left uppercase ${darkMode ? 'border-slate-800 text-slate-500' : 'border-slate-200 text-gray-500'}`}>
                          <th className="py-2 pr-4">Parameter</th>
                          <th className="py-2 pr-4">Goose v3</th>
                          <th className="py-2 pr-4">Goose v5</th>
                          <th className="py-2 pr-4">Goose</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parityComparisonParameterRows.map((row) => (
                          <tr key={row.label} className={`border-b last:border-b-0 ${darkMode ? 'border-slate-900' : 'border-slate-100'}`}>
                            <td className={`py-2 pr-4 font-semibold ${darkMode ? 'text-slate-200' : 'text-gray-700'}`}>{row.label}</td>
                            <td className="py-2 pr-4 font-mono">{row.bisonV3}</td>
                            <td className="py-2 pr-4 font-mono">{row.bisonV5}</td>
                            <td className="py-2 pr-4 font-mono">{row.simulationV5}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
              <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <div className={`relative rounded-lg border p-4 ${darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-gray-200'}`}>
                  {isParityGooseCardsRefreshing && (
                    <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg backdrop-blur-sm ${
                      darkMode ? 'bg-slate-950/45' : 'bg-white/65'
                    }`}>
                      <Loader2 className={`h-5 w-5 animate-spin ${darkMode ? 'text-sky-200' : 'text-indigo-700'}`} />
                      <div className={`text-center text-[11px] font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Refreshing Goose v3 replay
                      </div>
                    </div>
                  )}
                  <div className={isParityGooseCardsRefreshing ? 'pointer-events-none blur-[2px]' : ''}>
                    <div className={`text-xs font-medium ${darkMode ? 'text-slate-300' : 'text-gray-500'}`}>{bisonParityLabel}</div>
                    <div className={`mt-1 text-xl font-bold ${getValueColor(bisonV3CardPnl)}`}>
                      {formatNumber(bisonV3CardPnl)} PLN
                    </div>
                    <div className={`mt-2 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      Selected range: {liveProvisionalSummary?.realizedDays || 0} realised day{(liveProvisionalSummary?.realizedDays || 0) === 1 ? '' : 's'}. Same IDA2-comparable subset: {formatNumber(paritySummary.bison_ida1_pnl)} PLN across {parityComparableDays} day{parityComparableDays === 1 ? '' : 's'}.
                    </div>
                    {renderPathComposition(bisonV3CardComponents, 'PnL mix')}
                  </div>
                </div>
                <div className={`relative rounded-lg border p-4 ${darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-gray-200'}`}>
                  {isParityGooseCardsRefreshing && (
                    <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg backdrop-blur-sm ${
                      darkMode ? 'bg-slate-950/45' : 'bg-white/65'
                    }`}>
                      <Loader2 className={`h-5 w-5 animate-spin ${darkMode ? 'text-sky-200' : 'text-indigo-700'}`} />
                      <div className={`text-center text-[11px] font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                        Refreshing Goose v5 replay
                      </div>
                    </div>
                  )}
                  <div className={isParityGooseCardsRefreshing ? 'pointer-events-none blur-[2px]' : ''}>
                    <div className={`text-xs font-medium ${darkMode ? 'text-slate-300' : 'text-gray-500'}`}>{simParityLabel}</div>
                    <div className={`mt-1 text-xl font-bold ${getValueColor(bisonV5CardPnl)}`}>
                      {formatNumber(bisonV5CardPnl)} PLN
                    </div>
                    <div className={`mt-2 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      Selected range: {liveProvisionalSummary?.realizedDays || 0} realised day{(liveProvisionalSummary?.realizedDays || 0) === 1 ? '' : 's'}. Same IDA2-comparable subset: {formatNumber(paritySummary.v5_ida1_pnl)} PLN across {parityComparableDays} day{parityComparableDays === 1 ? '' : 's'}.
                    </div>
                    {renderPathComposition(bisonV5CardComponents, 'PnL mix')}
                  </div>
                </div>
                <div className={`rounded-lg border p-4 ${darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-gray-200'}`}>
                  <div className={`text-xs font-medium ${darkMode ? 'text-slate-300' : 'text-gray-500'}`}>{mooseIda1ParityLabel}</div>
                  <div className={`mt-1 text-xl font-bold ${getValueColor(mooseIda1CardPnl)}`}>
                    {formatNumber(mooseIda1CardPnl)} PLN
                  </div>
                  <div className={`mt-2 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    Same Goose IDA2 simulation path stopped at IDA1 before the final IDA2 overlay. Selected range: {paritySummary.simulation_v5_ida1_full_days ?? liveProvisionalSummary?.realizedDays ?? 0} realised day{Number(paritySummary.simulation_v5_ida1_full_days ?? liveProvisionalSummary?.realizedDays ?? 0) === 1 ? '' : 's'}.
                  </div>
                  {renderPathComposition(mooseIda1CardComponents, 'IDA1 PnL mix')}
                </div>
                <div className={`rounded-lg border p-4 ${darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-gray-200'}`}>
                  <div className={`text-xs font-medium ${darkMode ? 'text-slate-300' : 'text-gray-500'}`}>{mooseIda2ParityLabel}</div>
                  <div className={`mt-1 text-xl font-bold ${getValueColor(mooseIda2CardPnl)}`}>
                    {formatNumber(mooseIda2CardPnl)} PLN
                  </div>
                  <div className={`mt-2 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    Persisted final IDA2 full path on the IDA2-comparable subset: {parityComparableDays || 0} day{Number(parityComparableDays || 0) === 1 ? '' : 's'}. Pure IDA2 uplift vs Goose IDA1: {formatSignedNumber(mooseIda2PureUplift, ' PLN')}.
                  </div>
                  {renderPathComposition(mooseIda2CardComponents, 'Final PnL mix')}
                </div>
              </div>
              <div className={`mt-5 rounded-lg border p-4 ${
                darkMode ? 'border-sky-400/30 bg-sky-400/5' : 'border-indigo-100 bg-indigo-50/70'
              }`}>
                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <div className={`text-xs font-semibold uppercase ${darkMode ? 'text-sky-100' : 'text-indigo-800'}`}>
                      Headline deltas
                    </div>
                    <div className={`mt-1 text-xs ${darkMode ? 'text-sky-100/65' : 'text-indigo-700/80'}`}>
                      Right-hand path minus left-hand path. Kept below the main cards so the four cards stay as absolute path totals.
                    </div>
                  </div>
                </div>
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-[720px] w-full text-xs">
                    <thead>
                      <tr className={`border-b text-left uppercase ${darkMode ? 'border-sky-300/20 text-sky-100/55' : 'border-indigo-100 text-indigo-700/70'}`}>
                        <th className="py-2 pr-4">Comparison</th>
                        <th className="py-2 pr-4">Basis</th>
                        <th className="py-2 pr-4 text-right">Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parityHeadlineDeltaRows.map((row) => (
                        <tr key={row.label} className={`border-b last:border-b-0 ${darkMode ? 'border-sky-300/10' : 'border-indigo-100/80'}`}>
                          <td className={`py-2 pr-4 font-medium ${darkMode ? 'text-sky-50/90' : 'text-indigo-950'}`}>{row.label}</td>
                          <td className={`py-2 pr-4 ${darkMode ? 'text-sky-100/55' : 'text-indigo-700/70'}`}>{row.basis}</td>
                          <td className={`py-2 pr-4 text-right font-semibold tabular-nums ${getValueColor(row.value)}`}>
                            {formatSignedNumber(row.value, ' PLN')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={`mt-5 rounded-lg border p-4 ${
                darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-slate-200 bg-slate-50'
              }`}>
                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <div className={`text-xs font-semibold uppercase ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                      Stop benchmark matrix
                    </div>
                    <div className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Goose calculation keeps the legacy replay basis. Goose calculation runs v3, v5, and the official Goose row through the same Goose IDA2 simulation stop semantics.
                    </div>
                  </div>
                  <div className={`text-[11px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    selected range: {paritySelectedRangeDays} day{Number(paritySelectedRangeDays) === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-[960px] w-full text-xs">
                    <thead>
                      <tr className={`border-b text-left uppercase ${darkMode ? 'border-slate-800 text-slate-500' : 'border-slate-200 text-slate-500'}`}>
                        <th className="py-2 pr-4">Preset</th>
                        <th className="py-2 pr-4">Fix II Stop (Legacy calc)</th>
                        <th className="py-2 pr-4">Fix II Stop (Goose calc)</th>
                        <th className="py-2 pr-4">IDA1 Stop (Legacy calc)</th>
                        <th className="py-2 pr-4">IDA1 Stop (Goose calc)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parityStopBenchmarkRows.map((row) => (
                        <tr key={row.key} className={`border-b last:border-b-0 ${darkMode ? 'border-slate-900' : 'border-slate-200'}`}>
                          <td className={`py-3 pr-4 font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{row.label}</td>
                          {[
                            row.bisonFix2,
                            row.mooseFix2,
                            row.bisonIda1,
                            row.mooseIda1,
                          ].map((metric, metricIndex) => (
                            <td
                              key={`${row.key}-${metricIndex}`}
                              className="py-3 pr-4 align-top"
                              title={[
                                `${resultsCopy.afterFees}: ${formatNumber(metric.afterFees)} PLN`,
                                `${resultsCopy.fees}: ${formatNumber(metric.fees)} PLN`,
                                `${resultsCopy.vol}: ${metric.volume === null || metric.volume === undefined ? '-' : `${formatNumber(metric.volume)} MWh`}`,
                                `PLN/MWh: ${metric.profitPerMwh === null || metric.profitPerMwh === undefined ? '-' : formatNumber(metric.profitPerMwh)}`,
                                `${resultsCopy.days}: ${metric.days}`,
                              ].join('\n')}
                            >
                              <div className={`font-semibold tabular-nums ${getValueColor(metric.pnl)}`}>
                                {formatNumber(metric.pnl)} PLN
                              </div>
                              <div className={`mt-1 tabular-nums ${getValueColor(metric.afterFees)}`}>
                                {resultsCopy.afterFees} {formatNumber(metric.afterFees)} PLN
                              </div>
                              <div className={darkMode ? 'mt-1 text-slate-500' : 'mt-1 text-slate-400'}>
                                {metric.volume === null || metric.volume === undefined ? '-' : `${formatNumber(metric.volume)} MWh`}
                              </div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={`mt-5 rounded-lg border px-4 py-3 text-sm ${
                darkMode
                  ? 'border-amber-300/60 bg-amber-300/15 text-amber-50'
                  : 'border-amber-300 bg-amber-50 text-amber-950'
              }`}>
                {isModifiedSimulationActive
                  ? 'The table is sorted by the largest absolute Goose IDA2 Final delta versus Goose v5 IDA1 across the current in-memory replay for the selected range.'
                  : 'The table is sorted by the largest absolute Goose IDA2 Final delta versus Goose v5 IDA1 on days that also have persisted Goose IDA2 Final settle.'}
              </div>
              <div className={`mt-3 rounded-lg border px-4 py-3 text-xs ${
                darkMode
                  ? 'border-slate-700 bg-slate-950/30 text-slate-300'
                  : 'border-slate-200 bg-slate-50 text-slate-600'
              }`}>
                {isModifiedSimulationActive
                  ? 'Read it left to right: the modified Goose v3 IDA1 replay, the modified Goose v5 IDA1 replay, then the current Goose IDA2 path under the same active parameters. The delta columns show overlay uplift versus Goose v5 and end-to-end uplift versus Goose v3.'
                  : 'Read it left to right: production Goose v3 IDA1, the same Goose mechanics with v5 forecasts, then the official Goose IDA2 overlay result. The delta columns show overlay uplift versus Goose v5 and end-to-end uplift versus Goose v3.'}
              </div>
              <div className="mt-5 overflow-x-auto">
                <table className="min-w-[980px] w-full text-sm">
                  <thead>
                    <tr className={`border-b text-left text-xs uppercase ${darkMode ? 'border-slate-700 text-slate-400' : 'border-gray-200 text-gray-500'}`}>
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4">Goose v3 IDA1</th>
                      <th className="py-2 pr-4">Goose v5 IDA1</th>
                      <th className="py-2 pr-4">Goose IDA2 Final</th>
                      <th className="py-2 pr-4 text-right" title="Goose IDA2 Final minus Goose v5 IDA1 for the same day. Positive means the overlay improved the same v5 path.">Delta vs v5</th>
                      <th className="py-2 pr-4 text-right" title="Goose IDA2 Final minus Goose v3 IDA1 for the same day. Positive means Goose beat the current production Goose reference.">Delta vs v3</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parityTopDiscrepancyRows.map((row) => (
                      <tr key={row.date} className={`border-b ${darkMode ? 'border-slate-800' : 'border-gray-100'}`}>
                        <td className={`py-2 pr-4 font-medium ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>{String(row.date).slice(0, 10)}</td>
                        <td className={`py-2 pr-4 ${getValueColor(row.bison_ida1_pnl)}`}>{formatNumber(row.bison_ida1_pnl)}</td>
                        <td className={`py-2 pr-4 ${getValueColor(row.v5_ida1_pnl)}`}>{formatNumber(row.v5_ida1_pnl)}</td>
                        <td className={`py-2 pr-4 ${getValueColor(row.moose_final_ida2_pnl)}`}>{formatNumber(row.moose_final_ida2_pnl)}</td>
                        <td className={`py-2 pr-4 text-right font-semibold tabular-nums ${getValueColor(row.delta_moose_vs_v5_ida1)}`}>{formatSignedNumber(row.delta_moose_vs_v5_ida1)}</td>
                        <td className={`py-2 pr-4 text-right font-semibold tabular-nums ${getValueColor(row.delta_moose_vs_bison_ida1)}`}>{formatSignedNumber(row.delta_moose_vs_bison_ida1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parityRows.length > parityTopDiscrepancyRows.length && (
                <div className={`mt-3 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  Showing top {parityTopDiscrepancyRows.length} Goose vs Goose v5 overlay deltas out of {parityRows.length} days.
                </div>
              )}
              <div className="mt-5 grid gap-3 lg:grid-cols-2">
                {[
                  { title: 'Goose IDA2 Final adds most vs Goose v5 IDA1', rows: parityTopMoosePositiveRows },
                  { title: 'Goose IDA2 Final hurts most vs Goose v5 IDA1', rows: parityTopMooseNegativeRows },
                ].map((group) => (
                  <div key={group.title} className={`rounded-lg border p-4 ${darkMode ? 'border-slate-700 bg-slate-950/30' : 'border-slate-200 bg-slate-50'}`}>
                    <div className={`text-xs font-semibold uppercase ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{group.title}</div>
                    <div className="mt-3 space-y-2">
                      {group.rows.map((row) => (
                        <div key={`${group.title}-${row.date}`} className="flex items-center justify-between gap-3 text-sm">
                          <span className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{String(row.date).slice(0, 10)}</span>
                          <span className={`font-semibold ${getValueColor(row.delta_moose_vs_v5_ida1)}`}>
                            {formatSignedNumber(row.delta_moose_vs_v5_ida1, ' PLN')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
                </>
              )}
            </div>
          )}

          <div className={`${simulationInsetCardClass} p-6`}>
            <div className="mb-5 flex flex-wrap items-center gap-3">
              <div className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl border ${
                darkMode
                  ? 'border-emerald-400/20 bg-emerald-500/12 text-emerald-100'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-800'
              }`}>
                <TrendingUp className="h-5 w-5" />
              </div>
              <div>
                <div className={simulationEyebrowClass}>{howItWorksLanguage === 'pl' ? 'Results overview' : 'Results overview'}</div>
                <h2 className={`mt-1 text-2xl font-bold ${darkMode ? 'text-slate-50' : 'text-slate-950'}`}>{resultsCopy.summaryTitle}</h2>
              </div>
            </div>
            <div className={`mb-5 rounded-[26px] border px-5 py-4 ${
              darkMode
                ? 'border-emerald-400/25 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.16),_transparent_32%),linear-gradient(135deg,rgba(6,95,70,0.25),rgba(15,23,42,0.88))]'
                : 'border-emerald-200 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.10),_transparent_28%),linear-gradient(135deg,#ecfdf5,#f8fafc)]'
            }`}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div>
                    <div className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                      darkMode ? 'bg-slate-950/45 text-emerald-100 ring-1 ring-emerald-400/20' : 'bg-white/80 text-emerald-700'
                    }`}>
                      {strategyResultLabel}
                    </div>
                  <div className={`mt-3 text-3xl font-bold ${getValueColor(strategyResultValue)}`}>
                    {formatNumber(strategyResultValue)} PLN
                  </div>
                  <div className={`mt-1 text-sm font-medium ${getValueColor(strategyResultAfterFees)}`}>
                    {resultsCopy.afterFees}: {formatNumber(strategyResultAfterFees)} PLN
                  </div>
                  <div className="mt-1 text-sm text-emerald-900/75">
                    {resultsCopy.fees}: {formatNumber(strategyResultFees)} PLN
                  </div>
                </div>
                <div className={`max-w-xl rounded-2xl px-4 py-3 text-sm shadow-sm ring-1 ${
                  darkMode
                    ? 'bg-slate-950/55 text-emerald-50 ring-emerald-400/15'
                    : 'bg-white/80 text-emerald-900 ring-emerald-100'
                }`}>
                  {resultsCopy.liveTotalInfo}
                </div>
              </div>
              {mixedSummaryRange && (
                <div className={`mt-3 rounded-xl border px-4 py-3 text-sm ${
                  darkMode
                    ? 'border-amber-300/35 bg-amber-500/12 text-amber-50'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
                }`}>
                  {resultsCopy.mixedRangeInfo({
                    realizedDays: liveProvisionalSummary.realizedDays,
                    partialDays: liveProvisionalSummary.partialDays,
                    comparableDays: comparableSummary.comparableDays,
                  })}
                </div>
              )}
            </div>
            <div className={`mb-5 rounded-2xl border px-5 py-4 ${
              darkMode
                ? 'border-violet-500/30 bg-violet-950/35'
                : 'border-violet-200 bg-violet-50'
            }`}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <div className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                    darkMode
                      ? 'bg-violet-400/15 text-violet-100'
                      : 'bg-white/80 text-violet-700'
                  }`}>
                    {resultsCopy.strategyProfitPerMwh}
                  </div>
                  <div className={`mt-3 text-3xl font-bold ${getValueColor(liveProvisionalSummary.avg_profit_per_mwh)}`}>
                    {formatNumber(liveProvisionalSummary.avg_profit_per_mwh)} PLN
                  </div>
                  <div className={`mt-1 text-sm ${darkMode ? 'text-violet-100/80' : 'text-violet-900/80'}`}>
                    {resultsCopy.strategyProfitHelper}
                  </div>
                </div>
                <div className={`max-w-xl rounded-2xl px-4 py-3 text-sm shadow-sm ring-1 ${
                  darkMode
                    ? 'bg-slate-950/65 text-violet-100 ring-violet-500/20'
                    : 'bg-white/80 text-violet-900 ring-violet-100'
                }`}>
                  {resultsCopy.strategyProfitInfo}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 text-sm">
              <div className="md:col-span-2 xl:col-span-3">
                <div className={`rounded-xl border px-4 py-4 ${
                  darkMode
                    ? 'border-slate-600 bg-slate-900/75'
                    : 'border-slate-200 bg-slate-50'
                }`}>
                  <div className="space-y-5">
                    <div>
                      <div className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-500'}`}>
                        {resultsCopy.rangeSnapshot}
                      </div>
                      <div className={`mt-1 text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {resultsCopy.rangeSnapshotBody}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className={`overflow-hidden rounded-xl border ${
                        darkMode ? 'border-slate-600' : 'border-slate-200'
                      }`}>
                        <div className="overflow-x-auto">
                          <div className={`grid min-w-[36rem] grid-cols-[1.15fr_repeat(4,minmax(9rem,1fr))] text-[11px] font-semibold uppercase tracking-wide ${
                            darkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-500'
                          }`}>
                            <div className="px-3 py-2">{resultsCopy.byStrategyLeg}</div>
                            {rangeSnapshotSummary.componentColumns.map((column) => (
                              <div
                                key={column.key}
                                className={`px-3 py-2 ${rangeSnapshotComponentStyles[column.key]?.header || ''}`}
                              >
                                {column.label}
                              </div>
                            ))}
                          </div>
                          {[
                            {
                              key: 'pnl',
                              label: resultsCopy.legPnl,
                              value: (column) => `${formatNumber(column.pnl)} PLN`,
                              valueClass: (column) => getValueColor(column.pnl),
                            },
                            {
                              key: 'volume',
                              label: resultsCopy.grossVolume,
                              value: (column) => `${formatInteger(column.volume)} MWh`,
                              valueClass: () => darkMode ? 'text-slate-100' : 'text-slate-900',
                            },
                            {
                              key: 'pnlPerMwh',
                              label: 'PLN / MWh',
                              value: (column) => (column.pnlPerMwh === null ? '-' : `${formatNumber(column.pnlPerMwh)} PLN/MWh`),
                              valueClass: (column) => getValueColor(column.pnlPerMwh),
                            },
                          ].map((row) => (
                            <div
                              key={row.key}
                              className={`grid min-w-[36rem] grid-cols-[1.15fr_repeat(4,minmax(9rem,1fr))] border-t text-sm ${
                                darkMode ? 'border-slate-600 bg-slate-900/40' : 'border-slate-200 bg-white/80'
                              }`}
                            >
                              <div className={`px-3 py-3 font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{row.label}</div>
                              {rangeSnapshotSummary.componentColumns.map((column) => (
                                <div
                                  key={`${row.key}-${column.key}`}
                                  className={`px-3 py-3 font-semibold ${rangeSnapshotComponentStyles[column.key]?.cell || ''} ${row.valueClass(column)}`}
                                >
                                  {row.value(column)}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className={`mt-2 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {resultsCopy.componentsNote}
                      </div>
                    </div>
                    <div className={`overflow-hidden rounded-xl border ${
                      darkMode ? 'border-slate-600' : 'border-slate-200'
                    }`}>
                      <div className={`grid grid-cols-[1.75fr_repeat(4,minmax(0,1fr))] text-[11px] font-semibold uppercase tracking-wide ${
                        darkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-500'
                      }`}>
                        <div className="px-4 py-2">{resultsCopy.metric}</div>
                        <div className="px-4 py-2">{resultsCopy.total}</div>
                        <div className="px-4 py-2">{resultsCopy.profitable}</div>
                        <div className="px-4 py-2">{resultsCopy.flat}</div>
                        <div className="px-4 py-2">{resultsCopy.loss}</div>
                      </div>
                      {[
                        {
                          key: 'days',
                          label: resultsCopy.days,
                          stats: rangeSnapshotSummary.dayStats,
                        },
                        {
                          key: 'baseDays',
                          label: resultsCopy.basePnlDays,
                          stats: rangeSnapshotSummary.baseDayStats,
                        },
                      ].map((row) => (
                        <div
                          key={row.key}
                          className={`grid grid-cols-[1.75fr_repeat(4,minmax(0,1fr))] border-t text-sm ${
                            darkMode ? 'border-slate-600 text-slate-100' : 'border-slate-200 text-slate-900'
                          }`}
                        >
                          <div className="px-4 py-3 font-medium">{row.label}</div>
                          <div className="px-4 py-3 font-semibold">
                            {row.stats.total > 0 ? row.stats.total : (row.emptyLabel || '0')}
                          </div>
                          <div className="px-4 py-3">
                            <div className="font-semibold text-emerald-400">{row.stats.profitable}</div>
                            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {row.stats.total > 0 ? `${formatNumber(row.stats.profitablePct)}%` : '-'}
                            </div>
                          </div>
                          <div className="px-4 py-3">
                            <div className={`font-semibold ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{row.stats.flat}</div>
                            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {row.stats.total > 0 ? `${formatNumber(row.stats.flatPct)}%` : '-'}
                            </div>
                          </div>
                          <div className="px-4 py-3">
                            <div className="font-semibold text-rose-400">{row.stats.loss}</div>
                            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {row.stats.total > 0 ? `${formatNumber(row.stats.lossPct)}%` : '-'}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className={`grid grid-cols-[1.15fr_3.85fr] border-t text-sm ${
                        darkMode ? 'border-slate-600 text-slate-100' : 'border-slate-200 text-slate-900'
                      }`}>
                        <div className="px-4 py-3 font-medium">{resultsCopy.baseSetupDirection}</div>
                        <div className="px-4 py-3">
                          <span className="font-semibold text-emerald-400">{rangeSnapshotSummary.baseActions.buy} buy</span>
                          <span className={`mx-2 ${darkMode ? 'text-slate-500' : 'text-slate-300'}`}>/</span>
                          <span className="font-semibold text-rose-300">{rangeSnapshotSummary.baseActions.sell} sell</span>
                          <span className={`mx-2 ${darkMode ? 'text-slate-500' : 'text-slate-300'}`}>/</span>
                          <span className="font-semibold text-cyan-300">{rangeSnapshotSummary.baseActions.settled} settled activity</span>
                          <span className={`mx-2 ${darkMode ? 'text-slate-500' : 'text-slate-300'}`}>/</span>
                          <span className={`font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-700'}`}>{rangeSnapshotSummary.baseActions.idle} idle</span>
                          <div className="mt-2 flex flex-wrap gap-2 text-xs">
                            <span className={`inline-flex items-center rounded-full border px-2.5 py-1 font-semibold ${
                              darkMode ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            }`}>
                              {resultsCopy.baseProfitableDays}: {rangeSnapshotSummary.baseDayStats.profitable}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={`grid grid-cols-[1.15fr_3.85fr] border-t text-sm ${
                        darkMode ? 'border-slate-600 text-slate-100' : 'border-slate-200 text-slate-900'
                      }`}>
                        <div className="px-4 py-3 font-medium">{resultsCopy.avgDailyPnl}</div>
                        <div className={`px-4 py-3 font-semibold ${getValueColor(rangeSnapshotSummary.avgDailyPnl)}`}>
                          {formatNumber(rangeSnapshotSummary.avgDailyPnl)} PLN
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-first md:col-span-2 xl:col-span-3">
                <div className={`rounded-xl border px-4 py-4 ${
                  darkMode
                    ? 'border-cyan-500/30 bg-cyan-950/30'
                    : 'border-cyan-200 bg-cyan-50'
                }`}>
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <div className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
                        darkMode
                          ? 'bg-cyan-400/15 text-cyan-100'
                          : 'bg-white/80 text-cyan-800'
                      }`}>
                        {resultsCopy.totalVolume}
                      </div>
                      <div className={`mt-3 text-3xl font-bold ${darkMode ? 'text-cyan-50' : 'text-cyan-950'}`}>
                        {formatInteger(liveProvisionalSummary.total_volume)} MWh
                      </div>
                      <div className={`mt-1 text-sm ${darkMode ? 'text-cyan-100/80' : 'text-cyan-900/80'}`}>
                        {resultsCopy.totalVolumeBody}
                      </div>
                      <div className={`mt-1 text-xs ${darkMode ? 'text-cyan-100/65' : 'text-cyan-900/70'}`}>
                        {resultsCopy.venueChipsNote}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <div className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        darkMode
                          ? 'border-cyan-500/25 bg-slate-950/60 text-cyan-100'
                          : 'border-cyan-200 bg-white/80 text-cyan-900'
                      }`}>
                        Base: {formatInteger(liveProvisionalSummary.venue_base_mwh)} MWh
                      </div>
                      <div className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        darkMode
                          ? 'border-cyan-500/25 bg-slate-950/60 text-cyan-100'
                          : 'border-cyan-200 bg-white/80 text-cyan-900'
                      }`}>
                        Fix I: {formatInteger(liveProvisionalSummary.venue_fix_i_mwh)} MWh
                      </div>
                      <div className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        darkMode
                          ? 'border-cyan-500/25 bg-slate-950/60 text-cyan-100'
                          : 'border-cyan-200 bg-white/80 text-cyan-900'
                      }`}>
                        Fix II: {formatInteger(liveProvisionalSummary.venue_fix_ii_mwh)} MWh
                      </div>
                      <div className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        darkMode
                          ? 'border-cyan-500/25 bg-slate-950/60 text-cyan-100'
                          : 'border-cyan-200 bg-white/80 text-cyan-900'
                      }`}>
                        IDA1: {formatInteger(liveProvisionalSummary.venue_ida1_mwh)} MWh
                      </div>
                      <div className={`rounded-full border px-3 py-1 text-xs font-medium ${
                        darkMode
                          ? 'border-cyan-500/25 bg-slate-950/60 text-cyan-100'
                          : 'border-cyan-200 bg-white/80 text-cyan-900'
                      }`}>
                        IDA2: {formatInteger(liveProvisionalSummary.venue_ida2_mwh)} MWh
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={`md:col-span-2 xl:col-span-3 rounded-xl border px-4 py-3 ${
                darkMode
                  ? 'border-slate-600 bg-slate-900/70'
                  : 'border-slate-200 bg-slate-50'
              }`}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                      darkMode ? 'bg-indigo-400/15 text-indigo-200' : 'bg-indigo-50 text-indigo-700'
                    }`}>
                      <BarChart3 className="h-4 w-4" />
                    </div>
                    <div>
                      <div className={`font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>{resultsCopy.benchmarkStopComparison}</div>
                      <div className={`mt-0.5 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {resultsCopy.ida2ComparableDays(comparableSummary.comparableDays)}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-start gap-2 lg:justify-end">
                    {[
                      { label: 'Base', value: comparableSummary.total_combined_pnl_base_stop },
                      { label: 'Fixing', value: comparableSummary.fix_layer_pnl },
                      { label: 'IDA1 Stop', value: comparableSummary.total_combined_pnl },
                      { label: 'IDA2 Final', value: comparableSummary.total_combined_pnl_ida2_close },
                      { label: 'Carry', value: comparableSummary.ida2_layer_pnl },
                    ].map((item) => (
                      <span
                        key={item.label}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          darkMode
                            ? 'border-slate-600 bg-slate-950/40 text-slate-200'
                            : 'border-slate-200 bg-white text-slate-700'
                        }`}
                      >
                        <span className={getValueColor(item.value)}>{formatNumber(item.value)}</span>
                        <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>PLN</span>
                      </span>
                    ))}
                  <button
                    type="button"
                    onClick={() => setIsBenchmarkSectionExpanded((prev) => !prev)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${
                      darkMode
                        ? 'border-slate-500 bg-slate-800 text-slate-100 hover:bg-slate-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {isBenchmarkSectionExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {isBenchmarkSectionExpanded ? resultsCopy.collapseBenchmark : resultsCopy.expandBenchmark}
                  </button>
                  </div>
                </div>
                {isBenchmarkSectionExpanded ? (
                  <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {benchmarkComparisonRows.map((row) => {
                      const tooltip = [
                        row.helper,
                        `${resultsCopy.afterFees}: ${formatNumber(row.afterFeesValue)} PLN`,
                        `${resultsCopy.fees}: ${formatNumber(row.feesValue)} PLN`,
                        `${resultsCopy.totalVolumeTooltip}: ${row.totalVolume === null || row.totalVolume === undefined ? '-' : `${formatNumber(row.totalVolume)} MWh`}`,
                        `${resultsCopy.profitPerMwh}: ${row.profitPerMwh === null || row.profitPerMwh === undefined ? '-' : `${formatNumber(row.profitPerMwh)} PLN/MWh`}`,
                      ].join('\n');
                      return (
                        <div
                          key={row.key || row.label}
                          className={`flex min-h-[150px] flex-col justify-between rounded-lg border p-3 ${
                            darkMode
                              ? 'border-slate-700 bg-slate-950/35'
                              : 'border-slate-200 bg-white'
                          }`}
                          title={tooltip}
                        >
                          <div>
                            <div className="flex items-start justify-between gap-2">
                              <div className={`text-[11px] font-semibold uppercase leading-snug ${
                                darkMode ? 'text-slate-300' : 'text-slate-600'
                              }`}>
                                {row.label}
                              </div>
                              <Info className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                            </div>
                            <div className={`mt-3 text-lg font-bold leading-tight ${getValueColor(row.totalValue)}`}>
                              {formatNumber(row.totalValue)} PLN
                            </div>
                            <div className={`mt-1 text-xs ${getValueColor(row.afterFeesValue)}`}>
                              {resultsCopy.afterFees} {formatNumber(row.afterFeesValue)} PLN
                            </div>
                            {row.carryBenefit != null ? (
                              <div className={`mt-1 text-xs font-semibold ${getValueColor(row.carryBenefit)}`}>
                                {`Carry vs IDA1: ${row.carryBenefit >= 0 ? '+' : ''}${formatNumber(row.carryBenefit)} PLN`}
                              </div>
                            ) : null}
                          </div>
                          <div className={`mt-3 border-t pt-2 text-[11px] ${
                            darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'
                          }`}>
                            <div className="flex items-center justify-between gap-2">
                              <span>{resultsCopy.vol}</span>
                              <span className={darkMode ? 'text-slate-200' : 'text-slate-700'}>
                                {row.totalVolume === null || row.totalVolume === undefined ? '-' : `${formatNumber(row.totalVolume)} MWh`}
                              </span>
                            </div>
                            <div className="mt-1 flex items-center justify-between gap-2">
                              <span>PLN/MWh</span>
                              <span className={`font-semibold ${getValueColor(row.profitPerMwh)}`}>
                                {row.profitPerMwh === null || row.profitPerMwh === undefined ? '-' : formatNumber(row.profitPerMwh)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <div className="md:col-span-2 xl:col-span-3">
                <div className={`rounded-xl border px-4 py-3 ${
                  darkMode
                    ? 'border-slate-600 bg-slate-900/70'
                    : 'border-slate-200 bg-slate-50'
                }`}>
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                        darkMode ? 'bg-emerald-400/15 text-emerald-200' : 'bg-emerald-50 text-emerald-700'
                      }`}>
                        <Activity className="h-4 w-4" />
                      </div>
                      <div>
                        <div className={`font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>{resultsCopy.finalMaeTitle}</div>
                        <div className={`mt-0.5 max-w-3xl text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {resultsCopy.finalMaeSubtitle}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-start gap-2 lg:justify-end">
                      {finalMaeRows.map((row) => (
                        <span
                          key={row.key}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                            darkMode
                              ? 'border-slate-600 bg-slate-950/40 text-slate-200'
                              : 'border-slate-200 bg-white text-slate-700'
                          }`}
                          title={row.helper}
                        >
                          <span>{row.label}</span>
                          <span className={darkMode ? 'text-slate-500' : 'text-slate-300'}>/</span>
                          <span>{row.data?.available ? `${formatNumber(row.data.mae)} PLN/MWh` : '-'}</span>
                        </span>
                      ))}
                      {finalMaeSummary.error && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                          {resultsCopy.maeRefreshWarning}: {finalMaeSummary.error}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setIsFinalMaeSectionExpanded((prev) => !prev)}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${
                          darkMode
                            ? 'border-slate-500 bg-slate-800 text-slate-100 hover:bg-slate-700'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {isFinalMaeSectionExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        {isFinalMaeSectionExpanded ? resultsCopy.collapseFinalMae : resultsCopy.expandFinalMae}
                      </button>
                    </div>
                  </div>
                  {isFinalMaeSectionExpanded ? (
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                      {finalMaeRows.map((row) => (
                        <div key={row.key} className={`rounded-xl border px-4 py-4 shadow-sm ${
                          darkMode
                            ? 'border-slate-500 bg-slate-900/85'
                            : 'border-slate-200 bg-white'
                        }`}>
                          <div className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{row.label}</div>
                          <div className={`mt-1 text-xs ${darkMode ? 'text-slate-300' : 'text-slate-500'}`}>{row.helper}</div>
                          {row.data?.available ? (
                            <>
                              <div className={`mt-4 text-2xl font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {formatNumber(row.data.mae)} PLN/MWh
                              </div>
                              <div className={`mt-1 text-xs font-medium uppercase tracking-wide ${darkMode ? 'text-slate-300' : 'text-slate-500'}`}>
                                {resultsCopy.finalMae}
                              </div>
                              <div className="mt-4 space-y-2 text-xs">
                                <div className="flex items-center justify-between gap-3">
                                  <span className={darkMode ? 'text-slate-300' : 'text-slate-500'}>{resultsCopy.maeValue}</span>
                                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                    {formatNumber(row.data.mae)} PLN/MWh
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span className={darkMode ? 'text-slate-300' : 'text-slate-500'}>{resultsCopy.bias}</span>
                                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                    {formatSignedNumber(row.data.bias, ' PLN/MWh')}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span className={darkMode ? 'text-slate-300' : 'text-slate-500'}>{resultsCopy.matchedHours}</span>
                                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{row.data.hours ?? 0}</span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span className={darkMode ? 'text-slate-300' : 'text-slate-500'}>{resultsCopy.matchedDays}</span>
                                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{row.data.days ?? 0}</span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span className={darkMode ? 'text-slate-300' : 'text-slate-500'}>{resultsCopy.avgForecast}</span>
                                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                    {formatNumber(row.data.avg_forecast)} PLN/MWh
                                  </span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span className={darkMode ? 'text-slate-300' : 'text-slate-500'}>{resultsCopy.avgSettled}</span>
                                  <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                    {formatNumber(row.data.avg_actual)} PLN/MWh
                                  </span>
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className={`mt-4 rounded-lg border border-dashed px-3 py-3 text-sm ${
                              darkMode
                                ? 'border-slate-500 bg-slate-800/60 text-slate-200'
                                : 'border-slate-200 bg-slate-50 text-slate-600'
                            }`}>
                              {resultsCopy.noSettledPrices}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className={`md:col-span-2 xl:col-span-3 rounded-xl border px-4 py-3 ${
                darkMode
                  ? 'border-slate-600 bg-slate-900/70'
                  : 'border-slate-200 bg-slate-50'
              }`}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                      darkMode ? 'bg-cyan-400/15 text-cyan-200' : 'bg-cyan-50 text-cyan-700'
                    }`}>
                      <TrendingUp className="h-4 w-4" />
                    </div>
                    <div>
                      <div className={`font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>{resultsCopy.pnlLegsActivity}</div>
                      <div className={`mt-0.5 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {resultsCopy.tradingDaysSubtitle({
                          tradingDays: summary.trading_days ?? 0,
                          ida2CompleteDays: summary.ida2_complete_days ?? 0,
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-start gap-2 lg:justify-end">
                    {rangeSnapshotSummary.componentColumns.map((item) => (
                      <span
                        key={item.key}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                          darkMode
                            ? 'border-slate-600 bg-slate-950/40 text-slate-200'
                            : 'border-slate-200 bg-white text-slate-700'
                        }`}
                      >
                        <span>{item.key === 'fixing' ? 'Fix' : item.label.replace(' leg', '')}</span>
                        <span className={getValueColor(item.pnl)}>{formatSignedNumber(item.pnl, ' PLN')}</span>
                      </span>
                    ))}
                  <button
                    type="button"
                    onClick={() => setIsPnlComponentsSectionExpanded((prev) => !prev)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${
                      darkMode
                        ? 'border-slate-500 bg-slate-800 text-slate-100 hover:bg-slate-700'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {isPnlComponentsSectionExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {isPnlComponentsSectionExpanded ? resultsCopy.collapsePnlActivity : resultsCopy.expandPnlActivity}
                  </button>
                  </div>
                </div>
              </div>
              {isPnlComponentsSectionExpanded ? (
                <div className={`md:col-span-2 xl:col-span-3 rounded-xl border px-4 py-4 ${
                  darkMode
                    ? 'border-slate-600 bg-slate-900/70'
                    : 'border-slate-200 bg-slate-50'
                }`}>
                  <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2 xl:grid-cols-3">
                  <div>
                    <div className="text-gray-500">{resultsCopy.columns.basePnl}</div>
                    <div className={`font-semibold ${getValueColor(rangeSnapshotSummary.componentTotals.basePnl)}`}>{formatNumber(rangeSnapshotSummary.componentTotals.basePnl)} PLN</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.fixLegPnl}</div>
                    <div className={`font-semibold ${getValueColor(rangeSnapshotSummary.componentTotals.fixingPnl)}`}>{formatNumber(rangeSnapshotSummary.componentTotals.fixingPnl)} PLN</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.ida1LegPnl}</div>
                    <div className={`font-semibold ${getValueColor(rangeSnapshotSummary.componentTotals.ida1Pnl)}`}>{formatNumber(rangeSnapshotSummary.componentTotals.ida1Pnl)} PLN</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.ida2LegPnl}</div>
                    <div className={`font-semibold ${getValueColor(rangeSnapshotSummary.componentTotals.ida2Pnl)}`}>{formatNumber(rangeSnapshotSummary.componentTotals.ida2Pnl)} PLN</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.tradingFinalisedDays}</div>
                    <div className="font-semibold text-gray-800">{summary.trading_days ?? 0} / {summary.settled_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.ida2CompleteDays}</div>
                    <div className="font-semibold text-gray-800">{summary.ida2_complete_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.ida1CompleteDays}</div>
                    <div className="font-semibold text-gray-800">{summary.ida1_complete_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.historicalFix2CloseDays}</div>
                    <div className="font-semibold text-gray-800">{summary.historical_fix2_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.historicalIda1CloseDays}</div>
                    <div className="font-semibold text-gray-800">{summary.historical_ida1_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.ida1FallbackDays}</div>
                    <div className="font-semibold text-gray-800">{summary.ida1_fallback_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.ida2NoIda1Days}</div>
                    <div className="font-semibold text-gray-800">{summary.ida2_no_ida1_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.fix2FallbackDays}</div>
                    <div className="font-semibold text-gray-800">{summary.fix2_fallback_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.partialFix2SettleDays}</div>
                    <div className="font-semibold text-gray-800">{summary.partial_fix2_settle_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.fix2BetterThanIda1Days}</div>
                    <div className="font-semibold text-gray-800">{summary.fix2_better_than_ida1_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.ida1BetterThanFix2Days}</div>
                    <div className="font-semibold text-gray-800">{summary.ida1_better_than_fix2_days ?? 0}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.avgDailyPnl}</div>
                    <div className={`font-semibold ${getValueColor(rangeSnapshotSummary.avgDailyPnl)}`}>{formatNumber(rangeSnapshotSummary.avgDailyPnl)} PLN</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.venueBase}</div>
                    <div className="font-semibold text-gray-800">{formatInteger(liveProvisionalSummary.venue_base_mwh)} MWh</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.venueFixI}</div>
                    <div className="font-semibold text-gray-800">{formatInteger(liveProvisionalSummary.venue_fix_i_mwh)} MWh</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.venueFixII}</div>
                    <div className="font-semibold text-gray-800">{formatInteger(liveProvisionalSummary.venue_fix_ii_mwh)} MWh</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.venueIda1}</div>
                    <div className="font-semibold text-gray-800">{formatInteger(liveProvisionalSummary.venue_ida1_mwh)} MWh</div>
                  </div>
                  <div>
                    <div className="text-gray-500">{resultsCopy.venueIda2}</div>
                    <div className="font-semibold text-gray-800">{formatInteger(liveProvisionalSummary.venue_ida2_mwh)} MWh</div>
                  </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <AccumulatedPnLChart
            rows={dailyRows}
            parityRows={parityRows}
            formatNumber={formatNumber}
            darkMode={darkMode}
            copy={resultsCopy.accumulatedChart}
            getIda1BenchmarkPnl={getIda1BenchmarkPnl}
          />
          </>
          )}

        {/* ── Results Range — Daily Results ─────────────────────── */}
        {isDailyResultsSection && (
        <div ref={dailyResultsSectionRef} className={`${simulationInsetCardClass} overflow-visible`}>
          <div className={`flex flex-wrap items-center gap-3 border-b px-6 py-4 ${
            darkMode ? 'border-slate-700/70 bg-slate-950/30' : 'border-slate-200 bg-white/80'
          }`}>
            <div className="flex items-center gap-2 shrink-0">
              <div className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border ${
                darkMode
                  ? 'border-cyan-400/20 bg-cyan-500/12 text-cyan-100'
                  : 'border-cyan-200 bg-cyan-50 text-cyan-800'
              }`}>
                <Calendar className="h-5 w-5" />
              </div>
              <div>
                <div className={simulationEyebrowClass}>{howItWorksLanguage === 'pl' ? 'Day by day' : 'Day by day'}</div>
                <h2 className={`mt-1 text-2xl font-bold ${darkMode ? 'text-slate-50' : 'text-slate-950'}`}>{resultsCopy.dailyTitle}</h2>
              </div>
            </div>
            <div className={`min-w-0 flex-1 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {resultsCopy.dailySubtitle}
            </div>
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <label className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                {resultsCopy.dailySortBy}
              </label>
              <select
                value={dailyResultsSortKey}
                onChange={(event) => setDailyResultsSortKey(event.target.value)}
                className={`rounded-lg px-3 py-2 text-sm font-medium shadow-sm outline-none transition focus:ring-2 ${
                  darkMode
                    ? 'border border-slate-700 bg-slate-950 text-slate-100 focus:border-cyan-400 focus:ring-cyan-500/20'
                    : 'border border-gray-200 bg-white text-gray-700 focus:border-blue-400 focus:ring-blue-100'
                }`}
              >
                <option value="date">{resultsCopy.dailySortOptions.date}</option>
                <option value="result">{resultsCopy.dailySortOptions.result}</option>
                <option value="basePnl">{resultsCopy.dailySortOptions.basePnl}</option>
                <option value="fixPnl">{resultsCopy.dailySortOptions.fixPnl}</option>
                <option value="ida1Pnl">{resultsCopy.dailySortOptions.ida1Pnl}</option>
                <option value="ida2Pnl">{resultsCopy.dailySortOptions.ida2Pnl}</option>
                <option value="volume">{resultsCopy.dailySortOptions.volume}</option>
              </select>
              <label className={`text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                {resultsCopy.dailySortDirection}
              </label>
              <button
                type="button"
                onClick={() => setDailyResultsSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'))}
                className={`inline-flex items-center rounded-lg px-3 py-2 text-sm font-semibold shadow-sm transition ${
                  darkMode
                    ? 'border border-slate-700 bg-slate-950 text-slate-100 hover:border-cyan-400/40 hover:text-cyan-100'
                    : 'border border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:text-blue-700'
                }`}
              >
                {dailyResultsSortKey === 'date'
                  ? (dailyResultsSortDirection === 'desc' ? resultsCopy.dailySortNewestFirst : resultsCopy.dailySortOldestFirst)
                  : (dailyResultsSortDirection === 'desc' ? resultsCopy.dailySortHighestFirst : resultsCopy.dailySortLowestFirst)}
              </button>
            </div>
          </div>
          <div
            ref={dailyResultsStickyWrapperRef}
            className={`sticky top-0 z-20 backdrop-blur transition-opacity ${
              darkMode ? 'bg-slate-950/95' : 'bg-white/95'
            } ${
              hideDailyFloatingScroll ? 'pointer-events-none opacity-0' : 'opacity-100'
            }`}
          >
              <div
                ref={dailyResultsHeaderRef}
                onScroll={() => syncDailyResultsScroll(dailyResultsHeaderRef, [dailyResultsScrollBodyRef])}
                className={`overflow-x-auto border-b ${darkMode ? 'border-slate-700/70' : 'border-gray-200'}`}
              >
                <table className={`${dailyResultsTableMinWidthClass} w-full table-fixed text-sm`}>
                  <thead className={darkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-50'}>
                    <tr>
                      {dailyResultsColumns.map((column) => (
                        <th
                          key={column.label}
                          className={`${column.widthClass} border-b px-3 py-3 text-left text-xs font-medium uppercase ${
                            darkMode
                              ? 'border-slate-700/70 bg-slate-900 text-slate-400'
                              : 'border-gray-200 bg-gray-50 text-gray-500'
                          }`}
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>
          </div>
          <div
            ref={dailyResultsScrollBodyRef}
            onScroll={() => syncDailyResultsScroll(dailyResultsScrollBodyRef, [dailyResultsHeaderRef])}
            className="overflow-x-auto"
          >
            <table className={`${dailyResultsTableMinWidthClass} w-full table-fixed text-sm`}>
              <tbody>
                {dailyRows.length === 0 ? (
                  <tr>
                    <td colSpan={dailyResultsColumns.length} className="px-4 py-6 text-gray-500 text-center">{resultsCopy.noSimulationResults}</td>
                  </tr>
                ) : (
                  sortedDailyRows.map((row) => {
                    const isExpanded = expandedDate === row.date;
                    const rawDayDetails = (
                      runPanelMode.settle === 'history'
                      && selectedSettleDetails
                      && String(selectedSettleDetails.summary?.date || '') === String(row.date)
                    )
                      ? selectedSettleDetails
                      : dayDetailsByDate[row.date];
                    const dayDetails = rawDayDetails && row?.is_modified_simulation
                      ? {
                          ...rawDayDetails,
                          summary: {
                            ...(rawDayDetails.summary || {}),
                            ...row,
                            warning_message: rawDayDetails.summary?.warning_message,
                          },
                        }
                      : rawDayDetails;
                    const actualDailyResult = getActualDailyResultValue(row);
                    const actualDailyAfterFees = getActualDailyAfterFeesValue(row);
                    const parityRow = parityRowsByDate.get(String(row.date).slice(0, 10));
                    const bisonV5Ida1ReferencePnl = getGooseV5Ida1ReferencePnl(parityRow);
                    const bisonV5Ida1ReferenceAccPnl = bisonV5Ida1AccByDate[String(row.date)] ?? null;
                    const bisonV5Ida1ReferenceLoading = parityReportLoading && !parityRow;
                    const legacyGooseComparablePnl = getLegacyGooseComparablePnl(dayDetails?.summary || row);
                    const legacyGooseComparableAccPnl = legacyGooseComparableAccByDate[String(row.date)] ?? null;
                    const baseContext = row.base_context || {};
                    const dayDetailsHourlyRows = dayDetails?.hourly || [];
                    const q50Averages = getDailyQ50Averages(dayDetailsHourlyRows);
                    const actualAverages = getDailyActualPriceAverages(dayDetailsHourlyRows);
                    const isDayOfficialSettleStoppedAtFix2 = Boolean(isPartialFix2Settle(dayDetails?.summary || row));
                    const dayStageAvailability = {
                      fix_i: actualAverages.fix_i !== null,
                      fix_ii: actualAverages.fix2 !== null,
                      ida1: isDayOfficialSettleStoppedAtFix2 ? false : actualAverages.ida1 !== null,
                      ida2: isDayOfficialSettleStoppedAtFix2 ? false : actualAverages.ida2 !== null,
                    };
                    const dayHasPostFixIStageRows = (dayDetailsHourlyRows || []).some((hourRow) => (
                      Math.abs(Number(hourRow?.closed_volume_fix2 || 0)) > 0.0001
                      || Math.abs(Number(hourRow?.closed_volume_ida1 || 0)) > 0.0001
                      || Math.abs(Number(hourRow?.closed_volume_ida2 || 0)) > 0.0001
                      || Math.abs(Number(hourRow?.residual_to_ida2 || 0)) > 0.0001
                      || (hourRow?.fix_ii_price !== null && hourRow?.fix_ii_price !== undefined)
                      || (hourRow?.ida1_price !== null && hourRow?.ida1_price !== undefined)
                      || (hourRow?.ida2_price !== null && hourRow?.ida2_price !== undefined)
                    ));
                    const dayExecutionPathNotice = isDayOfficialSettleStoppedAtFix2 && dayHasPostFixIStageRows
                      ? {
                          title: pipelineCopy.executionFix2FallbackNoticeTitle,
                          body: pipelineCopy.executionFix2FallbackNoticeBody,
                        }
                      : null;
                    const dayWarningMessage = dayDetails?.summary?.warning_message
                      ? (howItWorksLanguage === 'pl' && String(dayDetails.summary.warning_message).startsWith('Settled from predictions_sim.fix2_hourly_plan')
                        ? resultsCopy.settledFromPlan
                        : dayDetails.summary.warning_message)
                      : '';
                    const collapsedGooseRiskRow = gooseRiskDayRowByDate.get(String(row.date).slice(0, 10)) || null;
                    return (
                      <React.Fragment key={row.date}>
                        <tr className={`border-t ${getRowClass(row)}`}>
                          <td className={`${dailyResultsColumns[0].widthClass} px-3 py-3`}>
                            <button
                              type="button"
                              onClick={() => toggleDay(row.date)}
                              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                            >
                              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              <span className="text-xs font-medium">{resultsCopy.view}</span>
                            </button>
                          </td>
                          <td className={`${dailyResultsColumns[1].widthClass} px-3 py-3`}>{String(row.date).slice(0, 10)}</td>
                          <td className={`${dailyResultsColumns[2].widthClass} px-3 py-3`}>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex min-h-[30px] items-center justify-center whitespace-nowrap rounded-full border px-3 py-1 text-[11px] font-semibold leading-none ${getDisplayedDayStatusBadgeClass(row)}`}>
                                {getDisplayedDayStatusLabel(row)}
                              </span>
                              {row.is_modified_simulation ? (
                                <span
                                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                                    darkMode
                                      ? 'border-emerald-300/40 bg-emerald-500/10 text-emerald-100'
                                      : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                  }`}
                                  title={`${resultsCopy.modifiedReplayDeltaTitle}: ${formatSignedNumber(row.modified_replay_delta_ida2_close_pnl, ' PLN')}`}
                                >
                                  {resultsCopy.modified}
                                </span>
                              ) : null}
                              {isManualRecalcActive(row.recalc_status) ? (
                                <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${getManualRecalcBadgeClass(row.recalc_status)}`}>
                                  {getManualRecalcLabel(row.recalc_status)}
                                </span>
                              ) : null}
                              {isManualRecalcActive(row.base_execution_recalc_status) ? (
                                <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${getManualRecalcBadgeClass(row.base_execution_recalc_status)}`}>
                                  {getBaseExecutionRecalcLabel(row.base_execution_recalc_status, howItWorksLanguage)}
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className={`${dailyResultsColumns[3].widthClass} px-3 py-3`}>
                            <div className="space-y-1">
                              <div className={`text-base font-semibold tabular-nums ${getValueColor(actualDailyResult)}`}>
                                {formatNumber(actualDailyResult)} PLN
                              </div>
                              <div className={`text-xs ${getValueColor(actualDailyAfterFees)}`}>
                                {resultsCopy.afterFees}: {formatNumber(actualDailyAfterFees)} PLN
                              </div>
                              {row.is_modified_simulation && row.modified_replay_delta_ida2_close_pnl !== null && row.modified_replay_delta_ida2_close_pnl !== undefined ? (
                                <div className={`text-[11px] ${getValueColor(row.modified_replay_delta_ida2_close_pnl)}`}>
                                  {resultsCopy.modifiedDelta}: {formatSignedNumber(row.modified_replay_delta_ida2_close_pnl, ' PLN')}
                                </div>
                              ) : null}
                            </div>
                          </td>
	                          <td className={`${dailyResultsColumns[4].widthClass} px-3 py-3`}>
	                            <div className="flex max-w-full min-w-0 flex-col items-start gap-1.5">
                              <div
                                className={`inline-flex max-w-full shrink-0 items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold leading-none ${getBaseActionBadgeClass(baseContext, row)}`}
                                title={[
                                  getBaseSourceLabel(baseContext, row),
                                  `${resultsCopy.venue}: ${formatNumber(getBaseDailyVenueVolume(baseContext, row))} MWh`,
                                  shouldShowBaseSettledVolume(baseContext, row)
                                    ? `${resultsCopy.settledClosed}: ${formatNumber(getBaseSettledVolume(baseContext, row))} MWh`
                                    : null,
                                  baseContext?.runtime_base_price === null || baseContext?.runtime_base_price === undefined
                                    ? `${resultsCopy.runtimePrice}: -`
                                    : `${resultsCopy.runtimePrice}: ${formatNumber(baseContext.runtime_base_price)} PLN/MWh`,
                                ].filter(Boolean).join('\n')}
                              >
                                {getBaseSetupLabel(baseContext, row)}
                              </div>
                              {renderSetupBaseActivityBadges(collapsedGooseRiskRow, { stacked: true, compact: true })}
                            </div>
                          </td>
                          <td className={`${dailyResultsColumns[5].widthClass} px-3 py-3 tabular-nums ${getValueColor(row.base_pnl)}`}>{formatNumber(row.base_pnl)}</td>
                          <td className={`${dailyResultsColumns[6].widthClass} px-3 py-3 tabular-nums ${getValueColor(row.probabilistic_pnl)}`}>{formatNumber(row.probabilistic_pnl)}</td>
                          <td className={`${dailyResultsColumns[7].widthClass} px-3 py-3 tabular-nums ${getValueColor(row.ida1_pnl)}`}>{isPartialFix2Settle(row) ? '-' : formatNumber(row.ida1_pnl)}</td>
                          <td className={`${dailyResultsColumns[8].widthClass} px-3 py-3 tabular-nums ${getValueColor(row.ida2_pnl)}`}>{isIda2CloseUnavailable(row) ? '-' : formatNumber(row.ida2_pnl)}</td>
                          <td className={`${dailyResultsColumns[9].widthClass} px-3 py-3 tabular-nums text-gray-900`}>{formatInteger(getPathVolume(row))}</td>
                        </tr>
                        {isExpanded && (
                          <tr className="border-t bg-gray-50">
                              <td colSpan={dailyResultsColumns.length} className="p-0 align-top">
                              {detailsLoadingDate === row.date ? (
                                <div className="p-6">
                                  {renderInlineLoading(resultsCopy.loadingDayDetails, 'emerald')}
                                </div>
                              ) : !dayDetails ? (
                                <div className="p-6 text-gray-500">{resultsCopy.noDayDetails}</div>
                              ) : (
                                <div ref={expandedDetailRegionRef} className="w-full max-w-full min-w-0 space-y-3 p-3">
                                  {dayWarningMessage && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-amber-900 text-sm">
                                      {dayWarningMessage}
                                    </div>
                                  )}

                                  {dayDetails.summary.base_execution_recalc_status
                                  && !shouldSuppressEarlyFix2BaseExecutionRecalcFailure(dayDetails.summary.base_execution_recalc_status, dayDetails.date || row.date) ? (
                                    <div className={`rounded-xl border px-4 py-3 text-sm ${
                                      isManualRecalcActive(dayDetails.summary.base_execution_recalc_status)
                                        ? 'border-amber-200 bg-amber-50 text-amber-900'
                                        : String(dayDetails.summary.base_execution_recalc_status?.status || '').toLowerCase() === 'failed'
                                          ? 'border-rose-200 bg-rose-50 text-rose-900'
                                          : 'border-emerald-200 bg-emerald-50 text-emerald-900'
                                    }`}>
                                      <div className="font-semibold">
                                        {getBaseExecutionRecalcLabel(dayDetails.summary.base_execution_recalc_status, howItWorksLanguage)}
                                      </div>
                                      {isManualRecalcActive(dayDetails.summary.base_execution_recalc_status) ? (
                                        <div className="mt-1 text-xs">
                                          {simCopy.letsTrade.baseSettleRecalcActive}
                                        </div>
                                      ) : null}
                                    </div>
                                  ) : null}

                                  <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                                    <div>
                                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {resultsCopy.dayResult}
                                      </div>
                                      <div className={`mt-1 text-2xl font-bold ${getValueColor(getActualDailyResultValue(dayDetails.summary))}`}>
                                        {formatNumber(getActualDailyResultValue(dayDetails.summary))} PLN
                                      </div>
                                      <div className={`mt-1 text-sm font-medium ${getValueColor(getActualDailyAfterFeesValue(dayDetails.summary))}`}>
                                        {resultsCopy.afterFees}: {formatNumber(getActualDailyAfterFeesValue(dayDetails.summary))} PLN
                                      </div>
                                    </div>
                                    <div className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700">
                                      {getActualResultLabel(dayDetails.summary.status)}
                                    </div>
                                  </div>

                                  {renderExecutionPathPanel({
                                    rows: dayDetails.hourly || [],
                                    summary: dayDetails.summary || {},
                                    availability: dayStageAvailability,
                                    copy: pipelineCopy,
                                    compact: true,
                                    expanded: false,
                                    quarterPathKeyPrefix: `daily-detail:${row.date}`,
                                    notice: dayExecutionPathNotice,
                                  })}

                                  <div className="sim-day-detail-masonry">
                                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                                      <div className="text-xs font-semibold uppercase text-gray-500">Goose IDA1 Stop</div>
                                      <div className={`text-lg font-semibold ${getValueColor(dayDetails.summary.daily_pnl)}`}>{isPartialFix2Settle(dayDetails.summary) ? '-' : `${formatNumber(dayDetails.summary.daily_pnl)} PLN`}</div>
                                      <div className={`text-xs mt-1 ${getValueColor(dayDetails.summary.daily_pnl_after_fees)}`}>{resultsCopy.afterFees}: {isPartialFix2Settle(dayDetails.summary) ? '-' : `${formatNumber(dayDetails.summary.daily_pnl_after_fees)} PLN`}</div>
                                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                                        <span>{resultsCopy.vol} {getDayPathVolume(dayDetails.summary, 'ida1') === null ? '-' : `${formatNumber(getDayPathVolume(dayDetails.summary, 'ida1'))} MWh`}</span>
                                        <span className={getValueColor(getDayPathProfitPerMwh(dayDetails.summary, 'ida1'))}>PLN/MWh {getDayPathProfitPerMwh(dayDetails.summary, 'ida1') === null ? '-' : formatNumber(getDayPathProfitPerMwh(dayDetails.summary, 'ida1'))}</span>
                                      </div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                                      <div className="text-xs font-semibold uppercase text-gray-500">Goose IDA2 Final</div>
                                      <div className={`text-lg font-semibold ${getValueColor(dayDetails.summary.daily_pnl_ida2_close)}`}>{isIda2CloseUnavailable(dayDetails.summary) ? '-' : `${formatNumber(dayDetails.summary.daily_pnl_ida2_close)} PLN`}</div>
                                      <div className={`text-xs mt-1 ${getValueColor(dayDetails.summary.daily_pnl_after_fees_ida2_close)}`}>{resultsCopy.afterFees}: {isIda2CloseUnavailable(dayDetails.summary) ? '-' : `${formatNumber(dayDetails.summary.daily_pnl_after_fees_ida2_close)} PLN`}</div>
                                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                                        <span>{resultsCopy.vol} {getDayPathVolume(dayDetails.summary, 'ida2') === null ? '-' : `${formatNumber(getDayPathVolume(dayDetails.summary, 'ida2'))} MWh`}</span>
                                        <span className={getValueColor(getDayPathProfitPerMwh(dayDetails.summary, 'ida2'))}>PLN/MWh {getDayPathProfitPerMwh(dayDetails.summary, 'ida2') === null ? '-' : formatNumber(getDayPathProfitPerMwh(dayDetails.summary, 'ida2'))}</span>
                                      </div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                                      <div className="text-xs font-semibold uppercase text-gray-500">Fix II Stop</div>
                                      <div className={`text-lg font-semibold ${getValueColor(dayDetails.summary.daily_pnl_fix2_close)}`}>{formatNumber(dayDetails.summary.daily_pnl_fix2_close)} PLN</div>
                                      <div className={`text-xs mt-1 ${getValueColor(dayDetails.summary.daily_pnl_after_fees_fix2_close)}`}>{resultsCopy.afterFees}: {formatNumber(dayDetails.summary.daily_pnl_after_fees_fix2_close)} PLN</div>
                                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                                        <span>{resultsCopy.vol} {getDayPathVolume(dayDetails.summary, 'fix2') === null ? '-' : `${formatNumber(getDayPathVolume(dayDetails.summary, 'fix2'))} MWh`}</span>
                                        <span className={getValueColor(getDayPathProfitPerMwh(dayDetails.summary, 'fix2'))}>PLN/MWh {getDayPathProfitPerMwh(dayDetails.summary, 'fix2') === null ? '-' : formatNumber(getDayPathProfitPerMwh(dayDetails.summary, 'fix2'))}</span>
                                      </div>
                                    </div>
                                    <div
                                      className="rounded-lg border border-gray-200 bg-white p-3"
                                      title="Standalone Goose v5 IDA1 replay reference for the same day."
                                    >
                                      <div className="text-xs font-semibold uppercase text-gray-500">Goose IDA1 Stop</div>
                                      <div className={`text-lg font-semibold ${getValueColor(bisonV5Ida1ReferencePnl)}`}>
                                        {bisonV5Ida1ReferenceLoading ? resultsCopy.loading : bisonV5Ida1ReferencePnl === null ? '-' : `${formatNumber(bisonV5Ida1ReferencePnl)} PLN`}
                                      </div>
                                      <div className={`mt-1 text-xs ${getValueColor(getGooseV5Ida1ReferenceAfterFees(parityRow))}`}>
                                        {resultsCopy.afterFees}: {bisonV5Ida1ReferenceLoading ? resultsCopy.loadingLower : getGooseV5Ida1ReferenceAfterFees(parityRow) === null ? '-' : `${formatNumber(getGooseV5Ida1ReferenceAfterFees(parityRow))} PLN`}
                                      </div>
                                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                                        <span>{resultsCopy.vol} {bisonV5Ida1ReferenceLoading ? resultsCopy.loadingLower : getGooseV5Ida1ReferenceVolume(parityRow) === null ? '-' : `${formatNumber(getGooseV5Ida1ReferenceVolume(parityRow))} MWh`}</span>
                                        <span>{resultsCopy.fees} {bisonV5Ida1ReferenceLoading ? resultsCopy.loadingLower : getGooseV5Ida1ReferenceFees(parityRow) === null ? '-' : `${formatNumber(getGooseV5Ida1ReferenceFees(parityRow))} PLN`}</span>
                                      </div>
                                    </div>
                                    <div
                                      className="rounded-lg border border-gray-200 bg-white p-3"
                                      title="Legacy Goose comparable benchmark: Base PnL + Fixing PnL, before IDA1."
                                    >
                                      <div className="text-xs font-semibold uppercase text-gray-500">Fix II Stop (Goose live stop)</div>
                                      <div className={`text-lg font-semibold ${getValueColor(legacyGooseComparablePnl)}`}>
                                        {legacyGooseComparablePnl === null ? '-' : `${formatNumber(legacyGooseComparablePnl)} PLN`}
                                      </div>
                                      <div className={`mt-1 text-xs ${getValueColor(dayDetails.summary.daily_pnl_after_fees_legacy_bison_fixing)}`}>
                                        {resultsCopy.afterFees}: {dayDetails.summary.daily_pnl_after_fees_legacy_bison_fixing === null || dayDetails.summary.daily_pnl_after_fees_legacy_bison_fixing === undefined ? '-' : `${formatNumber(dayDetails.summary.daily_pnl_after_fees_legacy_bison_fixing)} PLN`}
                                      </div>
                                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                                        <span>{resultsCopy.vol} {getLegacyGooseComparableVolume(dayDetails.summary) === null ? '-' : `${formatNumber(getLegacyGooseComparableVolume(dayDetails.summary))} MWh`}</span>
                                        <span>{resultsCopy.fees} {getLegacyGooseComparableFees(dayDetails.summary) === null ? '-' : `${formatNumber(getLegacyGooseComparableFees(dayDetails.summary))} PLN`}</span>
                                      </div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-white p-3" title="Goose IDA2 Final minus Goose IDA1 Stop for this day.">
                                      <div className="text-xs font-semibold uppercase text-gray-500">IDA2 vs IDA1 Stop</div>
                                      <div className={`mt-1 text-lg font-semibold ${getBenchmarkDeltaColor(dayDetails.summary.delta_pnl_ida2_close)}`}>{isIda2CloseUnavailable(dayDetails.summary) ? '-' : formatSignedNumber(dayDetails.summary.delta_pnl_ida2_close, ' PLN')}</div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-white p-3" title="Fix II Stop minus Goose IDA1 Stop for this day.">
                                      <div className="text-xs font-semibold uppercase text-gray-500">Fix II vs IDA1 Stop</div>
                                      <div className={`mt-1 text-lg font-semibold ${getBenchmarkDeltaColor(dayDetails.summary.delta_pnl_fix2_close)}`}>{formatSignedNumber(dayDetails.summary.delta_pnl_fix2_close, ' PLN')}</div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                                      <div className="text-xs font-semibold uppercase text-gray-500">{resultsCopy.avgQ50Forecast}</div>
                                      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                                        {[
                                          ['Fix II', q50Averages.fix2],
                                          ['IDA1', q50Averages.ida1],
                                          ['IDA2', q50Averages.ida2],
                                        ].map(([label, value]) => (
                                          <div key={label} className="rounded-lg bg-gray-50 px-2 py-2">
                                            <div className="text-gray-500">{label}</div>
                                            <div className="mt-0.5 font-semibold text-gray-900">
                                              {value === null ? '-' : `${formatNumber(value)} PLN/MWh`}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                                      <div className="text-xs font-semibold uppercase text-gray-500">{resultsCopy.avgActualPrices}</div>
                                      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                                        {[
                                          ['Fix II', actualAverages.fix2],
                                          ['IDA1', actualAverages.ida1],
                                          ['IDA2', actualAverages.ida2],
                                        ].map(([label, value]) => (
                                          <div key={label} className="rounded-lg bg-gray-50 px-2 py-2">
                                            <div className="text-gray-500">{label}</div>
                                            <div className="mt-0.5 font-semibold text-gray-900">
                                              {value === null ? '-' : `${formatNumber(value)} PLN/MWh`}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                    <div className="rounded-lg border border-gray-200 bg-white p-3">
                                      <div className="text-xs font-semibold uppercase text-gray-500">{resultsCopy.baseSetupCard}</div>
                                      <div
                                        title={getBaseSetupHint(dayDetails.summary.base_context, dayDetails.summary)}
                                        className={`mt-2 inline-flex max-w-full items-center whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold leading-none ${getBaseActionBadgeClass(dayDetails.summary.base_context, dayDetails.summary)}`}
                                      >
                                        {getBaseSetupLabel(dayDetails.summary.base_context, dayDetails.summary)}
                                      </div>
                                      <div className="mt-2">
                                        {renderSetupBaseActivityBadges(collapsedGooseRiskRow, { stacked: true })}
                                      </div>
                                      <div className="mt-2 grid gap-x-4 gap-y-1 text-xs text-gray-500 sm:grid-cols-2">
                                        <div>{resultsCopy.runtimePrice}: {dayDetails.summary.base_context?.runtime_base_price === null || dayDetails.summary.base_context?.runtime_base_price === undefined ? '-' : `${formatNumber(dayDetails.summary.base_context.runtime_base_price)} PLN/MWh`}</div>
                                        <div className="flex flex-wrap items-center gap-1">
                                          {shouldShowBaseSourceBadge(dayDetails.summary.base_context, dayDetails.summary) ? (
                                            <>
                                              <span>{resultsCopy.source}:</span>
                                              <span className={`inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-none ${getBaseSourceBadgeClass(dayDetails.summary.base_context, dayDetails.summary)}`}>
                                                {getBaseSourceLabel(dayDetails.summary.base_context, dayDetails.summary)}
                                              </span>
                                            </>
                                          ) : null}
                                        </div>
                                        <div>{resultsCopy.venue}: {formatNumber(getBaseDailyVenueVolume(dayDetails.summary.base_context, dayDetails.summary))} MWh</div>
                                        {shouldShowBaseSettledVolume(dayDetails.summary.base_context, dayDetails.summary) && (
                                          <div>{resultsCopy.settledClosed}: {formatNumber(getBaseSettledVolume(dayDetails.summary.base_context, dayDetails.summary))} MWh</div>
                                        )}
                                      </div>
                                      {dayDetails.summary.base_context?.stored_override_base_price !== null && dayDetails.summary.base_context?.stored_override_base_price !== undefined ? (
                                        <div className="mt-2 text-xs text-cyan-700">
                                          {resultsCopy.storedOverridePrice}: {formatNumber(dayDetails.summary.base_context.stored_override_base_price)} PLN/MWh
                                          {' '}<span className="text-cyan-600">({resultsCopy.informationalNotPnl})</span>
                                        </div>
                                      ) : null}
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.columns.basePnl}</div>
                                      <div className={`text-lg font-semibold ${getValueColor(dayDetails.summary.base_pnl)}`}>{formatNumber(dayDetails.summary.base_pnl)} PLN</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.fixLegPnl}</div>
                                      <div className={`text-lg font-semibold ${getValueColor(dayDetails.summary.probabilistic_pnl)}`}>{formatNumber(dayDetails.summary.probabilistic_pnl)} PLN</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.ida1LegPnl}</div>
                                      <div className={`text-lg font-semibold ${getValueColor(dayDetails.summary.ida1_pnl)}`}>{isPartialFix2Settle(dayDetails.summary) ? '-' : `${formatNumber(dayDetails.summary.ida1_pnl)} PLN`}</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.ida2LegPnl}</div>
                                      <div className={`text-lg font-semibold ${getValueColor(dayDetails.summary.ida2_pnl)}`}>{isIda2CloseUnavailable(dayDetails.summary) ? '-' : `${formatNumber(dayDetails.summary.ida2_pnl)} PLN`}</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.mooseIda2Overlay}</div>
                                      <div className={`mt-2 inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${getIda2PolicyBadgeClass(dayDetails.summary.ida2_policy_runtime_mode || dayDetails.summary.ida2_policy_mode)}`}>
                                        {formatIda2PolicyLabel(dayDetails.summary.ida2_policy_runtime_mode || dayDetails.summary.ida2_policy_mode)}
                                      </div>
                                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                                        <div>
                                          {resultsCopy.candidates}: {formatInteger(dayDetails.summary.ida2_policy_candidate_count)}
                                        </div>
                                        <div>
                                          {resultsCopy.selected}: {formatNumber(dayDetails.summary.ida2_policy_selected_carry_volume_mwh)} MWh
                                        </div>
                                        <div>
                                          {resultsCopy.mooseRows}: {formatInteger(dayDetails.summary.ida2_policy_moose_model_candidate_count)}
                                        </div>
                                        <div>
                                          {resultsCopy.mooseVolume}: {formatNumber(dayDetails.summary.ida2_policy_moose_model_selected_volume_mwh)} MWh
                                        </div>
                                      </div>
                                      <div className="mt-2 text-xs text-gray-500">
                                        {resultsCopy.mooseOverlayNote}
                                      </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.totalBuyValue}</div>
                                      <div className="text-lg font-semibold text-gray-800">{formatNumber(dayDetails.summary.total_buy_value)} PLN</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.totalSellValue}</div>
                                      <div className="text-lg font-semibold text-gray-800">{formatNumber(dayDetails.summary.total_sell_value)} PLN</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.volumeTransactionFees}</div>
                                      <div className="text-lg font-semibold text-gray-800">{formatNumber(dayDetails.summary.volume_fee)} / {formatNumber(dayDetails.summary.transaction_fee)} PLN</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.activeHours}</div>
                                      <div className="text-lg font-semibold text-gray-800">{dayDetails.summary.active_hours}</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.positiveNegativeHours}</div>
                                      <div className="text-lg font-semibold text-gray-800">{dayDetails.summary.positive_hours} / {dayDetails.summary.negative_hours}</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.totalVolume}</div>
                                      <div className="text-lg font-semibold text-gray-800">{formatInteger(dayDetails.summary.path_volume_mwh ?? dayDetails.summary.total_volume)} MWh</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.accMooseIda1Stop}</div>
                                      <div className={`text-lg font-semibold ${getValueColor(dayDetails.summary.acc_pnl)}`}>{formatNumber(dayDetails.summary.acc_pnl)} PLN</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.accMooseIda2Final}</div>
                                      <div className={`text-lg font-semibold ${getValueColor(dayDetails.summary.acc_pnl_ida2_close)}`}>{isIda2CloseUnavailable(dayDetails.summary) ? '-' : `${formatNumber(dayDetails.summary.acc_pnl_ida2_close)} PLN`}</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.accFixIIStopBenchmark}</div>
                                      <div className={`text-lg font-semibold ${getValueColor(dayDetails.summary.acc_pnl_fix2_close)}`}>{formatNumber(dayDetails.summary.acc_pnl_fix2_close)} PLN</div>
                                    </div>
                                    <div
                                      className="bg-white rounded-xl border border-gray-200 p-4"
                                      title="Cumulative Goose v5 IDA1 stop replay reference over the selected range."
                                    >
                                      <div className="text-gray-500 text-sm">{resultsCopy.accIda1StopGoose}</div>
                                      <div className={`text-lg font-semibold ${getValueColor(bisonV5Ida1ReferenceAccPnl)}`}>
                                        {bisonV5Ida1ReferenceLoading ? resultsCopy.loading : bisonV5Ida1ReferenceAccPnl === null ? '-' : `${formatNumber(bisonV5Ida1ReferenceAccPnl)} PLN`}
                                      </div>
                                    </div>
                                    <div
                                      className="bg-white rounded-xl border border-gray-200 p-4"
                                      title="Legacy Goose comparable cumulative result: sum of Base PnL + Fixing PnL before IDA1."
                                    >
                                      <div className="text-gray-500 text-sm">{resultsCopy.accFixIIStopGoose}</div>
                                      <div className={`text-lg font-semibold ${getValueColor(legacyGooseComparableAccPnl)}`}>
                                        {legacyGooseComparableAccPnl === null ? '-' : `${formatNumber(legacyGooseComparableAccPnl)} PLN`}
                                      </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.venueFixI}</div>
                                      <div className="text-lg font-semibold text-gray-800">{formatInteger(dayDetails.summary.venue_fix_i_mwh)} MWh</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.venueFixII}</div>
                                      <div className="text-lg font-semibold text-gray-800">{formatInteger(dayDetails.summary.venue_fix_ii_mwh)} MWh</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.venueIda1}</div>
                                      <div className="text-lg font-semibold text-gray-800">{formatInteger(dayDetails.summary.venue_ida1_mwh)} MWh</div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                                      <div className="text-gray-500 text-sm">{resultsCopy.venueIda2}</div>
                                      <div className="text-lg font-semibold text-gray-800">{isIda2CloseUnavailable(dayDetails.summary) ? '-' : `${formatInteger(dayDetails.summary.venue_ida2_mwh)} MWh`}</div>
                                    </div>
                                  </div>

                                  <div className="flex flex-wrap gap-2">
                                    <div className="text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                                      {resultsCopy.capacitySource}: {Array.isArray(dayDetails.metadata.capacity_source)
                                        ? dayDetails.metadata.capacity_source.join(', ')
                                        : (dayDetails.metadata.capacity_source || 'nordpool')}
                                    </div>
                                    <div className="text-xs bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
                                      {resultsCopy.issueDate}: {Array.isArray(dayDetails.metadata.issue_date)
                                        ? dayDetails.metadata.issue_date.join(', ')
                                        : formatWarsawDateTime(dayDetails.metadata.issue_date)}
                                    </div>
                                    <div className="text-xs bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
                                      {resultsCopy.loadBands}: {Object.entries(dayDetails.metadata.load_band_counts || {}).map(([label, count]) => `${label}=${count}`).join(', ') || '-'}
                                    </div>
                                    <div className="text-xs bg-gray-100 text-gray-800 px-3 py-1 rounded-full">
                                      {resultsCopy.blendAlphas}: {(dayDetails.metadata.blend_alphas || []).length ? dayDetails.metadata.blend_alphas.join(', ') : '-'}
                                    </div>
                                  </div>

                                  <HourlyPnLChart rows={dayDetails.hourly} formatNumber={formatNumber} />

                                  <div className="rounded-2xl border border-gray-200 bg-white">
                                    <div className="flex flex-col gap-3 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                                      <div>
                                        <div className="text-sm font-semibold text-gray-800">{resultsCopy.hourlyBreakdown}</div>
                                        <div className="mt-1 text-xs text-gray-500">
                                          {resultsCopy.hourlyBreakdownBody}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <div className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600">
                                          {dayDetails.hourly.length} {resultsCopy.rows}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (isHourlyBreakdownExpanded) {
                                              setExpandedDailyQuarterPaths({});
                                            }
                                            setIsHourlyBreakdownExpanded((current) => !current);
                                          }}
                                          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                                        >
                                          {isHourlyBreakdownExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                          {isHourlyBreakdownExpanded ? resultsCopy.collapseHourlyBreakdown : resultsCopy.expandHourlyBreakdown}
                                        </button>
                                      </div>
                                    </div>
                                    {isHourlyBreakdownExpanded ? (
                                      <div className="min-w-0 px-5 pb-5">
                                        <div className="min-w-0 w-full max-w-full overflow-hidden">
                                            <div
                                              ref={detailResultsHeaderRef}
                                              onScroll={() => syncDetailResultsScroll(detailResultsHeaderRef, [detailResultsScrollBodyRef])}
                                              className="sticky top-2 z-10 mb-2 w-full max-w-full overflow-x-auto rounded-lg border border-gray-200 bg-white/95 shadow-sm backdrop-blur"
                                            >
                                              <div className="h-3 min-w-[3900px]" />
                                            </div>

                                            <div
                                              ref={detailResultsScrollBodyRef}
                                              onScroll={() => syncDetailResultsScroll(detailResultsScrollBodyRef, [detailResultsHeaderRef])}
                                              className="w-full max-w-full overflow-x-auto rounded-xl border border-gray-200"
                                            >
                                              <table className="min-w-[3900px] w-max overflow-hidden bg-white text-sm">
                                              <thead className="bg-gray-100">
                                                <tr>
                                                  {hourlyBreakdownHeaders.map((label, headerIndex) => (
                                                    <th
                                                      key={label}
                                                      className={`sticky top-0 z-[2] border-b border-gray-200 bg-gray-100 px-3 py-2 text-left text-xs font-semibold uppercase text-gray-500 ${
                                                        headerIndex === 0 ? 'left-0 z-[3] shadow-[6px_0_12px_rgba(15,23,42,0.08)]' : ''
                                                      }`}
                                                    >
                                                      {label}
                                                    </th>
                                                  ))}
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {[...(dayDetails.hourly || [])].sort(compareHourSlots).map((hourRow) => {
                                                  const hourLabel = formatHourLabel(hourRow.hour);
                                                  const quarterPaths = Array.isArray(hourRow.quarter_paths) ? hourRow.quarter_paths : [];
                                                  const quarterKey = `${row.date}-${getHourSlotKey(hourRow)}`;
                                                  const isQuarterPathExpanded = Boolean(expandedDailyQuarterPaths[quarterKey]);
                                                  return (
                                                  <React.Fragment key={quarterKey}>
                                                    <tr className={`border-t align-top ${getHourlyRowClass(hourRow, dayStageAvailability)}`}>
                                                      <td className="sticky left-0 z-[1] bg-inherit px-3 py-2 font-semibold shadow-[6px_0_12px_rgba(15,23,42,0.08)]">
                                                        <div>{hourLabel}</div>
                                                        {quarterPaths.length ? (
                                                          <button
                                                            type="button"
                                                            onClick={() => toggleDailyQuarterPathExpanded(quarterKey)}
                                                            className="mt-1 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
                                                          >
                                                            {isQuarterPathExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                                            {isQuarterPathExpanded ? resultsCopy.collapse15mPath : resultsCopy.expand15mPath}
                                                          </button>
                                                        ) : null}
                                                      </td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.fix_i_price)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.fix_ii_price)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.ida1_price)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.ida2_price)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.stacked_q10)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.stacked_q30)}</td>
                                                      <td className="px-3 py-2 font-semibold">{formatNumber(hourRow.stacked_q50)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.stacked_q70)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.stacked_q90)}</td>
                                                      <td className="px-3 py-2">
                                                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${getAuctionActionBadgeClass(hourRow.executed_action)}`}>
                                                          {hourRow.executed_action ? formatBaseHedgeActionLabel(hourRow.executed_action) : '-'}
                                                        </span>
                                                      </td>
                                                      <td className="px-3 py-2">
                                                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${getAuctionActionBadgeClass(hourRow.fix2_close_action)}`}>
                                                          {hourRow.fix2_close_action ? formatBaseHedgeActionLabel(hourRow.fix2_close_action) : '-'}
                                                        </span>
                                                      </td>
                                                      <td className="px-3 py-2">
                                                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${getAuctionActionBadgeClass(hourRow.ida1_close_action)}`}>
                                                          {hourRow.ida1_close_action ? formatBaseHedgeActionLabel(hourRow.ida1_close_action) : '-'}
                                                        </span>
                                                      </td>
                                                      <td className="px-3 py-2">
                                                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase ${getAuctionActionBadgeClass(hourRow.ida2_close_action)}`}>
                                                          {hourRow.ida2_close_action ? formatBaseHedgeActionLabel(hourRow.ida2_close_action) : '-'}
                                                        </span>
                                                      </td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.executed_volume_fix1)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.closed_volume_fix2)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.closed_volume_ida1)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.closed_volume_ida2)}</td>
                                                      <td className="px-3 py-2 font-semibold">{formatNumber(hourRow.final_volume_contribution)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.bought_value)}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.sold_value)}</td>
                                                      <td className={`px-3 py-2 ${getValueColor(hourRow.fix_ii_pnl)}`}>{formatNumber(hourRow.fix_ii_pnl)}</td>
                                                      <td className={`px-3 py-2 ${getValueColor(hourRow.ida1_arbitrage_pnl)}`}>{formatNumber(hourRow.ida1_arbitrage_pnl)}</td>
                                                      <td className={`px-3 py-2 ${getValueColor(hourRow.ida2_arbitrage_pnl)}`}>{formatNumber(hourRow.ida2_arbitrage_pnl)}</td>
                                                      <td className={`px-3 py-2 font-semibold ${getValueColor(hourRow.arbitrage_hourly_pnl)}`}>{formatNumber(hourRow.arbitrage_hourly_pnl)}</td>
                                                      <td className={`px-3 py-2 font-semibold ${getValueColor(hourRow.arbitrage_hourly_pnl_ida2_close)}`}>{formatNumber(hourRow.arbitrage_hourly_pnl_ida2_close)}</td>
                                                      <td className={`px-3 py-2 ${getBenchmarkDeltaColor(hourRow.hourly_pnl_delta_ida2_close)}`}>{formatNumber(hourRow.hourly_pnl_delta_ida2_close)}</td>
                                                      <td className={`px-3 py-2 font-semibold ${getValueColor(hourRow.hourly_pnl_fix2_close)}`}>{formatNumber(hourRow.hourly_pnl_fix2_close)}</td>
                                                      <td className={`px-3 py-2 ${getBenchmarkDeltaColor(hourRow.hourly_pnl_delta_fix2_close)}`}>{formatNumber(hourRow.hourly_pnl_delta_fix2_close)}</td>
                                                      <td className="px-3 py-2">{hourRow.load_band || '-'}</td>
                                                      <td className="px-3 py-2">{formatNumber(hourRow.blend_alpha)}</td>
                                                      <td className="px-3 py-2">{hourRow.capacity_source || 'nordpool'}</td>
                                                    </tr>
                                                    {isQuarterPathExpanded ? (
                                                    <tr className={`${getHourlyRowClass(hourRow, dayStageAvailability)} border-t border-gray-200/70`}>
                                                      <td colSpan={hourlyBreakdownHeaders.length} className="px-3 py-3">
                                                        <div className="rounded-xl border border-gray-200 bg-white/70 px-3 py-3 shadow-sm">
                                                          <div className="mb-2 flex items-center justify-between gap-3">
                                                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                              {resultsCopy.path15mFor(hourLabel)}
                                                            </div>
                                                            <div className={`text-xs font-semibold ${getValueColor(hourRow.arbitrage_hourly_pnl_ida2_close)}`}>
                                                              Arb Goose IDA2 Final {formatNumber(hourRow.arbitrage_hourly_pnl_ida2_close)} PLN
                                                            </div>
                                                          </div>
                                                          {quarterPaths.length ? (
                                                          <div className="grid min-w-[720px] grid-cols-1 gap-2 xl:grid-cols-2 2xl:grid-cols-4">
                                                            {quarterPaths.map((quarterPath) => (
                                                              <div key={`${row.date}-${getHourSlotKey(hourRow)}-${quarterPath.quarter_label}`} className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 text-[11px] leading-4 text-gray-700">
                                                                <div className="flex items-center justify-between gap-3">
                                                                  <span className="font-semibold text-gray-800">{quarterPath.quarter_label || '-'}</span>
                                                                  <span className={getValueColor(quarterPath.quarterly_pnl)}>
                                                                    PnL {formatNumber(quarterPath.quarterly_pnl)}
                                                                  </span>
                                                                </div>
                                                                <div className="mt-2 grid grid-cols-1 gap-1">
                                                                  {[
                                                                    ['Fix II', quarterPath.fix2_leg],
                                                                    ['IDA1', quarterPath.ida1_leg],
                                                                    ['IDA2', quarterPath.ida2_leg],
                                                                  ].map(([label, leg]) => (
                                                                    <div key={`${quarterPath.quarter_label}-${label}`} className={`rounded-md border border-l-4 bg-white px-2 py-1.5 ${getAuctionStageCardClass(leg?.action, leg?.pnl, label)}`}>
                                                                      <div className="flex items-center justify-between gap-2">
                                                                        <span className={`font-medium ${getAuctionStageLabelClass(label)}`}>{label}</span>
                                                                        <span className={`inline-flex rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${getAuctionActionBadgeClass(leg?.action)}`}>
                                                                          {leg?.action ? formatBaseHedgeActionLabel(leg.action) : '-'}
                                                                        </span>
                                                                      </div>
                                                                      <div className="mt-1 text-gray-700">
                                                                        {formatNumber(leg?.volume_mwh)} MWh @ {formatNumber(leg?.price)}
                                                                      </div>
                                                                      <div className={getValueColor(leg?.pnl)}>
                                                                        {formatNumber(leg?.pnl)} PLN
                                                                      </div>
                                                                    </div>
                                                                  ))}
                                                                </div>
                                                                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-gray-500">
                                                                  <span>Curve {formatNumber(quarterPath.curve_net_volume)}</span>
                                                                  <span>Path {quarterPath.base_carry_path || '-'}</span>
                                                                </div>
                                                              </div>
                                                            ))}
                                                          </div>
                                                          ) : (
                                                            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-xs text-gray-500">
                                                              {resultsCopy.path15mEmpty}
                                                            </div>
                                                          )}
                                                        </div>
                                                      </td>
                                                    </tr>
                                                    ) : null}
                                                  </React.Fragment>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
        </div>
        )}

        {isDayRadarSection && renderDayRadarSection()}

        {/* ── Pipeline Status + Run Buttons + Execution View ── */}
        {isPipelineFamilySection && (
        <div className={`${simulationBandClass} p-6`}>
          {/* Simulation Date + Run buttons */}
          <div className="mb-5 space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <Calendar size={16} className="text-blue-600" />
              <span className="text-sm font-semibold text-blue-700 uppercase tracking-wide">{pipelineCopy.simulationDate}</span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => shiftSimulationDate(-1)}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border transition ${
                    darkMode
                      ? 'border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="-1 dzień"
                  aria-label="-1 dzień"
                >
                  <ChevronLeft size={16} />
                </button>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={`h-10 rounded-lg border px-3 text-sm font-semibold tabular-nums focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    darkMode
                      ? 'border-slate-700 bg-slate-950 text-slate-100'
                      : 'border-gray-300 bg-white text-gray-800'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => shiftSimulationDate(1)}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-lg border transition ${
                    darkMode
                      ? 'border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  title="+1 dzień"
                  aria-label="+1 dzień"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
              <button
                onClick={() => loadPipelineData({ forceOrderPlan: true })}
                disabled={loading}
                title={pipelineCopy.refreshTitle}
                className="sim-run-button sim-run-button-refresh px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? pipelineCopy.loading : pipelineCopy.refreshStatus}
              </button>
            </div>
            {isPipelineSection && (
            <div className={`rounded-2xl border p-4 ${
              darkMode
                ? 'border-slate-700/70 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-900'
                : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50/70 shadow-sm'
            }`}>
              <div className="grid gap-4 xl:grid-cols-[1.2fr_0.95fr]">
                <div className={`rounded-2xl border p-4 ${
                  darkMode ? 'border-slate-700/70 bg-slate-950/45' : 'border-slate-200 bg-white/90'
                }`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className={`text-xs font-semibold uppercase tracking-[0.24em] ${
                        darkMode ? 'text-cyan-300/80' : 'text-cyan-700'
                      }`}>
                        {howItWorksLanguage === 'pl' ? 'Monitoring pipeline' : 'Pipeline monitoring'}
                      </div>
                      <div className={`mt-2 max-w-2xl text-sm leading-relaxed ${
                        darkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>
                        {howItWorksLanguage === 'pl'
                          ? 'Ten widok monitoruje trening i predykcję dla Goose v3, Goose v5, IDA1 i IDA2, a ręczne akcje pozwalają uruchomić wybrane kroki dla każdego z modeli.'
                          : 'This view monitors training and prediction for Goose v3, Goose v5, IDA1, and IDA2, and the manual actions let you trigger selected steps for each model.'}
                      </div>
                    </div>
                    <div className={`rounded-2xl border px-4 py-3 text-right ${
                      darkMode ? 'border-slate-700 bg-slate-900/80 text-slate-100' : 'border-slate-200 bg-white text-slate-800'
                    }`}>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-500">
                        {howItWorksLanguage === 'pl' ? 'Delivery date' : 'Delivery date'}
                      </div>
                      <div className="mt-1 text-xl font-semibold tabular-nums">{date}</div>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div className={`rounded-2xl border p-4 ${
                      darkMode ? 'border-emerald-500/20 bg-emerald-500/10' : 'border-emerald-200 bg-emerald-50/80'
                    }`}>
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-emerald-500" />
                        <div className="text-sm font-semibold text-slate-100">{howItWorksLanguage === 'pl' ? 'Train' : 'Train'}</div>
                      </div>
                      <div className={`mt-2 text-sm ${
                        darkMode ? 'text-emerald-100/90' : 'text-emerald-900'
                      }`}>
                        {howItWorksLanguage === 'pl'
                          ? 'Monitorujemy gotowość treningu, artefakty modelu i ostatni widoczny snapshot dla Goose v3, Goose v5, IDA1 (ida1-v1.0.3) i IDA2 (ida2-v1.0.0).'
                          : 'We monitor training readiness, model artefacts, and the latest visible snapshot for Goose v3, Goose v5, IDA1 (ida1-v1.0.3), and IDA2 (ida2-v1.0.0).'}
                      </div>
                    </div>
                    <div className={`rounded-2xl border p-4 ${
                      darkMode ? 'border-violet-500/20 bg-violet-500/10' : 'border-violet-200 bg-violet-50/80'
                    }`}>
                      <div className="flex items-center gap-2">
                        <Activity className="h-4 w-4 text-violet-500" />
                        <div className="text-sm font-semibold text-slate-100">{howItWorksLanguage === 'pl' ? 'Predict' : 'Predict'}</div>
                      </div>
                      <div className={`mt-2 text-sm ${
                        darkMode ? 'text-violet-100/90' : 'text-violet-900'
                      }`}>
                        {howItWorksLanguage === 'pl'
                          ? 'Monitorujemy gotowe outputy predykcji dla wszystkich czterech modeli (Fix II, IDA1, IDA2) oraz ich bieżącą gotowość na wybraną datę delivery.'
                          : 'We monitor ready prediction outputs for all four models (Fix II, IDA1, IDA2) and their current readiness for the selected delivery date.'}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`rounded-2xl border p-4 ${
                  darkMode ? 'border-amber-400/20 bg-amber-500/10' : 'border-amber-200 bg-amber-50/85'
                }`}>
                  <div className="flex items-center gap-2">
                    <Play className="h-4 w-4 text-amber-500" />
                    <div className={`text-xs font-semibold uppercase tracking-[0.24em] ${
                      darkMode ? 'text-amber-200' : 'text-amber-800'
                    }`}>
                      {howItWorksLanguage === 'pl' ? 'Ręczne akcje modeli' : 'Manual model actions'}
                    </div>
                  </div>
                  <div className={`mt-2 text-sm leading-relaxed ${
                    darkMode ? 'text-amber-50/90' : 'text-amber-950'
                  }`}>
                    {howItWorksLanguage === 'pl'
                      ? 'Z tego panelu możesz ręcznie uruchomić wybrane kroki train i predict dla Goose v3, Goose v5, IDA1 (ida1-v1.0.3) i IDA2 (ida2-v1.0.0).'
                      : 'Use this panel to manually trigger selected train and predict steps for Goose v3, Goose v5, IDA1 (ida1-v1.0.3), and IDA2 (ida2-v1.0.0).'}
                  </div>
                  <div className="mt-4 space-y-3">
                    <div className={`rounded-2xl border p-3 ${
                      darkMode ? 'border-slate-700/70 bg-slate-950/45' : 'border-white/70 bg-white'
                    }`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className={`text-sm font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            {howItWorksLanguage === 'pl' ? 'Train modelu' : 'Model train'}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">{selectedPipelineTrainTarget.helper}</div>
                        </div>
                        <div className="flex gap-2">
                          <select
                            value={pipelineTrainTarget}
                            onChange={(event) => setPipelineTrainTarget(event.target.value)}
                            className={`min-w-0 rounded-xl border px-3 py-2 text-sm font-semibold focus:ring-2 focus:ring-blue-500 ${
                              darkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-gray-300 bg-white text-gray-800'
                            }`}
                          >
                            {pipelineTrainTargets.map((target) => (
                              <option key={target.key} value={target.key}>{target.label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleRun('/v5-sim/run-train', selectedPipelineTrainTarget.payload, { trainResultMode: selectedPipelineTrainTarget.displayMode })}
                            disabled={loading}
                            title={selectedPipelineTrainTarget.helper}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            <Play size={14} /> {pipelineCopy.run}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className={`rounded-2xl border p-3 ${
                      darkMode ? 'border-slate-700/70 bg-slate-950/45' : 'border-white/70 bg-white'
                    }`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className={`text-sm font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            {howItWorksLanguage === 'pl' ? 'Predict modelu' : 'Model predict'}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">{selectedPipelinePredictTarget.helper}</div>
                        </div>
                        <div className="flex gap-2">
                          <select
                            value={pipelinePredictTarget}
                            onChange={(event) => setPipelinePredictTarget(event.target.value)}
                            className={`min-w-0 rounded-xl border px-3 py-2 text-sm font-semibold focus:ring-2 focus:ring-violet-500 ${
                              darkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-gray-300 bg-white text-gray-800'
                            }`}
                          >
                            {pipelinePredictTargets.map((target) => (
                              <option key={target.key} value={target.key}>{target.label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleRun('/v5-sim/run-predict', selectedPipelinePredictTarget.payload, { predictResultMode: selectedPipelinePredictTarget.displayMode })}
                            disabled={loading}
                            title={selectedPipelinePredictTarget.helper}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            <Play size={14} /> {pipelineCopy.run}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>

          {isPipelineSection && (
          <>
          <div className="flex items-center gap-2 mb-3">
            <Bot className={`h-5 w-5 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`} />
            <h3 className={`text-lg font-bold ${darkMode ? 'text-slate-50' : 'text-gray-800'}`}>{pipelineCopy.pipelineStatus}</h3>
          </div>
          {loading || orderPlanLoading ? (
            renderSectionLoading(pipelineCopy.loadingPipeline, 'blue')
          ) : !statusData ? (
            <div className="text-gray-500 mb-5">{pipelineCopy.noRun}</div>
          ) : (
            <div className="space-y-5 mb-5 text-sm">
              <div className={`rounded-2xl border p-4 ${
                darkMode ? 'border-slate-700/70 bg-slate-950/35' : 'border-slate-200 bg-white'
              }`}>
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-[0.24em] ${
                      darkMode ? 'text-emerald-300/80' : 'text-emerald-700'
                    }`}>
                      {howItWorksLanguage === 'pl' ? 'Train monitor' : 'Train monitor'}
                    </div>
                    <div className="mt-1 text-sm text-slate-500">
                      {howItWorksLanguage === 'pl'
                        ? 'Monitoring gotowości treningu i artefaktów modelu dla Goose v3, Goose v5, IDA1 i IDA2 na tę samą datę delivery.'
                        : 'Monitoring training readiness and model artefacts for Goose v3, Goose v5, IDA1, and IDA2 on the same delivery date.'}
                    </div>
                  </div>
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  {pipelineTrainCards.map((card) => (
                    <div
                      key={`train-${card.variantKey}`}
                      className={`rounded-2xl border p-4 ${
                        card.colorKey === 'goose-v5'
                          ? (darkMode ? 'border-cyan-500/20 bg-cyan-500/10' : 'border-cyan-200 bg-cyan-50/80')
                          : card.colorKey === 'ida1'
                            ? (darkMode ? 'border-amber-500/20 bg-amber-500/10' : 'border-amber-200 bg-amber-50/80')
                            : card.colorKey === 'ida2'
                              ? (darkMode ? 'border-orange-500/20 bg-orange-500/10' : 'border-orange-200 bg-orange-50/80')
                              : (darkMode ? 'border-emerald-500/20 bg-emerald-500/10' : 'border-emerald-200 bg-emerald-50/80')
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className={`text-lg font-semibold ${darkMode ? 'text-slate-50' : 'text-slate-900'}`}>{card.label}</div>
                          <div className={`mt-1 text-xs uppercase tracking-[0.18em] ${
                            darkMode ? 'text-slate-300/70' : 'text-slate-500'
                          }`}>
                            {card.sourceLabel}
                          </div>
                        </div>
                        {renderStatusBadge(card.statusBadge)}
                      </div>
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <div className={`rounded-xl border px-3 py-2 ${
                          darkMode ? 'border-slate-700/70 bg-slate-950/45' : 'border-white/70 bg-white'
                        }`}>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                            {howItWorksLanguage === 'pl' ? 'Model' : 'Model'}
                          </div>
                          <div className={`mt-1 text-base font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>{card.modelVersion}</div>
                        </div>
                        <div className={`rounded-xl border px-3 py-2 ${
                          darkMode ? 'border-slate-700/70 bg-slate-950/45' : 'border-white/70 bg-white'
                        }`}>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                            {howItWorksLanguage === 'pl' ? 'Ostatni snapshot' : 'Latest snapshot'}
                          </div>
                          <div className={`mt-1 text-base font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            {formatWarsawDateTime(card.latestAt)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-slate-500">
                        <span className="font-semibold">{howItWorksLanguage === 'pl' ? 'Artefakt:' : 'Artefact:'}</span>{' '}
                        {card.artifactPath || '-'}
                      </div>
                      {card.warning ? (
                        <div className="mt-3 text-xs text-amber-700">
                          <span className="font-semibold">{pipelineCopy.labels.warning}:</span> {card.warning}
                        </div>
                      ) : null}
                      {card.error ? (
                        <div className="mt-2 text-xs text-rose-700">
                          <span className="font-semibold">{pipelineCopy.labels.error}:</span> {card.error}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
              <div className={`rounded-2xl border p-4 ${
                darkMode ? 'border-slate-700/70 bg-slate-950/35' : 'border-slate-200 bg-white'
              }`}>
                <div className="mb-3 flex items-start justify-between gap-3">
                  <div>
                    <div className={`text-xs font-semibold uppercase tracking-[0.24em] ${
                      darkMode ? 'text-violet-300/80' : 'text-violet-700'
                    }`}>
                      {howItWorksLanguage === 'pl' ? 'Predict monitor' : 'Predict monitor'}
                    </div>
                    <div className="mt-1 text-sm text-slate-500">
                      {howItWorksLanguage === 'pl'
                        ? 'Monitoring gotowych outputów predykcji dla Goose v3, Goose v5, IDA1 i IDA2 oraz ich bieżącej gotowości na wybraną datę.'
                        : 'Monitoring ready prediction outputs for Goose v3, Goose v5, IDA1, and IDA2 and their current readiness for the selected date.'}
                    </div>
                  </div>
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  {pipelinePredictCards.map((card) => (
                    <div
                      key={`predict-${card.variantKey}`}
                      className={`rounded-2xl border p-4 ${
                        card.colorKey === 'goose-v5'
                          ? (darkMode ? 'border-violet-500/20 bg-violet-500/10' : 'border-violet-200 bg-violet-50/80')
                          : card.colorKey === 'ida1'
                            ? (darkMode ? 'border-amber-500/20 bg-amber-500/10' : 'border-amber-200 bg-amber-50/80')
                            : card.colorKey === 'ida2'
                              ? (darkMode ? 'border-orange-500/20 bg-orange-500/10' : 'border-orange-200 bg-orange-50/80')
                              : (darkMode ? 'border-fuchsia-500/20 bg-fuchsia-500/10' : 'border-fuchsia-200 bg-fuchsia-50/80')
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className={`text-lg font-semibold ${darkMode ? 'text-slate-50' : 'text-slate-900'}`}>{card.label}</div>
                          <div className={`mt-1 text-xs uppercase tracking-[0.18em] ${
                            darkMode ? 'text-slate-300/70' : 'text-slate-500'
                          }`}>
                            {card.sourceLabel}
                          </div>
                        </div>
                        {renderStatusBadge(card.statusBadge)}
                      </div>
                      <div className="mt-4 grid gap-3 sm:grid-cols-3">
                        <div className={`rounded-xl border px-3 py-2 ${
                          darkMode ? 'border-slate-700/70 bg-slate-950/45' : 'border-white/70 bg-white'
                        }`}>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                            {howItWorksLanguage === 'pl' ? 'Model' : 'Model'}
                          </div>
                          <div className={`mt-1 text-base font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>{card.modelVersion}</div>
                        </div>
                        <div className={`rounded-xl border px-3 py-2 ${
                          darkMode ? 'border-slate-700/70 bg-slate-950/45' : 'border-white/70 bg-white'
                        }`}>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                            {pipelineCopy.labels.planRows}
                          </div>
                          <div className={`mt-1 text-base font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>{formatInteger(card.rowsCount)}</div>
                        </div>
                        <div className={`rounded-xl border px-3 py-2 ${
                          darkMode ? 'border-slate-700/70 bg-slate-950/45' : 'border-white/70 bg-white'
                        }`}>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                            {pipelineCopy.labels.latestIssue}
                          </div>
                          <div className={`mt-1 text-base font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            {formatWarsawDateTime(card.latestAt)}
                          </div>
                        </div>
                      </div>
                      {card.warning ? (
                        <div className="mt-3 text-xs text-amber-700">
                          <span className="font-semibold">{pipelineCopy.labels.warning}:</span> {card.warning}
                        </div>
                      ) : null}
                      {card.error ? (
                        <div className="mt-2 text-xs text-rose-700">
                          <span className="font-semibold">{pipelineCopy.labels.error}:</span> {card.error}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          </>
          )}

          {/* Execution View */}
          {(isPipelinePredictionsSection || isPipelineDataSection || isPipelineAuditSection) && (
          <div>
          {isPipelinePredictionsSection && hasPendingFixReadiness && (
            <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              {pipelineCopy.pendingReadiness}
            </div>
          )}
          <div className={`rounded-xl border p-4 ${darkMode ? 'border-slate-700 bg-slate-900/35' : 'border-gray-200 bg-white'}`}>
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-base font-semibold text-gray-800">
                  {isPipelinePredictionsSection
                    ? pipelineCopy.predictionsMode
                    : isPipelineAuditSection
                      ? pipelineCopy.auditMode
                      : pipelineCopy.dataMode}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {isPipelinePredictionsSection
                    ? pipelineCopy.predictionsBody
                    : isPipelineAuditSection
                      ? pipelineCopy.auditBody
                    : pipelineCopy.dataBody}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                {(isPipelinePredictionsSection || isPipelineDataSection) ? (
                  <div className={`inline-flex items-center whitespace-nowrap rounded-xl border px-4 py-2 text-sm font-extrabold uppercase tracking-[0.16em] ${
                    darkMode
                      ? 'border-cyan-300/40 bg-cyan-400/12 text-cyan-100'
                      : 'border-cyan-200 bg-cyan-50 text-cyan-800'
                  }`}>
                    {activePredictVariantLabel}
                  </div>
                ) : null}
                {nonNordpoolPlan && (
                  <div className="text-xs text-amber-700 bg-amber-100 border border-amber-200 px-3 py-1 rounded-full">
                    Capacity fallback used in current plan
                  </div>
                )}
              </div>
            </div>
            {orderPlanLoading && activePredictRows.length === 0 && !isPipelineDataSection && (
              <div className="mb-3">
                {renderInlineLoading(
                  isPipelinePredictionsSection
                    ? pipelineCopy.loadingPredictionRows
                    : pipelineCopy.loadingExecutionRows,
                  'blue',
                )}
              </div>
            )}
            <div className="mb-3 flex flex-wrap gap-2">
              <div
                className={`sim-prediction-chip rounded-full border px-3 py-1 text-xs ${darkMode ? 'bg-slate-700/80 text-slate-100 border-slate-500' : 'bg-gray-100 text-gray-800 border-gray-200'}`}
                title={
                  activeFix2IssueDates.length
                    ? `${activePredictVariantLabel} · Fix II forecast issued at: ${activeFix2IssueDates.map(formatWarsawDateTime).join(', ')}.`
                    : `${activePredictVariantLabel} · Fix II forecast is not available yet for this date.`
                }
              >
                <span className="font-semibold">{activePredictVariantLabel} · Fix II forecast:</span>
                {' '}
                <span className="font-semibold">
                  {activeFix2IssueDates.length
                    ? activeFix2IssueDates.map(formatWarsawDateTime).join(', ')
                    : 'not ready'}
                </span>
                {renderStatusBadge(activePredictFix2PlanBadge, { className: 'ml-2' })}
              </div>
              <div
                className={`sim-prediction-chip rounded-full border px-3 py-1 text-xs ${getStagePredictChipClass('ida1')}`}
                title={activePredictIda1Badge.tooltip || activePredictIda1Badge.label}
              >
                <span className="font-semibold">{pipelineCopy.labels.ida1Prediction}:</span>
                {' '}
                <span className="font-semibold">
                  {predictModeMap?.ida1?.issueDate
                    ? formatWarsawDateTime(predictModeMap.ida1.issueDate)
                  : getStagePredictStatusLabel('ida1')}
                </span>
                {renderStatusBadge(activePredictIda1Badge, { className: 'ml-2' })}
              </div>
              <div
                className={`sim-prediction-chip rounded-full border px-3 py-1 text-xs ${getStagePredictChipClass('ida2')}`}
                title={activePredictIda2Badge.tooltip || activePredictIda2Badge.label}
              >
                <span className="font-semibold">{pipelineCopy.labels.ida2Prediction}:</span>
                {' '}
                <span className="font-semibold">
                  {predictModeMap?.ida2?.issueDate
                    ? formatWarsawDateTime(predictModeMap.ida2.issueDate)
                    : getStagePredictStatusLabel('ida2')}
                </span>
                {renderStatusBadge(activePredictIda2Badge, { className: 'ml-2' })}
              </div>
            </div>
            {orderPlanLoading && activePredictRows.length === 0 && !isPipelineDataSection ? (
              <div className="text-sm text-gray-500">
                {renderInlineLoading(
                  isPipelinePredictionsSection
                    ? pipelineCopy.loadingPredictionRows
                    : pipelineCopy.loadingExecutionRows,
                  'blue',
                )}
              </div>
            ) : activePredictRows.length === 0 && !isPipelineDataSection && !isPipelineAuditSection ? (
              <div className="text-sm text-gray-500">
                {isPipelinePredictionsSection
                  ? pipelineCopy.noPredictions
                  : pipelineCopy.noExecutionData}
              </div>
            ) : isPipelinePredictionsSection ? (
              <>
              <div className={`mb-3 flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2 text-xs ${
                darkMode
                  ? 'border-slate-700 bg-slate-900/40 text-slate-300'
                  : 'border-gray-200 bg-gray-50 text-gray-600'
              }`}>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-700">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-400" />
                  <span>{pipelineCopy.favorableSignal}</span>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-rose-700">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-rose-400" />
                  <span>{pipelineCopy.unfavorableSignal}</span>
                </div>
              </div>
              <div
                ref={orderPlanHeaderRef}
                onScroll={() => syncOrderPlanScroll(orderPlanHeaderRef, [orderPlanScrollBodyRef])}
                className="sticky top-0 z-10 overflow-x-auto border border-gray-200 border-b-0 rounded-t-xl bg-white/95 backdrop-blur"
              >
                <table className={`w-max ${orderPlanTableMinWidthClass} table-fixed text-sm`}>
                  <thead className="bg-gray-50">
                    <tr>
                      {orderPlanColumns.map((column) => (
                        <th key={column.label} className={`${column.widthClass} px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap`}>{column.label}</th>
                      ))}
                    </tr>
                  </thead>
                </table>
              </div>
              <div
                ref={orderPlanScrollBodyRef}
                onScroll={() => syncOrderPlanScroll(orderPlanScrollBodyRef, [orderPlanHeaderRef])}
                className="overflow-x-auto border border-gray-200 rounded-b-xl"
              >
                <table className={`w-max ${orderPlanTableMinWidthClass} table-fixed text-sm`}>
                  <tbody>
                    {activePredictRows.map((row) => {
                      const arbitrageSignal = getArbitrageSignal(row);
                      const displayedFixIAction = arbitrageSignal.executedAction || '-';
                      const displayedFixIVolume = arbitrageSignal.executedAction
                        ? arbitrageSignal.executedVolume
                        : Number(row?.executed_volume_fix1 || 0);
                      return (
                      <tr
                        key={`${date}-${row.target_hour_waw}`}
                        className={`border-t ${arbitrageSignal.rowClass}`}
                        title={
                          arbitrageSignal.nextLegLabel
                            ? `Arbitrage ${arbitrageSignal.state}: ${displayedFixIAction} at Fix I ${formatNumber(row.fix_i_price)} vs ${arbitrageSignal.nextLegLabel} ${formatNumber(arbitrageSignal.nextLegPrice)}`
                            : `Arbitrage ${arbitrageSignal.state}`
                        }
                      >
                        <td className={`${orderPlanColumns[0].widthClass} px-4 py-3`}>{row.target_hour_waw}</td>
                        <td className={`${orderPlanColumns[1].widthClass} px-4 py-3`}>{formatNumber(row.fix_i_price)}</td>
                        <td className={`${orderPlanColumns[2].widthClass} px-4 py-3`}>{formatNumber(row.fix_ii_price)}</td>
	                        <td className={`${orderPlanColumns[3].widthClass} px-4 py-3`}>
	                          {row.ida1_prediction_available ? formatNumber(row.ida1_price) : getPredictTablePlaceholder('ida1')}
	                        </td>
	                        <td className={`${orderPlanColumns[4].widthClass} px-4 py-3`}>
	                          {row.ida2_prediction_available ? formatNumber(row.ida2_price) : getPredictTablePlaceholder('ida2')}
	                        </td>
                        <td className={`${orderPlanColumns[5].widthClass} px-4 py-3`}>{formatNumber(row.stacked_q10)}</td>
                        <td className={`${orderPlanColumns[6].widthClass} px-4 py-3`}>{formatNumber(row.stacked_q30)}</td>
                        <td className={`${orderPlanColumns[7].widthClass} px-4 py-3 font-semibold`}>{formatNumber(row.stacked_q50)}</td>
                        <td className={`${orderPlanColumns[8].widthClass} px-4 py-3`}>{formatNumber(row.stacked_q70)}</td>
                        <td className={`${orderPlanColumns[9].widthClass} px-4 py-3`}>{formatNumber(row.stacked_q90)}</td>
                        <td className={`${orderPlanColumns[10].widthClass} px-4 py-3`}>
                          <div className="flex items-center gap-2">
                            <span>{displayedFixIAction}</span>
                            {arbitrageSignal.executedAction ? (
                              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${arbitrageSignal.badgeClass}`}>
                                {arbitrageSignal.label}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className={`${orderPlanColumns[11].widthClass} px-4 py-3`}>{formatNumber(displayedFixIVolume)}</td>
                        <td className={`${orderPlanColumns[12].widthClass} px-4 py-3`}>
                          {row.closed_at_fix2 === null || row.closed_at_fix2 === undefined
                            ? '-'
                            : row.closed_at_fix2
                              ? 'Yes'
                              : 'No'}
                        </td>
                        <td className={`${orderPlanColumns[13].widthClass} px-4 py-3`}>{formatNumber(row.closed_volume_ida1)}</td>
                        <td className={`${orderPlanColumns[14].widthClass} px-4 py-3`}>{formatNumber(row.residual_to_ida2)}</td>
                        <td className={`${orderPlanColumns[15].widthClass} px-4 py-3`}>{formatNumber(row.closed_volume_ida2)}</td>
                        <td className={`${orderPlanColumns[16].widthClass} px-4 py-3 font-semibold text-gray-900`}>{formatNumber(row.final_volume_contribution)}</td>
                        <td className={`${orderPlanColumns[17].widthClass} px-4 py-3`}>{row.load_band || '-'}</td>
                        <td className={`${orderPlanColumns[18].widthClass} px-4 py-3`}>{formatNumber(row.blend_alpha)}</td>
                        <td className={`${orderPlanColumns[19].widthClass} px-4 py-3`}>{row.capacity_source || 'nordpool'}</td>
                        <td className={`${orderPlanColumns[20].widthClass} px-4 py-3`}>{formatWarsawDateTime(row.issue_date)}</td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
              </>
            ) : (
              <>
                {isPipelineDataSection ? (
                <div className={`mb-5 rounded-xl border p-4 ${
                  darkMode
                    ? 'border-cyan-400/30 bg-cyan-400/10'
                    : 'border-cyan-200 bg-cyan-50/70'
                }`}>
                  <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className={`text-base font-bold ${darkMode ? 'text-cyan-50' : 'text-slate-900'}`}>
                        {pipelineCopy.inputDatasetTitle}
                      </div>
                      <div className={`mt-1 max-w-4xl text-xs leading-5 ${darkMode ? 'text-cyan-100/80' : 'text-slate-600'}`}>
                        {pipelineCopy.inputDatasetBody}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-3">
                      <div className={`inline-flex items-center rounded-xl border px-4 py-2 text-sm font-extrabold uppercase tracking-[0.16em] ${
                        darkMode ? 'border-cyan-300/35 bg-cyan-300/10 text-cyan-100' : 'border-cyan-200 bg-white text-cyan-800'
                      }`}>
                        {activePredictVariantLabel}
                      </div>
                      <button
                        type="button"
                        onClick={() => loadPredictionInputData({ force: true })}
                        disabled={predictionInputLoading}
                        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                          darkMode
                            ? 'border-cyan-300/50 bg-cyan-300/10 text-cyan-50 hover:bg-cyan-300/20 disabled:opacity-60'
                            : 'border-cyan-200 bg-white text-cyan-900 hover:border-cyan-300 hover:bg-cyan-50 disabled:opacity-60'
                        }`}
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${predictionInputLoading ? 'animate-spin' : ''}`} />
                        {pipelineCopy.refreshStatus}
                      </button>
                    </div>
                  </div>

                  {predictionInputLoading && !activePredictionInputData ? (
                    <div className="mb-3">
                      {renderInlineLoading(pipelineCopy.inputDatasetLoading, 'blue')}
                    </div>
                  ) : null}
                  {predictionInputError ? (
                    <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${
                      darkMode
                        ? 'border-rose-300/40 bg-rose-400/10 text-rose-100'
                        : 'border-rose-200 bg-rose-50 text-rose-800'
                    }`}>
                      {pipelineCopy.inputDatasetError} {predictionInputError}
                    </div>
                  ) : null}

                  <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                    {predictionInputSourceCards.map((source) => {
                      const statusMeta = getStatusBadgeMeta(source.status, formatSourceStatusLabel(source.status));
                      return (
                        <div
                          key={source.key || source.label}
                          className={`rounded-xl border px-3 py-3 ${
                            darkMode ? 'border-slate-700 bg-slate-950/45' : 'border-slate-200 bg-white'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <div className={`truncate text-xs font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {source.kind || 'source'}
                              </div>
                              <div className={`mt-1 truncate text-sm font-bold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`} title={source.label}>
                                {source.label || source.key}
                              </div>
                            </div>
                            <span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusMeta.badgeClass}`}>
                              {statusMeta.label}
                            </span>
                          </div>
                          <div className={`mt-3 flex flex-wrap gap-2 text-xs ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            <span>{formatInteger(source.rows)} {pipelineCopy.sourceRows}</span>
                            {source.columns !== null && source.columns !== undefined ? (
                              <span>{formatInteger(source.columns)} {pipelineCopy.sourceColumns}</span>
                            ) : null}
                          </div>
                          {source.note ? (
                            <div className={`mt-2 line-clamp-2 text-[11px] leading-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`} title={source.note}>
                              {source.note}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mb-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
	                    {[
	                      { label: pipelineCopy.baseDataset, value: `${formatInteger(predictionInputSummary.input_rows)} x ${formatInteger(predictionInputSummary.input_visible_columns ?? predictionInputSummary.input_columns)}` },
	                      { label: pipelineCopy.inputSignals, value: `${formatInteger(predictionInputSummary.signal_rows)} x ${formatInteger(predictionInputSummary.signal_visible_columns ?? predictionInputSummary.signal_columns)}` },
	                      { label: pipelineCopy.modelOutputs, value: `${formatInteger(predictionInputSummary.plan_rows)} ${pipelineCopy.labels.planRows}` },
	                      { label: pipelineCopy.predictionColumns.capacitySource, value: formatDatasetValue(predictionInputSummary.capacity_source) },
	                      { label: pipelineCopy.predictionColumns.loadBand, value: formatDatasetValue(predictionInputSummary.load_band_counts) },
                    ].map((item) => (
                      <div key={item.label} className={`rounded-xl border px-3 py-3 ${
                        darkMode ? 'border-slate-700 bg-slate-950/35' : 'border-slate-200 bg-white/80'
                      }`}>
                        <div className={`text-[11px] font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {item.label}
                        </div>
                        <div className={`mt-1 text-sm font-bold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                          {item.value || '-'}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mb-3">
	                    <div className={`text-sm font-bold ${darkMode ? 'text-cyan-50' : 'text-slate-900'}`}>
	                      {pipelineCopy.datasetTableTitle}
	                    </div>
	                    <div className={`mt-1 text-xs ${darkMode ? 'text-cyan-100/75' : 'text-slate-600'}`}>
	                      {pipelineCopy.datasetTableBody}
	                    </div>
	                    <div className={`mt-2 flex flex-wrap gap-2 text-[11px] font-semibold ${
	                      darkMode ? 'text-cyan-100/85' : 'text-cyan-900'
	                    }`}>
	                      <span className={`rounded-full border px-2.5 py-1 ${
	                        darkMode ? 'border-cyan-300/35 bg-cyan-300/10' : 'border-cyan-200 bg-white'
	                      }`}>
	                        {pipelineCopy.datasetCoverage}: {formatInteger(predictionDatasetColumns.length - 1)} {pipelineCopy.visibleColumns}
	                      </span>
	                      {predictionDatasetHiddenEmptyColumns > 0 ? (
	                        <span className={`rounded-full border px-2.5 py-1 ${
	                          darkMode ? 'border-amber-300/35 bg-amber-300/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900'
	                        }`}>
	                          {formatInteger(predictionDatasetHiddenEmptyColumns)} {pipelineCopy.hiddenEmptyColumns}
	                        </span>
	                      ) : null}
	                    </div>
	                  </div>
                  <div
                    ref={predictionInputHeaderRef}
                    onScroll={() => syncOrderPlanScroll(predictionInputHeaderRef, [predictionInputScrollBodyRef])}
                    className={`mb-1 overflow-x-auto rounded-t-xl border border-b-0 ${
                      darkMode ? 'border-slate-700 bg-slate-950/55' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div style={{ width: `${predictionDatasetTableWidth}px`, height: 14 }} />
                  </div>
                  <div
                    ref={predictionInputScrollBodyRef}
                    onScroll={() => syncOrderPlanScroll(predictionInputScrollBodyRef, [predictionInputHeaderRef])}
                    className={`max-h-[620px] overflow-auto rounded-b-xl border ${
                      darkMode ? 'border-slate-700 bg-slate-950/35' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <table className="table-fixed text-xs" style={{ width: `${predictionDatasetTableWidth}px` }}>
	                      <thead className={`sticky top-0 z-30 ${darkMode ? 'bg-slate-900' : 'bg-slate-100'}`}>
                        <tr>
                          {predictionDatasetColumns.map((column) => (
                            <th
                              key={column.key}
                              className={`px-2.5 py-3 text-left align-bottom font-semibold uppercase tracking-wide text-slate-500 ${
                                column.key === 'hour'
                                  ? `sticky left-0 z-20 w-[72px] ${darkMode ? 'bg-slate-900' : 'bg-slate-100'}`
                                  : 'w-[118px]'
                              }`}
                            >
	                              {column.group ? (
	                                <div className="mb-1 truncate text-[10px] font-semibold uppercase tracking-wide text-slate-400">
	                                  {column.group}
	                                  {column.coverage !== undefined ? ` · ${formatInteger(column.coverage)}/24` : ''}
	                                </div>
                              ) : null}
                              <div className="truncate" title={column.label}>{column.label}</div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {predictionInputRows.map((row) => (
                          <tr
                            key={`prediction-input-${date}-${row.hour}`}
                            className={`border-t transition-colors ${
                              darkMode ? 'border-slate-800 hover:bg-slate-900/70' : 'border-slate-200 hover:bg-slate-50'
                            }`}
                          >
                            {predictionDatasetColumns.map((column) => (
                              <td
                                key={`${row.hour}-${column.key}`}
                                className={`px-2.5 py-2 align-top tabular-nums ${
                                  column.key === 'hour'
                                    ? `sticky left-0 z-10 font-bold ${getDatasetCellTone(column.key)}`
                                    : getDatasetCellTone(column.key)
                                }`}
                              >
                                <div className="truncate" title={column.key === 'hour' ? column.value(row) : formatDatasetValue(column.value(row))}>
                                  {column.key === 'hour'
                                    ? column.value(row)
                                    : formatDatasetValue(column.value(row))}
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                ) : null}

                {isPipelineAuditSection ? (
                <>
                {(() => {
                  const dataDeliveryRow = dailyRows.find((row) => String(row?.date || '').slice(0, 10) === date) || null;
                  const auditDayDetails = dayDetailsByDate[date] || null;
                  const auditDayDetailRows = Array.isArray(auditDayDetails?.hourly) ? auditDayDetails.hourly : [];
                  const auditDaySummary = auditDayDetails?.summary || null;
                  const auditPathSummarySource = auditDaySummary || dataDeliveryRow || {};
                  const isDataPartialFix2Live = Boolean(auditPathSummarySource && isPartialFix2SettleRow(auditPathSummarySource));
                  const auditSettleStageAvailability = orderAuditSummary?.settle_stage_availability || {};
                  const auditHasOfficialSettleBasis = Boolean(
                    auditSettleStageAvailability.fix2
                    || auditSettleStageAvailability.ida1
                    || auditSettleStageAvailability.ida2
                  );
                  const auditHasTraderPlanRowsForDate = Boolean(
                    letsTradeOrderPlanDate === date
                    && Array.isArray(letsTradeOrderPlan)
                    && letsTradeOrderPlan.length
                  );
                  const auditHasFinalReplayDay = Boolean(
                    (
                      (dataDeliveryRow && isRealizedSimulationRow(dataDeliveryRow))
                      || auditHasOfficialSettleBasis
                    )
                    && !isDataPartialFix2Live
                  );
                  const auditTraderPlanRows = auditHasTraderPlanRowsForDate
                    ? letsTradeOrderPlan
                    : auditHasFinalReplayDay
                      ? []
                      : activePredictRows;
                  const auditHasSettledDayDetails = Boolean(
                    (auditHasFinalReplayDay || isDataPartialFix2Live)
                    && auditDaySummary
                    && auditDayDetailRows.length > 0
                  );
                  const useFix1PartialRows = false;
                  const hasStagePrice = (value) => value !== null && value !== undefined;
                  const rowHasAuditComponentRowsFor = (stageRow, stageKey) => (
                    Array.isArray(stageRow?.execution_components?.[stageKey])
                    && stageRow.execution_components[stageKey].some((component) => Math.abs(Number(component?.volume_mwh || 0)) > 0.0001)
                  );
                  const rowsHaveAuditPostFixIStageRows = (rows = []) => (rows || []).some((stageRow) => (
                    Math.abs(Number(stageRow?.closed_volume_fix2 || 0)) > 0.0001
                    || Math.abs(Number(stageRow?.closed_volume_ida1 || 0)) > 0.0001
                    || Math.abs(Number(stageRow?.closed_volume_ida2 || 0)) > 0.0001
                    || Math.abs(Number(stageRow?.residual_to_ida2 || 0)) > 0.0001
                    || rowHasAuditComponentRowsFor(stageRow, 'fix_ii')
                    || rowHasAuditComponentRowsFor(stageRow, 'ida1')
                    || rowHasAuditComponentRowsFor(stageRow, 'ida2')
                    || hasStagePrice(stageRow?.fix_ii_price)
                    || hasStagePrice(stageRow?.ida1_price)
                    || hasStagePrice(stageRow?.ida2_price)
                  ));
                  const stagedPathRows = useFix1PartialRows
                    ? fix1PartialRows
                    : auditHasSettledDayDetails
                      ? auditDayDetailRows
                      : auditHasFinalReplayDay
                        ? []
                        : auditTraderPlanRows;
                  const marketRowsForAvailability = stagedPathRows;
                  const hasAuditPostFixIStageRows = rowsHaveAuditPostFixIStageRows(stagedPathRows);
                  const hasAuditExecutionRowsFor = (volumeKey, priceKey) => marketRowsForAvailability.some((stageRow) => (
                    Number(stageRow?.[volumeKey] || 0) > 0.0001
                    && hasStagePrice(stageRow?.[priceKey])
                  ));
                  const auditMarketAverages = getDailyMarketPriceAverages(marketRowsForAvailability);
                  const auditFix2Settled = Boolean(auditSettleStageAvailability.fix2 || isDataPartialFix2Live || auditHasSettledDayDetails);
                  const auditIda1Settled = Boolean(auditSettleStageAvailability.ida1);
                  const auditIda2Settled = Boolean(auditSettleStageAvailability.ida2);
                  const auditPathAvailability = {
                    fix_i: useFix1PartialRows
                      || auditHasSettledDayDetails
                      || hasAuditExecutionRowsFor('executed_volume_fix1', 'fix_i_price'),
                    fix_ii: !useFix1PartialRows && auditFix2Settled,
                    ida1: !useFix1PartialRows && (
                      isDataPartialFix2Live || !auditIda1Settled
                        ? false
                        : auditMarketAverages.ida1 !== null
                    ),
                    ida2: !useFix1PartialRows && (
                      isDataPartialFix2Live || !auditIda2Settled
                        ? false
                        : auditMarketAverages.ida2 !== null
                    ),
                  };
                  const auditPathSummary = auditHasSettledDayDetails
                    ? (auditDaySummary || dataDeliveryRow || {})
                    : dataDeliveryRow || auditDaySummary || (useFix1PartialRows ? fix1PartialSummary : null) || activeSettleSummary || {};
                  const auditPathNotice = isDataPartialFix2Live && hasAuditPostFixIStageRows
                    ? {
                        title: pipelineCopy.executionFix2FallbackNoticeTitle,
                        body: pipelineCopy.executionFix2FallbackNoticeBody,
                      }
                    : auditHasFinalReplayDay && !auditHasSettledDayDetails
                      ? {
                          title: pipelineCopy.executionSettledDetailsMissingTitle,
                          body: pipelineCopy.executionSettledDetailsMissingBody,
                        }
                    : null;
                  return renderExecutionPathPanel({
                    rows: stagedPathRows,
                    summary: auditPathSummary,
                    availability: auditPathAvailability,
                    copy: pipelineCopy,
                    compact: true,
                    expanded: isPipelineAuditExecutionPathExpanded,
                    onToggle: () => setIsPipelineAuditExecutionPathExpanded((prev) => !prev),
                    quarterPathKeyPrefix: `audit:${date}`,
                    showHeader: false,
                    notice: auditPathNotice,
                  });
                })()}
                <div className={`mt-6 rounded-xl border p-4 ${
                  darkMode ? 'border-slate-700 bg-slate-950/35' : 'border-gray-200 bg-white'
                }`}>
                  {(() => {
                    const auditStatus = orderAuditSummary.status || 'ok';
                    const auditIsGooseLive = Boolean(orderAuditSummary.goose_live);
                    const auditOk = auditStatus === 'ok';
                    const auditHistorical = auditStatus === 'historical_replay';
                    const auditLabel = auditOk
                      ? pipelineCopy.auditOk
                      : auditHistorical
                        ? pipelineCopy.auditHistoricalReplay
                        : pipelineCopy.auditMismatch;
                    const auditStatusClass = auditOk
                      ? (darkMode ? 'border-emerald-400 bg-emerald-500/15 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700')
                      : auditHistorical
                        ? (darkMode ? 'border-sky-400 bg-sky-500/15 text-sky-200' : 'border-sky-200 bg-sky-50 text-sky-800')
                        : (darkMode ? 'border-amber-400 bg-amber-500/15 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800');
                    const fixIIMatrixMeta = orderAuditSummary.fix_ii_matrix || {};
                    const fixIIMatrixReady = fixIIMatrixMeta.status === 'ready';
                    const fixIIMatrixSteps = auditIsGooseLive
                      ? 'Goose live'
                      : fixIIMatrixReady && fixIIMatrixMeta.price_steps
                      ? `${formatInteger(fixIIMatrixMeta.price_steps)}/${formatInteger(fixIIMatrixMeta.max_price_steps || 124)}`
                      : (fixIIMatrixMeta.status === 'unavailable' ? 'n/a' : '-');
                    const fixIIMatrixRange = `${formatNumber(fixIIMatrixMeta.min_price ?? -2500)} / ${formatNumber(fixIIMatrixMeta.max_price ?? 20000)}`;
                    const auditSummaryCards = auditIsGooseLive ? [
                      ['Mismatch', orderAuditSummary.mismatch_count ?? 0],
                      ['Fix I venue', `${formatNumber(orderAuditSummary.venue_fix_i_mwh)} MWh`],
                      ['Goose risk Fix I', `${formatNumber(orderAuditSummary.goose_fix_i_order_mwh ?? 0)} MWh`],
                      ['BASE Fix I', `${formatNumber(orderAuditSummary.base_fix_i_mwh)} MWh`],
                      ['Fix II close', `${formatNumber(orderAuditSummary.venue_fix_ii_mwh)} MWh`],
                      ['IDA1 close', `${formatNumber(orderAuditSummary.venue_ida1_mwh)} MWh`],
                      ['IDA2 close', `${formatNumber(orderAuditSummary.venue_ida2_mwh)} MWh`],
                      ['BASE venue', `${formatNumber(orderAuditSummary.venue_base_mwh)} MWh`],
                      ['Fix I hours', `${formatInteger(orderAuditSummary.goose_fix_i_order_hours || 0)}h`],
                      ['Audit basis', fixIIMatrixSteps],
                    ] : [
                      ['Mismatch', orderAuditSummary.mismatch_count ?? 0],
                      ['Replay drift', orderAuditSummary.historical_replay_diff_count ?? 0],
                      ['Fix I venue', `${formatNumber(orderAuditSummary.venue_fix_i_mwh)} MWh`],
                      ['Fix II venue', `${formatNumber(orderAuditSummary.venue_fix_ii_mwh)} MWh`],
                      ['Fix II matrix', fixIIMatrixSteps],
                      ['Matrix range', fixIIMatrixRange],
                      ['IDA1 venue', `${formatNumber(orderAuditSummary.venue_ida1_mwh)} MWh`],
                      ['IDA2 venue', `${formatNumber(orderAuditSummary.venue_ida2_mwh)} MWh`],
                      ['Fix I ladder', `${formatNumber(orderAuditSummary.fix_i_ladder_mwh)} MWh`],
                      ['BASE Fix I', `${formatNumber(orderAuditSummary.base_fix_i_mwh)} MWh`],
                      ['Fix I hedge', `${formatNumber(orderAuditSummary.base_fix_i_hedge_mwh)} MWh`],
                    ];
                    return (
                      <>
                  <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className={`text-base font-bold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                        {pipelineCopy.auditConsistencyTitle}
                      </div>
                      <div className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        {pipelineCopy.auditConsistencyBody}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-extrabold uppercase ${auditStatusClass}`}>
                        {auditLabel}
                      </span>
                      {orderAuditHourSummaries.length ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (orderAuditAllHoursExpanded) {
                              setExpandedOrderAuditHours(new Set());
                            } else {
                              setExpandedOrderAuditHours(new Set(orderAuditHourSummaries.map((hour) => getOrderAuditHourLabel(hour))));
                            }
                          }}
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                            darkMode
                              ? 'border-slate-600 bg-slate-900 text-slate-100 hover:bg-slate-800'
                              : 'border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100'
                          }`}
                        >
                          {orderAuditAllHoursExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                          {orderAuditAllHoursExpanded ? 'Zwiń godziny' : 'Rozwiń godziny'}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => loadOrderAudit({ force: true })}
                        disabled={orderAuditLoading}
                        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                          darkMode
                            ? 'border-slate-600 bg-slate-900 text-slate-100 hover:bg-slate-800 disabled:opacity-60'
                            : 'border-slate-200 bg-slate-50 text-slate-800 hover:bg-slate-100 disabled:opacity-60'
                        }`}
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${orderAuditLoading ? 'animate-spin' : ''}`} />
                        {pipelineCopy.refreshStatus}
                      </button>
                    </div>
                  </div>
                  {auditHistorical ? (
                    <div className={`mb-4 rounded-lg border px-3 py-2 text-xs ${
                      darkMode ? 'border-sky-400/40 bg-sky-400/10 text-sky-100' : 'border-sky-200 bg-sky-50 text-sky-800'
                    }`}>
                      {pipelineCopy.auditHistoricalReplayBody}
                    </div>
                  ) : null}

                  <div className="mb-4 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                    {auditSummaryCards.map(([label, value]) => (
                      <div key={label} className={`rounded-lg border px-3 py-2 ${
                        darkMode ? 'border-slate-700 bg-slate-900/45' : 'border-slate-200 bg-slate-50'
                      }`}>
                        <div className={`text-[10px] font-semibold uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</div>
                        <div className={`mt-1 text-sm font-bold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>{value}</div>
                      </div>
                    ))}
                  </div>
                      </>
                    );
                  })()}

                  {orderAuditLoading && !activeOrderAuditData ? (
                    <div className="mb-3">{renderInlineLoading(pipelineCopy.auditLoading, 'blue')}</div>
                  ) : null}
                  {orderAuditError ? (
                    <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${
                      darkMode ? 'border-rose-300/40 bg-rose-400/10 text-rose-100' : 'border-rose-200 bg-rose-50 text-rose-800'
                    }`}>
                      {pipelineCopy.auditError} {orderAuditError}
                    </div>
                  ) : null}
                  {!orderAuditError && orderAuditData?.warning ? (
                    <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${
                      darkMode ? 'border-amber-300/40 bg-amber-400/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-800'
                    }`}>
                      {orderAuditData.warning}
                    </div>
                  ) : null}
                  {orderAuditEntries.length ? (
                    <div className="mb-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {orderAuditBasisOptions.map((option) => {
                          const active = orderAuditBasisFilter === option.key;
                          return (
                            <button
                              key={option.key}
                              type="button"
                              onClick={() => setOrderAuditBasisFilter(option.key)}
                              className={`rounded-full border px-3 py-1 text-xs font-bold transition ${
                                active
                                  ? (darkMode ? 'border-cyan-300 bg-cyan-400/15 text-cyan-100' : 'border-cyan-300 bg-cyan-50 text-cyan-900')
                                  : (darkMode ? 'border-slate-700 bg-slate-950 text-slate-400 hover:bg-slate-900' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
                              }`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                      <div className={`mt-2 text-[11px] leading-4 ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                        {orderAuditSummary.goose_live
                          ? 'Domyślnie pokazujemy ścieżkę Goose live: Fix I execution, domknięcie Fix II oraz ewentualne IDA1/IDA2 overlay. To nie jest legacy Noble/SAPRI matrix check.'
                          : 'Domyślnie pokazujemy tylko oficjalne komponenty rozliczenia. Fix II 3-bucket intent to modelowy szkic koszyka; finalny order Noble/SAPRI jest optymalizowany do matrixa z limitem kroków cenowych.'}
                      </div>
                    </div>
                  ) : null}

                  {orderAuditHourSummaries.length ? (
                    <div className={`max-h-[640px] overflow-auto rounded-xl border ${
                      darkMode ? 'border-slate-700' : 'border-slate-200'
                    }`}>
                      <table className="min-w-[1180px] w-full text-xs">
                        <thead className={darkMode ? 'bg-slate-900 text-slate-400' : 'bg-slate-50 text-slate-500'}>
                          <tr>
                            {['H/Q', 'Etap', 'Rola', 'Ordery', 'Limit / cena', 'Expected', 'Kalkulacja', 'PnL komponentu', 'Status orderów'].map((header) => (
                              <th key={header} className="px-3 py-2 text-left font-semibold uppercase tracking-wide">{header}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {orderAuditHourSummaries.map((hour) => {
                            const hourKey = getOrderAuditHourLabel(hour);
                            const expanded = expandedOrderAuditHours.has(hourKey);
                            const hourStatusOk = Number(hour.mismatchCount || 0) === 0;
                            const hourStatusInfo = hourStatusOk && Number(hour.previewCount || 0) > 0;
                            return (
                              <React.Fragment key={`audit-hour-${hourKey}`}>
                                <tr className={`border-t ${
                                  darkMode ? 'border-slate-700 bg-slate-900/55' : 'border-slate-200 bg-slate-50'
                                }`}>
                                  <td className={`px-3 py-2 font-bold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                    <button
                                      type="button"
                                      onClick={() => toggleOrderAuditHour(hourKey)}
                                      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-extrabold transition ${
                                        darkMode
                                          ? 'border-slate-600 bg-slate-950 text-slate-100 hover:bg-slate-800'
                                          : 'border-slate-200 bg-white text-slate-900 hover:bg-slate-100'
                                      }`}
                                    >
                                      {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                      {hourKey}
                                    </button>
                                  </td>
                                  <td className="px-3 py-2 font-semibold">SUMMARY</td>
                                  <td className="px-3 py-2">
                                    <div className={`font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                      {formatInteger(hour.entries.length)} etapów audytu
                                    </div>
                                    <div className={darkMode ? 'text-slate-500' : 'text-slate-500'}>
                                      {hour.mismatchCount ? `${formatInteger(hour.mismatchCount)} mismatch` : hour.previewCount ? `${formatInteger(hour.previewCount)} nieoficjalne` : 'bez rozjazdów'}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div>{formatInteger(hour.orderCount)} submitted</div>
                                    <div className={darkMode ? 'text-slate-500' : 'text-slate-500'}>{formatInteger(hour.executedOrderCount)} executed</div>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div>submitted vol {formatNumber(hour.submittedVolume)} MWh</div>
                                    <div className={darkMode ? 'text-slate-500' : 'text-slate-500'}>executed est. {formatNumber(hour.expectedVolume)} MWh</div>
                                  </td>
                                  <td className="px-3 py-2 font-semibold">{formatNumber(hour.expectedVolume)} MWh</td>
                                  <td className="px-3 py-2 font-semibold">{formatNumber(hour.calculationVolume)} MWh</td>
                                  <td className={`px-3 py-2 ${getValueColor(hour.pnl)}`}>
                                    <div>{formatNumber(hour.pnl)} PLN</div>
                                    {Math.abs(Number(hour.pnlComponentDelta || 0)) > 0.05 ? (
                                      <div className={darkMode ? 'mt-1 text-[10px] text-slate-500' : 'mt-1 text-[10px] text-slate-500'}>
                                        komponenty: {formatNumber(hour.componentPnl)} PLN
                                      </div>
                                    ) : null}
                                  </td>
                                  <td className="px-3 py-2">
                                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase ${
                                      hourStatusOk
                                        ? (hourStatusInfo
                                          ? (darkMode ? 'border-slate-500 bg-slate-500/10 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600')
                                          : (darkMode ? 'border-emerald-400/60 bg-emerald-400/10 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700'))
                                        : (darkMode ? 'border-rose-400/60 bg-rose-400/10 text-rose-200' : 'border-rose-200 bg-rose-50 text-rose-700')
                                    }`}>
                                      {hourStatusOk ? (hourStatusInfo ? 'INFO' : 'OK') : 'CHECK'}
                                    </span>
                                  </td>
                                </tr>
                                {expanded ? hour.entries.map((entry, index) => {
                                  const statusOk = entry.status === 'ok';
                                  const statusInfo = ['preview_only', 'calculation_only', 'historical_replay_diff'].includes(entry.status);
                                  const statusPreviewOnly = entry.status === 'preview_only';
                                  const entryPnl = Number(entry.calculation_pnl);
                                  const hasComponentPnl = Number.isFinite(entryPnl) && Math.abs(entryPnl) > 0.005;
                                  const pnlDisplay = statusPreviewOnly
                                    ? '-'
                                    : entry.calculation_pnl !== null && entry.calculation_pnl !== undefined
                                      ? `${formatNumber(entry.calculation_pnl)} PLN`
                                      : '-';
                                  const basisMeta = getOrderAuditBasisMeta(entry.basis);
                                  const qLabel = entry.qh !== null && entry.qh !== undefined ? ` q${Number(entry.qh) + 1}` : '';
                                  const firstOrder = Array.isArray(entry.orders) ? entry.orders[0] : null;
                                  const sourceLabel = entry.source || firstOrder?.source || '-';
                                  const guardLabel = firstOrder?.guard_label || firstOrder?.guard_type || '';
                                  const orders = Array.isArray(entry.orders) ? entry.orders : [];
                                  return (
                                    <tr key={`${entry.stage}-${entry.hour}-${entry.qh ?? 'h'}-${entry.role}-${index}`} className={`border-t ${
                                      darkMode ? 'border-slate-800' : 'border-slate-200'
                                    }`}>
                                      <td className={`px-3 py-2 pl-8 font-bold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>{formatHourLabel(entry.hour)}{qLabel}</td>
                                      <td className="px-3 py-2">{String(entry.stage || '').toUpperCase()}</td>
                                      <td className="px-3 py-2">
                                        <div className={`font-semibold ${darkMode ? 'text-slate-100' : 'text-slate-900'}`}>{entry.label || entry.role}</div>
                                        <div className="mt-1 flex flex-wrap items-center gap-1">
                                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase ${basisMeta.className}`}>
                                            {basisMeta.label}
                                          </span>
                                          <span className={darkMode ? 'text-slate-500' : 'text-slate-500'}>{sourceLabel}</span>
                                        </div>
                                        <div className={darkMode ? 'mt-1 text-[10px] leading-4 text-slate-500' : 'mt-1 text-[10px] leading-4 text-slate-500'}>
                                          Cena: {entry.price_source || '-'}
                                        </div>
                                        <div className={darkMode ? 'text-[10px] leading-4 text-slate-500' : 'text-[10px] leading-4 text-slate-500'}>
                                          Wolumen: {entry.volume_source || '-'}
                                        </div>
                                        {guardLabel ? (
                                          <span title="Goose risk management wykrył ryzyko: część godzin trafiła do orderu hedge Fix I, część może być pominięta przez ekonomię." className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${
                                            darkMode ? 'border-amber-400/60 bg-amber-400/10 text-amber-200' : 'border-amber-300 bg-amber-50 text-amber-800'
                                          }`}>
                                            {guardLabel}
                                          </span>
                                        ) : null}
                                      </td>
                                      <td className="px-3 py-2">
                                        <div>{formatInteger(entry.order_count)} submitted</div>
                                        <div className={darkMode ? 'text-slate-500' : 'text-slate-500'}>{formatInteger(entry.executed_order_count)} executed</div>
                                        {orders.length ? (
                                          <div className="mt-1 flex max-w-[260px] flex-wrap gap-1">
                                            {orders.map((order, orderIndex) => (
                                              <span
                                                key={`${entry.stage}-${entry.hour}-${entry.qh ?? 'h'}-${entry.role}-order-${orderIndex}`}
                                                title={`${order.instrument || ''} ${order.execution_reason?.pl || (order.executes ? 'executed' : 'submitted only')}`}
                                                className={`inline-flex rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${
                                                  order.executes
                                                    ? (darkMode ? 'border-emerald-400/50 bg-emerald-400/10 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700')
                                                    : (darkMode ? 'border-slate-600 bg-slate-800 text-slate-300' : 'border-slate-200 bg-slate-100 text-slate-600')
                                                }`}
                                              >
                                                {order.side || '-'} {formatNumber(order.volume_mwh)} @ {formatNumber(order.limit_price)} {order.executes ? 'EXEC' : 'SUB'}
                                              </span>
                                            ))}
                                          </div>
                                        ) : null}
                                        {orders.length ? (
                                          <div className={darkMode ? 'mt-1 text-[10px] leading-4 text-slate-500' : 'mt-1 text-[10px] leading-4 text-slate-500'}>
                                            {orders.slice(0, 2).map((order, orderIndex) => (
                                              <div key={`${entry.stage}-${entry.hour}-${entry.qh ?? 'h'}-${entry.role}-reason-${orderIndex}`}>
                                                {order.execution_reason?.pl || (order.executes ? 'Order wykonany.' : 'Order niewykonany.')}
                                              </div>
                                            ))}
                                            {orders.length > 2 ? <div>+{orders.length - 2} kolejne ordery w tooltipach badge.</div> : null}
                                          </div>
                                        ) : null}
                                      </td>
                                      <td className="px-3 py-2">
                                        <div>aukcja {formatNumber(entry.auction_price)}</div>
                                        <div className={darkMode ? 'text-slate-500' : 'text-slate-500'}>vol {formatNumber(entry.submitted_volume_mwh)} MWh</div>
                                      </td>
                                      <td className="px-3 py-2 font-semibold">
                                        {statusPreviewOnly ? `${formatNumber(entry.expected_execution_mwh)} MWh preview` : `${formatNumber(entry.expected_execution_mwh)} MWh`}
                                      </td>
                                      <td className="px-3 py-2 font-semibold">
                                        {statusPreviewOnly ? '-' : `${formatNumber(entry.calculation_volume_mwh)} MWh`}
                                      </td>
                                      <td className={`px-3 py-2 ${hasComponentPnl ? getValueColor(entry.calculation_pnl) : (darkMode ? 'text-slate-500' : 'text-slate-500')}`}>
                                        {pnlDisplay}
                                      </td>
                                      <td className="px-3 py-2">
                                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase ${
                                          statusOk
                                            ? (darkMode ? 'border-emerald-400/60 bg-emerald-400/10 text-emerald-200' : 'border-emerald-200 bg-emerald-50 text-emerald-700')
                                            : statusInfo
                                              ? (darkMode ? 'border-slate-500 bg-slate-500/10 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600')
                                              : (darkMode ? 'border-rose-400/60 bg-rose-400/10 text-rose-200' : 'border-rose-200 bg-rose-50 text-rose-700')
                                        }`}>
                                          {statusOk ? 'OK ordery' : entry.status}
                                        </span>
                                        {entry.raw_status ? (
                                          <div className={darkMode ? 'mt-1 text-[10px] text-slate-500' : 'mt-1 text-[10px] text-slate-500'}>
                                            raw: {entry.raw_status}
                                          </div>
                                        ) : null}
                                      </td>
                                    </tr>
                                  );
                                }) : null}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className={`rounded-lg border px-3 py-4 text-sm ${darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
                      {pipelineCopy.auditNoRows}
                    </div>
                  )}
                </div>
                </>
                ) : null}
              </>
            )}
          </div>
          </div>
          )}
          {isPipelineOrdersSection && (
          <div className={`${simulationInsetCardClass} mt-6 p-4`}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                  <div className="flex items-center gap-2">
                    <div className={`text-base font-semibold ${darkMode ? 'text-slate-50' : 'text-slate-900'}`}>{pipelineCopy.orderPreviewTitle}</div>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wide ${
                      orderPreviewFormat === 'bos'
                        ? (darkMode ? 'bg-emerald-500 text-white' : 'bg-emerald-600 text-white')
                        : (darkMode ? 'bg-amber-500 text-slate-900' : 'bg-amber-500 text-white')
                    }`}>
                      {orderPreviewFormat === 'bos' ? 'BOŚ' : 'Noble'}
                    </span>
                  </div>
                  <div className={`mt-1 text-xs ${darkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                  {typeof pipelineCopy.orderPreviewBody === 'function'
                    ? pipelineCopy.orderPreviewBody(orderPreviewFormat === 'bos' ? 'BOŚ' : 'Noble')
                    : pipelineCopy.orderPreviewBody}
                  </div>
                </div>
              <div className="flex flex-wrap items-center justify-end gap-3">
                <div className={`inline-flex items-center rounded-xl border p-1 ${darkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-200 bg-gray-50'}`}>
                  {[{ key: 'noble', label: 'Noble' }, { key: 'bos', label: 'BOŚ' }].map((fmt) => {
                    const active = orderPreviewFormat === fmt.key;
                    const activeClass = active
                      ? (fmt.key === 'bos'
                          ? (darkMode ? 'bg-emerald-500 text-white shadow-sm' : 'bg-emerald-600 text-white shadow-sm')
                          : (darkMode ? 'bg-amber-500 text-slate-900 shadow-sm' : 'bg-amber-500 text-white shadow-sm'))
                      : (darkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-gray-700 hover:bg-white');
                    return (
                      <button
                        key={fmt.key}
                        type="button"
                        onClick={() => setOrderPreviewFormat(fmt.key)}
                        className={`whitespace-nowrap rounded-lg px-4 py-1.5 text-sm font-bold transition-colors ${activeClass}`}
                      >
                        {fmt.label}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={exportNoblePreviewXlsx}
                  disabled={!noblePackageHasRows}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'text-amber-100 bg-amber-500/15 border border-amber-500/50 hover:bg-amber-500/25' : 'text-amber-800 bg-amber-50 border border-amber-300 hover:bg-amber-100'}`}
                >
                  <FileSpreadsheet size={16} />
                  {pipelineCopy.exportXlsx}
                </button>
                <button
                  type="button"
                  onClick={exportBosLadderXlsx}
                  disabled={!bosLadderHasRows || bosXlsxDownloading}
                  className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${darkMode ? 'text-emerald-100 bg-emerald-500/15 border border-emerald-500/50 hover:bg-emerald-500/25' : 'text-emerald-800 bg-emerald-50 border border-emerald-300 hover:bg-emerald-100'}`}
                >
                  <FileSpreadsheet size={16} />
                  {bosXlsxDownloading ? pipelineCopy.loading : pipelineCopy.exportBosXlsx}
                </button>
              </div>
            </div>
            {orderPreviewMode === 'fix_i' ? (() => {
              const selectedDayRow = dailyRows.find((row) => String(row?.date || '').slice(0, 10) === String(date || '').slice(0, 10)) || {};
              const selectedDaySummary = dayDetailsByDate[date]?.summary || selectedDayRow;
              const ordersGuardInfo = getDailyBaseHedgeInfo(selectedDaySummary);
              const buyGuardSummary = ordersGuardInfo?.buyGuardSummary || {};
              if (!buyGuardSummary.hasIntervention) return null;
              return (
                <div className={`mb-3 rounded-xl border px-4 py-3 text-sm ${
                  darkMode ? 'border-cyan-300/30 bg-cyan-500/10 text-cyan-100' : 'border-cyan-200 bg-cyan-50 text-cyan-900'
                }`}>
                  <div className="flex min-w-0 items-start gap-2">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <div className="font-extrabold">{baseHedgeCopy.fixIBuyGuardLabel}</div>
                      <div className={`mt-1 text-xs leading-5 ${darkMode ? 'text-cyan-100/85' : 'text-cyan-900/80'}`}>
                        {howItWorksLanguage === 'pl'
                          ? `Order BUY Fix I został zablokowany albo ograniczony przez Goose risk management: ${baseHedgeCopy.guardInterventionHours(buyGuardSummary.blockedHours, buyGuardSummary.cappedHours)}. To nie jest submitted order, więc nie trafia do koszyka Noble jako zlecenie.`
                          : `Fix I BUY order was blocked or capped by Goose risk management: ${baseHedgeCopy.guardInterventionHours(buyGuardSummary.blockedHours, buyGuardSummary.cappedHours)}. It is not a submitted order, so it does not enter the Noble basket as an order.`}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {(buyGuardSummary.rows || []).map((row) => (
                          <span
                            key={`orders-buy-guard-${row.hourNumber}-${row.action}`}
                            className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-extrabold ${
                              darkMode ? 'border-cyan-300/35 bg-slate-950/45 text-cyan-100' : 'border-cyan-200 bg-white text-cyan-900'
                            }`}
                          >
                            {formatHourLabel(row.hourNumber)} · {row.action === 'block' ? baseGuardCopy.blockedBadge : baseGuardCopy.cappedBadge}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })() : null}
            {orderPreviewFormat === 'noble' && activeNoblePreviewSourceMeta ? (
              <div className={`mb-3 rounded-xl border px-4 py-3 shadow-sm ${activeNoblePreviewSourceMeta.panelClass}`}>
                <div className="flex min-w-0 items-start gap-2">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold">{pipelineCopy.previewSource}</span>
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${activeNoblePreviewSourceMeta.badgeClass}`}>
                        {activeNoblePreviewSourceMeta.label}
                      </span>
                    </div>
                    <div className="mt-1 text-sm leading-5">{activeNoblePreviewSourceMeta.body}</div>
                    <div className="mt-2 text-xs opacity-90">{activeNoblePreviewSourceMeta.legend}</div>
                  </div>
                </div>
              </div>
            ) : null}
            {orderPreviewFormat === 'noble' && (() => {
              const ltCopy = simCopy?.letsTrade || {};
              const ltDeadlineLabel = ltCopy.deadlineLabel || 'Noble do';
              const stageCards = [
                { key: 'fix_i', label: 'Fix I', rows: nobleOrderPreviewRows.fix_i || [], helper: ltCopy.orderHelpers?.fix_i, deadline: ltCopy.orderDeadlines?.fix_i },
                {
                  key: 'fix_ii',
                  label: 'Fix II',
                  rows: nobleOrderPreviewRows.fix_ii || [],
                  helper: isIda1OrderPreviewReady ? ltCopy.orderHelpers?.fix_ii : ltCopy.orderHelpers?.fixIIWaitingIda1,
                  deadline: ltCopy.orderDeadlines?.fix_ii,
                  muted: !isIda1OrderPreviewReady,
                },
                {
                  key: 'ida1',
                  label: 'IDA1',
                  rows: nobleOrderPreviewRows.ida1 || [],
                  helper: isIda1OrderPreviewReady ? ltCopy.orderHelpers?.ida1Ready : ltCopy.orderHelpers?.ida1Pending,
                  deadline: ltCopy.orderDeadlines?.ida1,
                  muted: !isIda1OrderPreviewReady,
                },
                {
                  key: 'ida2',
                  label: 'IDA2',
                  rows: nobleOrderPreviewRows.ida2 || [],
                  helper: isIda2OrderPreviewReady ? ltCopy.orderHelpers?.ida2Ready : ltCopy.orderHelpers?.ida2Pending,
                  deadline: ltCopy.orderDeadlines?.ida2,
                  muted: !isIda2OrderPreviewReady,
                },
              ];
              return (
                <div className="mt-2 mb-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                  {stageCards.map((stage) => {
                    const active = orderPreviewMode === stage.key;
                    const mutedPreview = Boolean(stage.muted);
                    return (
                      <button
                        key={stage.key}
                        type="button"
                        onClick={() => handleOrderPreviewModeSelect(stage.key)}
                        className={`rounded-lg border p-3 text-left transition ${
                          active
                            ? (darkMode ? 'border-amber-400 bg-amber-500/20 text-amber-50' : 'border-amber-500 bg-amber-50 text-amber-900')
                            : mutedPreview
                              ? (darkMode ? 'border-slate-800 bg-slate-950/50 text-slate-500 opacity-70 hover:bg-slate-900/70' : 'border-gray-200 bg-gray-100 text-gray-500 opacity-70 hover:bg-gray-50')
                              : (darkMode ? 'border-slate-700 bg-slate-950/70 text-slate-200 hover:bg-slate-900' : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-white')
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-sm font-bold">{stage.label}</span>
                          <span className="text-xs font-semibold tabular-nums">{stage.rows.length} rows</span>
                        </div>
                        <div className={`mt-1 text-[11px] ${active ? '' : darkMode ? 'text-slate-500' : 'text-gray-500'}`}>{stage.helper}</div>
                        <div className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold tabular-nums ${
                          active
                            ? (darkMode ? 'border-amber-300/50 bg-amber-300/10 text-amber-50' : 'border-amber-200 bg-white text-amber-700')
                            : mutedPreview
                              ? (darkMode ? 'border-slate-800 bg-slate-900/60 text-slate-500' : 'border-gray-200 bg-white/70 text-gray-400')
                              : (darkMode ? 'border-slate-700 bg-slate-900 text-slate-400' : 'border-gray-200 bg-white text-gray-500')
                        }`}>
                          <Clock3 className="h-3 w-3" />
                          <span>{ltDeadlineLabel} {stage.deadline}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {orderPreviewFormat === 'bos' ? (
              !bosLadderHasRows ? (
                <div className="text-sm text-gray-500">{pipelineCopy.noOrderPlan}</div>
              ) : (
                <>
                  <div className={`mb-3 rounded-xl border px-4 py-3 shadow-sm ${darkMode ? 'border-emerald-800/70 bg-emerald-950/30 text-emerald-100' : 'border-emerald-200 bg-emerald-50/80 text-emerald-900'}`}>
                    <div className="flex min-w-0 items-start gap-2">
                      <Info className="mt-0.5 h-4 w-4 shrink-0" />
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold">{pipelineCopy.previewSource}</span>
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${darkMode ? 'border-emerald-400/50 bg-emerald-500/15 text-emerald-100' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
                            Źródło: Goose live staged orders + BASE close + IDA2 carry, layout BOŚ
                          </span>
                        </div>
                        <div className="mt-1 text-sm leading-5">
                          {howItWorksLanguage === 'pl'
                            ? 'Te same dane co Noble (drabinka Fix II z buy/sell triggerami, BASE close per stage, IDA1/IDA2 preview close), ułożone sekcja-per-etap z kolumnami Ilość K / Limit K / Limit S / Ilość S.'
                            : 'Same data as Noble (Fix II ladder with buy/sell triggers, BASE close per stage, IDA1/IDA2 preview close), laid out section-per-stage with Ilość K / Limit K / Limit S / Ilość S columns.'}
                        </div>
                      </div>
                    </div>
                  </div>
                  {renderBosLadderPreview({ forceExpand: true })}
                </>
              )
            ) : orderPlanLoading && activePredictRows.length === 0 ? (
              <div className="text-sm text-gray-500">
                {renderInlineLoading(pipelineCopy.loadingOrderPlan, 'blue')}
              </div>
            ) : activePredictRows.length === 0 ? (
              <div className="text-sm text-gray-500">{pipelineCopy.noOrderPlan}</div>
            ) : orderPreviewMode === 'fix_i' && externalFixIPreviewLoading && activeNoblePreviewRows.length === 0 ? (
              <div className="text-sm text-gray-500">
                {renderInlineLoading(pipelineCopy.loadingFixI, 'emerald')}
              </div>
            ) : orderPreviewMode === 'ida1' && externalIda1PreviewLoading && activeNoblePreviewRows.length === 0 ? (
              <div className="text-sm text-gray-500">
                {renderInlineLoading(pipelineCopy.loadingIda1, 'emerald')}
              </div>
            ) : orderPreviewMode === 'ida2' && externalIda2PreviewLoading && activeNoblePreviewRows.length === 0 ? (
              <div className="text-sm text-gray-500">
                {renderInlineLoading(pipelineCopy.loadingIda2, 'emerald')}
              </div>
            ) : orderPreviewMode === 'fix_i' && externalFixIPreviewError && activeNoblePreviewRows.length === 0 ? (
              <div className={`flex flex-col gap-3 rounded-xl border px-4 py-3 text-sm ${darkMode ? 'border-amber-800 bg-amber-950/40 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                <div>{pipelineCopy.failedFixI} {externalFixIPreviewError}</div>
                <div>
                  <button
                    type="button"
                    onClick={handleRetryExternalFixIPreview}
                    className={`inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium ${darkMode ? 'bg-amber-900 text-amber-100 hover:bg-amber-800' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}
                  >
                    {pipelineCopy.retryFixI}
                  </button>
                </div>
              </div>
            ) : orderPreviewMode === 'ida1' && externalIda1PreviewError && activeNoblePreviewRows.length === 0 ? (
              <div className={`flex flex-col gap-3 rounded-xl border px-4 py-3 text-sm ${darkMode ? 'border-amber-800 bg-amber-950/40 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                <div>{pipelineCopy.failedIda1} {externalIda1PreviewError}</div>
                <div>
                  <button
                    type="button"
                    onClick={handleRetryExternalIda1Preview}
                    className={`inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium ${darkMode ? 'bg-amber-900 text-amber-100 hover:bg-amber-800' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}
                  >
                    {pipelineCopy.retryIda1}
                  </button>
                </div>
              </div>
            ) : orderPreviewMode === 'ida2' && externalIda2PreviewError && activeNoblePreviewRows.length === 0 ? (
              <div className={`flex flex-col gap-3 rounded-xl border px-4 py-3 text-sm ${darkMode ? 'border-amber-800 bg-amber-950/40 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                <div>{pipelineCopy.failedIda2} {externalIda2PreviewError}</div>
                <div>
                  <button
                    type="button"
                    onClick={handleRetryExternalIda2Preview}
                    className={`inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium ${darkMode ? 'bg-amber-900 text-amber-100 hover:bg-amber-800' : 'bg-amber-100 text-amber-900 hover:bg-amber-200'}`}
                  >
                    {pipelineCopy.retryIda2}
                  </button>
                </div>
              </div>
            ) : activeNoblePreviewRows.length === 0 ? (
              <div className="text-sm text-gray-500">
                {activeNoblePreviewEmptyMessage}
              </div>
            ) : (
              <>
                {isActiveFixIIPreviewAwaitingIda1 ? (
                  <div className={`mb-3 rounded-xl border px-4 py-3 text-sm ${darkMode ? 'border-amber-800 bg-amber-950/40 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                    <div className="font-semibold">{howItWorksLanguage === 'pl' ? 'Wstępny podgląd Fix II' : 'Preliminary Fix II preview'}</div>
                    <div className={`mt-1 text-xs ${darkMode ? 'text-amber-100/80' : 'text-amber-900/80'}`}>
                      {howItWorksLanguage === 'pl'
                        ? 'Fix II korzysta z kwartalnych kwantyli IDA1. Dopóki predykcja IDA1 nie jest gotowa, traktuj te wiersze jako staged preview, a nie finalny koszyk Noble.'
                        : 'Fix II uses quarterly IDA1 quantiles. Until the IDA1 prediction is ready, treat these rows as a staged preview, not the final Noble basket.'}
                    </div>
                  </div>
                ) : null}
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                {activeNoblePreviewShowsLimitPrice && activeNoblePreviewHasReferencePrice ? (
                  <div className={`border-b px-4 py-3 text-xs ${darkMode ? 'border-slate-700 bg-slate-900/70 text-slate-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                    {pipelineCopy.limitPriceNote}
                  </div>
                ) : null}
                <table className={`min-w-full text-sm ${isActiveFixIIPreviewAwaitingIda1 ? 'opacity-70 grayscale-[0.25]' : ''}`}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{pipelineCopy.orderColumns.instrument}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{pipelineCopy.orderColumns.side}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{activeNoblePreviewShowsLimitPrice ? pipelineCopy.orderColumns.limitPrice : pipelineCopy.orderColumns.price}</th>
                      {activeNoblePreviewHasReferencePrice ? (
                        <>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{pipelineCopy.orderColumns.referenceQ50}</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{pipelineCopy.orderColumns.margin}</th>
                        </>
                      ) : null}
                      {activeNoblePreviewHasSourceLabel ? (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Źródło</th>
                      ) : null}
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{pipelineCopy.orderColumns.volume}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeNoblePreviewRows.map((order, index) => (
                      <tr key={`${order.instrument}-${order.side}-${index}`} className="border-t border-gray-100">
                        <td className="px-4 py-3 font-mono text-xs text-gray-700 whitespace-nowrap">{order.instrument}</td>
                        <td className="px-4 py-3">{order.side}</td>
                        <td className="px-4 py-3">{formatNumber(order.price)}</td>
                        {activeNoblePreviewHasReferencePrice ? (
                          <>
                            <td className="px-4 py-3">{formatNumber(order.reference_price)}</td>
                            <td className="px-4 py-3">{formatNumber(order.limit_margin)}</td>
                          </>
                        ) : null}
                        {activeNoblePreviewHasSourceLabel ? (
                          <td className="px-4 py-3">
                            {getNoblePreviewSourceLabel(order) ? (
                              <span className="inline-flex rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                                {getNoblePreviewSourceLabel(order)}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        ) : null}
                        <td className="px-4 py-3">{formatOrderVolume(order.volume)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
          )}
        </div>
        )}

        {isOverridesSection && (
        <div className="rounded-xl border-2 border-cyan-200 bg-cyan-50/30 p-1 space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="text-sm font-medium text-cyan-700">BASE overrides</div>
                <div className="mt-1 text-sm text-gray-500">
                  Pin a day to a manual BASE leg or reuse the latest saved monitor-derived BASE sizing, then optionally trigger the full AWS recalc chain.
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setOverrideSaveStatus(null);
                  loadOverrides();
                  if (overrideFormState.date) {
                    loadOverrideEditor(overrideFormState.date);
                  }
                }}
                className="inline-flex items-center rounded-xl border border-cyan-300 bg-cyan-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-cyan-700"
              >
                Refresh BASE overrides
              </button>
            </div>

            {overrideSaveStatus && (
              <div className={`rounded-xl border px-4 py-3 text-sm ${
                overrideSaveStatus.kind === 'success'
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-amber-200 bg-amber-50 text-amber-900'
              }`}>
                <div className="font-semibold">
                  BASE override {overrideSaveStatus.action}.
                </div>
                {overrideSaveStatus.triggeredRuns?.length ? (
                  <div className="mt-2 space-y-1">
                    {overrideSaveStatus.triggeredRuns.map((run) => (
                      <div key={`${run.stage}-${run.run_id || run.status}`} className="text-xs leading-relaxed">
                        <span className="font-semibold">{run.stage}</span>: {run.status || 'unknown'}
                        {run.run_id ? ` (${run.run_id})` : ''}
                        {run.message ? ` — ${run.message}` : ''}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-1">No AWS recalc runs were triggered.</div>
                )}
                {overrideSaveStatus.recalcStatus ? (
                  <div className={`mt-3 inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getManualRecalcBadgeClass(overrideSaveStatus.recalcStatus)}`}>
                    {getManualRecalcLabel(overrideSaveStatus.recalcStatus)}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => navigate('/fix2-v5-sim/pipeline')}
                  className="mt-3 inline-flex items-center rounded-lg border border-current px-3 py-1.5 text-xs font-semibold transition hover:bg-white/70"
                >
                  Open Pipeline tab
                </button>
              </div>
            )}

            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
              <div className="rounded-2xl border border-gray-200 bg-white p-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">Active overrides</div>
                    <div className="mt-1 text-sm text-gray-500">
                      Range follows the current Summary / Daily Results filter: {startDate} to {endDate}.
                    </div>
                  </div>
                </div>
                <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Source</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Base Action</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Volume</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Base Price</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Updated By</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Updated At / Recalc</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Note</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overridesLoading ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-6">
                            {renderInlineLoading('Loading BASE overrides...', 'cyan')}
                          </td>
                        </tr>
                      ) : overridesData.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-6 text-center text-gray-500">
                            No active BASE overrides in the selected range.
                          </td>
                        </tr>
                      ) : overridesData.map((override) => (
                        <tr key={override.date} className="border-t border-gray-100">
                          <td className="px-4 py-3 font-medium text-gray-900">{override.date}</td>
                          <td className="px-4 py-3 text-gray-600">{override.source === 'monitor_data' ? 'Monitor data' : 'Manual input'}</td>
                          <td className="px-4 py-3">{override.base_action}</td>
                          <td className="px-4 py-3">{formatNumber(override.volume)} MW</td>
                          <td className="px-4 py-3">
                            {override.base_price === null || override.base_price === undefined
                              ? 'n/a'
                              : `${formatNumber(override.base_price)} PLN/MWh`}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{override.updated_by || 'unknown'}</td>
                          <td className="px-4 py-3 text-gray-600">
                            <div>{formatWarsawDateTime(override.updated_at)}</div>
                            {override.recalc_status ? (
                              <div className={`mt-2 inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${getManualRecalcBadgeClass(override.recalc_status)}`}>
                                {getManualRecalcLabel(override.recalc_status)}
                              </div>
                            ) : null}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{override.note || '-'}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => navigate(`/fix2-v5-sim/overrides?date=${override.date}`)}
                                className="inline-flex items-center rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-semibold text-cyan-900 transition hover:bg-cyan-100"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteOverride(override.date)}
                                className="inline-flex items-center rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-900 transition hover:bg-rose-100"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-5">
                <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-lg font-semibold text-gray-900">
                        {overrideFormState.mode === 'edit' ? 'Edit override' : 'Add override'}
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        Override BASE sizing manually, or reuse the BASE already encoded in the latest saved Goose schedule for one delivery date.
                      </div>
                    </div>
                </div>

                <form className="mt-5 space-y-4" onSubmit={handleSaveOverride}>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Date</label>
                    <input
                      type="date"
                      value={overrideFormState.date}
                      onChange={(event) => {
                        const nextDate = event.target.value;
                        handleOverrideFieldChange('date', nextDate);
                        if (nextDate) {
                          navigate(`/fix2-v5-sim/overrides?date=${nextDate}`, { replace: true });
                          loadOverrideEditor(nextDate);
                        }
                      }}
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-gray-900 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Override source</label>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <button
                        type="button"
                        onClick={() => handleOverrideFieldChange('source', 'manual')}
                        className={`rounded-xl border px-4 py-3 text-left transition ${
                          overrideFormState.source === 'manual'
                            ? 'border-cyan-300 bg-cyan-50 text-cyan-900 shadow-sm'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-cyan-200'
                        }`}
                      >
                        <div className="text-sm font-semibold">Manual input</div>
                        <div className="mt-1 text-xs text-gray-500">
                          Pin BASE action, sizing, and optional stored price by hand.
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOverrideFieldChange('source', 'monitor')}
                        className={`rounded-xl border px-4 py-3 text-left transition ${
                          overrideFormState.source === 'monitor'
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-950 shadow-sm'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-emerald-200'
                        }`}
                      >
                        <div className="text-sm font-semibold">Override with monitor data</div>
                        <div className="mt-1 text-xs text-gray-500">
                          Use the latest saved Goose schedule for this date, if one is available.
                        </div>
                      </button>
                    </div>
                  </div>

                  {overrideFormState.source === 'monitor' ? (
                    <div className={`rounded-xl border px-4 py-3 text-sm ${
                      overrideFormState.monitorCandidate?.available
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
                        : 'border-amber-200 bg-amber-50 text-amber-900'
                    }`}>
                      {overrideFormState.monitorCandidate?.available ? (
                        <>
                          <div className="font-semibold">
                            Monitor-derived BASE: {overrideFormState.monitorCandidate.base_action} {formatNumber(overrideFormState.monitorCandidate.volume)} MW
                          </div>
                          <div className="mt-1 text-xs text-emerald-800">
                            Source file: {overrideFormState.monitorCandidate.source_filename || 'latest saved Goose schedule'}
                          </div>
                          <div className="mt-1 text-xs text-emerald-800">
                            Uploaded: {formatWarsawDateTime(overrideFormState.monitorCandidate.uploaded_at)}
                          </div>
                          <div className="mt-2 text-xs text-emerald-800">
                            This source reuses the BASE adjustment already encoded in the latest `goose_grafik_dostaw` workbook for the selected date.
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-semibold">Monitor-derived BASE is not available yet for this date.</div>
                          <div className="mt-1 text-xs text-amber-800">
                            {overrideFormState.monitorCandidate?.error || 'Save a Goose schedule workbook for this day first, then reload the editor.'}
                          </div>
                        </>
                      )}
                    </div>
                  ) : null}

                  {overrideFormState.source === 'manual' ? (
                    <>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">Base action</label>
                        <select
                          value={overrideFormState.baseAction}
                          onChange={(event) => handleOverrideFieldChange('baseAction', event.target.value)}
                          className="w-full rounded-xl border border-gray-300 px-3 py-2 text-gray-900 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                        >
                          <option value="Buy">Buy</option>
                          <option value="Sell">Sell</option>
                          <option value="Idle">Idle</option>
                        </select>
                      </div>

                      {overrideFormState.baseAction !== 'Idle' ? (
                        <>
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Volume (MW)</label>
                            <input
                              type="number"
                              step="1"
                              min="0"
                              value={overrideFormState.volume}
                              onChange={(event) => handleOverrideFieldChange('volume', event.target.value)}
                              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-gray-900 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                            />
                          </div>

                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Stored base price (PLN/MWh)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={overrideFormState.basePrice}
                              onChange={(event) => handleOverrideFieldChange('basePrice', event.target.value)}
                              placeholder={
                                overrideFormState.fixIBasePrice === null || overrideFormState.fixIBasePrice === undefined
                                  ? 'Optional; informational only'
                                  : `Optional; current Fix I base price is ${formatNumber(overrideFormState.fixIBasePrice)}`
                              }
                              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-gray-900 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                            />
                          </div>
                        </>
                      ) : (
                        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                          Idle override forces the BASE leg to zero sizing for this day.
                        </div>
                      )}
                    </>
                  ) : null}

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Note</label>
                    <textarea
                      rows={4}
                      value={overrideFormState.note}
                      onChange={(event) => handleOverrideFieldChange('note', event.target.value)}
                      className="w-full rounded-xl border border-gray-300 px-3 py-2 text-gray-900 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20"
                      placeholder="Why are we freezing this day?"
                    />
                  </div>

                  <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={overrideFormState.triggerRecalc}
                      onChange={(event) => handleOverrideFieldChange('triggerRecalc', event.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                    <span>Trigger AWS recalc after save or delete.</span>
                  </label>

                  {overrideFormState.mode === 'edit' && (overrideFormState.updatedAt || overrideFormState.updatedBy) ? (
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                      Updated by {overrideFormState.updatedBy || 'unknown'} on {formatWarsawDateTime(overrideFormState.updatedAt)}.
                    </div>
                  ) : null}

                  {overrideFormState.recalcStatus ? (
                    <div className={`rounded-xl border px-4 py-3 text-sm ${
                      isManualRecalcActive(overrideFormState.recalcStatus)
                        ? 'border-amber-200 bg-amber-50 text-amber-900'
                        : getManualRecalcBadgeClass(overrideFormState.recalcStatus)
                    }`}>
                      <div className="font-semibold">{getManualRecalcLabel(overrideFormState.recalcStatus)}</div>
                      {isManualRecalcActive(overrideFormState.recalcStatus) ? (
                        <div className="mt-1 text-xs">
                          Results keep the manual badge immediately, but PnL should be treated as pending until the staged predict and settle chain finishes.
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="submit"
                      disabled={overrideFormLoading || (overrideFormState.source === 'monitor' && !overrideFormState.monitorCandidate?.available)}
                      className="inline-flex items-center rounded-xl border border-cyan-300 bg-cyan-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {overrideFormLoading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      disabled={overrideFormLoading}
                      onClick={() => {
                        navigate('/fix2-v5-sim/overrides', { replace: true });
                        resetOverrideForm(date);
                      }}
                      className="inline-flex items-center rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Cancel
                    </button>
                    {overrideFormState.mode === 'edit' ? (
                      <button
                        type="button"
                        disabled={overrideFormLoading}
                        onClick={() => handleDeleteOverride(overrideFormState.date, {
                          triggerRecalc: overrideFormState.triggerRecalc,
                        })}
                        className="inline-flex items-center rounded-xl border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-900 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        Delete override
                      </button>
                    ) : null}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        )}

        {isHowSection && (
        <div id="simulation-how-it-works" className="space-y-5 scroll-mt-24">
          <div
            className={`overflow-hidden rounded-[28px] border ${
              darkMode ? 'border-cyan-500/30 bg-slate-950/60' : 'border-cyan-200 bg-white'
            }`}
          >
            <div
              className={`border-b px-5 py-5 lg:px-6 lg:py-6 ${
                darkMode
                  ? 'border-cyan-500/20 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.22),_transparent_34%),linear-gradient(135deg,rgba(8,47,73,0.92),rgba(15,23,42,0.96))]'
                  : 'border-cyan-100 bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.15),_transparent_32%),linear-gradient(135deg,#f0fdfa,#eff6ff)]'
              }`}
            >
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(420px,0.95fr)] xl:items-end">
                <div className="min-w-0 max-w-4xl">
                  <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] ${
                    darkMode
                      ? 'border-cyan-400/30 bg-cyan-500/10 text-cyan-100'
                      : 'border-cyan-200 bg-white/80 text-cyan-900'
                  }`}>
                    <BookOpen className="h-3.5 w-3.5" />
                    {howItWorksCopy.headerTitle}
                  </div>
                  <div className={`mt-3 text-2xl font-semibold leading-tight lg:text-[2rem] ${darkMode ? 'text-slate-50' : 'text-slate-950'}`}>
                    {howItWorksLanguage === 'pl'
                      ? 'Krótki przewodnik po aktualnej logice Goose IDA2 simulation'
                      : 'A compact guide to the current Goose IDA2 simulation logic'}
                  </div>
                  <div className={`mt-3 max-w-3xl text-sm leading-7 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {howItWorksCopy.headerSubtitle}
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[420px]">
                  {howItWorksCopy.quickReadCards.slice(0, 3).map((card, index) => (
                    <div
                      key={card.label}
                      className={`rounded-2xl border px-4 py-4 shadow-sm ${
                        index === 0
                          ? darkMode
                            ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-50'
                            : 'border-emerald-200 bg-white/85 text-emerald-950'
                          : index === 1
                            ? darkMode
                              ? 'border-sky-400/25 bg-sky-500/10 text-sky-50'
                              : 'border-sky-200 bg-white/85 text-sky-950'
                            : darkMode
                              ? 'border-violet-400/25 bg-violet-500/10 text-violet-50'
                              : 'border-violet-200 bg-white/85 text-violet-950'
                      }`}
                    >
                      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] opacity-75">{card.label}</div>
                      <div className={`mt-2 text-sm leading-6 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{renderHowText(card.body)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="px-5 py-5 lg:px-6 lg:py-6">
              <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
                <div
                  className={`rounded-3xl border p-5 ${
                    darkMode
                      ? 'border-emerald-400/25 bg-emerald-950/20'
                      : 'border-emerald-200 bg-emerald-50/90'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 rounded-2xl p-2.5 ${darkMode ? 'bg-emerald-400/15 text-emerald-100' : 'bg-emerald-100 text-emerald-900'}`}>
                      <Activity className="h-5 w-5" />
                    </div>
                    <div>
                      <div className={`text-lg font-semibold ${darkMode ? 'text-emerald-50' : 'text-emerald-950'}`}>{howItWorksCopy.flowTitle}</div>
                      <div className={`mt-2 text-sm leading-7 ${darkMode ? 'text-slate-300' : 'text-emerald-950/80'}`}>{renderHowText(howItWorksCopy.flowCaption)}</div>
                    </div>
                  </div>
                  <div className="mt-5 grid gap-3 lg:grid-cols-4">
                    {howItWorksCopy.flowSteps.map((step, index) => (
                      <div key={step.label} className="relative">
                        <div className={`h-full rounded-2xl border px-4 py-4 ${
                          darkMode
                            ? 'border-emerald-400/20 bg-slate-950/35'
                            : 'border-emerald-200 bg-white/75'
                        }`}>
                          <div className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${darkMode ? 'text-emerald-200/80' : 'text-emerald-900/70'}`}>
                            {howItWorksLanguage === 'pl' ? `Etap ${index + 1}` : `Step ${index + 1}`}
                          </div>
                          <div className={`mt-2 text-base font-semibold ${darkMode ? 'text-slate-50' : 'text-slate-950'}`}>{step.label}</div>
                          <div className={`mt-2 text-sm leading-6 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{step.note}</div>
                        </div>
                        {index < howItWorksCopy.flowSteps.length - 1 ? (
                          <div className={`pointer-events-none absolute -right-2 top-1/2 hidden -translate-y-1/2 lg:block ${darkMode ? 'text-emerald-300/60' : 'text-emerald-600/70'}`}>
                            <ArrowUp className="h-4 w-4 rotate-90" />
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className={`rounded-3xl border p-5 ${
                    darkMode
                      ? 'border-sky-400/25 bg-sky-950/20'
                      : 'border-sky-200 bg-sky-50/85'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 rounded-2xl p-2.5 ${darkMode ? 'bg-sky-400/15 text-sky-100' : 'bg-sky-100 text-sky-900'}`}>
                      <Radar className="h-5 w-5" />
                    </div>
                    <div>
                      <div className={`text-lg font-semibold ${darkMode ? 'text-sky-50' : 'text-sky-950'}`}>{howItWorksCopy.quickReadTitle}</div>
                      <div className={`mt-2 text-sm leading-7 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {renderHowText(howItWorksCopy.quickReadCards[3]?.body || '')}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 space-y-3">
                    {howItWorksCopy.quickReadCards.slice(3).map((card) => (
                      <div
                        key={card.label}
                        className={`rounded-2xl border px-4 py-3 ${
                          darkMode
                            ? 'border-sky-400/20 bg-slate-950/35'
                            : 'border-sky-200 bg-white/75'
                        }`}
                      >
                        <div className={`text-sm font-semibold ${darkMode ? 'text-slate-50' : 'text-slate-950'}`}>{card.label}</div>
                        <div className={`mt-1 text-sm leading-6 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{renderHowText(card.body)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-5 xl:grid-cols-[1.05fr_0.95fr]">
                <div
                  className={`rounded-3xl border p-5 ${
                    darkMode
                      ? 'border-indigo-400/25 bg-indigo-950/20'
                      : 'border-indigo-200 bg-indigo-50/85'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 rounded-2xl p-2.5 ${darkMode ? 'bg-indigo-400/15 text-indigo-100' : 'bg-indigo-100 text-indigo-900'}`}>
                      <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                      <div className={`text-lg font-semibold ${darkMode ? 'text-indigo-50' : 'text-indigo-950'}`}>{howItWorksCopy.overview.title}</div>
                      {howItWorksCopy.overview.paragraphs.map((paragraph) => (
                        <div key={paragraph} className={`mt-2 text-sm leading-7 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                          {renderHowText(paragraph)}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-5 grid gap-3 md:grid-cols-2">
                    <div className={`rounded-2xl border px-4 py-4 ${darkMode ? 'border-indigo-400/20 bg-slate-950/35' : 'border-indigo-200 bg-white/75'}`}>
                      <div className={`text-sm font-semibold ${darkMode ? 'text-indigo-100' : 'text-indigo-950'}`}>{howItWorksCopy.baseSource.title}</div>
                      {howItWorksCopy.baseSource.paragraphs.map((paragraph) => (
                        <div key={paragraph} className={`mt-2 text-sm leading-6 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                          {renderHowText(paragraph)}
                        </div>
                      ))}
                    </div>
                    <div className={`rounded-2xl border px-4 py-4 ${darkMode ? 'border-indigo-400/20 bg-slate-950/35' : 'border-indigo-200 bg-white/75'}`}>
                      <div className={`text-sm font-semibold ${darkMode ? 'text-indigo-100' : 'text-indigo-950'}`}>{howItWorksCopy.manualOverrides.title}</div>
                      <ul className={`mt-2 space-y-2 text-sm leading-6 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {howItWorksCopy.manualOverrides.bullets.map((bullet) => (
                          <li key={bullet}>{renderHowText(bullet)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div
                  className={`rounded-3xl border p-5 ${
                    darkMode
                      ? 'border-amber-400/25 bg-amber-950/20'
                      : 'border-amber-200 bg-amber-50/90'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 rounded-2xl p-2.5 ${darkMode ? 'bg-amber-400/15 text-amber-100' : 'bg-amber-100 text-amber-900'}`}>
                      <BarChart3 className="h-5 w-5" />
                    </div>
                    <div>
                      <div className={`text-lg font-semibold ${darkMode ? 'text-amber-50' : 'text-amber-950'}`}>{howItWorksCopy.pipelineReason.title}</div>
                      <ul className={`mt-3 space-y-2 text-sm leading-7 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {howItWorksCopy.pipelineReason.bullets.map((bullet) => (
                          <li key={bullet}>{renderHowText(bullet)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="mt-5 grid gap-3">
                    <div className={`rounded-2xl border px-4 py-4 ${darkMode ? 'border-amber-400/20 bg-slate-950/35' : 'border-amber-200 bg-white/80'}`}>
                      <div className={`text-sm font-semibold ${darkMode ? 'text-amber-100' : 'text-amber-950'}`}>{howItWorksCopy.scheduler.title}</div>
                      <div className={`mt-2 text-sm leading-6 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{howItWorksCopy.scheduler.intro}</div>
                      <div className="mt-3 grid gap-2">
                        {howItWorksCopy.scheduler.bullets.map((bullet) => {
                          const { lead, description } = splitSchedulerBullet(bullet);
                          return (
                            <div key={bullet} className={`rounded-xl border px-3 py-3 ${darkMode ? 'border-amber-400/20 bg-amber-500/5' : 'border-amber-200 bg-amber-50/60'}`}>
                              <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${darkMode ? 'text-amber-100/80' : 'text-amber-900/75'}`}>{lead}</div>
                              {description ? (
                                <div className={`mt-1 text-sm leading-6 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{renderHowText(description)}</div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div className={`rounded-2xl border px-4 py-4 ${darkMode ? 'border-amber-400/20 bg-slate-950/35' : 'border-amber-200 bg-white/80'}`}>
                      <div className={`text-sm font-semibold ${darkMode ? 'text-amber-100' : 'text-amber-950'}`}>{howItWorksCopy.executionMonitor.title}</div>
                      <ul className={`mt-2 space-y-2 text-sm leading-6 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {howItWorksCopy.executionMonitor.bullets.map((bullet) => (
                          <li key={bullet}>{renderHowText(bullet)}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-5 lg:grid-cols-3">
                <div className={`rounded-3xl border p-5 ${darkMode ? 'border-slate-700 bg-slate-900/45' : 'border-slate-200 bg-slate-50/90'}`}>
                  <div className={`text-lg font-semibold ${darkMode ? 'text-slate-50' : 'text-slate-950'}`}>{howItWorksCopy.v5Model.title}</div>
                  <ul className={`mt-3 space-y-2 text-sm leading-7 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {howItWorksCopy.v5Model.bullets.map((bullet) => (
                      <li key={bullet}>{renderHowText(bullet)}</li>
                    ))}
                  </ul>
                  <div className={`mt-4 border-t pt-4 ${darkMode ? 'border-slate-700 text-slate-300' : 'border-slate-200 text-slate-700'}`}>
                    <div className="text-sm font-semibold">{howItWorksCopy.ida2Model.title}</div>
                    <ul className="mt-2 space-y-2 text-sm leading-6">
                      {howItWorksCopy.ida2Model.bullets.map((bullet) => (
                        <li key={bullet}>{renderHowText(bullet)}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className={`rounded-3xl border p-5 ${darkMode ? 'border-cyan-500/25 bg-cyan-950/15' : 'border-cyan-200 bg-cyan-50/85'}`}>
                  <div className={`text-lg font-semibold ${darkMode ? 'text-cyan-50' : 'text-cyan-950'}`}>{howItWorksCopy.mooseModel.title}</div>
                  <ul className={`mt-3 space-y-2 text-sm leading-7 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {howItWorksCopy.mooseModel.bullets.map((bullet) => (
                      <li key={bullet}>{renderHowText(bullet)}</li>
                    ))}
                  </ul>
                  <div className={`mt-4 border-t pt-4 ${darkMode ? 'border-cyan-500/15 text-slate-300' : 'border-cyan-200 text-slate-700'}`}>
                    <div className="text-sm font-semibold">{howItWorksCopy.residualFlow.title}</div>
                    <ul className="mt-2 space-y-2 text-sm leading-6">
                      {howItWorksCopy.residualFlow.bullets.map((bullet) => (
                        <li key={bullet}>{renderHowText(bullet)}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className={`rounded-3xl border p-5 ${darkMode ? 'border-rose-500/20 bg-rose-950/15' : 'border-rose-200 bg-rose-50/85'}`}>
                  <div className={`text-lg font-semibold ${darkMode ? 'text-rose-50' : 'text-rose-950'}`}>{howItWorksCopy.status.title}</div>
                  <ul className={`mt-3 space-y-2 text-sm leading-7 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {howItWorksCopy.status.bullets.map((bullet) => (
                      <li key={bullet}>{renderHowText(bullet)}</li>
                    ))}
                  </ul>
                  <div className={`mt-4 border-t pt-4 ${darkMode ? 'border-rose-500/15 text-slate-300' : 'border-rose-200 text-slate-700'}`}>
                    <div className="text-sm font-semibold">{howItWorksCopy.numbers.title}</div>
                    <ul className="mt-2 space-y-2 text-sm leading-6">
                      {howItWorksCopy.numbers.bullets.map((bullet) => (
                        <li key={bullet}>{renderHowText(bullet)}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-5 lg:grid-cols-2">
                <div className={`rounded-3xl border p-5 ${darkMode ? 'border-sky-500/20 bg-sky-950/15' : 'border-sky-200 bg-sky-50/90'}`}>
                  <div className={`text-lg font-semibold ${darkMode ? 'text-sky-50' : 'text-sky-950'}`}>{howItWorksCopy.volumeAllocation.title}</div>
                  <ul className={`mt-3 grid gap-2 text-sm leading-7 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {howItWorksCopy.volumeAllocation.bullets.map((bullet) => (
                      <li key={bullet}>{renderHowText(bullet)}</li>
                    ))}
                  </ul>
                </div>
                <div className={`rounded-3xl border p-5 ${darkMode ? 'border-violet-500/20 bg-violet-950/15' : 'border-violet-200 bg-violet-50/90'}`}>
                  <div className={`text-lg font-semibold ${darkMode ? 'text-violet-50' : 'text-violet-950'}`}>{howItWorksCopy.runtimeParameters.title}</div>
                  <ul className={`mt-3 grid gap-2 text-sm leading-7 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {howItWorksCopy.runtimeParameters.bullets.map((bullet) => (
                      <li key={bullet}>{renderHowText(bullet)}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
        {isResultsSection && summary && (strategyResultValue >= 0 ? (
          <div className="flex items-center gap-2 text-green-700 text-sm">
            <TrendingUp className="w-4 h-4" />
            {resultsCopy.strategyProfitable}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-red-700 text-sm">
            <TrendingDown className="w-4 h-4" />
            {resultsCopy.strategyNegative}
          </div>
        ))}
      </div>
    </div>
  );
}

export default Fix2V5Sim;
